import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, Optional, SkipSelf, ElementRef, Component, Input, ViewChild, EventEmitter, Output, HostBinding, isDevMode, ContentChildren, forwardRef, HostListener, NgModule, Directive, Pipe, Attribute, ChangeDetectionStrategy, ViewEncapsulation, ContentChild, Renderer2, ViewChildren, TemplateRef } from '@angular/core';
import * as i1$1 from '@angular/cdk/a11y';
import { A11yModule, ActiveDescendantKeyManager } from '@angular/cdk/a11y';
import * as i2 from '@angular/common';
import { CommonModule, isPlatformBrowser, DOCUMENT, DatePipe, CurrencyPipe, formatDate } from '@angular/common';
import { tap, catchError, finalize, share, first, takeUntil, debounceTime, take } from 'rxjs/operators';
import { of, throwError, Subject, timer, fromEvent, merge, takeUntil as takeUntil$1, throttleTime, interval, BehaviorSubject } from 'rxjs';
import * as i1 from '@angular/common/http';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i3 from '@angular/cdk/overlay';
import { OverlayConfig, ConnectionPositionPair, OverlayModule } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import * as i1$4 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i1$3 from '@angular/forms';
import { FormsModule, FormControlName, ReactiveFormsModule, NG_VALUE_ACCESSOR, NG_VALIDATORS, Validators, FormControl } from '@angular/forms';
import * as i1$2 from '@angular/cdk/clipboard';
import { __decorate, __param, __metadata } from 'tslib';
import { NINE, ZERO, DELETE as DELETE$1, ENTER, END, HOME, DOWN_ARROW, UP_ARROW, RIGHT_ARROW, LEFT_ARROW, SPACE, ESCAPE } from '@angular/cdk/keycodes';
import { isArray, isEqual } from 'lodash';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { ScrollingModule } from '@angular/cdk/scrolling';
import * as i2$1 from '@angular/cdk/layout';

const SERVER_URL = new InjectionToken('SERVER_URL');
class CdsIconRegistry {
    constructor(http, platformId, serverUrl) {
        this.http = http;
        this.platformId = platformId;
        this.serverUrl = serverUrl;
        this.cachedIconByUrl = new Map();
        this.iconsFetchingByUrl = new Map();
        this._assetsFolder = './cds-assets';
    }
    setAssetsFolder(assetsFolder) {
        this._assetsFolder = assetsFolder;
    }
    getAssetsFolder() {
        return this._assetsFolder;
    }
    /** Add a SVG to the registry by passing a name and the SVG data. */
    addSvgByData(data, name) {
        if (!this.cachedIconByUrl.has(name)) {
            this.cachedIconByUrl.set(name, data);
        }
    }
    /** Add a SVG to the registry by passing a name and the SVG source url. */
    addSvgByUrl(url, name = url) {
        if (!this.cachedIconByUrl.has(name)) {
            const data = this._fetchSvgFromUrl(url);
            this.iconsFetchingByUrl.set(name, data);
        }
    }
    /** Load a SVG to the registry from a URL. */
    loadSvg(name, subFolder) {
        const svgByName = this.getSvgByName(name);
        if (svgByName)
            return svgByName;
        const sFolder = name.includes('b1') ? 'b1' : name.includes('white') ? 'white' : subFolder;
        const filePath = `${this._assetsFolder}/svgs/icons/${sFolder}/${name}.svg`;
        const svgData = this._fetchSvgFromUrl(filePath, name);
        return svgData;
    }
    /** Get loaded SVG from registry by name. */
    getSvgByName(name) {
        if (this.cachedIconByUrl.has(name)) {
            return of(this.cachedIconByUrl.get(name));
        }
        else if (this.iconsFetchingByUrl.has(name)) {
            return this.iconsFetchingByUrl.get(name);
        }
    }
    /** Remove a SVG from the registry by URL (or name). */
    unloadSvg(name) {
        return this.cachedIconByUrl.delete(name);
    }
    /**fetch SVG data from given URL */
    _fetchSvgFromUrl(url, name = url) {
        if (!url)
            throw Error('URL is empty or undefined');
        const fetchingInProgress = this.iconsFetchingByUrl.get(url);
        if (fetchingInProgress)
            return fetchingInProgress;
        const svgRequest = this.http.get(url, { responseType: 'text' }).pipe(tap(req => this.cachedIconByUrl.set(name, req)), catchError(err => {
            return throwError(err);
        }), finalize(() => this.iconsFetchingByUrl.delete(name)), share());
        this.iconsFetchingByUrl.set(name, svgRequest);
        return svgRequest;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconRegistry, deps: [{ token: i1.HttpClient }, { token: PLATFORM_ID }, { token: SERVER_URL, optional: true }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconRegistry, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconRegistry, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [SERVER_URL]
                }] }] });
function ICON_REGISTRY_PROVIDER_FACTORY(parentRegistry, httpClient, platformId, serverUrl) {
    return (parentRegistry || new CdsIconRegistry(httpClient, platformId, serverUrl));
}
const ICON_REGISTRY_PROVIDER = {
    provide: CdsIconRegistry,
    deps: [
        [new Optional(), new SkipSelf(), CdsIconRegistry],
        HttpClient,
        [PLATFORM_ID],
        [new Optional(), SERVER_URL]
    ],
    useFactory: ICON_REGISTRY_PROVIDER_FACTORY
};

class CdsIcon {
    set shade(style) {
        this._shade =
            this.type === '2021'
                ? ''
                : style === 'dark'
                    ? 'b1'
                    : style === 'light'
                        ? 'white'
                        : style;
    }
    get shade() {
        return this._shade;
    }
    /**
     * SVG Icons are name in this manner: 000-name-shade(-circle)
     *000: 3 digits code for 'icon', 2 digits code for 'sfi'
     *name: name of the icon
     *shade: 'b1' for dark, 'white' for light
     *(-circle): include -circle in file name if includeCircle is true
     */
    set name(name) {
        this._name = name;
        this.type = this.type
            ? this.type
            : name.split('-')[0].length === 2
                ? 'sfi'
                : 'icon';
    }
    get name() {
        return this._name;
    }
    constructor(iconRegistry, cdr) {
        this.iconRegistry = iconRegistry;
        this.cdr = cdr;
        this.role = 'img';
        this.idStr = '';
        this.size = 'medium';
        this.isHoverSfi = false;
        this.includeCircle = false;
        this.tabIndex = null;
        this._shade = '';
    }
    ngAfterViewInit() {
        this.init();
    }
    ngOnDestroy() {
        this.destroy();
    }
    ngOnChanges() {
        if (this._svgElement)
            this.destroy();
        this.init();
    }
    get circleString() {
        return (this.type === 'sfi' && this.isHoverSfi) ||
            (this.includeCircle && this.type !== '2021')
            ? '-circle'
            : '';
    }
    get svgName() {
        const shade = this._shade ? `-${this._shade}` : '';
        return `${this._name}${shade}${this.circleString}`;
    }
    /**
     * Small Format icon
     */
    get SFIName() {
        const alt = this.isHoverSfi ? '-hover' : this.circleString;
        return `${this._shade}-${this._name}${alt}`;
    }
    destroy() {
        if (this.svgDivElementRef?.nativeElement)
            this.svgDivElementRef.nativeElement.innerHTML = '';
        this._svgElement = undefined;
        if (this._iconSubscription)
            this._iconSubscription.unsubscribe();
    }
    init() {
        this.type === 'sfi' ? this.getSmallFormatIconClass() : this.getSVGIcon();
    }
    getSVGIcon() {
        this.iconClass = `${this.size}${this.circleString}`;
        const subFolder = this.type === 'sfi' || this.type === 'icon' ? null : this.type;
        this._iconSubscription = this.iconRegistry
            .loadSvg(this.svgName, this.type)
            .subscribe((data) => this.initSvg(data));
    }
    getSmallFormatIconClass() {
        this.iconClass = `sfi ${this.SFIName} ${this.size}${this.circleString}`;
        if (this.type === 'sfi') {
            const sfiImg = this.getSfiImage();
            if (sfiImg != undefined && this.svgDivElementRef != undefined)
                this.svgDivElementRef.nativeElement.style.backgroundImage = `url("${sfiImg}")`;
        }
    }
    initSvg(data) {
        if (!data || !this.svgDivElementRef)
            return;
        const divElem = this.svgDivElementRef.nativeElement;
        divElem.innerHTML = data;
        this._svgElement = divElem.firstChild;
        this.cdr.markForCheck();
        if (this.pathColor || this.circleColor) {
            this.changeFillColorInSvg(this._svgElement, this.pathColor, this.circleColor);
        }
    }
    /**
     * Changes fill color inside the SVG file
     * @param svgEl the SVG file to update
     * @param pathColor the color to update
     * @param circleColor the color of the circle to update
     */
    changeFillColorInSvg(svgEl, pathColor, circleColor) {
        const svgPaths = svgEl.querySelectorAll('path');
        const svgCircles = svgEl.querySelectorAll('circle');
        if (svgPaths?.length && pathColor) {
            svgPaths.forEach(svgPath => {
                svgPath.style.fill = pathColor;
            });
        }
        if (svgCircles?.length && circleColor) {
            svgCircles.forEach(svgCircle => {
                svgCircle.style.fill = circleColor;
            });
        }
    }
    getSfiImage() {
        const hoverOrCircle = this.isHoverSfi ? '-hover' : this.circleString;
        let svgName = `${this.name}${hoverOrCircle}`;
        let fillColor = this.pathColor
            ? this.pathColor
            : this.shade === 'b1'
                ? '#002a54'
                : '#fff';
        fillColor = fillColor.replace('#', '%23');
        // console.log('getSfiImage', {name: this.name, shade: this.shade, pathColor: this.pathColor, svgName, fillColor});
        const sfiIcons = {
            '01-up': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7.99'%3E%3Ctitle%3E01-up-b1%3C/title%3E%3Cpath d='M18,15.45a1,1,0,0,1-.73-.32L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18,15.45Z' transform='translate(-5 -7.46)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '01-up-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M13.41,8.05a2,2,0,0,0-2.82,0L5.27,13.77a1,1,0,0,0,1.46,1.36L12,9.47l5.27,5.66a1,1,0,1,0,1.46-1.36Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '01-up-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E01-up-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.68,14.18a1,1,0,0,1-1.41,0L12,9.47,6.73,15.13a1,1,0,0,1-1.46-1.36l5.32-5.72a2,2,0,0,1,2.82,0l5.32,5.72A1,1,0,0,1,18.68,15.18Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '02-down': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-b1%3C/title%3E%3Cpath d='M12,16.52a1.94,1.94,0,0,1-1.41-.62L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36L13.41,15.9A1.94,1.94,0,0,1,12,16.52Z' transform='translate(-5 -8.5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '02-down-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M17.27,8.82,12,14.48,6.73,8.82a1,1,0,0,0-1.46,1.36l5.32,5.72a1.91,1.91,0,0,0,2.82,0l5.32-5.72a1,1,0,1,0-1.46-1.36Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '02-down-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E02-down-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.73,9.18L13.41,15.9a1.91,1.91,0,0,1-2.82,0L5.27,10.18A1,1,0,0,1,6.73,8.82L12,14.48l5.27-5.66a1,1,0,1,1,1.46,1.36Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '03-left': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Cpath d='M14.45,19a1,1,0,0,1-.68-.27L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12l5.66,5.26a1,1,0,0,1,0,1.42A1,1,0,0,1,14.45,19Z' transform='translate(-7.43 -5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '03-left-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M15.18,5.32a1,1,0,0,0-1.41-.06L8.05,10.58A2,2,0,0,0,7.43,12a2,2,0,0,0,.62,1.41l5.72,5.32a1,1,0,0,0,1.36-1.47L9.47,12l5.66-5.27A1,1,0,0,0,15.18,5.32Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '03-left-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E03-left-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm3.13,16.26a1,1,0,0,1,0,1.42,1,1,0,0,1-1.41.05L8.05,13.41A2,2,0,0,1,7.43,12a2,2,0,0,1,.62-1.42l5.72-5.32a1,1,0,0,1,1.36,1.47L9.47,12Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '04-right': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8.02 14'%3E%3Ctitle%3E04-right-b1%3C/title%3E%3Cpath d='M10,19a1,1,0,0,1-.73-.32,1,1,0,0,1,.05-1.42L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12a2,2,0,0,1-.62,1.41l-5.72,5.32A1,1,0,0,1,10,19Z' transform='translate(-9 -5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '04-right-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M10.68,5.26A1,1,0,0,0,9.32,6.73L15,12,9.32,17.26a1,1,0,0,0-.05,1.42,1,1,0,0,0,1.41.05l5.72-5.32A2,2,0,0,0,17,12a2,2,0,0,0-.62-1.42Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '04-right-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E04-right-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm4.4,12.41-5.72,5.32a1,1,0,0,1-1.36-1.47L15,12,9.32,6.73a1,1,0,0,1,1.36-1.47l5.72,5.32A2,2,0,0,1,17,12,2,2,0,0,1,16.4,13.41Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '05-check': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 9.99'%3E%3Ctitle%3E05-check-b1%3C/title%3E%3Cpath d='M9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43l-8.1,7.86A1.53,1.53,0,0,1,9.54,17Z' transform='translate(-5 -7)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '05-check-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M17.3,7.28,9.54,14.8,6.7,12a1,1,0,0,0-1.4,1.44l3.19,3.09a1.51,1.51,0,0,0,1,.42,1.53,1.53,0,0,0,1.06-.42l8.1-7.86a1,1,0,1,0-1.4-1.43Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '05-check-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E05-check-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.7,7.71-8.1,7.86A1.53,1.53,0,0,1,9.54,17a1.51,1.51,0,0,1-1-.42L5.3,13.48A1,1,0,0,1,6.7,12L9.54,14.8,17.3,7.28a1,1,0,1,1,1.4,1.43Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '06-close': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-b1%3C/title%3E%3Cpath d='M13.41,12l3.8-3.79a1,1,0,1,0-1.42-1.42L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-6.5 -6.5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '06-close-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M17.21,6.79a1,1,0,0,0-1.42,0L12,10.59,8.21,6.79A1,1,0,1,0,6.79,8.21L10.59,12l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L12,13.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42L13.41,12l3.8-3.79A1,1,0,0,0,17.21,6.79Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '06-close-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm5.21,14.79a1,1,0,0,1,0,1.42,1,1,0,0,1-1.42,0L12,13.41l-3.79,3.8a1,1,0,0,1-1.42,0,1,1,0,0,1,0-1.42L10.59,12,6.79,8.21A1,1,0,1,1,8.21,6.79L12,10.59l3.79-3.8a1,1,0,1,1,1.42,1.42L13.41,12Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '07-equal': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 7'%3E%3Ctitle%3E07-equal-b1%3C/title%3E%3Cpath d='M18,10.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:${fillColor}'/%3E%3Cpath d='M18,15.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -8.5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '07-equal-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,8.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,13.5H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '07-equal-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E07-equal-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,14.5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-5H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '08-plus': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E08-plus-b1%3C/title%3E%3Cpath d='M18,11H13V6a1,1,0,0,0-2,0v5H6a1,1,0,0,0,0,2h5v5a1,1,0,0,0,2,0V13h5a1,1,0,0,0,0-2Z' transform='translate(-5 -5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '08-plus-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '08-plus-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '09-minus': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 2'%3E%3Ctitle%3E09-minus-b1%3C/title%3E%3Cpath d='M18,13H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-5 -11)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '09-minus-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '09-minus-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E09-minus-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,12H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '10-navigation': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 10'%3E%3Ctitle%3E10-navigation-b1%3C/title%3E%3Cpath d='M6,9H18a1,1,0,0,0,0-2H6A1,1,0,0,0,6,9Z' transform='translate(-5 -7)' style='fill:${fillColor}'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:${fillColor}'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-5 -7)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '10-navigation-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,7H6A1,1,0,0,0,6,9H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,11H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18,15H6a1,1,0,0,0,0,2H18a1,1,0,0,0,0-2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '10-navigation-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E10-navigation-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6,16H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6a1,1,0,0,1,0-2H18a1,1,0,0,1,0,2Zm0-4H6A1,1,0,0,1,6,7H18a1,1,0,0,1,0,2Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '11-favorite': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Ctitle%3E11-favorite-b1%3C/title%3E%3Cpath d='M12,7.33l.71,2.26L13,10.64h3.31l-1.85,1.43-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33M12,4.5a.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10a.87.87,0,0,0-.88-.88h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0Z' transform='translate(-5 -4.5)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '11-favorite-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-alt%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3Cpath d='M18.12,9.14h-4l-1.27-4a.9.9,0,0,0-.55-.56.93.93,0,0,0-.29,0,.88.88,0,0,0-.83.58L9.72,9.14H5.88a.91.91,0,0,0-.7.34.88.88,0,0,0,.16,1.23l3.31,2.55L7.41,17.37a.87.87,0,0,0,.15.78.86.86,0,0,0,.7.35.89.89,0,0,0,.53-.18L12,15.86l3.23,2.45a.87.87,0,0,0,.53.18,1.15,1.15,0,0,0,.26,0,.88.88,0,0,0,.58-1.1l-1.24-4.11,3.28-2.54A.85.85,0,0,0,19,10,.87.87,0,0,0,18.12,9.14Zm-3.66,2.93-.81.63.3,1L14.63,16l-1.7-1.3L12,14l-.91.69L9.4,16l.69-2.28.29-1-.82-.63L7.71,10.64h3.07l.35-1L12,7.33l.71,2.26L13,10.64h3.31Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '11-favorite-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E11-favorite-b1-hover%3C/title%3E%3Cpolygon points='12 9.64 11.66 8.59 10.95 6.33 10.13 8.64 9.78 9.64 8.72 9.64 6.71 9.64 8.56 11.07 9.38 11.7 9.09 12.69 8.4 14.97 10.11 13.66 11.02 12.97 11.93 13.66 13.63 14.96 12.95 12.69 12.65 11.7 13.46 11.07 15.31 9.64 13.1 9.64 12 9.64' style='fill:${fillColor}'/%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm6.66,9.71-3.28,2.54,1.24,4.11a.88.88,0,0,1-.58,1.1,1.15,1.15,0,0,1-.26,0,.87.87,0,0,1-.53-.18L12,15.86,8.79,18.32a.89.89,0,0,1-.53.18.86.86,0,0,1-.7-.35.87.87,0,0,1-.15-.78l1.24-4.11L5.34,10.71a.88.88,0,0,1-.16-1.23.91.91,0,0,1,.7-.34H9.72l1.44-4.06A.88.88,0,0,1,12,4.5a.93.93,0,0,1,.29,0,.9.9,0,0,1,.55.56l1.27,4h4A.87.87,0,0,1,19,10,.85.85,0,0,1,18.66,10.71Z' transform='translate(-1 -1)' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '12-info': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,21A10,10,0,1,1,22,12,10,10,0,0,1,12,22Z' transform='translate(-1 -1)' style='fill:${fillColor};fill-rule:evenodd'/%3E%3Cpath d='M12,9a1,1,0,0,0-1,1v8a1,1,0,0,0,2,0V10A1,1,0,0,0,12,9Z' transform='translate(-1 -1)' style='fill:${fillColor};fill-rule:evenodd'/%3E%3Ccircle cx='11' cy='5' r='1' style='fill:${fillColor}'/%3E%3C/svg%3E`,
            '12-info-circle': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 1c5.514 0 10 4.486 10 10 0 5.515-4.486 10-10 10S2 17.515 2 12C2 6.485 6.486 2 12 2m0 17.149a1 1 0 0 1-1-1v-8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1m1-13a1 1 0 1 1-2 0 1 1 0 0 1 2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='${fillColor}'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='${fillColor}'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A`,
            '12-info-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 18.149a1 1 0 0 1-1-1v-8a1 1 0 1 1 2 0v8a1 1 0 0 1-1 1m1-13a1 1 0 1 1-2 0 1 1 0 0 1 2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='${fillColor}'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='${fillColor}'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A`,
            '13-help': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12 1C5.925 1 1 5.926 1 12c0 6.075 4.925 11 11 11s11-4.925 11-11c0-6.074-4.925-11-11-11m0 1c5.514 0 10 4.486 10 10 0 5.515-4.486 10-10 10S2 17.515 2 12C2 6.485 6.486 2 12 2m1 16.155a1 1 0 1 1-2 0 1 1 0 0 1 2 0M12.053 7.15c.344 0 .67.04.944.082.61.094 1.036.593 1.036 1.215 0 .457-.223.739-.417.934l-1.336 1.276a3.97 3.97 0 0 0-1.268 2.919v.572c0 .554.45 1.005 1.003 1.005.553 0 1.002-.451 1.002-1.005v-.572c0-.361.05-.892.49-1.348l1.66-1.547c.56-.593.872-1.387.872-2.234 0-1.598-1.113-2.934-2.646-3.177-.696-.155-1.901-.15-2.533.01a3.112 3.112 0 0 0-1.865 1.092 4.701 4.701 0 0 0-.117.167c-.154.212-.223.397-.223.588 0 .552.45 1.002 1.003 1.002.317 0 .495-.086.68-.33.052-.07.336-.427.892-.56.258-.06.534-.09.823-.09' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='${fillColor}'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='${fillColor}'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A`,
            '13-help-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E12-info-b1-hover%3C/title%3E%3Cpath d='M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm1,17a1,1,0,0,1-2,0V10a1,1,0,0,1,2,0ZM12,7a1,1,0,1,1,1-1A1,1,0,0,1,12,7Z' transform='translate(-1 -1)' style='fill:${fillColor};fill-rule:evenodd'/%3E%3C/svg%3E`,
            '14-warning': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12.001 1c-.511 0-1.003.3-1.245.816L1.144 20.882a1.535 1.535 0 0 0-.144.651C1 22.343 1.621 23 2.388 23h19.224c.213 0 .424-.052.616-.152.686-.36.967-1.24.627-1.966L13.244 1.816a1.432 1.432 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1m0 1a.32.32 0 0 1 .151.039c.078.04.144.112.199.228l9.599 19.039c.112.239.025.546-.186.656a.33.33 0 0 1-.152.038H2.388c-.21 0-.388-.214-.388-.467 0-.079.017-.157.037-.201L11.662 2.24c.083-.177.23-.24.339-.24M12 15.996a1 1 0 0 1-1-1v-7a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1m-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='${fillColor}'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='${fillColor}'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A`,
            '14-warning-hover': `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M12.001 1c-.511 0-1.003.3-1.245.816L1.144 20.882a1.535 1.535 0 0 0-.144.651C1 22.343 1.621 23 2.388 23h19.224c.213 0 .424-.052.616-.152.686-.36.967-1.24.627-1.966L13.244 1.816a1.432 1.432 0 0 0-.629-.664A1.321 1.321 0 0 0 12.001 1M12 15.996a1 1 0 0 1-1-1v-7a1 1 0 1 1 2 0v7a1 1 0 0 1-1 1m-1 3a1 1 0 1 1 2 0 1 1 0 0 1-2 0' id='a'/%3E%3C/defs%3E%3Cg transform='translate(.2 .2)' fill='none' fill-rule='evenodd'%3E%3Cmask id='b' fill='${fillColor}'%3E%3Cuse xlink:href='%23a'/%3E%3C/mask%3E%3Cuse fill='%23000' xlink:href='%23a'/%3E%3Cg mask='url(%23b)' fill='${fillColor}'%3E%3Cpath d='M0 0h24v24H0z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E%0A`,
        };
        return sfiIcons[svgName];
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIcon, deps: [{ token: CdsIconRegistry }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsIcon, selector: "cds-icon", inputs: { role: "role", idStr: "idStr", ariaLabel: "ariaLabel", size: "size", isHoverSfi: "isHoverSfi", includeCircle: "includeCircle", type: "type", svgStyle: "svgStyle", tabIndex: "tabIndex", altText: "altText", shade: "shade", name: "name", pathColor: "pathColor", circleColor: "circleColor" }, host: { classAttribute: "cds-icon" }, viewQueries: [{ propertyName: "svgDivElementRef", first: true, predicate: ["svgContainer"], descendants: true, read: ElementRef }], usesOnChanges: true, ngImport: i0, template: `
    <div
      #svgContainer
      [attr.role]="role"
      [attr.tabindex]="tabIndex"
      [ngStyle]="type === 'glyph' ? null : svgStyle"
      [ngClass]="[iconClass]"
      [attr.id]="idStr"
      [attr.alt]="altText"
      [attr.aria-label]="ariaLabel"
    ></div>
  `, isInline: true, styles: [":host{line-height:0}:host div{display:inline-block;line-height:0px}.small{width:12px;height:12px}.medium{width:var(--cds-icon-width, 14px);height:var(--cds-icon-height, 14px)}.large{width:16px;height:16px}.small-circle{width:18px;height:18px}.medium-circle{width:20px;height:20px}.large-circle{width:22px;height:22px}.sfi{background-repeat:no-repeat}.sfi.small{width:12px;height:12px;margin:3px}.sfi.medium{width:14px;height:14px;margin:3px}.sfi.large{width:16px;height:16px;margin:4px}.sfi.small-circle{width:18px;height:18px;margin:5px}.sfi.medium-circle{width:20px;height:20px;margin:5px}.sfi.large-circle{width:22px;height:22px;margin:5px}.sfi.g1-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-06-close:hover,.sfi.g1-06-close:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-white%3C/title%3E%3Cpath d='M20,24a1.94,1.94,0,0,1-1.41-.62l-5.32-5.72a1,1,0,0,1,1.46-1.36L20,22l5.27-5.66a1,1,0,0,1,1.46,1.36l-5.32,5.72A1.94,1.94,0,0,1,20,24' transform='translate(-13 -15.99)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIcon, decorators: [{
            type: Component,
            args: [{ selector: 'cds-icon', template: `
    <div
      #svgContainer
      [attr.role]="role"
      [attr.tabindex]="tabIndex"
      [ngStyle]="type === 'glyph' ? null : svgStyle"
      [ngClass]="[iconClass]"
      [attr.id]="idStr"
      [attr.alt]="altText"
      [attr.aria-label]="ariaLabel"
    ></div>
  `, host: { class: 'cds-icon' }, styles: [":host{line-height:0}:host div{display:inline-block;line-height:0px}.small{width:12px;height:12px}.medium{width:var(--cds-icon-width, 14px);height:var(--cds-icon-height, 14px)}.large{width:16px;height:16px}.small-circle{width:18px;height:18px}.medium-circle{width:20px;height:20px}.large-circle{width:22px;height:22px}.sfi{background-repeat:no-repeat}.sfi.small{width:12px;height:12px;margin:3px}.sfi.medium{width:14px;height:14px;margin:3px}.sfi.large{width:16px;height:16px;margin:4px}.sfi.small-circle{width:18px;height:18px;margin:5px}.sfi.medium-circle{width:20px;height:20px;margin:5px}.sfi.large-circle{width:22px;height:22px;margin:5px}.sfi.g1-06-close{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 11'%3E%3Ctitle%3E06-close-white%3C/title%3E%3Cpath d='M21.41,20l3.8-3.79a1,1,0,0,0-1.42-1.42L20,18.59l-3.79-3.8a1,1,0,0,0-1.42,1.42L18.59,20l-3.8,3.79a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L20,21.41l3.79,3.8a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.42Z' transform='translate(-14.5 -14.5)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-06-close:hover,.sfi.g1-06-close:focus{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 22 22'%3E%3Ctitle%3E06-close-white-hover%3C/title%3E%3Cpath d='M21.41,20.15l3.8-3.79a1,1,0,0,0,0-1.41,1,1,0,0,0-1.42,0L20,18.74,16.21,15a1,1,0,0,0-1.42,0,1,1,0,0,0,0,1.41l3.8,3.79L14.79,24a1,1,0,0,0,0,1.41,1,1,0,0,0,1.42,0L20,21.57l3.79,3.79a1,1,0,0,0,1.42,0,1,1,0,0,0,0-1.41ZM20,9A11,11,0,1,0,31,20,11,11,0,0,0,20,9' transform='translate(-9 -9)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}.sfi.g1-02-down{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 8.02'%3E%3Ctitle%3E02-down-white%3C/title%3E%3Cpath d='M20,24a1.94,1.94,0,0,1-1.41-.62l-5.32-5.72a1,1,0,0,1,1.46-1.36L20,22l5.27-5.66a1,1,0,0,1,1.46,1.36l-5.32,5.72A1.94,1.94,0,0,1,20,24' transform='translate(-13 -15.99)' style='fill:%23666;fill-rule:evenodd'/%3E%3C/svg%3E\")}\n"] }]
        }], ctorParameters: () => [{ type: CdsIconRegistry }, { type: i0.ChangeDetectorRef }], propDecorators: { role: [{
                type: Input
            }], idStr: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], size: [{
                type: Input
            }], isHoverSfi: [{
                type: Input
            }], includeCircle: [{
                type: Input
            }], type: [{
                type: Input
            }], svgStyle: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], altText: [{
                type: Input
            }], shade: [{
                type: Input
            }], name: [{
                type: Input
            }], pathColor: [{
                type: Input
            }], circleColor: [{
                type: Input
            }], svgDivElementRef: [{
                type: ViewChild,
                args: ['svgContainer', { read: ElementRef }]
            }] } });

/**
 * @Component: Accordion Section
 * @description This component generates an accordion section
 */
class AccordionSectionComponent {
    set isOpen(isOpen) {
        if (this.isAndroidDevice)
            this.announcer.announce(`${isOpen ? 'expanded' : 'collapse'}`);
        this.isExpanded = this._isOpen = isOpen;
        this.isOpenChange.emit(this._isOpen);
    }
    get isOpen() {
        return this._isOpen;
    }
    // #endregion
    get isAndroidDevice() {
        if (!navigator || !navigator.userAgent)
            return false;
        else
            return /(android)/i.test(navigator.userAgent);
    }
    constructor(announcer) {
        this.announcer = announcer;
        // #region // -------- Inputs/Outputs ---------- //
        /**
         * @property disabled
         * @description disables the click event on the accordian section
         */
        this.disabled = false;
        /**
         * @property id
         * @description This attribute sets the HTML ID for the expanding content area and the value of the
         * aria-controls attribute
         * @return string
         */
        this.idStr = null;
        /**
         * @property icon
         * @description This attribute sets the value for displaying an icon
         * @return string
         */
        this.icon = 'None';
        /**
         * @property sectionClicked
         * @description emits when the section button is clicked
         * @twoway false
         */
        this.sectionClicked = new EventEmitter();
        /**
         * @property sectionFocused
         * @description emits when the section button is focused
         * @twoway false
         */
        this.sectionFocused = new EventEmitter();
        /**
         * @property isOpen
         * @description Checks if the current accordion section is openend/expanded
         * @twoway true
         */
        this._isOpen = false;
        this.isOpenChange = new EventEmitter();
        this.cssClassParent = false;
        this.cssClassLevel1 = false;
        this.attributeRole = "listitem";
        this.isExpanded = false;
    }
    toggleSection(sectionIndex) {
        if (!this.disabled) {
            this.sectionClicked.emit(sectionIndex);
            this.isOpen = !this.isOpen;
            if (this.isAndroidDevice)
                this.announcer.announce(`${this.isOpen ? 'expanded' : 'collapse'}`);
            this.isExpanded = this._isOpen = this.isOpen;
            this.isOpenChange.emit(this._isOpen);
        }
    }
    focusButton() {
        this.button.nativeElement.focus();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AccordionSectionComponent, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: AccordionSectionComponent, selector: "cds-accordion-section", inputs: { disabled: "disabled", idStr: "idStr", icon: "icon", isOpen: "isOpen" }, outputs: { sectionClicked: "sectionClicked", sectionFocused: "sectionFocused", isOpenChange: "isOpenChange" }, host: { properties: { "class.cds-parent": "this.cssClassParent", "class.cds-level1": "this.cssClassLevel1", "attr.role": "this.attributeRole", "class.cds-is-expanded": "this.isExpanded" }, classAttribute: "cds-accordion-section" }, viewQueries: [{ propertyName: "button", first: true, predicate: ["button"], descendants: true }], ngImport: i0, template: `
    <button
      #button
      [attr.id]="idStr + '-button'"
      [attr.aria-controls]="idStr"
      [attr.aria-expanded]="isOpen ? true : false"
      [attr.aria-disabled]="disabled ? true : null"
      [attr.disabled]="disabled ? true : null"
      class="cds-category"
      (click)="toggleSection(sectionIndex)"
      [ngClass]="{ cdsDisabled: disabled }"
    >
      <div class="accordion-inner" [ngStyle]="{ 'justify-content': (this.icon == 'Right' ? 'space-between' : 'normal') }" >
        <cds-icon
          *ngIf="this.icon == 'Left'"
          [ngClass]="{ 'icon-expanded': isOpen }"
          class="icon-Left"
          name="02-down-default"
          type="2021"
          altText="Icon"
          ariaLabel="Icon"
          aria-hidden="true"
        >
        </cds-icon>
        <ng-content select="[cdsAccordionHeader]"></ng-content>
        <cds-icon
          *ngIf="this.icon == 'Right'"
          [ngClass]="{ 'icon-expanded': isOpen }"
          class="icon-Right"
          name="02-down-default"
          type="2021"
          altText="Icon"
          ariaLabel="Icon"
          aria-hidden="true"
        >
        </cds-icon>
      </div>
    </button>

    <!-- expandable area-->
    <div class="cds-accordionSubMenu" [attr.id]="idStr">
      <div class="accordion-row">
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true, styles: [".cds-category{border:none;padding:0;text-align:left;width:100%;min-height:55px;white-space:nowrap;background-color:var(--cds-accordion-category-bg-color, #fff)}.cds-accordionSubMenu{display:none;width:100%;margin:auto;max-height:0;overflow:hidden;transition:max-height .15s ease-out,background-color .5s ease-in-out,visibility 1ms ease .501s;border-bottom:1px solid #dcdcdc;background-color:var(--cds-accordion-submenu-bg-color, #eee)}:host.cds-is-expanded .cds-category{background-color:var(--cds-accordion-expanded-category-bg-color, #eee)}:host.cds-is-expanded>.cds-accordionSubMenu{display:block;max-height:300em;transition:max-height .25s ease-in,background-color .5s ease-in-out 2ms}.cds-category:hover{cursor:pointer}.cds-category ::ng-deep .cds-hold-still{position:relative}:host.cdsDisabled{pointer-events:none;opacity:.9}:host button{outline:none}:host button:focus{outline:2px solid #056dae}:host cds-icon{--cds-icon-path-fill: var(--cds-accordion-icon-color, #056dae);margin:5px}:host cds-icon.icon-expanded ::ng-deep path{transform:rotate(180deg);transform-origin:center}:host cds-icon.icon-Right{float:right}:host cds-icon.icon-Right ::ng-deep .sfi.medium{height:8px;top:5px;outline:none}:host cds-icon ::ng-deep .sfi.b1-02-down{display:inline-block;position:relative;transition:transform .4s ease-in-out;transform:rotate(0);transform-origin:50%}:host cds-icon ::ng-deep .sfi.medium{height:8px;top:1px;outline:none}:host .accordion-inner{display:flex;flex-wrap:nowrap;align-items:center}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AccordionSectionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-accordion-section', host: { class: 'cds-accordion-section' }, template: `
    <button
      #button
      [attr.id]="idStr + '-button'"
      [attr.aria-controls]="idStr"
      [attr.aria-expanded]="isOpen ? true : false"
      [attr.aria-disabled]="disabled ? true : null"
      [attr.disabled]="disabled ? true : null"
      class="cds-category"
      (click)="toggleSection(sectionIndex)"
      [ngClass]="{ cdsDisabled: disabled }"
    >
      <div class="accordion-inner" [ngStyle]="{ 'justify-content': (this.icon == 'Right' ? 'space-between' : 'normal') }" >
        <cds-icon
          *ngIf="this.icon == 'Left'"
          [ngClass]="{ 'icon-expanded': isOpen }"
          class="icon-Left"
          name="02-down-default"
          type="2021"
          altText="Icon"
          ariaLabel="Icon"
          aria-hidden="true"
        >
        </cds-icon>
        <ng-content select="[cdsAccordionHeader]"></ng-content>
        <cds-icon
          *ngIf="this.icon == 'Right'"
          [ngClass]="{ 'icon-expanded': isOpen }"
          class="icon-Right"
          name="02-down-default"
          type="2021"
          altText="Icon"
          ariaLabel="Icon"
          aria-hidden="true"
        >
        </cds-icon>
      </div>
    </button>

    <!-- expandable area-->
    <div class="cds-accordionSubMenu" [attr.id]="idStr">
      <div class="accordion-row">
        <ng-content></ng-content>
      </div>
    </div>
  `, styles: [".cds-category{border:none;padding:0;text-align:left;width:100%;min-height:55px;white-space:nowrap;background-color:var(--cds-accordion-category-bg-color, #fff)}.cds-accordionSubMenu{display:none;width:100%;margin:auto;max-height:0;overflow:hidden;transition:max-height .15s ease-out,background-color .5s ease-in-out,visibility 1ms ease .501s;border-bottom:1px solid #dcdcdc;background-color:var(--cds-accordion-submenu-bg-color, #eee)}:host.cds-is-expanded .cds-category{background-color:var(--cds-accordion-expanded-category-bg-color, #eee)}:host.cds-is-expanded>.cds-accordionSubMenu{display:block;max-height:300em;transition:max-height .25s ease-in,background-color .5s ease-in-out 2ms}.cds-category:hover{cursor:pointer}.cds-category ::ng-deep .cds-hold-still{position:relative}:host.cdsDisabled{pointer-events:none;opacity:.9}:host button{outline:none}:host button:focus{outline:2px solid #056dae}:host cds-icon{--cds-icon-path-fill: var(--cds-accordion-icon-color, #056dae);margin:5px}:host cds-icon.icon-expanded ::ng-deep path{transform:rotate(180deg);transform-origin:center}:host cds-icon.icon-Right{float:right}:host cds-icon.icon-Right ::ng-deep .sfi.medium{height:8px;top:5px;outline:none}:host cds-icon ::ng-deep .sfi.b1-02-down{display:inline-block;position:relative;transition:transform .4s ease-in-out;transform:rotate(0);transform-origin:50%}:host cds-icon ::ng-deep .sfi.medium{height:8px;top:1px;outline:none}:host .accordion-inner{display:flex;flex-wrap:nowrap;align-items:center}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { disabled: [{
                type: Input
            }], idStr: [{
                type: Input
            }], icon: [{
                type: Input
            }], sectionClicked: [{
                type: Output
            }], sectionFocused: [{
                type: Output
            }], isOpen: [{
                type: Input
            }], isOpenChange: [{
                type: Output
            }], button: [{
                type: ViewChild,
                args: ['button']
            }], cssClassParent: [{
                type: HostBinding,
                args: ['class.cds-parent']
            }], cssClassLevel1: [{
                type: HostBinding,
                args: ['class.cds-level1']
            }], attributeRole: [{
                type: HostBinding,
                args: ['attr.role']
            }], isExpanded: [{
                type: HostBinding,
                args: ['class.cds-is-expanded']
            }] } });

/**
 * @Component: Accordion
 * @description This component generates the wrapper, title, and expand/collapse
 * button for accordions. Sections for the accordion are a separate component.
 */
class AccordionComponent {
    get noSectionsOpen() {
        if (this.sections) {
            return this.sections.find(section => section.isOpen) === undefined;
        }
    }
    onKeyPressHome(event) {
        event.preventDefault();
        this.sections.first.focusButton();
    }
    onKeyPressEnd(event) {
        event.preventDefault();
        this.sections.last.focusButton();
    }
    // #region // ---------- LIFECYCLE ---------- //
    constructor(announcer) {
        this.announcer = announcer;
        // #region // ---------- Properties ---------- //
        /**
         * @property allSectionsOpen
         * @description Whether all sections are opened/expanded
         */
        this.allSectionsOpen = true;
        // #endregion
        // #region // -------- Inputs ---------- //
        /**
         * @property multi
         * @description whether mulitple section are permitted to be open at the same time
         */
        this.multi = true;
        /**
         * @property expandAllButtonText
         * @description Button text displayed when all items are collapsed
         * @return string
         */
        this.closeOthersText = 'Other Sections Closed';
        /**
         * @property displayExpandToggle
         * @description Show or hide the expand all / collapse all button
         * @return boolean
         */
        this.displayExpandToggle = false;
        /**
         * @property collapseAllText
         * @description Button text displayed when all sections expanded
         * @return string
         */
        this.collapseAllText = 'Collapse All';
        /**
        * @property expandAllText
        * @description Button text displayed when all sections can be expandeds
        * @return string
        */
        this.expandAllText = 'Expand All';
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Accordion <citi-accordion> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    ngAfterContentInit() {
        this.sections.forEach((section, index) => (section.sectionIndex = index));
        this.checkAllSections();
        this.sections.forEach(section => {
            // This isOpenChange subscribe is key for managing allSectionsOpen
            section.isOpenChange.subscribe(isOpen => {
                this.checkAllSections();
            });
            section.sectionClicked.subscribe(sectionIndex => {
                this.listenForClicks(sectionIndex);
            });
        });
    }
    // #endregion
    // #region // ------------ Methods ------------ //
    /**
     * @Method: listenForClicks
     * @description Used in sectionClicked subscription in life cycle hook above.
     * Listen for clicks to close other sections when multi not active
     * @return Void
     */
    listenForClicks(index) {
        if (!this.multi)
            this.setAllSection(index);
    }
    /**
     * @Method: checkAllSections
     * @description Used in sectionClicked subscription in life cycle hook above.
     * Listen for clicks to close other sections when multi not active
     */
    checkAllSections() {
        if (this.sections.toArray().every(s => s.isOpen))
            this.allSectionsOpen = true;
        if (this.sections.some(section => !section.isOpen))
            this.allSectionsOpen = false;
    }
    /**
     * @Method: setAllSection
     * @description closes all other sections except for excluded (newly opened section)
     * @return Void
     */
    setAllSection(excludeIndex) {
        this.sections.forEach(section => {
            if (section.sectionIndex !== excludeIndex && section.isOpen) {
                section.isOpen = false;
                // prevents collision with "expand"/"collapse" announcements from the sections
                // setTimeout(() => this.announcer.announce(this.closeOthersText), 101);
                // NOTE: this seems to override the closeOthersText announcement above
                // but I think this would be the prefered announcment.
                let sectionNumber = section.sectionIndex + 1;
                setTimeout(() => this.announcer.announce('Section ' + sectionNumber + ' collapsed'), 104);
            }
        });
    }
    /**
     * @Method: toggleAllSections
     * @description Opens/closes all sections
     * @return Void
     */
    toggleAllSections() {
        this.allSectionsOpen = !this.allSectionsOpen;
        this.sections.forEach(section => (section.isOpen = this.allSectionsOpen));
        this.announcer.announce(this.allSectionsOpen ? 'All sections expanded' : 'All sections collapsed');
    }
    /**
     * @Method: expandAllSections
     * @description Opens all sections
     * @return Void
     */
    expandAllSections() {
        this.sections.forEach(section => { section.isOpen = true; });
        this.allSectionsOpen = true;
    }
    /**
     * @Method: collapseAllSections
     * @description Closes all sections
     * @return Void
     */
    collapseAllSections() {
        this.sections.forEach(section => { section.isOpen = false; });
        this.allSectionsOpen = false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AccordionComponent, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: AccordionComponent, selector: "cds-accordion,.cds-accordion", inputs: { multi: "multi", closeOthersText: "closeOthersText", displayExpandToggle: "displayExpandToggle", collapseAllText: "collapseAllText", expandAllText: "expandAllText", accordionRole: "accordionRole" }, host: { listeners: { "keydown.home": "onKeyPressHome($event)", "keydown.end": "onKeyPressEnd($event)" }, classAttribute: "cds-accordion" }, queries: [{ propertyName: "sections", predicate: i0.forwardRef(() => AccordionSectionComponent) }], ngImport: i0, template: `
    <div>
      <button
        class="cds-expand-toggle"
        *ngIf="displayExpandToggle"
        [attr.aria-expanded]="allSectionsOpen"
        (click)="allSectionsOpen ? collapseAllSections() : expandAllSections()">
        {{ allSectionsOpen ? collapseAllText : expandAllText }}
      </button>

      <div class="accordion menu form linear">
        <div class="cds-title" [attr.role]="accordionRole">
            <ng-content></ng-content>
        </div>
      </div>
    </div>
  `, isInline: true, styles: [":host .cds-expand-toggle{float:right;background:none;border:none;color:#056dae;font-size:16px;font-weight:300;letter-spacing:0;line-height:24px;margin-bottom:18px;border-radius:6px}:host .cds-expand-toggle:focus{outline:2px solid #056dae}:host .cds-expand-toggle:hover{color:#002256}:host .accordion.menu .cds-title{margin-bottom:4px}:host ::ng-deep div[cdsaccordionheader]{display:inline-block;margin:5px;white-space:normal}:host ::ng-deep fieldset.dark{margin-bottom:0}:host ::ng-deep .cds-hide-toggle-button{display:none}:host ::ng-deep .cds-hidden{display:none}:host ::ng-deep .cds-accordion-module fieldset{width:100%;position:relative}:host ::ng-deep .cds-accordion-module{padding:10px 20px!important;margin-top:30px auto!important}:host ::ng-deep .accordion ul.cds-mainnav{list-style:none;margin:0;padding:0;width:100%;clear:both}:host ::ng-deep .accordion ul.cds-subnav{list-style:none;margin:0;padding:5px;width:100%;clear:both}:host ::ng-deep .cds-accordion-title{line-height:3rem}:host ::ng-deep .toggle-accordion{margin-left:15px}:host.cds-accordion-light{background-color:#fff}:host.cds-accordion-light ::ng-deep .cds-category{color:#333;border-bottom:1px solid #cecece;padding-left:21px;padding-right:21px;display:block;font-size:16px;line-height:22px}:host.cds-accordion-light ::ng-deep .cds-is-expanded .cds-category{background-color:#f1f1f1!important;border-bottom:none;border-top:none}:host.cds-accordion-light ::ng-deep .cds-accordionSubMenu{padding:0 20px 40px 56px;color:#333;background-color:#f1f1f1!important}:host.cds-accordion-light ::ng-deep .toggle-accordion{outline:none;background:none;margin-left:15px;border:none;color:gray}:host.cds-accordion-light ::ng-deep #toggleIcon{color:#056dae;font-size:16px;font-weight:700}:host.cds-accordion-dark ::ng-deep .cds-category{color:#333;background-color:#f1f1f1;border-bottom:1px solid #a8a8a8;padding-left:21px;padding-right:21px;display:block;font-size:16px;line-height:22px}:host.cds-accordion-dark ::ng-deep .cds-is-expanded .cds-category{background-color:#f9f9f9!important;border-bottom:none}:host.cds-accordion-dark ::ng-deep .cds-accordionSubMenu{padding:0 20px 40px 56px;color:#333;background-color:#f9f9f9!important}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: AccordionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-accordion,.cds-accordion', host: { class: 'cds-accordion' }, template: `
    <div>
      <button
        class="cds-expand-toggle"
        *ngIf="displayExpandToggle"
        [attr.aria-expanded]="allSectionsOpen"
        (click)="allSectionsOpen ? collapseAllSections() : expandAllSections()">
        {{ allSectionsOpen ? collapseAllText : expandAllText }}
      </button>

      <div class="accordion menu form linear">
        <div class="cds-title" [attr.role]="accordionRole">
            <ng-content></ng-content>
        </div>
      </div>
    </div>
  `, styles: [":host .cds-expand-toggle{float:right;background:none;border:none;color:#056dae;font-size:16px;font-weight:300;letter-spacing:0;line-height:24px;margin-bottom:18px;border-radius:6px}:host .cds-expand-toggle:focus{outline:2px solid #056dae}:host .cds-expand-toggle:hover{color:#002256}:host .accordion.menu .cds-title{margin-bottom:4px}:host ::ng-deep div[cdsaccordionheader]{display:inline-block;margin:5px;white-space:normal}:host ::ng-deep fieldset.dark{margin-bottom:0}:host ::ng-deep .cds-hide-toggle-button{display:none}:host ::ng-deep .cds-hidden{display:none}:host ::ng-deep .cds-accordion-module fieldset{width:100%;position:relative}:host ::ng-deep .cds-accordion-module{padding:10px 20px!important;margin-top:30px auto!important}:host ::ng-deep .accordion ul.cds-mainnav{list-style:none;margin:0;padding:0;width:100%;clear:both}:host ::ng-deep .accordion ul.cds-subnav{list-style:none;margin:0;padding:5px;width:100%;clear:both}:host ::ng-deep .cds-accordion-title{line-height:3rem}:host ::ng-deep .toggle-accordion{margin-left:15px}:host.cds-accordion-light{background-color:#fff}:host.cds-accordion-light ::ng-deep .cds-category{color:#333;border-bottom:1px solid #cecece;padding-left:21px;padding-right:21px;display:block;font-size:16px;line-height:22px}:host.cds-accordion-light ::ng-deep .cds-is-expanded .cds-category{background-color:#f1f1f1!important;border-bottom:none;border-top:none}:host.cds-accordion-light ::ng-deep .cds-accordionSubMenu{padding:0 20px 40px 56px;color:#333;background-color:#f1f1f1!important}:host.cds-accordion-light ::ng-deep .toggle-accordion{outline:none;background:none;margin-left:15px;border:none;color:gray}:host.cds-accordion-light ::ng-deep #toggleIcon{color:#056dae;font-size:16px;font-weight:700}:host.cds-accordion-dark ::ng-deep .cds-category{color:#333;background-color:#f1f1f1;border-bottom:1px solid #a8a8a8;padding-left:21px;padding-right:21px;display:block;font-size:16px;line-height:22px}:host.cds-accordion-dark ::ng-deep .cds-is-expanded .cds-category{background-color:#f9f9f9!important;border-bottom:none}:host.cds-accordion-dark ::ng-deep .cds-accordionSubMenu{padding:0 20px 40px 56px;color:#333;background-color:#f9f9f9!important}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { multi: [{
                type: Input
            }], closeOthersText: [{
                type: Input
            }], displayExpandToggle: [{
                type: Input
            }], collapseAllText: [{
                type: Input
            }], expandAllText: [{
                type: Input
            }], accordionRole: [{
                type: Input
            }], sections: [{
                type: ContentChildren,
                args: [forwardRef(() => AccordionSectionComponent)]
            }], onKeyPressHome: [{
                type: HostListener,
                args: ['keydown.home', ['$event']]
            }], onKeyPressEnd: [{
                type: HostListener,
                args: ['keydown.end', ['$event']]
            }] } });

class CdsIconWrapper {
    constructor() {
        this.flexDirection = 'row';
        this.padding = '0px';
        this.alignment = 'center';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconWrapper, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsIconWrapper, selector: "cds-icon-wrapper, [cds-icon-wrapper]", inputs: { flexDirection: "flexDirection", padding: "padding", alignment: "alignment" }, host: { classAttribute: "cds-icon-wrapper" }, ngImport: i0, template: `
    <div
      class="flex-wrapper wrapper-text"
      [ngStyle]="{
        'flex-direction': flexDirection,
        padding: padding,
        'align-items': alignment
      }"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, styles: [".flex-wrapper{display:inline-flex;flex-wrap:wrap;position:relative}.wrapper-text{font-size:16px}\n"], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconWrapper, decorators: [{
            type: Component,
            args: [{ selector: 'cds-icon-wrapper, [cds-icon-wrapper]', host: { class: 'cds-icon-wrapper' }, template: `
    <div
      class="flex-wrapper wrapper-text"
      [ngStyle]="{
        'flex-direction': flexDirection,
        padding: padding,
        'align-items': alignment
      }"
    >
      <ng-content></ng-content>
    </div>
  `, styles: [".flex-wrapper{display:inline-flex;flex-wrap:wrap;position:relative}.wrapper-text{font-size:16px}\n"] }]
        }], propDecorators: { flexDirection: [{
                type: Input
            }], padding: [{
                type: Input
            }], alignment: [{
                type: Input
            }] } });

class CdsIconModule {
    static forRoot() {
        return {
            ngModule: CdsIconModule,
            providers: [ICON_REGISTRY_PROVIDER, HttpClientModule]
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsIconModule, declarations: [CdsIconWrapper, CdsIcon], imports: [CommonModule, HttpClientModule], exports: [CdsIconWrapper, CdsIcon] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconModule, imports: [CommonModule, HttpClientModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsIconModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsIconWrapper, CdsIcon],
                    imports: [CommonModule, HttpClientModule,
                        // BrowserTransferStateModule
                    ],
                    exports: [CdsIconWrapper, CdsIcon]
                }]
        }] });

// import { StripHtml } from '../../core/pipes/strip-html/strip-html.module';
// import { CdsIconModule } from '@citi-gcg-173875/common-ui-cds';
const COMPONENTS$S = [AccordionComponent, AccordionSectionComponent];
const MODULES$A = [CommonModule, CdsIconModule];
class Accordion {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: Accordion, declarations: [AccordionComponent, AccordionSectionComponent], imports: [CommonModule, CdsIconModule], exports: [AccordionComponent, AccordionSectionComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion, imports: [MODULES$A] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$A],
                    declarations: [...COMPONENTS$S],
                    exports: [...COMPONENTS$S]
                }]
        }] });

class CdsFocusTrap {
    constructor(focusTrapFactory, elementRef, platformId, _document) {
        this.focusTrapFactory = focusTrapFactory;
        this.elementRef = elementRef;
        this.platformId = platformId;
        this._document = _document;
        this.saveFocusedElement();
    }
    ngOnInit() {
        this.focusTrap = this.focusTrapFactory.create(this.elementRef.nativeElement);
        this.focusTrap.focusInitialElementWhenReady();
    }
    ngOnDestroy() {
        this.destroy();
    }
    destroy() {
        this.focusTrap.destroy();
        this.restoreFocus();
    }
    restoreFocus() {
        if (isPlatformBrowser(this.platformId)) {
            this.previouslyFocusedElement.focus();
        }
    }
    saveFocusedElement() {
        if (isPlatformBrowser(this.platformId)) {
            this.previouslyFocusedElement = this._document.activeElement;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFocusTrap, deps: [{ token: i1$1.ConfigurableFocusTrapFactory }, { token: i0.ElementRef }, { token: PLATFORM_ID }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsFocusTrap, selector: "[cdsFocusTrap]", exportAs: ["cdsFocusTrap"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFocusTrap, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsFocusTrap]',
                    exportAs: 'cdsFocusTrap'
                }]
        }], ctorParameters: () => [{ type: i1$1.ConfigurableFocusTrapFactory }, { type: i0.ElementRef }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class CdsFocusTrapModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFocusTrapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFocusTrapModule, declarations: [CdsFocusTrap], imports: [CommonModule], exports: [CdsFocusTrap] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFocusTrapModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFocusTrapModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsFocusTrap],
                    imports: [CommonModule],
                    exports: [CdsFocusTrap]
                }]
        }] });

/**
 * Gets an array of predecessor elements until you reach a specific element
 * @param node pass in the node to find its predecessors
 * @param [selector] limits the array of predecessors to the closest element matching the selector
 * @returns array of predecessor elements of the provided node, until it reaches the element matching the selector
 */
function getElementParentsUntil(node, selector) {
    const parentElements = [];
    let parent = node.parentElement;
    while (parent) {
        if (selector &&
            parent?.closest &&
            parent?.closest(selector) === parent) {
            break;
        }
        if (parent.nodeType === 1) {
            parentElements.unshift(parent);
        }
        parent = parent.parentElement;
    }
    return parentElements;
}
/**
 * Gets an array of all predecessor elements
 * @param node pass in the node to find its predecessors
 * @returns array of predecessor elements of the provided node
 */
function getElementParents(node) {
    return getElementParentsUntil(node);
}

/**
 * Gets element children
 * @param parentNode pass in the node to get it's children
 * @param [skipChildNodes] the child node (or array of node children) you want to skip (optional)
 * @returns an array of the child elements of the supplied node
 */
function getElementChildren(parentNode, skipChildNodes) {
    const returnChildren = [];
    if (!parentNode) {
        return returnChildren;
    }
    let child = parentNode.firstChild;
    while (child) {
        const isSkipped = Array.isArray(skipChildNodes)
            ? skipChildNodes.includes(child)
            : skipChildNodes === child;
        // check for Element type and if skipped
        if (child.nodeType === 1 && !isSkipped) {
            returnChildren.push(child);
        }
        child = child.nextSibling;
    }
    return returnChildren;
}

/**
 * Gets element siblings
 * @param node pass in the node to find it's siblings
 * @returns an array of the sibling elements
 */
function getElementSiblings(node) {
    return getElementChildren(node.parentNode, [node]);
}

class CdsAriaHideOuterDomDirective {
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(el, _renderer) {
        this.el = el;
        this._renderer = _renderer;
        // #region ========== Properties ========== //
        this._parentalSiblings = [];
        /**
         * Array of CSS selectors that should be excluded from having aria-hidden applied
         * @default '.cdk-live-announcer-element' live announcer should not be hidden from screen readers
         */
        this.outerDomExceptions = ['.cdk-live-announcer-element'];
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            if (simpleChanges.hasOwnProperty(propName)) {
                const change = simpleChanges[propName];
                if (propName === 'isOuterDomAriaHidden' && !change.isFirstChange()) {
                    if (this.isOuterDomAriaHidden) {
                        this._ariaHideParentalSiblings();
                    }
                    else {
                        this._ariaRestoreParentalSiblings();
                    }
                }
            }
        }
    }
    ngOnDestroy() {
        this._ariaRestoreParentalSiblings();
    }
    // #endregion
    // #region ========== Public Methods ========== //
    show() {
        this._ariaRestoreParentalSiblings();
    }
    hide() {
        this._ariaHideParentalSiblings();
    }
    // #endregion
    // #region ========== Private Methods ========== //
    _ariaHideParentalSiblings() {
        // This method should not be run again when _parentalSiblings array is not empty.
        // However, in case it is, we need to clear it first before running again.
        if (this._parentalSiblings.length) {
            this._ariaRestoreParentalSiblings();
        }
        getElementSiblings(this.el.nativeElement).forEach(sibling => {
            if (!this._isElementExcluded(sibling)) {
                this._applyAriaHidden(sibling);
            }
        });
        getElementParentsUntil(this.el.nativeElement, 'body').forEach(parent => {
            getElementSiblings(parent).forEach(sibling => {
                if (!this._isElementExcluded(sibling)) {
                    this._applyAriaHidden(sibling);
                }
            });
        });
    }
    _applyAriaHidden(el) {
        const currentAriaHiddenAttribute = el.getAttribute('aria-hidden');
        // first store element's attribute value for aria-hidden
        this._parentalSiblings.push({
            element: el,
            attributeName: 'aria-hidden',
            previousAttributeValue: currentAriaHiddenAttribute
        });
        // now set aria-hidden to false
        this._renderer.setAttribute(el, 'aria-hidden', 'true');
    }
    _ariaRestoreParentalSiblings() {
        this._parentalSiblings.forEach(parentSibling => {
            if (parentSibling.previousAttributeValue) {
                this._renderer.setAttribute(parentSibling.element, parentSibling.attributeName, parentSibling.previousAttributeValue);
            }
            else {
                this._renderer.removeAttribute(parentSibling.element, parentSibling.attributeName);
            }
        });
        // after resetting values, clear array
        this._parentalSiblings.length = 0;
    }
    _isElementExcluded(el) {
        return el.matches(this.outerDomExceptions.join(','));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAriaHideOuterDomDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsAriaHideOuterDomDirective, selector: "[cdsAriaHideOuterDom]", inputs: { isOuterDomAriaHidden: "isOuterDomAriaHidden", outerDomExceptions: "outerDomExceptions" }, exportAs: ["cdsAriaHideOuterDom"], usesOnChanges: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAriaHideOuterDomDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsAriaHideOuterDom]',
                    exportAs: 'cdsAriaHideOuterDom'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { isOuterDomAriaHidden: [{
                type: Input
            }], outerDomExceptions: [{
                type: Input
            }] } });

class CdsAriaHideOuterDomModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAriaHideOuterDomModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsAriaHideOuterDomModule, declarations: [CdsAriaHideOuterDomDirective], imports: [CommonModule], exports: [CdsAriaHideOuterDomDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAriaHideOuterDomModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAriaHideOuterDomModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsAriaHideOuterDomDirective],
                    imports: [CommonModule],
                    exports: [CdsAriaHideOuterDomDirective]
                }]
        }] });

/**
 * @description the idea is to only console in dev mode to get a
 * developers attention but not in production
 */
const devError = (message, prefix = 'Citi') => {
    if (isDevMode())
        console.error(`[${prefix}] ${message}`);
};

/**
 * @description the idea is to only kill in dev mode to get a developers attention
 * but allow the program to run (with a defect) in production
 */
const devKill = (message, prefix = 'Citi') => {
    if (isDevMode())
        throw new Error(`[${prefix}] ${message}`);
};

/**
 * @description the idea is to only console in dev mode to get a
 * developers attention but not in production
 */
const devWarn = (message, prefix = 'Citi') => {
    if (isDevMode())
        console.warn(`[${prefix}] ${message}`);
};

/**
 * This method is like `findIndex` except that it iterates over elements
 * of `collection` from right to left.
 *
 * @param array The array to inspect.
 * @param predicate The function invoked per iteration.
 * @returns the index of the found element, else `-1`.
 * @example
 *
 * const users = [
 *   { 'user': 'barney',  'active': true },
 *   { 'user': 'pebbles', 'active': true }
 *   { 'user': 'fred',    'active': false },
 *   { 'user': 'pebbles', 'active': false }
 * ]
 *
 * findLastIndex(users, ({ user }) => user == 'pebbles')  => 3
 */
function findLastIndex(array, predicate) {
    const length = array == null ? 0 : array.length;
    if (!length)
        return -1;
    return baseFindIndex(array, predicate, length - 1, true);
}
function baseFindIndex(array, predicate, fromIndex = 0, fromRight = false) {
    const { length } = array;
    let index = fromIndex + (fromRight ? 1 : -1);
    while (fromRight ? index-- : ++index < length) {
        if (predicate(array[index]))
            return index;
    }
    return -1;
}

function getFocableAbleElements(root) {
    return root.querySelectorAll(`
    a[href]:not([tabindex='-1']),
    area[href]:not([tabindex='-1']),
    input:not([disabled]):not([tabindex='-1']),
    select:not([disabled]):not([tabindex='-1']),
    textarea:not([disabled]):not([tabindex='-1']),
    button:not([disabled]):not([tabindex='-1']),
    iframe:not([tabindex='-1']),
    [tabindex]:not([tabindex='-1']),
    [contentEditable=true]:not([tabindex='-1']
  `);
}

/**
 * Gets random string
 * @param [length] length of string you want returned (default: 8)
 * @param [fromChars] string of characters to choose from (default: 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890')
 * @returns random string
 */
function getRandomString(length, fromChars) {
    length = length && length < 1000 && length > 0 ? length : 8;
    fromChars =
        fromChars && fromChars.length < 1000
            ? fromChars
            : 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890';
    const fromCharsLength = fromChars.length;
    let randomId = '';
    let i = 0;
    while (i < length) {
        const random = Math.floor(Math.random() * fromCharsLength);
        randomId += fromChars.charAt(random);
        i++;
    }
    return randomId;
}

/**
 * Round a number to a specified precision
 * @param float The number you want to round
 * @param precision The precision to round to
 * @returns The rounded float number
 */
function getRoundedFloat(float, precision = 2) {
    const precisionDiv = Math.pow(10, precision);
    return Math.round(float * precisionDiv) / precisionDiv;
}

/**
 * Gets scroll parents
 * @param node pass in the node to get it's parents that are scrollable
 * @returns array of elements that are scrollable
 */
function getScrollParents(node) {
    const scrollParents = [];
    let parent = node.parentElement;
    while (parent) {
        if (parent.nodeType === 1 && parent.scrollHeight > parent.clientHeight) {
            scrollParents.unshift(parent);
        }
        parent = parent.parentElement;
    }
    return scrollParents;
}

/**
 * Gets scrollbar width
 * @param doc Document
 * @returns scrollbar width
 */
function getScrollbarWidth(doc) {
    if (!doc) {
        return 0;
    }
    // Creating invisible container
    const outer = doc.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar to appear
    outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
    doc.body.appendChild(outer);
    // Creating inner element and placing it in the container
    const inner = doc.createElement('div');
    outer.appendChild(inner);
    // Calculating difference between container's full width and the child width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);
    return scrollbarWidth;
}

/**
 * Determines whether date is valid
 * @param date value to be checked
 * @returns boolean
 */
function isDate(date) {
    return date instanceof Date && !isNaN(date);
}

// TODO move into citi-utils
/**
 * @description Waits until the microtask queue is empty and then executes code. Less hacky than setTimout and doesn't force another digest
 * @example
 *   onStable(this.ngZone, () => this.input.nativeElement.focus())
 */
function onStable(ngZone, func) {
    ngZone.runOutsideAngular(() => {
        ngZone.onStable
            .asObservable()
            .pipe(first())
            .subscribe(() => func());
    });
}

function toRawType(value) {
    const _toString = Object.prototype.toString;
    const stringified = _toString.call(value);
    return stringified.slice ? stringified.slice(8, -1) : 'Undefined';
}

class CdsFullTextMaskPipe {
    transform(value, hider) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        return value.replace(/.*/g, txt => {
            return hider.repeat(txt.length);
        });
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipe, name: "cdsFullTextMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsFullTextMask' }]
        }] });

class CdsFullTextMaskPipeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipeModule, declarations: [CdsFullTextMaskPipe], imports: [CommonModule], exports: [CdsFullTextMaskPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipeModule, providers: [CdsFullTextMaskPipe], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullTextMaskPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdsFullTextMaskPipe],
                    exports: [CdsFullTextMaskPipe],
                    providers: [CdsFullTextMaskPipe]
                }]
        }] });

class CdsPartialEmailMaskPipe {
    transform(value, hider) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        const splitIndex = value.indexOf('@') === -1 ? 0 : value.indexOf('@');
        const preAt = value.slice(0, splitIndex);
        const preAtMasked = preAt.replace(/.*/, txt => {
            const firstChar = txt.slice(0, 1);
            const txtToMask = txt.slice(1, txt.length - 1);
            const maskedTxt = hider.repeat(txtToMask.length);
            const lastChar = txt.length > 1 ? txt.slice(txt.length - 1) : '';
            return `${firstChar}${maskedTxt}${lastChar}`;
        });
        const postAt = value.slice(splitIndex, value.length);
        return `${preAtMasked}${postAt}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipe, name: "cdsPartialEmailMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsPartialEmailMask' }]
        }] });

class CdsPartialEmailMaskPipeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipeModule, declarations: [CdsPartialEmailMaskPipe], imports: [CommonModule], exports: [CdsPartialEmailMaskPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipeModule, providers: [CdsPartialEmailMaskPipe], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialEmailMaskPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdsPartialEmailMaskPipe],
                    exports: [CdsPartialEmailMaskPipe],
                    providers: [CdsPartialEmailMaskPipe]
                }]
        }] });

class CdsPartialTextMaskPipe {
    transform(value, postMaskLength, hider, preMaskLength) {
        if (!value)
            return value;
        if (!postMaskLength && postMaskLength !== 0)
            postMaskLength = 4;
        if (!hider)
            hider = '•';
        if (!preMaskLength)
            preMaskLength = 0;
        // unmasked length cannot be less than 0 or more than the value length
        preMaskLength = Math.max(Math.min(preMaskLength, value.length), 0);
        postMaskLength = Math.max(Math.min(postMaskLength, value.length - preMaskLength), 0);
        const preMaskText = value.slice(0, preMaskLength);
        const maskedText = value
            .slice(preMaskLength, value.length - postMaskLength)
            .replace(/.*/g, (txt) => {
            return hider.repeat(txt.length);
        });
        const postMaskText = value.slice(value.length - postMaskLength);
        return `${preMaskText}${maskedText}${postMaskText}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipe, name: "cdsPartialTextMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsPartialTextMask' }]
        }] });

class CdsPartialTextMaskPipeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipeModule, declarations: [CdsPartialTextMaskPipe], imports: [CommonModule], exports: [CdsPartialTextMaskPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipeModule, providers: [CdsPartialTextMaskPipe], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialTextMaskPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdsPartialTextMaskPipe],
                    exports: [CdsPartialTextMaskPipe],
                    providers: [CdsPartialTextMaskPipe]
                }]
        }] });

class CdsMediumDateMaskPipe {
    transform(value, hider, hide) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        let newDate = new Date(value);
        if (!hide)
            return hider.repeat(value.length);
        // month
        if (hide.includes('m'))
            value = hider.repeat(3) + value.substr(3);
        // day
        let day = newDate.getDate();
        if (hide.includes('d')) {
            value = value.replace(day.toString(), hider.repeat(day.toString().length));
        }
        // year
        let year = newDate.getFullYear();
        if (hide.includes('y'))
            value = value.replace(year.toString(), hider.repeat(year.toString().length));
        return value;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMediumDateMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsMediumDateMaskPipe, name: "cdsMediumDateMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMediumDateMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsMediumDateMask' }]
        }] });

class CdsShortDateMaskPipe {
    transform(value, hider, hide, format, reveal) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        let newDate = new Date(value);
        let separator;
        if (!hide)
            return hider.repeat(value.length);
        // month
        let month = (newDate.getMonth() + 1).toString();
        if (hide.includes('m') && !reveal) {
            month = hider.repeat(month.toString().length);
        }
        // day
        let day = newDate.getDate().toString();
        if (hide.includes('d') && !reveal) {
            day = hider.repeat(day.toString().length);
        }
        // year
        let year = newDate.getFullYear().toString();
        if (hide.includes('y') && !reveal) {
            year = hider.repeat(year.toString().length);
        }
        separator = format.includes("/") ? "/" : format.includes("-") ? "-" : separator;
        if (format.charAt(0).toLowerCase() === 'd') {
            return `${day}${separator}${month}${separator}${year}`;
        }
        return `${month}${separator}${day}${separator}${year}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsShortDateMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsShortDateMaskPipe, name: "cdsShortDateMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsShortDateMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsShortDateMask' }]
        }] });

class CdsShortMonthMaskPipe {
    transform(value, hider, hide, format, reveal) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        let newDate = new Date(value);
        let separator;
        if (!hide)
            return hider.repeat(value.length);
        // month
        let month = (newDate.getMonth() + 1).toString();
        if (hide.includes('m') && !reveal) {
            month = hider.repeat(month.toString().length);
        }
        // year
        let year = newDate.getFullYear().toString();
        if (hide.includes('y') && !reveal) {
            year = hider.repeat(year.toString().length);
        }
        separator = format.includes("/") ? "/" : format.includes("-") ? "-" : separator;
        return `${month}${separator}${year}`;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsShortMonthMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsShortMonthMaskPipe, name: "cdsShortMonthMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsShortMonthMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsShortMonthMask' }]
        }] });

const MONTH_LIST$1 = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
class CdsLongDateMaskPipe {
    transform(value, hider, hide) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        let newDate = new Date(value);
        if (!hide)
            return hider.repeat(value.length);
        // month
        if (hide.includes('m')) {
            let month = newDate.getMonth();
            let month_name = MONTH_LIST$1[month];
            value = value = value.replace(month_name.toString(), hider.repeat(month_name.toString().length));
        }
        // day
        let day = newDate.getDate();
        if (hide.includes('d')) {
            value = value.replace(day.toString(), hider.repeat(day.toString().length));
        }
        // year
        let year = newDate.getFullYear();
        if (hide.includes('y'))
            value = value.replace(year.toString(), hider.repeat(year.toString().length));
        return value;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLongDateMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsLongDateMaskPipe, name: "cdsLongDateMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLongDateMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsLongDateMask' }]
        }] });

const MONTH_LIST = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_LIST = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Satureday'];
class CdsFullDateMaskPipe {
    transform(value, hider, hide) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        let newDate = new Date(value);
        if (!hide)
            return hider.repeat(value.length);
        // month
        if (hide.includes('m')) {
            let month = newDate.getMonth();
            let month_name = MONTH_LIST[month];
            value = value = value.replace(month_name.toString(), hider.repeat(month_name.toString().length));
        }
        // date
        let date = newDate.getDate();
        if (hide.includes('d')) {
            value = value = value.replace(date.toString(), hider.repeat(date.toString().length));
        }
        // day
        if (hide.includes('d')) {
            let day = newDate.getDay();
            let day_name = DAY_LIST[day];
            value = value = value.replace(day_name.toString(), hider.repeat(day_name.toString().length));
        }
        // year
        let year = newDate.getFullYear();
        if (hide.includes('y'))
            value = value.replace(year.toString(), hider.repeat(year.toString().length));
        return value;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullDateMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFullDateMaskPipe, name: "cdsFullDateMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFullDateMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsFullDateMask' }]
        }] });

class CdsMonthYearDateMaskPipe {
    transform(value, hider, hide) {
        if (!value)
            return value;
        if (!hider)
            hider = '•';
        let newDate = new Date(value);
        if (!hide)
            return hider.repeat(value.length);
        // month
        let month = newDate.getMonth() + 1;
        if (hide.includes('m'))
            value = value.replace(month.toString(), hider.repeat(month.toString().length));
        // day
        let day = newDate.getDate();
        if (hide.includes('d')) {
            value = value.replace(day.toString(), hider.repeat(day.toString().length));
        }
        // year
        let year = newDate.getFullYear();
        if (hide.includes('y'))
            value = value.substr(0, (value.length - 2)) + hider.repeat(2);
        return value;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMonthYearDateMaskPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsMonthYearDateMaskPipe, name: "cdsMonthYearDateMask" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMonthYearDateMaskPipe, decorators: [{
            type: Pipe,
            args: [{ name: 'cdsMonthYearDateMask' }]
        }] });

const MODULES$z = [CdsMediumDateMaskPipe, CdsShortDateMaskPipe, CdsLongDateMaskPipe, CdsFullDateMaskPipe, CdsMonthYearDateMaskPipe, CdsShortMonthMaskPipe];
class CdsDateMaskPipeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMaskPipeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMaskPipeModule, declarations: [CdsMediumDateMaskPipe, CdsShortDateMaskPipe, CdsLongDateMaskPipe, CdsFullDateMaskPipe, CdsMonthYearDateMaskPipe, CdsShortMonthMaskPipe], imports: [CommonModule], exports: [CdsMediumDateMaskPipe, CdsShortDateMaskPipe, CdsLongDateMaskPipe, CdsFullDateMaskPipe, CdsMonthYearDateMaskPipe, CdsShortMonthMaskPipe] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMaskPipeModule, providers: [...MODULES$z], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMaskPipeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...MODULES$z],
                    exports: [...MODULES$z],
                    providers: [...MODULES$z]
                }]
        }] });

class CdsWebService {
    get document() {
        return this._document;
    }
    get window() {
        return this._window;
    }
    constructor(_doc) {
        this._doc = _doc;
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsWebService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsWebService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsWebService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class CdsAnnouncerService {
    constructor(
    // private _renderer: Renderer2,
    _rendererFactory, _web) {
        this._rendererFactory = _rendererFactory;
        this._web = _web;
        this._renderer = this._rendererFactory.createRenderer(null, null);
    }
    announce(announceText, duration, ariaLive) {
        announceText = announceText || '';
        duration = duration > 0 ? duration : 200;
        ariaLive = ariaLive || 'polite';
        // Create Elements/Text Node
        const newAnnounceContainer = this._renderer.createElement('div');
        const newAnnounceText = this._renderer.createText(announceText);
        // Update Container
        this._renderer.setAttribute(newAnnounceContainer, 'aria-atomic', 'true');
        this._renderer.setAttribute(newAnnounceContainer, 'aria-live', ariaLive);
        // Styles need to be added here since this is element is added to <body> tag
        // and is therefore outside of the scope of our CDS styles
        this._renderer.setStyle(newAnnounceContainer, 'position', 'absolute');
        this._renderer.setStyle(newAnnounceContainer, 'opacity', '0.01');
        this._renderer.setStyle(newAnnounceContainer, 'width', '1px');
        this._renderer.setStyle(newAnnounceContainer, 'height', '1px');
        this._renderer.setStyle(newAnnounceContainer, 'overflow', 'hidden');
        this._renderer.setStyle(newAnnounceContainer, 'bottom', '0');
        // Add announce container to DOM
        this._renderer.appendChild(this._web.document?.body, newAnnounceContainer);
        setTimeout(() => {
            this._renderer.appendChild(newAnnounceContainer, newAnnounceText);
            setTimeout(() => {
                this._renderer.removeChild(this._web.document?.body, newAnnounceContainer);
            }, duration);
        }, 200);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAnnouncerService, deps: [{ token: i0.RendererFactory2 }, { token: CdsWebService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAnnouncerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAnnouncerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i0.RendererFactory2 }, { type: CdsWebService }] });

const NAVIGATOR = new InjectionToken('NavigatorToken');
/* Define abstract class for obtaining reference to the global Navigator object. */
class NavigatorRef {
    get nativeNavigator() {
        throw new Error('Not implemented.');
    }
}
/* Define class that implements the abstract class and returns the native Navigator object. */
class BrowserNavigatorRef extends NavigatorRef {
    constructor(platformId) {
        super();
        this.platformId = platformId;
    }
    get nativeNavigator() {
        if (isPlatformBrowser(this.platformId)) {
            return navigator;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserNavigatorRef, deps: [{ token: PLATFORM_ID }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserNavigatorRef, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: BrowserNavigatorRef, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }] });
/* Create an factory function that returns the native Navigator object. */
function navigatorFactory(browserNavigatorRef, platformId) {
    if (isPlatformBrowser(platformId)) {
        return browserNavigatorRef.nativeNavigator;
    }
    return null;
}
const browserNavigatorProvider = {
    provide: NavigatorRef,
    useClass: BrowserNavigatorRef
};
/* Create an injectable provider that uses the NavigatorFactory function for returning the native Navigator object. */
const NavigatorProvider = {
    provide: NAVIGATOR,
    useFactory: navigatorFactory,
    deps: [NavigatorRef, PLATFORM_ID]
};
/* Create an array of providers. */
const NAVIGATOR_PROVIDER = [browserNavigatorProvider, NavigatorProvider];

class TaggingDataLayerService {
    get _hasTagMethod() {
        return !!(this._window && this._window.taggingDataLayer);
    }
    // #endregion
    // #region // ========== Constructor ========== //
    constructor(_doc) {
        this._doc = _doc;
        this._document = this._doc;
        this._window = this._document.defaultView;
    }
    // #endregion
    // #region // ========== Methods ========== //
    /**
     * Update events data
     */
    updateEventData(eventData, eventDataFromApp) {
        if (this._hasTagMethod) {
            if (eventDataFromApp) {
                this._window.taggingDataLayer.updateEventData(eventData, eventDataFromApp);
            }
            else {
                this._window.taggingDataLayer.updateEventData(eventData);
            }
        }
    }
    /**
    * Set application data
    */
    applicationData(applicationContent, appSpecificData) {
        if (this._hasTagMethod) {
            if (appSpecificData) {
                this._window.taggingDataLayer.applicationData(applicationContent, appSpecificData);
            }
            else {
                this._window.taggingDataLayer.applicationData(applicationContent);
            }
        }
    }
    /**
    * Tagging Transformation static data from cms promise
    */
    makeCMSCallPromise(customerId) {
        if (this._hasTagMethod) {
            this._window.taggingDataLayer.makeCMSCallPromise(customerId);
        }
    }
    /**
     * Add new specific data to data layer
     */
    addAppData(parentObject, appSpecificData) {
        if (this._hasTagMethod) {
            this._window.taggingDataLayer.addAppData(parentObject, appSpecificData);
        }
    }
    /**
    * Process CMS data
    */
    processCmsData(response, customerId) {
        if (this._hasTagMethod) {
            this._window.taggingDataLayer.processCmsData(response, customerId);
        }
    }
    /**
     * Get CCID if the user is logged in
     */
    getCCIDFromCCSID(ccsid, cb) {
        if (this._hasTagMethod) {
            this._window.taggingDataLayer.getCCIDFromCCSID(ccsid, cb);
        }
    }
    /**
     * Logging tagging errors
     */
    logTaggingError() {
        if (this._hasTagMethod) {
            this._window.taggingDataLayer.logTaggingError();
        }
    }
    getEnvironmentDetails(browserUrl) {
        if (browserUrl) {
            return this._window && this._window.taggingDataLayer && this._window.taggingDataLayer.getEnvironmentDetails(browserUrl);
        }
        return this._window && this._window.taggingDataLayer && this._window.taggingDataLayer.getEnvironmentDetails();
    }
    getErrorFormat() {
        return this._window && this._window.taggingDataLayer && this._window.taggingDataLayer.getErrorFormat();
    }
    setErrorLogs(errorLogs) {
        if (this._hasTagMethod) {
            this._window.taggingDataLayer.setErrorLogs(errorLogs);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TaggingDataLayerService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TaggingDataLayerService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TaggingDataLayerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class CdsThemeService {
    constructor(_doc) {
        this._doc = _doc;
        // themingBlue: string = "cbolui-cds-blue"; // Default
        this.themingGold = "cbolui-cds-gold";
        this.themingCPC = "cbolui-cds-cpc";
        this.themingPriority = "cbolui-cds-priority";
        this.themeChanged = new EventEmitter();
        this._document = _doc;
        this._window = this._document.defaultView;
    }
    initTheme() {
        const theme = this.getThemeFromCustomerType() || this.getThemeFromDom();
        // console.log('CDS THEME: ', theme);
        this.setTheme(theme);
    }
    getThemeFromCustomerType() {
        let spbs = JSON.parse(this._window?.sessionStorage.getItem('sessionPostBrandingStorage'));
        let userBranding = spbs?.userBranding;
        let theme = '';
        if (userBranding === "Gold") {
            theme = this.themingGold;
        }
        else if (userBranding === "CPC") {
            theme = this.themingCPC;
        }
        else if (userBranding === "Priority") {
            theme = this.themingPriority;
        }
        // console.log('customerType theme', theme);
        return theme;
    }
    getThemeFromDom() {
        const themeEl = this._document?.querySelector('.cbolui-cds [class*=cbolui-cds-], .cbolui-cds[class*=cbolui-cds-]');
        const themeClass = themeEl?.classList.value.split(' ').find(cls => cls.includes('cbolui-cds-'));
        // console.log('from dom theme', themeClass);
        return themeClass || '';
    }
    getTheme() {
        return this.theme;
    }
    setTheme(theme) {
        this.theme = theme;
        this.themeChanged.emit(theme);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsThemeService, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsThemeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }] });

class CdsAnnouncerComponent {
    constructor(_renderer, _web) {
        this._renderer = _renderer;
        this._web = _web;
        if (isDevMode()) {
            console.error('%cCdsAnnouncerComponent is deprecated. Please use CdsAnnouncerService instead.', 'background-color: #f00; color: #fff;');
        }
    }
    announce(announceText, duration, ariaLive) {
        announceText = announceText || '';
        duration = duration > 0 ? duration : 200;
        ariaLive = ariaLive || 'polite';
        // Create Elements/Text Node
        const newAnnounceContainer = this._renderer.createElement('div');
        const newAnnounceText = this._renderer.createText(announceText);
        // Update Container
        this._renderer.setAttribute(newAnnounceContainer, 'aria-atomic', 'true');
        this._renderer.setAttribute(newAnnounceContainer, 'aria-live', ariaLive);
        // Styles need to be added here since this is element is added to <body> tag
        // and is therefore outside of the scope of our CDS styles
        this._renderer.setStyle(newAnnounceContainer, 'position', 'absolute');
        this._renderer.setStyle(newAnnounceContainer, 'opacity', '0.01');
        this._renderer.setStyle(newAnnounceContainer, 'width', '1px');
        this._renderer.setStyle(newAnnounceContainer, 'height', '1px');
        this._renderer.setStyle(newAnnounceContainer, 'overflow', 'hidden');
        this._renderer.setStyle(newAnnounceContainer, 'bottom', '0');
        // Add announce container to DOM
        this._renderer.appendChild(this._web.document?.body, newAnnounceContainer);
        setTimeout(() => {
            this._renderer.appendChild(newAnnounceContainer, newAnnounceText);
            setTimeout(() => {
                this._renderer.removeChild(this._web.document?.body, newAnnounceContainer);
            }, duration);
        }, 100);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAnnouncerComponent, deps: [{ token: i0.Renderer2 }, { token: CdsWebService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsAnnouncerComponent, isStandalone: true, selector: "cds-announcer", ngImport: i0, template: ``, isInline: true, styles: [":host{position:absolute;overflow:hidden;border:0;clip:rect(0 0 0 0);width:1px;height:1px;padding:0;margin:-1px;outline:0}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAnnouncerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-announcer', standalone: true, imports: [CommonModule], template: ``, styles: [":host{position:absolute;overflow:hidden;border:0;clip:rect(0 0 0 0);width:1px;height:1px;padding:0;margin:-1px;outline:0}\n"] }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: CdsWebService }] });

class Accordion2SectionComponent {
    set isOpen(isOpen) {
        this.isExpanded = this._isOpen = isOpen;
        this.isOpenChange.emit(this._isOpen);
    }
    get isOpen() {
        return this._isOpen;
    }
    // #endregion
    get isAndroidDevice() {
        if (!navigator || !navigator.userAgent)
            return false;
        else
            return /(android)/i.test(navigator.userAgent);
    }
    get toggleIconName() {
        return this.expandedIconName && this.isOpen
            ? this.expandedIconName
            : this.iconName === '007-plus' && this.isOpen
                ? '008-minus'
                : !!this.iconName ? this.iconName : '114-chevron-down';
    }
    get isLeftIcon() {
        return this.icon.toLowerCase() === 'left';
    }
    constructor(announcer) {
        this.announcer = announcer;
        // #region // -------- Inputs/Outputs ---------- //
        /**
         * disables the click event on the accordian section
         */
        this.disabled = false;
        /**
         * This attribute sets the HTML ID for the expanding content area and the value of the
         * aria-controls attribute
         */
        this.idStr = null;
        /**
         * This attribute sets the value for displaying an icon
         */
        this.icon = 'None';
        /**
         * This attribute sets the value for displaying an icon
         */
        this.iconName = '114-chevron-down';
        this.panelHeaderLevel = 5;
        /**
         * emits when the section button is clicked
         */
        this.sectionClicked = new EventEmitter();
        /**
         * emits when the section button is focused
         */
        this.sectionFocused = new EventEmitter();
        /**
         * Checks if the current accordion section is openend/expanded
         */
        this._isOpen = false;
        this.cssClassParent = false;
        this.cssClassLevel1 = false;
        this.isExpanded = false;
        this.isOpenChange = new EventEmitter();
    }
    toggleSection(sectionIndex) {
        if (!this.disabled) {
            let announceText = `section ${sectionIndex + 1}`;
            if (this.ariaLabel) {
                announceText = this.ariaLabel;
            }
            else if (this.header?.nativeElement?.innerText) {
                announceText = this.header.nativeElement.innerText;
            }
            this.announcer.announce(announceText);
            this.sectionClicked.emit(sectionIndex);
            this.isOpen = !this.isOpen;
            this.isExpanded = this._isOpen = this.isOpen;
            this.isOpenChange.emit(this._isOpen);
        }
    }
    focusButton() {
        this.button.nativeElement.focus();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2SectionComponent, deps: [{ token: CdsAnnouncerService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: Accordion2SectionComponent, selector: "cds-accordion2-section", inputs: { disabled: "disabled", idStr: "idStr", icon: "icon", iconName: "iconName", expandedIconName: "expandedIconName", isIconRotatePrevented: "isIconRotatePrevented", ariaLabel: "ariaLabel", panelHeaderLevel: "panelHeaderLevel", isOpen: "isOpen" }, outputs: { sectionClicked: "sectionClicked", sectionFocused: "sectionFocused", isOpenChange: "isOpenChange" }, host: { properties: { "class.cds-parent": "this.cssClassParent", "class.cds-level1": "this.cssClassLevel1", "class.cds-is-expanded": "this.isExpanded" }, classAttribute: "cds-accordion2-section" }, viewQueries: [{ propertyName: "button", first: true, predicate: ["button"], descendants: true }, { propertyName: "header", first: true, predicate: ["header"], descendants: true }], ngImport: i0, template: `
  <div role="heading" [attr.aria-level]="panelHeaderLevel">
    <button
      #button
      [attr.aria-label]="ariaLabel"
      class="accordion2-button-wrapper"
      type="button"
      size="default"
      (click)="toggleSection(sectionIndex)"
      [attr.aria-controls]="idStr"
      [attr.aria-expanded]="isOpen ? true : false"
      [attr.disabled]="disabled ? true : null"
      [ngClass]="{ 'accordion2-disabled': disabled }"
      >
      <div class="cds-category">
        <div
          class="accordion2-inner"
          [class.accordion2-icon-left]="isLeftIcon"
        >
          <div
            #header
            class="accordion2-header"
            [class.accordion2-header-left]="isLeftIcon"
          >
            <ng-content select="[cdsAccordion2Header]"></ng-content>
          </div>
          <cds-icon
            [name]="toggleIconName"
            [class.icon-expanded]="isOpen"
            [class.icon-rotate]="!isIconRotatePrevented && isOpen"
            type="2021"
            altText="Icon"
            ariaLabel="Icon"
            aria-hidden="true"
            [svgStyle]="{ 'width.px': '22', 'height.px': '22' }"
          ></cds-icon>
        </div>
      </div>
    </button>
  </div>

  <!-- expandable area -->
  <div class="cds-accordion2-submenu" [attr.id]="idStr">
    <ng-content></ng-content>
  </div>
  `, isInline: true, styles: [".cds-accordion2-submenu{display:none;width:100%;margin:auto;padding:var(--cds-accordion2-submenu-padding, 16px 16px 0 16px);background-color:var(--cds-accordion2-submenu-bg-color, #ffffff);max-height:0;overflow:hidden;transition:max-height .15s ease-out,background-color .5s ease-in-out,visibility 1ms ease .501s}.accordion2-button-wrapper{padding:0;margin:0;border:none;width:100%;text-align:left;background-color:var(--cds-accordion2-button-wrapper-bg-color, #eff3f8);border:var(--cds-accordion2-button-wrapper-border-width, 0) solid var(--cds-accordion2-button-wrapper-border-color, transparent)}.accordion2-button-wrapper cds-icon{--cds-icon-path-fill: var(--cds-accordion2-button-wrapper-icon-path-fill)}.accordion2-button-wrapper:hover:enabled{--cds-accordion2-button-wrapper-bg-color: var( --cds-accordion2-button-wrapper-hover-bg-color )}.accordion2-button-wrapper:hover:enabled cds-icon{--cds-icon-path-fill: var( --cds-accordion2-button-wrapper-hover-icon-path-fill, #034c81 )}.accordion2-button-wrapper:focus-visible{box-shadow:0 0 0 var(--cds-accordion2-button-wrapper-outline-offset, 2px) #fff;outline:var(--cds-accordion2-button-wrapper-outline-width, 2px) solid var(--cds-accordion2-button-wrapper-outline-color, #002a54);outline-offset:var(--cds-accordion2-button-wrapper-outline-offset, 2px);position:relative}.accordion2-button-wrapper:disabled{cursor:default}.accordion2-button-wrapper:disabled cds-icon{--cds-icon-path-fill: var( --cds-accordion2-button-wrapper-disabled-icon-path-fill, #ccc )}:host-context(.cds-accordion2-outline-inverse){--cds-accordion2-button-wrapper-outline-color: var( --cds-accordion2-button-wrapper-outline-inverse-color )}.cds-category{outline:none;padding-left:0;display:flex;min-height:48px;color:#333}.cds-category.accordion2-disabled{pointer-events:none;opacity:.9;background-color:#fff}.accordion2-inner{flex:1 1 100%;display:flex;flex-wrap:nowrap;align-items:var(--cds-accordion2-inner-align-items, start);padding-left:16px;justify-content:space-between}.accordion2-inner cds-icon{padding:12px 16px}.accordion2-inner cds-icon.icon-expanded.icon-rotate{transform:rotate(180deg);transform-origin:center}.accordion2-header{padding:12px 0;color:var(--cds-accordion2-header-color);font-family:var(--cds-accordion2-header-font-family, Interstate_Regular);font-weight:var(--cds-accordion2-header-font-weight)}.accordion2-icon-left{justify-content:flex-end;flex-direction:row-reverse;padding-left:0}:host.cds-is-expanded .accordion2-button-wrapper{background-color:var(--cds-accordion2-expanded-button-wrapper-bg-color, #eff3f8)}:host.cds-is-expanded>.cds-accordion2-submenu{display:flex;max-height:initial;transition:max-height .25s ease-in,background-color .5s ease-in-out 2ms}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2SectionComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-accordion2-section', host: { class: 'cds-accordion2-section' }, template: `
  <div role="heading" [attr.aria-level]="panelHeaderLevel">
    <button
      #button
      [attr.aria-label]="ariaLabel"
      class="accordion2-button-wrapper"
      type="button"
      size="default"
      (click)="toggleSection(sectionIndex)"
      [attr.aria-controls]="idStr"
      [attr.aria-expanded]="isOpen ? true : false"
      [attr.disabled]="disabled ? true : null"
      [ngClass]="{ 'accordion2-disabled': disabled }"
      >
      <div class="cds-category">
        <div
          class="accordion2-inner"
          [class.accordion2-icon-left]="isLeftIcon"
        >
          <div
            #header
            class="accordion2-header"
            [class.accordion2-header-left]="isLeftIcon"
          >
            <ng-content select="[cdsAccordion2Header]"></ng-content>
          </div>
          <cds-icon
            [name]="toggleIconName"
            [class.icon-expanded]="isOpen"
            [class.icon-rotate]="!isIconRotatePrevented && isOpen"
            type="2021"
            altText="Icon"
            ariaLabel="Icon"
            aria-hidden="true"
            [svgStyle]="{ 'width.px': '22', 'height.px': '22' }"
          ></cds-icon>
        </div>
      </div>
    </button>
  </div>

  <!-- expandable area -->
  <div class="cds-accordion2-submenu" [attr.id]="idStr">
    <ng-content></ng-content>
  </div>
  `, styles: [".cds-accordion2-submenu{display:none;width:100%;margin:auto;padding:var(--cds-accordion2-submenu-padding, 16px 16px 0 16px);background-color:var(--cds-accordion2-submenu-bg-color, #ffffff);max-height:0;overflow:hidden;transition:max-height .15s ease-out,background-color .5s ease-in-out,visibility 1ms ease .501s}.accordion2-button-wrapper{padding:0;margin:0;border:none;width:100%;text-align:left;background-color:var(--cds-accordion2-button-wrapper-bg-color, #eff3f8);border:var(--cds-accordion2-button-wrapper-border-width, 0) solid var(--cds-accordion2-button-wrapper-border-color, transparent)}.accordion2-button-wrapper cds-icon{--cds-icon-path-fill: var(--cds-accordion2-button-wrapper-icon-path-fill)}.accordion2-button-wrapper:hover:enabled{--cds-accordion2-button-wrapper-bg-color: var( --cds-accordion2-button-wrapper-hover-bg-color )}.accordion2-button-wrapper:hover:enabled cds-icon{--cds-icon-path-fill: var( --cds-accordion2-button-wrapper-hover-icon-path-fill, #034c81 )}.accordion2-button-wrapper:focus-visible{box-shadow:0 0 0 var(--cds-accordion2-button-wrapper-outline-offset, 2px) #fff;outline:var(--cds-accordion2-button-wrapper-outline-width, 2px) solid var(--cds-accordion2-button-wrapper-outline-color, #002a54);outline-offset:var(--cds-accordion2-button-wrapper-outline-offset, 2px);position:relative}.accordion2-button-wrapper:disabled{cursor:default}.accordion2-button-wrapper:disabled cds-icon{--cds-icon-path-fill: var( --cds-accordion2-button-wrapper-disabled-icon-path-fill, #ccc )}:host-context(.cds-accordion2-outline-inverse){--cds-accordion2-button-wrapper-outline-color: var( --cds-accordion2-button-wrapper-outline-inverse-color )}.cds-category{outline:none;padding-left:0;display:flex;min-height:48px;color:#333}.cds-category.accordion2-disabled{pointer-events:none;opacity:.9;background-color:#fff}.accordion2-inner{flex:1 1 100%;display:flex;flex-wrap:nowrap;align-items:var(--cds-accordion2-inner-align-items, start);padding-left:16px;justify-content:space-between}.accordion2-inner cds-icon{padding:12px 16px}.accordion2-inner cds-icon.icon-expanded.icon-rotate{transform:rotate(180deg);transform-origin:center}.accordion2-header{padding:12px 0;color:var(--cds-accordion2-header-color);font-family:var(--cds-accordion2-header-font-family, Interstate_Regular);font-weight:var(--cds-accordion2-header-font-weight)}.accordion2-icon-left{justify-content:flex-end;flex-direction:row-reverse;padding-left:0}:host.cds-is-expanded .accordion2-button-wrapper{background-color:var(--cds-accordion2-expanded-button-wrapper-bg-color, #eff3f8)}:host.cds-is-expanded>.cds-accordion2-submenu{display:flex;max-height:initial;transition:max-height .25s ease-in,background-color .5s ease-in-out 2ms}\n"] }]
        }], ctorParameters: () => [{ type: CdsAnnouncerService }], propDecorators: { disabled: [{
                type: Input
            }], idStr: [{
                type: Input
            }], icon: [{
                type: Input
            }], iconName: [{
                type: Input
            }], expandedIconName: [{
                type: Input
            }], isIconRotatePrevented: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], panelHeaderLevel: [{
                type: Input
            }], sectionClicked: [{
                type: Output
            }], sectionFocused: [{
                type: Output
            }], isOpen: [{
                type: Input
            }], button: [{
                type: ViewChild,
                args: ['button']
            }], header: [{
                type: ViewChild,
                args: ['header']
            }], cssClassParent: [{
                type: HostBinding,
                args: ['class.cds-parent']
            }], cssClassLevel1: [{
                type: HostBinding,
                args: ['class.cds-level1']
            }], isExpanded: [{
                type: HostBinding,
                args: ['class.cds-is-expanded']
            }], isOpenChange: [{
                type: Output
            }] } });

class CdsButton {
    // #endregion
    // #region // ---------- HostBinding ---------- //
    // Button types
    get _primary() {
        return this.cdsButton === 'primary' || !this.cdsButton;
    }
    get _primaryInverse() {
        return this.cdsButton === 'primaryInverse';
    }
    get _secondary() {
        return this.cdsButton === 'secondary';
    }
    get _secondaryInverse() {
        return this.cdsButton === 'secondaryInverse';
    }
    get _alternate() {
        return this.cdsButton === 'alternate';
    }
    get _alternateInverse() {
        return this.cdsButton === 'alternateInverse';
    }
    get _tertiary() {
        return this.cdsButton === 'tertiary';
    }
    get _tertiaryInverse() {
        return this.cdsButton === 'tertiaryInverse';
    }
    get _inlineLink() {
        return this.cdsButton === 'inlineLink';
    }
    get _inlineInverse() {
        return this.cdsButton === 'inlineInverse';
    }
    // Button Sizes
    get _default() {
        return this.size === 'default' || !this.size;
    }
    ;
    get _xsmall() {
        return this.size === 'xsmall';
    }
    ;
    get _small() {
        return this.size === 'small';
    }
    ;
    get _large() {
        return this.size === 'large';
    }
    ;
    // Button Icons
    get _icon() {
        return this.buttonIcon === 'icon';
    }
    get _iconRound() {
        return this.buttonIcon === 'iconRound';
    }
    // Button Arrows
    get _arrowRight() {
        return this.arrowRight;
    }
    get _arrowLeft() {
        return this.arrowLeft;
    }
    set JFPToken(JFPToken) {
        this._hasJFPToken = coerceBooleanProperty(JFPToken);
        // If the JFP Token is set, append it to the href
        // this will not work before AfterViewInit, but is needed here in case JFPToken is set to true after AfterViewInit is called
        this.setJfpToken();
    }
    get JFPToken() {
        return !!this._hasJFPToken;
    }
    // #endregion
    // #region // ---------- Constructor / Lifecycle Hooks ---------- //
    constructor(_el, _renderer) {
        this._el = _el;
        this._renderer = _renderer;
    }
    ngAfterViewInit() {
        // If the JFPToken is set, append it to the href
        this.setJfpToken();
    }
    // #endregion
    // #region // ---------- Methods ---------- //
    /**
     * Set the JFP Token to the href
     */
    setJfpToken() {
        const href = this._el.nativeElement.getAttribute('href');
        if (href && this.JFPToken) {
            this._renderer.setAttribute(this._el.nativeElement, 'href', this.addJfpToken(href));
        }
    }
    addJfpToken(href) {
        // If the target is set to _blank
        if (!href.match('JFP_TOKEN')) {
            // Check if there is a JFP Token in session storage
            const jfpToken = sessionStorage.sessionJFP_TOKEN;
            if (jfpToken) {
                // Append the correct prefix
                href += href.match(/\?/g) ? '&' : '?';
                // Append the JFP_TOKEN
                href += 'JFP_TOKEN=' + jfpToken;
            }
        }
        return href;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsButton, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: { cdsButton: "cdsButton", size: "size", buttonIcon: "buttonIcon", arrowRight: "arrowRight", arrowLeft: "arrowLeft", JFPToken: "JFPToken" }, host: { properties: { "class.cds-button-primary": "this._primary", "class.cds-button-primary-inverse": "this._primaryInverse", "class.cds-button-secondary": "this._secondary", "class.cds-button-secondary-inverse": "this._secondaryInverse", "class.cds-button-alternate": "this._alternate", "class.cds-button-alternate-inverse": "this._alternateInverse", "class.cds-button-tertiary": "this._tertiary", "class.cds-button-tertiary-inverse": "this._tertiaryInverse", "class.cds-button-inline-link": "this._inlineLink", "class.cds-button-inline-inverse": "this._inlineInverse", "class.cds-button-df": "this._default", "class.cds-button-xs": "this._xsmall", "class.cds-button-sm": "this._small", "class.cds-button-lg": "this._large", "class.cds-button-icon": "this._icon", "class.cds-button-icon-round": "this._iconRound", "class.cds-button-chevron-right": "this._arrowRight", "class.cds-button-chevron-left": "this._arrowLeft" }, classAttribute: "cds-button" }, exportAs: ["cdsButton"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsButton, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]',
                    exportAs: 'cdsButton',
                    host: { class: 'cds-button' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }], propDecorators: { _primary: [{
                type: HostBinding,
                args: ['class.cds-button-primary']
            }], _primaryInverse: [{
                type: HostBinding,
                args: ['class.cds-button-primary-inverse']
            }], _secondary: [{
                type: HostBinding,
                args: ['class.cds-button-secondary']
            }], _secondaryInverse: [{
                type: HostBinding,
                args: ['class.cds-button-secondary-inverse']
            }], _alternate: [{
                type: HostBinding,
                args: ['class.cds-button-alternate']
            }], _alternateInverse: [{
                type: HostBinding,
                args: ['class.cds-button-alternate-inverse']
            }], _tertiary: [{
                type: HostBinding,
                args: ['class.cds-button-tertiary']
            }], _tertiaryInverse: [{
                type: HostBinding,
                args: ['class.cds-button-tertiary-inverse']
            }], _inlineLink: [{
                type: HostBinding,
                args: ['class.cds-button-inline-link']
            }], _inlineInverse: [{
                type: HostBinding,
                args: ['class.cds-button-inline-inverse']
            }], _default: [{
                type: HostBinding,
                args: ['class.cds-button-df']
            }], _xsmall: [{
                type: HostBinding,
                args: ['class.cds-button-xs']
            }], _small: [{
                type: HostBinding,
                args: ['class.cds-button-sm']
            }], _large: [{
                type: HostBinding,
                args: ['class.cds-button-lg']
            }], _icon: [{
                type: HostBinding,
                args: ['class.cds-button-icon']
            }], _iconRound: [{
                type: HostBinding,
                args: ['class.cds-button-icon-round']
            }], _arrowRight: [{
                type: HostBinding,
                args: ['class.cds-button-chevron-right']
            }], _arrowLeft: [{
                type: HostBinding,
                args: ['class.cds-button-chevron-left']
            }], cdsButton: [{
                type: Input
            }], size: [{
                type: Input
            }], buttonIcon: [{
                type: Input
            }], arrowRight: [{
                type: Input
            }], arrowLeft: [{
                type: Input
            }], JFPToken: [{
                type: Input
            }] } });

/**
 * This component generates the wrapper, title, and expand/collapse
 * button for accordions. Sections for the accordion are a separate component.
 */
class Accordion2Component {
    get noSectionsOpen() {
        if (this.sections) {
            return this.sections.find(section => section.isOpen) === undefined;
        }
    }
    onKeyPressHome(event) {
        event.preventDefault();
        this.sections.first.focusButton();
    }
    onKeyPressEnd(event) {
        event.preventDefault();
        this.sections.last.focusButton();
    }
    get outlineInverse() {
        return this.isOutlineInverse;
    }
    // #region // ---------- LIFECYCLE ---------- //
    constructor(announcer) {
        this.announcer = announcer;
        // #endregion
        // #region // -------- Inputs ---------- //
        /**
         * whether mulitple section are permitted to be open at the same time
         */
        this.multi = true;
        /**
         * Button text displayed when all items are collapsed
         * @deprecated not used
         */
        this.closeOthersText = 'Other Sections Closed';
        /**
         * Button text displayed when all sections can be collapsed
         */
        this.collapseAllText = 'Collapse All';
        /**
        * Button text displayed when all sections can be expanded
        */
        this.expandAllText = 'Expand All';
        /**
         * Annouced by screen reader along with number of sections
         */
        this.sectionsText = 'sections';
        /**
         * Text to announce when all sections are expanded
         */
        this.allSectionExpandedText = 'All sections expanded';
        /**
         * Text to announce when all sections are collapsed
         */
        this.allSectionCollapsedText = 'All sections collapsed';
        /**
         * Word to use in announcer when section is collapsed
         */
        this.collapsedText = 'collapsed';
    }
    ngAfterContentInit() {
        this.sections.forEach((section, index) => (section.sectionIndex = index));
        this.checkAllSections();
        this.sections.forEach(section => {
            // This isOpenChange subscribe is key for managing allSectionsOpen
            section.isOpenChange.subscribe(isOpen => {
                this.checkAllSections();
            });
            section.sectionClicked.subscribe(sectionIndex => {
                this.listenForClicks(sectionIndex);
            });
        });
    }
    // #endregion
    // #region // ------------ Methods ------------ //
    getSectionLength() {
        return `${this.sections.length} ${this.sectionsText}`;
    }
    /**
     * Used in sectionClicked subscription in life cycle hook above.
     * Listen for clicks to close other sections when multi not active
     */
    listenForClicks(index) {
        if (!this.multi)
            this.setAllSection(index);
    }
    /**
     * Used in sectionClicked subscription in life cycle hook above.
     * Listen for clicks to close other sections when multi not active
     */
    checkAllSections() {
        const areAllOpen = this.sections.toArray().every(s => s.isOpen);
        const areAllClosed = this.sections.toArray().every(s => !s.isOpen);
        if (areAllOpen !== this.allSectionsOpen) {
            this.allSectionsOpen = areAllOpen;
        }
        if (areAllClosed !== this.allSectionsClosed) {
            this.allSectionsClosed = areAllClosed;
        }
    }
    /**
     * closes all other sections except for excluded (newly opened section)
     */
    setAllSection(excludeIndex) {
        this.sections.forEach(section => {
            if (section.sectionIndex !== excludeIndex && section.isOpen) {
                section.isOpen = false;
                let sectionNumber = section.sectionIndex + 1;
                setTimeout(() => {
                    let announceText = `Section ${sectionNumber} ${this.collapsedText}`;
                    if (section.ariaLabel) {
                        announceText = `${section.ariaLabel} ${this.collapsedText}`;
                    }
                    else if (section.header?.nativeElement?.innerText) {
                        announceText = `${section.header.nativeElement.innerText} ${this.collapsedText}`;
                    }
                    this.announcer.announce(announceText);
                });
            }
        });
    }
    /**
     * Opens/closes all sections
     */
    toggleAllSections() {
        this.allSectionsOpen = !this.allSectionsOpen;
        this.allSectionsClosed = !this.allSectionsOpen;
        this.sections.forEach(section => (section.isOpen = this.allSectionsOpen));
    }
    /**
     * Opens all sections
     */
    expandAllSections() {
        this.sections.forEach(section => { section.isOpen = true; });
        this.allSectionsOpen = true;
        this.allSectionsClosed = false;
        this.announcer.announce(this.allSectionExpandedText);
    }
    /**
     * Closes all sections
     */
    collapseAllSections() {
        this.sections.forEach(section => { section.isOpen = false; });
        this.allSectionsOpen = false;
        this.allSectionsClosed = true;
        this.announcer.announce(this.allSectionCollapsedText);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2Component, deps: [{ token: CdsAnnouncerService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.0.3", type: Accordion2Component, selector: "cds-accordion2,.cds-accordion2", inputs: { multi: "multi", closeOthersText: "closeOthersText", displayExpandToggle: "displayExpandToggle", collapseAllText: "collapseAllText", expandAllText: "expandAllText", sectionsText: "sectionsText", allSectionExpandedText: "allSectionExpandedText", allSectionCollapsedText: "allSectionCollapsedText", collapsedText: "collapsedText", disabled: "disabled", isButtonInverse: "isButtonInverse", isOutlineInverse: "isOutlineInverse" }, host: { listeners: { "keydown.home": "onKeyPressHome($event)", "keydown.end": "onKeyPressEnd($event)" }, properties: { "class.cds-accordion2-outline-inverse": "this.outlineInverse" }, classAttribute: "cds-accordion2" }, queries: [{ propertyName: "sections", predicate: i0.forwardRef(() => Accordion2SectionComponent) }], ngImport: i0, template: `
    <div class="accordion2-wrapper">
      @if (displayExpandToggle) {
      <div class="accordion2-expand-collapse-button-wrapper">
        @if (!allSectionsOpen) {
        <button
          class="expand-toggle"
          [cdsButton]="isButtonInverse ? 'inlineInverse' : 'tertiary'"
          [size]="isButtonInverse ? null : 'xsmall'"
          [disabled]="disabled"
          (click)="expandAllSections()"
        >
          {{ expandAllText }}
          <span class="sr-only">&nbsp;{{ getSectionLength() }}</span>
        </button>
        }
        @if (!allSectionsClosed) {
        <button
          class="expand-toggle"
          [cdsButton]="isButtonInverse ? 'inlineInverse' : 'tertiary'"
          [size]="isButtonInverse ? null : 'xsmall'"
          [disabled]="disabled"
          (click)="collapseAllSections()"
        >
          {{ collapseAllText }}
          <span class="sr-only">&nbsp;{{ getSectionLength() }}</span>
        </button>
        }
      </div>
      }
      <div class="accordion2-menu">
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true, styles: [".accordion2-wrapper{display:flex;flex-direction:column;flex-wrap:nowrap;justify-content:flex-start;align-items:flex-start;gap:10px}.accordion2-expand-collapse-button-wrapper{display:flex;justify-content:flex-end;width:100%}.accordion2-menu{display:flex;flex-direction:column;gap:2px;width:100%}:host .expand-toggle{align-self:flex-end;font-weight:var(--cds-accordion2-expand-toggle-font-weight, 300);text-underline-offset:var(--cds-accordion2-expand-toggle-underline-offset, 4px)}:host ::ng-deep fieldset.dark{margin-bottom:0}:host ::ng-deep .cds-hide-toggle-button{display:none}:host ::ng-deep .cds-hidden{display:none}:host ::ng-deep .cds-accordion2-module fieldset{width:100%;position:relative}:host ::ng-deep .cds-accordion2-module{padding:10px 20px!important;margin-top:30px auto!important}:host ::ng-deep .accordion2 ul.cds-mainnav{list-style:none;margin:0;padding:0;width:100%;clear:both}:host ::ng-deep .accordion2 ul.cds-subnav{list-style:none;margin:0;padding:5px;width:100%;clear:both}:host ::ng-deep .cds-accordion2-title{line-height:3rem}:host ::ng-deep .toggle-accordion2{margin-left:15px}:host.cds-accordion2-light ::ng-deep .toggle-accordion2{outline:none;background:none;margin-left:15px;border:none;color:gray}\n"], dependencies: [{ kind: "directive", type: CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2Component, decorators: [{
            type: Component,
            args: [{ selector: 'cds-accordion2,.cds-accordion2', host: { class: 'cds-accordion2' }, template: `
    <div class="accordion2-wrapper">
      @if (displayExpandToggle) {
      <div class="accordion2-expand-collapse-button-wrapper">
        @if (!allSectionsOpen) {
        <button
          class="expand-toggle"
          [cdsButton]="isButtonInverse ? 'inlineInverse' : 'tertiary'"
          [size]="isButtonInverse ? null : 'xsmall'"
          [disabled]="disabled"
          (click)="expandAllSections()"
        >
          {{ expandAllText }}
          <span class="sr-only">&nbsp;{{ getSectionLength() }}</span>
        </button>
        }
        @if (!allSectionsClosed) {
        <button
          class="expand-toggle"
          [cdsButton]="isButtonInverse ? 'inlineInverse' : 'tertiary'"
          [size]="isButtonInverse ? null : 'xsmall'"
          [disabled]="disabled"
          (click)="collapseAllSections()"
        >
          {{ collapseAllText }}
          <span class="sr-only">&nbsp;{{ getSectionLength() }}</span>
        </button>
        }
      </div>
      }
      <div class="accordion2-menu">
        <ng-content></ng-content>
      </div>
    </div>
  `, styles: [".accordion2-wrapper{display:flex;flex-direction:column;flex-wrap:nowrap;justify-content:flex-start;align-items:flex-start;gap:10px}.accordion2-expand-collapse-button-wrapper{display:flex;justify-content:flex-end;width:100%}.accordion2-menu{display:flex;flex-direction:column;gap:2px;width:100%}:host .expand-toggle{align-self:flex-end;font-weight:var(--cds-accordion2-expand-toggle-font-weight, 300);text-underline-offset:var(--cds-accordion2-expand-toggle-underline-offset, 4px)}:host ::ng-deep fieldset.dark{margin-bottom:0}:host ::ng-deep .cds-hide-toggle-button{display:none}:host ::ng-deep .cds-hidden{display:none}:host ::ng-deep .cds-accordion2-module fieldset{width:100%;position:relative}:host ::ng-deep .cds-accordion2-module{padding:10px 20px!important;margin-top:30px auto!important}:host ::ng-deep .accordion2 ul.cds-mainnav{list-style:none;margin:0;padding:0;width:100%;clear:both}:host ::ng-deep .accordion2 ul.cds-subnav{list-style:none;margin:0;padding:5px;width:100%;clear:both}:host ::ng-deep .cds-accordion2-title{line-height:3rem}:host ::ng-deep .toggle-accordion2{margin-left:15px}:host.cds-accordion2-light ::ng-deep .toggle-accordion2{outline:none;background:none;margin-left:15px;border:none;color:gray}\n"] }]
        }], ctorParameters: () => [{ type: CdsAnnouncerService }], propDecorators: { multi: [{
                type: Input
            }], closeOthersText: [{
                type: Input
            }], displayExpandToggle: [{
                type: Input
            }], collapseAllText: [{
                type: Input
            }], expandAllText: [{
                type: Input
            }], sectionsText: [{
                type: Input
            }], allSectionExpandedText: [{
                type: Input
            }], allSectionCollapsedText: [{
                type: Input
            }], collapsedText: [{
                type: Input
            }], disabled: [{
                type: Input
            }], isButtonInverse: [{
                type: Input
            }], isOutlineInverse: [{
                type: Input
            }], sections: [{
                type: ContentChildren,
                args: [forwardRef(() => Accordion2SectionComponent)]
            }], onKeyPressHome: [{
                type: HostListener,
                args: ['keydown.home', ['$event']]
            }], onKeyPressEnd: [{
                type: HostListener,
                args: ['keydown.end', ['$event']]
            }], outlineInverse: [{
                type: HostBinding,
                args: ['class.cds-accordion2-outline-inverse']
            }] } });

const COMPONENTS$R = [CdsButton];
const MODULES$y = [CommonModule];
class CdsButtonModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsButtonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsButtonModule, declarations: [CdsButton], imports: [CommonModule], exports: [CdsButton] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsButtonModule, imports: [MODULES$y] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsButtonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$R],
                    imports: [...MODULES$y],
                    exports: [...COMPONENTS$R]
                }]
        }] });

const COMPONENTS$Q = [Accordion2Component, Accordion2SectionComponent];
const MODULES$x = [CommonModule, CdsIconModule, CdsButtonModule];
class Accordion2 {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: Accordion2, declarations: [Accordion2Component, Accordion2SectionComponent], imports: [CommonModule, CdsIconModule, CdsButtonModule], exports: [Accordion2Component, Accordion2SectionComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2, imports: [MODULES$x] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Accordion2, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$x],
                    declarations: [...COMPONENTS$Q],
                    exports: [...COMPONENTS$Q]
                }]
        }] });

class CdsAlertDialog {
    constructor() {
        this.backgroundColor = '#eee';
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Alert Dialog <citi-alert-dialog> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    get styles() {
        return this.backgroundColor;
    }
    get classes() {
        switch (this.alertDialogType) {
            case 'critical':
                return 'criticalAlert';
            case 'success':
                return 'successAlert';
            case 'marketing':
                return 'marketingAlert';
            case 'technical':
                return 'technicalAlert';
            default:
                return '';
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAlertDialog, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsAlertDialog, selector: "cds-alert-dialog", inputs: { alertDialogType: "alertDialogType", backgroundColor: "backgroundColor" }, host: { properties: { "style.background-color": "this.styles", "class": "this.classes" } }, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, styles: [":host{display:flex;padding:10px}:host.criticalAlert{border-left:4px solid #d60000}:host.successAlert{border-left:4px solid #006e0a}:host.marketingAlert{border-left:4px solid #056dae}:host.technicalAlert{border-left:4px solid #666666}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAlertDialog, decorators: [{
            type: Component,
            args: [{ selector: 'cds-alert-dialog', template: `
    <ng-content></ng-content>
  `, styles: [":host{display:flex;padding:10px}:host.criticalAlert{border-left:4px solid #d60000}:host.successAlert{border-left:4px solid #006e0a}:host.marketingAlert{border-left:4px solid #056dae}:host.technicalAlert{border-left:4px solid #666666}\n"] }]
        }], ctorParameters: () => [], propDecorators: { alertDialogType: [{
                type: Input
            }], backgroundColor: [{
                type: Input
            }], styles: [{
                type: HostBinding,
                args: ['style.background-color']
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }] } });

const COMPONENTS$P = [CdsAlertDialog];
const MODULES$w = [CommonModule];
class CdsAlertDialogModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAlertDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsAlertDialogModule, declarations: [CdsAlertDialog], imports: [CommonModule], exports: [CdsAlertDialog] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAlertDialogModule, imports: [MODULES$w] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsAlertDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$P],
                    imports: [...MODULES$w],
                    exports: [...COMPONENTS$P]
                }]
        }] });

class CdsList {
    set cdsList(listType) {
        if (!listType)
            return;
        this.unordered = this.ordered = this.checkMark = this.xMark = false;
        switch (listType) {
            case 'unordered':
                this.unordered = true;
                break;
            case 'ordered':
                this.ordered = true;
                break;
            case 'check-mark':
                this.checkMark = true;
                break;
            case 'x-mark':
                this.xMark = true;
                break;
        }
    }
    constructor() {
        // Button types
        this.unordered = true;
        this.ordered = false;
        this.checkMark = false;
        this.xMark = false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsList, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsList, selector: "ul[cdsList],ul[cds-list],ol[cdsList],ol[cds-list]", inputs: { cdsList: "cdsList" }, host: { properties: { "class.cds-list-unordered": "this.unordered", "class.cds-list-ordered": "this.ordered", "class.cds-list-check-mark": "this.checkMark", "class.cds-list-x-mark": "this.xMark" }, classAttribute: "cds-list" }, exportAs: ["cdsList"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsList, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ul[cdsList],ul[cds-list],ol[cdsList],ol[cds-list]',
                    exportAs: 'cdsList',
                    host: { class: 'cds-list' }
                }]
        }], ctorParameters: () => [], propDecorators: { unordered: [{
                type: HostBinding,
                args: ['class.cds-list-unordered']
            }], ordered: [{
                type: HostBinding,
                args: ['class.cds-list-ordered']
            }], checkMark: [{
                type: HostBinding,
                args: ['class.cds-list-check-mark']
            }], xMark: [{
                type: HostBinding,
                args: ['class.cds-list-x-mark']
            }], cdsList: [{
                type: Input
            }] } });

const COMPONENTS$O = [CdsList];
const MODULES$v = [CommonModule];
class CdsListModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsListModule, declarations: [CdsList], imports: [CommonModule], exports: [CdsList] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsListModule, imports: [MODULES$v] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsListModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$O],
                    imports: [...MODULES$v],
                    exports: [CdsList]
                }]
        }] });

const DEFAULT_CONFIG = {
    hasBackdrop: false,
    panelClass: 'cds-overlay-panel'
};
class CdsOverlayPanel {
    constructor(host, vcr, overlay, viewContainerRef, announcer, themeService) {
        this.host = host;
        this.vcr = vcr;
        this.overlay = overlay;
        this.viewContainerRef = viewContainerRef;
        this.announcer = announcer;
        this.themeService = themeService;
        this.alive = true;
        this.showing = false;
        this.backdropClick = new EventEmitter();
    }
    getOverlayConfig(config) {
        return new OverlayConfig({
            width: this.origin.offsetWidth,
            backdropClass: config.backdropClass,
            positionStrategy: this.positionStrategy || this.getOverlayPosition(),
            scrollStrategy: this.scrollStrategy || this.getOverlayScroll(),
            maxHeight: config.maxHeight,
            panelClass: config.panelClass,
            hasBackdrop: config.hasBackdrop
        });
    }
    openDropdown(config = {}) {
        this.showing = true;
        this.syncWidth();
        const dialogConfig = { ...DEFAULT_CONFIG, ...config };
        this.overlayRef = this.createOverlay(dialogConfig);
        this.overlayRef.backdropClick().subscribe(() => this.close());
        this.overlayRef.detachments().subscribe(() => this.close());
        const template = new TemplatePortal(this.templateRef, this.vcr);
        this.overlayRef.attach(template);
        this.overlayRef.hostElement.classList.add('cbolui-cds');
        const themeClass = this.themeService.getTheme();
        // console.log('themeClass from service', themeClass);
        if (themeClass) {
            this.overlayRef.hostElement.classList.add(themeClass);
        }
    }
    createOverlay(config) {
        const overlayConfig = this.getOverlayConfig(config);
        return this.overlay.create(overlayConfig);
    }
    getOverlayPosition() {
        const positions = [
            new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
            new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'end', overlayY: 'bottom' })
        ];
        return this.overlay
            .position()
            .flexibleConnectedTo(this.origin)
            .withPositions(positions)
            .withFlexibleDimensions(false)
            .withPush(false);
    }
    getOverlayScroll() {
        return this.overlay.scrollStrategies.reposition();
    }
    onWinResize() {
        this.syncWidth();
    }
    syncWidth() {
        if (this.overlayRef) {
            const refRect = this.origin.getBoundingClientRect();
            this.overlayRef.updateSize({ width: refRect.width });
        }
        else
            return;
    }
    ngOnDestroy() {
        this.alive = false;
    }
    // @HostBinding('attr.role') role = 'application';
    close() {
        if (!!this.overlayRef) {
            this.overlayRef ? this.overlayRef.detach() : null;
            this.overlayRef ? this.overlayRef.dispose() : null;
            this.overlayRef = null;
            this.showing = false;
            this.announcer.announce('Collapsed');
            this.backdropClick.emit(true); // emit backdropClick to control arrow rotate
        }
    }
    get origin() {
        return this.host.nativeElement;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOverlayPanel, deps: [{ token: i0.ElementRef }, { token: i0.ViewContainerRef }, { token: i3.Overlay }, { token: i0.ViewContainerRef }, { token: i1$1.LiveAnnouncer }, { token: CdsThemeService }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsOverlayPanel, selector: "[cdsOverlayPanel]", inputs: { templateRef: "templateRef", positionStrategy: "positionStrategy", scrollStrategy: "scrollStrategy" }, outputs: { backdropClick: "backdropClick" }, host: { listeners: { "window:resize": "onWinResize()", "window:keydown.escape": "close()" } }, exportAs: ["cdsOverlayPanel"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOverlayPanel, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsOverlayPanel]',
                    exportAs: 'cdsOverlayPanel'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.ViewContainerRef }, { type: i3.Overlay }, { type: i0.ViewContainerRef }, { type: i1$1.LiveAnnouncer }, { type: CdsThemeService }], propDecorators: { templateRef: [{
                type: Input
            }], positionStrategy: [{
                type: Input
            }], scrollStrategy: [{
                type: Input
            }], backdropClick: [{
                type: Output
            }], onWinResize: [{
                type: HostListener,
                args: ['window:resize']
            }], close: [{
                type: HostListener,
                args: ['window:keydown.escape']
            }] } });

class CdsPopupHost {
    // #endregion
    constructor(host, liveAnnouncer, overlay) {
        this.host = host;
        this.liveAnnouncer = liveAnnouncer;
        this.overlay = overlay;
    }
    // #region // *********************** NgHooks *********************** //
    ngOnInit() {
        if (!this.panel)
            this.panel = this.host;
    }
    // #endregion
    // #region // *********************** HostListener *********************** //
    toggleDropdown() {
        !this.panel.showing ? this.open() : this.close(this.panel);
    }
    escape() {
        this.close(this.panel);
    }
    onWinResize() {
        if (this.panel.overlayRef) {
            setTimeout(() => {
                // To over-write overlay panel resize event
                this.panel.overlayRef.updateSize({ minWidth: this.panelWidth });
            }, 100);
        }
    }
    // #endregion
    // #region // *********************** Methods *********************** //
    open() {
        if (!!this.overlayPosition) {
            this.panel.positionStrategy = this.getOverlayPosition();
        }
        this.panel.scrollStrategy = this.getOverlayScroll();
        this.liveAnnouncer.announce(`Popup expanded`);
        setTimeout(() => {
            // added for ADA Compliance
            this.panel.openDropdown({
                panelClass: 'cds-popup-panel',
                hasBackdrop: true,
                backdropClass: 'cds-popup-overlay-backdrop-class'
            });
            this.panel.overlayRef.updateSize({ width: this.panelWidth });
            this.panel.overlayRef.updatePosition();
        }, 100);
    }
    close(panelRef) {
        panelRef.close();
        this.liveAnnouncer.announce(`Popup closed`);
    }
    getOverlayPosition() {
        let positions;
        switch (this.overlayPosition) {
            case 'bottom-right':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' })
                ];
                break;
            case 'bottom-left':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' })
                ];
                break;
            case 'bottom-center':
                positions = [
                    new ConnectionPositionPair({ originX: 'center', originY: 'bottom' }, { overlayX: 'center', overlayY: 'top' })
                ];
                break;
            case 'top-right':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' })
                ];
                break;
            case 'top-left':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'end', overlayY: 'bottom' })
                ];
                break;
            case 'top-center':
                positions = [
                    new ConnectionPositionPair({ originX: 'center', originY: 'top' }, { overlayX: 'center', overlayY: 'bottom' })
                ];
                break;
            case 'right-bottom':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'start', overlayY: 'top' })
                ];
                break;
            case 'right-top':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'start', overlayY: 'bottom' })
                ];
                break;
            case 'left-top':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'end', overlayY: 'bottom' })
                ];
                break;
            case 'left-bottom':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'end', overlayY: 'top' })
                ];
                break;
            case 'right':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
                    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' })
                ];
                break;
            default:
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' })
                ];
                break;
        }
        return this.overlay
            .position()
            .flexibleConnectedTo(this.panel.origin)
            .withPositions(positions)
            .withFlexibleDimensions(false)
            .withPush(false);
    }
    getOverlayScroll() {
        return this.scrollStrategy || this.overlay.scrollStrategies.reposition();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupHost, deps: [{ token: CdsOverlayPanel }, { token: i1$1.LiveAnnouncer }, { token: i3.Overlay }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsPopupHost, selector: "[cdsPopupHost]", inputs: { panelWidth: "panelWidth", panel: "panel", overlayPosition: "overlayPosition", scrollStrategy: "scrollStrategy" }, host: { listeners: { "click": "toggleDropdown()", "window:escape": "escape($event)", "window:resize": "onWinResize()" }, classAttribute: "cds-popup-host" }, exportAs: ["cdsPopupHost"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupHost, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsPopupHost]',
                    exportAs: 'cdsPopupHost',
                    host: { class: 'cds-popup-host' }
                }]
        }], ctorParameters: () => [{ type: CdsOverlayPanel }, { type: i1$1.LiveAnnouncer }, { type: i3.Overlay }], propDecorators: { panelWidth: [{
                type: Input
            }], panel: [{
                type: Input
            }], overlayPosition: [{
                type: Input
            }], scrollStrategy: [{
                type: Input
            }], toggleDropdown: [{
                type: HostListener,
                args: ['click']
            }], escape: [{
                type: HostListener,
                args: ['window:escape', ['$event']]
            }], onWinResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

let nextUniqueId$e = 0;
class CdsPopupPanel {
    // #endregion
    constructor(id, _renderer, _cdr) {
        this._renderer = _renderer;
        this._cdr = _cdr;
        // #region // *********************** Properties *********************** //
        this.visibleArrow = true;
        this.arrowPosition = 'bottom-right';
        // #endregion
        // #region // *********************** Declarations *********************** //
        this.cdsArrow = 'cds-arrow-top';
        this.cdsShadowArrow = 'cds-arrow-top-shadow';
        this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-rev';
        this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-col-rev';
        this.role = "dialog";
        this._attributesToRemoveUponClose = [];
        this._unsub$ = new Subject();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Popup Panel <citi-popup-panel> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
        this.panelId = id || `cds-popup-panel-${nextUniqueId$e++}`;
    }
    // #region // *********************** ngHooks *********************** //
    ngAfterViewInit() {
        timer(0)
            .pipe(takeUntil(this._unsub$))
            .subscribe(() => {
            timer(0)
                .pipe(takeUntil(this._unsub$))
                .subscribe(() => {
                this.popUpContainer.nativeElement.scrollIntoView();
            });
            this._focusInModal();
            this._cdr.detectChanges();
        });
    }
    // #endregion
    get class() {
        switch (this.arrowPosition) {
            case 'bottom-left':
                this.cdsArrow = 'cds-arrow-top';
                this.cdsShadowArrow = 'cds-arrow-top-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-rev';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-rev';
                return 'host-bottom';
            case 'bottom-right':
                this.cdsArrow = 'cds-arrow-top';
                this.cdsShadowArrow = 'cds-arrow-top-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-bottom';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-bottom';
                return 'host-bottom-right';
            case 'bottom-center':
                this.cdsArrow = 'cds-arrow-top-center';
                this.cdsShadowArrow = 'cds-arrow-top-center-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-center';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-center';
                return 'host-bottom';
            case 'top-left':
                this.cdsArrow = 'cds-arrow-bottom';
                this.cdsShadowArrow = 'cds-arrow-bottom';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-rev-top';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-rev-top';
                return 'host-top';
            case 'top-right':
                this.cdsArrow = 'cds-arrow-bottom';
                this.cdsShadowArrow = 'cds-arrow-bottom';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-top';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-top';
                return 'host-top';
            case 'top-center':
                this.cdsArrow = 'cds-arrow-bottom-center';
                this.cdsShadowArrow = 'cds-arrow-bottom-center';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-center';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-center';
                return 'host-top';
            case 'left-top':
                this.cdsArrow = 'cds-arrow-right';
                this.cdsShadowArrow = 'cds-arrow-right-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-rev-left';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-col-rev-left';
                return 'host-left';
            case 'left-bottom':
                this.cdsArrow = 'cds-arrow-right';
                this.cdsShadowArrow = 'cds-arrow-right-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-left';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-col-left';
                return 'host-left';
            case 'right-top':
                this.cdsArrow = 'cds-arrow-left';
                this.cdsShadowArrow = 'cds-arrow-left-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-rev';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-col-rev';
                return 'host-right';
            case 'right-bottom':
                this.cdsArrow = 'cds-arrow-left';
                this.cdsShadowArrow = 'cds-arrow-left-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-col';
                return 'host-right';
            default:
                this.cdsArrow = 'cds-arrow-top';
                this.cdsShadowArrow = 'cds-arrow-top-shadow';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-rev';
                this.cdsArrowShadowContainer = 'cds-tooltip-arrow-shadow-container-row-rev';
                return 'host-bottom';
        }
    }
    // #region // *********************** Methods *********************** //
    _focusInModal() {
        const popUpInitalFocusElement = this.popUpContainer.nativeElement.querySelector('.cds-popup-initial-focus, [cdsPopupInitialFocus]');
        if (!!popUpInitalFocusElement) {
            if (!['button', 'a', 'input', 'textarea'].includes(popUpInitalFocusElement.tagName.toLowerCase())) {
                this._setAttributeToBeRemovedWhenClosed(popUpInitalFocusElement, 'tabindex', '-1');
            }
            popUpInitalFocusElement.focus();
        }
        else if (this.focusTrapDirective?.focusTrap?.hasAttached()) {
            this.focusTrapDirective.focusTrap.focusInitialElementWhenReady();
        }
    }
    _setAttributeToBeRemovedWhenClosed(element, attributeName, attributeValue) {
        if (!element.hasAttribute(attributeName)) {
            this._renderer.setAttribute(element, attributeName, attributeValue);
            // set these attributes to be removed after close
            this._attributesToRemoveUponClose.push({ element, attributeName });
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupPanel, deps: [{ token: 'id', attribute: true }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPopupPanel, selector: "cds-popup-panel", inputs: { visibleArrow: "visibleArrow", arrowPosition: "arrowPosition", popupShade: "popupShade", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby" }, host: { properties: { "attr.id": "this.panelId", "attr.role": "this.role", "class": "this.class" }, classAttribute: "cds-popup-panel" }, viewQueries: [{ propertyName: "popUpContainer", first: true, predicate: ["popUpContainer"], descendants: true }, { propertyName: "focusTrapDirective", first: true, predicate: ["focusTrapDirective"], descendants: true }], ngImport: i0, template: `
    <div [ngClass]="cdsArrowShadowContainer" class="arrowShadow" *ngIf="visibleArrow">
      <div
      class="cds-tooltip-arrow-shadow"
      [ngClass]="cdsShadowArrow"
      style.borderBottomColor="#EEE"
      ></div>
    </div>
    <div 
      [ngClass]="cdsArrowContainer" 
      *ngIf="visibleArrow"
    >

      <div
        class="cds-tooltip-arrow"
        [ngClass]="cdsArrow"
        [style.borderBottomColor]="popupShade"
      >
      </div>
    </div>

    <div class="cds-popup-panel-container"
      [attr.id]="panelId ? panelId + '-container' : null"
      [style.backgroundColor]="popupShade"
      #popUpContainer
      cdsFocusTrap
      #focusTrapDirective="cdsFocusTrap"
      [attr.aria-label]="ariaLabel ? ariaLabel : null"
      [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby : null"
    >
      <div class="cds-popup-header">
        <ng-content select="[popupHeader]"></ng-content>
      </div>
      <div class= "cds-popup-content" role="region"><ng-content></ng-content></div>
      <div class="cds-popup-footer">
        <ng-content select="[popupFooter]"></ng-content>
      </div>
    </div>
  `, isInline: true, styles: [":host{display:flex;color:#333}.cds-popup-panel-container{width:100%;padding:16px 7px 17px 16px;background-color:#eee;border-radius:4px;box-shadow:0 0 4px #e6e4e4}.cds-tooltip-arrow-container-col-rev{display:flex;flex-direction:column-reverse;padding-bottom:17px}.cds-tooltip-arrow-shadow-container-col-rev{display:flex;flex-direction:column-reverse;padding-bottom:17px;margin-right:-10px}.cds-tooltip-arrow-container-col-rev-left{display:flex;flex-direction:column-reverse;padding-bottom:17px}.cds-tooltip-arrow-shadow-container-col-rev-left{display:flex;flex-direction:column-reverse;padding-bottom:17px;margin-right:-14px}.cds-tooltip-arrow-container-col{display:flex;flex-direction:column;padding-bottom:12px;padding-top:10px}.cds-tooltip-arrow-shadow-container-col{display:flex;flex-direction:column;padding-bottom:12px;padding-top:10px;margin-right:-14px}.cds-tooltip-arrow-container-col-left{display:flex;flex-direction:column;padding-top:9px}.cds-tooltip-arrow-shadow-container-col-left{display:flex;flex-direction:column;padding-top:9px;margin-right:-13px}.cds-tooltip-arrow-container-row-rev{display:flex;flex-direction:row-reverse;padding-right:14px;padding-bottom:10px}.cds-tooltip-arrow-shadow-container-row-rev{display:flex;flex-direction:row-reverse;padding-right:14px;padding-bottom:1px}.cds-tooltip-arrow-container-row-rev-top,.cds-tooltip-arrow-shadow-container-row-rev-top{display:flex;flex-direction:row-reverse;padding-right:14px;padding-top:2px}.cds-tooltip-arrow-container-row{display:flex;flex-direction:row;padding-bottom:11px;padding-left:1px}.cds-tooltip-arrow-container-row-bottom{display:flex;flex-direction:row;padding-bottom:10px;padding-left:14px}.cds-tooltip-arrow-shadow-container-row-bottom{display:flex;flex-direction:row;padding-left:14px}.cds-tooltip-arrow-container-row-top,.cds-tooltip-arrow-shadow-container-row-top{display:flex;flex-direction:row;padding-right:1px;padding-top:3px;padding-left:14px}.cds-tooltip-arrow-container-row-center,.cds-tooltip-arrow-shadow-container-row-center{display:flex;flex-direction:row}.arrowShadow{position:relative}.cds-tooltip-arrow{border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:15px solid #eeeeee;z-index:1}.cds-tooltip-arrow-shadow{border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:15px solid #eeeeee;z-index:-1;position:inherit;margin-top:-15px!important}.cds-arrow-left{transform:rotate(-90deg);margin-top:8px;margin-right:-7px;margin-left:-7px}.cds-arrow-left-shadow{transform:rotate(-90deg);margin-top:8px!important;margin-right:-6px;margin-left:-7px;position:inherit}.cds-arrow-right{transform:rotate(90deg);margin-top:8px;margin-left:-7px;margin-right:-7px}.cds-arrow-right-shadow{transform:rotate(90deg);margin-top:8px!important;margin-left:-7px;margin-right:-7px;left:-11px}.cds-arrow-top{margin-right:1px;margin-left:1px;margin-bottom:-10px}.cds-arrow-top-shadow{margin-right:1px;margin-left:1px;bottom:-14px}.cds-arrow-bottom{transform:rotate(-180deg);margin-left:1px;margin-top:-3px}.cds-arrow-top-center{margin:0 auto}.cds-arrow-top-center-shadow{margin:-13px auto}.cds-arrow-bottom-center{transform:rotate(-180deg);margin:0 auto}:host(.host-bottom){flex-direction:column}:host(.host-bottom-right){flex-direction:column;margin-left:-3px}:host(.host-top){flex-direction:column-reverse}:host(.host-left){flex-direction:row-reverse}:host(.host-right){flex-direction:row}.cds-popup-content{color:#333;font-size:12px;font-weight:300;letter-spacing:0;line-height:18px}.cds-popup-header{text-align:right;float:right;width:auto;color:#333;font-family:Interstate;font-size:12px;font-weight:700;letter-spacing:0;line-height:18px}.cds-popup-footer{text-align:right;margin-top:10px}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsFocusTrap, selector: "[cdsFocusTrap]", exportAs: ["cdsFocusTrap"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupPanel, decorators: [{
            type: Component,
            args: [{ selector: 'cds-popup-panel', host: { class: 'cds-popup-panel' }, template: `
    <div [ngClass]="cdsArrowShadowContainer" class="arrowShadow" *ngIf="visibleArrow">
      <div
      class="cds-tooltip-arrow-shadow"
      [ngClass]="cdsShadowArrow"
      style.borderBottomColor="#EEE"
      ></div>
    </div>
    <div 
      [ngClass]="cdsArrowContainer" 
      *ngIf="visibleArrow"
    >

      <div
        class="cds-tooltip-arrow"
        [ngClass]="cdsArrow"
        [style.borderBottomColor]="popupShade"
      >
      </div>
    </div>

    <div class="cds-popup-panel-container"
      [attr.id]="panelId ? panelId + '-container' : null"
      [style.backgroundColor]="popupShade"
      #popUpContainer
      cdsFocusTrap
      #focusTrapDirective="cdsFocusTrap"
      [attr.aria-label]="ariaLabel ? ariaLabel : null"
      [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby : null"
    >
      <div class="cds-popup-header">
        <ng-content select="[popupHeader]"></ng-content>
      </div>
      <div class= "cds-popup-content" role="region"><ng-content></ng-content></div>
      <div class="cds-popup-footer">
        <ng-content select="[popupFooter]"></ng-content>
      </div>
    </div>
  `, styles: [":host{display:flex;color:#333}.cds-popup-panel-container{width:100%;padding:16px 7px 17px 16px;background-color:#eee;border-radius:4px;box-shadow:0 0 4px #e6e4e4}.cds-tooltip-arrow-container-col-rev{display:flex;flex-direction:column-reverse;padding-bottom:17px}.cds-tooltip-arrow-shadow-container-col-rev{display:flex;flex-direction:column-reverse;padding-bottom:17px;margin-right:-10px}.cds-tooltip-arrow-container-col-rev-left{display:flex;flex-direction:column-reverse;padding-bottom:17px}.cds-tooltip-arrow-shadow-container-col-rev-left{display:flex;flex-direction:column-reverse;padding-bottom:17px;margin-right:-14px}.cds-tooltip-arrow-container-col{display:flex;flex-direction:column;padding-bottom:12px;padding-top:10px}.cds-tooltip-arrow-shadow-container-col{display:flex;flex-direction:column;padding-bottom:12px;padding-top:10px;margin-right:-14px}.cds-tooltip-arrow-container-col-left{display:flex;flex-direction:column;padding-top:9px}.cds-tooltip-arrow-shadow-container-col-left{display:flex;flex-direction:column;padding-top:9px;margin-right:-13px}.cds-tooltip-arrow-container-row-rev{display:flex;flex-direction:row-reverse;padding-right:14px;padding-bottom:10px}.cds-tooltip-arrow-shadow-container-row-rev{display:flex;flex-direction:row-reverse;padding-right:14px;padding-bottom:1px}.cds-tooltip-arrow-container-row-rev-top,.cds-tooltip-arrow-shadow-container-row-rev-top{display:flex;flex-direction:row-reverse;padding-right:14px;padding-top:2px}.cds-tooltip-arrow-container-row{display:flex;flex-direction:row;padding-bottom:11px;padding-left:1px}.cds-tooltip-arrow-container-row-bottom{display:flex;flex-direction:row;padding-bottom:10px;padding-left:14px}.cds-tooltip-arrow-shadow-container-row-bottom{display:flex;flex-direction:row;padding-left:14px}.cds-tooltip-arrow-container-row-top,.cds-tooltip-arrow-shadow-container-row-top{display:flex;flex-direction:row;padding-right:1px;padding-top:3px;padding-left:14px}.cds-tooltip-arrow-container-row-center,.cds-tooltip-arrow-shadow-container-row-center{display:flex;flex-direction:row}.arrowShadow{position:relative}.cds-tooltip-arrow{border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:15px solid #eeeeee;z-index:1}.cds-tooltip-arrow-shadow{border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:15px solid #eeeeee;z-index:-1;position:inherit;margin-top:-15px!important}.cds-arrow-left{transform:rotate(-90deg);margin-top:8px;margin-right:-7px;margin-left:-7px}.cds-arrow-left-shadow{transform:rotate(-90deg);margin-top:8px!important;margin-right:-6px;margin-left:-7px;position:inherit}.cds-arrow-right{transform:rotate(90deg);margin-top:8px;margin-left:-7px;margin-right:-7px}.cds-arrow-right-shadow{transform:rotate(90deg);margin-top:8px!important;margin-left:-7px;margin-right:-7px;left:-11px}.cds-arrow-top{margin-right:1px;margin-left:1px;margin-bottom:-10px}.cds-arrow-top-shadow{margin-right:1px;margin-left:1px;bottom:-14px}.cds-arrow-bottom{transform:rotate(-180deg);margin-left:1px;margin-top:-3px}.cds-arrow-top-center{margin:0 auto}.cds-arrow-top-center-shadow{margin:-13px auto}.cds-arrow-bottom-center{transform:rotate(-180deg);margin:0 auto}:host(.host-bottom){flex-direction:column}:host(.host-bottom-right){flex-direction:column;margin-left:-3px}:host(.host-top){flex-direction:column-reverse}:host(.host-left){flex-direction:row-reverse}:host(.host-right){flex-direction:row}.cds-popup-content{color:#333;font-size:12px;font-weight:300;letter-spacing:0;line-height:18px}.cds-popup-header{text-align:right;float:right;width:auto;color:#333;font-family:Interstate;font-size:12px;font-weight:700;letter-spacing:0;line-height:18px}.cds-popup-footer{text-align:right;margin-top:10px}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }], propDecorators: { visibleArrow: [{
                type: Input
            }], arrowPosition: [{
                type: Input
            }], popupShade: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], panelId: [{
                type: HostBinding,
                args: ['attr.id']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], popUpContainer: [{
                type: ViewChild,
                args: ['popUpContainer']
            }], focusTrapDirective: [{
                type: ViewChild,
                args: ['focusTrapDirective']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CdsPopupClose {
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
    }
    ngOnInit() { }
    onClick() {
        this.panel.close();
        this.liveAnnouncer.announce(`popup closing`);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupClose, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsPopupClose, selector: "a[cdsPopupClose],button[cdsPopupClose]", inputs: { panel: "panel" }, host: { listeners: { "click": "onClick()" }, classAttribute: "cds-popup-close" }, exportAs: ["cdsPopupClose"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupClose, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[cdsPopupClose],button[cdsPopupClose]',
                    exportAs: 'cdsPopupClose',
                    host: { class: 'cds-popup-close' }
                }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { panel: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class CdsPopupFocus {
    constructor(host) {
        this.host = host;
    }
    focus() {
        this.host.nativeElement.focus();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupFocus, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsPopupFocus, selector: "[cdsPopupFocus]", host: { attributes: { "tabindex": "0" }, classAttribute: "cds-popup-focus" }, exportAs: ["cdsPopupFocus"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupFocus, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsPopupFocus]',
                    exportAs: 'cdsPopupFocus',
                    host: { class: 'cds-popup-focus', tabindex: '0' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

const COMPONENTS$N = [CdsPopupHost, CdsPopupPanel, CdsPopupClose, CdsPopupFocus];
const MODULES$u = [CommonModule, A11yModule, CdsFocusTrapModule];
class CdsPopupPanelModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupPanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupPanelModule, declarations: [CdsPopupHost, CdsPopupPanel, CdsPopupClose, CdsPopupFocus], imports: [CommonModule, A11yModule, CdsFocusTrapModule], exports: [CdsPopupHost, CdsPopupPanel, CdsPopupClose, CdsPopupFocus] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupPanelModule, imports: [MODULES$u] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupPanelModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$N],
                    imports: [...MODULES$u],
                    exports: [...COMPONENTS$N]
                }]
        }] });

let _popup2UniqueIdCounter = 0;
class CdsPopup2Component {
    /**
     * Sets id for popup2
     */
    set idStr(val) {
        this._idStr = val || this._uid;
    }
    get idStr() {
        return this._idStr || this._uid;
    }
    /**
     * Shows/Hides the popup
     */
    set show(isShown) {
        if (isShown) {
            this._open();
        }
        else {
            this._close();
        }
    }
    get show() {
        return this._isShown;
    }
    // #endregion
    // #region ========== HostListener ========== //
    /**
     * Closes popup if clicking outside of popup.
     * @param e PointerEvent
     *
     * NOTE: Using 'mousedown'/'touchstart' instead of 'click'
     * to prevent close/open happening at same time in certain cases.
     */
    _documentMousedown(e) {
        if (this._isShown && !this.isKeptOpenOnOutsideClick) {
            const isInPopup = this.popup2Container.nativeElement.contains(e.target);
            const isInPopupHost = this.popupHost?.contains(e.target);
            if (!isInPopup && !isInPopupHost) {
                this._closeFromWithinComponent();
            }
        }
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_doc, _announcer) {
        this._doc = _doc;
        this._announcer = _announcer;
        this._closeEventUnsub$ = new Subject();
        // #endregion
        // #region ========== Input / Output ========== //
        /**
         * aria-label for popup close button
         */
        this.closeAriaLabel = 'popup close';
        this._uid = `CdsPopup2_${_popup2UniqueIdCounter++}`;
        /**
         * Change the popup2 role
         * (Don't display in documentation. For Single Step only)
         */
        this.role = 'tooltip';
        /**
         * Enables two way binding for show
         */
        this.showChange = new EventEmitter();
        /**
         * Title aria-level
         */
        this.titleAriaLevel = 2;
        this._document = this._doc;
        this._window = this._document?.defaultView;
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Popup2 <cds-popup2> is deprecated. Please discontinue using and update to CDS Popup Dialog <cds-popup-dialog>', 'background-color: #f00; color: #fff;');
        }
    }
    ngOnDestroy() {
        this._closeEventUnsub$.next();
        this._closeEventUnsub$.complete();
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * closes popup
     */
    _close() {
        // Focus back on button before removing popup from DOM
        this.popupHost?.focus();
        setTimeout(() => {
            // Unsubscribe from reposition listening events and close popup
            this._closeEventUnsub$.next();
            this._isShown = false;
            this._isVisible = false;
        }, 0);
    }
    /**
     * Use this method if 'close' is triggered from within component and not from 'show' input.
     */
    _closeFromWithinComponent() {
        this._close();
        // Emitter can not be inside the _close() method since _close() is also called from 'show' @Input.
        this.showChange.emit(false);
    }
    /**
     * Prevents the escape key from propagating to parent elements (such as modal) when popup is open.
     */
    _escapeKeydown(e) {
        if (this.show && !this.isKeptOpenOnEscape) {
            e.stopPropagation();
            this._closeFromWithinComponent();
        }
    }
    /**
     * If focus leaves popup, then it is closed
     * @param e FocusEvent
     */
    _focusoutPopup2(e) {
        const isInPopup = this.popup2Container.nativeElement.contains(e.relatedTarget);
        if (this._isShown && !isInPopup && !this.isKeptOpenOnFocusout) {
            this._closeFromWithinComponent();
        }
    }
    /**
     * Opens popup, then sets up listeners to reposition popup on resize/scroll/etc.
     */
    _open() {
        // adds popup to DOM, but should be hidden first
        this._isShown = true;
        // setTimeout allows popup to be added to DOM first (so it can get dimensions while still hidden)
        setTimeout(() => {
            // Gets dimensions and positions the popup on open
            this._repositionPopup2();
            // Will listen for other events and reposition popup
            const fromScroll$ = fromEvent(this._window, 'scroll');
            const fromResize$ = fromEvent(this._window, 'resize');
            const scrollParents$ = getScrollParents(this.popup2Container.nativeElement)
                .filter(el => el?.nodeName?.toLowerCase() !== 'html')
                .map(elem => fromEvent(elem, 'scroll'));
            merge(fromResize$, fromScroll$, ...scrollParents$)
                .pipe(takeUntil$1(this._closeEventUnsub$), throttleTime(10, null, { leading: false, trailing: true }))
                .subscribe(obv => {
                if (!this.isKeptOpenOnScrollResize) {
                    // If popup is collapsed on scroll/resize and the popupHost is currently focused,
                    // then it needs to announce what was collapsed
                    if (this._document.activeElement === this.popupHost) {
                        this._announcer.announce(`${this.titleText} popup`, 200);
                        this._isVisible = false;
                        setTimeout(() => {
                            this._closeFromWithinComponent();
                        }, 400);
                    }
                    else {
                        this._closeFromWithinComponent();
                    }
                }
                else {
                    this._repositionPopup2();
                }
            });
            // Will listen for escape event on host element (button) and stop propagation
            fromEvent(this.popupHost, 'keydown')
                .pipe(takeUntil$1(this._closeEventUnsub$))
                .subscribe((e) => {
                if (e.key === 'Escape') {
                    this._escapeKeydown(e);
                }
            });
            // Now that popup has been positioned, we can now make it visible
            this._isVisible = true;
        }, 0);
    }
    /**
     * Gets dimensions of popup and host button and positions popup accordingly
     */
    _repositionPopup2() {
        if (this.popupHost && this.popup2Container) {
            if (this.popupHost.offsetHeight === 0 && this.popupHost.offsetWidth === 0) {
                this.popupHost = this._document.getElementById(this.popupHost.id);
            }
            const hostRect = this.popupHost?.getBoundingClientRect();
            const caretRect = this.popup2Container.nativeElement?.querySelector('.cds-popup2-caret')?.getBoundingClientRect();
            const containerRect = this.popup2Container.nativeElement.getBoundingClientRect();
            const hostHorizontalCenter = hostRect.left + (hostRect.width / 2);
            const hostVerticalCenter = hostRect.top + (hostRect.height / 2);
            switch (this.position) {
                case 'right-middle':
                    this._containerTop = hostVerticalCenter - (containerRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
                case 'right-bottom':
                    this._containerTop = (hostVerticalCenter - containerRect.height) + (caretRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
                case 'left-top':
                    this._containerTop = hostVerticalCenter - (caretRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'left-middle':
                    this._containerTop = hostVerticalCenter - (containerRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'left-bottom':
                    this._containerTop = (hostVerticalCenter - containerRect.height) + (caretRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'below-left':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = hostHorizontalCenter - (caretRect.width / 2);
                    break;
                case 'below-center':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = hostHorizontalCenter - (containerRect.width / 2);
                    break;
                case 'below-right':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = (hostHorizontalCenter - containerRect.width) + (caretRect.width / 2);
                    break;
                case 'above-left':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = hostHorizontalCenter - (caretRect.width / 2);
                    break;
                case 'above-center':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = hostHorizontalCenter - (containerRect.width / 2);
                    break;
                case 'above-right':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = (hostHorizontalCenter - containerRect.width) + (caretRect.width / 2);
                    break;
                case 'right-top':
                // falls through to default
                default:
                    this._containerTop = hostVerticalCenter - (caretRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
            }
            // Round the numbers
            this._containerTop = Math.round(this._containerTop);
            this._containerLeft = Math.round(this._containerLeft);
        }
    }
    /**
     * Repositions popup2 component.
     * If your content changes while the Popup2 is open it may affect the position of the popup2
     * You may need to reposition the popup after the content is changed (after DOM has been updated)
     */
    reposition() {
        if (this.show) {
            this._repositionPopup2();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopup2Component, deps: [{ token: DOCUMENT }, { token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPopup2Component, selector: "cds-popup2", inputs: { closeAriaLabel: "closeAriaLabel", idStr: "idStr", isInverse: "isInverse", isKeptOpenOnEscape: "isKeptOpenOnEscape", isKeptOpenOnFocusout: "isKeptOpenOnFocusout", isKeptOpenOnOutsideClick: "isKeptOpenOnOutsideClick", isKeptOpenOnScrollResize: "isKeptOpenOnScrollResize", maxWidth: "maxWidth", popupHost: "popupHost", position: "position", role: "role", show: "show", titleAriaLevel: "titleAriaLevel", titleText: "titleText" }, outputs: { showChange: "showChange" }, host: { listeners: { "focusout": "_focusoutPopup2($event)", "document:mousedown": "_documentMousedown($event)", "document:touchstart": "_documentMousedown($event)" }, classAttribute: "cds-popup2" }, viewQueries: [{ propertyName: "popup2Container", first: true, predicate: ["popup2Container"], descendants: true }], ngImport: i0, template: `
    <div
      #popup2Container
      *ngIf="show"
      [id]="idStr"
      class="cds-popup2-container"
      [ngClass]="position"
      [class.cds-popup2-dark]="!isInverse"
      [class.cds-popup2-visible]="_isVisible"
      [style.maxWidth]="maxWidth"
      [style.top.px]="_containerTop"
      [style.left.px]="_containerLeft"
      (keydown.escape)="_escapeKeydown($event)"
    >
      <div class="cds-popup2-caret-wrapper">
        <div class="cds-popup2-caret"></div>
      </div>
      <div [attr.role]="role" class="cds-popup2-bubble cds-popup2-grid-wrapper">
        <!-- #region grid-title -->
        <div class="cds-popup2-title cds-popup2-grid-title">
          <div role="heading" [attr.aria-level]="titleAriaLevel">{{titleText}}</div>
        </div>
        <!-- #endregion grid-title -->
        <!-- #region grid-content -->
        <div class="cds-popup2-content cds-popup2-grid-content">
          <ng-content></ng-content>
        </div>
        <!-- #endregion grid-content -->
        <!-- #region grid-close -->
        <div class="cds-popup2-close cds-popup2-grid-close">
          <button
            class="cds-popup2-close-button"
            type="button"
            [attr.aria-label]="closeAriaLabel"
            (click)="_closeFromWithinComponent()"
          >
            <cds-icon
              type="2021"
              name="044-close"
              aria-hidden="true"
              [svgStyle]="{'width.px': 16,'height.px': 16}"
            ></cds-icon>
          </button>
        </div>
        <!-- #endregion grid-close -->
      </div>
    </div>
  `, isInline: true, styles: [".cds-popup2-container{width:100%;max-width:322px;display:flex;visibility:hidden;z-index:1;position:fixed}.cds-popup2-visible{visibility:visible}.cds-popup2-caret-wrapper{flex:0 0 22px;overflow:hidden;display:flex;align-items:flex-start}.cds-popup2-caret{width:22px;height:76px;display:flex;align-items:center;margin:0 -12px 0 auto}.cds-popup2-caret:before{content:\"\";display:flex;width:20px;height:20px;background-color:#fcfcfc;box-shadow:0 1px 10px #0000001f;transform:rotate(45deg)}.cds-popup2-bubble{flex-grow:1;padding:16px;background-color:#fcfcfc;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-popup2-title{font-size:18px;font-weight:700;line-height:24px;flex-grow:1}.cds-popup2-close-button{border:none;background:none;padding:0;width:24px;height:24px}.cds-popup2-grid-wrapper{display:grid;row-gap:8px;grid-template-columns:auto 0fr;grid-template-areas:\"title close\" \"main main\"}.cds-popup2-grid-title{grid-area:title}.cds-popup2-grid-content{grid-area:main}.cds-popup2-grid-close{grid-area:close}.cds-popup2-dark .cds-popup2-caret:before,.cds-popup2-dark .cds-popup2-bubble{background-color:#333;color:#fff}.cds-popup2-dark .cds-popup2-close-button{--cds-icon-path-fill: #fff}.right-middle .cds-popup2-caret-wrapper,.left-middle .cds-popup2-caret-wrapper{align-items:center}.right-bottom .cds-popup2-caret-wrapper,.left-bottom .cds-popup2-caret-wrapper{align-items:flex-end;align-items:end}.left-top,.left-middle,.left-bottom{flex-direction:row-reverse}.left-top .cds-popup2-caret,.left-middle .cds-popup2-caret,.left-bottom .cds-popup2-caret{margin:0 auto 0 -12px}.below-left,.below-center,.below-right,.above-left,.above-center,.above-right{max-width:300px}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret,.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{width:76px;height:22px;justify-content:center}.below-left,.below-center,.below-right{flex-direction:column}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret{margin:auto 0 -12px}.above-left,.above-center,.above-right{flex-direction:column-reverse}.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{margin:-12px 0 auto}.below-center .cds-popup2-caret-wrapper,.above-center .cds-popup2-caret-wrapper{justify-content:center}.below-right .cds-popup2-caret-wrapper,.above-right .cds-popup2-caret-wrapper{justify-content:flex-end;justify-content:right}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopup2Component, decorators: [{
            type: Component,
            args: [{ selector: 'cds-popup2', template: `
    <div
      #popup2Container
      *ngIf="show"
      [id]="idStr"
      class="cds-popup2-container"
      [ngClass]="position"
      [class.cds-popup2-dark]="!isInverse"
      [class.cds-popup2-visible]="_isVisible"
      [style.maxWidth]="maxWidth"
      [style.top.px]="_containerTop"
      [style.left.px]="_containerLeft"
      (keydown.escape)="_escapeKeydown($event)"
    >
      <div class="cds-popup2-caret-wrapper">
        <div class="cds-popup2-caret"></div>
      </div>
      <div [attr.role]="role" class="cds-popup2-bubble cds-popup2-grid-wrapper">
        <!-- #region grid-title -->
        <div class="cds-popup2-title cds-popup2-grid-title">
          <div role="heading" [attr.aria-level]="titleAriaLevel">{{titleText}}</div>
        </div>
        <!-- #endregion grid-title -->
        <!-- #region grid-content -->
        <div class="cds-popup2-content cds-popup2-grid-content">
          <ng-content></ng-content>
        </div>
        <!-- #endregion grid-content -->
        <!-- #region grid-close -->
        <div class="cds-popup2-close cds-popup2-grid-close">
          <button
            class="cds-popup2-close-button"
            type="button"
            [attr.aria-label]="closeAriaLabel"
            (click)="_closeFromWithinComponent()"
          >
            <cds-icon
              type="2021"
              name="044-close"
              aria-hidden="true"
              [svgStyle]="{'width.px': 16,'height.px': 16}"
            ></cds-icon>
          </button>
        </div>
        <!-- #endregion grid-close -->
      </div>
    </div>
  `, host: {
                        class: 'cds-popup2',
                        '(focusout)': '_focusoutPopup2($event)',
                    }, styles: [".cds-popup2-container{width:100%;max-width:322px;display:flex;visibility:hidden;z-index:1;position:fixed}.cds-popup2-visible{visibility:visible}.cds-popup2-caret-wrapper{flex:0 0 22px;overflow:hidden;display:flex;align-items:flex-start}.cds-popup2-caret{width:22px;height:76px;display:flex;align-items:center;margin:0 -12px 0 auto}.cds-popup2-caret:before{content:\"\";display:flex;width:20px;height:20px;background-color:#fcfcfc;box-shadow:0 1px 10px #0000001f;transform:rotate(45deg)}.cds-popup2-bubble{flex-grow:1;padding:16px;background-color:#fcfcfc;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-popup2-title{font-size:18px;font-weight:700;line-height:24px;flex-grow:1}.cds-popup2-close-button{border:none;background:none;padding:0;width:24px;height:24px}.cds-popup2-grid-wrapper{display:grid;row-gap:8px;grid-template-columns:auto 0fr;grid-template-areas:\"title close\" \"main main\"}.cds-popup2-grid-title{grid-area:title}.cds-popup2-grid-content{grid-area:main}.cds-popup2-grid-close{grid-area:close}.cds-popup2-dark .cds-popup2-caret:before,.cds-popup2-dark .cds-popup2-bubble{background-color:#333;color:#fff}.cds-popup2-dark .cds-popup2-close-button{--cds-icon-path-fill: #fff}.right-middle .cds-popup2-caret-wrapper,.left-middle .cds-popup2-caret-wrapper{align-items:center}.right-bottom .cds-popup2-caret-wrapper,.left-bottom .cds-popup2-caret-wrapper{align-items:flex-end;align-items:end}.left-top,.left-middle,.left-bottom{flex-direction:row-reverse}.left-top .cds-popup2-caret,.left-middle .cds-popup2-caret,.left-bottom .cds-popup2-caret{margin:0 auto 0 -12px}.below-left,.below-center,.below-right,.above-left,.above-center,.above-right{max-width:300px}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret,.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{width:76px;height:22px;justify-content:center}.below-left,.below-center,.below-right{flex-direction:column}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret{margin:auto 0 -12px}.above-left,.above-center,.above-right{flex-direction:column-reverse}.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{margin:-12px 0 auto}.below-center .cds-popup2-caret-wrapper,.above-center .cds-popup2-caret-wrapper{justify-content:center}.below-right .cds-popup2-caret-wrapper,.above-right .cds-popup2-caret-wrapper{justify-content:flex-end;justify-content:right}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1$1.LiveAnnouncer }], propDecorators: { popup2Container: [{
                type: ViewChild,
                args: ['popup2Container']
            }], closeAriaLabel: [{
                type: Input
            }], idStr: [{
                type: Input
            }], isInverse: [{
                type: Input
            }], isKeptOpenOnEscape: [{
                type: Input
            }], isKeptOpenOnFocusout: [{
                type: Input
            }], isKeptOpenOnOutsideClick: [{
                type: Input
            }], isKeptOpenOnScrollResize: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], popupHost: [{
                type: Input
            }], position: [{
                type: Input
            }], role: [{
                type: Input
            }], show: [{
                type: Input
            }], showChange: [{
                type: Output
            }], titleAriaLevel: [{
                type: Input
            }], titleText: [{
                type: Input
            }], _documentMousedown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }, {
                type: HostListener,
                args: ['document:touchstart', ['$event']]
            }] } });

let nextId$6 = 0;
class CdsBannerAlert {
    get _body() {
        return this._web.document.querySelector('body');
    }
    /**
     * Sets id for banner alert
     */
    set idStr(id) {
        this._idStr = id;
        this._applyId();
    }
    get idStr() {
        return this._idStr;
    }
    /**
     * Determines if the banner alert to open or close
     */
    set openBannerAlert(isOpen) {
        if (this._openBannerAlert !== isOpen) {
            this._openBannerAlert = isOpen;
            if (!isOpen) {
                this._focusAfterClose();
            }
        }
    }
    get openBannerAlert() {
        return this._openBannerAlert;
    }
    // #endregion
    // #region ========== HostBinding ========== //
    /**
     * sets background-color to host
     */
    get styles() {
        if (this.bannerAlertBgColor)
            return this.bannerAlertBgColor;
    }
    /**
     * setting custom color to component
     */
    get color() {
        if (this.bannerAlertColor)
            return `${this.bannerAlertColor}`;
    }
    /**
     * sets class to host based on the bannerAlertType
     */
    get classes() {
        switch (this.bannerAlertType) {
            case 'critical':
                return 'critical-alert';
            case 'success':
                return 'success-alert';
            case 'marketing':
                return 'marketing-alert';
            case 'technical':
                return 'technical-alert';
            case 'custom':
                return 'custom-alert';
            default:
                return '';
        }
    }
    /**
     * toggles between display block and none based on the triggerr on closeBannerAlert() method
     */
    get display() {
        return this.openBannerAlert ? 'block' : 'none';
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_elRef, _renderer, _web, _announcer) {
        this._elRef = _elRef;
        this._renderer = _renderer;
        this._web = _web;
        this._announcer = _announcer;
        // #region ========== Properties ========== //
        this._focusableElementStr = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        // #endregion
        // #region ========== Input / Output ========== //
        /**
         * Determines the message to announce when closeBannerAlert method is triggered.
         */
        this.announceCloseMessage = 'Alert Closed';
        this._openBannerAlert = true;
        /**
         * Emits when openBannerAlert changes
         */
        this.openBannerAlertChange = new EventEmitter();
    }
    ngOnInit() {
        this._applyId();
    }
    ngOnDestroy() {
        this.closeBannerAlert();
    }
    // #endregion
    // #region ========== Methods ========== //
    _applyId() {
        if (this.idStr) {
            this._renderer.setAttribute(this._elRef.nativeElement, 'id', this.idStr);
        }
        else {
            this._renderer.setAttribute(this._elRef.nativeElement, 'id', `banner-alert-${nextId$6++}`);
        }
    }
    /**
     * sets openBannerAlert to false when triggered
     */
    closeBannerAlert() {
        this._announcer.announce(this.announceCloseMessage, 100);
        setTimeout(() => {
            this.openBannerAlert = false;
            this.openBannerAlertChange.emit(this.openBannerAlert);
        }, 200);
    }
    _focusAfterClose() {
        const focusEl = this.closeFocusSelector ? this._web.document.querySelector(this.closeFocusSelector) : null;
        if (focusEl) {
            focusEl.focus();
        }
        else if (this.isFocusOnFirstFocusableElement) {
            this._body.querySelector(this._focusableElementStr).focus();
        }
        else {
            this._focusOnFirstFocusableElementInDomAfterCurrentElement();
        }
    }
    _focusOnFirstFocusableElementInDomAfterCurrentElement() {
        // const element = this._elRef.nativeElement;
        // Get list of all focusable elements inside body (including current Banner Alert component so index can be calculated)
        const focusableElements = this._body.querySelectorAll(`#${this.idStr}, ${this._focusableElementStr}`);
        // Filter out focusable elements that are inside current Banner Alert component
        const filteredFocusableElements = this._filterElementsOutsideCurrentElement(this._elRef.nativeElement, focusableElements);
        // Get index of current element in the filtered list
        const currentElementIndex = Array.from(filteredFocusableElements).indexOf(this._elRef.nativeElement);
        // If there are focusable elements outside current element, focus on next focusable element, else focus on body
        if (filteredFocusableElements.length && currentElementIndex !== -1) {
            // index of element to focus on
            // - will be next element if current element is not the last element in the list
            // - will be previous element if current element is the last element in the list
            // - will be 0 if there is only one focusable element in the list
            const newElementIndex = currentElementIndex + 1 < filteredFocusableElements.length ? currentElementIndex + 1 : currentElementIndex - 1 > 0 ? currentElementIndex - 1 : 0;
            if (newElementIndex === currentElementIndex) {
                this._body.focus();
            }
            else {
                filteredFocusableElements[newElementIndex].focus();
            }
        }
    }
    /**
     * Filters out elements that are inside current element
     * @param element current element
     * @param nodeList list of all focusable elements inside body + current Banner Alert component
     * @returns list of focusable elements that are outside current element
     */
    _filterElementsOutsideCurrentElement(element, nodeList) {
        const filteredNodeList = Array.from(nodeList).filter(node => {
            if (!element.contains(node) || node === element) {
                return node;
            }
        });
        return filteredNodeList;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBannerAlert, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CdsWebService }, { token: CdsAnnouncerService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsBannerAlert, selector: "cds-banner-alert", inputs: { announceCloseMessage: "announceCloseMessage", bannerAlertBgColor: "bannerAlertBgColor", bannerAlertColor: "bannerAlertColor", bannerAlertType: "bannerAlertType", closeFocusSelector: "closeFocusSelector", idStr: "idStr", isFocusOnFirstFocusableElement: "isFocusOnFirstFocusableElement", openBannerAlert: "openBannerAlert" }, outputs: { openBannerAlertChange: "openBannerAlertChange" }, host: { attributes: { "role": "alert" }, properties: { "style.background-color": "this.styles", "style.border-left-color": "this.color", "class": "this.classes", "style.display": "this.display" }, classAttribute: "cds-banner-alert" }, ngImport: i0, template: `
    <ng-content ></ng-content>
  `, isInline: true, styles: [":host{display:block;box-shadow:0 0 10px #0000001f;padding:8px;background-color:var(--cds-banner-alert-bg-color, #ffffff);border-radius:4px;min-height:44px}:host.critical-alert,:host.success-alert,:host.marketing-alert,:host.technical-alert{border-left:.42rem solid transparent}:host ::ng-deep .cds-banner-alert-content-parent{display:flex;gap:8px;font-size:14px;line-height:18px}:host ::ng-deep .cds-banner-alert-content-wrap{flex:1 1 auto}:host ::ng-deep .cds-banner-alert-content-title,:host ::ng-deep .cds-banner-alert-content-title2{font-weight:700;margin-bottom:.2em}:host ::ng-deep .cds-banner-alert-content{margin:0;font-family:var(--cds-banner-alert-body-font, \"Interstate_Light\"),sans-serif}:host ::ng-deep .cds-banner-alert-content .inlineLink-button,:host ::ng-deep .cds-banner-alert-content .inline-link-button{padding:0!important}:host ::ng-deep .cds-banner-close-button{border:none;background-color:transparent;padding:4px;--cds-icon-path-fill: var(--cds-banner-alert-close-button-color)}:host ::ng-deep .cds-banner-close-button:hover{--cds-icon-path-fill: var(--cds-banner-alert-close-button-hover-icon-color)}:host.critical-alert{border-left-color:var(--cds-banner-alert-critical-border-left-color, var(--cds-banner-alert-critical-color))}:host.critical-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-critical-icon-color, var(--cds-banner-alert-critical-color) )}:host.critical-alert ::ng-deep .cds-banner-alert-content-title,:host.critical-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-critical-title-color)}:host.success-alert{border-left-color:var(--cds-banner-alert-success-border-left-color, var(--cds-banner-alert-success-color))}:host.success-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-success-icon-color, var(--cds-banner-alert-success-color) )}:host.success-alert ::ng-deep .cds-banner-alert-content-title,:host.success-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-success-title-color)}:host.marketing-alert{border-left-color:var(--cds-banner-alert-marketing-border-left-color, var(--cds-banner-alert-marketing-color))}:host.marketing-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-marketing-icon-color, var(--cds-banner-alert-marketing-color) )}:host.marketing-alert ::ng-deep .cds-banner-alert-content-title,:host.marketing-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-marketing-title-color)}:host.technical-alert{border-left-color:var(--cds-banner-alert-technical-border-left-color, var(--cds-banner-alert-technical-color))}:host.technical-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-technical-icon-color, var(--cds-banner-alert-technical-color) )}:host.technical-alert ::ng-deep .cds-banner-alert-content-title,:host.technical-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-technical-title-color)}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBannerAlert, decorators: [{
            type: Component,
            args: [{ selector: 'cds-banner-alert', host: { class: 'cds-banner-alert', role: 'alert' }, template: `
    <ng-content ></ng-content>
  `, styles: [":host{display:block;box-shadow:0 0 10px #0000001f;padding:8px;background-color:var(--cds-banner-alert-bg-color, #ffffff);border-radius:4px;min-height:44px}:host.critical-alert,:host.success-alert,:host.marketing-alert,:host.technical-alert{border-left:.42rem solid transparent}:host ::ng-deep .cds-banner-alert-content-parent{display:flex;gap:8px;font-size:14px;line-height:18px}:host ::ng-deep .cds-banner-alert-content-wrap{flex:1 1 auto}:host ::ng-deep .cds-banner-alert-content-title,:host ::ng-deep .cds-banner-alert-content-title2{font-weight:700;margin-bottom:.2em}:host ::ng-deep .cds-banner-alert-content{margin:0;font-family:var(--cds-banner-alert-body-font, \"Interstate_Light\"),sans-serif}:host ::ng-deep .cds-banner-alert-content .inlineLink-button,:host ::ng-deep .cds-banner-alert-content .inline-link-button{padding:0!important}:host ::ng-deep .cds-banner-close-button{border:none;background-color:transparent;padding:4px;--cds-icon-path-fill: var(--cds-banner-alert-close-button-color)}:host ::ng-deep .cds-banner-close-button:hover{--cds-icon-path-fill: var(--cds-banner-alert-close-button-hover-icon-color)}:host.critical-alert{border-left-color:var(--cds-banner-alert-critical-border-left-color, var(--cds-banner-alert-critical-color))}:host.critical-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-critical-icon-color, var(--cds-banner-alert-critical-color) )}:host.critical-alert ::ng-deep .cds-banner-alert-content-title,:host.critical-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-critical-title-color)}:host.success-alert{border-left-color:var(--cds-banner-alert-success-border-left-color, var(--cds-banner-alert-success-color))}:host.success-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-success-icon-color, var(--cds-banner-alert-success-color) )}:host.success-alert ::ng-deep .cds-banner-alert-content-title,:host.success-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-success-title-color)}:host.marketing-alert{border-left-color:var(--cds-banner-alert-marketing-border-left-color, var(--cds-banner-alert-marketing-color))}:host.marketing-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-marketing-icon-color, var(--cds-banner-alert-marketing-color) )}:host.marketing-alert ::ng-deep .cds-banner-alert-content-title,:host.marketing-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-marketing-title-color)}:host.technical-alert{border-left-color:var(--cds-banner-alert-technical-border-left-color, var(--cds-banner-alert-technical-color))}:host.technical-alert ::ng-deep .cds-banner-alert-icon{--cds-icon-path-fill: var( --cds-banner-alert-technical-icon-color, var(--cds-banner-alert-technical-color) )}:host.technical-alert ::ng-deep .cds-banner-alert-content-title,:host.technical-alert ::ng-deep .cds-banner-alert-content-title2{color:var(--cds-banner-alert-technical-title-color)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CdsWebService }, { type: CdsAnnouncerService }], propDecorators: { announceCloseMessage: [{
                type: Input
            }], bannerAlertBgColor: [{
                type: Input
            }], bannerAlertColor: [{
                type: Input
            }], bannerAlertType: [{
                type: Input
            }], closeFocusSelector: [{
                type: Input
            }], idStr: [{
                type: Input
            }], isFocusOnFirstFocusableElement: [{
                type: Input
            }], openBannerAlert: [{
                type: Input
            }], openBannerAlertChange: [{
                type: Output
            }], styles: [{
                type: HostBinding,
                args: ['style.background-color']
            }], color: [{
                type: HostBinding,
                args: ['style.border-left-color']
            }], classes: [{
                type: HostBinding,
                args: ['class']
            }], display: [{
                type: HostBinding,
                args: ['style.display']
            }] } });

const MODULES$t = [CommonModule, RouterModule];
class CdsBannerAlertModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBannerAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsBannerAlertModule, declarations: [CdsBannerAlert], imports: [CommonModule, RouterModule], exports: [CdsBannerAlert] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBannerAlertModule, imports: [MODULES$t] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBannerAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$t],
                    declarations: [CdsBannerAlert],
                    exports: [CdsBannerAlert]
                }]
        }] });

class CdsCTA {
    set size(sizes) {
        this.small = this.medium = this.large = this.iconSmall = this.iconMed = this.iconLarge = false;
        switch (sizes) {
            case 'small':
                this.small = true;
                break;
            case 'medium':
                this.medium = true;
                break;
            case 'large':
                this.large = true;
                break;
            case 'icon-small':
                this.iconSmall = true;
                break;
            case 'icon-medium':
                this.iconMed = true;
                break;
            case 'icon-large':
                this.iconLarge = true;
                break;
        }
    }
    set cdsCta(buttonType) {
        if (!buttonType)
            return;
        this.primary = this.alternatePrimary = this.secondary = this.tertiary = false;
        switch (buttonType) {
            case 'primary':
                this.primary = true;
                break;
            case 'alternatePrimary':
                this.alternatePrimary = true;
                break;
            case 'whitePrimary':
                this.whitePrimary = true;
                break;
            case 'secondary':
                this.secondary = true;
                this.arrowRight = true;
                break;
            case 'tertiary':
                this.tertiary = true;
                break;
        }
    }
    set customerType(customer) {
        switch (customer) {
            case 'gold':
                this.gold = true;
                break;
            case 'priority':
                this.priority = true;
                break;
        }
    }
    constructor(_href) {
        this._href = _href;
        // Arrows types
        this.arrowRight = false;
        this.arrowLeft = false;
        this.small = false;
        this.medium = true;
        this.large = false;
        this.iconSmall = false;
        this.iconMed = false;
        this.iconLarge = false;
        // Button types
        this.primary = true;
        this.alternatePrimary = false;
        this.whitePrimary = false;
        this.secondary = false;
        this.tertiary = false;
        // Customer types
        this.gold = false;
        this.priority = false;
        this.appliedHref = this._href;
    }
    set JFPToken(JFPToken) {
        this._isJfpToken = coerceBooleanProperty(JFPToken);
        this.appliedHref = this.getModifiedJfpHref(this._isJfpToken);
    }
    /**
     * Alternate way of passing href.
     *
     * NOTE:
     * - The default href gets modified upon initialization, so
     * if you pass a variable to the href ( example: href="{{myUrlVar}}" )
     * the variable association will be overridden.
     *
     * - In order to pass variables to the href, you can use ctaHref
     * instead ( example: [ctaHref]="myUrlVar" )
     */
    set ctaHref(url) {
        this._href = url;
        this.appliedHref = this.getModifiedJfpHref(this._isJfpToken);
    }
    getModifiedJfpHref(isJFPToken) {
        return isJFPToken ? this.addJfpToken(this._href) : this._href;
    }
    addJfpToken(href) {
        // If the target is set to _blank
        if (!href.match('JFP_TOKEN')) {
            // Check if there is a JFP Token in session storage
            const jfpToken = sessionStorage.sessionJFP_TOKEN;
            if (jfpToken) {
                // Append the correct prefix
                href += href.match(/\?/g) ? '&' : '?';
                // Append the JFP_TOKEN
                href += 'JFP_TOKEN=' + jfpToken;
            }
        }
        return href;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCTA, deps: [{ token: 'href', attribute: true, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsCTA, selector: "button[cdsCta],button[cds-cta],a[cdsCta],a[cds-cta]", inputs: { arrowRight: "arrowRight", arrowLeft: "arrowLeft", size: "size", cdsCta: "cdsCta", customerType: "customerType", JFPToken: "JFPToken", ctaHref: "ctaHref" }, host: { properties: { "class.cds-cta-chevron-right": "this.arrowRight", "class.cds-cta-chevron-left": "this.arrowLeft", "class.cds-cta-sm": "this.small", "class.cds-cta-md": "this.medium", "class.cds-cta-lg": "this.large", "class.cds-cta-icon-sm": "this.iconSmall", "class.cds-cta-icon-md": "this.iconMed", "class.cds-cta-icon-lg": "this.iconLarge", "class.cds-cta-primary": "this.primary", "class.cds-cta-alternatePrimary": "this.alternatePrimary", "class.cds-cta-whitePrimary": "this.whitePrimary", "class.cds-cta-secondary": "this.secondary", "class.cds-cta-tertiary": "this.tertiary", "class.cds-cta-gold": "this.gold", "class.cds-cta-priority": "this.priority", "attr.href": "this.appliedHref" }, classAttribute: "cds-cta" }, exportAs: ["cdsCta"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCTA, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[cdsCta],button[cds-cta],a[cdsCta],a[cds-cta]',
                    exportAs: 'cdsCta',
                    host: { class: 'cds-cta' }
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Attribute,
                    args: ['href']
                }] }], propDecorators: { arrowRight: [{
                type: HostBinding,
                args: ['class.cds-cta-chevron-right']
            }, {
                type: Input
            }], arrowLeft: [{
                type: HostBinding,
                args: ['class.cds-cta-chevron-left']
            }, {
                type: Input
            }], small: [{
                type: HostBinding,
                args: ['class.cds-cta-sm']
            }], medium: [{
                type: HostBinding,
                args: ['class.cds-cta-md']
            }], large: [{
                type: HostBinding,
                args: ['class.cds-cta-lg']
            }], iconSmall: [{
                type: HostBinding,
                args: ['class.cds-cta-icon-sm']
            }], iconMed: [{
                type: HostBinding,
                args: ['class.cds-cta-icon-md']
            }], iconLarge: [{
                type: HostBinding,
                args: ['class.cds-cta-icon-lg']
            }], size: [{
                type: Input
            }], primary: [{
                type: HostBinding,
                args: ['class.cds-cta-primary']
            }], alternatePrimary: [{
                type: HostBinding,
                args: ['class.cds-cta-alternatePrimary']
            }], whitePrimary: [{
                type: HostBinding,
                args: ['class.cds-cta-whitePrimary']
            }], secondary: [{
                type: HostBinding,
                args: ['class.cds-cta-secondary']
            }], tertiary: [{
                type: HostBinding,
                args: ['class.cds-cta-tertiary']
            }], cdsCta: [{
                type: Input
            }], gold: [{
                type: HostBinding,
                args: ['class.cds-cta-gold']
            }], priority: [{
                type: HostBinding,
                args: ['class.cds-cta-priority']
            }], customerType: [{
                type: Input
            }], appliedHref: [{
                type: HostBinding,
                args: ['attr.href']
            }], JFPToken: [{
                type: Input
            }], ctaHref: [{
                type: Input
            }] } });

const COMPONENTS$M = [CdsCTA];
const MODULES$s = [CommonModule];
class CdsCTAModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCTAModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCTAModule, declarations: [CdsCTA], imports: [CommonModule], exports: [CdsCTA] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCTAModule, imports: [MODULES$s] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCTAModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$M],
                    imports: [...MODULES$s],
                    exports: [...COMPONENTS$M]
                }]
        }] });

/**
 * @description This is the column component and is used to leverage the grid
 * from Bootstrap.
 */
// TODO: Kyle - we should delete this in favor of just using the "row" class
class CdsRowComponent {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRowComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsRowComponent, selector: "cds-row", inputs: { classes: "classes" }, ngImport: i0, template: `
    <div class="row {{ classes }}">
      <ng-content></ng-content>
    </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-row',
                    template: `
    <div class="row {{ classes }}">
      <ng-content></ng-content>
    </div>
  `
                }]
        }], propDecorators: { classes: [{
                type: Input
            }] } });

/**
 * @Component: Column
 * @Section: GridLayouts
 * This is the column component and is used to leverage the grid
 * from Bootstrap.
 */
class CdsColumnComponent {
    constructor() {
        this.columnClasses = [];
        this.colClasses = '';
    }
    ngOnChanges() {
        const classList = [];
        // Columns
        if (this.xs) {
            classList.push(`col-${this.xs}`);
        }
        if (this.sm) {
            classList.push(`col-sm-${this.sm}`);
        }
        if (this.md) {
            classList.push(`col-md-${this.md}`);
        }
        if (this.lg) {
            classList.push(`col-lg-${this.lg}`);
        }
        if (this.xl) {
            classList.push(`col-xl-${this.xl}`);
        }
        if (this.xxl) {
            classList.push(`col-xxl-${this.xxl}`);
        }
        // Offset
        if (this.offXs || this.offXs === 0) {
            classList.push(`offset-${this.offXs}`);
        }
        if (this.offSm || this.offSm === 0) {
            classList.push(`offset-sm-${this.offSm}`);
        }
        if (this.offMd || this.offMd === 0) {
            classList.push(`offset-md-${this.offMd}`);
        }
        if (this.offLg || this.offLg === 0) {
            classList.push(`offset-lg-${this.offLg}`);
        }
        if (this.offXl || this.offXl === 0) {
            classList.push(`offset-xl-${this.offXl}`);
        }
        if (this.offXxl || this.offXxl === 0) {
            classList.push(`offset-xxl-${this.offXxl}`);
        }
        // Classes
        if (this.classes) {
            classList.push(this.classes);
        }
        this.colClasses = classList.join(' ');
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsColumnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsColumnComponent, selector: "cds-column", inputs: { xs: "xs", sm: "sm", md: "md", lg: "lg", xl: "xl", xxl: "xxl", offXs: "offXs", offSm: "offSm", offMd: "offMd", offLg: "offLg", offXl: "offXl", offXxl: "offXxl", classes: "classes" }, host: { properties: { "class": "this.colClasses" }, classAttribute: "cds-column" }, usesOnChanges: true, ngImport: i0, template: `
      <ng-content></ng-content>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-column',
                    host: { class: 'cds-column' },
                    template: `
      <ng-content></ng-content>
  `
                }]
        }], ctorParameters: () => [], propDecorators: { xs: [{
                type: Input
            }], sm: [{
                type: Input
            }], md: [{
                type: Input
            }], lg: [{
                type: Input
            }], xl: [{
                type: Input
            }], xxl: [{
                type: Input
            }], offXs: [{
                type: Input
            }], offSm: [{
                type: Input
            }], offMd: [{
                type: Input
            }], offLg: [{
                type: Input
            }], offXl: [{
                type: Input
            }], offXxl: [{
                type: Input
            }], classes: [{
                type: Input
            }], colClasses: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CdsGridModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsGridModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsGridModule, declarations: [CdsRowComponent, CdsColumnComponent], imports: [CommonModule], exports: [CdsRowComponent, CdsColumnComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsGridModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsGridModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    exports: [CdsRowComponent, CdsColumnComponent],
                    declarations: [CdsRowComponent, CdsColumnComponent]
                }]
        }] });

/**
* @Component: JAMP
* @description This component generates a JAMP (Just A Moment Please) element that can be shown during loading
*/
class JampComponent {
    set useGif(value) {
        this._useGif = value;
        this.loadingClass = this._useGif ? '' : 'loading';
    }
    ;
    get useGif() { return this._useGif; }
    ;
    set visible(show) {
        this._visible = coerceBooleanProperty(show);
        if (this._visible) {
            this.show();
            this.announcer.announce(this.waitMessage);
        }
        else {
            this.hide();
        }
    }
    get visible() { return this._visible; }
    ;
    set pagelevel(value) {
        this.isPageLevel = value;
    }
    ;
    get pagelevel() { return this.isPageLevel; }
    ;
    /**
    * @property additionalCssClasses
    * @description Adds the given string as additional classes to the class attribute of the jamp. This will replaces
    * any CSS classes not in DDL standard with the string provided in the parameter.
    * @return string
    * @twoway false
    */
    set additionalCssClasses(value) {
        this._additionalCssClasses = value;
        this.updateCssClasses();
    }
    get additionalCssClasses() {
        return this._additionalCssClasses;
    }
    constructor(announcer, _renderer, _doc) {
        this.announcer = announcer;
        this._renderer = _renderer;
        this._doc = _doc;
        this.isHidden = true;
        this.isPageLevel = false;
        this._additionalCssClasses = '';
        this.jampCssClass = '';
        this.loadingClass = '';
        this._document = this._doc;
    }
    ngOnInit() {
        if (this.pagelevel && this.visible) {
            this.isHidden = true;
            this.show();
        }
        this.updateCssClasses();
    }
    ngOnChanges(changes) {
        if (changes.hasOwnProperty('visible') && changes.visible.previousValue
            && !changes.visible.currentValue) {
            this.announcer.announce(this.readyMessage);
        }
    }
    updateCssClasses() {
        const classSet = this._additionalCssClasses.split(' ');
        classSet.push('jamp-css');
        if (this.isCenterAligned)
            classSet.push('jamp-center-css');
        this.jampCssClass = classSet.join(' ');
    }
    show() {
        if (this.isHidden) {
            this.isHidden = false;
            if (this.isPageLevel) {
                this._previousFocusedElement = this._document.activeElement;
                this._isPageLevelJampVisible = true;
                this._setBodyAttributes(this._isPageLevelJampVisible);
                setTimeout(() => {
                    this.jampPageLevelContent?.nativeElement?.focus();
                    this._isDomAriaHidden = true;
                }, 100);
            }
            else {
                this.announcer.announce(this.waitMessage);
            }
        }
    }
    /**
    * @name hide
    * @description Makes the JAMP hidden.
    * @return Void
    */
    hide() {
        if (!this.isHidden) {
            this.announcer.announce(this.readyMessage, 200);
            this.isHidden = true;
            this._isDomAriaHidden = false;
            if (this.isPageLevel) {
                this._setBodyAttributes(!this.isHidden);
                setTimeout(() => {
                    this._previousFocusedElement?.focus();
                    this._isPageLevelJampVisible = false;
                }, 10);
            }
        }
    }
    _setBodyAttributes(isJampVisible) {
        if (isJampVisible && this.isPageLevel) {
            this._renderer.addClass(this._document.body, 'cds-jamp-open');
        }
        else {
            this._renderer.removeClass(this._document.body, 'cds-jamp-open');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: JampComponent, deps: [{ token: i1$1.LiveAnnouncer }, { token: i0.Renderer2 }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: JampComponent, selector: "cds-jamp", inputs: { useGif: "useGif", visible: "visible", pagelevel: "pagelevel", htmlContent: "htmlContent", additionalCssClasses: "additionalCssClasses", waitMessage: "waitMessage", readyMessage: "readyMessage", isCenterAligned: "isCenterAligned" }, host: { properties: { "class.hidden": "this.isHidden", "class.jamp-page-level": "this.isPageLevel" } }, viewQueries: [{ propertyName: "jampPageLevelContent", first: true, predicate: ["jampPageLevelContent"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div class="fillHeight" *ngIf="!isPageLevel; else pageLevelJamp">
      <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')">
        <div class="jamp {{jampCssClass}}">
          <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
          <div class="message" [innerHTML]="htmlContent">
            <ng-content></ng-content>
          </div>
        </div>
      </div>
    </div>
    <ng-template #pageLevelJamp>
      <div class="fillHeight" *ngIf="_isPageLevelJampVisible" cdsAriaHideOuterDom [isOuterDomAriaHidden]="_isDomAriaHidden">
        <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')" cdkTrapFocus>
          <div #jampPageLevelContent class="jamp {{jampCssClass}}" tabindex="0" role="dialog">
            <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
            <div class="message" [innerHTML]="htmlContent">
              <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
            </div>
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template #contentTemplate><ng-content></ng-content></ng-template>
    `, isInline: true, styles: [".jamp.white .img{background-image:url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjIuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAyNSAyNSIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjUgMjU7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojQ0NDQ0NDO30KCS5zdDF7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojMDU2REFFO30KPC9zdHlsZT4KPHBhdGggaWQ9IkJhc2UiIGNsYXNzPSJzdDAiIGQ9Ik0xMi41LDBDMTkuNCwwLDI1LDUuNiwyNSwxMi41UzE5LjQsMjUsMTIuNSwyNVMwLDE5LjQsMCwxMi41UzUuNiwwLDEyLjUsMHogTTEyLjUsMgoJQzYuNywyLDIsNi43LDIsMTIuNVM2LjcsMjMsMTIuNSwyM1MyMywxOC4zLDIzLDEyLjVTMTguMywyLDEyLjUsMnoiLz4KPHBhdGggaWQ9IkNvbXBsZXRlZCIgY2xhc3M9InN0MSIgZD0iTTMuNiwyMS4zTDUsMTkuOWMyLDIsNC42LDMuMSw3LjUsMy4xQzE4LjMsMjMsMjMsMTguMywyMywxMi41QzIzLDYuOCwxOC40LDIuMiwxMi44LDJsLTAuMywwVjAKCUMxOS40LDAsMjUsNS42LDI1LDEyLjVTMTkuNCwyNSwxMi41LDI1Yy0zLjMsMC02LjMtMS4zLTguNi0zLjVMMy42LDIxLjN6Ii8+Cjwvc3ZnPgo=)}.jamp.white .message{color:#fff}.jamp .img{background-image:url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjIuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAyNSAyNSIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjUgMjU7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojQ0NDQ0NDO30KCS5zdDF7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojMDU2REFFO30KPC9zdHlsZT4KPHBhdGggaWQ9IkJhc2UiIGNsYXNzPSJzdDAiIGQ9Ik0xMi41LDBDMTkuNCwwLDI1LDUuNiwyNSwxMi41UzE5LjQsMjUsMTIuNSwyNVMwLDE5LjQsMCwxMi41UzUuNiwwLDEyLjUsMHogTTEyLjUsMgoJQzYuNywyLDIsNi43LDIsMTIuNVM2LjcsMjMsMTIuNSwyM1MyMywxOC4zLDIzLDEyLjVTMTguMywyLDEyLjUsMnoiLz4KPHBhdGggaWQ9IkNvbXBsZXRlZCIgY2xhc3M9InN0MSIgZD0iTTMuNiwyMS4zTDUsMTkuOWMyLDIsNC42LDMuMSw3LjUsMy4xQzE4LjMsMjMsMjMsMTguMywyMywxMi41QzIzLDYuOCwxOC40LDIuMiwxMi44LDJsLTAuMywwVjAKCUMxOS40LDAsMjUsNS42LDI1LDEyLjVTMTkuNCwyNSwxMi41LDI1Yy0zLjMsMC02LjMtMS4zLTguNi0zLjVMMy42LDIxLjN6Ii8+Cjwvc3ZnPgo=);background-repeat:no-repeat;animation:spin 1.16s infinite linear;background-position:0 0;background-size:contain;padding-top:5px;margin-right:3px;width:34px;height:34px;display:inline-block}.jamp .message{position:relative;top:-9px}.jamp.jamp-css .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.1.0%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.Drop_x0020_Shadow%7Bfill%3Anone%3B%7D%0A%09.Round_x0020_Corners_x0020_2_x0020_pt%7Bfill%3A%23FFFFFF%3Bstroke%3A%23000000%3Bstroke-miterlimit%3A10%3B%7D%0A%09.Live_x0020_Reflect_x0020_X%7Bfill%3Anone%3B%7D%0A%09.Bevel_x0020_Soft%7Bfill%3Aurl%28%23SVGID_1_%29%3B%7D%0A%09.Dusk%7Bfill%3A%23FFFFFF%3B%7D%0A%09.Foliage_GS%7Bfill%3A%23FFDD00%3B%7D%0A%09.Pompadour_GS%7Bfill-rule%3Aevenodd%3Bclip-rule%3Aevenodd%3Bfill%3A%2344ADE2%3B%7D%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23056EAE%3B%7D%0A%3C/style%3E%0A%3ClinearGradient%20id%3D%22SVGID_1_%22%20gradientUnits%3D%22userSpaceOnUse%22%20x1%3D%220%22%20y1%3D%220%22%20x2%3D%220.7071%22%20y2%3D%220.7071%22%3E%0A%09%3Cstop%20%20offset%3D%220%22%20style%3D%22stop-color%3A%23DEDFE3%22/%3E%0A%09%3Cstop%20%20offset%3D%220.1738%22%20style%3D%22stop-color%3A%23D8D9DD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.352%22%20style%3D%22stop-color%3A%23C9CACD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.5323%22%20style%3D%22stop-color%3A%23B4B5B8%22/%3E%0A%09%3Cstop%20%20offset%3D%220.7139%22%20style%3D%22stop-color%3A%23989A9C%22/%3E%0A%09%3Cstop%20%20offset%3D%220.8949%22%20style%3D%22stop-color%3A%23797C7E%22/%3E%0A%09%3Cstop%20%20offset%3D%221%22%20style%3D%22stop-color%3A%23656B6C%22/%3E%0A%3C/linearGradient%3E%0A%3Cg%3E%0A%09%3Crect%20x%3D%220%22%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat;animation:spin 1.16s infinite linear}.jamp.jamp-css.white .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23FFFFFF%3B%7D%0A%3C/style%3E%0A%3Cg%3E%0A%09%3Crect%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}:host.jamp-page-level{background:rgba(255,255,255,.9);position:fixed;z-index:100;inset:0}:host.jamp-page-level .row{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}:host.jamp-page-level .jamp:focus-visible{outline:none}.jamp-center-css{width:calc(100% - 60px);max-height:calc(100% - 60px);position:absolute!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)}.jamp{position:relative!important;top:50%!important;left:0%!important;text-align:center!important;transform:translateY(-50%)!important;margin:0}.fillHeight{min-height:100%;height:100%}.position-initial{position:initial}.citi-modal :host .jamp{margin:30px 0 10px}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: CdsAriaHideOuterDomDirective, selector: "[cdsAriaHideOuterDom]", inputs: ["isOuterDomAriaHidden", "outerDomExceptions"], exportAs: ["cdsAriaHideOuterDom"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: JampComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-jamp', template: `
    <div class="fillHeight" *ngIf="!isPageLevel; else pageLevelJamp">
      <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')">
        <div class="jamp {{jampCssClass}}">
          <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
          <div class="message" [innerHTML]="htmlContent">
            <ng-content></ng-content>
          </div>
        </div>
      </div>
    </div>
    <ng-template #pageLevelJamp>
      <div class="fillHeight" *ngIf="_isPageLevelJampVisible" cdsAriaHideOuterDom [isOuterDomAriaHidden]="_isDomAriaHidden">
        <div class="alignmentEl fillHeight" [ngClass]="(isCenterAligned ? 'position-initial' : '')" cdkTrapFocus>
          <div #jampPageLevelContent class="jamp {{jampCssClass}}" tabindex="0" role="dialog">
            <div class="img {{loadingClass}}" role="img" [attr.aria-label]="waitMessage"></div>
            <div class="message" [innerHTML]="htmlContent">
              <ng-container *ngTemplateOutlet="contentTemplate"></ng-container>
            </div>
          </div>
        </div>
      </div>
    </ng-template>
    <ng-template #contentTemplate><ng-content></ng-content></ng-template>
    `, styles: [".jamp.white .img{background-image:url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjIuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAyNSAyNSIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjUgMjU7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojQ0NDQ0NDO30KCS5zdDF7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojMDU2REFFO30KPC9zdHlsZT4KPHBhdGggaWQ9IkJhc2UiIGNsYXNzPSJzdDAiIGQ9Ik0xMi41LDBDMTkuNCwwLDI1LDUuNiwyNSwxMi41UzE5LjQsMjUsMTIuNSwyNVMwLDE5LjQsMCwxMi41UzUuNiwwLDEyLjUsMHogTTEyLjUsMgoJQzYuNywyLDIsNi43LDIsMTIuNVM2LjcsMjMsMTIuNSwyM1MyMywxOC4zLDIzLDEyLjVTMTguMywyLDEyLjUsMnoiLz4KPHBhdGggaWQ9IkNvbXBsZXRlZCIgY2xhc3M9InN0MSIgZD0iTTMuNiwyMS4zTDUsMTkuOWMyLDIsNC42LDMuMSw3LjUsMy4xQzE4LjMsMjMsMjMsMTguMywyMywxMi41QzIzLDYuOCwxOC40LDIuMiwxMi44LDJsLTAuMywwVjAKCUMxOS40LDAsMjUsNS42LDI1LDEyLjVTMTkuNCwyNSwxMi41LDI1Yy0zLjMsMC02LjMtMS4zLTguNi0zLjVMMy42LDIxLjN6Ii8+Cjwvc3ZnPgo=)}.jamp.white .message{color:#fff}.jamp .img{background-image:url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI0LjIuMSwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCAyNSAyNSIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgMjUgMjU7IiB4bWw6c3BhY2U9InByZXNlcnZlIj4KPHN0eWxlIHR5cGU9InRleHQvY3NzIj4KCS5zdDB7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojQ0NDQ0NDO30KCS5zdDF7ZmlsbC1ydWxlOmV2ZW5vZGQ7Y2xpcC1ydWxlOmV2ZW5vZGQ7ZmlsbDojMDU2REFFO30KPC9zdHlsZT4KPHBhdGggaWQ9IkJhc2UiIGNsYXNzPSJzdDAiIGQ9Ik0xMi41LDBDMTkuNCwwLDI1LDUuNiwyNSwxMi41UzE5LjQsMjUsMTIuNSwyNVMwLDE5LjQsMCwxMi41UzUuNiwwLDEyLjUsMHogTTEyLjUsMgoJQzYuNywyLDIsNi43LDIsMTIuNVM2LjcsMjMsMTIuNSwyM1MyMywxOC4zLDIzLDEyLjVTMTguMywyLDEyLjUsMnoiLz4KPHBhdGggaWQ9IkNvbXBsZXRlZCIgY2xhc3M9InN0MSIgZD0iTTMuNiwyMS4zTDUsMTkuOWMyLDIsNC42LDMuMSw3LjUsMy4xQzE4LjMsMjMsMjMsMTguMywyMywxMi41QzIzLDYuOCwxOC40LDIuMiwxMi44LDJsLTAuMywwVjAKCUMxOS40LDAsMjUsNS42LDI1LDEyLjVTMTkuNCwyNSwxMi41LDI1Yy0zLjMsMC02LjMtMS4zLTguNi0zLjVMMy42LDIxLjN6Ii8+Cjwvc3ZnPgo=);background-repeat:no-repeat;animation:spin 1.16s infinite linear;background-position:0 0;background-size:contain;padding-top:5px;margin-right:3px;width:34px;height:34px;display:inline-block}.jamp .message{position:relative;top:-9px}.jamp.jamp-css .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.1.0%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.Drop_x0020_Shadow%7Bfill%3Anone%3B%7D%0A%09.Round_x0020_Corners_x0020_2_x0020_pt%7Bfill%3A%23FFFFFF%3Bstroke%3A%23000000%3Bstroke-miterlimit%3A10%3B%7D%0A%09.Live_x0020_Reflect_x0020_X%7Bfill%3Anone%3B%7D%0A%09.Bevel_x0020_Soft%7Bfill%3Aurl%28%23SVGID_1_%29%3B%7D%0A%09.Dusk%7Bfill%3A%23FFFFFF%3B%7D%0A%09.Foliage_GS%7Bfill%3A%23FFDD00%3B%7D%0A%09.Pompadour_GS%7Bfill-rule%3Aevenodd%3Bclip-rule%3Aevenodd%3Bfill%3A%2344ADE2%3B%7D%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23056EAE%3B%7D%0A%3C/style%3E%0A%3ClinearGradient%20id%3D%22SVGID_1_%22%20gradientUnits%3D%22userSpaceOnUse%22%20x1%3D%220%22%20y1%3D%220%22%20x2%3D%220.7071%22%20y2%3D%220.7071%22%3E%0A%09%3Cstop%20%20offset%3D%220%22%20style%3D%22stop-color%3A%23DEDFE3%22/%3E%0A%09%3Cstop%20%20offset%3D%220.1738%22%20style%3D%22stop-color%3A%23D8D9DD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.352%22%20style%3D%22stop-color%3A%23C9CACD%22/%3E%0A%09%3Cstop%20%20offset%3D%220.5323%22%20style%3D%22stop-color%3A%23B4B5B8%22/%3E%0A%09%3Cstop%20%20offset%3D%220.7139%22%20style%3D%22stop-color%3A%23989A9C%22/%3E%0A%09%3Cstop%20%20offset%3D%220.8949%22%20style%3D%22stop-color%3A%23797C7E%22/%3E%0A%09%3Cstop%20%20offset%3D%221%22%20style%3D%22stop-color%3A%23656B6C%22/%3E%0A%3C/linearGradient%3E%0A%3Cg%3E%0A%09%3Crect%20x%3D%220%22%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat;animation:spin 1.16s infinite linear}.jamp.jamp-css.white .loading{background-image:url(data:image/svg+xml;charset=US-ASCII,%3C%3Fxml%20version%3D%221.0%22%20encoding%3D%22utf-8%22%3F%3E%0A%3C%21--%20Generator%3A%20Adobe%20Illustrator%2019.2.1%2C%20SVG%20Export%20Plug-In%20.%20SVG%20Version%3A%206.00%20Build%200%29%20%20--%3E%0A%3Csvg%20version%3D%221.1%22%20id%3D%22Layer_1%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20xmlns%3Axlink%3D%22http%3A//www.w3.org/1999/xlink%22%20x%3D%220px%22%20y%3D%220px%22%0A%09%20viewBox%3D%220%200%2030%2030%22%20style%3D%22enable-background%3Anew%200%200%2030%2030%3B%22%20xml%3Aspace%3D%22preserve%22%3E%0A%3Cstyle%20type%3D%22text/css%22%3E%0A%09.st0%7Bfill%3Anone%3B%7D%0A%09.st1%7Bfill%3A%23FFFFFF%3B%7D%0A%3C/style%3E%0A%3Cg%3E%0A%09%3Crect%20class%3D%22st0%22%20width%3D%2230%22%20height%3D%2230%22/%3E%0A%09%3Cpath%20class%3D%22st1%22%20d%3D%22M2.2%2C17.5C2.1%2C16.7%2C2%2C15.8%2C2%2C15C2%2C7.8%2C7.8%2C2%2C15%2C2s13%2C5.8%2C13%2C13c0%2C0.8-0.1%2C1.7-0.2%2C2.5h2%0A%09%09c0.1-0.8%2C0.2-1.6%2C0.2-2.5c0-8.3-6.7-15-15-15S0%2C6.7%2C0%2C15c0%2C0.8%2C0.1%2C1.7%2C0.2%2C2.5H2.2z%22/%3E%0A%3C/g%3E%0A%3C/svg%3E%0A);background-repeat:no-repeat}@keyframes spin{0%{transform:rotate(0)}to{transform:rotate(360deg)}}:host.jamp-page-level{background:rgba(255,255,255,.9);position:fixed;z-index:100;inset:0}:host.jamp-page-level .row{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%)}:host.jamp-page-level .jamp:focus-visible{outline:none}.jamp-center-css{width:calc(100% - 60px);max-height:calc(100% - 60px);position:absolute!important;top:50%!important;left:50%!important;transform:translate(-50%,-50%)}.jamp{position:relative!important;top:50%!important;left:0%!important;text-align:center!important;transform:translateY(-50%)!important;margin:0}.fillHeight{min-height:100%;height:100%}.position-initial{position:initial}.citi-modal :host .jamp{margin:30px 0 10px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { isHidden: [{
                type: HostBinding,
                args: ['class.hidden']
            }], isPageLevel: [{
                type: HostBinding,
                args: ['class.jamp-page-level']
            }], useGif: [{
                type: Input
            }], visible: [{
                type: Input
            }], pagelevel: [{
                type: Input
            }], htmlContent: [{
                type: Input
            }], additionalCssClasses: [{
                type: Input
            }], waitMessage: [{
                type: Input
            }], readyMessage: [{
                type: Input
            }], isCenterAligned: [{
                type: Input
            }], jampPageLevelContent: [{
                type: ViewChild,
                args: ['jampPageLevelContent']
            }] } });

const COMPONENTS$L = [
    JampComponent
];
const MODULES$r = [
    CommonModule,
    CdsAriaHideOuterDomModule,
    // ContentHtml
];
class CdsJampModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsJampModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsJampModule, declarations: [JampComponent], imports: [CommonModule,
            CdsAriaHideOuterDomModule], exports: [JampComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsJampModule, imports: [MODULES$r] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsJampModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$r],
                    declarations: [...COMPONENTS$L],
                    exports: [JampComponent]
                }]
        }] });

class CdsLogoComponent {
    constructor() {
        this.darkBG = false;
        this.type = "fdic";
        this.signage1 = "FDIC-Insured - Backed by the full faith and credit of the U.S. Government";
        this.signage2 = "Citibank, N.A.";
    }
    get hostClass() {
        return this.type.includes('zelle-') ? 'cds-logo-zelle' : null;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLogoComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsLogoComponent, isStandalone: true, selector: "cds-logo", inputs: { darkBG: "darkBG", type: "type", signage1: "signage1", signage2: "signage2", ariaLabel: "ariaLabel", pathColor: "pathColor", svgStyle: "svgStyle" }, host: { properties: { "class": "this.hostClass" }, classAttribute: "cds-logo" }, ngImport: i0, template: `
  <div *ngIf="this.type === 'fdic'">
    <div class="icon-wrap">
      <cds-icon
        idStr="icon-id"
        ariaLabel="FDIC Logo"
        altText="FDIC Logo"
        type="2021"
        name="fdic"
        [pathColor]="this.darkBG ? '#ffffff' : '#003256'"
        [svgStyle]="{'width.px': '38' , 'height.px': '16' }"
      ></cds-icon>
      <p class="signage"
        [innerHTML]='this.signage1'
        [style.color]="this.darkBG ? '#ffffff' : '#000000'">
      </p>
    </div>
    <p class="signage-2"
      [style.color]="this.darkBG ? '#ffffff' : '#000000'"
      [innerHTML]="this.signage2">
    </p>
  </div>

  <div *ngIf="type === 'fdic-member'">
    <div class="icon-wrap">
      <cds-icon
        idStr="icon-id"
        ariaLabel="FDIC Member Logo"
        altText="FDIC Member Logo"
        type="2021"
        name="fdic-member"
        [pathColor]="this.darkBG ? '#ffffff' : '#000000'"
        [svgStyle]="{'width.px': '38' , 'height.px': '24' }"
      ></cds-icon>
    </div>
  </div>

  <div *ngIf="!type.includes('fdic')">
    <cds-icon
      [ngClass]="type"
      [ariaLabel]="ariaLabel"
      [altText]="ariaLabel"
      type="2021"
      [name]="type"
      [svgStyle]="svgStyle"
      [pathColor]="pathColor"
    ></cds-icon>
  </div>
    `, isInline: true, styles: [".signage{font-family:source-sans;font-size:13px;font-style:italic;font-weight:400;line-height:16px;text-align:left}.icon-wrap{display:flex;flex-direction:row;gap:8px}.signage-2{font-family:source-sans;font-size:10px;font-weight:400;line-height:12px;text-align:left;margin-top:-12px}:host.cds-logo-zelle{--cds-icon-path-fill: #6d1ed4}:host.cds-logo-zelle-grey{--cds-icon-path-fill: #939598}:host.cds-logo-zelle-black{--cds-icon-path-fill: #000000}:host.cds-logo-zelle-white{--cds-icon-path-fill: #ffffff}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: CdsIconModule }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLogoComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-logo', standalone: true, template: `
  <div *ngIf="this.type === 'fdic'">
    <div class="icon-wrap">
      <cds-icon
        idStr="icon-id"
        ariaLabel="FDIC Logo"
        altText="FDIC Logo"
        type="2021"
        name="fdic"
        [pathColor]="this.darkBG ? '#ffffff' : '#003256'"
        [svgStyle]="{'width.px': '38' , 'height.px': '16' }"
      ></cds-icon>
      <p class="signage"
        [innerHTML]='this.signage1'
        [style.color]="this.darkBG ? '#ffffff' : '#000000'">
      </p>
    </div>
    <p class="signage-2"
      [style.color]="this.darkBG ? '#ffffff' : '#000000'"
      [innerHTML]="this.signage2">
    </p>
  </div>

  <div *ngIf="type === 'fdic-member'">
    <div class="icon-wrap">
      <cds-icon
        idStr="icon-id"
        ariaLabel="FDIC Member Logo"
        altText="FDIC Member Logo"
        type="2021"
        name="fdic-member"
        [pathColor]="this.darkBG ? '#ffffff' : '#000000'"
        [svgStyle]="{'width.px': '38' , 'height.px': '24' }"
      ></cds-icon>
    </div>
  </div>

  <div *ngIf="!type.includes('fdic')">
    <cds-icon
      [ngClass]="type"
      [ariaLabel]="ariaLabel"
      [altText]="ariaLabel"
      type="2021"
      [name]="type"
      [svgStyle]="svgStyle"
      [pathColor]="pathColor"
    ></cds-icon>
  </div>
    `, imports: [CommonModule, CdsIconModule], host: { class: 'cds-logo' }, styles: [".signage{font-family:source-sans;font-size:13px;font-style:italic;font-weight:400;line-height:16px;text-align:left}.icon-wrap{display:flex;flex-direction:row;gap:8px}.signage-2{font-family:source-sans;font-size:10px;font-weight:400;line-height:12px;text-align:left;margin-top:-12px}:host.cds-logo-zelle{--cds-icon-path-fill: #6d1ed4}:host.cds-logo-zelle-grey{--cds-icon-path-fill: #939598}:host.cds-logo-zelle-black{--cds-icon-path-fill: #000000}:host.cds-logo-zelle-white{--cds-icon-path-fill: #ffffff}\n"] }]
        }], propDecorators: { hostClass: [{
                type: HostBinding,
                args: ['class']
            }], darkBG: [{
                type: Input
            }], type: [{
                type: Input
            }], signage1: [{
                type: Input
            }], signage2: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], pathColor: [{
                type: Input
            }], svgStyle: [{
                type: Input
            }] } });

class CdsLogoModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLogoModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsLogoModule, imports: [CommonModule,
            CdsLogoComponent,
            CdsIconModule], exports: [CdsLogoComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLogoModule, imports: [CommonModule,
            CdsLogoComponent,
            CdsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLogoModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CdsLogoComponent,
                        CdsIconModule
                    ],
                    declarations: [],
                    exports: [CdsLogoComponent],
                }]
        }] });

class CdsLoadingPlaceholder {
    constructor(announcer) {
        this.announcer = announcer;
        // Set classes on host component selector <cds-loading-placeholder>
        this.isHidden = true;
        this.isCircle = false; // Whether shape is Circle
        this.shapeWidth = 70; // Width of a shape
        this.shapeHeight = 60; // Height of a shape
    }
    set visible(show) {
        this._visible = coerceBooleanProperty(show);
        this.isHidden = !this._visible;
        if (this._visible)
            this.announcer.announce(this.waitMessage);
    }
    get visible() { return this._visible; }
    ;
    getStyles() {
        const styles = {
            'height.px': this.shapeHeight ? this.shapeHeight / 3 : '',
            'margin-bottom.px': this.shapeHeight ? this.shapeHeight / 3 : ''
        };
        return styles;
    }
    getCircleStyles() {
        const styles = {
            'width.px': this.shapeHeight ? this.shapeHeight : '',
            'height.px': this.shapeHeight ? this.shapeHeight : ''
        };
        return styles;
    }
    ngOnChanges(changes) {
        if (changes.hasOwnProperty('visible') && changes.visible.previousValue
            && !changes.visible.currentValue) {
            this.announcer.announce(this.readyMessage);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLoadingPlaceholder, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsLoadingPlaceholder, selector: "cds-loading-placeholder", inputs: { visible: "visible", isCircle: "isCircle", shapeWidth: "shapeWidth", shapeHeight: "shapeHeight", waitMessage: "waitMessage", readyMessage: "readyMessage" }, host: { properties: { "class.hidden": "this.isHidden" } }, usesOnChanges: true, ngImport: i0, template: `
  <div class="cds-lp-container" [ngStyle]="{'width.%':shapeWidth}">
    <div *ngIf="isCircle" class="lp-circle shimmer" [ngStyle]="getCircleStyles()"></div>

    <div role="img" class="lp-bar-container" [attr.aria-label]="waitMessage">
      <div class="lp-bar top-bar shimmer" [ngStyle]="getStyles()"></div>
      <div class="lp-bar shimmer" [ngStyle]="{'height.px':shapeHeight/3}"></div>
    </div>
  </div>`, isInline: true, styles: [".cds-lp-container{display:flex;animation:fadeIn .5s linear}.lp-circle{height:60px;width:60px;border-radius:50%;margin-right:15px}.lp-bar-container{flex:1 1 auto}.lp-bar{height:20px;width:50%;border-radius:4px}.lp-bar.top-bar{margin-bottom:20px;width:100%}.shimmer{background-size:1000px 100%;animation:shimmer 2s infinite linear}@keyframes shimmer{0%{background-position:-1000px 0}to{background-position:1000px 0}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLoadingPlaceholder, decorators: [{
            type: Component,
            args: [{ selector: 'cds-loading-placeholder', template: `
  <div class="cds-lp-container" [ngStyle]="{'width.%':shapeWidth}">
    <div *ngIf="isCircle" class="lp-circle shimmer" [ngStyle]="getCircleStyles()"></div>

    <div role="img" class="lp-bar-container" [attr.aria-label]="waitMessage">
      <div class="lp-bar top-bar shimmer" [ngStyle]="getStyles()"></div>
      <div class="lp-bar shimmer" [ngStyle]="{'height.px':shapeHeight/3}"></div>
    </div>
  </div>`, styles: [".cds-lp-container{display:flex;animation:fadeIn .5s linear}.lp-circle{height:60px;width:60px;border-radius:50%;margin-right:15px}.lp-bar-container{flex:1 1 auto}.lp-bar{height:20px;width:50%;border-radius:4px}.lp-bar.top-bar{margin-bottom:20px;width:100%}.shimmer{background-size:1000px 100%;animation:shimmer 2s infinite linear}@keyframes shimmer{0%{background-position:-1000px 0}to{background-position:1000px 0}}@keyframes fadeIn{0%{opacity:0}to{opacity:1}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { isHidden: [{
                type: HostBinding,
                args: ['class.hidden']
            }], visible: [{
                type: Input
            }], isCircle: [{
                type: Input
            }], shapeWidth: [{
                type: Input
            }], shapeHeight: [{
                type: Input
            }], waitMessage: [{
                type: Input
            }], readyMessage: [{
                type: Input
            }] } });

const COMPONENTS$K = [CdsLoadingPlaceholder];
class CdsLoadingPlaceholderModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLoadingPlaceholderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsLoadingPlaceholderModule, declarations: [CdsLoadingPlaceholder], imports: [CommonModule], exports: [CdsLoadingPlaceholder] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLoadingPlaceholderModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLoadingPlaceholderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdsLoadingPlaceholder],
                    exports: [CdsLoadingPlaceholder]
                }]
        }] });

class CdsModalDialogComponent {
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_document, platformId, _cdr, _renderer) {
        this._document = _document;
        this.platformId = platformId;
        this._cdr = _cdr;
        this._renderer = _renderer;
        // #region ========== Properties ========== //
        this._unsub$ = new Subject();
        this._attributesToRemoveUponClose = [];
        /**
         * Allows the modal to be closed when clicking on the background.
         */
        this.isClosedOnBackdropClick = true;
        /**
         * Allows the modal to be closed when using the 'Escape' key.
         */
        this.isClosedOnEscape = true;
        /**
         * Allows modal open/close animation transition.
         */
        this.hasFade = true;
        /**
         * @property ariaRole
         * @description the ariaRole to define a role for the modal
         * @return string
         */
        this.ariaRole = 'dialog';
        /**
         * Time in milliseconds for closing animation.
         * This should match time from styles.
         */
        this.fadeTime = 150;
        // #endregion
        // #region ========== Outputs ========== //
        /**
         * Emits when 'isOpen' is changed.
         */
        this.isOpenChange = new EventEmitter();
        /**
         * Emits at start of open process (before animation starts)
         */
        this.openStart = new EventEmitter();
        /**
         * Emits at end of open process (after animation ends)
         */
        this.openEnd = new EventEmitter();
        /**
         * Emits at start of close process (before animation starts)
         */
        this.closeStart = new EventEmitter();
        /**
         * Emits at end of close process (after animation ends)
         */
        this.closeEnd = new EventEmitter();
    }
    ngOnInit() { }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            if (simpleChanges.hasOwnProperty(propName)) {
                const change = simpleChanges[propName];
                if (propName === 'isOpen' && !change.isFirstChange()) {
                    if (this.isOpen) {
                        this._openModal();
                    }
                    else {
                        this._closeModal();
                    }
                }
            }
        }
    }
    ngAfterViewInit() {
        // if isOpen is initialized as true, call _openModal
        if (this.isOpen) {
            this._openModal();
        }
    }
    ngOnDestroy() {
        // Remove body class/style if navigating away from page
        this._setBodyAttributes(false);
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListener ========== //
    hostClick(event) {
        if (this.isClosedOnBackdropClick &&
            !this.modalDialog.nativeElement?.contains(event.target)) {
            this.close();
        }
    }
    docKeydown(event) {
        if (this.isClosedOnEscape && this.isOpen) {
            this.close();
        }
    }
    // #endregion
    // #region ========== Host Binding ========== //
    get hostHidden() {
        return !this._isDisplayBlock;
    }
    // #endregion
    // #region ========== Public Methods ========== //
    /**
     * @deprecated
     */
    open() {
        // if closed, open modal
        if (isDevMode()) {
            console.warn('The use of the open() function is depricated. Please use the isOpen input instead.');
        }
        if (!this.isOpen) {
            this.isOpen = true;
            this.isOpenChange.emit(this.isOpen);
        }
    }
    /**
    * @deprecated
    */
    close() {
        // if open, close modal
        if (isDevMode()) {
            console.warn('The use of the close() function is depricated. Please use the isOpen input instead.');
        }
        if (this.isOpen) {
            this.isOpen = false;
            this.isOpenChange.emit(this.isOpen);
        }
    }
    /**
     * @deprecated
     */
    toggle() {
        if (isDevMode()) {
            console.warn('The use of the toggle() function is depricated. Please use the isOpen input instead.');
        }
        this.isOpen = !this.isOpen;
        this.isOpenChange.emit(this.isOpen);
    }
    // #endregion
    // #region ========== Private Methods ========== //
    _openModal() {
        this._previousFocusedElement = this._document.activeElement;
        this._isDisplayBlock = true;
        this._setBodyAttributes(true);
        this.openStart.emit();
        this._cdr.detectChanges();
        timer(0)
            .pipe(takeUntil(this._unsub$))
            .subscribe(() => {
            timer(0)
                .pipe(takeUntil(this._unsub$))
                .subscribe(() => {
                if (isPlatformBrowser(this.platformId)) {
                    this.modalDialog.nativeElement?.scrollIntoView();
                }
            });
            this._isShowClassActive = true;
            this._focusInModal();
            this.openEnd.emit();
            this._cdr.detectChanges();
        });
    }
    _closeModal() {
        this._isShowClassActive = false;
        this.closeStart.emit();
        this._cdr.detectChanges();
        timer(this.hasFade ? this.fadeTime : 0)
            .pipe(takeUntil(this._unsub$))
            .subscribe(() => {
            this._isDisplayBlock = false;
            this._setBodyAttributes(false);
            this._removeSetAttributes();
            if (!this.isRestoreFocusDisabled) {
                this._restorePreviousFocus();
            }
            this.closeEnd.emit();
            this._cdr.detectChanges();
        });
    }
    _setBodyAttributes(isModalOpen) {
        if (isModalOpen) {
            const scrollbarWidth = getScrollbarWidth(this._document);
            this._renderer.addClass(this._document.body, 'cds-modal-open');
            this._renderer.setStyle(this._document.body, 'padding-right', `${scrollbarWidth}px`);
        }
        else {
            this._renderer.removeClass(this._document.body, 'cds-modal-open');
            this._renderer.removeStyle(this._document.body, 'padding-right');
        }
    }
    _focusInModal() {
        const modalInitalFocusElement = this.modalDialog.nativeElement?.querySelector('.cds-modal-initial-focus, [cdsModalInitialFocus]');
        if (!!modalInitalFocusElement) {
            if (!['button', 'a', 'input', 'textarea'].includes(modalInitalFocusElement.tagName.toLowerCase())) {
                this._setAttributeToBeRemovedWhenClosed(modalInitalFocusElement, 'tabindex', '-1');
            }
            modalInitalFocusElement.focus();
        }
        else if (this.focusTrapDirective?.focusTrap?.hasAttached()) {
            this.focusTrapDirective.focusTrap.focusInitialElementWhenReady();
        }
    }
    _restorePreviousFocus() {
        this._previousFocusedElement?.focus();
    }
    _setAttributeToBeRemovedWhenClosed(element, attributeName, attributeValue) {
        if (!element.hasAttribute(attributeName)) {
            this._renderer.setAttribute(element, attributeName, attributeValue);
            // set these attributes to be removed after close
            this._attributesToRemoveUponClose.push({ element, attributeName });
        }
    }
    _removeSetAttributes() {
        this._attributesToRemoveUponClose.forEach((item) => {
            this._renderer.removeAttribute(item.element, item.attributeName);
        });
        this._attributesToRemoveUponClose.length = 0;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsModalDialogComponent, deps: [{ token: DOCUMENT }, { token: PLATFORM_ID }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsModalDialogComponent, selector: "cds-modal-dialog", inputs: { isOpen: "isOpen", isClosedOnBackdropClick: "isClosedOnBackdropClick", isClosedOnEscape: "isClosedOnEscape", hasFade: "hasFade", ariaRole: "ariaRole", isRestoreFocusDisabled: "isRestoreFocusDisabled", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", fadeTime: "fadeTime" }, outputs: { isOpenChange: "isOpenChange", openStart: "openStart", openEnd: "openEnd", closeStart: "closeStart", closeEnd: "closeEnd" }, host: { listeners: { "click": "hostClick($event)", "touchstart": "hostClick($event)", "document:keydown.escape": "docKeydown($event)" }, properties: { "attr.aria-hidden": "this.hostHidden" } }, viewQueries: [{ propertyName: "modal", first: true, predicate: ["modal"], descendants: true }, { propertyName: "modalDialog", first: true, predicate: ["modalDialog"], descendants: true }, { propertyName: "focusTrapDirective", first: true, predicate: ["focusTrapDirective"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div cdsAriaHideOuterDom [isOuterDomAriaHidden]="_isDisplayBlock" [attr.aria-hidden]="!_isDisplayBlock">
      <div
        #modal
        class="cds-modal"
        [class.cds-modal-fade]="hasFade"
        [class.cds-modal-show]="_isShowClassActive"
        [style.display]="_isDisplayBlock ? 'block' : 'none'"
        [attr.aria-hidden]="false"
      >
        <div
          [attr.role]="ariaRole"
          #modalDialog
          class="cds-modal-dialog"
          cdkTrapFocus
          #focusTrapDirective="cdkTrapFocus"
          [attr.aria-hidden]="false"
          [attr.aria-label]="ariaLabel ? ariaLabel : null"
          [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby : null"
        >
          <ng-content></ng-content>
        </div>
      </div>
      <div
        class="cds-modal-backdrop"
        [class.cds-modal-fade]="hasFade"
        [class.cds-modal-show]="_isShowClassActive"
        [style.display]="_isDisplayBlock ? 'block' : 'none'"
      ></div>
    </div>
  `, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: CdsAriaHideOuterDomDirective, selector: "[cdsAriaHideOuterDom]", inputs: ["isOuterDomAriaHidden", "outerDomExceptions"], exportAs: ["cdsAriaHideOuterDom"] }, { kind: "directive", type: i1$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsModalDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-modal-dialog', template: `
    <div cdsAriaHideOuterDom [isOuterDomAriaHidden]="_isDisplayBlock" [attr.aria-hidden]="!_isDisplayBlock">
      <div
        #modal
        class="cds-modal"
        [class.cds-modal-fade]="hasFade"
        [class.cds-modal-show]="_isShowClassActive"
        [style.display]="_isDisplayBlock ? 'block' : 'none'"
        [attr.aria-hidden]="false"
      >
        <div
          [attr.role]="ariaRole"
          #modalDialog
          class="cds-modal-dialog"
          cdkTrapFocus
          #focusTrapDirective="cdkTrapFocus"
          [attr.aria-hidden]="false"
          [attr.aria-label]="ariaLabel ? ariaLabel : null"
          [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby : null"
        >
          <ng-content></ng-content>
        </div>
      </div>
      <div
        class="cds-modal-backdrop"
        [class.cds-modal-fade]="hasFade"
        [class.cds-modal-show]="_isShowClassActive"
        [style.display]="_isDisplayBlock ? 'block' : 'none'"
      ></div>
    </div>
  `, changeDetection: ChangeDetectionStrategy.OnPush }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: Object, decorators: [{
                    type: Inject,
                    args: [PLATFORM_ID]
                }] }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }], propDecorators: { isOpen: [{
                type: Input
            }], isClosedOnBackdropClick: [{
                type: Input
            }], isClosedOnEscape: [{
                type: Input
            }], hasFade: [{
                type: Input
            }], ariaRole: [{
                type: Input
            }], isRestoreFocusDisabled: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], fadeTime: [{
                type: Input
            }], isOpenChange: [{
                type: Output
            }], openStart: [{
                type: Output
            }], openEnd: [{
                type: Output
            }], closeStart: [{
                type: Output
            }], closeEnd: [{
                type: Output
            }], modal: [{
                type: ViewChild,
                args: ['modal']
            }], modalDialog: [{
                type: ViewChild,
                args: ['modalDialog']
            }], focusTrapDirective: [{
                type: ViewChild,
                args: ['focusTrapDirective']
            }], hostClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['touchstart', ['$event']]
            }], docKeydown: [{
                type: HostListener,
                args: ['document:keydown.escape', ['$event']]
            }], hostHidden: [{
                type: HostBinding,
                args: ['attr.aria-hidden']
            }] } });

class CdsModalDialogModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsModalDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsModalDialogModule, declarations: [CdsModalDialogComponent], imports: [CommonModule,
            CdsFocusTrapModule,
            CdsAriaHideOuterDomModule,
            A11yModule], exports: [CdsModalDialogComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsModalDialogModule, imports: [CommonModule,
            CdsFocusTrapModule,
            CdsAriaHideOuterDomModule,
            A11yModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsModalDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        CdsFocusTrapModule,
                        CdsAriaHideOuterDomModule,
                        A11yModule,
                    ],
                    declarations: [CdsModalDialogComponent],
                    exports: [CdsModalDialogComponent],
                }]
        }] });

const COMPONENTS$J = [CdsOverlayPanel];
const MODULES$q = [CommonModule];
class OverlayPanelModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: OverlayPanelModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: OverlayPanelModule, declarations: [CdsOverlayPanel], imports: [CommonModule, OverlayModule], exports: [CdsOverlayPanel, OverlayModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: OverlayPanelModule, imports: [MODULES$q, OverlayModule, OverlayModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: OverlayPanelModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$q, OverlayModule],
                    declarations: [...COMPONENTS$J],
                    exports: [...COMPONENTS$J, OverlayModule]
                }]
        }] });

class CdsLayoutComponent {
    constructor() {
        this.fullbleed = false;
    }
    get layout() {
        return `cds-layout ${this.bootstrapClass}`;
    }
    get bootstrapClass() {
        return this.fullbleed ? 'container-fluid' : 'container';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLayoutComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsLayoutComponent, selector: "cds-layout,.cds-layout", inputs: { fullbleed: "fullbleed" }, host: { properties: { "class": "this.layout" } }, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, styles: [":host{display:block;max-width:1440px;min-height:650px;padding-top:40px;border-left:1px dashed #333;border-right:1px dashed #333}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsLayoutComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-layout,.cds-layout', template: `
    <ng-content></ng-content>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:block;max-width:1440px;min-height:650px;padding-top:40px;border-left:1px dashed #333;border-right:1px dashed #333}\n"] }]
        }], propDecorators: { fullbleed: [{
                type: Input
            }], layout: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CdsPageLayoutModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageLayoutModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPageLayoutModule, declarations: [CdsLayoutComponent], imports: [CommonModule], exports: [CdsLayoutComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageLayoutModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageLayoutModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdsLayoutComponent],
                    exports: [CdsLayoutComponent]
                }]
        }] });

class CdsChip {
    constructor() {
        this.selected = false;
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Chip <citi-chip> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    get getSelected() {
        return this.selected;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsChip, selector: "button[cdsChip], button[cds-chip], a[cdsChip], a[cds-chip]", inputs: { selected: "selected" }, host: { properties: { "class.selected": "this.selected", "attr.aria-pressed": "this.getSelected" }, classAttribute: "cds-chip" }, exportAs: ["cdsChip"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[cdsChip], button[cds-chip], a[cdsChip], a[cds-chip]',
                    exportAs: 'cdsChip',
                    host: { class: 'cds-chip', }
                }]
        }], ctorParameters: () => [], propDecorators: { selected: [{
                type: HostBinding,
                args: ['class.selected']
            }, {
                type: Input
            }], getSelected: [{
                type: HostBinding,
                args: ['attr.aria-pressed']
            }] } });

const COMPONENTS$I = [CdsChip];
const MODULES$p = [CommonModule];
class CdsChipModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsChipModule, declarations: [CdsChip], imports: [CommonModule], exports: [CdsChip] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipModule, imports: [MODULES$p] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$I],
                    imports: [...MODULES$p],
                    exports: [...COMPONENTS$I]
                }]
        }] });

class CdsChip2 {
    constructor() {
        this.selected = false;
    }
    get getSelected() {
        return this.selected;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip2, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsChip2, selector: "button[cdsChip2], button[cds-chip2], a[cdsChip2], a[cds-chip2]", inputs: { selected: "selected" }, host: { properties: { "class.selected": "this.selected", "attr.aria-pressed": "this.getSelected" }, classAttribute: "cds-chip2" }, exportAs: ["cdsChip2"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip2, decorators: [{
            type: Directive,
            args: [{
                    selector: 'button[cdsChip2], button[cds-chip2], a[cdsChip2], a[cds-chip2]',
                    exportAs: 'cdsChip2',
                    host: { class: 'cds-chip2', }
                }]
        }], propDecorators: { selected: [{
                type: HostBinding,
                args: ['class.selected']
            }, {
                type: Input
            }], getSelected: [{
                type: HostBinding,
                args: ['attr.aria-pressed']
            }] } });

const COMPONENTS$H = [CdsChip2];
const MODULES$o = [CommonModule];
class CdsChip2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsChip2Module, declarations: [CdsChip2], imports: [CommonModule], exports: [CdsChip2] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip2Module, imports: [MODULES$o] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip2Module, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$H],
                    imports: [...MODULES$o],
                    exports: [...COMPONENTS$H]
                }]
        }] });

class CdsChip3Component {
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
        // #region ========== Input / Output ========== //
        /**
         * Chip icon type.
         */
        this.showIcon = 'none';
        /**
         * Type of leading icon.
         */
        this.iconID = '025-favorite';
        /**
         * Chip label text
         */
        this.chipText = 'Chip';
        /**
         * If the chip is pressed on.
         */
        this.isSelected = false;
        /**
         * Sets chip to disabled
         */
        this.isDisabled = false;
        /**
         *Emits when close button on chips are clicked on
         */
        this.removeChip = new EventEmitter();
        /**
         * Emits when chips are clicked on
         */
        this.clickChip = new EventEmitter();
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Gets aria label for specific chip type.
     */
    get chipAriaLabel() {
        let label = this.chipText;
        if (this.showIcon == 'close') {
            label = this.chipText + ' with clickable close button';
        }
        return label;
    }
    /**
     * Selects/Unselects chip when clicked on
     */
    onClick() {
        this.isSelected = !this.isSelected;
        this.clickChip.emit();
        if (this.isSelected) {
            this.liveAnnouncer.announce(this.chipText + ' is selected');
        }
        else {
            this.liveAnnouncer.announce(this.chipText + ' is unselected');
        }
    }
    /**
     * Announces chip is removed. Journey teams will need to implement the logic for chip removal, as shown in the playground.
     */
    rmClick() {
        if (!this.isDisabled) {
            this.liveAnnouncer.announce(this.chipText + ' is removed');
        }
        this.removeChip.emit(false);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip3Component, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsChip3Component, selector: "cds-chip3", inputs: { showIcon: "showIcon", iconID: "iconID", chipText: "chipText", isSelected: "isSelected", isDisabled: "isDisabled" }, outputs: { removeChip: "removeChip", clickChip: "clickChip" }, exportAs: ["cdsChip3"], ngImport: i0, template: `
    <div class="chip3-container">
      <div
        class="chip3-content"
        [ngClass]="{ selected: isSelected, disabled: isDisabled }"
      >
        <button
          class="cds-chip3"
          type="button"
          [ngClass]="{
            selected: isSelected,
            'chip3-close-button': showIcon == 'close'
          }"
          (click)="onClick()"
          [disabled]="isDisabled"
          [name]="chipText"
          [attr.aria-label]="chipAriaLabel"
          [attr.aria-pressed]="isSelected"
          [attr.aria-disabled]="isDisabled"
        >
          <cds-icon
            *ngIf="showIcon == 'leading'"
            aria-hidden="true"
            type="2021"
            [name]="iconID"
            [altText]="iconID"
            [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
            [pathColor]="isSelected ? '#FFF' : ''"
          ></cds-icon>

          <div>
            {{ chipText }}
          </div>
        </button>

        <cds-icon
          class="chip3-close-icon"
          *ngIf="showIcon == 'close'"
          type="2021"
          [ariaLabel]="isDisabled ? 'disabled' : 'close button for ' + chipText"
          (click)="rmClick()"
          name="044-close"
          (keyup.enter)="rmClick()"
          (keydown.Space)="rmClick()"
          [tabindex]="!isDisabled ? 0 : -1"
          altText="Close button"
          [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
          [pathColor]="isSelected ? '#FFF' : isDisabled ? '#CCCCCC' : ''"
        ></cds-icon>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChip3Component, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-chip3',
                    template: `
    <div class="chip3-container">
      <div
        class="chip3-content"
        [ngClass]="{ selected: isSelected, disabled: isDisabled }"
      >
        <button
          class="cds-chip3"
          type="button"
          [ngClass]="{
            selected: isSelected,
            'chip3-close-button': showIcon == 'close'
          }"
          (click)="onClick()"
          [disabled]="isDisabled"
          [name]="chipText"
          [attr.aria-label]="chipAriaLabel"
          [attr.aria-pressed]="isSelected"
          [attr.aria-disabled]="isDisabled"
        >
          <cds-icon
            *ngIf="showIcon == 'leading'"
            aria-hidden="true"
            type="2021"
            [name]="iconID"
            [altText]="iconID"
            [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
            [pathColor]="isSelected ? '#FFF' : ''"
          ></cds-icon>

          <div>
            {{ chipText }}
          </div>
        </button>

        <cds-icon
          class="chip3-close-icon"
          *ngIf="showIcon == 'close'"
          type="2021"
          [ariaLabel]="isDisabled ? 'disabled' : 'close button for ' + chipText"
          (click)="rmClick()"
          name="044-close"
          (keyup.enter)="rmClick()"
          (keydown.Space)="rmClick()"
          [tabindex]="!isDisabled ? 0 : -1"
          altText="Close button"
          [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
          [pathColor]="isSelected ? '#FFF' : isDisabled ? '#CCCCCC' : ''"
        ></cds-icon>
      </div>
    </div>
  `,
                    exportAs: 'cdsChip3'
                }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { showIcon: [{
                type: Input
            }], iconID: [{
                type: Input
            }], chipText: [{
                type: Input
            }], isSelected: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], removeChip: [{
                type: Output
            }], clickChip: [{
                type: Output
            }] } });

class CdsChipBarComponent {
    constructor() {
        /**
        * Alignment of the chip bar
        */
        this.chipAlignment = 'left';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsChipBarComponent, selector: "cds-chip-bar", inputs: { chipAlignment: "chipAlignment" }, ngImport: i0, template: `
    <div
      class="chip-bar-container"
      [ngClass]="{
        'chip-bar-right': chipAlignment == 'right'
      }"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, styles: [".chip-bar-container{display:flex;flex-wrap:wrap!important;margin-bottom:-8px}.chip-bar-right{flex-direction:row;justify-content:flex-end}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-chip-bar', template: `
    <div
      class="chip-bar-container"
      [ngClass]="{
        'chip-bar-right': chipAlignment == 'right'
      }"
    >
      <ng-content></ng-content>
    </div>
  `, styles: [".chip-bar-container{display:flex;flex-wrap:wrap!important;margin-bottom:-8px}.chip-bar-right{flex-direction:row;justify-content:flex-end}\n"] }]
        }], propDecorators: { chipAlignment: [{
                type: Input
            }] } });

const COMPONENTS$G = [CdsChip3Component, CdsChipBarComponent];
const MODULES$n = [CommonModule, FormsModule, CdsIconModule];
class CdsChipBarModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsChipBarModule, declarations: [CdsChip3Component, CdsChipBarComponent], imports: [CommonModule, FormsModule, CdsIconModule], exports: [CdsChip3Component, CdsChipBarComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipBarModule, imports: [MODULES$n] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsChipBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$G],
                    imports: [...MODULES$n],
                    exports: [...COMPONENTS$G]
                }]
        }] });

class CdsTimerComponent {
    constructor() {
        // For playground testing purposes
        this.currentTimeStart = null;
        // Timer Formats
        this.monthsAndWeeks = false;
        this.weeksAndDays = false;
        this.daysAndHours = false;
        this.hoursAndMinutes = false;
        this.lessThan1Minute = false;
    }
    // Calculates time remaining 
    getTimeDifference() {
        let timerStart, timerEnd;
        switch (this.timerState) {
            case 'upcoming':
                timerStart = new Date().getTime();
                timerEnd = this.stringToDateConverter(this.timerStart) || this.timerStart;
                this.timeDifference = timerEnd - timerStart;
                break;
            case 'active':
                timerStart = new Date().getTime();
                timerEnd = this.stringToDateConverter(this.timerEnd) || this.timerEnd;
                this.timeDifference = timerEnd - timerStart;
                break;
        }
        this.allocateTimeUnits(this.timeDifference);
    }
    // Converts date string into ms
    stringToDateConverter(dateStr) {
        if (typeof dateStr === 'number')
            return null; // For playground testing purposes only. If number, return nothing
        let date = dateStr.split(' ')[0];
        let time = dateStr.split(' ')[1];
        let dateArr = date.split('/');
        let timeArr = time.split(':');
        dateArr = dateArr.map((str, index) => {
            if (index === 1)
                return Number(str) - 1; // months are 0-indexed, so minus 1
            else
                return Number(str);
        });
        timeArr = timeArr.map(str => Number(str));
        const result = new Date(dateArr[0], dateArr[1], dateArr[2], timeArr[0], timeArr[1], timeArr[2]);
        return result;
    }
    // Converts time remaining (in ms) into the following units:
    allocateTimeUnits(timeDifference) {
        this.seconds = Math.floor(timeDifference / 1000 % 60);
        this.minutes = Math.floor(timeDifference / 60000 % 60);
        this.hours = Math.floor(timeDifference / 3600000 % 24);
        this.days = Math.floor(timeDifference / 86400000 % 7);
        this.weeks = Math.floor(timeDifference / 604800000 % 4);
        // One month from current date, in weeks and days
        const currentWeeksPerMonth = Math.floor(timeDifference / 604800000);
        if (currentWeeksPerMonth <= 4)
            this.weeks = currentWeeksPerMonth;
        else
            this.weeks = Math.floor(currentWeeksPerMonth % 4);
        // If 4 weeks is currently displayed, then months should be hidden. Set to 0
        if (currentWeeksPerMonth === 4)
            this.months = 0;
        else
            this.months = Math.floor(timeDifference / 2629743833.3334);
        this.updateTimerFormat(timeDifference);
    }
    updateTimerFormat(timeDifference) {
        this.monthsAndWeeks = this.weeksAndDays = this.daysAndHours = this.hoursAndMinutes = this.lessThan1Minute = false;
        switch (this.timerState) {
            case 'active':
                if (timeDifference > 3024000000)
                    this.monthsAndWeeks = true; // Display months and weeks
                if (timeDifference < 3024000000)
                    this.weeksAndDays = true; // Display weeks and days
                if (timeDifference < 604800000)
                    this.daysAndHours = true; // Display days and hours
                if (timeDifference < 86400000)
                    this.hoursAndMinutes = true; // Display hours and minutes 
                if (timeDifference < 60000)
                    this.lessThan1Minute = true; // Display "Less than 1 Minute"
                if (timeDifference <= 1000)
                    this.timerState = 'ending';
                break;
            case 'upcoming':
                if (timeDifference <= 1000) {
                    this.timerState = 'active';
                    this.getTimeDifference();
                }
                ;
                break;
        }
    }
    ngOnInit() {
        this.getTimeDifference();
        this.subscription = interval(1000)
            .subscribe(() => {
            this.getTimeDifference(); // Continuously updates timer every 1s
        });
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTimerComponent, selector: "cds-timer", inputs: { timerStart: "timerStart", timerEnd: "timerEnd", timerState: "timerState", customContent: "customContent", currentTimeStart: "currentTimeStart", displaySeconds: "displaySeconds" }, host: { classAttribute: "cds-timer" }, ngImport: i0, template: `
  <div role="timer" class="timer">
  <!-- Deal Ended timer -->
  <div [class]="timerState" class="wrapper" *ngIf="timerState === 'ending'">
    <cds-icon
      class="timer-icon"
      [type]="2021"
      name="028-clock"
      aria-hidden="true"
      [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
      pathColor="#333333"
    ></cds-icon>
    <span>Deal Ended</span>
  </div>

  <!-- Active timer -->
  <div [class]="timerState" class="wrapper" *ngIf="timerState === 'active'">
    <cds-icon
      class="timer-icon"
      [type]="2021"
      name="028-clock"
      aria-hidden="true"
      [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
      pathColor="#333333"
    ></cds-icon>
    <span [ngPlural]="months">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Month&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ months }} Months&nbsp;</ng-template>
    </span>
    <span [ngPlural]="weeks" *ngIf="monthsAndWeeks || weeksAndDays">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Week&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ weeks }} Weeks&nbsp;</ng-template>
    </span>
    <span [ngPlural]="days" *ngIf="weeksAndDays || daysAndHours">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Day&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ days }} Days&nbsp;</ng-template>
    </span>
    <span [ngPlural]="hours" *ngIf="daysAndHours || hoursAndMinutes">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Hour&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ hours }} Hours&nbsp;</ng-template>
    </span>
    <span [ngPlural]="minutes" *ngIf="hoursAndMinutes">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Minute&nbsp;</ng-template>
      <ng-template ngPluralCase="other"
        >{{ minutes }} Minutes&nbsp;</ng-template
      >
    </span>
    <span *ngIf="lessThan1Minute">Less than 1 Minute&nbsp;</span>
    <span [ngPlural]="seconds" *ngIf="displaySeconds">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Second&nbsp;</ng-template>
      <ng-template ngPluralCase="other"
        >{{ seconds }} Seconds&nbsp;</ng-template
      >
    </span>
  </div>

  <!-- Upcoming timer -->
  <div [class]="timerState" class="wrapper" *ngIf="timerState === 'upcoming'">
    <cds-icon
      class="timer-icon"
      [type]="2021"
      name="028-clock"
      aria-hidden="true"
      [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
      pathColor="#333333"
    ></cds-icon>
    <span>{{ customContent }}</span>
  </div>
</div>

`, isInline: true, styles: [".timer{font-family:Interstate_Regular;font-weight:400;font-size:14px;line-height:21px}.timer-icon{margin:4px 5px}.wrapper{display:inline-flex;flex-direction:row;align-items:center;border-radius:4px;padding-right:4px}.active{background-color:#ffedaa;padding-right:0}.upcoming{background-color:#c5e4f3}.ending{background-color:#ccc}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgPlural, selector: "[ngPlural]", inputs: ["ngPlural"] }, { kind: "directive", type: i2.NgPluralCase, selector: "[ngPluralCase]" }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-timer', template: `
  <div role="timer" class="timer">
  <!-- Deal Ended timer -->
  <div [class]="timerState" class="wrapper" *ngIf="timerState === 'ending'">
    <cds-icon
      class="timer-icon"
      [type]="2021"
      name="028-clock"
      aria-hidden="true"
      [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
      pathColor="#333333"
    ></cds-icon>
    <span>Deal Ended</span>
  </div>

  <!-- Active timer -->
  <div [class]="timerState" class="wrapper" *ngIf="timerState === 'active'">
    <cds-icon
      class="timer-icon"
      [type]="2021"
      name="028-clock"
      aria-hidden="true"
      [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
      pathColor="#333333"
    ></cds-icon>
    <span [ngPlural]="months">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Month&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ months }} Months&nbsp;</ng-template>
    </span>
    <span [ngPlural]="weeks" *ngIf="monthsAndWeeks || weeksAndDays">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Week&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ weeks }} Weeks&nbsp;</ng-template>
    </span>
    <span [ngPlural]="days" *ngIf="weeksAndDays || daysAndHours">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Day&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ days }} Days&nbsp;</ng-template>
    </span>
    <span [ngPlural]="hours" *ngIf="daysAndHours || hoursAndMinutes">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Hour&nbsp;</ng-template>
      <ng-template ngPluralCase="other">{{ hours }} Hours&nbsp;</ng-template>
    </span>
    <span [ngPlural]="minutes" *ngIf="hoursAndMinutes">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Minute&nbsp;</ng-template>
      <ng-template ngPluralCase="other"
        >{{ minutes }} Minutes&nbsp;</ng-template
      >
    </span>
    <span *ngIf="lessThan1Minute">Less than 1 Minute&nbsp;</span>
    <span [ngPlural]="seconds" *ngIf="displaySeconds">
      <ng-template ngPluralCase="=0"></ng-template>
      <ng-template ngPluralCase="=1">1 Second&nbsp;</ng-template>
      <ng-template ngPluralCase="other"
        >{{ seconds }} Seconds&nbsp;</ng-template
      >
    </span>
  </div>

  <!-- Upcoming timer -->
  <div [class]="timerState" class="wrapper" *ngIf="timerState === 'upcoming'">
    <cds-icon
      class="timer-icon"
      [type]="2021"
      name="028-clock"
      aria-hidden="true"
      [svgStyle]="{ 'width.px': 16, 'height.px': 16 }"
      pathColor="#333333"
    ></cds-icon>
    <span>{{ customContent }}</span>
  </div>
</div>

`, host: { class: 'cds-timer' }, styles: [".timer{font-family:Interstate_Regular;font-weight:400;font-size:14px;line-height:21px}.timer-icon{margin:4px 5px}.wrapper{display:inline-flex;flex-direction:row;align-items:center;border-radius:4px;padding-right:4px}.active{background-color:#ffedaa;padding-right:0}.upcoming{background-color:#c5e4f3}.ending{background-color:#ccc}\n"] }]
        }], propDecorators: { timerStart: [{
                type: Input
            }], timerEnd: [{
                type: Input
            }], timerState: [{
                type: Input
            }], customContent: [{
                type: Input
            }], currentTimeStart: [{
                type: Input
            }], displaySeconds: [{
                type: Input
            }] } });

class CdsTimerModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTimerModule, declarations: [CdsTimerComponent], imports: [CommonModule,
            CdsIconModule,
            CdsLoadingPlaceholderModule], exports: [CdsTimerComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimerModule, imports: [CommonModule,
            CdsIconModule,
            CdsLoadingPlaceholderModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimerModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsTimerComponent],
                    imports: [
                        CommonModule,
                        CdsIconModule,
                        CdsLoadingPlaceholderModule
                    ],
                    exports: [CdsTimerComponent]
                }]
        }] });
;

class CdsMessageBarComponent {
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_announcer) {
        this._announcer = _announcer;
        // #region ========== Input / Output ========== //
        /**
         * Determines the message to announce when close method is triggered.
         */
        this.announceCloseMessage = 'Message Bar Closed';
        // /**
        //  * Determines the element to focus onto when Message Bar is closed.
        //  * String should be a valid CSS selector.
        //  */
        // @Input() closeFocusSelector: string;
        /**
         * Show/Hide the message bar
         * @note Preferrable to use close() method to close the message bar as it will announce the message.
         */
        this.isOpen = true;
        /**
         * Determines the role of message bar
         */
        this.messageBarRole = 'alert';
        /**
         * Emits isOpen value when message bar is closed
         */
        this.isOpenChange = new EventEmitter();
    }
    // #endregion
    // #region ========== Methods ========== //
    close() {
        this.isOpen = false;
        this.isOpenChange.emit(this.isOpen);
        this._announcer.announce(this.announceCloseMessage);
        // this._focusCloseButton();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMessageBarComponent, deps: [{ token: CdsAnnouncerService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsMessageBarComponent, isStandalone: true, selector: "cds-message-bar", inputs: { announceCloseMessage: "announceCloseMessage", isOpen: "isOpen", messageBarRole: "messageBarRole", messageBarType: "messageBarType" }, outputs: { isOpenChange: "isOpenChange" }, host: { classAttribute: "cds-message-bar" }, ngImport: i0, template: `
    <div
      class="cmb-main"
      [class.cmb-closed]="!isOpen"
      [class.cmb-error]="messageBarType === 'error'"
      [class.cmb-info]="messageBarType === 'info'"
      [class.cmb-info-inverse]="messageBarType === 'infoInverse'"
      [class.cmb-success]="messageBarType === 'success'"
      [class.cmb-system]="messageBarType === 'system'"
      [class.cmb-warning]="messageBarType === 'warning'"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, styles: [".cmb-main{display:flex;gap:8px;border-radius:var(--cds-corner-xs, 4px);padding:8px;font-size:14px;background-color:var(--cds-message-bar-bg-info, #eff3f8);--cds-button-inline-link-sm-padding: 0}.cmb-main ::ng-deep .cds-message-bar-icon{--cds-icon-width: 24px;--cds-icon-height: 24px;--cds-icon-path-fill: #333333}.cmb-main ::ng-deep .cds-message-bar-content{flex:1 1 auto}.cmb-main ::ng-deep .cds-message-bar-title{margin:0;font-size:14px;line-height:20px;font-weight:700}.cmb-main ::ng-deep .cds-message-bar-close-btn button,.cmb-main ::ng-deep button.cds-message-bar-close-btn{--cds-icon-width: 16px;--cds-icon-height: 16px;border:none;background-color:transparent;padding:0;margin:0;width:24px;height:24px}.cmb-closed{display:none}.cmb-error{background-color:var(--cds-message-bar-bg-error, #ffebeb)}.cmb-error ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #d60000}.cmb-error ::ng-deep .cds-message-bar-title{color:var(--cds-message-bar-error-color, #d60000)}.cmb-info-inverse{background-color:var(--cds-message-bar-bg-info-inverse, #ffffff)}.cmb-info-inverse ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #333333}.cmb-success{background-color:var(--cds-message-bar-bg-success, #ebffed)}.cmb-success ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #006e0a}.cmb-system{background-color:var(--cds-message-bar-bg-system, #eeeeee)}.cmb-system ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #333333}.cmb-warning{background-color:var(--cds-message-bar-bg-warning, #fff4cc)}.cmb-warning ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #93671a}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMessageBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-message-bar', standalone: true, imports: [CommonModule], template: `
    <div
      class="cmb-main"
      [class.cmb-closed]="!isOpen"
      [class.cmb-error]="messageBarType === 'error'"
      [class.cmb-info]="messageBarType === 'info'"
      [class.cmb-info-inverse]="messageBarType === 'infoInverse'"
      [class.cmb-success]="messageBarType === 'success'"
      [class.cmb-system]="messageBarType === 'system'"
      [class.cmb-warning]="messageBarType === 'warning'"
    >
      <ng-content></ng-content>
    </div>
  `, host: { class: 'cds-message-bar' }, styles: [".cmb-main{display:flex;gap:8px;border-radius:var(--cds-corner-xs, 4px);padding:8px;font-size:14px;background-color:var(--cds-message-bar-bg-info, #eff3f8);--cds-button-inline-link-sm-padding: 0}.cmb-main ::ng-deep .cds-message-bar-icon{--cds-icon-width: 24px;--cds-icon-height: 24px;--cds-icon-path-fill: #333333}.cmb-main ::ng-deep .cds-message-bar-content{flex:1 1 auto}.cmb-main ::ng-deep .cds-message-bar-title{margin:0;font-size:14px;line-height:20px;font-weight:700}.cmb-main ::ng-deep .cds-message-bar-close-btn button,.cmb-main ::ng-deep button.cds-message-bar-close-btn{--cds-icon-width: 16px;--cds-icon-height: 16px;border:none;background-color:transparent;padding:0;margin:0;width:24px;height:24px}.cmb-closed{display:none}.cmb-error{background-color:var(--cds-message-bar-bg-error, #ffebeb)}.cmb-error ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #d60000}.cmb-error ::ng-deep .cds-message-bar-title{color:var(--cds-message-bar-error-color, #d60000)}.cmb-info-inverse{background-color:var(--cds-message-bar-bg-info-inverse, #ffffff)}.cmb-info-inverse ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #333333}.cmb-success{background-color:var(--cds-message-bar-bg-success, #ebffed)}.cmb-success ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #006e0a}.cmb-system{background-color:var(--cds-message-bar-bg-system, #eeeeee)}.cmb-system ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #333333}.cmb-warning{background-color:var(--cds-message-bar-bg-warning, #fff4cc)}.cmb-warning ::ng-deep .cds-message-bar-icon{--cds-icon-path-fill: #93671a}\n"] }]
        }], ctorParameters: () => [{ type: CdsAnnouncerService }], propDecorators: { announceCloseMessage: [{
                type: Input
            }], isOpen: [{
                type: Input
            }], messageBarRole: [{
                type: Input
            }], messageBarType: [{
                type: Input
            }], isOpenChange: [{
                type: Output
            }] } });

const COMPONENTS$F = [CdsPopup2Component];
class CdsPopup2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopup2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPopup2Module, declarations: [CdsPopup2Component], imports: [CommonModule, CdsIconModule], exports: [CdsPopup2Component] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopup2Module, imports: [CommonModule, CdsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopup2Module, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$F],
                    imports: [CommonModule, CdsIconModule],
                    exports: [...COMPONENTS$F]
                }]
        }] });

let _popupDialogUniqueIdCounter = 0;
class CdsPopupDialogComponent {
    /**
     * Sets id for popup2
     */
    set idStr(val) {
        this._idStr = val || this._uid;
    }
    get idStr() {
        return this._idStr || this._uid;
    }
    /**
     * Shows/Hides the popup
     */
    set show(isShown) {
        if (!!this._isShown !== !!isShown) {
            if (isShown) {
                this._open();
            }
            else {
                this._close();
            }
        }
    }
    get show() {
        return this._isShown;
    }
    // #endregion
    // #region ========== HostListener ========== //
    /**
     * Closes popup if clicking outside of popup.
     * @param e PointerEvent
     *
     * NOTE: Using 'mousedown'/'touchstart' instead of 'click'
     * to prevent close/open happening at same time in certain cases.
     */
    _documentMousedown(e) {
        if (this._isShown && !this.isKeptOpenOnOutsideClick) {
            const isInPopup = this.popup2Container.nativeElement.contains(e.target);
            const isInPopupHost = this.popupHost?.contains(e.target);
            if (!isInPopup && !isInPopupHost) {
                this._closeFromWithinComponent();
            }
        }
    }
    // Automatically scrolls to focused element in popup if off screen
    onTabHandler(event) {
        const container = this.popupDialog;
        setTimeout(() => {
            let activeEl = document.activeElement;
            if (container?.nativeElement == activeEl || container?.nativeElement.contains(activeEl)) {
                this.scrollIntoView(activeEl);
            }
        });
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_doc, _announcer, _cdr, _renderer) {
        this._doc = _doc;
        this._announcer = _announcer;
        this._cdr = _cdr;
        this._renderer = _renderer;
        this._tabIndex = 0;
        this._closeEventUnsub$ = new Subject();
        /**
         * aria-label for popup close button
         */
        this.closeAriaLabel = 'popup close';
        this._uid = `CdsPopupDialog_${_popupDialogUniqueIdCounter++}`;
        /**
         * Keep open if escape key pressed
         * (Don't display in documentation. For Single Step only)
         */
        // @Input() isKeptOpenOnEscape: boolean = false;
        /**
         * Keep open if Focus leaves popup
         * (Don't display in documentation. For Single Step only)
         */
        this.isKeptOpenOnFocusout = false;
        /**
         * Keep open if click event happens outside popup
         * (Don't display in documentation. For Single Step only)
         */
        this.isKeptOpenOnOutsideClick = false;
        /**
         * Keep open on scroll/resize events
         * (Don't display in documentation. For Single Step only)
         */
        this.isKeptOpenOnScrollResize = true;
        /**
         * Change the popup2 role
         * (Don't display in documentation. For Single Step only)
         */
        this.ariaRole = 'dialog';
        /**
         * Enables two way binding for show
         */
        this.showChange = new EventEmitter();
        /**
         * Title aria-level
         */
        this.titleAriaLevel = 2;
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    ngOnDestroy() {
        this._closeEventUnsub$.next();
        this._closeEventUnsub$.complete();
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * scrolls element into view
     */
    scrollIntoView(activeEl) {
        let prevScrollBehavior = this.isKeptOpenOnScrollResize;
        this.isKeptOpenOnScrollResize = true;
        let rect = activeEl.getBoundingClientRect();
        let height = rect.bottom - rect.top;
        if (rect.top < 0) {
            window.scrollTo({ top: rect.top + window.scrollY - 32 });
        }
        else if (rect.bottom > window.innerHeight && height < window.innerHeight) {
            window.scrollTo({ top: rect.bottom + window.scrollY - window.innerHeight + 32 });
        }
        // Timeout makes the poup not close on autoscroll
        setTimeout(() => {
            // return to dafault behavior
            this.isKeptOpenOnScrollResize = prevScrollBehavior;
        }, 500);
    }
    /**
     * closes popup
     */
    _close() {
        setTimeout(() => {
            // Focus back on button before removing popup from DOM
            this.popupHost?.focus();
            // Unsubscribe from reposition listening events and close popup
            this._closeEventUnsub$.next();
            this._isShown = false;
            this._isVisible = false;
            this._tabIndex = 0;
        }, 0);
    }
    /**
     * Use this method if 'close' is triggered from within component and not from 'show' input.
     */
    _closeFromWithinComponent() {
        this._close();
        // Emitter can not be inside the _close() method since _close() is also called from 'show' @Input.
        this.showChange.emit(false);
    }
    /**
     * Prevents the escape key from propagating to parent elements (such as modal) when popup is open.
     */
    _escapeKeydown(e) {
        if (this.show) {
            e.stopPropagation();
            this._closeFromWithinComponent();
        }
    }
    /**
     * If focus leaves popup, then it is closed
     * @param e FocusEvent
     */
    _focusoutPopup2(e) {
        const isInPopup = this.popup2Container.nativeElement.contains(e.relatedTarget);
        if (this._isShown && !isInPopup && !this.isKeptOpenOnFocusout) {
            this._closeFromWithinComponent();
        }
    }
    /**
     * Opens popup, then sets up listeners to reposition popup on resize/scroll/etc.
     */
    _open() {
        // adds popup to DOM, but should be hidden first
        this._isShown = true;
        // setTimeout allows popup to be added to DOM first (so it can get dimensions while still hidden)
        setTimeout(() => {
            // Gets dimensions and positions the popup on open
            this._repositionPopupDialog();
            // Will listen for other events and reposition popup
            const fromScroll$ = fromEvent(this._window, 'scroll');
            const fromResize$ = fromEvent(this._window, 'resize');
            const fromOrientation$ = fromEvent(this._window, 'orientationchange');
            const scrollParents$ = getScrollParents(this.popup2Container.nativeElement)
                .filter(el => el?.nodeName?.toLowerCase() !== 'html')
                .map(elem => fromEvent(elem, 'scroll'));
            merge(fromResize$, fromScroll$, fromOrientation$, ...scrollParents$)
                .pipe(takeUntil$1(this._closeEventUnsub$), throttleTime(10, null, { leading: false, trailing: true }))
                .subscribe(obv => {
                if (!this.isKeptOpenOnScrollResize) {
                    // If popup is collapsed on scroll/resize and the popupHost is currently focused,
                    // then it needs to announce what was collapsed
                    if (this._document.activeElement === this.popupHost) {
                        // this._announcer.announce(`${this.titleText} popup`, 200);
                        this._isVisible = false;
                        setTimeout(() => {
                            this._closeFromWithinComponent();
                        }, 400);
                    }
                    else {
                        this._closeFromWithinComponent();
                    }
                }
                else {
                    if (obv.type == 'resize' || obv.type == 'orientationchange') {
                        setTimeout(() => {
                            this._repositionPopupDialog();
                        }, 750);
                    }
                    else {
                        //scroll event can update faster
                        this._repositionPopupDialog();
                    }
                }
            });
            // Will listen for escape event on host element (button) and stop propagation
            fromEvent(this.popupHost, 'keydown')
                .pipe(takeUntil$1(this._closeEventUnsub$))
                .subscribe((e) => {
                if (e.key === 'Escape') {
                    this._escapeKeydown(e);
                }
            });
            // Now that popup has been positioned, we can now make it visible
            this._isVisible = true;
            if (this.focusTrapDirective?.focusTrap?.hasAttached()) {
                this.focusTrapDirective.focusTrap.focusInitialElementWhenReady();
                setTimeout(() => {
                    this.scrollIntoView(document.activeElement);
                    this._tabIndex = -1;
                });
            }
        }, 0);
    }
    /**
     * Gets dimensions of popup and host button and positions popup accordingly
     */
    _repositionPopupDialog() {
        if (this.popupHost && this.popup2Container) {
            const hostRect = this.popupHost?.getBoundingClientRect();
            const caretRect = this.popup2Container.nativeElement?.querySelector('.cds-popup2-caret')?.getBoundingClientRect();
            const containerRect = this.popup2Container.nativeElement.getBoundingClientRect();
            const hostHorizontalCenter = hostRect.left + (hostRect.width / 2);
            const hostVerticalCenter = hostRect.top + (hostRect.height / 2);
            switch (this.position) {
                case 'right-middle':
                    this._containerTop = hostVerticalCenter - (containerRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
                case 'right-bottom':
                    this._containerTop = (hostVerticalCenter - containerRect.height) + (caretRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
                case 'left-top':
                    this._containerTop = hostVerticalCenter - (caretRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'left-middle':
                    this._containerTop = hostVerticalCenter - (containerRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'left-bottom':
                    this._containerTop = (hostVerticalCenter - containerRect.height) + (caretRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'below-left':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = hostHorizontalCenter - (caretRect.width / 2);
                    break;
                case 'below-center':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = hostHorizontalCenter - (containerRect.width / 2);
                    break;
                case 'below-right':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = (hostHorizontalCenter - containerRect.width) + (caretRect.width / 2);
                    break;
                case 'above-left':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = hostHorizontalCenter - (caretRect.width / 2);
                    break;
                case 'above-center':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = hostHorizontalCenter - (containerRect.width / 2);
                    break;
                case 'above-right':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = (hostHorizontalCenter - containerRect.width) + (caretRect.width / 2);
                    break;
                case 'right-top':
                // falls through to default
                default:
                    this._containerTop = hostVerticalCenter - (caretRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
            }
            // Round the numbers
            this._containerTop = Math.round(this._containerTop);
            this._containerLeft = Math.round(this._containerLeft);
        }
    }
    /**
     * Repositions popup2 component.
     * If your content changes while the Popup2 is open it may affect the position of the popup2
     * You may need to reposition the popup after the content is changed (after DOM has been updated)
     */
    reposition() {
        if (this.show) {
            this._repositionPopupDialog();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupDialogComponent, deps: [{ token: DOCUMENT }, { token: i1$1.LiveAnnouncer }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPopupDialogComponent, selector: "cds-popup-dialog", inputs: { closeAriaLabel: "closeAriaLabel", idStr: "idStr", isInverse: "isInverse", showOverlay: "showOverlay", isKeptOpenOnFocusout: "isKeptOpenOnFocusout", isKeptOpenOnOutsideClick: "isKeptOpenOnOutsideClick", isKeptOpenOnScrollResize: "isKeptOpenOnScrollResize", maxWidth: "maxWidth", popupHost: "popupHost", position: "position", ariaRole: "ariaRole", show: "show", titleAriaLevel: "titleAriaLevel", titleText: "titleText" }, outputs: { showChange: "showChange" }, host: { listeners: { "focusout": "_focusoutPopup2($event)", "document:mousedown": "_documentMousedown($event)", "document:touchstart": "_documentMousedown($event)", "document:keydown.tab": "onTabHandler($event)", "document:keydown.shift.tab": "onTabHandler($event)" }, classAttribute: "cds-popup-dialog" }, viewQueries: [{ propertyName: "popup2Container", first: true, predicate: ["popup2Container"], descendants: true }, { propertyName: "popupDialog", first: true, predicate: ["popupDialog"], descendants: true }, { propertyName: "closeBtn", first: true, predicate: ["closeBtn"], descendants: true }, { propertyName: "focusTrapDirective", first: true, predicate: ["focusTrapDirective"], descendants: true }], ngImport: i0, template: `
    <div cdsAriaHideOuterDom [isOuterDomAriaHidden]="_isVisible" [attr.aria-hidden]="!_isVisible">
      <div
        #popup2Container
        *ngIf="show"
        class="cds-popup2-container"
        [attr.role]="ariaRole"
        [attr.aria-label]="titleText"
        [id]="idStr"
        [ngClass]="position"
        [class.cds-popup2-dark]="!isInverse"
        [class.cds-popup2-visible]="_isVisible"
        [style.maxWidth]="maxWidth"
        [style.top.px]="_containerTop"
        [style.left.px]="_containerLeft"
        (keydown.escape)="_escapeKeydown($event)"
      >
        <div class="cds-popup2-caret-wrapper">
          <div class="cds-popup2-caret"></div>
        </div>
        <div
          #popupDialog
          [attr.role]="ariaRole"
          class="cds-popup2-bubble cds-popup2-grid-wrapper"
          cdkTrapFocus
          #focusTrapDirective="cdkTrapFocus"
          [tabIndex]="_tabIndex"
        >
        <!-- #region grid-close -->
          <div class="cds-popup2-close cds-popup2-grid-close">
            <button
              #closeBtn
              class="cds-popup2-close-button"
              type="button"
              [attr.aria-label]="closeAriaLabel"
              (click)="_closeFromWithinComponent()"
            >
              <cds-icon
                type="2021"
                name="044-close"
                aria-hidden="true"
                [svgStyle]="{'width.px': 16,'height.px': 16}"
              ></cds-icon>
            </button>
          </div>
          <!-- #endregion grid-close -->

          <!-- #region grid-title -->
          <div class="cds-popup2-title cds-popup2-grid-title">
            <div role="heading" [attr.aria-level]="titleAriaLevel">{{titleText}}</div>
          </div>
          <!-- #endregion grid-title -->

          <!-- #region grid-content -->
          <div class="cds-popup2-content cds-popup2-grid-content">
            <ng-content></ng-content>
          </div>
          <!-- #endregion grid-content -->

        </div>
      </div>
     <div
          class="cds-modal-backdrop"
          [class.cds-modal-show]="show && showOverlay"
          [class.cds-modal-fade]="show && !showOverlay"
          [style.display]="_isVisible ? 'block' : 'none'"
        >
      </div>
    </div>
  `, isInline: true, styles: [".cds-popup2-container{width:100%;max-width:322px;display:flex;visibility:hidden;z-index:10001;position:fixed}.cds-popup2-visible{visibility:visible}.cds-popup2-caret-wrapper{flex:0 0 22px;overflow:hidden;display:flex;align-items:flex-start}.cds-popup2-caret{width:22px;height:76px;display:flex;align-items:center;margin:0 -12px 0 auto}.cds-popup2-caret:before{content:\"\";display:flex;width:20px;height:20px;background-color:#fcfcfc;box-shadow:0 1px 10px #0000001f;transform:rotate(45deg)}.cds-popup2-bubble{flex-grow:1;padding:16px;background-color:#fcfcfc;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-popup2-title{font-size:18px;font-weight:700;line-height:24px;flex-grow:1}.cds-popup2-close-button{border:none;background:none;padding:0;width:24px;height:24px}.cds-popup2-grid-wrapper{display:grid;row-gap:8px;grid-template-columns:auto 0fr;grid-template-areas:\"title close\" \"main main\"}.cds-popup2-grid-title{grid-area:title}.cds-popup2-grid-content{grid-area:main}.cds-popup2-grid-close{grid-area:close}.cds-popup2-dark .cds-popup2-caret:before,.cds-popup2-dark .cds-popup2-bubble{background-color:#333;color:#fff}.cds-popup2-dark .cds-popup2-close-button ::ng-deep path{fill:#fff}.right-middle .cds-popup2-caret-wrapper,.left-middle .cds-popup2-caret-wrapper{align-items:center}.right-bottom .cds-popup2-caret-wrapper,.left-bottom .cds-popup2-caret-wrapper{align-items:flex-end;align-items:end}.left-top,.left-middle,.left-bottom{flex-direction:row-reverse}.left-top .cds-popup2-caret,.left-middle .cds-popup2-caret,.left-bottom .cds-popup2-caret{margin:0 auto 0 -12px}.below-left,.below-center,.below-right,.above-left,.above-center,.above-right{max-width:300px}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret,.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{width:76px;height:22px;justify-content:center}.below-left,.below-center,.below-right{flex-direction:column}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret{margin:auto 0 -12px}.above-left,.above-center,.above-right{flex-direction:column-reverse}.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{margin:-12px 0 auto}.below-center .cds-popup2-caret-wrapper,.above-center .cds-popup2-caret-wrapper{justify-content:center}.below-right .cds-popup2-caret-wrapper,.above-right .cds-popup2-caret-wrapper{justify-content:flex-end;justify-content:right}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }, { kind: "directive", type: CdsAriaHideOuterDomDirective, selector: "[cdsAriaHideOuterDom]", inputs: ["isOuterDomAriaHidden", "outerDomExceptions"], exportAs: ["cdsAriaHideOuterDom"] }, { kind: "directive", type: i1$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-popup-dialog', template: `
    <div cdsAriaHideOuterDom [isOuterDomAriaHidden]="_isVisible" [attr.aria-hidden]="!_isVisible">
      <div
        #popup2Container
        *ngIf="show"
        class="cds-popup2-container"
        [attr.role]="ariaRole"
        [attr.aria-label]="titleText"
        [id]="idStr"
        [ngClass]="position"
        [class.cds-popup2-dark]="!isInverse"
        [class.cds-popup2-visible]="_isVisible"
        [style.maxWidth]="maxWidth"
        [style.top.px]="_containerTop"
        [style.left.px]="_containerLeft"
        (keydown.escape)="_escapeKeydown($event)"
      >
        <div class="cds-popup2-caret-wrapper">
          <div class="cds-popup2-caret"></div>
        </div>
        <div
          #popupDialog
          [attr.role]="ariaRole"
          class="cds-popup2-bubble cds-popup2-grid-wrapper"
          cdkTrapFocus
          #focusTrapDirective="cdkTrapFocus"
          [tabIndex]="_tabIndex"
        >
        <!-- #region grid-close -->
          <div class="cds-popup2-close cds-popup2-grid-close">
            <button
              #closeBtn
              class="cds-popup2-close-button"
              type="button"
              [attr.aria-label]="closeAriaLabel"
              (click)="_closeFromWithinComponent()"
            >
              <cds-icon
                type="2021"
                name="044-close"
                aria-hidden="true"
                [svgStyle]="{'width.px': 16,'height.px': 16}"
              ></cds-icon>
            </button>
          </div>
          <!-- #endregion grid-close -->

          <!-- #region grid-title -->
          <div class="cds-popup2-title cds-popup2-grid-title">
            <div role="heading" [attr.aria-level]="titleAriaLevel">{{titleText}}</div>
          </div>
          <!-- #endregion grid-title -->

          <!-- #region grid-content -->
          <div class="cds-popup2-content cds-popup2-grid-content">
            <ng-content></ng-content>
          </div>
          <!-- #endregion grid-content -->

        </div>
      </div>
     <div
          class="cds-modal-backdrop"
          [class.cds-modal-show]="show && showOverlay"
          [class.cds-modal-fade]="show && !showOverlay"
          [style.display]="_isVisible ? 'block' : 'none'"
        >
      </div>
    </div>
  `, host: {
                        class: 'cds-popup-dialog',
                        '(focusout)': '_focusoutPopup2($event)',
                    }, styles: [".cds-popup2-container{width:100%;max-width:322px;display:flex;visibility:hidden;z-index:10001;position:fixed}.cds-popup2-visible{visibility:visible}.cds-popup2-caret-wrapper{flex:0 0 22px;overflow:hidden;display:flex;align-items:flex-start}.cds-popup2-caret{width:22px;height:76px;display:flex;align-items:center;margin:0 -12px 0 auto}.cds-popup2-caret:before{content:\"\";display:flex;width:20px;height:20px;background-color:#fcfcfc;box-shadow:0 1px 10px #0000001f;transform:rotate(45deg)}.cds-popup2-bubble{flex-grow:1;padding:16px;background-color:#fcfcfc;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-popup2-title{font-size:18px;font-weight:700;line-height:24px;flex-grow:1}.cds-popup2-close-button{border:none;background:none;padding:0;width:24px;height:24px}.cds-popup2-grid-wrapper{display:grid;row-gap:8px;grid-template-columns:auto 0fr;grid-template-areas:\"title close\" \"main main\"}.cds-popup2-grid-title{grid-area:title}.cds-popup2-grid-content{grid-area:main}.cds-popup2-grid-close{grid-area:close}.cds-popup2-dark .cds-popup2-caret:before,.cds-popup2-dark .cds-popup2-bubble{background-color:#333;color:#fff}.cds-popup2-dark .cds-popup2-close-button ::ng-deep path{fill:#fff}.right-middle .cds-popup2-caret-wrapper,.left-middle .cds-popup2-caret-wrapper{align-items:center}.right-bottom .cds-popup2-caret-wrapper,.left-bottom .cds-popup2-caret-wrapper{align-items:flex-end;align-items:end}.left-top,.left-middle,.left-bottom{flex-direction:row-reverse}.left-top .cds-popup2-caret,.left-middle .cds-popup2-caret,.left-bottom .cds-popup2-caret{margin:0 auto 0 -12px}.below-left,.below-center,.below-right,.above-left,.above-center,.above-right{max-width:300px}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret,.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{width:76px;height:22px;justify-content:center}.below-left,.below-center,.below-right{flex-direction:column}.below-left .cds-popup2-caret,.below-center .cds-popup2-caret,.below-right .cds-popup2-caret{margin:auto 0 -12px}.above-left,.above-center,.above-right{flex-direction:column-reverse}.above-left .cds-popup2-caret,.above-center .cds-popup2-caret,.above-right .cds-popup2-caret{margin:-12px 0 auto}.below-center .cds-popup2-caret-wrapper,.above-center .cds-popup2-caret-wrapper{justify-content:center}.below-right .cds-popup2-caret-wrapper,.above-right .cds-popup2-caret-wrapper{justify-content:flex-end;justify-content:right}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1$1.LiveAnnouncer }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }], propDecorators: { popup2Container: [{
                type: ViewChild,
                args: ['popup2Container']
            }], popupDialog: [{
                type: ViewChild,
                args: ['popupDialog']
            }], closeBtn: [{
                type: ViewChild,
                args: ['closeBtn']
            }], focusTrapDirective: [{
                type: ViewChild,
                args: ['focusTrapDirective']
            }], closeAriaLabel: [{
                type: Input
            }], idStr: [{
                type: Input
            }], isInverse: [{
                type: Input
            }], showOverlay: [{
                type: Input
            }], isKeptOpenOnFocusout: [{
                type: Input
            }], isKeptOpenOnOutsideClick: [{
                type: Input
            }], isKeptOpenOnScrollResize: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], popupHost: [{
                type: Input
            }], position: [{
                type: Input
            }], ariaRole: [{
                type: Input
            }], show: [{
                type: Input
            }], showChange: [{
                type: Output
            }], titleAriaLevel: [{
                type: Input
            }], titleText: [{
                type: Input
            }], _documentMousedown: [{
                type: HostListener,
                args: ['document:mousedown', ['$event']]
            }, {
                type: HostListener,
                args: ['document:touchstart', ['$event']]
            }], onTabHandler: [{
                type: HostListener,
                args: ['document:keydown.tab', ['$event']]
            }, {
                type: HostListener,
                args: ['document:keydown.shift.tab', ['$event']]
            }] } });

const COMPONENTS$E = [CdsPopupDialogComponent];
class CdsPopupDialogModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupDialogModule, declarations: [CdsPopupDialogComponent], imports: [CommonModule,
            CdsIconModule,
            CdsFocusTrapModule,
            CdsAriaHideOuterDomModule,
            A11yModule], exports: [CdsPopupDialogComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupDialogModule, imports: [CommonModule,
            CdsIconModule,
            CdsFocusTrapModule,
            CdsAriaHideOuterDomModule,
            A11yModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPopupDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$E],
                    imports: [
                        CommonModule,
                        CdsIconModule,
                        CdsFocusTrapModule,
                        CdsAriaHideOuterDomModule,
                        A11yModule
                    ],
                    exports: [...COMPONENTS$E]
                }]
        }] });

let nextUniqueId$d = 0;
class CdsPageDotsComponent {
    constructor(_liveAnnouncer, el, _ngZone) {
        this._liveAnnouncer = _liveAnnouncer;
        this.el = el;
        this._ngZone = _ngZone;
        this._currentPage = 1;
        this._isclickable = false;
        this._uid = `cds-pagedots-${nextUniqueId$d++}`;
        this.announceStr = 'Page';
        this.currentPageChange = new EventEmitter();
        this.totalPagesChange = new EventEmitter();
    }
    set currentPage(pageNumber) {
        this._currentPage = Math.max(1, pageNumber);
        this.updatePage;
    }
    get currentPage() {
        return this._currentPage;
    }
    set totalPages(pageNumber) {
        this._totalPages = Math.max(2, pageNumber);
        this.totalPagesArray = [];
        for (let i = 1; i <= this._totalPages; i++) {
            this.totalPagesArray.push(i);
        }
    }
    get totalPages() {
        return this._totalPages;
    }
    // @Output() activePageNumberChange: EventEmitter<number> = new EventEmitter();
    currentPageLimit(pageNumber) {
        return Math.min(this.totalPages, Math.max(1, pageNumber));
    }
    setPagedotsId(page) {
        return this._uid + '-' + page;
    }
    nextPage(page) {
        this.currentPage = page;
        this.totalPagesChange.emit(this.totalPages);
        this.currentPageChange.emit(this.currentPage);
        // this.activePageNumberChange.emit(this.currentPage);
        this.reCastFocus(page);
    }
    reCastFocus(page) {
        const focusele = '#' + this._uid + '-' + page;
        onStable(this._ngZone, () => this.el.nativeElement.querySelector(focusele).focus());
    }
    announceTotalPages() {
        return this.announceStr + ' with ' + this.totalPages + ' ' + this.announceStr;
    }
    announcePage(page) {
        let announcepage = '';
        return page === this.totalPages ? 'Last page, ' + this.announceStr + ' ' + page : this.announceStr + ' ' + page;
    }
    updatePage() {
        //makes sure the values are valid
        this.totalPages = Math.max(2, this.totalPages);
        this._currentPage = this.currentPageLimit(this.currentPage);
        //emits the changes
        this.totalPagesChange.emit(this.totalPages);
        this.currentPageChange.emit(this.currentPage);
    }
    ngOnInit() {
        this._isclickable = this.isclickable;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageDotsComponent, deps: [{ token: i1$1.LiveAnnouncer }, { token: i0.ElementRef }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPageDotsComponent, selector: "cds-page-dots", inputs: { announceStr: "announceStr", isclickable: "isclickable", currentPage: "currentPage", totalPages: "totalPages" }, outputs: { currentPageChange: "currentPageChange", totalPagesChange: "totalPagesChange" }, host: { classAttribute: "cds-page-dots" }, ngImport: i0, template: `
    <nav aria-label="Pagination">
      <ul class="ul-clickable" *ngIf="isclickable">
        <li class="misc-dot-state-next" *ngFor="let page of totalPagesArray; index as i">
          <a href="javascript:;" class="clickable-dot" [attr.id]="setPagedotsId(page)" (keyup.enter)="nextPage(page)" (click)="nextPage(page)" [attr.aria-label]="announcePage(page)" tabindex="0" *ngIf="page != currentPage"></a>
          <!-- <span class="dot current" [attr.id]="setPagedotsId(page)" [attr.aria-label]="'Current page ' + page" tabindex="0" *ngIf="page == currentPage"></span> -->
          <a href="javascript:;" class="clickable-dot clickable-current" [attr.id]="setPagedotsId(page)" aria-current="true" [attr.aria-label]="announcePage(page)" tabindex="0" *ngIf="page == currentPage"></a>
        </li> 
      </ul>
    
      <div *ngIf="!isclickable">
        <span class="misc-dot-state-next" *ngFor="let page of totalPagesArray">
          <span class="dot" *ngIf="page < currentPage">
          </span>
          <span class="dot current" *ngIf="page == currentPage">
          </span> 
          <span class="dot" *ngIf="page >= currentPage && page < totalPages"></span>
        </span>
      </div>
    </nav>
  `, isInline: true, styles: [".dot{height:8px;width:8px;background-color:#ccc;border-radius:4px;display:inline-block;padding:4px;margin:8px;border:none}.clickable-dot{position:relative;height:8px;width:8px;background-color:#cecece;border-radius:4px;display:inline-block;padding:4px;margin:8px 8px 8px 4px;border:none}.clickable-dot:focus:before{content:\"\";display:inline-block;position:absolute;height:28px;width:28px;border:1px solid #002a54;border-radius:8px;top:-10px;left:-10px}.clickable-dot:hover,.clickable-dot:focus{outline:none;height:8px;width:8px;background-color:#888;border-radius:4px;display:inline-block;padding:4px;margin:8px 8px 8px 4px}.current{width:16px;background-color:#056dae}.current:focus:before{left:-6px}.clickable-current{width:16px!important;background-color:#056dae}.clickable-current:focus:before{left:-6px}span.announcePage{position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden}.misc-dot-state-next{height:24px;width:24px;display:inline}.ul-clickable{height:24px;width:24px;display:inline;padding:0}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageDotsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-page-dots', host: { class: 'cds-page-dots' }, template: `
    <nav aria-label="Pagination">
      <ul class="ul-clickable" *ngIf="isclickable">
        <li class="misc-dot-state-next" *ngFor="let page of totalPagesArray; index as i">
          <a href="javascript:;" class="clickable-dot" [attr.id]="setPagedotsId(page)" (keyup.enter)="nextPage(page)" (click)="nextPage(page)" [attr.aria-label]="announcePage(page)" tabindex="0" *ngIf="page != currentPage"></a>
          <!-- <span class="dot current" [attr.id]="setPagedotsId(page)" [attr.aria-label]="'Current page ' + page" tabindex="0" *ngIf="page == currentPage"></span> -->
          <a href="javascript:;" class="clickable-dot clickable-current" [attr.id]="setPagedotsId(page)" aria-current="true" [attr.aria-label]="announcePage(page)" tabindex="0" *ngIf="page == currentPage"></a>
        </li> 
      </ul>
    
      <div *ngIf="!isclickable">
        <span class="misc-dot-state-next" *ngFor="let page of totalPagesArray">
          <span class="dot" *ngIf="page < currentPage">
          </span>
          <span class="dot current" *ngIf="page == currentPage">
          </span> 
          <span class="dot" *ngIf="page >= currentPage && page < totalPages"></span>
        </span>
      </div>
    </nav>
  `, styles: [".dot{height:8px;width:8px;background-color:#ccc;border-radius:4px;display:inline-block;padding:4px;margin:8px;border:none}.clickable-dot{position:relative;height:8px;width:8px;background-color:#cecece;border-radius:4px;display:inline-block;padding:4px;margin:8px 8px 8px 4px;border:none}.clickable-dot:focus:before{content:\"\";display:inline-block;position:absolute;height:28px;width:28px;border:1px solid #002a54;border-radius:8px;top:-10px;left:-10px}.clickable-dot:hover,.clickable-dot:focus{outline:none;height:8px;width:8px;background-color:#888;border-radius:4px;display:inline-block;padding:4px;margin:8px 8px 8px 4px}.current{width:16px;background-color:#056dae}.current:focus:before{left:-6px}.clickable-current{width:16px!important;background-color:#056dae}.clickable-current:focus:before{left:-6px}span.announcePage{position:absolute;left:-10000px;top:auto;width:1px;height:1px;overflow:hidden}.misc-dot-state-next{height:24px;width:24px;display:inline}.ul-clickable{height:24px;width:24px;display:inline;padding:0}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i0.ElementRef }, { type: i0.NgZone }], propDecorators: { announceStr: [{
                type: Input
            }], isclickable: [{
                type: Input
            }], currentPage: [{
                type: Input
            }], totalPages: [{
                type: Input
            }], currentPageChange: [{
                type: Output
            }], totalPagesChange: [{
                type: Output
            }] } });

class CdsPageDotsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageDotsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPageDotsModule, declarations: [CdsPageDotsComponent], imports: [CommonModule], exports: [CdsPageDotsComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageDotsModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPageDotsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsPageDotsComponent],
                    imports: [CommonModule],
                    exports: [CdsPageDotsComponent]
                }]
        }] });

let _tourUniqueIdCounter = 0;
class CdsTourComponent {
    /**
     * Sets id for tour
     */
    set idStr(val) {
        this._idStr = val || this._uid;
    }
    get idStr() {
        return this._idStr || this._uid;
    }
    set showModal(show) {
        this._showModal = show;
    }
    /**
     * Shows/Hides the tour
     */
    set show(isShown) {
        if (isShown) {
            this._open();
        }
        else {
            this._close();
        }
    }
    get show() {
        return this._isShown;
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_doc, _announcer, _renderer) {
        this._doc = _doc;
        this._announcer = _announcer;
        this._renderer = _renderer;
        // #region ========== Properties ========== //
        this.stepArr = [];
        this._showModal = false;
        this._showButton = false;
        this._closeEventUnsub$ = new Subject();
        this._attributesToRemoveUponClose = [];
        // #endregion
        // #region ========== Input / Output ========== //
        /**
         * aria-label for tour close button
         */
        this.closeAriaLabel = 'tour close';
        /**
       * @property ariaRole
       * @description the ariaRole to define a role for the modal
       * @return string
       */
        this.ariaRole = 'dialog';
        this._uid = `CdsTour_${_tourUniqueIdCounter++}`;
        /**
         * Title aria-level
         */
        this.titleAriaLevel = 1;
        /**
        * Show back button
        */
        this.showBackButton = true;
        /**
        * Show next button
        */
        this.showNextButton = true;
        /**
        * Show Done Button
        */
        this.showDoneButton = false;
        /**
        * Hide the page dots for the tracker
        */
        this.hidePageDots = false;
        /**
         * Enables two way binding for show
         */
        this.onChangeBackBtn = new EventEmitter();
        this.onChangeNextBtn = new EventEmitter();
        this.onChangeDoneBtn = new EventEmitter();
        this.onChangeCloseBtn = new EventEmitter();
        this.onChangeShow = new EventEmitter();
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    ngOnInit() {
        // this.isBackButton = true
    }
    ngAfterViewInit() {
        this._announcer.announce(`Step ${this.currentStep} of ${this.totalSteps}`);
    }
    ngOnDestroy() {
        this._closeEventUnsub$.next();
        this._closeEventUnsub$.complete();
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * closes tour
     */
    _close() {
        this._closeEventUnsub$.next();
        this._isShown = false;
        this._isVisible = false;
    }
    /**
     * Use this method if 'close' is triggered from within component and not from 'show' input.
     */
    _closeFromWithinComponent() {
        this._close();
        // Emitter can not be inside the _close() method since _close() is also called from 'show' @Input.
        this.onChangeCloseBtn.emit();
    }
    onBackBtnClick() {
        this.onChangeBackBtn.emit();
    }
    onNextBtnClick() {
        this.onChangeNextBtn.emit();
    }
    onDoneBtnClick() {
        this.onChangeDoneBtn.emit();
    }
    /**
     * If focus leaves tour, then it is closed
     * @param e FocusEvent
     */
    _focusoutTour(e) {
        const isInTour = this.tourContainer.nativeElement.contains(e.relatedTarget);
        if (this._isShown && !isInTour && !this.isKeptOpenOnFocusout) {
            this._closeFromWithinComponent();
        }
    }
    /**
     * Opens tour, then sets up listeners to reposition tour on resize/scroll/etc.
     */
    _open() {
        // adds tour to DOM, but should be hidden first
        this._isShown = true;
        // setTimeout allows tour to be added to DOM first (so it can get dimensions while still hidden)
        setTimeout(() => {
            this._focusInModal();
            // Gets dimensions and positions the tour on open
            this._repositionTour();
            // Will listen for other events and reposition tour
            const fromScroll$ = fromEvent(this._window, 'scroll');
            const fromResize$ = fromEvent(this._window, 'resize');
            const scrollParents$ = getScrollParents(this.tourContainer.nativeElement)
                .filter(el => el?.nodeName?.toLowerCase() !== 'html')
                .map(elem => fromEvent(elem, 'scroll'));
            merge(fromResize$, fromScroll$, ...scrollParents$)
                .pipe(takeUntil$1(this._closeEventUnsub$), throttleTime(10, null, { leading: false, trailing: true }))
                .subscribe(obv => {
                this._repositionTour();
            });
            // Now that tour has been positioned, we can now make it visible
            this._isVisible = true;
            // OPEN EVENT
        }, 0);
        this.onChangeShow.emit();
    }
    /**
     * Gets dimensions of tour and host button and positions tour accordingly
     */
    _repositionTour() {
        if (this.tourHost && this.tourContainer) {
            const hostRect = this.tourHost?.getBoundingClientRect();
            const caretRect = this.tourContainer.nativeElement?.querySelector('.cds-tour-caret')?.getBoundingClientRect();
            const containerRect = this.tourContainer.nativeElement.getBoundingClientRect();
            const hostHorizontalCenter = hostRect.left + (hostRect.width / 2);
            const hostVerticalCenter = hostRect.top + (hostRect.height / 2);
            switch (this.position) {
                case 'right-middle':
                    this._containerTop = hostVerticalCenter - (containerRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
                case 'right-bottom':
                    this._containerTop = (hostVerticalCenter - containerRect.height) + (caretRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
                case 'left-top':
                    this._containerTop = hostVerticalCenter - (caretRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'left-middle':
                    this._containerTop = hostVerticalCenter - (containerRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'left-bottom':
                    this._containerTop = (hostVerticalCenter - containerRect.height) + (caretRect.height / 2);
                    this._containerLeft = hostRect.left - containerRect.width;
                    break;
                case 'below-left':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = hostHorizontalCenter - (caretRect.width / 2);
                    break;
                case 'below-center':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = hostHorizontalCenter - (containerRect.width / 2);
                    break;
                case 'below-right':
                    this._containerTop = hostRect.bottom;
                    this._containerLeft = (hostHorizontalCenter - containerRect.width) + (caretRect.width / 2);
                    break;
                case 'above-left':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = hostHorizontalCenter - (caretRect.width / 2);
                    break;
                case 'above-center':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = hostHorizontalCenter - (containerRect.width / 2);
                    break;
                case 'above-right':
                    this._containerTop = hostRect.top - containerRect.height;
                    this._containerLeft = (hostHorizontalCenter - containerRect.width) + (caretRect.width / 2);
                    break;
                case 'right-top':
                // falls through to default
                default:
                    this._containerTop = hostVerticalCenter - (caretRect.height / 2);
                    this._containerLeft = hostRect.left + hostRect.width;
                    break;
            }
            // Round the numbers
            this._containerTop = Math.round(this._containerTop);
            this._containerLeft = Math.round(this._containerLeft);
        }
    }
    /**
     * Repositions tour component.
     * If your content changes while the tour is open it may affect the position of the tour
     * You may need to reposition the tour after the content is changed (after DOM has been updated)
     */
    reposition() {
        if (this.show) {
            this._repositionTour();
        }
    }
    _focusInModal() {
        const tourInitalFocusElement = this.tourDialog.nativeElement?.querySelector('.cds-tour-initial-focus, [cdsTourInitialFocus]');
        if (!!tourInitalFocusElement) {
            if (!['button', 'a', 'input', 'textarea'].includes(tourInitalFocusElement.tagName.toLowerCase())) {
                this._setAttributeToBeRemovedWhenClosed(tourInitalFocusElement, 'tabindex', '-1');
            }
            tourInitalFocusElement.focus();
        }
        else if (this.focusTrapDirective?.focusTrap?.hasAttached()) {
            this.focusTrapDirective.focusTrap.focusInitialElementWhenReady();
        }
    }
    _setAttributeToBeRemovedWhenClosed(element, attributeName, attributeValue) {
        if (!element.hasAttribute(attributeName)) {
            this._renderer.setAttribute(element, attributeName, attributeValue);
            // set these attributes to be removed after close
            this._attributesToRemoveUponClose.push({ element, attributeName });
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTourComponent, deps: [{ token: DOCUMENT }, { token: i1$1.LiveAnnouncer }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTourComponent, selector: "cds-tour", inputs: { closeAriaLabel: "closeAriaLabel", ariaRole: "ariaRole", idStr: "idStr", showModal: "showModal", isInverse: "isInverse", isKeptOpenOnFocusout: "isKeptOpenOnFocusout", maxWidth: "maxWidth", tourHost: "tourHost", position: "position", show: "show", titleAriaLevel: "titleAriaLevel", titleText: "titleText", contentText: "contentText", totalSteps: "totalSteps", currentStep: "currentStep", showBackButton: "showBackButton", showNextButton: "showNextButton", showDoneButton: "showDoneButton", hidePageDots: "hidePageDots" }, outputs: { onChangeBackBtn: "onChangeBackBtn", onChangeNextBtn: "onChangeNextBtn", onChangeDoneBtn: "onChangeDoneBtn", onChangeCloseBtn: "onChangeCloseBtn", onChangeShow: "onChangeShow" }, host: { listeners: { "focusout": "_focusoutTour($event)" }, classAttribute: "cds-tour" }, viewQueries: [{ propertyName: "tourContainer", first: true, predicate: ["tourContainer"], descendants: true }, { propertyName: "focusTrapDirective", first: true, predicate: ["focusTrapDirective"], descendants: true }, { propertyName: "tourDialog", first: true, predicate: ["tourDialog"], descendants: true }, { propertyName: "backButton", first: true, predicate: ["backButton"], descendants: true }], ngImport: i0, template: `
    <div
      #tourContainer
      *ngIf="show"
      [id]="idStr"
      class="cds-tour-container"
      [ngClass]="position"
      [class.cds-tour-dark]="!isInverse"
      [class.cds-tour-visible]="_isVisible"
      [style.maxWidth]="maxWidth"
      [style.top.px]="_containerTop"
      [style.left.px]="_containerLeft"
      [attr.role]="ariaRole"
    >
      <div class="cds-tour-caret-wrapper">
        <div class="cds-tour-caret"></div>
      </div>
      <div class="cds-tour-bubble cds-tour-grid-wrapper" #focusTrapDirective="cdkTrapFocus" #tourDialog cdkTrapFocus>
        <!-- #region grid-close -->
        <div class="cds-tour-close cds-tour-grid-close">
          <button
            class="cds-tour-close-button"
            type="button"
            [attr.aria-label]="closeAriaLabel"
            (click)="_closeFromWithinComponent()"
          >
            <cds-icon
              type="2021"
              name="044-close"
              aria-hidden="true"
              [svgStyle]="{'width.px': 16,'height.px': 16}"
            ></cds-icon>
          </button>
        </div>
         <!-- #endregion grid-close -->

         <!-- #region grid-title -->
        <div class="cds-tour-title cds-tour-grid-title">
          <div role="heading" [attr.aria-level]="titleAriaLevel" [innerHTML]="titleText"></div>
        </div>
        <!-- #endregion grid-title -->

        <!-- #region grid-content -->
        <div class="cds-tour-content cds-tour-grid-content">
          <p [innerHTML]="contentText"></p>
        </div>
        <!-- #endregion grid-content -->

        <!-- #region Footer -->
        <div class="tour-footer" >
          <button
            *ngIf="showBackButton; else noBackButtonPlaceholder"
            class="tour-button"
            [ngClass]="isInverse ? 'back-tour-button' : ''"
            [cdsButton]="isInverse? 'secondary' : 'secondaryInverse'"
            size="small"
            (click)="onBackBtnClick()"
          >Back</button>
          <ng-template #noBackButtonPlaceholder> 
            <div style="width: 48px"></div>
          </ng-template>
          <div class="choice-dots" [class.page-dots-hidden]="hidePageDots">
            <cds-page-dots
              [totalPages]="totalSteps"
              [currentPage]="currentStep"
            ></cds-page-dots>
          </div>
          <button *ngIf="showNextButton"
          class="tour-button"
            [cdsButton]="isInverse? 'primary' : 'secondary'"
            size="small"
            (click)="onNextBtnClick()"
          >Next</button>
          <button
          *ngIf="showDoneButton"
          class="tour-button"
          [cdsButton]="isInverse? 'primary' : 'secondary'"
          size="small"
          (click)="onDoneBtnClick()"
          >Done</button>
        </div>
        <!-- #endregion Footer -->
      </div>
    </div>
  `, isInline: true, styles: [".cds-tour-container{width:100%;max-width:322px;display:flex;visibility:hidden;z-index:1;position:fixed}.cds-tour-visible{visibility:visible}.cds-tour-caret-wrapper{flex:0 0 22px;overflow:hidden;display:flex;align-items:flex-start}.cds-tour-caret{width:22px;height:76px;display:flex;align-items:center;margin:0 -12px 0 auto}.cds-tour-caret:before{content:\"\";display:flex;width:20px;height:20px;background-color:#eff3f8;box-shadow:0 1px 10px #0000001f;transform:rotate(45deg)}.cds-tour-bubble{flex-grow:1;padding:16px;background-color:#eff3f8;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-tour-title{font-size:18px;font-weight:700;line-height:24px;flex-grow:1}.cds-tour-close-button{border:none;background:none;padding:0;width:24px;height:24px}.cds-tour-close{align-self:end;position:absolute}.cds-tour-grid-wrapper{display:flex;flex-direction:column;row-gap:8px}.cds-tour-grid-title{grid-area:title}.cds-tour-grid-content{grid-area:main}.cds-tour-grid-close{grid-area:close}.cds-tour-dark .cds-tour-caret:before,.cds-tour-dark .cds-tour-bubble{background-color:#002a54;color:#fff}.cds-tour-dark .cds-tour-close-button ::ng-deep path{fill:#fff}.right-middle .cds-tour-caret-wrapper,.left-middle .cds-tour-caret-wrapper{align-items:center}.right-bottom .cds-tour-caret-wrapper,.left-bottom .cds-tour-caret-wrapper{align-items:flex-end;align-items:end}.left-top,.left-middle,.left-bottom{flex-direction:row-reverse}.left-top .cds-tour-caret,.left-middle .cds-tour-caret,.left-bottom .cds-tour-caret{margin:0 auto 0 -12px}.below-left,.below-center,.below-right,.above-left,.above-center,.above-right{max-width:300px}.below-left .cds-tour-caret,.below-center .cds-tour-caret,.below-right .cds-tour-caret,.above-left .cds-tour-caret,.above-center .cds-tour-caret,.above-right .cds-tour-caret{width:76px;height:22px;justify-content:center}.below-left,.below-center,.below-right{flex-direction:column}.below-left .cds-tour-caret,.below-center .cds-tour-caret,.below-right .cds-tour-caret{margin:auto 0 -12px}.above-left,.above-center,.above-right{flex-direction:column-reverse}.above-left .cds-tour-caret,.above-center .cds-tour-caret,.above-right .cds-tour-caret{margin:-12px 0 auto}.below-center .cds-tour-caret-wrapper,.above-center .cds-tour-caret-wrapper{justify-content:center}.below-right .cds-tour-caret-wrapper,.above-right .cds-tour-caret-wrapper{justify-content:flex-end;justify-content:right}.tour-footer{display:flex;flex-flow:row nowrap;justify-content:space-between;align-items:center;gap:16px}.choice-dots{width:max-content}.page-dots-hidden{visibility:hidden}.tour-button{font-size:16px;padding:8px 12px;height:40px}.back-tour-button,.back-tour-button:hover{background-color:#eff3f8}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }, { kind: "component", type: CdsPageDotsComponent, selector: "cds-page-dots", inputs: ["announceStr", "isclickable", "currentPage", "totalPages"], outputs: ["currentPageChange", "totalPagesChange"] }, { kind: "directive", type: CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }, { kind: "directive", type: i1$1.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTourComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-tour', template: `
    <div
      #tourContainer
      *ngIf="show"
      [id]="idStr"
      class="cds-tour-container"
      [ngClass]="position"
      [class.cds-tour-dark]="!isInverse"
      [class.cds-tour-visible]="_isVisible"
      [style.maxWidth]="maxWidth"
      [style.top.px]="_containerTop"
      [style.left.px]="_containerLeft"
      [attr.role]="ariaRole"
    >
      <div class="cds-tour-caret-wrapper">
        <div class="cds-tour-caret"></div>
      </div>
      <div class="cds-tour-bubble cds-tour-grid-wrapper" #focusTrapDirective="cdkTrapFocus" #tourDialog cdkTrapFocus>
        <!-- #region grid-close -->
        <div class="cds-tour-close cds-tour-grid-close">
          <button
            class="cds-tour-close-button"
            type="button"
            [attr.aria-label]="closeAriaLabel"
            (click)="_closeFromWithinComponent()"
          >
            <cds-icon
              type="2021"
              name="044-close"
              aria-hidden="true"
              [svgStyle]="{'width.px': 16,'height.px': 16}"
            ></cds-icon>
          </button>
        </div>
         <!-- #endregion grid-close -->

         <!-- #region grid-title -->
        <div class="cds-tour-title cds-tour-grid-title">
          <div role="heading" [attr.aria-level]="titleAriaLevel" [innerHTML]="titleText"></div>
        </div>
        <!-- #endregion grid-title -->

        <!-- #region grid-content -->
        <div class="cds-tour-content cds-tour-grid-content">
          <p [innerHTML]="contentText"></p>
        </div>
        <!-- #endregion grid-content -->

        <!-- #region Footer -->
        <div class="tour-footer" >
          <button
            *ngIf="showBackButton; else noBackButtonPlaceholder"
            class="tour-button"
            [ngClass]="isInverse ? 'back-tour-button' : ''"
            [cdsButton]="isInverse? 'secondary' : 'secondaryInverse'"
            size="small"
            (click)="onBackBtnClick()"
          >Back</button>
          <ng-template #noBackButtonPlaceholder> 
            <div style="width: 48px"></div>
          </ng-template>
          <div class="choice-dots" [class.page-dots-hidden]="hidePageDots">
            <cds-page-dots
              [totalPages]="totalSteps"
              [currentPage]="currentStep"
            ></cds-page-dots>
          </div>
          <button *ngIf="showNextButton"
          class="tour-button"
            [cdsButton]="isInverse? 'primary' : 'secondary'"
            size="small"
            (click)="onNextBtnClick()"
          >Next</button>
          <button
          *ngIf="showDoneButton"
          class="tour-button"
          [cdsButton]="isInverse? 'primary' : 'secondary'"
          size="small"
          (click)="onDoneBtnClick()"
          >Done</button>
        </div>
        <!-- #endregion Footer -->
      </div>
    </div>
  `, host: {
                        class: 'cds-tour',
                        '(focusout)': '_focusoutTour($event)',
                    }, styles: [".cds-tour-container{width:100%;max-width:322px;display:flex;visibility:hidden;z-index:1;position:fixed}.cds-tour-visible{visibility:visible}.cds-tour-caret-wrapper{flex:0 0 22px;overflow:hidden;display:flex;align-items:flex-start}.cds-tour-caret{width:22px;height:76px;display:flex;align-items:center;margin:0 -12px 0 auto}.cds-tour-caret:before{content:\"\";display:flex;width:20px;height:20px;background-color:#eff3f8;box-shadow:0 1px 10px #0000001f;transform:rotate(45deg)}.cds-tour-bubble{flex-grow:1;padding:16px;background-color:#eff3f8;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-tour-title{font-size:18px;font-weight:700;line-height:24px;flex-grow:1}.cds-tour-close-button{border:none;background:none;padding:0;width:24px;height:24px}.cds-tour-close{align-self:end;position:absolute}.cds-tour-grid-wrapper{display:flex;flex-direction:column;row-gap:8px}.cds-tour-grid-title{grid-area:title}.cds-tour-grid-content{grid-area:main}.cds-tour-grid-close{grid-area:close}.cds-tour-dark .cds-tour-caret:before,.cds-tour-dark .cds-tour-bubble{background-color:#002a54;color:#fff}.cds-tour-dark .cds-tour-close-button ::ng-deep path{fill:#fff}.right-middle .cds-tour-caret-wrapper,.left-middle .cds-tour-caret-wrapper{align-items:center}.right-bottom .cds-tour-caret-wrapper,.left-bottom .cds-tour-caret-wrapper{align-items:flex-end;align-items:end}.left-top,.left-middle,.left-bottom{flex-direction:row-reverse}.left-top .cds-tour-caret,.left-middle .cds-tour-caret,.left-bottom .cds-tour-caret{margin:0 auto 0 -12px}.below-left,.below-center,.below-right,.above-left,.above-center,.above-right{max-width:300px}.below-left .cds-tour-caret,.below-center .cds-tour-caret,.below-right .cds-tour-caret,.above-left .cds-tour-caret,.above-center .cds-tour-caret,.above-right .cds-tour-caret{width:76px;height:22px;justify-content:center}.below-left,.below-center,.below-right{flex-direction:column}.below-left .cds-tour-caret,.below-center .cds-tour-caret,.below-right .cds-tour-caret{margin:auto 0 -12px}.above-left,.above-center,.above-right{flex-direction:column-reverse}.above-left .cds-tour-caret,.above-center .cds-tour-caret,.above-right .cds-tour-caret{margin:-12px 0 auto}.below-center .cds-tour-caret-wrapper,.above-center .cds-tour-caret-wrapper{justify-content:center}.below-right .cds-tour-caret-wrapper,.above-right .cds-tour-caret-wrapper{justify-content:flex-end;justify-content:right}.tour-footer{display:flex;flex-flow:row nowrap;justify-content:space-between;align-items:center;gap:16px}.choice-dots{width:max-content}.page-dots-hidden{visibility:hidden}.tour-button{font-size:16px;padding:8px 12px;height:40px}.back-tour-button,.back-tour-button:hover{background-color:#eff3f8}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1$1.LiveAnnouncer }, { type: i0.Renderer2 }], propDecorators: { tourContainer: [{
                type: ViewChild,
                args: ['tourContainer']
            }], focusTrapDirective: [{
                type: ViewChild,
                args: ['focusTrapDirective']
            }], tourDialog: [{
                type: ViewChild,
                args: ['tourDialog']
            }], backButton: [{
                type: ViewChild,
                args: ['backButton']
            }], closeAriaLabel: [{
                type: Input
            }], ariaRole: [{
                type: Input
            }], idStr: [{
                type: Input
            }], showModal: [{
                type: Input
            }], isInverse: [{
                type: Input
            }], isKeptOpenOnFocusout: [{
                type: Input
            }], maxWidth: [{
                type: Input
            }], tourHost: [{
                type: Input
            }], position: [{
                type: Input
            }], show: [{
                type: Input
            }], titleAriaLevel: [{
                type: Input
            }], titleText: [{
                type: Input
            }], contentText: [{
                type: Input
            }], totalSteps: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], showBackButton: [{
                type: Input
            }], showNextButton: [{
                type: Input
            }], showDoneButton: [{
                type: Input
            }], hidePageDots: [{
                type: Input
            }], onChangeBackBtn: [{
                type: Output
            }], onChangeNextBtn: [{
                type: Output
            }], onChangeDoneBtn: [{
                type: Output
            }], onChangeCloseBtn: [{
                type: Output
            }], onChangeShow: [{
                type: Output
            }] } });

const COMPONENTS$D = [CdsTourComponent];
class CdsTourModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTourModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTourModule, declarations: [CdsTourComponent], imports: [CommonModule, CdsIconModule, CdsPageDotsModule, CdsButtonModule, A11yModule], exports: [CdsTourComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTourModule, imports: [CommonModule, CdsIconModule, CdsPageDotsModule, CdsButtonModule, A11yModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTourModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$D],
                    imports: [CommonModule, CdsIconModule, CdsPageDotsModule, CdsButtonModule, A11yModule],
                    exports: [...COMPONENTS$D]
                }]
        }] });

class ActionBarComponent {
    constructor() {
        this.dividers = true;
        this.background = 'none';
    }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ActionBarComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: ActionBarComponent, selector: "cds-action-bar", inputs: { actionAlign: "actionAlign", dividers: "dividers", background: "background" }, ngImport: i0, template: `
    <div class="cds-action-bar">
      <div
        class="cds-action-content"
        [class.divided]="dividers"
        [style.justify-content]="actionAlign"
        [style.background-color]="background"
      >
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: ActionBarComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-action-bar',
                    template: `
    <div class="cds-action-bar">
      <div
        class="cds-action-content"
        [class.divided]="dividers"
        [style.justify-content]="actionAlign"
        [style.background-color]="background"
      >
        <ng-content></ng-content>
      </div>
    </div>
  `
                }]
        }], ctorParameters: () => [], propDecorators: { actionAlign: [{
                type: Input
            }], dividers: [{
                type: Input
            }], background: [{
                type: Input
            }] } });

class TileComponent {
    set backgroundUrl(backgroundUrl) {
        this._backgroundUrl = `url("${backgroundUrl}")`;
    }
    get backgroundUrl() {
        return this._backgroundUrl;
    }
    constructor() {
        this.padding = '15px';
        this._backgroundUrl = 'none';
    }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: TileComponent, selector: "cds-tile", inputs: { height: "height", width: "width", padding: "padding", backgroundUrl: "backgroundUrl" }, ngImport: i0, template: `
    <div
      class="cds-tile"
      [ngStyle]="{
        height: this.height,
        width: this.width,
        'background-image': this.backgroundUrl
      }"
    >
      <div class="tile-content" [style.padding]="padding">
        <ng-content select="[cdsTileContent]"></ng-content>
      </div>
      <div class="action">
        <ng-content select="[cdsTileAction]"></ng-content>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: TileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-tile',
                    template: `
    <div
      class="cds-tile"
      [ngStyle]="{
        height: this.height,
        width: this.width,
        'background-image': this.backgroundUrl
      }"
    >
      <div class="tile-content" [style.padding]="padding">
        <ng-content select="[cdsTileContent]"></ng-content>
      </div>
      <div class="action">
        <ng-content select="[cdsTileAction]"></ng-content>
      </div>
    </div>
  `
                }]
        }], ctorParameters: () => [], propDecorators: { height: [{
                type: Input
            }], width: [{
                type: Input
            }], padding: [{
                type: Input
            }], backgroundUrl: [{
                type: Input
            }] } });

const COMPONENTS$C = [TileComponent, ActionBarComponent];
const MODULES$m = [CommonModule];
class CdsTileModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTileModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTileModule, declarations: [TileComponent, ActionBarComponent], imports: [CommonModule], exports: [TileComponent, ActionBarComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTileModule, imports: [MODULES$m] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTileModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$C],
                    imports: [...MODULES$m],
                    exports: [...COMPONENTS$C]
                }]
        }] });

class CdsTooltipHost {
    // #endregion
    constructor(host, liveAnnouncer, overlay, _document) {
        this.host = host;
        this.liveAnnouncer = liveAnnouncer;
        this.overlay = overlay;
        this._document = _document;
        this.overlayPosition = 'top-right'; // #endregion
        /**
         * @property tooltipTitle
         * @description The title text of the tooltip
         * @return string
         * @twoway false
         */
        this.tooltipTitle = '';
        /**
         * @property tooltipPosition
         * @description Where the tooltip should be positioned.
         * Falls back to "auto" positioning if tooltip would extend past the limits of the screen.
         * Valid values: left, right, bottom, top, auto.
         * @return string
         * @twoway false
         */
        this.tooltipPosition = 'bottom';
        /**
         * @property tooltipOpened
         * @description Emits when the tooltip is opended by a mouseenter or focusin event
         * @Type: EventEmitter<any>
         * @twoway false
         */
        this.tooltipOpened = new EventEmitter();
        this.keyDownOpened = new EventEmitter();
        /**
         * @property tooltipClosed
         * @description Emits when the tooltip is opended by a mouseout, focusout, or keydown.escape event
         * @Type: EventEmitter<any>
         * @twoway false
         */
        this.tooltipClosed = new EventEmitter();
    }
    ngOnInit() {
        if (!this.panel) {
            this.panel = this.host;
        }
    }
    // #region ----------------- HostListener ------------- ///
    open() {
        this.panel.positionStrategy = this.getOverlayPosition();
        if (!this.panel.showing) {
            setTimeout(() => {
                this.liveAnnouncer.announce(`Tool tip expanded ${this.tooltipTitle} ${this.tooltipMessage}`);
            }, 250);
            this.panel.openDropdown({
                panelClass: 'cds-tooltip'
            });
            this.panel.overlayRef.updateSize({ width: this.panelWidth });
            this.panel.overlayRef.updatePosition();
        }
    }
    close() {
        if (this.panel.showing) {
            this.panel.close();
            this.liveAnnouncer.announce(`Tooltip Collapsed`);
        }
    }
    // #endregion
    ngDestroy() {
        this.panel.close();
        this.liveAnnouncer.announce(`Tooltip Collapsed`);
    }
    getOverlayPosition() {
        let positions;
        switch (this.overlayPosition) {
            case 'bottom-right':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' })
                ];
                break;
            case 'bottom-left':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'end', overlayY: 'top' })
                ];
                break;
            case 'bottom-center':
                positions = [
                    new ConnectionPositionPair({ originX: 'center', originY: 'bottom' }, { overlayX: 'center', overlayY: 'top' })
                ];
                break;
            case 'top-right':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'start', overlayY: 'bottom' })
                ];
                break;
            case 'top-left':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'end', overlayY: 'bottom' })
                ];
                break;
            case 'top-center':
                positions = [
                    new ConnectionPositionPair({ originX: 'center', originY: 'top' }, { overlayX: 'center', overlayY: 'bottom' })
                ];
                break;
            case 'right-bottom':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'top' }, { overlayX: 'start', overlayY: 'top' })
                ];
                break;
            case 'right-top':
                positions = [
                    new ConnectionPositionPair({ originX: 'end', originY: 'bottom' }, { overlayX: 'start', overlayY: 'bottom' })
                ];
                break;
            case 'left-top':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'end', overlayY: 'bottom' })
                ];
                break;
            case 'left-bottom':
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'top' }, { overlayX: 'end', overlayY: 'top' })
                ];
                break;
            default:
                positions = [
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' }),
                    new ConnectionPositionPair({ originX: 'start', originY: 'bottom' }, { overlayX: 'start', overlayY: 'top' })
                ];
                break;
        }
        return this.overlay
            .position()
            .flexibleConnectedTo(this.panel.origin)
            .withPositions(positions)
            .withFlexibleDimensions(false)
            .withPush(false);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipHost, deps: [{ token: CdsOverlayPanel }, { token: i1$1.LiveAnnouncer }, { token: i3.Overlay }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsTooltipHost, selector: "[CdsTooltipHost], [cdsTooltipHost]", inputs: { panelWidth: "panelWidth", panel: "panel", overlayPosition: "overlayPosition", tooltipMessage: "tooltipMessage", tooltipTitle: "tooltipTitle", tooltipPosition: "tooltipPosition" }, outputs: { tooltipOpened: "tooltipOpened", keyDownOpened: "keyDownOpened", tooltipClosed: "tooltipClosed" }, host: { listeners: { "mouseenter": "open()", "focusin": "open()", "mouseleave": "close()", "focusout": "close()", "window:keydown.escape": "close()" }, classAttribute: "cds-tooltip-host" }, viewQueries: [{ propertyName: "ttd", first: true, predicate: ["ttd"], descendants: true }], exportAs: ["CdsTooltipHost"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipHost, decorators: [{
            type: Directive,
            args: [{
                    selector: '[CdsTooltipHost], [cdsTooltipHost]',
                    exportAs: 'CdsTooltipHost',
                    host: {
                        class: 'cds-tooltip-host'
                    }
                }]
        }], ctorParameters: () => [{ type: CdsOverlayPanel }, { type: i1$1.LiveAnnouncer }, { type: i3.Overlay }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { panelWidth: [{
                type: Input
            }], panel: [{
                type: Input
            }], overlayPosition: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipTitle: [{
                type: Input
            }], tooltipPosition: [{
                type: Input
            }], tooltipOpened: [{
                type: Output
            }], keyDownOpened: [{
                type: Output
            }], tooltipClosed: [{
                type: Output
            }], ttd: [{
                type: ViewChild,
                args: ['ttd']
            }], open: [{
                type: HostListener,
                args: ['mouseenter']
            }, {
                type: HostListener,
                args: ['focusin']
            }], close: [{
                type: HostListener,
                args: ['mouseleave']
            }, {
                type: HostListener,
                args: ['focusout']
            }, {
                type: HostListener,
                args: ['window:keydown.escape']
            }] } });

let nextCDSTooltipIndex = 0;
class CdsTooltip {
    // @Input() arrowPosition = 'bottom';
    set tooltipId(tooltipId) {
        if (!tooltipId) {
            // Generate and set the unique ID
            this._tooltipId = 'cds-tooltip-' + nextCDSTooltipIndex++;
        }
        else
            this._tooltipId = tooltipId;
    }
    set grayTooltip(color) {
        if (color) {
            this._grayTooltip = "#EEEEEE";
        }
        else
            this._grayTooltip = "#ffffff";
    }
    // #endregion
    // #region ----------------- HostListener ------------- ///
    get class() {
        switch (this.arrowPosition) {
            case 'bottom-left':
                this.cdsArrow = 'cds-arrow-top';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-rev';
                return 'host-bottom';
            case 'bottom-right':
                this.cdsArrow = 'cds-arrow-top';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row';
                return 'host-bottom-right';
            case 'bottom-center':
                this.cdsArrow = 'cds-arrow-top-center';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-center';
                return 'host-bottom';
            case 'top-left':
                this.cdsArrow = 'cds-arrow-bottom';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-rev-top';
                return 'host-top';
            case 'top-right':
                this.cdsArrow = 'cds-arrow-bottom';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-top';
                return 'host-top';
            case 'top-center':
                this.cdsArrow = 'cds-arrow-bottom-center';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-center';
                return 'host-top';
            case 'left-top':
                this.cdsArrow = 'cds-arrow-right';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-rev-left';
                return 'host-left';
            case 'left-bottom':
                this.cdsArrow = 'cds-arrow-right';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-left';
                return 'host-left';
            case 'right-top':
                this.cdsArrow = 'cds-arrow-left';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-rev';
                return 'host-right';
            case 'right-bottom':
                this.cdsArrow = 'cds-arrow-left';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-col';
                return 'host-right';
            default:
                this.cdsArrow = 'cds-arrow-top';
                this.cdsArrowContainer = 'cds-tooltip-arrow-container-row-rev';
                return 'host-bottom';
        }
    }
    constructor(id, _renderer, _cdr) {
        this._renderer = _renderer;
        this._cdr = _cdr;
        // #region /// ----------------- Properties ------------------ ///
        // Whether the tooltip is showing or not
        this._isShowing = false;
        // Sets the position of the tooltip arrow
        this._arrowPosition = 'cds-arrow-bottom';
        this._grayTooltip = '#ffffff';
        this.cdsArrow = 'cds-arrow-top';
        this.cdsArrowContainer = 'cds-tooltip-arrow-container-col-rev';
        this._attributesToRemoveUponClose = [];
        this._unsub$ = new Subject();
        // #endregion
        // #region ------------- INPUT / OUTPUT --------------//
        this.arrowPosition = 'bottom-right';
        /**
         * @property tooltipId
         * @description A unique ID string for this tooltip to be used as reference by aria tags as needed
         * @return string
         * @twoway false
         */
        this.visibleArrow = true;
        /**
         * @property tooltipPosition
         * @description Where the tooltip should be positioned.
         * Falls back to "auto" positioning if tooltip would extend past the limits of the screen.
         * Valid values: left, right, bottom, top, auto.
         * @return string
         * @twoway false
         */
        this.tooltipPosition = 'top-right';
        this.title = true;
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Tooltip <citi-tooltip> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
        // this.panelId = id || `cds-tooltip-${nextUniqueId++}`;
    }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltip, deps: [{ token: 'id', attribute: true }, { token: i0.Renderer2 }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTooltip, selector: "cds-tooltip", inputs: { arrowPosition: "arrowPosition", visibleArrow: "visibleArrow", tooltipId: "tooltipId", grayTooltip: "grayTooltip", tooltipMessage: "tooltipMessage", tooltipPosition: "tooltipPosition", ttd: "ttd", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", title: "title" }, host: { properties: { "attr.id": "this.panelId", "class": "this.class" } }, viewQueries: [{ propertyName: "tooltipContainer", first: true, predicate: ["tooltipContainer"], descendants: true }, { propertyName: "focusTrapDirective", first: true, predicate: ["focusTrapDirective"], descendants: true }], ngImport: i0, template: `
    <div [ngClass]="cdsArrowContainer" *ngIf="visibleArrow">
      <div class="cds-tooltip-arrow" [ngClass]="cdsArrow" [style.border-bottom]="'15px solid ' + _grayTooltip"></div>
    </div>
    
    <div *ngIf="title"  class="cds-tooltip-container" [style.background-color]="_grayTooltip" [attr.id]="_tooltipId">
       <div role="tooltip" >
        <ng-content select="[popupHeader]"></ng-content>
       </div>
       <div><ng-content></ng-content></div>
    </div>
    <div *ngIf="!title" class="cds-tooltip-container1" [style.background-color]="_grayTooltip" [attr.id]="_tooltipId">
      
      <div role="tooltip"><ng-content select="[popupHeader2]"></ng-content></div>
    </div> 
    
  `, isInline: true, styles: [".cds-tooltip-container{width:100%;min-height:76px;border-radius:4px}.cds-tooltip-container1{width:288px;min-height:49px;border-radius:4px}.cds-tooltip-arrow-container-col-rev{display:flex;flex-direction:column-reverse;padding-bottom:3px}.cds-tooltip-arrow-container-col{display:flex;flex-direction:column;padding-bottom:10px}.cds-tooltip-arrow-container-col-left{display:flex;flex-direction:column;padding-right:9px}.cds-tooltip-arrow-container-col-rev-left{display:flex;flex-direction:column-reverse;padding-bottom:11px}.cds-tooltip-arrow-container-row-rev{display:flex;flex-direction:row-reverse;padding-right:3px;padding-bottom:10px}.cds-tooltip-arrow-container-row-rev-top{display:flex;flex-direction:row-reverse;padding-right:1px;padding-top:2px}.cds-tooltip-arrow-container-row{display:flex;flex-direction:row;padding-bottom:10px;padding-left:1px}.cds-tooltip-arrow-container-row-top{display:flex;flex-direction:row;padding-right:1px;padding-top:2px}.cds-tooltip-arrow-container-row-center{display:flex;flex-direction:row}.cds-tooltip-arrow{border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:15px solid #eee}:host{display:flex;color:#333}.cds-arrow-left{transform:rotate(-90deg);margin:10px -7px 8px}.cds-arrow-right{transform:rotate(90deg);margin-top:10px;margin-left:-7px;margin-right:-7px}.cds-arrow-top{margin-left:5px;margin-bottom:-10px}.cds-arrow-bottom{transform:rotate(-180deg);margin-left:3px;margin-top:-3px;margin-right:3px}.cds-arrow-top-center{margin:0 auto}.cds-arrow-bottom-center{transform:rotate(-180deg);margin:0 auto}:host(.host-bottom){flex-direction:column}:host(.host-bottom-right){flex-direction:column;margin-left:-3px}:host(.host-top){flex-direction:column-reverse}:host(.host-left){flex-direction:row-reverse;margin-left:-15px}:host(.host-right){flex-direction:row}.cds-tooltip-header{text-align:right;float:right}.cds-tooltip-footer{text-align:right}@media only screen and (max-width: 500px){:host{position:relative;left:120px}}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltip, decorators: [{
            type: Component,
            args: [{ selector: 'cds-tooltip', template: `
    <div [ngClass]="cdsArrowContainer" *ngIf="visibleArrow">
      <div class="cds-tooltip-arrow" [ngClass]="cdsArrow" [style.border-bottom]="'15px solid ' + _grayTooltip"></div>
    </div>
    
    <div *ngIf="title"  class="cds-tooltip-container" [style.background-color]="_grayTooltip" [attr.id]="_tooltipId">
       <div role="tooltip" >
        <ng-content select="[popupHeader]"></ng-content>
       </div>
       <div><ng-content></ng-content></div>
    </div>
    <div *ngIf="!title" class="cds-tooltip-container1" [style.background-color]="_grayTooltip" [attr.id]="_tooltipId">
      
      <div role="tooltip"><ng-content select="[popupHeader2]"></ng-content></div>
    </div> 
    
  `, styles: [".cds-tooltip-container{width:100%;min-height:76px;border-radius:4px}.cds-tooltip-container1{width:288px;min-height:49px;border-radius:4px}.cds-tooltip-arrow-container-col-rev{display:flex;flex-direction:column-reverse;padding-bottom:3px}.cds-tooltip-arrow-container-col{display:flex;flex-direction:column;padding-bottom:10px}.cds-tooltip-arrow-container-col-left{display:flex;flex-direction:column;padding-right:9px}.cds-tooltip-arrow-container-col-rev-left{display:flex;flex-direction:column-reverse;padding-bottom:11px}.cds-tooltip-arrow-container-row-rev{display:flex;flex-direction:row-reverse;padding-right:3px;padding-bottom:10px}.cds-tooltip-arrow-container-row-rev-top{display:flex;flex-direction:row-reverse;padding-right:1px;padding-top:2px}.cds-tooltip-arrow-container-row{display:flex;flex-direction:row;padding-bottom:10px;padding-left:1px}.cds-tooltip-arrow-container-row-top{display:flex;flex-direction:row;padding-right:1px;padding-top:2px}.cds-tooltip-arrow-container-row-center{display:flex;flex-direction:row}.cds-tooltip-arrow{border-left:14px solid transparent;border-right:14px solid transparent;border-bottom:15px solid #eee}:host{display:flex;color:#333}.cds-arrow-left{transform:rotate(-90deg);margin:10px -7px 8px}.cds-arrow-right{transform:rotate(90deg);margin-top:10px;margin-left:-7px;margin-right:-7px}.cds-arrow-top{margin-left:5px;margin-bottom:-10px}.cds-arrow-bottom{transform:rotate(-180deg);margin-left:3px;margin-top:-3px;margin-right:3px}.cds-arrow-top-center{margin:0 auto}.cds-arrow-bottom-center{transform:rotate(-180deg);margin:0 auto}:host(.host-bottom){flex-direction:column}:host(.host-bottom-right){flex-direction:column;margin-left:-3px}:host(.host-top){flex-direction:column-reverse}:host(.host-left){flex-direction:row-reverse;margin-left:-15px}:host(.host-right){flex-direction:row}.cds-tooltip-header{text-align:right;float:right}.cds-tooltip-footer{text-align:right}@media only screen and (max-width: 500px){:host{position:relative;left:120px}}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: i0.Renderer2 }, { type: i0.ChangeDetectorRef }], propDecorators: { panelId: [{
                type: HostBinding,
                args: ['attr.id']
            }], tooltipContainer: [{
                type: ViewChild,
                args: ['tooltipContainer']
            }], focusTrapDirective: [{
                type: ViewChild,
                args: ['focusTrapDirective']
            }], arrowPosition: [{
                type: Input
            }], visibleArrow: [{
                type: Input
            }], tooltipId: [{
                type: Input
            }], grayTooltip: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], tooltipPosition: [{
                type: Input
            }], ttd: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], title: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

class CdsTooltipClose {
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
    }
    onClick() {
        this.panel.close();
        this.liveAnnouncer.announce(`Tooltip closing`);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipClose, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsTooltipClose, selector: "a[cdsTooltipClose],button[cdsTooltipClose]", inputs: { panel: "panel" }, host: { listeners: { "click": "onClick()" }, classAttribute: "cds-tooltip-close" }, exportAs: ["cdsTooltipClose"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipClose, decorators: [{
            type: Directive,
            args: [{
                    selector: 'a[cdsTooltipClose],button[cdsTooltipClose]',
                    exportAs: 'cdsTooltipClose',
                    host: { class: 'cds-tooltip-close' }
                }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { panel: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }] } });

class CdsTooltipFocus {
    constructor(host) {
        this.host = host;
    }
    focus() {
        this.host.nativeElement.focus();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipFocus, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsTooltipFocus, selector: "[cdsTooltipFocus]", host: { attributes: { "tabindex": "0" }, classAttribute: "cds-tooltip-focus" }, exportAs: ["cdsTooltipFocus"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipFocus, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsTooltipFocus]',
                    exportAs: 'cdsTooltipFocus',
                    host: { class: 'cds-tooltip-focus', tabindex: '0' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }] });

/**
 * Gets dimensions of popup and host button and positions popup accordingly
 */
function repositionTooltip(tooltipHost, tooltip, tooltipId, position, doc) {
    let _tooltipTop;
    let _tooltipLeft;
    if (tooltipHost && tooltip) {
        const hostRect = tooltipHost?.getBoundingClientRect();
        const containerRect = tooltip.getBoundingClientRect();
        const hostHorizontalCenter = hostRect.left + (hostRect.width / 2);
        const hostVerticalCenter = hostRect.top + (hostRect.height / 2);
        switch (position) {
            case 'right-middle':
                _tooltipTop = hostVerticalCenter - (containerRect.height / 2);
                _tooltipLeft = hostRect.left + hostRect.width;
                break;
            case 'left-middle':
                _tooltipTop = hostVerticalCenter - (containerRect.height / 2);
                _tooltipLeft = hostRect.left - containerRect.width;
                break;
            case 'below-left':
                _tooltipTop = hostRect.bottom;
                _tooltipLeft = hostRect.left;
                break;
            case 'below-right':
                _tooltipTop = hostRect.bottom;
                _tooltipLeft = hostRect.right - containerRect.width;
                break;
            case 'above-left':
                _tooltipTop = hostRect.top - containerRect.height;
                _tooltipLeft = hostRect.left;
                break;
            case 'above-center':
                _tooltipTop = hostRect.top - containerRect.height;
                _tooltipLeft = hostHorizontalCenter - (containerRect.width / 2);
                break;
            case 'above-right':
                _tooltipTop = hostRect.top - containerRect.height;
                _tooltipLeft = hostRect.right - containerRect.width;
                ;
                break;
            case 'below-center':
            // falls through default
            default:
                _tooltipTop = hostRect.bottom;
                _tooltipLeft = hostHorizontalCenter - (containerRect.width / 2);
                break;
        }
        // Round the numbers
        _tooltipTop = Math.round(_tooltipTop);
        _tooltipLeft = Math.round(_tooltipLeft);
    }
    doc.getElementById(tooltipId).style.top = `${_tooltipTop}px`;
    doc.getElementById(tooltipId).style.left = `${_tooltipLeft}px`;
}

let _tooltipUniqueIdCounter = 0;
class CdsTooltip2Host {
    /**
     * Sets the message text of the tooltip2
     */
    set tooltipMessage(tooltipMessage) {
        this.tooltipElement.textContent = tooltipMessage;
    }
    /**
     * Toggles inverse color of the tooltip2 message text
     */
    set inverse(inverse) {
        // setTimeout allows tooltip2 to be added to DOM first (so it can add inverse class while still hidden)
        setTimeout(() => {
            if (inverse)
                this._renderer.addClass(this.tooltipElement, 'cds-tooltip2-inverse');
            else
                this._renderer.removeClass(this.tooltipElement, 'cds-tooltip2-inverse');
        });
    }
    /**
     * Sets id for tooltip2
     */
    set idStr(val) {
        this._idStr = val || this._uid;
    }
    get idStr() {
        return this._idStr || this._uid;
    }
    // #endregion ======= @Inputs ======= /
    // #region ======== @HostListeners ======== /
    /**
     * Hides tooltip2
     */
    hide() {
        this._renderer.removeClass(this.tooltipElement, 'cds-tooltip2-visible');
    }
    /**
     * Shows tooltip2
     */
    show() {
        this._renderer.addClass(this.tooltipElement, 'cds-tooltip2-visible');
        this.liveAnnouncer.announce(`Tooltip: ${this.tooltipElement.textContent}`);
        // Sets tooltip2 message margin depending on whether or not it is the Text Tooltip type
        this.tooltipType === 'text' ? this.setTextTooltipPosition(this.position) : this.setPosition(this.position);
        repositionTooltip(this.tooltipHost, this.tooltipElement, this._idStr, this.position, this._document);
    }
    /**
     * Repositions tooltip as user scrolls
     */
    repositionOnScroll() {
        repositionTooltip(this.tooltipHost, this.tooltipElement, this._idStr, this.position, this._document);
    }
    // #endregion ======= @HostListeners ======= /
    // #region ======== Methods ======== /
    /**
     * Sets margin around tooltip2 (button or icon type) message based on set position
     */
    setPosition(position) {
        this.clearPositionClasses();
        if (position.includes('below-'))
            position = 'below';
        else if (position.includes('right-'))
            position = 'right';
        else if (position.includes('left-'))
            position = 'left';
        else if (position.includes('above-'))
            position = 'above';
        this._renderer.addClass(this.tooltipElement, position);
    }
    /**
     * Sets margin around TEXT tooltip2 message based on set position
     */
    setTextTooltipPosition(position) {
        this.clearPositionClasses();
        if (position.includes('right-'))
            position = 'text-tooltip2-right';
        else if (position.includes('left-'))
            position = 'text-tooltip2-left';
        else if (position.includes('below-'))
            position = 'text-tooltip2-below';
        else if (position.includes('above-'))
            position = 'text-tooltip2-above';
        else
            position = ''; // Prevents unnecessary default position classes from being added to the element
        if (position)
            this._renderer.addClass(this.tooltipElement, position);
    }
    clearPositionClasses() {
        this._renderer.removeClass(this.tooltipElement, 'below');
        this._renderer.removeClass(this.tooltipElement, 'right');
        this._renderer.removeClass(this.tooltipElement, 'left');
        this._renderer.removeClass(this.tooltipElement, 'above');
        this._renderer.removeClass(this.tooltipElement, 'text-tooltip2-right');
        this._renderer.removeClass(this.tooltipElement, 'text-tooltip2-left');
        this._renderer.removeClass(this.tooltipElement, 'text-tooltip2-below');
        this._renderer.removeClass(this.tooltipElement, 'text-tooltip2-above');
    }
    // #endregion ======= Methods ======= /
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(_document, element, liveAnnouncer, _renderer) {
        this._document = _document;
        this.element = element;
        this.liveAnnouncer = liveAnnouncer;
        this._renderer = _renderer;
        this._uid = `CdsTooltip_${_tooltipUniqueIdCounter++}`;
        this.tooltipElement = this._document.createElement('div');
        this.textTooltipWrapper = this._document.createElement('div');
        this.textTooltip = this._document.createElement('p');
        this._document = this._document;
    }
    ngOnInit() {
        // Tooltip host element
        switch (this.tooltipType) {
            case 'text':
                this._renderer.addClass(this.element.nativeElement, 'cds-tooltip2-host');
                this._renderer.addClass(this.element.nativeElement, 'tooltip2-label');
                // Text tooltip label
                this._renderer.appendChild(this.element.nativeElement, this.textTooltip); //
                this._renderer.addClass(this.textTooltip, 'tooltip2-label-underline');
                this._renderer.setProperty(this.textTooltip, 'innerHTML', this.textTooltipLabel);
                this._renderer.setAttribute(this.textTooltip, 'tabindex', '0');
                this._renderer.appendChild(this.element.nativeElement, this.tooltipElement);
                break;
            case 'icon':
                this._renderer.appendChild(this.element.nativeElement, this.tooltipElement);
                this._renderer.setAttribute(this.element.nativeElement, 'tabindex', '0');
                this._renderer.addClass(this.element.nativeElement, 'tooltip2-icon'); // Makes sure host element is inline so icon tooltip aligns properly
                break;
            default: // Button tooltip
                this._renderer.appendChild(this.element.nativeElement, this.tooltipElement);
                this._renderer.setAttribute(this.element.nativeElement, 'tabindex', '0');
                break;
        }
        // Tooltip message
        this.tooltipElement.className = 'cds-tooltip2';
        this.tooltipElement.id = this._idStr;
    }
    ngOnDestroy() {
        this.tooltipElement.remove();
        this.textTooltipWrapper.remove();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltip2Host, deps: [{ token: DOCUMENT }, { token: i0.ElementRef }, { token: i1$1.LiveAnnouncer }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsTooltip2Host, selector: "[cdsTooltip2Host]", inputs: { tooltipHost: "tooltipHost", tooltipMessage: "tooltipMessage", textTooltipLabel: "textTooltipLabel", position: "position", tooltipType: "tooltipType", inverse: "inverse", idStr: "idStr" }, host: { listeners: { "mouseout": "hide()", "focusout": "hide()", "window:keydown.escape": "hide()", "mouseover": "show()", "focusin": "show()", "window:scroll": "repositionOnScroll($event)" } }, exportAs: ["cdsTooltip2Host"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltip2Host, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsTooltip2Host]',
                    exportAs: 'cdsTooltip2Host'
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.ElementRef }, { type: i1$1.LiveAnnouncer }, { type: i0.Renderer2 }], propDecorators: { tooltipHost: [{
                type: Input
            }], tooltipMessage: [{
                type: Input
            }], textTooltipLabel: [{
                type: Input
            }], position: [{
                type: Input
            }], tooltipType: [{
                type: Input
            }], inverse: [{
                type: Input
            }], idStr: [{
                type: Input
            }], hide: [{
                type: HostListener,
                args: ['mouseout']
            }, {
                type: HostListener,
                args: ['focusout']
            }, {
                type: HostListener,
                args: ['window:keydown.escape']
            }], show: [{
                type: HostListener,
                args: ['mouseover']
            }, {
                type: HostListener,
                args: ['focusin']
            }], repositionOnScroll: [{
                type: HostListener,
                args: ['window:scroll', ['$event']]
            }] } });

const COMPONENTS$B = [
    CdsTooltipHost,
    CdsTooltip,
    CdsTooltipClose,
    CdsTooltipFocus,
    CdsTooltip2Host
];
const MODULES$l = [CommonModule, OverlayPanelModule, CdsFocusTrapModule];
class CdsTooltipModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipModule, declarations: [CdsTooltipHost,
            CdsTooltip,
            CdsTooltipClose,
            CdsTooltipFocus,
            CdsTooltip2Host], imports: [CommonModule, OverlayPanelModule, CdsFocusTrapModule], exports: [CdsTooltipHost,
            CdsTooltip,
            CdsTooltipClose,
            CdsTooltipFocus,
            CdsTooltip2Host] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipModule, imports: [MODULES$l] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTooltipModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$B],
                    imports: [...MODULES$l],
                    exports: [...COMPONENTS$B]
                }]
        }] });

/**
 * @description this is an abstract class that the form field can query for
 * to manage form-level activities and sub component coordinatation
 */
class CdsFormBase {
    constructor() {
        /**
         * @description tells the form field whether or not the form element has focus
         */
        this.focusChange = new Subject();
        // /**
        //  * @description mechanism for casting focus in a standard way
        //  */
        // castFocus(): void {}
        /** Whether the control is required. */
        // readonly required: boolean;
        /** Whether the control is in an error state. */
        // readonly errorState: boolean;
    }
}

class CdsFormError {
    /**
     * @description mechanism for toggling the error message via binding
     * ```html
     *   <cds-error [(display)]="yourProperty"> ...
     * ```
     */
    set display(display) {
        this._display = coerceBooleanProperty(display) ? 'block' : 'none';
    }
    get display() {
        return this._display == 'block';
    }
    get shouldShowError() {
        if (!this._control)
            return false;
        if (this.onAny)
            return this._control.invalid;
        return typeof this.on === 'string'
            ? this._control.hasError(this.on)
            : this.on
                .map(errString => this._control.hasError(errString))
                .includes(true);
    }
    get textContent() {
        // return this.shouldShowError ? this.el.nativeElement.textContent : '';
        return this.shouldShowError && this.contentRef != undefined ? this.contentRef.nativeElement.innerText : '';
    }
    constructor(el) {
        this.el = el;
        this._display = 'none';
        /**
         * @description manual display toggle for success icon
         */
        this.showIcon = true;
        this.displayChanged = new EventEmitter();
        /**
         * @description diplays the error if the control fails any of its validators
         * ```html
         *   <cds-error [onAny]="true"> ...
         * ```
         */
        this.onAny = false;
        /**
         * Text to read to screen reader for error icon
         * @default 'Error:'
         */
        this.iconText = 'Error.';
    }
    /**
     * @description toggle the error message based on the control's validity
     */
    checkError(control) {
        this._control = control;
        this._display = this.shouldShowError ? 'block' : 'none';
        this.displayChanged.emit(this.display);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormError, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsFormError, selector: "cds-error,[cdsError]", inputs: { showIcon: "showIcon", display: "display", on: "on", onAny: "onAny", iconText: "iconText" }, outputs: { displayChanged: "displayChanged" }, host: { properties: { "style.display": "this._display" }, classAttribute: "cds-error" }, viewQueries: [{ propertyName: "contentRef", first: true, predicate: ["contentRef"], descendants: true, static: true }], ngImport: i0, template: `
     <div class="cds-error-msg-wrapper">
      <cds-icon
        *ngIf="showIcon"
        idStr="icon-id"
        type="2021"
        role="presentation"
        name="023-warning"
        [svgStyle]="{'width.px': '16' , 'height.px': '16' }"
        pathColor="#D60000"
      >
      </cds-icon>
      <div #contentRef>
        <span class="sr-only">{{iconText ? iconText + '&nbsp;' : ''}}</span>
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormError, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-error,[cdsError]',
                    host: { class: 'cds-error' },
                    template: `
     <div class="cds-error-msg-wrapper">
      <cds-icon
        *ngIf="showIcon"
        idStr="icon-id"
        type="2021"
        role="presentation"
        name="023-warning"
        [svgStyle]="{'width.px': '16' , 'height.px': '16' }"
        pathColor="#D60000"
      >
      </cds-icon>
      <div #contentRef>
        <span class="sr-only">{{iconText ? iconText + '&nbsp;' : ''}}</span>
        <ng-content></ng-content>
      </div>
    </div>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { _display: [{
                type: HostBinding,
                args: ['style.display']
            }], showIcon: [{
                type: Input
            }], display: [{
                type: Input
            }], displayChanged: [{
                type: Output
            }], on: [{
                type: Input
            }], onAny: [{
                type: Input
            }], iconText: [{
                type: Input
            }], contentRef: [{
                type: ViewChild,
                args: ['contentRef', { static: true }]
            }] } });

class CdsFormSuccess {
    /**
     * @description mechanism for toggling the success message via binding
     * ```html
     *   <cds-success [(display)]="yourProperty"> ...
     * ```
     */
    set display(display) {
        this._display = coerceBooleanProperty(display) ? 'block' : 'none';
    }
    get display() {
        return this._display == 'block';
    }
    get shouldShowSuccess() {
        if (!this._control)
            return false;
        if (this.onAll)
            return this._control.valid;
        return typeof this.on === 'string'
            ? !this._control.hasError(this.on)
            : !this.on
                .map(errString => this._control.hasError(errString))
                .includes(true);
    }
    get textContent() {
        return this.shouldShowSuccess && this.contentRef != undefined ? this.contentRef.nativeElement.innerText : '';
    }
    constructor(el) {
        this.el = el;
        this._display = 'none';
        /**
         * @description manual display toggle for success icon
         */
        this.showIcon = true;
        this.displayChanged = new EventEmitter();
        /**
         * @description diplays the success if the control passes all of its validators
         * ```html
         *   <cds-success [onAll]="true"> ...
         * ```
         */
        this.onAll = true;
        /**
         * Text to read to screen reader for error icon
         * @default 'Error:'
         */
        this.iconText = 'Success.';
    }
    /**
     * @description toggle the success message based on the control's validity
     */
    checkSuccess(control) {
        this._control = control;
        this._display = this.shouldShowSuccess ? 'block' : 'none'; // Display when there's no error
        this.displayChanged.emit(this.display);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormSuccess, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsFormSuccess, selector: "cds-success,[cdsSuccess]", inputs: { showIcon: "showIcon", display: "display", on: "on", onAll: "onAll", iconText: "iconText" }, outputs: { displayChanged: "displayChanged" }, host: { properties: { "style.display": "this._display" }, classAttribute: "cds-success" }, viewQueries: [{ propertyName: "contentRef", first: true, predicate: ["contentRef"], descendants: true, static: true }], ngImport: i0, template: `
    <div class="cds-success-msg-wrapper">
      <cds-icon
        *ngIf="showIcon"
        idStr="icon-id"
        type="2021"
        name="059-check"
        role="presentation"
        [svgStyle]="{'width.px': '16' , 'height.px': '16' }"
        pathColor="#006E0A"
      >
      </cds-icon>
      <div #contentRef>
        <ng-container *ngIf="showIcon">
          <span class="sr-only">{{iconText ? iconText + '&nbsp;' : ''}}</span>
        </ng-container>
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormSuccess, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-success,[cdsSuccess]',
                    host: { class: 'cds-success' },
                    template: `
    <div class="cds-success-msg-wrapper">
      <cds-icon
        *ngIf="showIcon"
        idStr="icon-id"
        type="2021"
        name="059-check"
        role="presentation"
        [svgStyle]="{'width.px': '16' , 'height.px': '16' }"
        pathColor="#006E0A"
      >
      </cds-icon>
      <div #contentRef>
        <ng-container *ngIf="showIcon">
          <span class="sr-only">{{iconText ? iconText + '&nbsp;' : ''}}</span>
        </ng-container>
        <ng-content></ng-content>
      </div>
    </div>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    encapsulation: ViewEncapsulation.None
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { _display: [{
                type: HostBinding,
                args: ['style.display']
            }], showIcon: [{
                type: Input
            }], display: [{
                type: Input
            }], displayChanged: [{
                type: Output
            }], on: [{
                type: Input
            }], onAll: [{
                type: Input
            }], iconText: [{
                type: Input
            }], contentRef: [{
                type: ViewChild,
                args: ['contentRef', { static: true }]
            }] } });

class CdsFormField {
    get inErrorState() {
        return this._inErrorState;
    }
    set inErrorState(error) {
        if (this._inErrorState === error)
            return;
        this._inErrorState = error;
        if (this.formBase) {
            this.formBase.isInErrorState = error;
        }
    }
    get inSuccessState() {
        return this._inSuccessState;
    }
    set inSuccessState(status) {
        if (this._inSuccessState === status)
            return;
        if (this.successRef?.nativeElement?.children?.length > 0) {
            this._inSuccessState = status;
            if (this.formBase) {
                this.formBase.isInSuccessState = status;
            }
        }
    }
    /**
     * @description ValidationErrors of the FormControl
     */
    get errors() {
        return this.control ? this.control.errors : null;
    }
    /**
     * @description whether the FormControl has been touched (blurred)
     */
    get touched() {
        return this.control ? this.control.touched : null;
    }
    set touched(isTouched) {
        if (isTouched) {
            this.control?.markAsTouched();
        }
        else {
            this.control?.markAsUntouched();
        }
    }
    /**
     * @description whether the FormControl is unmodified
     */
    get dirty() {
        return this.control ? this.control.dirty : null;
    }
    /**
     * @description whether the control value is valid given the validator functions
     */
    get valid() {
        return this.control ? this.control.valid : null;
    }
    /**
     * @description whether the form element is in focus
     */
    get focused() {
        return this._focused;
    }
    /**
     * @description whether the FormControl has a value
     */
    get hasValue() {
        return this._hasValue;
    }
    /**
     * @description whether the FormControl has a value. If the ControlValueAccessor
     * is wired up properly, then the component disabled styles and behaviors should
     * also be enabled
     */
    set disabled(disabled) {
        const coerceDisabled = coerceBooleanProperty(disabled);
        // if the value has changed and the control exists, then set the control to disabled
        if (this._disabled !== coerceDisabled) {
            this._disabled = coerceDisabled;
            if (coerceDisabled) {
                this.control?.disable();
            }
            else {
                this.control?.enable();
            }
            // if the field is disabled, then unsubscribe from the click event
            if (this._disabled) {
                // Unsubscribe click event handler
                this.clickEventUnsub$.next();
            }
            else {
                // Call method to re-enable click event handler
                this._clickEvenSubscription();
            }
        }
    }
    get disabled() {
        return this._disabled;
    }
    /**
     * @description the FormControl of the form element in the content projection
     */
    get control() {
        if (!this.controls || !this.controls.first)
            return null;
        return this.controls.first.control;
    }
    get fieldId() {
        if (!this.formBase)
            return null;
        return this.formBase.fieldId;
    }
    get fieldType() {
        if (!this.formBase)
            return null;
        return this.formBase.fieldType;
    }
    /**
     * @description all displayed error messages, concatinated
     */
    get allErrors() {
        const res = this.errorComps.map(err => err.textContent).join(' ');
        return res;
    }
    get allSuccesses() {
        const res = this.successComps.map(msg => msg.textContent).join(' ');
        return res;
    }
    // #region === Lifecycle === //
    constructor(_el, _cdr, _announcer) {
        this._el = _el;
        this._cdr = _cdr;
        this._announcer = _announcer;
        this.unsub$ = new Subject();
        this.clickEventUnsub$ = new Subject();
        /**
         * @description whether to display the error state
         */
        this._inErrorState = false;
        this._inSuccessState = false;
        this._focused = false;
        this._hasValue = false;
        this._disabled = false;
        /**
         * manual display toggle for warning icon
         * @deprecated
         */
        this.toggleWarningIcon = true;
    }
    ngAfterContentInit() {
        if (this.controls.length > 1)
            devKill('CdsFormFields may only contain 1 FormControl');
        if (this.formBase)
            this._observeStateChanges();
    }
    ngAfterViewInit() {
        // click event should not be available on disabled fields
        if (!this.disabled) {
            this._clickEvenSubscription();
        }
    }
    ngOnDestroy() {
        this.clickEventUnsub$.next();
        this.clickEventUnsub$.complete();
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region === Methods === //
    _observeStateChanges() {
        this.formBase.focusChange
            .pipe(tap((focused) => {
            this._focused = focused;
            // Announce errors and successes on focus
            if (focused) {
                if (['checkbox-tree', 'checkbox-group'].indexOf(this.fieldType) !== -1) {
                    // delay the announcement for specified fieldTypes to allow time to validate/render
                    setTimeout(() => {
                        this._announceMessageOnFocus();
                    }, 50);
                }
                else {
                    this._announceMessageOnFocus();
                }
            }
        }), takeUntil(this.unsub$))
            .subscribe();
        this.formBase.hasValueChange
            .pipe(tap((hasValue) => {
            this._hasValue = hasValue;
            this._cdr.markForCheck();
        }), takeUntil(this.unsub$))
            .subscribe();
    }
    _announceMessageOnFocus() {
        let res = '';
        if (this.inErrorState) {
            res += this.allErrors;
        }
        if (this.inSuccessState) {
            res += this.allSuccesses;
        }
        if (res !== '') {
            this._announcer.announce(res, 100);
        }
    }
    /**
     * Call validation on any event like this:
     * @example
     * ```html
     * <cds-form-field #field1>
     *   <input (blur)="field1.validate()">
     * </cds-form-field>
     * ```
     */
    validate(isAnnouncedOnlyOnStateChange) {
        if (this.disabled)
            return;
        // Sets the form control to touched on validate
        if (!this.touched) {
            this.touched = true;
        }
        const prevErrorState = this.inErrorState;
        const prevSuccessState = this.inSuccessState;
        if (this.control) {
            this.inErrorState = this.control.invalid;
            this.inSuccessState = (this.errorComps.length + this.successComps.length) > 0 && this.control.valid;
        }
        if (this.errorComps) {
            this.errorComps.forEach(err => err.checkError(this.control));
        }
        if (this.successComps) {
            this.successComps.forEach(err => err.checkSuccess(this.control));
        }
        if (isAnnouncedOnlyOnStateChange) {
            if (prevErrorState !== this.inErrorState || prevSuccessState !== this.inSuccessState) {
                this._announcer.announce(this.allErrors + this.allSuccesses, 500);
            }
        }
        else {
            if (this.allErrors?.trim() || this.allSuccesses?.trim()) {
                this._announcer.announce(this.allErrors + this.allSuccesses, 500);
            }
        }
    }
    /**
     * @description mechanism for checking for a specific error
     * @example
     * ```html
     * <button [disabled]="field.hasError('required')"> ...
     * ```
     */
    hasError(errorName) {
        return this.control ? this.control.hasError(errorName) : false;
    }
    /**
     * @description mechanism for checking for a specific success criteria
     * @example
     * ```html
     * <button [disabled]="field.hasSuccess('required')"> ...
     * ```
     */
    hasSuccess(errorName) {
        return this.control ? !this.control.hasError(errorName) : false;
    }
    /**
     * @description clears the error state and the
     */
    reset(newValue = null) {
        this.control.setValue(newValue);
        this.control.markAsPristine();
        this.control.markAsUntouched();
        this._hasValue = !!newValue;
        this.clearErrors();
        this.clearSuccesses();
    }
    /**
     * @description sets the value to null
     */
    clear() {
        this.control.setValue(null);
        this._hasValue = false;
    }
    /**
     * @description hides <cds-error> messages
     */
    clearErrors() {
        this.inErrorState = false;
        this.errorComps.forEach(err => (err.display = false));
    }
    clearSuccesses() {
        this.inSuccessState = false;
        this.successComps.forEach(msg => (msg.display = false));
    }
    /**
     * @description shows all <cds-error> messages
     */
    showErrors() {
        this.inErrorState = true;
        this.errorComps.forEach(err => (err.display = true));
    }
    showSuccess() {
        this.inSuccessState = true;
        this.successComps.forEach(msg => (msg.display = true));
    }
    /**
     * @description focuses the first field in the DOM that fails its
     * own validation rules
     */
    castFocus() {
        if (this._isFocusableFieldType()) {
            const first = focusable(this._el.nativeElement)[0];
            if (first)
                first.focus();
        }
    }
    _clickEvenSubscription() {
        if (this._isFocusableFieldType()) {
            fromEvent(this.contentWrapper.nativeElement, 'click')
                .pipe(takeUntil(this.clickEventUnsub$))
                .subscribe((event) => {
                this.castFocus();
            });
        }
    }
    _isFocusableFieldType() {
        return [
            'radio',
            'checkbox',
            'checkbox-group',
            'radio-group',
            'checkbox-tree-node',
            'checkbox-tree'
        ].indexOf(this.fieldType) === -1;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormField, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: CdsAnnouncerService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsFormField, selector: "cds-form-field,fieldset[cds-form-field],fieldset[cdsFormField]", inputs: { disabled: "disabled", toggleWarningIcon: "toggleWarningIcon" }, host: { properties: { "class.cds-focus": "this.focused", "class.cds-has-value": "this.hasValue", "class.cds-disabled": "this.disabled" }, classAttribute: "cds-form-field" }, queries: [{ propertyName: "formBase", first: true, predicate: CdsFormBase, descendants: true }, { propertyName: "controls", predicate: FormControlName }, { propertyName: "errorComps", predicate: CdsFormError }, { propertyName: "successComps", predicate: CdsFormSuccess }], viewQueries: [{ propertyName: "contentWrapper", first: true, predicate: ["contentWrapper"], descendants: true }, { propertyName: "successRef", first: true, predicate: ["successRef"], descendants: true }], ngImport: i0, template: `
    <ng-content select="legend"></ng-content>
    <ng-content role="alert" select="cds-error[above]"></ng-content>
    <ng-content select="[cdsLabel]"></ng-content>

    <div
      #contentWrapper
      class="cds-form-field-infix cds-form-field-content"
      [ngClass]="{
        'cds-form-field-input': fieldType == 'input',
        'cds-form-field-textarea': fieldType == 'textarea',
        'cds-form-field-dropdown': fieldType == 'dropdown',
        'cds-form-field-radio': fieldType == 'radio',
        'cds-form-field-checkbox': fieldType == 'checkbox',
        'cds-form-field-checkbox-group': fieldType == 'checkbox-group',
        'cds-form-field-checkbox-tree': fieldType == 'checkbox-tree',
        'cds-form-field-checkbox-tree-node': fieldType == 'checkbox-tree-node',
        'cds-form-text-field-focus': focused,
        'cds-form-field-success': inSuccessState,
        'cds-form-field-error': inErrorState,
        'cds-input-group-error': inErrorState,
        'cds-input-dd-group-error': inErrorState
      }"
    >
      <ng-content></ng-content>
    </div>
    <div [class.cds-form-field-msg-wrap-checkbox-tree]="fieldType === 'checkbox-tree'">
      <ng-content select="cds-error:not([above])"></ng-content>
      <div #successRef>
        <ng-content select="cds-success"></ng-content>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormField, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-form-field,fieldset[cds-form-field],fieldset[cdsFormField]',
                    host: { class: 'cds-form-field' },
                    template: `
    <ng-content select="legend"></ng-content>
    <ng-content role="alert" select="cds-error[above]"></ng-content>
    <ng-content select="[cdsLabel]"></ng-content>

    <div
      #contentWrapper
      class="cds-form-field-infix cds-form-field-content"
      [ngClass]="{
        'cds-form-field-input': fieldType == 'input',
        'cds-form-field-textarea': fieldType == 'textarea',
        'cds-form-field-dropdown': fieldType == 'dropdown',
        'cds-form-field-radio': fieldType == 'radio',
        'cds-form-field-checkbox': fieldType == 'checkbox',
        'cds-form-field-checkbox-group': fieldType == 'checkbox-group',
        'cds-form-field-checkbox-tree': fieldType == 'checkbox-tree',
        'cds-form-field-checkbox-tree-node': fieldType == 'checkbox-tree-node',
        'cds-form-text-field-focus': focused,
        'cds-form-field-success': inSuccessState,
        'cds-form-field-error': inErrorState,
        'cds-input-group-error': inErrorState,
        'cds-input-dd-group-error': inErrorState
      }"
    >
      <ng-content></ng-content>
    </div>
    <div [class.cds-form-field-msg-wrap-checkbox-tree]="fieldType === 'checkbox-tree'">
      <ng-content select="cds-error:not([above])"></ng-content>
      <div #successRef>
        <ng-content select="cds-success"></ng-content>
      </div>
    </div>
  `
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: CdsAnnouncerService }], propDecorators: { contentWrapper: [{
                type: ViewChild,
                args: ['contentWrapper']
            }], successRef: [{
                type: ViewChild,
                args: ['successRef']
            }], focused: [{
                type: HostBinding,
                args: ['class.cds-focus']
            }], hasValue: [{
                type: HostBinding,
                args: ['class.cds-has-value']
            }], disabled: [{
                type: HostBinding,
                args: ['class.cds-disabled']
            }, {
                type: Input
            }], toggleWarningIcon: [{
                type: Input
            }], controls: [{
                type: ContentChildren,
                args: [FormControlName]
            }], errorComps: [{
                type: ContentChildren,
                args: [CdsFormError]
            }], successComps: [{
                type: ContentChildren,
                args: [CdsFormSuccess]
            }], formBase: [{
                type: ContentChild,
                args: [CdsFormBase]
            }] } });
/**
 * @description because all of our form components implement the
 * ControlValueAccessor, the 'ng-invalid' will be added to all component
 * fields failing their own validation. From that element, we query for the
 * first, focusable element and focus it ... hopefully this accounts for all
 * cases.
 * @note I considered other approaches, but they all added to much
 * complexity. Perhaps there is a better approach.
 */
function focusable(root) {
    return root.querySelectorAll(`
    input.ng-invalid:not([disabled]):not([tabindex='-1']),
    dropdown.ng-invalid:not([disabled]):not([tabindex='-1']),
    radio.ng-invalid:not([disabled]):not([tabindex='-1']),
    checkbox.ng-invalid:not([disabled]):not([tabindex='-1']),
    textarea.ng-invalid:not([disabled]):not([tabindex='-1']),
    cds-date-picker.ng-invalid:not([disabled]):not([tabindex='-1']),
    [tabindex].ng-invalid:not([tabindex='-1']),
    .ng-invalid input:not([disabled]):not([tabindex='-1']),
    .ng-invalid dropdown:not([disabled]):not([tabindex='-1']),
    .ng-invalid radio:not([disabled]):not([tabindex='-1']),
    .ng-invalid checkbox:not([disabled]):not([tabindex='-1']),
    .ng-invalid checkbox-group:not([disabled]):not([tabindex='-1']),
    .ng-invalid textarea:not([disabled]):not([tabindex='-1']),
    .ng-invalid cds-date-picker:not([disabled]):not([tabindex='-1']),
    .ng-invalid [tabindex]:not([tabindex='-1'])
  `);
}

/**
 * @description the form label has 2 main functions
 *   1) the form
 */
class CdsFormLabel {
    get _for() {
        return this.attFor || this.formField?.fieldId || null;
    }
    constructor(attFor, formField) {
        this.attFor = attFor;
        this.formField = formField;
        this.static = false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormLabel, deps: [{ token: 'for', attribute: true }, { token: CdsFormField, optional: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsFormLabel, selector: "label[cdsLabel]", inputs: { static: "static" }, host: { properties: { "class.cds-static-label": "this.static", "attr.for": "this._for" }, classAttribute: "cds-label" }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormLabel, decorators: [{
            type: Directive,
            args: [{
                    selector: 'label[cdsLabel]',
                    host: { class: 'cds-label' }
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Attribute,
                    args: ['for']
                }] }, { type: CdsFormField, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [CdsFormField]
                }] }], propDecorators: { static: [{
                type: HostBinding,
                args: ['class.cds-static-label']
            }, {
                type: Input
            }], _for: [{
                type: HostBinding,
                args: ['attr.for']
            }] } });

// const FOCUSABLE_SELECTOR =
//   'input:not(.cds-prefix), select:not(.cds-prefix), textarea:not(.cds-prefix), [tabindex]:not([tabindex="-1"]:not(.cds-prefix)';
class CdsForm {
    get valid() {
        return this.formGroup ? this.formGroup.valid : null;
    }
    /**
     * @description clear all of the fields and reset their state
     * @option pass in a new value for the form fields
     * @example
     * ```html
     * <form [formGroup]="myFg" cdsForm #myForm>
     *
     *  <input formControlName="accountNumber">
     *  <input formControlName="depositAmount">
     *
     * </form>
     * <button (click)="myForm.reset({ depositAmount: 100 })">reset</button>
     * ```
     */
    reset(newFormValue = {}) {
        this.fields.forEach(field => field.reset());
        if (this.formGroup)
            this.formGroup.patchValue(newFormValue);
        this.formGroup.markAsPristine();
        this.formGroup.markAsUntouched();
    }
    clear() {
        this.fields.forEach(field => field.clear());
    }
    validate() {
        this.fields.forEach(field => {
            field.validate();
        });
    }
    focusFirstError() {
        const field = this.fields.find(field => field.control.invalid);
        if (field)
            field.castFocus();
        else
            devWarn('Could not cast focus. No invalid form fields found');
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsForm, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsForm, selector: "form[cdsForm]", inputs: { formGroup: "formGroup" }, queries: [{ propertyName: "fields", predicate: CdsFormField }], ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsForm, decorators: [{
            type: Component,
            args: [{
                    selector: 'form[cdsForm]',
                    template: `
    <ng-content></ng-content>
  `,
                    encapsulation: ViewEncapsulation.None
                }]
        }], propDecorators: { formGroup: [{
                type: Input
            }], fields: [{
                type: ContentChildren,
                args: [CdsFormField]
            }] } });

// import { CdsPrefix } from './form-prefix';
// import { CdsSuffix } from './form-suffix';
const MODULES$k = [CommonModule, ReactiveFormsModule, CdsIconModule];
const COMPONENTS$A = [
    CdsFormField,
    CdsFormError,
    CdsFormSuccess,
    CdsFormLabel,
    // CdsSuffix,
    // CdsPrefix,
    CdsForm
];
class CdsFormsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFormsModule, declarations: [CdsFormField,
            CdsFormError,
            CdsFormSuccess,
            CdsFormLabel,
            // CdsSuffix,
            // CdsPrefix,
            CdsForm], imports: [CommonModule, ReactiveFormsModule, CdsIconModule], exports: [ReactiveFormsModule, CdsFormField,
            CdsFormError,
            CdsFormSuccess,
            CdsFormLabel,
            // CdsSuffix,
            // CdsPrefix,
            CdsForm] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormsModule, imports: [MODULES$k, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$A],
                    imports: [...MODULES$k],
                    exports: [ReactiveFormsModule, ...COMPONENTS$A]
                }]
        }] });

let nextUniqueId$c = 0;
class CdsInput {
    // #endregion
    constructor(input, id) {
        this.input = input;
        this.id = id;
        // #region CdsFormBase properties
        this.fieldType = 'input';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        this.fieldId = this.id || `cds-input-${nextUniqueId$c++}`;
        setTimeout(() => this.checkValue());
    }
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (this.input.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInput, deps: [{ token: i0.ElementRef }, { token: 'id', attribute: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsInput, selector: "input[cdsInput],input[cds-input]", host: { listeners: { "focusin": "focusChanged(true)", "focusout": "focusChanged(false)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId", "class.cds-input-error": "this.isInErrorState", "class.cds-textarea-success": "this.isInSuccessState" }, classAttribute: "cds-input" }, providers: [{ provide: CdsFormBase, useExisting: CdsInput }], exportAs: ["cdsInput"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInput, decorators: [{
            type: Directive,
            args: [{
                    selector: 'input[cdsInput],input[cds-input]',
                    exportAs: 'cdsInput',
                    host: {
                        class: 'cds-input',
                        '(focusin)': 'focusChanged(true)',
                        '(focusout)': 'focusChanged(false)',
                        '(input)': 'checkValue()'
                    },
                    providers: [{ provide: CdsFormBase, useExisting: CdsInput }]
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], isInErrorState: [{
                type: HostBinding,
                args: ['class.cds-input-error']
            }], isInSuccessState: [{
                type: HostBinding,
                args: ['class.cds-textarea-success']
            }] } });

/**
 * @description base class with <input> helpers
 */
let MaskingBase = class MaskingBase {
    /**
     * @description <input> cursor start index
     */
    get start() {
        return this.inputEl.selectionStart || 0;
    }
    /**
     * @description <input> cursor end index
     */
    get end() {
        return this.inputEl.selectionEnd || 0;
    }
    /**
     * @description whether or not a cursor range is selected
     */
    get isRange() {
        return this.start !== this.end;
    }
    constructor(_document, _renderer) {
        this._document = _document;
        this._renderer = _renderer;
        this.handlePreventDefault = (event) => {
            const character = this.character(event);
            const isClipboardAction = event.ctrlKey && ['c', 'v', 'x'].includes(character);
            const isModifier = [event.metaKey, event.altKey].includes(true);
            if (character !== 'Tab' && !isModifier && !isClipboardAction)
                event.preventDefault(); // this is commented
        };
    }
    registerInput(inputEl) {
        if (!inputEl)
            devKill('Masking service registerInput() did not receive an <input>');
        else
            this.inputEl = inputEl;
    }
    // TODO modify as needed based on platform
    character(event) {
        switch (event.type) {
            case 'keydown':
                return event.key;
        }
    }
    /**
     * @description Sets the <input>'s cursor range. The <input>'s
     * caret-color is temporarily made transarent to hide the flickering
     * related to the cursor jumping to the end of the new input value
     */
    select(start, end) {
        if (this._document.activeElement === this.inputEl) {
            this._renderer.setStyle(this.inputEl, 'caret-color', 'transparent');
            setTimeout(() => {
                this.inputEl.setSelectionRange(start, end || start);
                this._renderer.setStyle(this.inputEl, 'caret-color', 'inherit');
            });
        }
    }
    /**
     * @description Sets the <input>'s single point cursor position
     */
    place(position) {
        this.select(position, position);
    }
};
MaskingBase = __decorate([
    __param(0, Inject(DOCUMENT)),
    __metadata("design:paramtypes", [Document,
        Renderer2])
], MaskingBase);

/**
 * @description checkers are character validator functions
 */
const singleChecker = (char) => char.length == 1;
const lowerCaseChecker = (char) => singleChecker(char) && /[a-z]{1}/.test(char);
const upperCaseChecker = (char) => singleChecker(char) && /[A-Z]{1}/.test(char);
const anyChecker = (char) => singleChecker(char);
const numericChecker = (char) => singleChecker(char) && /[0-9]{1}/.test(char);
const alphaNumericChecker = (char) => singleChecker(char) && /^[a-z0-9]{1}/i.test(char);
const nonZeroNumericChecker = (char) => singleChecker(char) && /[1-9]{1}/.test(char);
const lteChecker = (maxValue) => (char) => numericChecker(char) && parseInt(char, 10) <= maxValue;
const neverChecker = (char) => false;
const MASK_CHECKERS = {
    a: lowerCaseChecker,
    A: upperCaseChecker,
    9: numericChecker,
    8: lteChecker(8),
    7: lteChecker(7),
    6: lteChecker(6),
    5: lteChecker(5),
    4: lteChecker(4),
    3: lteChecker(3),
    2: lteChecker(2),
    1: lteChecker(1),
    0: lteChecker(0),
    '#': anyChecker,
    '&': alphaNumericChecker,
    '^': nonZeroNumericChecker
};
const MASK_SPECIALS = [' ', '/', '(', ')', '+', '-'];
const HIDER = '•';
const isWritable = (digit) => digit.writable;
const replaceAt = (original, newCharacters, index) => `${original.substr(0, index)}${newCharacters}${original.substr(index + 1, original.length)}`;

const FIXED_MASKS = Object.freeze({
    phone: '(999) 999-9999',
    partialPhone: '(*9*9*9)-*9*9*9-9999',
    hiddenPhone: '(*9*9*9) *9*9*9-*9*9*9*9',
    zipcode: '99999',
    hiddenZipcode: '*9*9*9*9*9',
    ssn: '999-99-9999',
    partialSsn: '*9*9*9-*9*9-9999',
    hiddenSsn: '*9*9*9-*9*9-*9*9*9*9',
    creditCard: '9999 9999 9999 9999',
    partialCreditCard: '*9*9*9*9 *9*9*9*9 *9*9*9*9 9999',
    hiddenCreditCard: '*9*9*9*9 *9*9*9*9 *9*9*9*9 *9*9*9*9'
});
const MASK_VALUE_ACCESSOR$2 = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsFixedMask),
    multi: true
};
const INVALID_MASK_WARNING$2 = 'Invalid mask provided. Supported characters are: a A # ^ & * 0-9 / ( ) + -';
const INVALID_TYPE_WARNING$1 = ' is an invalid <input> type for this mask. Only "text" & "tel" are valid. Changing to type="text" by default';
class CdsFixedMask extends MaskingBase {
    get inputEl() {
        if (!this.inputDir)
            devKill('masking requires a CdsInput as a child');
        return this.inputDir.input.nativeElement;
    }
    set inputText(text) {
        this.inputEl.value = text;
        /**
         * @description this ultimately tells the <cds-form-field> whether or
         * not to add the cds-has-value
         */
        this.inputDir.checkValue();
    }
    get inputText() {
        return this.inputEl.value;
    }
    get validInputType() {
        return ['tel', 'text'].includes(this.inputEl.type);
    }
    /**
     * @description the underlying values in the selected cursor range
     */
    get selection() {
        return this.digitData
            .slice(this.start, this.end)
            .filter(isWritable)
            .map(digit => digit.value)
            .join('');
    }
    /**
     * @description character used to hide characters like a password
     * @example
     *   •••-••-7258 or ***-**-7258
     */
    set hider(hider) {
        if (!['•', '*'].includes(hider))
            return;
        this._hider = hider;
        this.updateText();
    }
    get hider() {
        return this._hider;
    }
    /**
     * @description used to hide and reveal hidden characters
     */
    set reveal(reveal) {
        if (reveal === this._reveal)
            return;
        this._reveal = coerceBooleanProperty(reveal);
        if (!this.off)
            this.updateText();
    }
    get reveal() {
        return this._reveal;
    }
    set blank(blank) {
        this._blank = blank;
        this.updateText();
    }
    get blank() {
        return this._blank;
    }
    /**
     * @description dynamic masking / formatting pattern
     * @formatting
     * - 'a' = Lower Case Checker
     * - 'A' = Upper Case Checker
     * - '#' = Wildcard
     * - '^' = Non Zero Checker
     * - '9' = Numeric Checker (lower numbers denote a max value)
     * - '&' = Alpha Numeric
     * - '*' will hide any character that follows it
     *
     * These characters are reserved and will display uneditable in the <input>
     * - forward slash: /
     * - parenthesis:  ( )
     * - plus & minus: + -
     * - space: " "
     * @example
     * ```html
     * mask="*9*9*9-*9*9-9999" => '•••-••-7258'
     * ```
     */
    set mask(mask) {
        this.off = false; // Masking should ON by default
        this._mask = mask;
        if (!this.maskIsValid)
            return;
        const value = this.value;
        this.digitData.length = 0;
        this.storeDigitData(this.value);
        this.fillDigitData(value);
        this.updateText();
    }
    get mask() {
        return this._mask;
    }
    get maskIsValid() {
        const mask = FIXED_MASKS[this.mask] || this.mask;
        // eslint-disable-next-line no-useless-escape
        if (!!mask && !/^[aA#^&*0-9\/()+\- ]+$/.test(mask)) {
            this.off = true;
            devError(INVALID_MASK_WARNING$2);
            return false;
        }
        return true;
    }
    /**
     * @description Whether or not the mask operational
     */
    set off(off) {
        if (off === this._off)
            return;
        this._off = off;
        // onStable(this.ngZone, () => {
        off ? this.turnOffMasking() : this.turnOnMasking();
        // });
    }
    get off() {
        return this._off;
    }
    /**
     * @description mask string with out the hider modifiers '*'
     */
    get effectiveMask() {
        return (FIXED_MASKS[this.mask] || this.mask).replace(/\*/g, '');
    }
    get unmaskedValue() {
        return this.digitData.map(digit => digit.value).join('');
    }
    /**
     * @description first writable position with no current value
     */
    get firstAvailable() {
        return this.digitData.findIndex(digit => digit.writable && digit.value === this.blank);
    }
    /**
     * @description last writable position with no current value
     */
    get lastAvailable() {
        return findLastIndex(this.digitData, digit => digit.writable && digit.value !== this.blank);
    }
    /* index of the first writable position */
    get firstWritable() {
        return this.digitData.findIndex(isWritable);
    }
    /* index of the last writable position */
    get lastWritable() {
        return findLastIndex(this.digitData, isWritable);
    }
    /**
     * @description value to bind to when not using the formControlName
     */
    set value(value) {
        this.fillDigitData(value);
        this.updateText();
    }
    get value() {
        return this.digitData
            .filter(digit => digit.writable && digit.index <= this.lastAvailable)
            .map(digit => digit.value)
            .join('');
    }
    get isNull() {
        return this.digitData
            .filter(digit => digit.writable)
            .every(digit => digit.value == this.blank);
    }
    constructor(renderer, ngZone, clipboard, _liveAnnouncer, _document) {
        super(_document, renderer);
        this.renderer = renderer;
        this.ngZone = ngZone;
        this.clipboard = clipboard;
        this._liveAnnouncer = _liveAnnouncer;
        this._document = _document;
        this.unsub$ = new Subject();
        this.disabled = false;
        this._hider = HIDER;
        this._reveal = false;
        this._blank = '_';
        this._mask = '';
        this._off = false;
        /**
         * @description holds all relevant information for every character position
         * in the mask
         */
        this.digitData = [];
        this.valueChange = new EventEmitter();
        this.handleKeyEvent = (event) => {
            if (this.disabled)
                return;
            const character = this.character(event);
            // Non-Character-Entry Commands
            switch (character) {
                case 'Backspace':
                    return this.handleBackspace();
                case 'ArrowUp':
                    return this.arrowUp(event);
                case 'Delete':
                    return this.handleDelete();
                case 'ArrowDown':
                    return this.arrowDown(event);
                case 'ArrowLeft':
                    return this.arrowLeft(event);
                case 'ArrowRight':
                    return this.arrowRight(event);
            }
            // Character-Entry Commands
            if (!singleChecker(character))
                return;
            let position = this.start;
            // edge case shifts
            if (this.inputText == '' || this.isRange) {
                position = this.nextWritable(this.start);
                onStable(this.ngZone, () => this.place(position + 1));
            }
            const digitDatum = this.digitData[position];
            // handle valid keys
            if (digitDatum && digitDatum.accepts(character)) {
                if (this.isRange)
                    this.deleteRange();
                this.place(this.nextWritable());
                return this.updateCharacter(character, position);
            }
            else {
                this._liveAnnouncer.announce('invalid input', 200);
            }
        };
        this.handlePaste = (event) => {
            event.preventDefault();
            let word = event.clipboardData.getData('Text');
            let letter = 0;
            let position = this.start;
            this.digitData.slice(this.start, this.digitData.length).map(obj => {
                if (obj.writable && word[letter]) {
                    this.updateCharacter(word[letter], position);
                    letter++;
                    this.place(position + 1);
                }
                position++;
            });
        };
        this.handleCut = (event) => {
            this.place(this.start);
            this.deleteRange();
            this.writeValue(this.value);
            this.updateText();
        };
        this.copySelectionToClipboard = (event) => {
            event.preventDefault();
            if (this.disabled)
                return;
            else
                this.clipboard.copy(this.selection);
        };
        /**
         * @category FORM BINDING METHODS
         * All methods below are required for the ValueControlAccessor interface
         * which is needed to work with the ReactiveForms Module
         */
        this.onChange = (value) => { };
        this.onTouched = () => { };
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Fixed Mask <cds-fixed-mask> is deprecated. Please use the CDS Input Mask or one of our dedicated masking directive components instead.', 'background-color: #f00; color: #fff;');
        }
    }
    ngAfterContentInit() {
        this.setUpMasking();
        this.inputDir.checkValue();
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    turnOffMasking() {
        this.unsub$.next();
        this.inputText = this.digitData
            .filter(digit => digit.writable && digit.value != this.blank)
            .map(digit => digit.value)
            .join('');
        // reapplies the original type which was set inline
        this.renderer.setAttribute(this.inputEl, 'type', this.typeAttr);
    }
    turnOnMasking() {
        this.setUpMasking();
        this.updateText();
    }
    setUpMasking() {
        if (!this.maskIsValid)
            return;
        var userAgent = navigator.userAgent || navigator.vendor;
        if (/android/i.test(userAgent)) {
            return;
        }
        if (!this.validInputType) {
            this.typeAttr = this.inputEl.type;
            devWarn(`"${this.typeAttr}" ${INVALID_TYPE_WARNING$1}`);
            this.renderer.setAttribute(this.inputEl, 'type', 'text');
        }
        this.listenInput('keydown')
            .pipe(tap(this.handlePreventDefault), tap(this.handleKeyEvent))
            .subscribe();
        this.listenInput('focus')
            .pipe(tap(() => this.place(this.firstAvailable)))
            .subscribe();
        this.listenInput('paste')
            .pipe(tap(this.handlePaste))
            .subscribe();
        this.listenInput('cut')
            .pipe(tap(this.copySelectionToClipboard), tap(this.handleCut))
            .subscribe();
        this.listenInput('copy')
            .pipe(tap(this.copySelectionToClipboard))
            .subscribe();
    }
    /**
     * @description converts the user-defined mask into the data stucture
     * used for the masking calculations
     */
    storeDigitData(value) {
        if (this.digitData.length)
            return;
        const mask = FIXED_MASKS[this.mask] || this.mask;
        const maskArray = mask.split('');
        const indicies = [];
        maskArray.map((char, index) => {
            if (char === '*')
                indicies.push(index - indicies.length);
        });
        let digitData = maskArray
            .filter(char => char != '*')
            .map((char, index) => {
            const isSpecial = MASK_SPECIALS.includes(char);
            return {
                index: index,
                value: isSpecial ? char : this.blank,
                writable: !isSpecial,
                hidden: indicies.includes(index),
                accepts: MASK_CHECKERS[mask.replace(/\*/g, '').charAt(index)] || neverChecker
            };
        });
        this.digitData = digitData;
    }
    /**
     * @description subscribe helper for input events
     */
    listenInput(eventName) {
        return fromEvent(this.inputEl, eventName).pipe(takeUntil(this.unsub$));
    }
    arrowUp(event) {
        event.shiftKey
            ? this.select(this.firstWritable, this.start)
            : this.place(this.firstWritable);
    }
    arrowDown(event) {
        event.shiftKey
            ? this.select(this.start, this.lastWritable + 1)
            : this.place(this.effectiveMask.length);
    }
    arrowLeft(event) {
        event.shiftKey
            ? this.select(this.prevWritable(), this.end)
            : this.place(this.prevWritable());
    }
    arrowRight(event) {
        event.shiftKey
            ? this.select(this.start, this.nextWritable(this.end + 1))
            : this.place(this.nextWritable());
    }
    handleBackspace() {
        if (this.isRange) {
            this.place(this.start);
            this.deleteRange();
            this.writeValue(this.value);
            this.updateText();
        }
        else {
            this.place(this.prevWritable());
            this.updateCharacter(this.blank, this.prevWritable());
        }
    }
    handleDelete() {
        this.isRange
            ? this.deleteRange()
            : this.updateCharacter(this.blank, this.start);
        this.writeValue(this.value);
        this.updateText();
        this.place(this.start);
    }
    updateCharacter(character, position) {
        if (position > this.lastWritable)
            return;
        this.digitData[position].value = character || this.blank;
        this.writeValue(this.value);
        this.valueChange.emit(this.value);
    }
    /**
     * @description updates the text in the <input>
     */
    updateText() {
        onStable(this.ngZone, () => (this.inputText = this.isNull
            ? ''
            : this.unmaskedValue
                .split('')
                .map((char, index) => {
                const digit = this.digitData[index];
                if (!digit.writable)
                    return digit.value;
                else if (digit.value == this.blank)
                    return this.blank;
                else
                    return !digit.hidden || this.reveal ? char : this.hider;
            })
                .join('')));
    }
    /**
     * @description sets a multi-character string as the value or portion of it
     */
    fillDigitData(text, index = 0) {
        if (!text) {
            this.deleteRange(0, this.digitData.length);
            return;
        }
        let strValue = `${text}`;
        for (let i = index; i < this.digitData.length; i++) {
            const digit = this.digitData[i];
            const char = strValue.charAt(0);
            if (digit.writable) {
                if (digit.accepts(char) || char === '') {
                    digit.value = char || this.blank;
                }
                if (strValue)
                    strValue = strValue.substring(1);
            }
        }
    }
    nextWritable(fromIndex = this.start + 1) {
        const remaining = this.digitData.slice().splice(fromIndex);
        const nextWritable = remaining.find(isWritable);
        return Math.min(this.lastWritable, this.digitData.indexOf(nextWritable));
    }
    prevWritable(fromIndex = this.start) {
        const remaining = this.digitData.slice(0, fromIndex);
        return Math.max(this.firstWritable, findLastIndex(remaining, isWritable));
    }
    deleteRange(start = this.start, end = this.end) {
        for (let i = start; i < end; i++) {
            const digit = this.digitData[i];
            if (digit.writable)
                digit.value = this.blank;
        }
    }
    // Update control value
    writeValue(value) {
        // covert type to number if it was pass in that wat
        this.value = value;
        // trigger control change events
        this.onChange(this.value);
    }
    // Enable the control to recognize changes
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Enable control to recognize a touched state
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Enable control to recognize a disabled state
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFixedMask, deps: [{ token: i0.Renderer2 }, { token: i0.NgZone }, { token: i1$2.Clipboard }, { token: i1$1.LiveAnnouncer }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsFixedMask, selector: "cds-fixed-mask", inputs: { disabled: "disabled", hider: "hider", reveal: "reveal", blank: "blank", mask: "mask", off: "off", value: "value" }, outputs: { valueChange: "valueChange" }, host: { classAttribute: "cds-fixed-mask" }, providers: [MASK_VALUE_ACCESSOR$2], queries: [{ propertyName: "inputDir", first: true, predicate: CdsInput, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFixedMask, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-fixed-mask',
                    host: { class: 'cds-fixed-mask' },
                    template: `
    <ng-content></ng-content>
  `,
                    providers: [MASK_VALUE_ACCESSOR$2],
                    encapsulation: ViewEncapsulation.None,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: i0.NgZone }, { type: i1$2.Clipboard }, { type: i1$1.LiveAnnouncer }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { disabled: [{
                type: Input
            }], inputDir: [{
                type: ContentChild,
                args: [CdsInput, { static: true }]
            }], hider: [{
                type: Input
            }], reveal: [{
                type: Input
            }], blank: [{
                type: Input
            }], mask: [{
                type: Input
            }], off: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

const MASK_VALUE_ACCESSOR$1 = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsDateMask),
    multi: true
};
// https://angular.io/api/common/DatePipe
const DATE_PIPE_CHARS = 'GyMLwWdEabBmsSzZO'.split('');
const INVALID_MASK_WARNING$1 = 'Invalid mask provided. For formatting rules see: https://angular.io/api/common/DatePipe';
const INVALID_TYPE_WARNING = 'is an invalid <input> type for this mask. Only "text" is valid for this masking type. Changing to type="text" by default...';
class CdsDateMask extends MaskingBase {
    get inputEl() {
        if (!this.inputDir)
            devKill('masking requires a CdsInput as a child');
        return this.inputDir.input.nativeElement;
    }
    set inputText(str) {
        // Added this hack because I noticed the input text wasn't
        // initializing ...not sure why it works only sometimes
        onStable(this.ngZone, () => {
            this.inputEl.value = str;
            // Ultimately tells the <cds-form-field> whether or not to add
            // the cds-has-value
            this.inputDir.checkValue();
        });
    }
    get inputText() {
        return this.inputEl.value;
    }
    get validInputType() {
        return ['text'].includes(this.inputEl.type);
    }
    get formatIsValid() {
        const isValid = DATE_PIPE_CHARS.some(char => char == this.format);
        if (this.format && isValid) {
            devWarn(INVALID_MASK_WARNING$1);
            return false;
        }
        return true;
    }
    /**
     * @description character used to hide date value/characters like a password
     * @example
     *   •••-••-2020 or ***-**-2020
     */
    set hider(hider) {
        if (!['•', '*'].includes(hider))
            return;
        this._hider = hider;
        this.setTextFromDate(this.value);
    }
    get hider() {
        return this._hider;
    }
    /**
     * @description Reveals the hidden value/character if value is provided is true.
     */
    set reveal(reveal) {
        if (reveal === this._reveal)
            return;
        this._reveal = coerceBooleanProperty(reveal);
        this.setTextFromDate(this.value);
    }
    get reveal() {
        return this._reveal;
    }
    /**
     * @description value to bind to when not using the formControlName
     * @note this needs to be below all of the other @Input() proerties
     * @note clear the value by setting to an empty string
     */
    set value(value) {
        this._value = value;
        if (value !== null)
            this.setTextFromDate(value);
    }
    get value() {
        return this._value;
    }
    constructor(renderer, _document, ngZone, cdsMediumDateMask, cdsShortDateMask, cdsFullDateMask, cdsLongDateMask, cdsMonthYearDateMask) {
        super(_document, renderer);
        this.renderer = renderer;
        this._document = _document;
        this.ngZone = ngZone;
        this.cdsMediumDateMask = cdsMediumDateMask;
        this.cdsShortDateMask = cdsShortDateMask;
        this.cdsFullDateMask = cdsFullDateMask;
        this.cdsLongDateMask = cdsLongDateMask;
        this.cdsMonthYearDateMask = cdsMonthYearDateMask;
        this.unsub$ = new Subject();
        /**
         * @description A locale code for the locale format rules to use. By default it is 'en'
         */
        this.locale = 'en';
        /**
         * @description formats the date based on angular provided format value for date object. Default format is 'mediumDate'. Please visit https://angular.io/api/common/DatePipe to select right format.
         */
        this.format = 'mediumDate';
        /**
         * @description hiding elements
         */
        this.hideEle = 'mdy';
        this._hider = HIDER;
        this._reveal = true;
        this.valueChange = new EventEmitter();
        /**
         * @category FORM BINDING METHODS
         * All methods below are required for the ValueControlAccessor interface
         * which is needed to work with the ReactiveForms Module
         */
        this.onChange = (value) => { };
        this.onTouched = () => { };
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Date Mask <cds-date-mask> is deprecated. Please use the cdsDateMask directive instead.', 'background-color: #f00; color: #fff;');
        }
    }
    ngAfterContentInit() {
        this.inputDir.checkValue();
        this.setUpMasking();
        this.setTextFromDate(this.value);
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    setTextFromDate(date) {
        if (!date)
            return;
        const dateStr = new DatePipe(this.locale).transform(date, this.format);
        switch (this.format) {
            case 'mediumDate':
                this.inputText = this.reveal ? dateStr : this.cdsMediumDateMask.transform(dateStr, this.hider, this.hideEle);
                break;
            case 'shortDate':
                this.inputText = this.reveal ? dateStr : this.cdsShortDateMask.transform(dateStr, this.hider, this.hideEle);
                break;
            case 'longDate':
                this.inputText = this.reveal ? dateStr : this.cdsLongDateMask.transform(dateStr, this.hider, this.hideEle);
                break;
            case 'fullDate':
                this.inputText = this.reveal ? dateStr : this.cdsFullDateMask.transform(dateStr, this.hider, this.hideEle);
                break;
            case 'MonthYear':
                this.inputText = this.reveal ? dateStr : this.cdsMonthYearDateMask.transform(dateStr, this.hider, this.hideEle);
                break;
            default:
                this.inputText = this.reveal ? dateStr : this.hider.repeat(dateStr.length);
                break;
        }
        if (this.locale != 'en')
            this.inputText = this.reveal ? dateStr : this.hider.repeat(dateStr.length);
    }
    parseDateStr(dateString) {
        if (!dateString)
            return null;
        // apply user-defined parser if available. Otherwise Date.parse()
        if (this.dateParser) {
            const customParse = this.dateParser(dateString);
            if (toRawType(customParse) === 'Date')
                return customParse;
        }
        else {
            const dateNum = Date.parse(dateString);
            if (dateNum)
                return new Date(dateNum);
        }
        devWarn('Invalid date string');
        return null;
    }
    setUpMasking() {
        if (!this.formatIsValid)
            return;
        if (!this.validInputType) {
            devWarn(`"${this.inputEl.type}" ${INVALID_TYPE_WARNING}`);
            this.renderer.setAttribute(this.inputEl, 'type', 'text');
        }
        this.listenInput('blur')
            .pipe(
        /**
         * valueChange cannot be triggered on key events because it
         * will update the value (and text) unexpectedly.
         * @example "Apr 12 " => yeilds => "Apr 12, 2001" before typing the year
         */
        tap(() => this.valueChange.emit(this.value)), tap(() => this.setTextFromDate(this.value)), tap(() => this.onTouched()))
            .subscribe();
        this.listenInput('keyup')
            .pipe(tap(() => this.writeValue(this.parseDateStr(this.inputText))))
            .subscribe();
    }
    /**
     * @description subscribe helper for input events
     */
    listenInput(eventName) {
        return fromEvent(this.inputEl, eventName).pipe(takeUntil(this.unsub$));
    }
    // Update control value
    writeValue(value) {
        this._value = value;
        this.onChange(value);
    }
    // Enable the control to recognize changes
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Enable control to recognize a touched state
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMask, deps: [{ token: i0.Renderer2 }, { token: DOCUMENT }, { token: i0.NgZone }, { token: CdsMediumDateMaskPipe }, { token: CdsShortDateMaskPipe }, { token: CdsFullDateMaskPipe }, { token: CdsLongDateMaskPipe }, { token: CdsMonthYearDateMaskPipe }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsDateMask, selector: "cds-date-mask", inputs: { locale: "locale", format: "format", hideEle: "hideEle", hider: "hider", reveal: "reveal", dateParser: "dateParser", value: "value" }, outputs: { valueChange: "valueChange" }, host: { classAttribute: "cds-date-mask" }, providers: [MASK_VALUE_ACCESSOR$1], queries: [{ propertyName: "inputDir", first: true, predicate: CdsInput, descendants: true, static: true }], usesInheritance: true, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMask, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-date-mask',
                    host: { class: 'cds-date-mask' },
                    template: `
    <ng-content></ng-content>
  `,
                    providers: [MASK_VALUE_ACCESSOR$1],
                    encapsulation: ViewEncapsulation.None,
                    changeDetection: ChangeDetectionStrategy.OnPush
                }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.NgZone }, { type: CdsMediumDateMaskPipe }, { type: CdsShortDateMaskPipe }, { type: CdsFullDateMaskPipe }, { type: CdsLongDateMaskPipe }, { type: CdsMonthYearDateMaskPipe }], propDecorators: { inputDir: [{
                type: ContentChild,
                args: [CdsInput, { static: true }]
            }], locale: [{
                type: Input
            }], format: [{
                type: Input
            }], hideEle: [{
                type: Input
            }], hider: [{
                type: Input
            }], reveal: [{
                type: Input
            }], dateParser: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

const PARTIAL_MASK_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsPartialMask),
    multi: true
};
const INVALID_MASK_WARNING = `Invalid mask provided. Supported masks are: 'partialEmail' | 'fullText' | 'partialText'`;
class CdsPartialMask extends MaskingBase {
    get inputEl() {
        if (!this.cdsInput)
            devKill('masking requires a CdsInput as a child');
        return this.cdsInput.input.nativeElement;
    }
    get _isRangeSelected() {
        return this._cursorStart !== this._cursorEnd;
    }
    // #endregion
    // #region // ========== @Inputs / @Outputs ========== //
    /**
     * Two-way binding for value
     * Consider using Reactive Forms with 'formControlName'
     * or template based forms with [(ngModel)]
     */
    set value(value) {
        this._value = value;
        this._onChange(value);
        this.valueChange.emit(value);
        this._applyMask(value);
    }
    get value() {
        if (!this._value)
            this._value = '';
        return this._value;
    }
    // #endregion
    // #region // ========== Constructor ========== //
    constructor(_document, renderer, announcer, fullTextMask, partialTextMask, partialEmailMask) {
        super(_document, renderer);
        this._document = _document;
        this.renderer = renderer;
        this.announcer = announcer;
        this.fullTextMask = fullTextMask;
        this.partialTextMask = partialTextMask;
        this.partialEmailMask = partialEmailMask;
        // #region // ========== Properties ========== //
        this.unsub$ = new Subject();
        this._codes = Object.freeze({
            BACKSPACE: 8,
            DELETE: 46,
            KEY_TAB: 9
        });
        /**
         * Emits the changed value
         */
        this.valueChange = new EventEmitter();
        /**
         * Sets the type of mask to apply to cdsInput field
         * Supported masks are 'partialText', 'fullText', and 'partialEmail'
         */
        this.mask = 'partialText';
        /**
         * Sets the number of characters that will not be masked
         */
        this.unmaskedLength = 4;
        /**
         * character used to hide date value/characters like a password
         * @example
         *   `a•••••b@citi.com` or `a*****b@citi.com`
         */
        this.hider = HIDER;
        // #endregion
        // #region // ========== ControlValueAccessor ========== //
        this._onChange = (value) => { };
        this._onTouched = () => { };
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Partial Mask <cds-partial-mask> is deprecated. Please use the CDS Input Mask or one of our dedicated masking directive components instead.', 'background-color: #f00; color: #fff;');
        }
    }
    // #endregion
    // #region // ========== Lifecycle Hooks ========== //
    ngOnInit() { }
    ngAfterContentInit() {
        this._setUpMasking();
    }
    ngOnChanges(changes) {
        const inputList = [
            'reveal',
            'mask',
            'maxLength',
            'numbersOnly',
            'preventPaste',
            'unmaskedLength',
            'hider'
        ];
        let hasAtLeastOneProperty = false;
        for (let i = 0, il = inputList.length; i < il; i++) {
            const input = inputList[i];
            if (changes.hasOwnProperty(input)) {
                hasAtLeastOneProperty = true;
                break;
            }
        }
        if (hasAtLeastOneProperty) {
            this._applyMask(this.value);
        }
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region // ========== Methods ========== //
    _setUpMasking() {
        var userAgent = navigator.userAgent || navigator.vendor;
        if (/android/i.test(userAgent)) {
            return;
        }
        // handle click
        this._listenInput('click').subscribe(event => {
            event.preventDefault();
        });
        // handle paste
        this._listenInput('paste').subscribe(event => {
            event.preventDefault();
            if (this.inputEl.readOnly) {
                return;
            }
            // don't allow paste
            if (this.preventPaste) {
                this.announcer.announce('paste is disabled');
                return;
            }
            this._setSelections();
            // get current value before making modifications.
            // We don't want to modify the components value yet.
            let currentValue = this.value;
            if (this._isRangeSelected) {
                currentValue = this._removeSelection().newValue;
            }
            // get text from clipboard
            let clip = event.clipboardData.getData('Text');
            // only allow digits (and decimal) to be copied from clipboard when numbersOnly is true
            if (this.numbersOnly) {
                clip = clip.replace(/[^\d\.]/g, '');
            }
            // with maxLenth set, make sure paste from clipboard does not exceed maxLength
            if (this.maxLength) {
                clip = clip.slice(0, this.maxLength - currentValue.length);
            }
            const newValue = this._insertCharAt(currentValue, clip, this.inputEl.selectionStart);
            this.value = newValue;
            const newPos = this._cursorStart + clip.length;
            this.inputEl.setSelectionRange(newPos, newPos);
        });
        this._listenInput('cut').subscribe((event) => {
            event.preventDefault();
            if (this.inputEl.readOnly) {
                return;
            }
            this._setSelections();
            if (this._isRangeSelected) {
                const removeSection = this._removeSelection();
                const clipboard = removeSection.cutValue;
                event.clipboardData.setData('text/plain', clipboard);
                this.value = removeSection.newValue;
                this.inputEl.setSelectionRange(this._cursorStart, this._cursorStart);
            }
        });
        this._listenInput('copy').subscribe((event) => {
            event.preventDefault();
            this._setSelections();
            if (this._isRangeSelected) {
                const copySection = this._removeSelection();
                const clipboard = copySection.cutValue;
                event.clipboardData.setData('text/plain', clipboard);
            }
        });
        this._listenInput('keydown').subscribe((event) => {
            if (this.inputEl.readOnly) {
                return;
            }
            this._setSelections();
            if (event.metaKey || event.ctrlKey)
                return;
            // get the first available key code
            const keyCode = event.keyCode || event.which || event.charCode;
            switch (keyCode) {
                case this._codes.BACKSPACE:
                    this._handleRemoveChar(this._cursorStart);
                    event.preventDefault();
                    return;
                case this._codes.DELETE:
                    this._handleRemoveChar(this._cursorStart, true);
                    event.preventDefault();
                    return;
            }
        });
        this._listenInput('keypress').subscribe((event) => {
            if (event.metaKey ||
                event.ctrlKey ||
                this.inputEl.readOnly ||
                event.keyCode == this._codes.KEY_TAB ||
                [0, 8, 13, 27, 20].includes(event.which))
                return;
            event.preventDefault();
            let newValue = this.value;
            const isRangeSelected = this._isRangeSelected;
            const cursorPos = this.inputEl.selectionStart;
            if (isRangeSelected) {
                newValue = this._removeSelection().newValue;
            }
            // Handles ensuring that numbers only allows one decimal point only
            if ((this.numbersOnly && (event.which <= NINE && event.which >= ZERO || event.which == DELETE$1))) {
                let decimalPresent = false;
                for (let i = 0; i < this.inputEl.value.length; i++) {
                    if (this.value[i] == '.') {
                        decimalPresent = true;
                        break;
                    }
                }
                const charCodeNum = event.which || event.charCode || event.keyCode;
                if (String.fromCharCode(charCodeNum) == '.' && decimalPresent) {
                    return;
                }
            }
            //
            if ((this.numbersOnly && (event.which > NINE || (event.which < ZERO && event.which != DELETE$1))) ||
                (this.maxLength && this.inputEl.value.length >= this.maxLength)) {
                if (isRangeSelected) {
                    const charCodeLong = event.which || event.charCode || event.keyCode;
                    this.value = newValue;
                    if ((this.numbersOnly && (event.which <= 57 && event.which >= 48 || event.which == 46)) || !this.numbersOnly) {
                        this.value = this._insertCharAt(this.value, String.fromCharCode(charCodeLong), cursorPos);
                    }
                    this.inputEl.setSelectionRange(cursorPos + 1, cursorPos + 1);
                }
                return;
            }
            // set private '_value' so we don't trigger anything yet
            this._value = newValue;
            // get the available char code
            const charCode = event.which || event.charCode || event.keyCode;
            this._addCharacter(String.fromCharCode(charCode));
            setTimeout(() => {
                this.inputEl.scrollLeft = this.inputEl.scrollWidth;
            }, 0);
        });
        this._listenInput('blur').subscribe(event => {
            this._onTouched();
        });
    }
    _addCharacter(char) {
        const cursorPos = this.inputEl.selectionStart;
        const newValue = this._insertCharAt(this.value, char, cursorPos);
        this.value = newValue;
        this.inputEl.setSelectionRange(cursorPos + 1, cursorPos + 1);
    }
    _insertCharAt(str = '', char, index) {
        // TODO: move to core functions?
        str = str ? str : '';
        return str.substring(0, index) + char + str.substring(index);
    }
    _removeCharAt(str, index) {
        // TODO: move to core functions?
        str = str ? str : '';
        return str.substring(0, index) + str.substring(index + 1);
    }
    _setSelections() {
        this._cursorStart = this.inputEl.selectionStart;
        this._cursorEnd = this.inputEl.selectionEnd;
    }
    _removeSelection() {
        const cutStrArray = this.value.split('');
        const cutStr = cutStrArray
            .splice(this._cursorStart, Math.abs(this._cursorStart - this._cursorEnd))
            .join('');
        const newStr = cutStrArray.join('');
        return { newValue: newStr, cutValue: cutStr };
    }
    _handleRemoveChar(cursorPos, isDelete) {
        if (this._isRangeSelected) {
            const newString = this._removeSelection().newValue;
            this.value = newString;
            this.inputEl.setSelectionRange(this._cursorStart, this._cursorStart);
            return;
        }
        if ((isDelete && cursorPos >= this.value.length) ||
            (!isDelete && cursorPos <= 0)) {
            return;
        }
        const newValue = this._removeCharAt(this.value, isDelete ? cursorPos : cursorPos - 1);
        this.value = newValue;
        const newPostion = isDelete
            ? Math.min(cursorPos, newValue.length)
            : Math.max(cursorPos - 1, 0);
        this.inputEl.setSelectionRange(newPostion, newPostion);
    }
    _applyMask(value) {
        if (!this.reveal && this.mask) {
            switch (this.mask) {
                case 'fullText':
                    this.inputEl.value = this.fullTextMask.transform(value, this.hider);
                    break;
                case 'partialText':
                    this.inputEl.value = this.partialTextMask.transform(value, this.unmaskedLength, this.hider);
                    break;
                case 'partialEmail':
                    this.inputEl.value = this.partialEmailMask.transform(value, this.hider);
                    break;
                default:
                    devError(INVALID_MASK_WARNING);
                    this.inputEl.value = value;
                    break;
            }
        }
        else {
            this.inputEl.value = value;
        }
    }
    /**
     * @description subscribe helper for input events
     */
    _listenInput(eventName) {
        return fromEvent(this.inputEl, eventName).pipe(takeUntil(this.unsub$));
    }
    // Update control value
    writeValue(value) {
        this.value = value;
    }
    // Enable the control to recognize changes
    registerOnChange(fn) {
        this._onChange = fn;
    }
    // Enable control to recognize a touched state
    registerOnTouched(fn) {
        this._onTouched = fn;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialMask, deps: [{ token: DOCUMENT }, { token: i0.Renderer2 }, { token: i1$1.LiveAnnouncer }, { token: CdsFullTextMaskPipe }, { token: CdsPartialTextMaskPipe }, { token: CdsPartialEmailMaskPipe }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPartialMask, selector: "cds-partial-mask", inputs: { value: "value", mask: "mask", maxLength: "maxLength", unmaskedLength: "unmaskedLength", numbersOnly: "numbersOnly", preventPaste: "preventPaste", reveal: "reveal", hider: "hider" }, outputs: { valueChange: "valueChange" }, host: { classAttribute: "cds-partial-mask" }, providers: [PARTIAL_MASK_VALUE_ACCESSOR], queries: [{ propertyName: "cdsInput", first: true, predicate: CdsInput, descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPartialMask, decorators: [{
            type: Component,
            args: [{ selector: 'cds-partial-mask', template: `
    <ng-content></ng-content>
  `, host: {
                        class: 'cds-partial-mask'
                    }, providers: [PARTIAL_MASK_VALUE_ACCESSOR], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.Renderer2 }, { type: i1$1.LiveAnnouncer }, { type: CdsFullTextMaskPipe }, { type: CdsPartialTextMaskPipe }, { type: CdsPartialEmailMaskPipe }], propDecorators: { cdsInput: [{
                type: ContentChild,
                args: [CdsInput, { static: true }]
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], mask: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], unmaskedLength: [{
                type: Input
            }], numbersOnly: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], reveal: [{
                type: Input
            }], hider: [{
                type: Input
            }] } });

const MASK_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsCurrencyMask),
    multi: true
};
class CdsCurrencyMask extends MaskingBase {
    get inputEl() {
        if (!this.inputDir)
            devKill('masking requires a CdsInput as a child');
        return this.inputDir.input.nativeElement;
    }
    set inputText(str) {
        // Added this hack because I noticed the input text wasn't
        // initializing ...not sure why it works only sometimes
        onStable(this.ngZone, () => {
            this.inputEl.value = str;
            // Ultimately tells the <cds-form-field> whether or not to add
            // the cds-has-value
            this.inputDir.checkValue();
        });
    }
    get inputText() {
        return this.inputEl.value;
    }
    get validInputType() {
        return ['text'].includes(this.inputEl.type);
    }
    get isAndroid() {
        return /(android)/i.test(navigator.userAgent);
    }
    /**
     * @description value to bind to when not using the formControlName
     * @note this needs to be below all of the other @Input() proerties
     * @note clear the value by setting to an empty string
     */
    set value(value) {
        this._value = value;
        this._applyMask(value.toString());
    }
    get value() {
        return this._value;
    }
    set unmaskedValue(value) {
        value = parseFloat(value);
        if (Number.isNaN(value)) {
            this._unmaskedValue = undefined;
            this.inputEl.value = null;
        }
        else if (this._unmaskedValue !== value)
            this.reCalculate(value.toString());
    }
    /**
     * Constructor
     */
    constructor(renderer, _document, ngZone, clipboard, currencyPipe, announcer) {
        super(_document, renderer);
        this.renderer = renderer;
        this._document = _document;
        this.ngZone = ngZone;
        this.clipboard = clipboard;
        this.currencyPipe = currencyPipe;
        this.announcer = announcer;
        this.unsub$ = new Subject();
        // regExp for numeric
        this.numericValidator = char => /[0-9]{1}/.test(char);
        /**
         * Custom Inputs
        */
        this.thousandsSeparator = ',';
        this.decimalSeparator = '.';
        this.currencySign = '$';
        // number of decimal places
        this.precision = 2;
        this.currencyCode = 'USD';
        this.display = '';
        this.digitsInfo = '0.2-2';
        /**
         * Handle Events
         */
        this.handleCopy = (event) => {
            event.preventDefault();
            const subString = this.inputText.substr(this.inputEl.selectionStart, this.inputEl.selectionEnd);
            this.clipboard.copy(subString);
        };
        this.handleCut = (event) => {
            event.preventDefault();
            this.inputEl.value =
                this.inputText.substr(0, this.start) +
                    this.inputText.substr(this.end, this.inputText.length);
            this.masking();
        };
        this.handlePaste = (event) => {
            event.preventDefault();
            if (this.inputEl.readOnly) {
                return;
            }
            // get current value before making modifications.
            // We don't want to modify the components value yet.
            let currentValue = this.inputEl.value.slice(0, this.inputEl.selectionStart) + this.inputEl.value.slice(this.inputEl.selectionEnd, this.inputEl.value.length + 1);
            let checkCommaCount = currentValue.split(",").length - 1;
            let checkDecimalCount = currentValue.split(".").length - 1;
            if (this.maxLength != null && (currentValue.length - checkCommaCount - checkDecimalCount) >= this.maxLength) {
                return;
            }
            // get text from clipboard
            let clip = event.clipboardData.getData('Text');
            clip = clip.replace(/[^\d\.]/g, '');
            // with maxLenth set, make sure paste from clipboard does not exceed maxLength
            let inputTextLength = clip.length;
            if (this.maxLength != null && (currentValue.length - checkCommaCount - checkDecimalCount) + inputTextLength > this.maxLength) {
                if (clip.includes('.')) {
                    inputTextLength = inputTextLength - 1;
                    clip = clip.slice(0, this.maxLength - inputTextLength);
                    const lastCharacter = clip.charAt(clip.length - 1);
                    if (lastCharacter === '.') {
                        clip = clip.slice(0, this.maxLength - (inputTextLength - 1));
                    }
                    if (!clip.includes('.')) {
                        clip = clip.slice(0, this.maxLength - clip.length);
                    }
                }
                else {
                    clip = clip.slice(0, this.maxLength - (currentValue.length - checkCommaCount - checkDecimalCount));
                }
            }
            const newCursorPosition = this.inputEl.selectionStart + clip.length;
            const newValue = this.insertCharAt(currentValue, clip, this.inputEl.selectionStart);
            this.storeCountOfDigitsBeforeCursor();
            this.inputEl.value = newValue;
            this.reCalculate(this.inputText);
            this.writeValue(this.getUnMaskedValue(this.inputText));
            let checkComma = this.inputEl.value.split(",").length - 1;
            let checkCurrentValueComma = currentValue.split(",").length - 1;
            this.inputEl.selectionStart = newCursorPosition + checkComma - checkCurrentValueComma;
            this.inputEl.selectionEnd = newCursorPosition + checkComma - checkCurrentValueComma;
            this.reCalcCursor();
        };
        this.handleBlur = (event) => {
            const decimalSplit = this.getParts(this.inputEl.value);
            const decimalLength = decimalSplit.decimal ? decimalSplit.decimal.length : this.precision;
            if (decimalLength < this.precision) {
                const extraZero = this.precision - decimalLength;
                for (let i = 0; i < extraZero; i++) {
                    this.inputEl.value += 0;
                }
            }
        };
        this.handleKeyEvent = (event) => {
            // Non-Character-Entry Commands
            switch (event.key) {
                case 'Backspace':
                    return this.handleBackspace();
                case 'Delete':
                    return this.handleDelete();
                case 'ArrowUp':
                    return;
                case 'ArrowDown':
                    return;
                case 'ArrowLeft':
                    return;
                case 'ArrowRight':
                    return;
                case 'Control':
                    return;
                case 'Shift':
                    return;
            }
            // handle control / alt / shift
            if (event.ctrlKey || event.shiftKey || event.altKey)
                return;
            if (!this.numericValidator(event.key))
                this.inputEl.value = this.removeAlpha(this.inputText);
            this.masking();
        };
        this.handleKeyPressEvent = (event) => {
            this.checkposition = this.start;
            if (this.isValidInput(event.key)) {
                let selectedText = window.getSelection().toString();
                if (selectedText && this.inputEl.value.includes(selectedText)) {
                    let index = this.inputEl.value.indexOf(selectedText) + 1;
                    this.inputEl.value = this.inputText.replace(selectedText, event.key);
                    this.rememberPosition = index;
                    this.inputEl.setSelectionRange(index, index);
                }
                else {
                    this.inputEl.value = this.insertCharAt(this.inputText, event.key, this.checkposition);
                    const tempInput = this.inputText;
                    this.reCalculate(this.inputText);
                    if (this.inputText.length > tempInput.length) {
                        this.checkposition += 1;
                    }
                    this.inputEl.setSelectionRange(this.checkposition + 1, this.checkposition + 1);
                }
            }
        };
        /**
         * @category FORM BINDING METHODS
         * All methods below are required for the ValueControlAccessor interface
         * which is needed to work with the ReactiveForms Module
         */
        this.onChange = (value) => { };
        this.onTouched = () => { };
        this.rememberPosition = 1;
    }
    /**
     * Angular Hooks
     */
    ngAfterContentInit() {
        this.inputDir.checkValue();
        if (this.value != undefined && this.value != null) {
            this._applyMask(this.value.toString());
        }
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    ngOnInit() {
        this.allThousands = new RegExp(this.thousandsSeparator, 'g');
        if (this.inputDir?.fieldId) {
            this.renderer.setAttribute(this.inputDir.input.nativeElement, 'aria-labelledby', this.inputDir.fieldId + ' ' + this.inputDir.fieldId + '-currency-sign');
        }
    }
    ngOnChanges() {
        if (!!this.value)
            this._applyMask(this.value.toString());
    }
    /**
     * Main Apply Mask
     */
    _applyMask(value) {
        var userAgent = navigator.userAgent || navigator.vendor;
        if (/android/i.test(userAgent)) {
            return;
        }
        if (!this.validInputType)
            devKill('masking requires a cdsInput as a type text');
        this.listenInput('cut')
            .pipe(tap(this.handleCopy), tap(this.handleCut))
            .subscribe();
        this.listenInput('copy')
            .pipe(tap(this.handleCopy))
            .subscribe();
        this.listenInput('paste')
            .pipe(tap(this.handlePaste))
            .subscribe();
        this.listenInput('blur')
            .pipe(tap(() => this.onTouched()), tap(() => this.handleBlur()))
            .subscribe();
        this.listenInput('keypress')
            .pipe(tap(this.handlePreventDefault), tap(this.handleKeyPressEvent))
            .subscribe();
        this.listenInput('keyup')
            .pipe(tap(this.handlePreventDefault), tap(this.handleKeyEvent))
            .subscribe();
        this.inputEl.value = this.currencyPipe.transform(value, this.currencyCode, this.display, `0.${this.precision}-${this.precision}`, this.locale);
    }
    ;
    handleBackspace() {
        this.cursorBefore = this.start;
        if (this.inputEl.value.length >= 0) {
            this.storeCountOfDigitsBeforeCursor();
            const tempInput = this.inputText;
            this.reCalculate(this.inputText);
            this.writeValue(this.getUnMaskedValue(this.inputText));
            if (this.inputText.length < tempInput.length && this.cursorBefore !== 0) {
                this.cursorBefore -= 1;
            }
            this.inputEl.setSelectionRange(this.cursorBefore, this.cursorBefore);
        }
        ;
    }
    ;
    handleDelete() {
        this.cursorBefore = this.start;
        this.type = 'Delete';
        if (this.inputEl.value.length >= 0) {
            const subString = this.inputEl.value.substr(0, this.inputEl.selectionStart).length;
            const char = this.inputEl.value.indexOf(",");
            this.storeCountOfDigitsBeforeCursor();
            this.reCalculate(this.inputText);
            this.writeValue(this.getUnMaskedValue(this.inputText));
            if (char === 1 && subString > 1 && subString !== this.inputEl.value.length && this.inputEl.value[subString - 1] !== ',') {
                this.inputEl.setSelectionRange(subString - 1, subString - 1);
            }
            else if (this.inputEl.value[subString] === ',') {
                this.inputEl.setSelectionRange(subString + 1, subString + 1);
            }
            else {
                this.inputEl.setSelectionRange(subString, subString);
            }
        }
        ;
    }
    ;
    /**
     * Masking Steps (Method)
     */
    // breaks a numeric string into its whole and decimal parts
    getParts(value) {
        const decimalSplit = value.split(this.decimalSeparator);
        return {
            whole: decimalSplit[0],
            decimal: decimalSplit[1]
        };
    }
    // adds a digit when a decial is present
    addDigitWithDecimal(value) {
        const parts = this.getParts(value);
        const decimal = this.maskDecimal(parts.decimal);
        // update the input
        this.inputEl.value = `${this.maskWholeNumber(parts.whole)}${this.decimalSeparator}${decimal}`;
        // catch values less than 0
        const whole = parts.whole ? this.getUnMaskedValue(parts.whole) : '0';
        // get the new value
        this._unmaskedValue = parseFloat(`${whole}.${decimal}`);
    }
    // adds a digit when no decimal is present
    addDigitNoDecimal(charAdded) {
        if (charAdded !== '') {
            this.inputEl.value = this.maskWholeNumber(charAdded);
            this._unmaskedValue = this.getUnMaskedValue(this.inputEl.value);
        }
        else
            this.clear();
    }
    maskWholeNumber(wholeNumber) {
        if (!wholeNumber || wholeNumber === '0')
            return '0';
        // remove zeros at the beginnin if not right before the decimal.. only if length of whole number is greater then 1
        while (wholeNumber.charAt(0) === '0' && wholeNumber.length > 1)
            wholeNumber = wholeNumber.substr(1);
        // convert to string and remove thousands seperators
        wholeNumber = (wholeNumber + '').replace(this.allThousands, '');
        // add thousands separators in the correct positions
        return wholeNumber.length > 3
            ? this.reverseString(this.reverseString(wholeNumber)
                .match(/.{1,3}/g)
                .join(this.thousandsSeparator))
            : wholeNumber;
    }
    maskDecimal(decimal) {
        return (decimal + '').substring(0, this.precision);
    }
    getUnMaskedValue(value) {
        const newVal = parseFloat(value
            .split('')
            .filter(char => char !== this.thousandsSeparator)
            .join(''));
        return Number.isNaN(newVal) ? null : newVal;
    }
    removeAlpha(value) {
        return value
            .split('')
            .filter(char => this.numericValidator(char) ||
            char == this.thousandsSeparator ||
            char == this.decimalSeparator)
            .join('');
    }
    // applies the mask to the string
    reCalculate(value) {
        value = this.removeAlpha(value);
        this.hasDecimal(value)
            ? this.addDigitWithDecimal(value)
            : this.addDigitNoDecimal(value);
    }
    masking() {
        this.storeCountOfDigitsBeforeCursor();
        this.reCalculate(this.inputText);
        this.writeValue(this.getUnMaskedValue(this.inputText));
        this.reCalcCursor();
    }
    /**
     * Methods
     */
    isValidInput(char) {
        let valid = true;
        // first conditions
        if (this.numericValidator(char) ||
            char == this.thousandsSeparator ||
            char == this.decimalSeparator)
            valid = true;
        else
            return false;
        // if char is zero
        if (char == '0' && this.inputText == '0')
            return false;
        if (this.inputText == undefined || this.inputText == null) {
            return valid;
        }
        let inputTextLength = this.inputText.replace(/,/g, '').length;
        if (this.inputText.includes('.')) {
            inputTextLength = inputTextLength - 1;
        }
        let selectedText = window.getSelection().toString();
        if (this.maxLength != null && inputTextLength >= this.maxLength && selectedText == '') {
            return false;
        }
        // decimal validation
        if (this.hasDecimal(this.inputText)) {
            const parts = this.getParts(this.inputText);
            const decimal = parts.decimal;
            const decimalPos = this.inputText.length - decimal.length + 1;
            if (this.start > decimalPos && decimal.length >= this.precision)
                return false;
        }
        return valid;
    }
    storeCountOfDigitsBeforeCursor() {
        const subString = this.inputEl.value.substr(0, this.inputEl.selectionStart);
        this.digitsBefore = subString.replace(/\D/g, '').length;
        this.cursorBefore = subString.length;
        if (subString.indexOf(this.decimalSeparator) !== -1)
            this.digitsBefore++;
    }
    clear() {
        this.inputEl.value = '';
        this._unmaskedValue = undefined;
    }
    reCalcCursor(offSet = 0) {
        let newPosition = 0;
        // iterate through the string to find the next spot
        while (this.digitsBefore + offSet > 0) {
            const char = this.inputEl.value.charAt(newPosition);
            if (this.numericValidator(char) || char === this.decimalSeparator)
                this.digitsBefore--;
            newPosition++;
            if (newPosition > this.inputEl.value.length)
                break;
        }
        //skip it if it's a thousands seperator
        const nextChar = this.inputEl.value.charAt(newPosition);
        newPosition =
            nextChar === this.thousandsSeparator ? newPosition + 1 : newPosition;
        // never put the cursor pefore a leading 0
        if (newPosition == 0 && this.inputEl.value.charAt(newPosition) === '0')
            newPosition++;
    }
    /**
     * Utility
     */
    insertCharAt(string = '', char, index) {
        return string.substring(0, index) + char + string.substring(index);
    }
    reverseString(string) {
        return string
            .split('')
            .reverse()
            .join('');
    }
    // does the string have a decimal
    hasDecimal(value) {
        return value.indexOf(this.decimalSeparator) > -1;
    }
    wholeIsZeroish(value) {
        const whole = this.getParts(value).whole;
        return whole === '0' || whole == '';
    }
    // whether or not a decimal should be added
    shouldAddDecimalSeperator() {
        return (this.inputEl.value.indexOf(this.decimalSeparator) !== -1 && // decimal seperator
            this.precision > 0);
    }
    /**
     * @description subscribe helper for input events
     */
    listenInput(eventName) {
        return fromEvent(this.inputEl, eventName).pipe(takeUntil(this.unsub$));
    }
    // Update control value
    writeValue(value) {
        this._value = value;
        this.onChange(value);
    }
    // Enable the control to recognize changes
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Enable control to recognize a touched state
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCurrencyMask, deps: [{ token: i0.Renderer2 }, { token: DOCUMENT }, { token: i0.NgZone }, { token: i1$2.Clipboard }, { token: i2.CurrencyPipe }, { token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCurrencyMask, selector: "cds-currency-mask", inputs: { thousandsSeparator: "thousandsSeparator", decimalSeparator: "decimalSeparator", currencySign: "currencySign", precision: "precision", currencyCode: "currencyCode", display: "display", digitsInfo: "digitsInfo", locale: "locale", maxLength: "maxLength", value: "value" }, host: { classAttribute: "cds-currency-mask" }, providers: [MASK_VALUE_ACCESSOR], queries: [{ propertyName: "inputDir", first: true, predicate: CdsInput, descendants: true, static: true }], usesInheritance: true, usesOnChanges: true, ngImport: i0, template: `
  <span class="currency-sign" *ngIf="!isAndroid">{{ currencySign }}</span>
    <ng-content></ng-content>
  `, isInline: true, styles: [".currency-sign{padding:0 3px;position:absolute;margin-top:12px}\n", ".currency-sign{padding:0 3px}\n", ".cds-input{width:90%}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCurrencyMask, decorators: [{
            type: Component,
            args: [{ selector: 'cds-currency-mask', host: { class: 'cds-currency-mask' }, template: `
  <span class="currency-sign" *ngIf="!isAndroid">{{ currencySign }}</span>
    <ng-content></ng-content>
  `, providers: [MASK_VALUE_ACCESSOR], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".currency-sign{padding:0 3px;position:absolute;margin-top:12px}\n", ".currency-sign{padding:0 3px}\n", ".cds-input{width:90%}\n"] }]
        }], ctorParameters: () => [{ type: i0.Renderer2 }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i0.NgZone }, { type: i1$2.Clipboard }, { type: i2.CurrencyPipe }, { type: i1$1.LiveAnnouncer }], propDecorators: { inputDir: [{
                type: ContentChild,
                args: [CdsInput, { static: true }]
            }], thousandsSeparator: [{
                type: Input
            }], decimalSeparator: [{
                type: Input
            }], currencySign: [{
                type: Input
            }], precision: [{
                type: Input
            }], currencyCode: [{
                type: Input
            }], display: [{
                type: Input
            }], digitsInfo: [{
                type: Input
            }], locale: [{
                type: Input
            }], maxLength: [{
                type: Input
            }], value: [{
                type: Input
            }] } });
;

const COMPONENTS$z = [
    CdsInput,
    CdsFixedMask,
    CdsDateMask,
    CdsPartialMask,
    CdsCurrencyMask
];
const MODULES$j = [
    CommonModule,
    ReactiveFormsModule,
    CdsFullTextMaskPipeModule,
    CdsPartialTextMaskPipeModule,
    CdsPartialEmailMaskPipeModule,
    CdsDateMaskPipeModule
];
class CdsInputModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInputModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsInputModule, declarations: [CdsInput,
            CdsFixedMask,
            CdsDateMask,
            CdsPartialMask,
            CdsCurrencyMask], imports: [CommonModule,
            ReactiveFormsModule,
            CdsFullTextMaskPipeModule,
            CdsPartialTextMaskPipeModule,
            CdsPartialEmailMaskPipeModule,
            CdsDateMaskPipeModule], exports: [CdsInput,
            CdsFixedMask,
            CdsDateMask,
            CdsPartialMask,
            CdsCurrencyMask, ReactiveFormsModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInputModule, providers: [NAVIGATOR_PROVIDER, CurrencyPipe, DatePipe], imports: [MODULES$j, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInputModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$z],
                    imports: [...MODULES$j],
                    exports: [...COMPONENTS$z, ReactiveFormsModule],
                    providers: [NAVIGATOR_PROVIDER, CurrencyPipe, DatePipe]
                }]
        }] });

let nextUniqueId$b = 0;
class CdsTextarea {
    constructor(textarea, id) {
        this.textarea = textarea;
        this.fieldType = 'textarea';
        /**
         * Implemented as part of CdsFormBase.
         */
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        this.fieldId = id || `cds-textarea-${nextUniqueId$b++}`;
        setTimeout(() => this.checkValue());
    }
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (!this.textarea.nativeElement.hasAttribute('disabled') || !isFocused)
            this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.textarea.nativeElement.value);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextarea, deps: [{ token: i0.ElementRef }, { token: 'id', attribute: true }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsTextarea, selector: "textarea[cdsTextarea], textarea[cds-textarea]", host: { listeners: { "focusin": "focusChanged(true)", "focusout": "focusChanged(false)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId", "class.cds-textarea-error": "this.isInErrorState", "class.cds-textarea-success": "this.isInSuccessState" }, classAttribute: "cds-textarea" }, providers: [{ provide: CdsFormBase, useExisting: CdsTextarea }], exportAs: ["cdsTextarea"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextarea, decorators: [{
            type: Directive,
            args: [{
                    selector: 'textarea[cdsTextarea], textarea[cds-textarea]',
                    exportAs: 'cdsTextarea',
                    host: {
                        class: 'cds-textarea',
                        '(focusin)': 'focusChanged(true)',
                        '(focusout)': 'focusChanged(false)',
                        '(input)': 'checkValue()'
                    },
                    providers: [{ provide: CdsFormBase, useExisting: CdsTextarea }]
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], isInErrorState: [{
                type: HostBinding,
                args: ['class.cds-textarea-error']
            }], isInSuccessState: [{
                type: HostBinding,
                args: ['class.cds-textarea-success']
            }] } });

const COMPONENTS$y = [CdsTextarea];
const MODULES$i = [CommonModule];
class CdsTextareaModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextareaModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTextareaModule, declarations: [CdsTextarea], imports: [CommonModule], exports: [CdsTextarea] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextareaModule, imports: [MODULES$i] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextareaModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$y],
                    imports: [...MODULES$i],
                    exports: [CdsTextarea]
                }]
        }] });

/** Change event object emitted by CdsRadio  */
class CdsRadioChange {
    constructor(
    /** A reference to the CdsRadio itself */
    source, 
    /** The string value assigned to the CdsRadio. */
    value, 
    /** whether the new state is checked or not */
    checked) {
        this.source = source;
        this.value = value;
        this.checked = checked;
    }
}

let nextUniqueId$a = 0;
let nextUniqueName$5 = 0;
class CdsRadio {
    // #endregion
    // #region =========== HostBinding ========== //
    get centerClass() { return this.center; }
    get bottomClass() { return this.bottom; }
    /**
     * Whether this radio button is checked
     * @note only 1 radio can be checked, therefore only the last will reamain so
     * @deprecated use Angulars ngModel or reactive formControlName instead
     */
    set checked(checked) {
        const tempChecked = coerceBooleanProperty(checked);
        this._checked = tempChecked;
        if (this._checked) {
            this.onChange(this.value);
        }
    }
    get checked() {
        return this._checked;
    }
    /**
     * name of the individual radio to group certain inputs.
     * @note sets an auto increment name by deafult if none is provided
     */
    set name(name) {
        this._name = name || this._name || this._uname;
    }
    get name() {
        return this._name;
    }
    /**
     * id assigned to the native input element
     * @note if an id is not provided, an auto-increment id is applied
     */
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * whether the radio should be disabled
     * @note I chose the name "disabled" over "disabled" because I was getting
     * warnings. We should be consitent with this accross all form components
     */
    set disabled(disabled) {
        this._disabled = coerceBooleanProperty(disabled);
    }
    get disabled() {
        return this._disabled;
    }
    // #endregion
    // #region =========== Constructor / Life-Cycle Hooks ========== //
    constructor(input, _announcer) {
        this.input = input;
        this._announcer = _announcer;
        // #region =========== CdsFormBase ========== //
        this.fieldType = 'radio';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        /**
         * Whether the radio button should apply the aria-required attribute
         * @note I think this is only useful when using as a standalone radio - N8
         */
        // Constants for LiveAnnouncer
        this.expandedMessage = 'Additional information expanded';
        this.collapsedMessage = 'Additional information collapsed';
        /**
       * Emitted when the input loses focus
       */
        this.onTab = new EventEmitter();
        this._checked = false;
        /**
         * Two-way binding for checked property
         * @deprecated use Angulars ngModel or reactive formControlName instead
         */
        this.checkedChange = new EventEmitter();
        this._uname = `cds-radio-${nextUniqueName$5++}`;
        this._uid = `cds-radio-${nextUniqueId$a++}`;
        /**
         * Emitted when the input loses focus
         */
        this.blur = new EventEmitter();
        /**
         * Emitted when the input gains focus
         */
        this.focus = new EventEmitter();
        /**
         * Emitted when change is fired on the <input>.
         * Primarily used to pass the value up the the group, but also and easy way for
         * consumers to get a referece to the selected component (and element)
         */
        this.change = new EventEmitter();
        /**
         * emits when change comes from outside of component
         * helps notify radio-group of change
         */
        this.writeValueChange = new EventEmitter();
        // #endregion
        // #region =========== ControlValueAccessor Methods ========== //
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
        // set the default values if not provided
        this.idStr = this.idStr;
        this.name = this.name;
    }
    ngOnChanges(changes) {
        for (const propName in changes) {
            const change = changes[propName];
            if (propName === 'content' || propName === 'ariaDescribedby') {
                this._inputDescribedby =
                    this.ariaDescribedby && this.content
                        ? this.ariaDescribedby + ' ' + this.idStr + 'descby'
                        : this.ariaDescribedby
                            ? this.ariaDescribedby
                            : this.content
                                ? this.idStr + 'descby'
                                : null;
            }
        }
    }
    // #endregion
    // #region =========== Methods ========== //
    /**
     * Triggered when the radio button received a click or the
     * input recognized any change.  Clicking on a label element, will trigger
     * a change event on the associated input.
     */
    onRadioChange(event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        event.preventDefault();
        if (this.disabled || this.readonly) {
            return;
        }
        this._checked = event.target?.checked;
        // tell the FormControl to consider the control as 'touched'
        this.onTouched();
        if (this.value == null) {
            this.value = ' ';
        }
        this.onChange(this.value);
        this.change.emit(new CdsRadioChange(this, this.value, this._checked));
        this.checkedChange.emit(this._checked);
    }
    announceCollapse() {
        if (this.content && !this.checked) {
            setTimeout(() => {
                this._announcer.announce(this.collapsedMessage);
            }, 100);
        }
    }
    focusChanged(isFocused) {
        if (this.input.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /**
     * Allows Angular to update the model. The form will pass new values in here
     */
    writeValue(value) {
        const tempChecked = value === this.value;
        this.checked = tempChecked;
        this.writeValueChange.emit(new CdsRadioChange(this, this.value, tempChecked));
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadio, deps: [{ token: i0.ElementRef }, { token: CdsAnnouncerService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsRadio, selector: "cds-radio", inputs: { isInErrorState: "isInErrorState", value: "value", expandedMessage: "expandedMessage", collapsedMessage: "collapsedMessage", ariaRequired: "ariaRequired", label: "label", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", ariaControls: "ariaControls", content: "content", ariaSetSize: "ariaSetSize", ariaPosinset: "ariaPosinset", checked: "checked", name: "name", idStr: "idStr", disabled: "disabled", readonly: "readonly", bottom: "bottom", center: "center" }, outputs: { onTab: "onTab", checkedChange: "checkedChange", blur: "blur", focus: "focus", change: "change", writeValueChange: "writeValueChange" }, host: { listeners: { "focusin": "focusChanged(true)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId", "class.cds-radio-error": "this.isInErrorState", "attr.aria-busy": "this.busy", "class.position-center": "this.centerClass", "class.position-bottom": "this.bottomClass" }, classAttribute: "cds-radio" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsRadio)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsRadio }
        ], viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["elementRef"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <input
      #elementRef
      class="cds-radio-input"
      [class.cds-radio-error]="isInErrorState"
      [class.cds-radio-readonly]="readonly"
      type="radio"
      [attr.id]="idStr"
      [value]="value"
      [attr.name]="name"
      [checked]="checked"
      [attr.aria-required]="ariaRequired"
      [disabled]="disabled"
      [attr.content]="content"
      [attr.aria-describedby]="_inputDescribedby"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-setsize]="ariaSetSize"
      [attr.aria-posinset]="ariaPosinset"
      (blur)="blur.emit($event)"
      (focus)="focus.emit($event)"
      (keydown.Tab)="onTab.emit($event)"
      (change)="onRadioChange($event)"
      (click)="!this.readonly"
    />
    <label
      class="cds-radio-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
    >
    <span *ngIf="label" [outerHTML]="label"></span>
    <ng-content *ngIf="!label"></ng-content>
    </label>

    <!-- This is the projected content when the radio button is checked -->
    <!-- This will only toggle properly when used with the radio group -->
    <span *ngIf="content" [id]="idStr + 'descby'" class="sr-only" aria-hidden="true">
      {{this.checked ? this.expandedMessage : this.collapsedMessage}}
    </span>
    <ng-content *ngIf="checked" select="[cdsRadioAddition]"></ng-content>`, isInline: true, styles: [":host{display:flex;gap:var(--cds-radio-label-gap, 16px)}:host.position-bottom{align-items:flex-end}:host.position-center{align-items:center}:host-context(.cds-radio-outline-inverse) :focus-visible{--cds-radio-focus-outline-color: var(--cds-radio-focus-outline-inverse-color, #fff);--cds-radio-focus-inner-outline-color: var( --cds-radio-focus-inner-outline-inverse-color, #000)}.cds-radio-label{display:flex;gap:16px;margin:0;line-height:var(--cds-radio-label-line-height);padding:var(--cds-radio-label-padding)}.cds-radio-input{border:var(--cds-radio-border-width, 2px) solid var(--cds-radio-border-color, #056dae);border-radius:100%;width:var(--cds-radio-width, 24px);height:var(--cds-radio-width, 24px);appearance:none;flex:0 0 auto;background-color:var(--cds-radio-bg-color)}.cds-radio-input[type=radio]{margin:0}.cds-radio-input:checked{box-shadow:var(--cds-radio-checked-box-shadow-inset, inset 0 0 0 4px #fff);--cds-radio-border-color: var(--cds-radio-checked-border-color, #056dae);--cds-radio-bg-color: var(--cds-radio-checked-bg-color, #056dae);--cds-radio-border-width: var(--cds-radio-checked-border-width, 2px)}.cds-radio-input:hover:enabled{--cds-radio-border-color: var(--cds-radio-hover-border-color, #034c81);--cds-radio-checked-bg-color: var(--cds-radio-checked-hover-bg-color, #034c81)}.cds-radio-input:active:enabled{--cds-radio-border-color: var(--cds-radio-pressed-border-color, var(--cds-radio-hover-border-color, #034c81));--cds-radio-checked-bg-color: var(--cds-radio-checked-pressed-bg-color, var(--cds-radio-checked-hover-bg-color, #034c81))}.cds-radio-input:focus-visible{box-shadow:var(--cds-radio-checked-box-shadow-inset, inset 0 0 0 4px #fff),0 0 0 2px var(--cds-radio-focus-inner-outline-color, #fff);outline:2px solid var(--cds-radio-focus-outline-color, #034c81);outline-offset:2px}.cds-radio-input.cds-radio-error:enabled{--cds-radio-border-color: var(--cds-radio-error-border-color, #d60000);--cds-radio-checked-bg-color: var(--cds-radio-checked-error-bg-color, #d60000)}.cds-radio-input:disabled{--cds-radio-bg-color: var(--cds-radio-disabled-bg-color, transparent);--cds-radio-border-color: var(--cds-radio-disabled-border-color, #bbb);--cds-radio-checked-bg-color: var(--cds-radio-checked-disabled-bg-color, #bbb)}.cds-radio-input:disabled:checked{--cds-radio-bg-color: var(--cds-radio-checked-disabled-bg-color, #bbb)}.cds-radio-input:disabled+.cds-radio-label{color:var(--cds-radio-disabled-color, #bbb)}.cds-radio-input.cds-radio-readonly,.cds-radio-input.cds-radio-readonly:hover,.cds-radio-input.cds-radio-readonly:active{--cds-radio-bg-color: var(--cds-radio-readonly-bg-color, transparent);--cds-radio-border-color: var(--cds-radio-readonly-border-color, #666);--cds-radio-checked-bg-color: var(--cds-radio-checked-readonly-bg-color, #666)}.cds-radio-input.cds-radio-readonly:checked,.cds-radio-input.cds-radio-readonly:checked:hover{--cds-radio-bg-color: var(--cds-radio-checked-readonly-bg-color, #666)}.cds-radio-input.cds-radio-readonly+.cds-radio-label{color:var(--cds-radio-readonly-color, #666)}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadio, decorators: [{
            type: Component,
            args: [{ selector: 'cds-radio', host: {
                        class: 'cds-radio',
                        '(focusin)': 'focusChanged(true)',
                        '(input)': 'checkValue()'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsRadio)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsRadio }
                    ], template: `
    <input
      #elementRef
      class="cds-radio-input"
      [class.cds-radio-error]="isInErrorState"
      [class.cds-radio-readonly]="readonly"
      type="radio"
      [attr.id]="idStr"
      [value]="value"
      [attr.name]="name"
      [checked]="checked"
      [attr.aria-required]="ariaRequired"
      [disabled]="disabled"
      [attr.content]="content"
      [attr.aria-describedby]="_inputDescribedby"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-setsize]="ariaSetSize"
      [attr.aria-posinset]="ariaPosinset"
      (blur)="blur.emit($event)"
      (focus)="focus.emit($event)"
      (keydown.Tab)="onTab.emit($event)"
      (change)="onRadioChange($event)"
      (click)="!this.readonly"
    />
    <label
      class="cds-radio-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
    >
    <span *ngIf="label" [outerHTML]="label"></span>
    <ng-content *ngIf="!label"></ng-content>
    </label>

    <!-- This is the projected content when the radio button is checked -->
    <!-- This will only toggle properly when used with the radio group -->
    <span *ngIf="content" [id]="idStr + 'descby'" class="sr-only" aria-hidden="true">
      {{this.checked ? this.expandedMessage : this.collapsedMessage}}
    </span>
    <ng-content *ngIf="checked" select="[cdsRadioAddition]"></ng-content>`, styles: [":host{display:flex;gap:var(--cds-radio-label-gap, 16px)}:host.position-bottom{align-items:flex-end}:host.position-center{align-items:center}:host-context(.cds-radio-outline-inverse) :focus-visible{--cds-radio-focus-outline-color: var(--cds-radio-focus-outline-inverse-color, #fff);--cds-radio-focus-inner-outline-color: var( --cds-radio-focus-inner-outline-inverse-color, #000)}.cds-radio-label{display:flex;gap:16px;margin:0;line-height:var(--cds-radio-label-line-height);padding:var(--cds-radio-label-padding)}.cds-radio-input{border:var(--cds-radio-border-width, 2px) solid var(--cds-radio-border-color, #056dae);border-radius:100%;width:var(--cds-radio-width, 24px);height:var(--cds-radio-width, 24px);appearance:none;flex:0 0 auto;background-color:var(--cds-radio-bg-color)}.cds-radio-input[type=radio]{margin:0}.cds-radio-input:checked{box-shadow:var(--cds-radio-checked-box-shadow-inset, inset 0 0 0 4px #fff);--cds-radio-border-color: var(--cds-radio-checked-border-color, #056dae);--cds-radio-bg-color: var(--cds-radio-checked-bg-color, #056dae);--cds-radio-border-width: var(--cds-radio-checked-border-width, 2px)}.cds-radio-input:hover:enabled{--cds-radio-border-color: var(--cds-radio-hover-border-color, #034c81);--cds-radio-checked-bg-color: var(--cds-radio-checked-hover-bg-color, #034c81)}.cds-radio-input:active:enabled{--cds-radio-border-color: var(--cds-radio-pressed-border-color, var(--cds-radio-hover-border-color, #034c81));--cds-radio-checked-bg-color: var(--cds-radio-checked-pressed-bg-color, var(--cds-radio-checked-hover-bg-color, #034c81))}.cds-radio-input:focus-visible{box-shadow:var(--cds-radio-checked-box-shadow-inset, inset 0 0 0 4px #fff),0 0 0 2px var(--cds-radio-focus-inner-outline-color, #fff);outline:2px solid var(--cds-radio-focus-outline-color, #034c81);outline-offset:2px}.cds-radio-input.cds-radio-error:enabled{--cds-radio-border-color: var(--cds-radio-error-border-color, #d60000);--cds-radio-checked-bg-color: var(--cds-radio-checked-error-bg-color, #d60000)}.cds-radio-input:disabled{--cds-radio-bg-color: var(--cds-radio-disabled-bg-color, transparent);--cds-radio-border-color: var(--cds-radio-disabled-border-color, #bbb);--cds-radio-checked-bg-color: var(--cds-radio-checked-disabled-bg-color, #bbb)}.cds-radio-input:disabled:checked{--cds-radio-bg-color: var(--cds-radio-checked-disabled-bg-color, #bbb)}.cds-radio-input:disabled+.cds-radio-label{color:var(--cds-radio-disabled-color, #bbb)}.cds-radio-input.cds-radio-readonly,.cds-radio-input.cds-radio-readonly:hover,.cds-radio-input.cds-radio-readonly:active{--cds-radio-bg-color: var(--cds-radio-readonly-bg-color, transparent);--cds-radio-border-color: var(--cds-radio-readonly-border-color, #666);--cds-radio-checked-bg-color: var(--cds-radio-checked-readonly-bg-color, #666)}.cds-radio-input.cds-radio-readonly:checked,.cds-radio-input.cds-radio-readonly:checked:hover{--cds-radio-bg-color: var(--cds-radio-checked-readonly-bg-color, #666)}.cds-radio-input.cds-radio-readonly+.cds-radio-label{color:var(--cds-radio-readonly-color, #666)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CdsAnnouncerService }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], isInErrorState: [{
                type: HostBinding,
                args: ['class.cds-radio-error']
            }, {
                type: Input
            }], busy: [{
                type: HostBinding,
                args: ['attr.aria-busy']
            }], elementRef: [{
                type: ViewChild,
                args: ['elementRef']
            }], centerClass: [{
                type: HostBinding,
                args: ['class.position-center']
            }], bottomClass: [{
                type: HostBinding,
                args: ['class.position-bottom']
            }], value: [{
                type: Input
            }], expandedMessage: [{
                type: Input
            }], collapsedMessage: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], label: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaControls: [{
                type: Input
            }], content: [{
                type: Input
            }], ariaSetSize: [{
                type: Input
            }], ariaPosinset: [{
                type: Input
            }], onTab: [{
                type: Output
            }], checked: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }], name: [{
                type: Input
            }], idStr: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], bottom: [{
                type: Input
            }], center: [{
                type: Input
            }], blur: [{
                type: Output
            }], focus: [{
                type: Output
            }], change: [{
                type: Output
            }], writeValueChange: [{
                type: Output
            }] } });

let nextUniqueName$4 = 0;
class CdsRadioGroup {
    /**
     * gets the currently selected CdsRadio component
     */
    get checkedCdsRadio() {
        if (!this._radios)
            return null;
        return this._radios.filter(radio => radio.checked);
    }
    /**
     * name to be shared between all radios
     * @note radios must share a name to be recognized as a group
     */
    get name() {
        return this._name || this.fcn || this._uname;
    }
    set name(name) {
        this._name = name || this._name || this._uname;
        this.updateRadioButtonNames();
    }
    /**
     * disables the entire group
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.updateDisabled();
    }
    /**
     * Puts the entire group in readOnly state
     */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
        this._updateReadonly();
    }
    get readonly() {
        return this._readonly;
    }
    /**
     * two-way value binding mechanism
     * @deprecated use angular ngModel or reactive formControlName instead.
     * The property is still used, but should be considered private
     */
    get value() {
        return this._value;
    }
    set value(value) {
        if (this._value !== value) {
            this._value = value;
        }
    }
    // #endregion
    // #region =========== Constructor / Life-Cycle Hooks ========== //
    constructor(_el, fcn, _cdr) {
        this._el = _el;
        this.fcn = fcn;
        this._cdr = _cdr;
        // #region =========== CdsFormBase ========== //
        this.fieldType = 'radio-group';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        // #endregion
        // #region =========== Properties ========== //
        this.unsub$ = new Subject();
        // #endregion
        // #region =========== Input/Output ========== //
        /**
         * flex alignment of radios in the group
         */
        this.alignment = 'column';
        /**
         * sets the flex-wrap property to wrap the radio buttons in the group
         */
        this.flexWrap = false;
        /**
         * whether or not to trigger the error ribbon
         * @note this may be deprecated based on cds designs
         */
        this.error = false;
        /**
       * input for niche ADA error where ShiftTab does not announce error message
       * @deprecated this is no longer used
       */
        this.errorMessage = null;
        this._uname = `cds-radio-group-${nextUniqueName$4++}`;
        this._disabled = false;
        /**
         * @deprecated use angular ngModel or reactive formControlName instead.
         */
        this.valueChange = new EventEmitter();
        this.blur = new EventEmitter();
        // #endregion
        // #region =========== ControlValueAccessor Methods ========== //
        /** The method to be called in order to update ngModel */
        this.onChange = (value) => { };
        /** onTouch function registered via registerOnTouch (ControlValueAccessor) */
        this.onTouched = () => { };
    }
    ngAfterContentInit() {
        this.checkForInlineName();
        this._listenForChanges();
        // if the group is disabled, update the radios
        if (this.disabled) {
            this.updateDisabled();
        }
        // Delay setting the radios until the next tick to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this._setRadiosAfterContentInit(this.value);
        }, 0);
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region =========== Methods ========== //
    _focusoutRadioGroup(e) {
        const isInRadioGroup = this._el.nativeElement.contains(e.relatedTarget);
        if (!isInRadioGroup) {
            this._isFocused = false;
            this.focusChange.next(this._isFocused);
            this.onTouched();
            this.blur.emit();
        }
    }
    _focusinRadioGroup(e) {
        if (!this._isFocused) {
            this._isFocused = true;
            this.focusChange.next(this._isFocused);
        }
    }
    _setRadiosAfterContentInit(value) {
        if (!value)
            value = '';
        if (this._radios) {
            this._radios
                .forEach(radio => {
                radio.checked = radio.value === value;
            });
        }
    }
    /**
     * Sets default
     * @deprecated this is not the Angular way to set default values.
     * Please use Angular's reactive forms or ngModel instead.
     */
    setDefault(value) {
        this._setRadiosAfterContentInit(value);
    }
    clear() {
        this.deselectCurrent();
    }
    _listenForChanges() {
        this._radios.forEach(radio => {
            radio.change
                .pipe(takeUntil(this.unsub$))
                .subscribe(change => {
                this._updateRadioGroup(change, true);
                this.onTouched();
            });
            radio.writeValueChange
                .pipe(takeUntil(this.unsub$))
                .subscribe(change => {
                this._updateRadioGroup(change, false);
            });
        });
    }
    _updateRadioGroup(change, isFocusMaintained) {
        if (isFocusMaintained) {
            this.maintainFocus(change);
        }
        if (change.checked) {
            this.selectFromRadio(change);
        }
        else {
            this.deselectCurrent();
        }
        this._radios.forEach(radio => {
            radio.checked = radio.value === change.value;
        });
    }
    _updateRadios(value) {
        this._radios?.forEach(radio => {
            radio.checked = radio.value === value;
        });
    }
    //maintains the focus on same checkbox if keyboard has been used to check radio button.
    maintainFocus(getChange) {
        let elRef = getChange.source.elementRef.nativeElement;
        elRef.focus();
    }
    selectFromRadio(change) {
        this.onChange(change.value);
        this.valueChange.emit(change.value);
    }
    deselectCurrent() {
        if (this.checkedCdsRadio) {
            for (let radio of this.checkedCdsRadio) {
                radio.checked = false;
            }
        }
    }
    checkForInlineName() {
        const firstName = this._radios.first.name;
        const all = this._radios.map(r => r.name);
        if (all.every(name => name === firstName)) {
            this.name = firstName;
        }
        else {
            this.updateRadioButtonNames();
        }
    }
    updateRadioButtonNames() {
        // Wait for the next tick to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this._radios?.forEach(radio => {
                radio.name = this.name;
                this._cdr.markForCheck();
            });
        }, 0);
    }
    updateDisabled() {
        // Wait for the next tick to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this._radios?.forEach(radio => {
                radio.disabled = this._disabled;
                this._cdr.markForCheck();
            });
        }, 0);
    }
    // Updates readonly property to all child checkboxes.
    _updateReadonly() {
        // Wait for the next tick to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this._radios?.forEach(radio => {
                radio.readonly = this._readonly;
                this._cdr.markForCheck();
            });
        }, 0);
    }
    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(selected) {
        this.value = selected;
        this._updateRadios(selected);
    }
    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     * @param isDisabled Whether the control should be disabled.
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadioGroup, deps: [{ token: i0.ElementRef }, { token: 'formControlName', attribute: true, optional: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsRadioGroup, selector: "cds-radio-group", inputs: { alignment: "alignment", flexWrap: "flexWrap", error: "error", titleText: "titleText", errorMessage: "errorMessage", name: "name", disabled: "disabled", readonly: "readonly", value: "value" }, outputs: { valueChange: "valueChange", blur: "blur" }, host: { listeners: { "focusout": "_focusoutRadioGroup($event)", "focusin": "_focusinRadioGroup($event)" }, properties: { "class.cds-radio-group-error": "this.error" }, classAttribute: "cds-radio-group" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsRadioGroup)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsRadioGroup }
        ], queries: [{ propertyName: "_radios", predicate: CdsRadio }], ngImport: i0, template: `
  <legend *ngIf="titleText" class="legend-text">
    <span [innerHTML]="titleText"></span>
  </legend>
  <div
    role="radiogroup"
    class="radio-wrapper"
    [attr.aria-label]= "readonly? 'read only' : null"
    [class.radio-column]="alignment === 'column'"
    [class.radio-flex-wrap]="flexWrap"
    [attr.aria-disabled]="readonly"
  >
    <ng-content></ng-content>
  </div>
  `, isInline: true, styles: [":host{margin-top:var(--cds-radio-group-margin-top, 0px)}:host.cds-radio-group-error{border-left:2px solid var(--cds-radio-group-error-border-color, #d60000);padding-left:8px}.radio-wrapper{display:flex;row-gap:var(--cds-radio-row-gap, 16px);column-gap:24px}.radio-column{flex-direction:column}.radio-flex-wrap{flex-wrap:wrap}@media screen and (max-width: 768px){.radio-wrapper{flex-direction:column}}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadioGroup, decorators: [{
            type: Component,
            args: [{ selector: 'cds-radio-group', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsRadioGroup)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsRadioGroup }
                    ], host: {
                        class: 'cds-radio-group',
                        '(focusout)': '_focusoutRadioGroup($event)',
                        '(focusin)': '_focusinRadioGroup($event)'
                    }, template: `
  <legend *ngIf="titleText" class="legend-text">
    <span [innerHTML]="titleText"></span>
  </legend>
  <div
    role="radiogroup"
    class="radio-wrapper"
    [attr.aria-label]= "readonly? 'read only' : null"
    [class.radio-column]="alignment === 'column'"
    [class.radio-flex-wrap]="flexWrap"
    [attr.aria-disabled]="readonly"
  >
    <ng-content></ng-content>
  </div>
  `, styles: [":host{margin-top:var(--cds-radio-group-margin-top, 0px)}:host.cds-radio-group-error{border-left:2px solid var(--cds-radio-group-error-border-color, #d60000);padding-left:8px}.radio-wrapper{display:flex;row-gap:var(--cds-radio-row-gap, 16px);column-gap:24px}.radio-column{flex-direction:column}.radio-flex-wrap{flex-wrap:wrap}@media screen and (max-width: 768px){.radio-wrapper{flex-direction:column}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Attribute,
                    args: ['formControlName']
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { _radios: [{
                type: ContentChildren,
                args: [CdsRadio]
            }], alignment: [{
                type: Input
            }], flexWrap: [{
                type: Input
            }], error: [{
                type: HostBinding,
                args: ['class.cds-radio-group-error']
            }, {
                type: Input
            }], titleText: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], name: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], blur: [{
                type: Output
            }] } });

const COMPONENTS$x = [CdsRadio, CdsRadioGroup];
const MODULES$h = [CommonModule];
class CdsRadioModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadioModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsRadioModule, declarations: [CdsRadio, CdsRadioGroup], imports: [CommonModule], exports: [CdsRadio, CdsRadioGroup] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadioModule, imports: [MODULES$h] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadioModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$h],
                    declarations: [...COMPONENTS$x],
                    exports: [...COMPONENTS$x]
                }]
        }] });

function CdsSliderRangeValidator(minValue, maxValue) {
    return function validateRange(c) {
        let isInvalid = false;
        const err = {
            rangeError: {
                given: c.value,
                min: minValue,
                max: maxValue
            }
        };
        if (c.value !== null &&
            c.value !== undefined &&
            typeof c.value.min === 'number' &&
            typeof c.value.max === 'number') {
            isInvalid =
                c.value.min < +minValue ||
                    c.value.min > +maxValue ||
                    c.value.max < +minValue ||
                    c.value.max > +maxValue;
        }
        else {
            isInvalid = c.value < +minValue || c.value > +maxValue;
        }
        return isInvalid ? err : null;
    };
}

var SliderEnum;
(function (SliderEnum) {
    SliderEnum["currentValue"] = "currentValue";
    SliderEnum["currentMinValue"] = "currentMinValue";
    SliderEnum["currentMaxValue"] = "currentMaxValue";
    SliderEnum["handleValueChange"] = "handleValueChange";
    SliderEnum["handleMinValueChange"] = "handleMinValueChange";
    SliderEnum["handleMaxValueChange"] = "handleMaxValueChange";
})(SliderEnum || (SliderEnum = {}));
class CdsSlider {
    set value(value) {
        this._value = value;
        // Update the given value
        this.updateValue(value, SliderEnum.currentValue, SliderEnum.handleValueChange);
    }
    set minValue(value) {
        // Update the given value
        this.updateValue(value, SliderEnum.currentMinValue, SliderEnum.handleMinValueChange);
    }
    set maxValue(value) {
        // Update the given value
        this.updateValue(value, SliderEnum.currentMaxValue, SliderEnum.handleMaxValueChange);
    }
    // Document mouseup host listener
    onMouseUp(event) {
        this.handleDragEnd();
    }
    onTouchEnd(event) {
        this.handleDragEnd();
    }
    // Window resize host listener
    onWindowResize(event) {
        // Let the slider know what its new width is
        this.setSliderWidth();
    }
    constructor(announcer, changeRef, elementRef, _document) {
        this.announcer = announcer;
        this.changeRef = changeRef;
        this.elementRef = elementRef;
        this._document = _document;
        // #region =========== FormBase ========== //
        this.fieldType = 'slider';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        // #endregion
        this.showRightHandle = false;
        this.showLeftHandle = false;
        this.leftHandleOffset = 0;
        this.rightHandleOffset = 100;
        this.inputTimeoutLength = 250;
        this.mouseUp = new EventEmitter();
        this.interacting = false;
        this.isStartup = true;
        this.isDisabled = false;
        this.idStr = null;
        this.name = 'dropdown';
        this.range = 'min';
        this.size = 'small';
        this.min = 1;
        this.max = 5;
        this.step = 1;
        this.labels = [];
        this.handleLabel = 'value';
        this.minHandleLabel = 'minimum value';
        this.maxHandleLabel = 'maximum value';
        this.unitOfValue = 'dollars';
        /** Whether the Slider is disabled. */
        this.disabled = false;
        this.valueChange = new EventEmitter();
        this.minValueChange = new EventEmitter();
        this.maxValueChange = new EventEmitter();
        /**
         * @description Emitted when the input loses focus
         */
        this.blur = new EventEmitter();
        /**
         * @description Emitted when the input gains focus
         */
        this.focus = new EventEmitter();
        // #region /// --- ControlValueAccessor --- ///
        // tslint:disable-next-line: ban-types
        this.propagateChange = (_) => { };
        // tslint:disable-next-line: ban-types
        this.propagateTouched = () => { };
    }
    onClick(event) {
        this.handleValueChange(this.currentValue);
    }
    ngOnInit() {
        // Set up the slider on init
        this.setUpSlider(this.range);
    }
    // TODO: trigger this from a setter
    ngOnChanges(changes) {
        if (changes.min || changes.max) {
            this.validateFn = CdsSliderRangeValidator(this.min, this.max);
        }
    }
    ngAfterViewInit() {
        // We are no longer in startup now that this cycle has passed.
        this.isStartup = false;
    }
    // #endregion - Lifecycle Hooks
    // #region /// -------------------- Methods -------------------- ///
    // Gets a slider value based on a given offset (% from left of slider)
    getValueFromOffset(offset) {
        const min = parseInt(String(this.min), 10);
        const max = parseInt(String(this.max), 10);
        return (max - min) * (offset / 100) + min;
    }
    // Accounts for the steps input if there is one
    accountForSteps(value, property) {
        return !this.step ? value : Math.round(value / this.step) * this.step;
    }
    // check how many decimals in a number
    findDecimalPlace(value) {
        if (value < 0) {
            value = -value;
        }
        const valueString = value.toString();
        return valueString.includes('.') ? valueString.split('.')[1].length : 0;
    }
    // Calculates a valid value and returns it
    calculateValidValue(value, handleSide, property) {
        // Account for any steps
        let calculatedValue = this.accountForSteps(value, property);
        // Account for steps less than
        calculatedValue = parseFloat(calculatedValue.toFixed(this.findDecimalPlace(this.step)));
        // Account for the number being above or below the min or max
        calculatedValue =
            calculatedValue < this.min
                ? this.min
                : calculatedValue > this.max
                    ? this.max
                    : calculatedValue;
        // If this is a double handle range slider
        if (this.rangeType === 'double') {
            if (handleSide === 'left' && calculatedValue >= this.currentMaxValue) {
                if (calculatedValue >= this.min && calculatedValue <= this.max) {
                    // this.currentMaxValue = calculatedValue;
                    if (calculatedValue === this.currentMaxValue ||
                        calculatedValue > this.currentMaxValue) {
                        calculatedValue = this.currentMinValue;
                    }
                }
                else {
                    this.announcer.announce('The Maximum value of slider is' +
                        this.max +
                        ' Enter value less than the maximum value');
                }
            }
            else if (handleSide === 'right' &&
                calculatedValue <= this.currentMinValue) {
                if (calculatedValue >= this.min && calculatedValue <= this.max) {
                    if (calculatedValue === this.currentMinValue ||
                        calculatedValue < this.currentMinValue) {
                        calculatedValue = this.currentMaxValue;
                    }
                    calculatedValue = this.currentMaxValue;
                }
                else {
                    this.announcer.announce('The Minimum value of slider is' +
                        this.min +
                        ' Enter value greater than the maximum value');
                }
            }
        }
        // Return the calculated value
        return calculatedValue;
    }
    minOrMaxValReached(val) {
        let label = '';
        if (val == this.max) {
            label = ' You have reached the max value.';
        }
        if (val == this.min) {
            label = ' You have reached the min value.';
        }
        return label;
    }
    setNewValue(value, property, emitter, handleSide) {
        // Get the new value by making sure we calculate a valid value based on the given value
        const validValue = this.calculateValidValue(value, handleSide, property);
        // Detect if the validValue has changed
        if (this[property] !== validValue) {
            // Update the property to reflect the new value
            this[property] = validValue;
            // Emit the new value through the emitter
            this[emitter].emit(validValue);
            if (property === SliderEnum.currentMinValue ||
                property === SliderEnum.currentMaxValue) {
                this.propagateChange({
                    min: this.currentMinValue,
                    max: this.currentMaxValue
                });
            }
            else {
                this.propagateChange(validValue);
            }
        }
    }
    // Updates a value by performing any necessary steps and then calling the correct handler with the new value
    updateValue(newValue, valueProperty, handlerName) {
        const isMinMaxValue = !!(valueProperty === SliderEnum.currentValue &&
            newValue &&
            typeof newValue.min === 'number' &&
            typeof newValue.max === 'number');
        if (this.disabled)
            return;
        // If this is the startup lifecycle, just set the value to current value and do nothing else since the mechanisms aren't ready
        if (this.isStartup) {
            this[valueProperty] = newValue;
            if (isMinMaxValue) {
                this.currentMinValue = newValue.min;
                this.currentMaxValue = newValue.max;
            }
            return;
        }
        // We need to wait for the users input to finish, clear any existing timeouts set
        if (this.inputTimeout)
            clearTimeout(this.inputTimeout);
        // Set a new timeout to wait for the users input to finish
        this.inputTimeout = setTimeout(() => {
            // Otherwise if the user is not interacting with the mouse
            if (!this.interacting) {
                // Handle the value change
                this[handlerName](newValue);
                if (isMinMaxValue) {
                    this.handleMinValueChange(newValue.min);
                    this.handleMaxValueChange(newValue.max);
                }
            }
        }, this.inputTimeoutLength);
    }
    writeValue(value) {
        if (value !== undefined) {
            // Make sure min value is not outside of slider min
            if (value && value.min < this.min) {
                value.min = this.min;
            }
            // Make sure max value is not outside of slider max
            if (value && value.max > this.max) {
                value.max = this.max;
            }
            this.value = value;
        }
    }
    validate(c) {
        return this.validateFn(c);
    }
    registerOnChange(fn) {
        this.propagateChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    setDisableState(isDisable) {
        console.warn('in SLIDER disabled setter', this.disabled);
        this.disabled = isDisable;
        this.changeRef.detectChanges();
    }
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (this.elementRef.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    // #endregion - ControlValueAccessor
    // #region /// --- Getters --- ///
    // Gets an offset from a handle value
    getOffsetFromValue(value) {
        return ((value - this.min) / (this.max - this.min)) * 100;
    }
    // Determines the left handles offset from the left
    getLeftHandleOffset() {
        const offsetVal = this.getOffsetFromValue(this.rangeType === 'single' ? this.currentValue : this.currentMinValue);
        // for some reason, when set to 100, NVDA does not see the handle anymore
        // perhaps the handle is pushed out of the bounding box so NVDA doesn't see it
        return offsetVal === 100 ? 99.99 : offsetVal;
    }
    // Determines the right handles offset from the left
    getRightHandleOffset() {
        const offsetVal = this.getOffsetFromValue(this.rangeType === 'single' ? this.currentValue : this.currentMaxValue);
        // for some reason, when set to 100, NVDA does not see the handle anymore
        // perhaps the handle is pushed out of the bounding box so NVDA doesn't see it
        return offsetVal === 100 ? 99.99 : offsetVal;
    }
    // Gets the tracks offset from the left
    getTrackLeft() {
        return this.range === 'max' ? '0' : this.getLeftHandleOffset();
    }
    // Gets the width of the track
    getTrackWidth() {
        if (this.range === 'max')
            return this.getLeftHandleOffset();
        else if (this.range === 'min')
            return 100 - this.getLeftHandleOffset();
        else if (this.rangeType === 'double')
            return this.getRightHandleOffset() - this.getLeftHandleOffset();
    }
    // Gets whether a hash is highlight or not
    getHashHighlighted(index) {
        // what percent each step is across the slider
        const stepPercent = (100 / this.hashMarkArray.length) * (index + 1);
        // Return if the hash is highlighted based on if the left handle offset is greater than the offset of the given hash
        if (this.range === 'max')
            return this.getLeftHandleOffset() >= stepPercent ? 'highlighted' : null;
        else if (this.range === 'min')
            return this.getLeftHandleOffset() <= stepPercent ? 'highlighted' : null;
        else if (this.rangeType === 'double')
            return this.getLeftHandleOffset() <= stepPercent &&
                this.getRightHandleOffset() >= stepPercent
                ? 'highlighted'
                : null;
    }
    // #endregion - Getters
    // #region /// --- Setters --- ///
    // Sets the width of the slider
    setSliderWidth() {
        // I hate you set timeout
        setTimeout(() => {
            // Determine the width of the slider in the document
            this.sliderWidth = this.slider.nativeElement.offsetWidth;
            if (this.sliderWidth === 0) {
                this.setSliderWidth();
            }
        }, 50);
    }
    keydownHandler(event, handleType) {
        const handle = handleType === 'left'
            ? this.leftHandle.nativeElement
            : this.rightHandle.nativeElement;
        this.handlePositionChange({ event: event, handle: handle });
    }
    // Sets the initial slider state on first load of the slider component
    setSliderState(rangeType) {
        // If we are showing a true range slider
        if (rangeType === 'true' || rangeType === true) {
            // Set the left handle to show
            this.showLeftHandle = true;
            // Set the right handle to show
            this.showRightHandle = true;
            // Set the track to be 100% width to start since the range handles start at opposite ends of the slider
            this.trackWidth = 100;
            // Set the range type for reference
            this.rangeType = 'double';
            // Ensure that the current min value is set correctly
            this.handleMinValueChange(this.currentMinValue || this.min);
            // Ensure that the current max value is set correctly
            this.handleMaxValueChange(this.currentMaxValue || this.max);
            // Otherwise if the range type is 'max' (slider locked to max, slides toward left)
        }
        else if (rangeType === 'max') {
            // Set the left handle to hide
            this.showLeftHandle = false;
            // Set the right handle to show
            this.showRightHandle = true;
            // Set the track to be stuck to the left of the slider
            this.trackLeft = 0;
            // Set the range type for reference
            this.rangeType = 'single';
            // Ensure that the current value is set to the minimum value
            this.handleValueChange(this.currentValue || this.min);
            // Otherwise if the range type is 'min' (slider locked to min, slides toward right)
        }
        else if (rangeType === 'min') {
            // Set the left handle to show
            this.showLeftHandle = true;
            // Set the right handle to hide
            this.showRightHandle = false;
            // Set the track to be stuck to the right of the slider
            this.trackRight = 0;
            // Set the range type for reference
            this.rangeType = 'single';
            // Ensure that the current value is set to the minimum value
            this.handleValueChange(this.currentValue || this.max);
        }
    }
    // Calls all functions required to set up the slider
    setUpSlider(rangeType) {
        // Set the initial slider width
        this.setSliderWidth();
        // These updates need to wait for the DOM to be rendered, and thus are wrapped in a setTimeout to wait for the browser to render the DOM
        setTimeout(() => {
            this.setSliderState(rangeType);
        }, 0);
    }
    // #endregion - Setters
    // #region /// --- Handlers --- ///
    // Handles mouse down events on range slider handles
    handleSliderMousedown(event, handleType) {
        // Prevent browser behavior -- !! DO NOT REMOVE !!
        const handle = handleType === 'left'
            ? this.leftHandle.nativeElement
            : this.rightHandle.nativeElement;
        if (this.disabled)
            return;
        event.stopPropagation();
        // Let the component know what the current handle is
        this.currentHandle = handle;
        // Let everyone know the slider is being interacted with
        this.interacting = true;
        // Set the initial handle position so we can reference it as the mouse is moving
        const initialHandlePosition = event.target.offsetLeft;
        // Set the initial mouse position so we can use it as a reference as the mouse is moving
        const initialMouseX = event.type === 'touchstart'
            ? event.changedTouches[0].clientX
            : event.clientX;
        // Subscribe to mouse move observable until a mouseup is detected and use the handlePositionChange method to handle the move
        // Add the observable for the mouse move event on the document
        if (event.type === 'mousedown') {
            this.mouseMoveListener = fromEvent(this._document, 'mousemove');
        }
        else if (event.type === 'touchstart') {
            this.mouseMoveListener = fromEvent(this._document, 'touchmove');
        }
        this.mouseMoveListener.pipe(takeUntil(this.mouseUp)).subscribe(e2 => this.handlePositionChange({
            event: e2,
            handle: handle,
            initialHandlePosition: initialHandlePosition,
            initialMouseX: initialMouseX
        }));
    }
    // Handles anytime the dragging stuff should end
    handleDragEnd() {
        // Let the slider know the mouse was released
        this.mouseUp.next();
        // Release the interacting boolean
        this.interacting = false;
    }
    ////// root handlers above
    // Handles any change in the sliders value from the outside and updates the sliders position
    handleInputValue(options = {
        value: null,
        property: null,
        emitter: null,
        handleSide: null
    }) {
        // If the value is in range
        if (options.value >= this.min && options.value <= this.max) {
            // If this is a single handle slider
            if (this.rangeType === 'single') {
                // Set the new value
                this.setNewValue(options.value, options.property, options.emitter, options.handleSide);
                // Otherwise make sure there is no handle interferance to prevent false checks
            }
            else if (
            // Otherwise if this is a double handle slider and this is for the left handle
            options.handleSide === 'left' ||
                // Or this is for the right handle and the the value is above the min
                (options.handleSide === 'right' &&
                    options.value >= this.currentMinValue)) {
                // Set the new value
                this.setNewValue(options.value, options.property, options.emitter, options.handleSide);
            }
        }
    }
    // Handles mousemove events
    handleMouseMove(event, property, emitter, handleSide, initialMouseX, initialHandlePosition) {
        const clientX = event.type === 'touchmove'
            ? event.changedTouches[0].clientX
            : event.clientX;
        // Get the new position of the handle on the slider
        const newPosition = initialHandlePosition + (clientX - initialMouseX);
        // Get the percent of the value we should get
        const offset = (newPosition / this.sliderWidth) * 100;
        // Get the value using the previously calculated percent
        const value = this.getValueFromOffset(offset);
        // Set the new value on the slider
        if (handleSide === 'right') {
            if (this.currentMaxValue <= this.currentMinValue) {
                this.setNewValue(this.currentMinValue + this.step, property, emitter, handleSide);
            }
            else {
                this.setNewValue(value, property, emitter, handleSide);
            }
        }
        if (handleSide === 'left') {
            if (this.currentMinValue >= this.currentMaxValue) {
                this.setNewValue(this.currentMaxValue - this.step, property, emitter, handleSide);
            }
            else {
                this.setNewValue(value, property, emitter, handleSide);
            }
        }
    }
    // Handles all keydown events
    handleKeyDown(event, property, emitter, handleSide) {
        // Switch on the event's key code
        switch (event.which) {
            //Enter
            case 13:
                event.returnValue = false;
                event.preventDefault();
                event.stopPropagation();
                break;
            // Page Up
            case 33:
                // Prevent default browser actions
                event.preventDefault();
                event.stopPropagation();
                // Set the new value
                if ((this.currentMaxValue - this.currentMinValue >
                    (this.max - this.min) * 0.1 &&
                    handleSide === 'left' &&
                    this.rangeType === 'double') ||
                    (handleSide === 'right' && this.rangeType === 'double')) {
                    this.setNewValue(this[property] + (this.max - this.min) * 0.1, property, emitter, handleSide);
                }
                else if (this.rangeType === 'single') {
                    this.setNewValue(this[property] + (this.max - this.min) * 0.1, property, emitter, handleSide);
                }
                else {
                    this.announcer.announce('Range Slider Min Handle value ' +
                        this.currentMinValue +
                        ' will be equal to or greater than Max Handle Value ' +
                        this.currentMaxValue +
                        '. Adjust max handle Value to move min handle Value', 200);
                }
                break;
            // Page Down
            case 34:
                // Prevent default browser actions
                event.preventDefault();
                event.stopPropagation();
                // Set the new value
                if ((this.currentMaxValue - this.currentMinValue >
                    (this.max - this.min) * 0.1 &&
                    handleSide === 'right' &&
                    this.rangeType === 'double') ||
                    (handleSide === 'left' && this.rangeType === 'double')) {
                    this.setNewValue(this[property] - (this.max - this.min) * 0.1, property, emitter, handleSide);
                }
                else if (this.rangeType === 'single') {
                    this.setNewValue(this[property] - (this.max - this.min) * 0.1, property, emitter, handleSide);
                }
                else {
                    this.announcer.announce('Range Slider Max Handle value ' +
                        this.currentMaxValue +
                        ' will be equal to or less than Min Handle Value' +
                        this.currentMinValue +
                        '. Adjust min handle value to move max handle Value', 200);
                }
                break;
            // End Button
            case 35:
                // Prevent default browser actions
                event.preventDefault();
                event.stopPropagation();
                //Set the new value
                if (this.rangeType === 'double') {
                    if (handleSide === 'right') {
                        this.setNewValue(this.max, property, emitter, handleSide);
                    }
                    else {
                        this.setNewValue(this.currentMaxValue - this.step, property, emitter, handleSide);
                        this.announcer.announce('Currently Max Handle Value of range slider is ' +
                            this.currentMaxValue +
                            ' . So, Min Handle is adjusted to ' +
                            this.currentMinValue, 200);
                    }
                }
                else {
                    this.setNewValue(this.max, property, emitter, handleSide);
                }
                break;
            // Home button
            case 36:
                // Prevent default browser actions
                event.preventDefault();
                event.stopPropagation();
                //Set the new value
                if (this.rangeType === 'double') {
                    if (handleSide === 'left') {
                        this.setNewValue(this.min, property, emitter, handleSide);
                    }
                    else {
                        this.setNewValue(this.currentMinValue + this.step, property, emitter, handleSide);
                        this.announcer.announce('Currently Min Handle Value of range slider is ' +
                            this.currentMinValue +
                            ' . So, Max Handle is adjusted to ' +
                            this.currentMaxValue, 200);
                    }
                }
                else {
                    this.setNewValue(this.min, property, emitter, handleSide);
                }
                break;
            // Left/Down arrows
            case 37:
            case 40:
                // Prevent default browser actions
                event.preventDefault();
                event.stopPropagation();
                // Set the new value
                if ((this.currentMinValue < this.currentMaxValue - this.step &&
                    handleSide === 'right' &&
                    this.rangeType === 'double') ||
                    (handleSide === 'left' && this.rangeType === 'double')) {
                    this.setNewValue(this[property] - this.step, property, emitter, handleSide);
                }
                else if (this.rangeType === 'single') {
                    this.setNewValue(this[property] - this.step, property, emitter, handleSide);
                }
                else {
                    this.announcer.announce('Range Slider Max Handle value ' +
                        this.currentMaxValue +
                        ' will be equal to or less than Min Handle Value' +
                        this.currentMinValue +
                        '. Adjust min handle value to move max handle Value', 200);
                }
                break;
            // Up/Right arrows
            case 38:
            case 39:
                // Prevent default browser actions
                event.preventDefault();
                event.stopPropagation();
                // Set the new value
                if ((this.currentMinValue < this.currentMaxValue - this.step &&
                    handleSide === 'left' &&
                    this.rangeType === 'double') ||
                    (handleSide === 'right' && this.rangeType === 'double')) {
                    this.setNewValue(this[property] + this.step, property, emitter, handleSide);
                }
                else if (this.rangeType === 'single') {
                    this.setNewValue(this[property] + this.step, property, emitter, handleSide);
                }
                else {
                    this.announcer.announce('Range Slider Min Handle value ' +
                        this.currentMinValue +
                        ' will be equal to or greater than Max Handle Value ' +
                        this.currentMaxValue +
                        '. Adjust max handle Value to move min handle Value', 200);
                }
                break;
        }
    }
    // Handles events that cause the slider handle(s) to move
    handlePositionChange(options = {
        event: null,
        handle: null,
        position: null,
        initialHandlePosition: null,
        initialMouseX: null
    }) {
        if (this.disabled)
            return;
        // Determine the side of the handle
        const handleSide = options.handle.className.match('cds-left')
            ? 'left'
            : 'right';
        // Get the property we should change based on the slider state and handle side
        const property = this.rangeType === 'single'
            ? SliderEnum.currentValue
            : handleSide === 'left'
                ? SliderEnum.currentMinValue
                : SliderEnum.currentMaxValue;
        // Get the emitter we should use based on the slider state and handle side
        const emitter = this.rangeType === 'single'
            ? 'valueChange'
            : handleSide === 'left'
                ? 'minValueChange'
                : 'maxValueChange';
        // If this is a mousemove event
        if (options.event.type === 'mousemove' ||
            options.event.type === 'touchmove') {
            // Handle the mouse move
            this.handleMouseMove(options.event, property, emitter, handleSide, options.initialMouseX, options.initialHandlePosition);
            // Otherwise if this is a keydown event
        }
        else if (options.event.type === 'keydown') {
            // Handle the keydown event
            this.handleKeyDown(options.event, property, emitter, handleSide);
        }
    }
    // Handles value changes
    handleValueChange(value) {
        // Update the handle position
        this.handleInputValue({
            value: value,
            property: SliderEnum.currentValue,
            emitter: 'valueChange',
            handleSide: 'left'
        });
    }
    // Handles minimum value changes
    handleMinValueChange(value) {
        // Update the handle position
        this.handleInputValue({
            value: value,
            property: SliderEnum.currentMinValue,
            emitter: 'minValueChange',
            handleSide: 'left'
        });
    }
    // Handles maximum value changes
    handleMaxValueChange(value) {
        // Update the handle position
        this.handleInputValue({
            value: value,
            property: SliderEnum.currentMaxValue,
            emitter: 'maxValueChange',
            handleSide: 'right'
        });
    }
    // Handles when the slider is clicked
    handleSliderClick(event) {
        // Get the percent of the value we should get
        let offset = (event.offsetX / this.sliderWidth) * 100;
        // Get the direction we are going
        const direction = offset < this.getLeftHandleOffset() ? 'left' : 'right';
        // Account for direction when calculating offset
        offset = direction === 'left' ? Math.floor(offset) : Math.ceil(offset);
        // Get the value using the previously calculated percent
        const value = this.getValueFromOffset(offset);
        // Set the new value on the slider
        this.setNewValue(value, SliderEnum.currentValue, 'valueChange', 'left');
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSlider, deps: [{ token: i1$1.LiveAnnouncer }, { token: i0.ChangeDetectorRef }, { token: i0.ElementRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsSlider, selector: "cds-slider", inputs: { idStr: "idStr", name: "name", range: "range", size: "size", min: "min", max: "max", step: "step", minLabel: "minLabel", maxLabel: "maxLabel", labels: "labels", handleLabel: "handleLabel", minHandleLabel: "minHandleLabel", maxHandleLabel: "maxHandleLabel", unitOfValue: "unitOfValue", disabled: "disabled", value: "value", minValue: "minValue", maxValue: "maxValue" }, outputs: { valueChange: "valueChange", minValueChange: "minValueChange", maxValueChange: "maxValueChange", blur: "blur", focus: "focus" }, host: { listeners: { "focusin": "focusChanged(true)", "focusout": "focusChanged(false)", "document:mouseup": "onMouseUp($event)", "document:touchend": "onTouchEnd($event)", "window:resize": "onWindowResize($event)" }, properties: { "attr.id": "this.fieldId" }, classAttribute: "cds-slider" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsSlider)),
                multi: true
            },
            {
                provide: NG_VALIDATORS,
                useExisting: forwardRef((() => CdsSlider)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsSlider }
        ], viewQueries: [{ propertyName: "viewInput", first: true, predicate: ["viewInput"], descendants: true }, { propertyName: "slider", first: true, predicate: ["slider"], descendants: true }, { propertyName: "track", first: true, predicate: ["track"], descendants: true }, { propertyName: "leftHandle", first: true, predicate: ["leftHandle"], descendants: true, static: true }, { propertyName: "rightHandle", first: true, predicate: ["rightHandle"], descendants: true, static: true }, { propertyName: "line", first: true, predicate: ["line"], descendants: true, static: true }, { propertyName: "labelContainer", first: true, predicate: ["labelContainer"], descendants: true }, { propertyName: "hashTrack", first: true, predicate: ["hashTrack"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div
      #slider
      [attr.id]="idStr ? idStr : null"
      class="cds-slider {{ range }}"
      [ngClass]="disabled ? 'cds-slider-disabled' : null"
      [style.height]="size === 'large' ? '12px' : '4px'"
      [style.border-radius]="size === 'large' ? '12px' : '4px'"
    >
      <span
        #track
        class="cds-track"
        [style.height]="size === 'large' ? '12px' : '4px'"
        [style.left]="getTrackLeft() + '%'"
        [style.right]="trackRight + '%'"
        [style.width]="getTrackWidth() + '%'"
      ></span>
      <span #line class="cds-line"></span>
      <span
        #leftHandle
        class="cds-handle cds-left"
        [ngClass]="{
          'cds-handle cds-right': size === 'small',
          'cds-handle-large cds-right': size === 'large'
        }"
        [style.left]="getLeftHandleOffset() + '%'"
        [style.z-index]="currentHandle == (leftHandle ? '99' : 1)"
        [hidden]="!showLeftHandle"
        role="slider"
        aria-orientation="horizontal"
        [attr.aria-valuemax]="max"
        [attr.aria-valuemin]="min"
        [attr.aria-valuenow]="
          rangeType === 'single' ? currentValue : currentMinValue
        "
        [attr.aria-valuetext]="
          rangeType === 'single'
            ? currentValue + ' ' + unitOfValue + minOrMaxValReached(currentValue)
            : currentMinValue + ' ' + unitOfValue + minOrMaxValReached(currentMinValue)
        "
        [attr.aria-label]="
          rangeType === 'single' ? handleLabel : minHandleLabel
        "
        (focus)="focus.emit($event)"
        [attr.aria-disabled]="disabled"
        [attr.tabindex]="disabled ? null : '0'"
        (mousedown)="handleSliderMousedown($event, 'left')"
        (touchstart)="handleSliderMousedown($event, 'left')"
        (keydown)="keydownHandler($event, 'left')"
      >
      </span>
      <span
        #rightHandle
        class="cds-handle cds-right"
        [ngClass]="{
          'cds-handle cds-right': size === 'small',
          'cds-handle-large cds-right': size === 'large'
        }"
        [style.left]="getRightHandleOffset() + '%'"
        [style.z-index]="currentHandle == (rightHandle ? '99' : 1)"
        [hidden]="!showRightHandle"
        role="slider"
        [attr.aria-valuemax]="max"
        [attr.aria-valuemin]="min"
        [attr.aria-valuenow]="
          rangeType === 'single' ? currentValue : currentMinValue
        "
        [attr.aria-valuetext]="
          rangeType === 'single'
            ? currentValue + ' ' + unitOfValue + minOrMaxValReached(currentValue)
            : currentMaxValue + ' ' + unitOfValue + minOrMaxValReached(currentMaxValue)
        "
        [attr.aria-label]="
          rangeType === 'single' ? handleLabel : maxHandleLabel
        "
        (focus)="focus.emit($event)"
        [attr.aria-disabled]="disabled"
        [attr.tabindex]="disabled ? null : '0'"
        (mousedown)="handleSliderMousedown($event, 'right')"
        (touchstart)="handleSliderMousedown($event, 'right')"
        (keydown)="keydownHandler($event, 'right')"
      >
      </span>
      <input
        [attr.id]="idStr ? idStr + '_hidden' : null"
        [attr.name]="name"
        [(ngModel)]="_value"
        hidden
      />
    </div>
  `, isInline: true, styles: [".cds-slider{height:10px;background-color:#ccc;border-radius:5px;display:flex;align-items:center;position:relative}.cds-slider:before,.cds-slider:after{width:4px;height:10px;position:absolute;top:-6px}.cds-slider:before{left:0}.cds-slider:after{right:0}.cds-slider .cds-line{display:block;width:100%;height:20px}.cds-slider .cds-track{height:10px;width:0;display:block;pointer-events:none;position:absolute;top:0;border-radius:5px}.cds-slider .cds-handle-large{width:0;border:none;padding:0;position:absolute;z-index:99}.cds-slider .cds-handle-large:focus:after{border-radius:25px;backface-visibility:hidden}.cds-slider .cds-handle-large:after{content:\"\";width:32px;height:32px;border-radius:16px;display:block;cursor:pointer;position:absolute;top:-16px;left:-16px;background-repeat:no-repeat}.cds-slider .cds-handle-large.right{right:0}.cds-slider .cds-handle-large.left{left:0}.cds-slider .cds-handle-large.hidden{display:none}.cds-slider .cds-handle{width:0;border:none;padding:0;position:absolute;z-index:99}.cds-slider .cds-handle:focus:after{border-radius:25px;backface-visibility:hidden}.cds-slider .cds-handle:after{content:\"\";width:16px;height:16px;border-radius:16px;display:block;cursor:pointer;position:absolute;top:-8px;left:-8px;background-repeat:no-repeat}.cds-slider .cds-handle.right{right:0}.cds-slider .cds-handle.left{left:0}.cds-slider .cds-handle.hidden{display:none}.cds-slider .cds-hashTrack{display:flex;justify-content:space-between;position:absolute;left:0;top:0;width:100%}.cds-slider .cds-hashTrack .cds-hash{display:inline-block;height:12px;position:relative}.cds-slider .cds-hashTrack .cds-hash:after{content:\"\";display:block;width:1px;background-color:#999;height:100%;position:absolute;right:0}.cds-slider .cds-hashTrack .cds-hash.highlighted:after{background-color:#056dae}.cds-slider .cds-hashTrack .cds-hash:last-child:after{display:none}.cds-labels{display:flex;justify-content:space-between;margin-top:20px}.cds-value-labels{display:flex;justify-content:space-between;margin-top:15px;text-align:center}.cds-value-labels .cds-value-label{width:0;overflow:visible;white-space:nowrap;font-size:12px}.cds-value-labels .cds-value-label span{position:absolute;transform:translate(-50%)}.cds-value-labels .cds-value-label:first-child span{transform:none}.cds-value-labels .cds-value-label:last-child span{transform:translate(-100%)}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1$3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSlider, decorators: [{
            type: Component,
            args: [{ selector: 'cds-slider', host: {
                        class: 'cds-slider',
                        '(focusin)': 'focusChanged(true)',
                        '(focusout)': 'focusChanged(false)'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsSlider)),
                            multi: true
                        },
                        {
                            provide: NG_VALIDATORS,
                            useExisting: forwardRef((() => CdsSlider)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsSlider }
                    ], template: `
    <div
      #slider
      [attr.id]="idStr ? idStr : null"
      class="cds-slider {{ range }}"
      [ngClass]="disabled ? 'cds-slider-disabled' : null"
      [style.height]="size === 'large' ? '12px' : '4px'"
      [style.border-radius]="size === 'large' ? '12px' : '4px'"
    >
      <span
        #track
        class="cds-track"
        [style.height]="size === 'large' ? '12px' : '4px'"
        [style.left]="getTrackLeft() + '%'"
        [style.right]="trackRight + '%'"
        [style.width]="getTrackWidth() + '%'"
      ></span>
      <span #line class="cds-line"></span>
      <span
        #leftHandle
        class="cds-handle cds-left"
        [ngClass]="{
          'cds-handle cds-right': size === 'small',
          'cds-handle-large cds-right': size === 'large'
        }"
        [style.left]="getLeftHandleOffset() + '%'"
        [style.z-index]="currentHandle == (leftHandle ? '99' : 1)"
        [hidden]="!showLeftHandle"
        role="slider"
        aria-orientation="horizontal"
        [attr.aria-valuemax]="max"
        [attr.aria-valuemin]="min"
        [attr.aria-valuenow]="
          rangeType === 'single' ? currentValue : currentMinValue
        "
        [attr.aria-valuetext]="
          rangeType === 'single'
            ? currentValue + ' ' + unitOfValue + minOrMaxValReached(currentValue)
            : currentMinValue + ' ' + unitOfValue + minOrMaxValReached(currentMinValue)
        "
        [attr.aria-label]="
          rangeType === 'single' ? handleLabel : minHandleLabel
        "
        (focus)="focus.emit($event)"
        [attr.aria-disabled]="disabled"
        [attr.tabindex]="disabled ? null : '0'"
        (mousedown)="handleSliderMousedown($event, 'left')"
        (touchstart)="handleSliderMousedown($event, 'left')"
        (keydown)="keydownHandler($event, 'left')"
      >
      </span>
      <span
        #rightHandle
        class="cds-handle cds-right"
        [ngClass]="{
          'cds-handle cds-right': size === 'small',
          'cds-handle-large cds-right': size === 'large'
        }"
        [style.left]="getRightHandleOffset() + '%'"
        [style.z-index]="currentHandle == (rightHandle ? '99' : 1)"
        [hidden]="!showRightHandle"
        role="slider"
        [attr.aria-valuemax]="max"
        [attr.aria-valuemin]="min"
        [attr.aria-valuenow]="
          rangeType === 'single' ? currentValue : currentMinValue
        "
        [attr.aria-valuetext]="
          rangeType === 'single'
            ? currentValue + ' ' + unitOfValue + minOrMaxValReached(currentValue)
            : currentMaxValue + ' ' + unitOfValue + minOrMaxValReached(currentMaxValue)
        "
        [attr.aria-label]="
          rangeType === 'single' ? handleLabel : maxHandleLabel
        "
        (focus)="focus.emit($event)"
        [attr.aria-disabled]="disabled"
        [attr.tabindex]="disabled ? null : '0'"
        (mousedown)="handleSliderMousedown($event, 'right')"
        (touchstart)="handleSliderMousedown($event, 'right')"
        (keydown)="keydownHandler($event, 'right')"
      >
      </span>
      <input
        [attr.id]="idStr ? idStr + '_hidden' : null"
        [attr.name]="name"
        [(ngModel)]="_value"
        hidden
      />
    </div>
  `, styles: [".cds-slider{height:10px;background-color:#ccc;border-radius:5px;display:flex;align-items:center;position:relative}.cds-slider:before,.cds-slider:after{width:4px;height:10px;position:absolute;top:-6px}.cds-slider:before{left:0}.cds-slider:after{right:0}.cds-slider .cds-line{display:block;width:100%;height:20px}.cds-slider .cds-track{height:10px;width:0;display:block;pointer-events:none;position:absolute;top:0;border-radius:5px}.cds-slider .cds-handle-large{width:0;border:none;padding:0;position:absolute;z-index:99}.cds-slider .cds-handle-large:focus:after{border-radius:25px;backface-visibility:hidden}.cds-slider .cds-handle-large:after{content:\"\";width:32px;height:32px;border-radius:16px;display:block;cursor:pointer;position:absolute;top:-16px;left:-16px;background-repeat:no-repeat}.cds-slider .cds-handle-large.right{right:0}.cds-slider .cds-handle-large.left{left:0}.cds-slider .cds-handle-large.hidden{display:none}.cds-slider .cds-handle{width:0;border:none;padding:0;position:absolute;z-index:99}.cds-slider .cds-handle:focus:after{border-radius:25px;backface-visibility:hidden}.cds-slider .cds-handle:after{content:\"\";width:16px;height:16px;border-radius:16px;display:block;cursor:pointer;position:absolute;top:-8px;left:-8px;background-repeat:no-repeat}.cds-slider .cds-handle.right{right:0}.cds-slider .cds-handle.left{left:0}.cds-slider .cds-handle.hidden{display:none}.cds-slider .cds-hashTrack{display:flex;justify-content:space-between;position:absolute;left:0;top:0;width:100%}.cds-slider .cds-hashTrack .cds-hash{display:inline-block;height:12px;position:relative}.cds-slider .cds-hashTrack .cds-hash:after{content:\"\";display:block;width:1px;background-color:#999;height:100%;position:absolute;right:0}.cds-slider .cds-hashTrack .cds-hash.highlighted:after{background-color:#056dae}.cds-slider .cds-hashTrack .cds-hash:last-child:after{display:none}.cds-labels{display:flex;justify-content:space-between;margin-top:20px}.cds-value-labels{display:flex;justify-content:space-between;margin-top:15px;text-align:center}.cds-value-labels .cds-value-label{width:0;overflow:visible;white-space:nowrap;font-size:12px}.cds-value-labels .cds-value-label span{position:absolute;transform:translate(-50%)}.cds-value-labels .cds-value-label:first-child span{transform:none}.cds-value-labels .cds-value-label:last-child span{transform:translate(-100%)}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i0.ChangeDetectorRef }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], viewInput: [{
                type: ViewChild,
                args: ['viewInput', { static: false }]
            }], slider: [{
                type: ViewChild,
                args: ['slider', { static: false }]
            }], track: [{
                type: ViewChild,
                args: ['track', { static: false }]
            }], leftHandle: [{
                type: ViewChild,
                args: ['leftHandle', { static: true }]
            }], rightHandle: [{
                type: ViewChild,
                args: ['rightHandle', { static: true }]
            }], line: [{
                type: ViewChild,
                args: ['line', { static: true }]
            }], labelContainer: [{
                type: ViewChild,
                args: ['labelContainer', { static: false }]
            }], hashTrack: [{
                type: ViewChild,
                args: ['hashTrack', { static: false }]
            }], idStr: [{
                type: Input
            }], name: [{
                type: Input
            }], range: [{
                type: Input
            }], size: [{
                type: Input
            }], min: [{
                type: Input
            }], max: [{
                type: Input
            }], step: [{
                type: Input
            }], minLabel: [{
                type: Input
            }], maxLabel: [{
                type: Input
            }], labels: [{
                type: Input
            }], handleLabel: [{
                type: Input
            }], minHandleLabel: [{
                type: Input
            }], maxHandleLabel: [{
                type: Input
            }], unitOfValue: [{
                type: Input
            }], disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], minValue: [{
                type: Input
            }], minValueChange: [{
                type: Output
            }], maxValue: [{
                type: Input
            }], maxValueChange: [{
                type: Output
            }], blur: [{
                type: Output
            }], focus: [{
                type: Output
            }], onMouseUp: [{
                type: HostListener,
                args: ['document:mouseup', ['$event']]
            }], onTouchEnd: [{
                type: HostListener,
                args: ['document:touchend', ['$event']]
            }], onWindowResize: [{
                type: HostListener,
                args: ['window:resize', ['$event']]
            }] } });

const COMPONENTS$w = [CdsSlider];
const MODULES$g = [CommonModule, FormsModule];
class CdsSliderModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSliderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsSliderModule, declarations: [CdsSlider], imports: [CommonModule, FormsModule], exports: [CdsSlider] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSliderModule, imports: [MODULES$g] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSliderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$g],
                    declarations: [...COMPONENTS$w],
                    exports: [...COMPONENTS$w]
                }]
        }] });

/** Change event object emitted by CdsCheckbox  */
class CdsCheckboxChange {
    constructor(
    /** A reference to the CdsCheckbox itself */
    source, 
    /** The string value assigned to the CdsCheckbox. */
    value, 
    /** whether the new state is checked or not */
    checked) {
        this.source = source;
        this.value = value;
        this.checked = checked;
    }
}

const CHECKBOX_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsCheckbox),
    multi: true
};
let nextUniqueId$9 = 0;
let nextUniqueName$3 = 0;
class CdsCheckbox {
    /**
     * @description the label of the checkbox button
     * @note should be placed beside checkbox and read by nvda
     */
    set checked(checked) {
        onStable(this.ngZone, () => (this._checked = coerceBooleanProperty(checked)));
    }
    get checked() {
        return this._checked;
    }
    /**
     * @description name of the individual checkbox to group certain inputs.
     * @note sets an auto increment name by deafult if none is provided
     */
    set name(name) {
        this._name = name || this._name || this._uname;
    }
    get name() {
        return this._name;
    }
    /**
     * @description id assigned to the native input element
     * @note if an id is not provided, an auto-increment id is applied
     */
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * @description whether the checkbox should be disabled
     */
    set disabled(disabled) {
        this._disabled = coerceBooleanProperty(disabled);
    }
    get disabled() {
        return this._disabled;
    }
    constructor(ngZone, input, id, chxed) {
        this.ngZone = ngZone;
        this.input = input;
        this.id = id;
        this.chxed = chxed;
        /**
         * @description refernce to the navive input element for consumers
         * @note We should have all for components expose their native form
         * element (or focusable element) using the "elementRef" property consistently
         */
        this.fieldType = 'checkbox';
        /**
         * Implemented as part of CdsFormBase.
         */
        this.focusChange = new Subject();
        /**
         * Implemented as part of CdsFormBase.
         */
        this.hasValueChange = new Subject();
        /**
         * Implemented as part of CdsFormBase.
         */
        this.disabledChange = new Subject();
        this._checked = false;
        this.checkedChange = new EventEmitter();
        this._uname = `cds-checkbox-${nextUniqueName$3++}`;
        this._uid = `cds-checkbox-${nextUniqueId$9++}`;
        /**
         * @description Emitted when the input loses focus
         */
        this.blur = new EventEmitter();
        /**
         * @description Emitted when the input gains focus
         */
        this.focus = new EventEmitter();
        /**
         * @description Emitted when change is fired on the <input>.
         * Primarily used to pass the value up the the group, but also and easy way for
         * consumers to get a referece to the selected component (and element)
         */
        this.change = new EventEmitter();
        // #region // ----------------------- Form Binding Methods ----------------------- //
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Checkbox <citi-Checkbox> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
        this.fieldId = this.id || `cds-checkbox-${nextUniqueId$9++}`;
        setTimeout(() => this.checkValue());
        // set the default values if not provided
        if (chxed)
            this._checked = coerceBooleanProperty(this.chxed);
        this.idStr = this.idStr;
        /**
         * @description applies the provided name or an autoincrement one
         */
        this.name = this.name;
    }
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (this.input.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /**
     * @description Triggered when the checkbox button received a click or the
     * input recognized any change.  Clicking on a label element, will trigger
     * a change event on the associated input.
     */
    onCheckboxChange(event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        this._checked = !this.checked;
        // tell the FormControl to consider the control as 'touched'
        this.onTouched();
        this.change.emit(new CdsCheckboxChange(this, this.value, this._checked));
        this.writeValue(this._checked);
    }
    /**
     * @description Allows Angular to update the model. The form will pass new values in here
     */
    writeValue(checked) {
        /**
         * @note must be in the order of
         * 1) onChange()
         * 2) checkedChange
         */
        this.onChange(checked);
        this.checkedChange.emit(checked);
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox, deps: [{ token: i0.NgZone }, { token: i0.ElementRef }, { token: 'id', attribute: true }, { token: 'checked', attribute: true }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCheckbox, selector: "cds-checkbox", inputs: { value: "value", ariaRequired: "ariaRequired", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", label: "label", checked: "checked", name: "name", idStr: "idStr", disabled: "disabled" }, outputs: { checkedChange: "checkedChange", blur: "blur", focus: "focus", change: "change" }, host: { listeners: { "focusin": "focusChanged(true)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId", "attr.role": "this.role" }, classAttribute: "cds-checkbox" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCheckbox)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsCheckbox }
        ], viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["elementRef"], descendants: true }], ngImport: i0, template: `
  
    <input
      #elementRef
      class="cds-checkbox-input"
      type="checkbox"
      [ngClass]="{ 'cds-checkbox-error': isInErrorState }"
      [attr.id]="idStr"
      [value]="value"
      [attr.name]="name"
      [checked]="checked"
      [attr.aria-required]="ariaRequired"
      [disabled]="disabled"
      [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby +' '+idStr + '-label' : idStr + '-label'"
      [attr.aria-describedby]="ariaDescribedby"
      (blur)="blur.emit($event)"
      (focus)="focus.emit($event)"
      (change)="onCheckboxChange($event)"
    />
    <label
      class="cds-checkbox-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
      [innerHTML]="label"
    >
    </label>

    <!-- This is the projected content when the checkbox button is checked -->
    <!-- This will only toggle properly when used with the checkbox group -->
    <ng-content *ngIf="checked" select="[cdsCheckboxAddition]"></ng-content>
    
  `, isInline: true, styles: [":host{display:flex}.cds-checkbox-label:before{content:\"\";position:relative;top:7px;display:inline-block;width:24px;height:24px;margin-right:16px;border-radius:4px;background-color:transparent;margin-left:-13px;border:2px solid var(--cds-cb-border-color, #056dae)}.cds-checkbox-input{vertical-align:middle;margin-top:1px;display:inline-block;opacity:.01}.cds-checkbox-input:hover:enabled+.cds-checkbox-label:before{border-color:var(--cds-cb-hover-border-color, #034c81)}.cds-checkbox-input:focus-visible+.cds-checkbox-label{color:var(--cds-cb-focus-label-color)}.cds-checkbox-input:focus-visible+.cds-checkbox-label:before{border-radius:6px;backface-visibility:hidden;border-color:var(--cds-cb-focus-border-color);box-shadow:0 0 0 1px #fff,0 0 0 2px var(--cds-cb-focus-outline-color)}.cds-checkbox-input:focus-visible:checked+.cds-checkbox-label:before{box-shadow:0 0 0 1px #fff,0 0 0 2px var(--cds-cb-checked-focus-outline-color)}.cds-checkbox-input:checked+.cds-checkbox-label:before{background-repeat:no-repeat;background-position:center}.cds-checkbox-input:checked:enabled+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-inline-img, none)}.cds-checkbox-input:checked:enabled:hover+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-hover-inline-img, none)}.cds-checkbox-input.cds-checkbox-error:enabled+.cds-checkbox-label:before{border:2px solid var(--cds-cb-error-border-color)}.cds-checkbox-input.cds-checkbox-error:enabled:checked+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-error-inline-img, none)}.cds-checkbox-input:disabled+.cds-checkbox-label{color:var(--cds-cb-disabled-label-color, #bbb)}.cds-checkbox-input:disabled+.cds-checkbox-label:before{border-color:var(--cds-cb-disabled-border-color, #bbb)}.cds-checkbox-input:disabled:checked+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-disabled-inline-img, none)}[role=list]{margin:0;padding:0}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox', host: {
                        class: 'cds-checkbox',
                        '(focusin)': 'focusChanged(true)',
                        '(input)': 'checkValue()'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCheckbox)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsCheckbox }
                    ], template: `
  
    <input
      #elementRef
      class="cds-checkbox-input"
      type="checkbox"
      [ngClass]="{ 'cds-checkbox-error': isInErrorState }"
      [attr.id]="idStr"
      [value]="value"
      [attr.name]="name"
      [checked]="checked"
      [attr.aria-required]="ariaRequired"
      [disabled]="disabled"
      [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby +' '+idStr + '-label' : idStr + '-label'"
      [attr.aria-describedby]="ariaDescribedby"
      (blur)="blur.emit($event)"
      (focus)="focus.emit($event)"
      (change)="onCheckboxChange($event)"
    />
    <label
      class="cds-checkbox-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
      [innerHTML]="label"
    >
    </label>

    <!-- This is the projected content when the checkbox button is checked -->
    <!-- This will only toggle properly when used with the checkbox group -->
    <ng-content *ngIf="checked" select="[cdsCheckboxAddition]"></ng-content>
    
  `, styles: [":host{display:flex}.cds-checkbox-label:before{content:\"\";position:relative;top:7px;display:inline-block;width:24px;height:24px;margin-right:16px;border-radius:4px;background-color:transparent;margin-left:-13px;border:2px solid var(--cds-cb-border-color, #056dae)}.cds-checkbox-input{vertical-align:middle;margin-top:1px;display:inline-block;opacity:.01}.cds-checkbox-input:hover:enabled+.cds-checkbox-label:before{border-color:var(--cds-cb-hover-border-color, #034c81)}.cds-checkbox-input:focus-visible+.cds-checkbox-label{color:var(--cds-cb-focus-label-color)}.cds-checkbox-input:focus-visible+.cds-checkbox-label:before{border-radius:6px;backface-visibility:hidden;border-color:var(--cds-cb-focus-border-color);box-shadow:0 0 0 1px #fff,0 0 0 2px var(--cds-cb-focus-outline-color)}.cds-checkbox-input:focus-visible:checked+.cds-checkbox-label:before{box-shadow:0 0 0 1px #fff,0 0 0 2px var(--cds-cb-checked-focus-outline-color)}.cds-checkbox-input:checked+.cds-checkbox-label:before{background-repeat:no-repeat;background-position:center}.cds-checkbox-input:checked:enabled+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-inline-img, none)}.cds-checkbox-input:checked:enabled:hover+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-hover-inline-img, none)}.cds-checkbox-input.cds-checkbox-error:enabled+.cds-checkbox-label:before{border:2px solid var(--cds-cb-error-border-color)}.cds-checkbox-input.cds-checkbox-error:enabled:checked+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-error-inline-img, none)}.cds-checkbox-input:disabled+.cds-checkbox-label{color:var(--cds-cb-disabled-label-color, #bbb)}.cds-checkbox-input:disabled+.cds-checkbox-label:before{border-color:var(--cds-cb-disabled-border-color, #bbb)}.cds-checkbox-input:disabled:checked+.cds-checkbox-label:before{background-image:var(--cds-cb-checked-disabled-inline-img, none)}[role=list]{margin:0;padding:0}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['checked']
                }] }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], elementRef: [{
                type: ViewChild,
                args: ['elementRef']
            }], value: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], label: [{
                type: Input
            }], checked: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }], name: [{
                type: Input
            }], idStr: [{
                type: Input
            }], disabled: [{
                type: Input
            }], blur: [{
                type: Output
            }], focus: [{
                type: Output
            }], change: [{
                type: Output
            }] } });

let nextUniqueName$2 = 0;
/**
 * Provider Expression that allows checkbox-group-new to register as a ControlValueAccessor
 */
const CHECKBOX_GROUP_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsCheckboxGroup),
    multi: true
};
let nextUniqueId$8 = 0;
class CdsCheckboxGroup {
    /**
     * @description disables the entire group
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.updateDisabled();
    }
    /**
     * @description two-way value binding mechanism
     */
    get value() {
        return this._value || [];
    }
    set value(value) {
        if (!isArray(value))
            devKill('The binding value of a checkbox group is a string array');
        if (isEqual(this.value, value))
            return;
        isEqual(this.value, value) ? (this._value = value) : (this._value = []);
    }
    /**
     * @description gets the currently selected CdsCheckbox component
     */
    get checkedCdsCheckbox() {
        if (!this._checkboxes)
            return null;
        return this._checkboxes.find(checkbox => checkbox.checked);
    }
    constructor(id, input, fcn) {
        this.id = id;
        this.input = input;
        this.fcn = fcn;
        this.unsub$ = new Subject();
        /**
         * @description flex alignment of checkboxes in the group
         */
        this._uid = `cds-checkbox-${nextUniqueId$8++}`;
        /**
         * @description flex alignment of checkboxes in the group
         */
        this.fieldType = 'checkbox-group';
        /**
        * Implemented as part of CdsFormBase.
        */
        this.focusChange = new Subject();
        /**
         * Implemented as part of CdsFormBase.
         */
        this.hasValueChange = new Subject();
        /**
         * Implemented as part of CdsFormBase.
         */
        this.disabledChange = new Subject();
        /**
         * Implemented as part of CdsFormBase.
         */
        // @HostBinding('style.flex-direction')
        this.alignment = 'column';
        /**
         * @description whether or not to trigger the error ribbon
         * @note this may be deprecated based on cds designs
         */
        this.error = false;
        /**
         * @description name to be shared between all checkboxes
         * @note checkboxes must share a name to be recognized as a group
         */
        this.legendText = '';
        /** checkbox name property causes ADA Issues dealing with more than one checkbox sharing a name */
        /*@Input()
        get name() {
          return this._name;
        }
        set name(name: string) {
          this._name = name || this._name || this._uname;
          // this.updateCheckboxButtonNames();
        }
        private _name: string; */
        this._uname = `cds-checkbox-group-${nextUniqueName$2++}`;
        this._disabled = false;
        this._value = [];
        this.valueChange = new EventEmitter();
        // #region // ----------------------- Form Binding Methods ----------------------- //
        /** The method to be called in order to update ngModel */
        this.onChange = (value) => { };
        /** onTouch function registered via registerOnTouch (ControlValueAccessor) */
        this.onTouched = () => { };
        this.fieldId = this.id || `cds-checkbox-group-${nextUniqueId$8++}`;
    }
    /** Child CHECKBOX buttons. */
    ngAfterContentInit() {
        // this.disabled = this._disabled;
        // update child checkboxes to have role of listitem
        this.updateCheckboxRoles();
        // this.updateCheckboxButtonNames();
        this.selectFromSetter(this.value);
        this.listenForChanges();
        /**
         * @description applies name in the following order
         * 1) provided by user (set during 1st ngOnChanges)
         * 2) key for the formControlName
         * 3) auto-increments (for property binding only)
         *
         */
        // this.name = this._name || this.fcn || this.name;
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    selectFromSetter(value) {
        if (!value)
            value = [];
        if (this._checkboxes)
            this._checkboxes
                .filter(checkbox => value.includes(checkbox.value))
                .forEach(checkbox => (checkbox.checked = true));
    }
    clear() {
        this.deselectCurrent();
    }
    listenForChanges() {
        this._checkboxes.forEach(checkbox => {
            checkbox.change
                .pipe(tap(change => {
                this.maintainFocus(change);
                change.checked
                    ? this.selectFromCheckbox(change)
                    : this.deSelectFromCheckbox(change);
            }), tap(() => this.onTouched()), takeUntil(this.unsub$))
                .subscribe();
        });
    }
    //maintains the focus on same checkbox if keyboard has been used to check or uncheck.
    maintainFocus(getChange) {
        let elRef = getChange.source.elementRef.nativeElement;
        elRef.focus();
    }
    // Pushes the checked checkbox value into an value array and emits updated value.
    selectFromCheckbox(checkbox) {
        let val = [];
        if (this._value) {
            val = this._value;
            this._value.push(checkbox.value);
        }
        this.valueChange.emit(val);
        this.writeValue(val);
    }
    deselectCurrent() {
        if (this.checkedCdsCheckbox)
            this.checkedCdsCheckbox.checked = false;
    }
    // Pops the unchecked checkbox from value array and emits updated value.
    deSelectFromCheckbox(checkbox) {
        this._value = this.value.filter(str => str !== checkbox.value);
        this.valueChange.emit(this._value);
        this.writeValue(this._value);
    }
    // updates checkbox children to have a role of listitem
    // this needs to be done here so that checkboxes can be used without a checkbox group..
    // but when used with a checkbox group they will hava role of "listitem" (ADA requirement)
    updateCheckboxRoles() {
        if (this._checkboxes) {
            this._checkboxes.forEach(checkbox => {
                checkbox.role = 'listitem';
            });
        }
    }
    // Updates name property to all checkboxes.
    /*   private updateCheckboxButtonNames(): void {
        if (this._checkboxes) {
          this._checkboxes.forEach(checkbox => {
            checkbox.name = this.name;
          });
        }
      } */
    // Updates disabled property to all checkboxes.
    updateDisabled() {
        if (this._checkboxes) {
            this._checkboxes.forEach(checkbox => {
                checkbox.disabled = this._disabled;
            });
        }
    }
    /**
     * @description Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(value) {
        this._value = value;
        this.onChange(value);
    }
    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     * @param isDisabled Whether the control should be disabled.
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (this.input.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxGroup, deps: [{ token: 'id', attribute: true }, { token: i0.ElementRef }, { token: 'formControlName', attribute: true, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCheckboxGroup, selector: "cds-checkbox-group", inputs: { alignment: "alignment", error: "error", legendText: "legendText", ariaRequired: "ariaRequired", disabled: "disabled", value: "value" }, outputs: { valueChange: "valueChange" }, host: { properties: { "attr.id": "this.fieldId", "class.cds-checkbox-group-error": "this.error" }, classAttribute: "cds-checkbox-group" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCheckboxGroup)),
                multi: true
            }
        ], queries: [{ propertyName: "_checkboxes", predicate: CdsCheckbox }], viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["elementRef"], descendants: true }], ngImport: i0, template: `
  <legend class="legend-text" role="heading" aria-level="2"> 
  <span  [innerHTML]="legendText"> 
  </span> 
  </legend>
    <div 

      class="checkbox-wrapper"
      [style.flex-direction]="alignment"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, styles: [":host{display:flex;flex-direction:column}:host.cds-checkbox-group-error{padding-left:12px;border-left:2px solid var(--cds-cbg-error-border-color, #d60000)}.legend-text{color:var(--cds-cbg-legend-text-color)}.checkbox-wrapper{display:flex;flex-wrap:wrap}:host.cds-checkbox-group ::ng-deep .cds-checkbox-input.cds-checkbox-error+label:before{border-color:var(--cds-cbg-input-error-border-color)}:host.cds-checkbox-group ::ng-deep .cds-checkbox-input.cds-checkbox-error:checked+label:before{border-color:var(--cds-cbg-input-checked-error-border-color)}:host.cds-checkbox-group ::ng-deep .cds-checkbox-input.cds-checkbox-error:disabled+label:before{border-color:var(--cds-cbg-input-disabled-error-border-color)}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxGroup, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox-group', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCheckboxGroup)),
                            multi: true
                        }
                    ], host: {
                        class: 'cds-checkbox-group',
                    }, template: `
  <legend class="legend-text" role="heading" aria-level="2"> 
  <span  [innerHTML]="legendText"> 
  </span> 
  </legend>
    <div 

      class="checkbox-wrapper"
      [style.flex-direction]="alignment"
    >
      <ng-content></ng-content>
    </div>
  `, styles: [":host{display:flex;flex-direction:column}:host.cds-checkbox-group-error{padding-left:12px;border-left:2px solid var(--cds-cbg-error-border-color, #d60000)}.legend-text{color:var(--cds-cbg-legend-text-color)}.checkbox-wrapper{display:flex;flex-wrap:wrap}:host.cds-checkbox-group ::ng-deep .cds-checkbox-input.cds-checkbox-error+label:before{border-color:var(--cds-cbg-input-error-border-color)}:host.cds-checkbox-group ::ng-deep .cds-checkbox-input.cds-checkbox-error:checked+label:before{border-color:var(--cds-cbg-input-checked-error-border-color)}:host.cds-checkbox-group ::ng-deep .cds-checkbox-input.cds-checkbox-error:disabled+label:before{border-color:var(--cds-cbg-input-disabled-error-border-color)}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Attribute,
                    args: ['formControlName']
                }] }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], elementRef: [{
                type: ViewChild,
                args: ['elementRef']
            }], alignment: [{
                type: Input
            }], error: [{
                type: HostBinding,
                args: ['class.cds-checkbox-group-error']
            }, {
                type: Input
            }], legendText: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], disabled: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], _checkboxes: [{
                type: ContentChildren,
                args: [CdsCheckbox]
            }] } });

const COMPONENTS$v = [CdsCheckbox, CdsCheckboxGroup];
const MODULES$f = [CommonModule];
class CdsCheckboxModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxModule, declarations: [CdsCheckbox, CdsCheckboxGroup], imports: [CommonModule], exports: [CdsCheckbox, CdsCheckboxGroup] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxModule, imports: [MODULES$f] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$f],
                    declarations: [...COMPONENTS$v],
                    exports: [...COMPONENTS$v]
                }]
        }] });

/** Change event object emitted by CdsCheckbox  */
class CdsCheckbox2Change {
    constructor(
    /** A reference to the CdsCheckbox itself */
    source, 
    /** The string value assigned to the CdsCheckbox. */
    value, 
    /** whether the new state is checked or not */
    checked) {
        this.source = source;
        this.value = value;
        this.checked = checked;
    }
}

const CHECKBOX2_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsCheckbox2),
    multi: true
};
let nextUniqueId$7 = 0;
let nextUniqueName$1 = 0;
class CdsCheckbox2 {
    // #endregion
    // #region =========== HostBinding ========== //
    get centerClass() { return this.center; }
    get bottomClass() { return this.bottom; }
    /**
     * @description Whether this checkbox button is checked
     * @note only 1 checkbox can be checked, therefore only the last will reamain so
     */
    set checked(checked) {
        onStable(this.ngZone, () => {
            this._checked = coerceBooleanProperty(checked);
            this._cdr.detectChanges();
        });
    }
    get checked() {
        return this._checked;
    }
    /**
     * @description name of the individual checkbox to group certain inputs.
     * @note sets an auto increment name by deafult if none is provided
     */
    set name(name) {
        this._name = name || this._name || this._uname;
    }
    get name() {
        return this._name;
    }
    /**
     * @description id assigned to the native input element
     * @note if an id is not provided, an auto-increment id is applied
     */
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * @description whether the checkbox should be disabled
     */
    set disabled(disabled) {
        this._disabled = coerceBooleanProperty(disabled);
    }
    get disabled() {
        return this._disabled;
    }
    // #endregion
    // #region =========== Constructor / Life-Cycle Hooks ========== //
    constructor(ngZone, _announcer, input, id, chxed, _cdr) {
        this.ngZone = ngZone;
        this._announcer = _announcer;
        this.input = input;
        this.id = id;
        this.chxed = chxed;
        this._cdr = _cdr;
        // #region =========== CdsFormBase ========== //
        this.fieldType = 'checkbox';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        this.isExpanded = false;
        this._checked = false;
        /**
         * Two way binding for the checked property
         */
        this.checkedChange = new EventEmitter();
        this._uname = `cds-checkbox2-${nextUniqueName$1++}`;
        this._uid = `cds-checkbox2-${nextUniqueId$7++}`;
        /**
         * @description Emitted when the input loses focus
         */
        this.blur = new EventEmitter();
        /**
         * @description Emitted when the input loses focus
         */
        this.onShiftTab = new EventEmitter();
        /**
         * @description Emitted when the input loses focus
         */
        this.onTab = new EventEmitter();
        /**
         * @description Emitted when the input gains focus
         */
        this.focus = new EventEmitter();
        /**
         * @description Emitted when change is fired on the <input>.
         * Primarily used to pass the value up the the group, but also and easy way for
         * consumers to get a referece to the selected component (and element)
         */
        this.change = new EventEmitter();
        /**
         * emits when change comes from outside of component
         * helps notify checkbox2-group of change
         */
        this.writeValueChange = new EventEmitter();
        // #endregion
        // #region =========== ControlValueAccessor Methods ========== //
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
        this.fieldId = this.id || `cds-checkbox2-${nextUniqueId$7++}`;
        setTimeout(() => this.checkValue());
        // set the default values if not provided
        if (chxed)
            this._checked = coerceBooleanProperty(this.chxed);
        this.idStr = this.idStr;
        // applies the provided name or an autoincrement one
        this.name = this.name;
    }
    // #endregion
    // #region =========== Methods ========== //
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (this.input.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /**
     * Triggered when the checkbox button received a click or the
     * input recognized any change.  Clicking on a label element, will trigger
     * a change event on the associated input.
     */
    onCheckboxChange(event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        event.preventDefault();
        if (this.disabled || this.readonly) {
            return;
        }
        this._checked = !this.checked;
        if (this._checked)
            this.isExpanded = true;
        if (!this.checked)
            this.isExpanded = false;
        this.announceExpandedCollapsed();
        // tell the FormControl to consider the control as 'touched'
        this.onTouched();
        this.onChange(this._checked);
        this.change.emit(new CdsCheckbox2Change(this, this.value, this._checked));
        this.checkedChange.emit(this._checked);
    }
    // #region ------- Announce expanded/collapsed for checkbox has content -------- //
    announceExpandedCollapsed() {
        if (this.content && this.checked && this.isExpanded) {
            this.announceVal("Additional information Expanded");
        }
        else if (this.content && !this.checked && !this.isExpanded) {
            this.isExpanded = false;
            this.announceVal(`${this.label} Additional information collapsed`);
        }
    }
    announceExpandedContent(event) {
        const key = event.keyCode;
        if (this.content && this.isExpanded && this.checked) {
            if ((event.shiftKey && key === 9) || (key === 9)) {
                this.announceVal(`${this.label} Additional information Expanded`);
            }
        }
    }
    announceVal(label) {
        setTimeout(() => {
            this._announcer.announce(label);
        }, 300);
    }
    /**
     * @description Allows Angular to update the model. The form will pass new values in here
     */
    writeValue(checked) {
        this.checked = checked;
        this.writeValueChange.emit(new CdsCheckbox2Change(this, this.value, checked));
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2, deps: [{ token: i0.NgZone }, { token: CdsAnnouncerService }, { token: i0.ElementRef }, { token: 'id', attribute: true }, { token: 'checked', attribute: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCheckbox2, selector: "cds-checkbox2", inputs: { content: "content", value: "value", ariaRequired: "ariaRequired", ariaLabelledby: "ariaLabelledby", ariaDescribedby: "ariaDescribedby", label: "label", bottom: "bottom", top: "top", center: "center", checked: "checked", name: "name", idStr: "idStr", disabled: "disabled", readonly: "readonly" }, outputs: { checkedChange: "checkedChange", blur: "blur", onShiftTab: "onShiftTab", onTab: "onTab", focus: "focus", change: "change", writeValueChange: "writeValueChange" }, host: { listeners: { "focusin": "focusChanged(true)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId", "attr.role": "this.role", "class.position-center": "this.centerClass", "class.position-bottom": "this.bottomClass" }, classAttribute: "cds-checkbox2" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCheckbox2)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsCheckbox2 }
        ], viewQueries: [{ propertyName: "ngContent", first: true, predicate: ["ngContent"], descendants: true }, { propertyName: "elementRef", first: true, predicate: ["elementRef"], descendants: true }, { propertyName: "labelElement", first: true, predicate: ["labelElement"], descendants: true }], ngImport: i0, template: `
    <input
      #elementRef
      class="cds-checkbox2-input"
      type="checkbox"
      [class.cds-checkbox2-error]="isInErrorState"
      [class.cds-checkbox2-readonly]="readonly"
      [attr.id]="idStr"
      [attr.name]="name"
      [value]="value"
      [checked]="checked"
      [disabled]="disabled"
      [attr.content]="content"
      [attr.aria-required]="ariaRequired"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="_inputAriaDescribedby"
      [attr.aria-readonly]="readonly"
      (blur)="blur.emit($event)"
      (keydown.Tab)="onTab.emit($event)"
      (keydown.shift.Tab)="onShiftTab.emit($event)"
      (keyup)="announceExpandedContent($event)"
      (focus)="focus.emit($event)"
      (change)="onCheckboxChange($event)"
      (click)="!this.readonly"
    />
    <label
      #labelElement
      class="cds-checkbox2-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
      [innerHTML]="label"
    >
    </label>
    <!-- This is the projected content when the checkbox button is checked -->
    <!-- This will only toggle properly when used with the checkbox group -->
    <ng-content  *ngIf="checked && content" select="[cdsCheckbox2Addition]"></ng-content>
  `, isInline: true, styles: [":host{display:flex;gap:var(--cds-checkbox2-label-gap, 16px);margin:var(--cds-checkbox2-margin, 10px 0 0 0);align-items:center}:host.position-bottom{align-items:flex-end}:host.position-center{align-items:center}.cds-checkbox2-label{display:flex;gap:var(--cds-checkbox2-label-gap, 16px);margin:0;line-height:var(--cds-checkbox2-label-line-height);font-family:var(--cds-checkbox2-label-font);align-items:center}.cds-checkbox2-input{width:var(--cds-checkbox2-icon-size, 24px);height:var(--cds-checkbox2-icon-size, 24px);border-radius:var(--cds-checkbox2-before-border-radius, 4px);background-repeat:no-repeat;background-position:center;flex:0 0 auto;border:var(--cds-checkbox2-before-border-width, 2px) solid var(--cds-checkbox2-before-border-color, #056dae);appearance:none;background-color:var(--cds-checkbox2-default-background-color, none)}.cds-checkbox2-input[type=checkbox]{margin:0}.cds-checkbox2-input:checked:enabled{background-image:var(--cds-checkbox2-checked-inline-img, none);border-color:var(--cds-checkbox2-checked-before-border-color, transparent)}.cds-checkbox2-input:active:enabled{background-image:var(--cds-checkbox2-checked-hover-inline-img)}.cds-checkbox2-input:hover:enabled{border-color:var(--cds-checkbox2-hover-before-border-color, #034c81)}.cds-checkbox2-input:hover:enabled:checked{background-image:var(--cds-checkbox2-checked-hover-inline-img, none)}.cds-checkbox2-input:focus-visible{box-shadow:var(--cds-checkbox2-box-shadow, 0 0 0 2px #fff),0 0 0 2px var(--cds-checkbox2-focus-inner-outline-color, #fff);outline:2px solid var(--cds-checkbox2-focus-outline-color, #002a54);outline-offset:2px}.cds-checkbox2-input:focus-visible:checked{outline-color:var(--cds-checkbox2-focus-checked-outline-color, #002a54)}.cds-checkbox2-input.cds-checkbox2-error{border-color:var(--cds-checkbox2-error-border-color, #d60000)}.cds-checkbox2-input.cds-checkbox2-error:checked{background-image:var(--cds-checkbox2-checked-error-inline-img, none)}.cds-checkbox2-input:disabled{border-color:var(--cds-checkbox2-disabled-border-color, #ccc);background-color:var(--cds-checkbox2-disabled-background-color, transparent)}.cds-checkbox2-input:disabled:checked{background-image:var(--cds-checkbox2-checked-disabled-inline-img, none);background-color:transparent}.cds-checkbox2-input:disabled+.cds-checkbox2-label{color:var(--cds-checkbox2-disabled-text-color, #ccc)}.cds-checkbox2-input.cds-checkbox2-readonly{border-color:var(--cds-checkbox2-readonly-border-color, #666);background-color:var(--cds-checkbox2-readonly-background-color, transparent)}.cds-checkbox2-input.cds-checkbox2-readonly:hover,.cds-checkbox2-input.cds-checkbox2-readonly:active{border-color:var(--cds-checkbox2-readonly-border-color, #666);background-color:var(--cds-checkbox2-readonly-background-color, transparent);background-image:none}.cds-checkbox2-input.cds-checkbox2-readonly:checked,.cds-checkbox2-input.cds-checkbox2-readonly:checked:hover{border-color:var(--cds-checkbox2-readonly-border-color, #666);background-image:var(--cds-checkbox2-checked-readonly-inline-img, none);background-color:transparent}.cds-checkbox2-input.cds-checkbox2-readonly+.cds-checkbox2-label{color:var(--cds-checkbox2-readonly-text-color, #666)}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox2', host: {
                        class: 'cds-checkbox2',
                        '(focusin)': 'focusChanged(true)',
                        '(input)': 'checkValue()'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCheckbox2)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsCheckbox2 }
                    ], template: `
    <input
      #elementRef
      class="cds-checkbox2-input"
      type="checkbox"
      [class.cds-checkbox2-error]="isInErrorState"
      [class.cds-checkbox2-readonly]="readonly"
      [attr.id]="idStr"
      [attr.name]="name"
      [value]="value"
      [checked]="checked"
      [disabled]="disabled"
      [attr.content]="content"
      [attr.aria-required]="ariaRequired"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="_inputAriaDescribedby"
      [attr.aria-readonly]="readonly"
      (blur)="blur.emit($event)"
      (keydown.Tab)="onTab.emit($event)"
      (keydown.shift.Tab)="onShiftTab.emit($event)"
      (keyup)="announceExpandedContent($event)"
      (focus)="focus.emit($event)"
      (change)="onCheckboxChange($event)"
      (click)="!this.readonly"
    />
    <label
      #labelElement
      class="cds-checkbox2-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
      [innerHTML]="label"
    >
    </label>
    <!-- This is the projected content when the checkbox button is checked -->
    <!-- This will only toggle properly when used with the checkbox group -->
    <ng-content  *ngIf="checked && content" select="[cdsCheckbox2Addition]"></ng-content>
  `, styles: [":host{display:flex;gap:var(--cds-checkbox2-label-gap, 16px);margin:var(--cds-checkbox2-margin, 10px 0 0 0);align-items:center}:host.position-bottom{align-items:flex-end}:host.position-center{align-items:center}.cds-checkbox2-label{display:flex;gap:var(--cds-checkbox2-label-gap, 16px);margin:0;line-height:var(--cds-checkbox2-label-line-height);font-family:var(--cds-checkbox2-label-font);align-items:center}.cds-checkbox2-input{width:var(--cds-checkbox2-icon-size, 24px);height:var(--cds-checkbox2-icon-size, 24px);border-radius:var(--cds-checkbox2-before-border-radius, 4px);background-repeat:no-repeat;background-position:center;flex:0 0 auto;border:var(--cds-checkbox2-before-border-width, 2px) solid var(--cds-checkbox2-before-border-color, #056dae);appearance:none;background-color:var(--cds-checkbox2-default-background-color, none)}.cds-checkbox2-input[type=checkbox]{margin:0}.cds-checkbox2-input:checked:enabled{background-image:var(--cds-checkbox2-checked-inline-img, none);border-color:var(--cds-checkbox2-checked-before-border-color, transparent)}.cds-checkbox2-input:active:enabled{background-image:var(--cds-checkbox2-checked-hover-inline-img)}.cds-checkbox2-input:hover:enabled{border-color:var(--cds-checkbox2-hover-before-border-color, #034c81)}.cds-checkbox2-input:hover:enabled:checked{background-image:var(--cds-checkbox2-checked-hover-inline-img, none)}.cds-checkbox2-input:focus-visible{box-shadow:var(--cds-checkbox2-box-shadow, 0 0 0 2px #fff),0 0 0 2px var(--cds-checkbox2-focus-inner-outline-color, #fff);outline:2px solid var(--cds-checkbox2-focus-outline-color, #002a54);outline-offset:2px}.cds-checkbox2-input:focus-visible:checked{outline-color:var(--cds-checkbox2-focus-checked-outline-color, #002a54)}.cds-checkbox2-input.cds-checkbox2-error{border-color:var(--cds-checkbox2-error-border-color, #d60000)}.cds-checkbox2-input.cds-checkbox2-error:checked{background-image:var(--cds-checkbox2-checked-error-inline-img, none)}.cds-checkbox2-input:disabled{border-color:var(--cds-checkbox2-disabled-border-color, #ccc);background-color:var(--cds-checkbox2-disabled-background-color, transparent)}.cds-checkbox2-input:disabled:checked{background-image:var(--cds-checkbox2-checked-disabled-inline-img, none);background-color:transparent}.cds-checkbox2-input:disabled+.cds-checkbox2-label{color:var(--cds-checkbox2-disabled-text-color, #ccc)}.cds-checkbox2-input.cds-checkbox2-readonly{border-color:var(--cds-checkbox2-readonly-border-color, #666);background-color:var(--cds-checkbox2-readonly-background-color, transparent)}.cds-checkbox2-input.cds-checkbox2-readonly:hover,.cds-checkbox2-input.cds-checkbox2-readonly:active{border-color:var(--cds-checkbox2-readonly-border-color, #666);background-color:var(--cds-checkbox2-readonly-background-color, transparent);background-image:none}.cds-checkbox2-input.cds-checkbox2-readonly:checked,.cds-checkbox2-input.cds-checkbox2-readonly:checked:hover{border-color:var(--cds-checkbox2-readonly-border-color, #666);background-image:var(--cds-checkbox2-checked-readonly-inline-img, none);background-color:transparent}.cds-checkbox2-input.cds-checkbox2-readonly+.cds-checkbox2-label{color:var(--cds-checkbox2-readonly-text-color, #666)}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: CdsAnnouncerService }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['checked']
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], ngContent: [{
                type: ViewChild,
                args: ['ngContent']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], elementRef: [{
                type: ViewChild,
                args: ['elementRef']
            }], labelElement: [{
                type: ViewChild,
                args: ['labelElement']
            }], centerClass: [{
                type: HostBinding,
                args: ['class.position-center']
            }], bottomClass: [{
                type: HostBinding,
                args: ['class.position-bottom']
            }], content: [{
                type: Input
            }], value: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], label: [{
                type: Input
            }], bottom: [{
                type: Input
            }], top: [{
                type: Input
            }], center: [{
                type: Input
            }], checked: [{
                type: Input
            }], checkedChange: [{
                type: Output
            }], name: [{
                type: Input
            }], idStr: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], blur: [{
                type: Output
            }], onShiftTab: [{
                type: Output
            }], onTab: [{
                type: Output
            }], focus: [{
                type: Output
            }], change: [{
                type: Output
            }], writeValueChange: [{
                type: Output
            }] } });

let nextUniqueId$6 = 0;
class CdsCheckbox2Group {
    /**
     * gets the currently selected CdsCheckbox component
     */
    get checkedCdsCheckboxes() {
        if (!this._checkboxes)
            return null;
        return this._checkboxes.filter(checkbox => checkbox.checked);
    }
    /**
     * disables the entire group
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this.updateDisabled();
    }
    /**
     * Puts the entire group in readOnly state
     */
    set readonly(value) {
        this._readonly = coerceBooleanProperty(value);
        this._updateReadonly();
    }
    get readonly() {
        return this._readonly;
    }
    /**
     * Allows you to change/remove the role="list" attribute
     */
    set roleAttr(role) {
        this.role = role;
    }
    /**
     * two-way value binding mechanism
     */
    get value() {
        return this._value || [];
    }
    set value(value) {
        // check to make sure value passed in is an array
        if (!isArray(value)) {
            devKill('The binding value of a checkbox group is a string array');
        }
        // Do nothing if new value is same as current value
        if (isEqual(this.value, value)) {
            return;
        }
        this._value = value || [];
    }
    // #endregion
    // #region =========== Constructor / Life-Cycle Hooks ========== //
    constructor(_el, id, _cdr) {
        this._el = _el;
        this.id = id;
        this._cdr = _cdr;
        // #region =========== CdsFormBase ========== //
        this.fieldType = 'checkbox-group';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        // #endregion
        // #region =========== Properties ========== //
        this.unsub$ = new Subject();
        this.role = 'list';
        // #endregion
        // #region =========== Input/Output ========== //
        this.alignment = 'column';
        /**
         * whether or not to trigger the error ribbon
         * @note this may be deprecated based on cds designs
         */
        this.error = false;
        this._disabled = false;
        this._value = [];
        this.valueChange = new EventEmitter();
        /**
         * The event is emitted when the dropdown is blurred
         */
        this.blur = new EventEmitter();
        // #endregion
        // #region =========== ControlValueAccessor Methods ========== //
        /** The method to be called in order to update ngModel */
        this.onChange = (value) => { };
        /** onTouch function registered via registerOnTouch (ControlValueAccessor) */
        this.onTouched = () => { };
        this.fieldId = this.id || `cds-checkbox2-group-${nextUniqueId$6++}`;
    }
    /** Child CHECKBOX buttons. */
    ngAfterContentInit() {
        // update child checkboxes to have role of listitem
        this.updateCheckboxRoles();
        this._setCheckboxesAfterContentInit(this.value);
        this._listenForChanges();
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region =========== Methods ========== //
    /**
   * Finds out if focus truly leaves the dropdown component.
   * @param e FocusEvent
   */
    _focusoutCheckboxGroup(e) {
        const isInCheckboxGroup = this._el.nativeElement.contains(e.relatedTarget);
        if (!isInCheckboxGroup) {
            this._isFocused = false;
            this.focusChange.next(this._isFocused);
            this.onTouched();
            this.blur.emit();
        }
    }
    _focusinCheckboxGroup(e) {
        if (!this._isFocused) {
            this._isFocused = true;
            this.focusChange.next(this._isFocused);
        }
    }
    /**
     * Sets checkboxes after content init
     * @param value array of values of checkboxes to be checked
     */
    _setCheckboxesAfterContentInit(value) {
        if (!value)
            value = [];
        if (this._checkboxes)
            this._checkboxes
                .filter(checkbox => value.includes(checkbox.value))
                .forEach(checkbox => (checkbox.checked = true));
    }
    _updateCheckboxes(value) {
        if (!value)
            value = [];
        this._checkboxes?.forEach(checkbox => {
            checkbox.checked = value.includes(checkbox.value);
        });
    }
    _listenForChanges() {
        this._checkboxes.forEach(checkbox => {
            checkbox.change
                .pipe(takeUntil(this.unsub$))
                .subscribe(change => {
                this._updateCheckboxGroup(change, true);
                this.onTouched();
            });
            checkbox.writeValueChange
                .pipe(takeUntil(this.unsub$))
                .subscribe(change => {
                this._updateCheckboxGroup(change, false);
            });
        });
    }
    _updateCheckboxGroup(change, isFocusMaintained) {
        if (isFocusMaintained) {
            this.maintainFocus(change);
        }
        if (change.checked) {
            this.selectFromCheckbox(change);
        }
        else {
            this.deSelectFromCheckbox(change);
        }
    }
    //maintains the focus on same checkbox if keyboard has been used to check or uncheck.
    maintainFocus(getChange) {
        let elRef = getChange.source.elementRef.nativeElement;
        elRef.focus();
    }
    // Pushes the checked checkbox value into an value array and emits updated value.
    selectFromCheckbox(checkbox) {
        let val = [];
        if (this._value) {
            val = this._value;
            this._value.push(checkbox.value);
        }
        this.valueChange.emit(val);
        this.onChange(val);
    }
    // Pops the unchecked checkbox from value array and emits updated value.
    deSelectFromCheckbox(checkbox) {
        this._value = this.value.filter(str => str !== checkbox.value);
        this.valueChange.emit(this._value);
        this.onChange(this._value);
    }
    // updates checkbox children to have a role of listitem
    // this needs to be done here so that checkboxes can be used without a checkbox group..
    // but when used with a checkbox group they will hava role of "listitem" (ADA requirement)
    updateCheckboxRoles() {
        if (this._checkboxes && this.role === 'list') {
            this._checkboxes.forEach(checkbox => {
                // wait until next cycle to prevent "Expression has changed after it was checked" errors
                setTimeout(() => {
                    checkbox.role = 'listitem';
                    this._cdr.markForCheck();
                }, 0);
            });
        }
    }
    // Updates disabled property to all checkboxes.
    updateDisabled() {
        this._checkboxes?.forEach(checkbox => {
            checkbox.disabled = this._disabled;
        });
    }
    // Updates readonly property to all child checkboxes.
    _updateReadonly() {
        this._checkboxes?.forEach(checkbox => {
            checkbox.readonly = this._readonly;
        });
    }
    /**
     * This method seemed to have no purpose.
     * @deprecated this method does nothing
     */
    checkValue() {
        // this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(value) {
        this.value = value;
        this._updateCheckboxes(value);
    }
    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     * @param isDisabled Whether the control should be disabled.
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2Group, deps: [{ token: i0.ElementRef }, { token: 'id', attribute: true }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCheckbox2Group, selector: "cds-checkbox2-group", inputs: { alignment: "alignment", error: "error", legendText: "legendText", ariaRequired: "ariaRequired", disabled: "disabled", readonly: "readonly", roleAttr: "roleAttr", value: "value" }, outputs: { valueChange: "valueChange", blur: "blur" }, host: { listeners: { "focusout": "_focusoutCheckboxGroup($event)", "focusin": "_focusinCheckboxGroup($event)" }, properties: { "attr.id": "this.fieldId", "attr.role": "this.role", "class.cds-checkbox2-group-error": "this.error" }, classAttribute: "cds-checkbox2-group" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCheckbox2Group)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsCheckbox2Group }
        ], queries: [{ propertyName: "_checkboxes", predicate: CdsCheckbox2 }], viewQueries: [{ propertyName: "elementRef", first: true, predicate: ["elementRef"], descendants: true }], ngImport: i0, template: `
  <legend  *ngIf="legendText" class="legend-text">
    <span [innerHTML]="legendText"></span>
  </legend>
  <div class="checkbox2-wrapper" [class.checkbox2-column]="alignment === 'column'">
    <ng-content></ng-content>
  </div>
  `, isInline: true, styles: [":host{--cds-checkbox2-margin: 0}:host.cds-checkbox2-group-error{padding-left:8px;border-left:2px solid var(--cds-checkbox2-group-error-border-color, #d60000)}.legend-text{margin:0}.checkbox2-wrapper{display:flex;row-gap:var(--cds-checkbox2-row-gap, 16px);column-gap:24px}.checkbox2-column{flex-direction:column}@media screen and (max-width: 768px){.checkbox2-wrapper{flex-direction:column}}@media screen and (max-width: 350px){:host{min-width:280px}}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2Group, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox2-group', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCheckbox2Group)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsCheckbox2Group }
                    ], host: {
                        class: 'cds-checkbox2-group',
                        '(focusout)': '_focusoutCheckboxGroup($event)',
                        '(focusin)': '_focusinCheckboxGroup($event)'
                    }, template: `
  <legend  *ngIf="legendText" class="legend-text">
    <span [innerHTML]="legendText"></span>
  </legend>
  <div class="checkbox2-wrapper" [class.checkbox2-column]="alignment === 'column'">
    <ng-content></ng-content>
  </div>
  `, styles: [":host{--cds-checkbox2-margin: 0}:host.cds-checkbox2-group-error{padding-left:8px;border-left:2px solid var(--cds-checkbox2-group-error-border-color, #d60000)}.legend-text{margin:0}.checkbox2-wrapper{display:flex;row-gap:var(--cds-checkbox2-row-gap, 16px);column-gap:24px}.checkbox2-column{flex-direction:column}@media screen and (max-width: 768px){.checkbox2-wrapper{flex-direction:column}}@media screen and (max-width: 350px){:host{min-width:280px}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: i0.ChangeDetectorRef }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], elementRef: [{
                type: ViewChild,
                args: ['elementRef']
            }], _checkboxes: [{
                type: ContentChildren,
                args: [CdsCheckbox2]
            }], alignment: [{
                type: Input
            }], error: [{
                type: HostBinding,
                args: ['class.cds-checkbox2-group-error']
            }, {
                type: Input
            }], legendText: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], disabled: [{
                type: Input
            }], readonly: [{
                type: Input
            }], roleAttr: [{
                type: Input
            }], value: [{
                type: Input
            }], valueChange: [{
                type: Output
            }], blur: [{
                type: Output
            }] } });

const COMPONENTS$u = [CdsCheckbox2, CdsCheckbox2Group];
const MODULES$e = [CommonModule];
class CdsCheckbox2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2Module, declarations: [CdsCheckbox2, CdsCheckbox2Group], imports: [CommonModule], exports: [CdsCheckbox2, CdsCheckbox2Group] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2Module, imports: [MODULES$e] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckbox2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$e],
                    declarations: [...COMPONENTS$u],
                    exports: [...COMPONENTS$u]
                }]
        }] });

function cdsHasLower(control) {
    if (!control.value)
        return null; // handled by required
    return /[a-z]/.test(control.value)
        ? null // null denotes no error
        : { lowercase: true };
}

function cdsHasNumber(control) {
    if (!control.value)
        return null; // handled by required
    return /[0-9]/.test(control.value)
        ? null // null denotes no error
        : { number: true };
}

function cdsHasUpper(control) {
    if (!control.value)
        return null; // handled by required
    return /[A-Z]/.test(control.value)
        ? null // null denotes no error
        : { uppercase: true };
}

const cdsPasswordVal = (control) => {
    if (!control.value)
        return null; // required handles this
    const upperError = cdsHasUpper(control);
    const lowerError = cdsHasLower(control);
    const numberError = cdsHasNumber(control);
    const atLeast6 = control.value.length >= 6;
    const atMost20 = control.value.length <= 20;
    return !upperError && !lowerError && !numberError && atLeast6 && atMost20
        ? null
        : {
            ...upperError,
            ...lowerError,
            ...numberError,
            lessThan6: !atLeast6,
            moreThan20: !atMost20
        };
};
// @Directive({
//   selector:
//     '[cdsPassword][formControlName],[cdsPassword][formControl],[cdsPassword][ngModel]',
//   providers: [
//     {
//       provide: NG_VALIDATORS,
//       useExisting: PasswordValidatorDir,
//       multi: true,
//     },
//   ],
// })
// export class PasswordValidatorDir implements Validator {
//   validate(control: AbstractControl): ValidationErrors {
//     return cdsPasswordVal(control);
//   }
// }

const PHONE_PATTERN = '^(([0-9]{3})s*|[0-9]{3}-)[0-9]{3}-[0-9]{4}$';
const EMAIL_PATTERN = '^(([^<>()[]\\.,;:s@"]+(.[^<>()[]\\.,;:s@"]+)*)|(".+"))@(([[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}.[0-9]{1,3}])|(([a-zA-Z-0-9]+.)+[a-zA-Z]{2,}))$';
const ZIPCODE_PATTERN = '(^d{5}$)|(^d{5}-d{4}$)';
const SSN_PATTERN = '(d{0,3})(d{0,2})(d{0,4})';
const URL_PATTERN = '[-a-zA-Z0-9@:%._+~#=]{2,256}.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)';

/**
 * @description ensures that a confirm password field matches the poassword
 * @note I couldn't figure out how to apply this validation without a directive
 * @reference https://scotch.io/tutorials/how-to-implement-a-custom-validator-directive-confirm-password-in-angular-2
 */
// @Directive({
//   selector:
//     '[cdsConfirm][formControlName],[cdsConfirm][formControl],[cdsConfirm][ngModel]',
//   providers: [
//     {
//       provide: NG_VALIDATORS,
//       useExisting: forwardRef(() => ConfrmPasswordDir),
//       multi: true
//     }
//   ]
// })
// export class ConfrmPasswordDir implements Validator {
//   constructor(@Attribute('cdsConfirm') public confirmPassword: string) {}
//   validate(confirm: AbstractControl): ValidationErrors | null {
//     if (!confirm.value) return null; // required validator handles this
//     const password = confirm.root.get(this.confirmPassword);
//     const noMatch = confirm.value != password.value;
//     if (!password || noMatch) return { noMatch: true };
//     else return null;
//   }
// }

function cdsValidateEmail(control) {
    if (!control.value)
        return null; // required validator handles this
    return new RegExp(EMAIL_PATTERN).test(control.value) ? null : { email: true };
}
// @Directive({
//   selector:
//     '[formControlName][cdsEmail],[formControl][cdsEmail],[ngModel][cdsEmail]',
//   providers: [
//     {
//       provide: NG_VALIDATORS,
//       useValue: cdsValidateEmail,
//       multi: true
//     }
//   ]
// })
class EmailValidatorDir {
    validate(control) {
        return cdsValidateEmail(control);
    }
}

function cdsNoNumbers(control) {
    if (!control.value)
        return null; // handled by required
    return /^([^0-9]*)$/.test(control.value) ? null : { noNumber: true };
}

function cdsNotEmpty(control) {
    if (!control.value)
        return null; // handled by required
    if (typeof control.value != 'object' || !control.value.length)
        devError('cdsNotEmpty is an array validator. Do not use it to validate other types');
    return control.value.length > 0
        ? null // null denotes no error
        : { nonEmpty: true };
}

function cdsNoWhiteSpace(control) {
    const isWhitespace = (control.value || '').trim().length === 0;
    return isWhitespace ? { noWhitespace: { value: control.value } } : null;
}
class WhitespaceValidatorDir {
    validate(control) {
        return cdsNoWhiteSpace(control);
    }
}

// validator for checking if a date is after another date
function after(date) {
    return (control) => {
        const value = control.value;
        if (!value)
            return null; // handled by required validator
        return value.getTime() < date.getTime()
            ? {
                after: true
            }
            : null;
    };
}
// different implementation for validator for checking if a date is after another date
function afterValidator(afterDate) {
    return function (control) {
        const value = control.value;
        const date = new Date(Date.parse(value));
        if (date.getTime() && date.getTime() <= afterDate.getTime()) {
            return { 'after': true };
        }
        return null;
    };
}

function before(date) {
    return (control) => {
        const value = control.value;
        if (!value)
            return null; // handled by required validator
        return value.getTime() > date.getTime()
            ? {
                before: true
            }
            : null;
    };
}

// This validator checks if the date passed in control matches the date passed as an argument in validator method.
function isSame(date) {
    return (control) => {
        const value = control.value;
        if (!value)
            return null; // handled by required validator
        return value.getTime() === date.getTime()
            ? {
                isSame: true
            }
            : null;
    };
}

// validator for checking if a date matches pattern and is valid
function patternDateValidator(format) {
    return function (control) {
        const regex_map = new Map([
            ["M/d/yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["d/M/yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["M-d-yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["d-M-yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["M-yyyy", /^\d{1,2}[\/-]\d{4}$/],
            ["M/yyyy", /^\d{1,2}[\/-]\d{4}$/],
            ["MM/dd/yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["dd/MM/yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["MM-dd-yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["dd-MM-yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["MM-yyyy", /^\d{1,2}[\/-]\d{4}$/],
            ["MM/yyyy", /^\d{1,2}[\/-]\d{4}$/],
            ["MM/yy", /^\d{1,2}[\/-]\d{2}$/],
        ]);
        let value = control.value;
        if (value) {
            let convertedValue = convertedStringDateObject(value, format);
            const [day, month, year] = [convertedValue.day, convertedValue.month, convertedValue.year];
            const date = new Date(parseInt(convertedValue.year), parseInt(convertedValue.month) - 1, parseInt(convertedValue.day));
            if (value && !regex_map.get(format).test(value)) {
                return { 'pattern': true };
            }
            else if (isNaN(date.getTime()) && value) {
                return { 'invalidDate': true };
            }
            if (value) {
                let string_digits = [parseInt(convertedValue.year), parseInt(convertedValue.month), parseInt(convertedValue.day)];
                let date_digits = new Set([
                    date.getDate(),
                    date.getFullYear(),
                    date.getMonth() + 1
                ]);
                let isSubset = string_digits.every(element => date_digits.has(element));
                if (!isSubset) {
                    return { 'invalidDate': true };
                }
            }
            return null;
        }
        return null;
    };
}
// converts a month day format eg MM-YYYY to have a day of 01 for date parsing purposes
function convertedDateString(date, format) {
    if (date && date !== "") {
        const delimiter = date.includes("/") ? "/" : date.includes("-") ? "-" : null;
        const portions = date.split(/[-/]/);
        const dateSections = format.split(/[-/]/);
        if (format.charAt(0) === "d" && dateSections.length === 3) {
            if (portions && portions.length === 3)
                date = portions[1] + delimiter + portions[0] + delimiter + portions[2];
        }
        if (dateSections.length === 2 && dateSections[dateSections.length - 1].length === 4) {
            if (portions && portions.length === 2)
                date = portions[0] + delimiter + "03" + delimiter + portions[1];
        }
        else if (dateSections.length === 2) {
            if (portions && portions.length === 2)
                date = portions[0] + delimiter + "03" + delimiter + "20" + portions[1];
        }
    }
    return date;
}
function convertedStringDateObject(date, format) {
    // if input format doesnt have day or year, placeholder for date validation would be 02
    let dateObject = { day: '02', month: '02', year: '' };
    if (date && date !== "") {
        const delimiter = date.includes("/") ? "/" : date.includes("-") ? "-" : null;
        const portions = date.split(delimiter);
        const formatSections = format.split(delimiter);
        formatSections.forEach((section, index) => {
            if (section[0].toLowerCase() === 'd') {
                dateObject.day = portions[index];
            }
            else if (section[0].toLowerCase() === 'm') {
                dateObject.month = portions[index];
            }
            else if (section[0].toLowerCase() === 'y') {
                if (section.length === 2) {
                    // short year placeholder first two digits would be 2000's
                    dateObject.year = "20" + portions[index];
                }
                else {
                    dateObject.year = portions[index];
                }
            }
        });
    }
    return dateObject;
}

// validator for checking if a date is valid
// different implementation for validator for checking if a date is after another date
function validDate() {
    return function (control) {
        const date = new Date(Date.parse(control.value));
        if (isNaN(date.getTime()) && control.value) {
            return { 'invalidDate': true };
        }
        return null;
    };
}

/**
 * @description an set of validators which extends those provided by Angular
 */
class CdsValidators {
    // hijacked from Angular
    static { this.required = Validators.required; }
    static { this.min = Validators.min; }
    static { this.max = Validators.max; }
    static { this.minLength = Validators.minLength; }
    static { this.maxLength = Validators.maxLength; }
    static { this.pattern = Validators.pattern; }
    static { this.requiredTrue = Validators.requiredTrue; }
    static { this.nullValidator = Validators.nullValidator; }
    /**
     * @description Angular's Validators.email allows some emails that we
     * consider invalid, such as: 'myname@citi' with no '.com'
     */
    static email(c) {
        return cdsValidateEmail(c);
    }
    /**
     * @description Teams can compose their own, but this is intended to
     * be the login-auth standard set
     */
    static password(c) {
        return cdsPasswordVal(c);
    }
    static noNumbers(c) {
        return cdsNoNumbers(c);
    }
    static hasUppercase(c) {
        return cdsHasUpper(c);
    }
    static hasLowercase(c) {
        return cdsHasLower(c);
    }
    static hasNumber(c) {
        return cdsHasNumber(c);
    }
    static notEmpty(c) {
        return cdsNotEmpty(c);
    }
    static after(d) {
        return after(d);
    }
    static afterValidator(d) {
        return afterValidator(d);
    }
    static validDate() {
        return validDate();
    }
    static patternDate(format) {
        return patternDateValidator(format);
    }
    static patternDateFn(format) {
        return patternDateValidator(format);
    }
    static before(d) {
        return before(d);
    }
    static isSame(d) {
        return isSame(d);
    }
    static noWhiteSpace(c) {
        return cdsNoWhiteSpace(c);
    }
}

class DropdownService {
    constructor() {
        this.clickedOptionValue$ = new Subject();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DropdownService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DropdownService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DropdownService, decorators: [{
            type: Injectable
        }] });

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _uniqueIdCounter = 1;
class CdsOption {
    set value(value) {
        if (typeof value === 'number')
            value = value.toString();
        this._value = value;
    }
    get value() {
        return this._value;
    }
    /** The value of the option. */
    set label(lbl) {
        // Converts html entities (such as &reg; into text)
        const tempEl = this._document.createElement('span');
        tempEl.innerHTML = lbl || '';
        this._label = tempEl.textContent;
    }
    get label() {
        return this._label;
    }
    constructor(host, dropdownService, _doc) {
        this.host = host;
        this.dropdownService = dropdownService;
        this._doc = _doc;
        this.disabled = false;
        this._label = '';
        /** The unique ID of the option. */
        this.id = `cds-option-${_uniqueIdCounter++}`;
        this.click = new EventEmitter();
        this.ddValue = new EventEmitter();
        this.attrTabindex = 0;
        this._isActive = false;
        this._document = _doc;
    }
    get element() {
        return this.host.nativeElement;
    }
    get isActive() {
        return this._isActive;
    }
    set isActive(value) {
        this._isActive = value;
    }
    setValue() {
        this.click.emit(this.value);
        this.ddValue.emit(this.label);
    }
    emitClick(e) {
        e.preventDefault();
        e.stopPropagation();
        this.setValue();
        // lets service know when option is clicked and passes value
        this.dropdownService.clickedOptionValue$.next(this.value);
    }
    /**
     * This method sets display styles on the option to make it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setActiveStyles() {
        this._isActive = true;
    }
    /**
     * This method removes display styles on the option that made it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setInactiveStyles() {
        this._isActive = false;
    }
    /** Gets the label to be used when determining whether the option should be focused. */
    getLabel() {
        return this.label;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOption, deps: [{ token: i0.ElementRef }, { token: DropdownService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsOption, selector: "cds-option", inputs: { value: "value", disabled: "disabled", label: "label", id: "id" }, outputs: { click: "click", ddValue: "ddValue" }, host: { listeners: { "mousedown": "emitClick($event)" }, properties: { "attr.tabindex": "this.attrTabindex", "class.active": "this.isActive" }, classAttribute: "cds-option" }, ngImport: i0, template: ` <span #option class="select-option" [innerHTML]="label"></span> `, isInline: true, styles: [":host{display:block;cursor:pointer;font-size:16px;font-weight:300;letter-spacing:0;padding-top:10px;padding-bottom:10px;line-height:24px;padding-left:20px;color:#333}:host:hover{color:#056dae;font-weight:700}:host.active{color:#056dae;font-weight:700}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOption, decorators: [{
            type: Component,
            args: [{ selector: 'cds-option', host: { class: 'cds-option' }, template: ` <span #option class="select-option" [innerHTML]="label"></span> `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:block;cursor:pointer;font-size:16px;font-weight:300;letter-spacing:0;padding-top:10px;padding-bottom:10px;line-height:24px;padding-left:20px;color:#333}:host:hover{color:#056dae;font-weight:700}:host.active{color:#056dae;font-weight:700}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: DropdownService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], label: [{
                type: Input
            }], id: [{
                type: Input
            }], click: [{
                type: Output
            }], ddValue: [{
                type: Output
            }], attrTabindex: [{
                type: HostBinding,
                args: ['attr.tabindex']
            }], isActive: [{
                type: HostBinding,
                args: ['class.active']
            }], emitClick: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

class CdsSelectComponent {
    constructor(announcer) {
        this.announcer = announcer;
        this.selectionChange = new EventEmitter();
    }
    ngAfterViewInit() {
        this.keyManager = new ActiveDescendantKeyManager(this.options)
            .withVerticalOrientation()
            .withWrap()
            .withTypeAhead();
    }
    onKeyDown(event) {
        this.keyManager.onKeydown(event);
        // tslint:disable-next-line: deprecation
        if (event.keyCode === ENTER)
            this.keyManager.setActiveItem(this.keyManager.activeItemIndex);
        // tslint:disable-next-line: deprecation
        if (event.keyCode === END)
            this.keyManager.setLastItemActive();
        this.announceActive();
        // tslint:disable-next-line: deprecation
        if (event.keyCode === HOME)
            this.keyManager.setFirstItemActive();
        this.announceActive();
    }
    get selected() {
        if (!this.keyManager.activeItem)
            return null;
        return this.keyManager.activeItem.value;
    }
    selectOption(index) {
        this.keyManager.setActiveItem(index || this.keyManager.activeItemIndex);
        if (this.keyManager.activeItem)
            this.selectionChange.emit(this.keyManager.activeItem);
    }
    announceActive() {
        this.announcer.announce(`${this.selected}`);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSelectComponent, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsSelectComponent, selector: "cds-select", outputs: { selectionChange: "selectionChange" }, host: { classAttribute: "cds-select" }, queries: [{ propertyName: "options", predicate: CdsOption }], ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true, styles: [":host{width:100%;box-shadow:0 2px 14px #0003;display:block;overflow:auto}@media only screen and (max-width: 768px){:host{margin-left:15px;margin-right:7px}}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-select', host: { class: 'cds-select' }, template: `
    <ng-content></ng-content>
  `, styles: [":host{width:100%;box-shadow:0 2px 14px #0003;display:block;overflow:auto}@media only screen and (max-width: 768px){:host{margin-left:15px;margin-right:7px}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { options: [{
                type: ContentChildren,
                args: [CdsOption]
            }], selectionChange: [{
                type: Output
            }] } });

// import { CdsOptGroup } from './optgroup';
const COMPONENTS$t = [CdsSelectComponent];
const MODULES$d = [CommonModule];
class SelectModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SelectModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: SelectModule, declarations: [CdsSelectComponent], imports: [CommonModule], exports: [CdsSelectComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SelectModule, imports: [MODULES$d] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SelectModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$d],
                    declarations: [...COMPONENTS$t],
                    exports: [...COMPONENTS$t]
                }]
        }] });

let nextId$5 = 0;
/**
 * @Component: Disclaimer
 * @Section: Forms
 * @Description: This is the disclaimer component and is used to create reusable disclaimer
 * from Bootstrap.
 */
class SignOnComponent {
    get mobileText() {
        return '<div class=qr-icon></div>' + this.mobileButtonText;
    }
    set isRememberUsernameChecked(checked) {
        this._isRememberUsernameChecked = checked;
        // if (!this.userDD && checked) this.announcer.announce(this.rememberText)
    }
    get isRememberUsernameChecked() {
        return this._isRememberUsernameChecked;
    }
    // #endregion
    // #region // ---------------- Constructor / Lifecycle Hooks ---------------- ///
    constructor() {
        // signon button id
        this.signonButtonID = `signOn-${nextId$5}`;
        // mobile button id
        this.mobileButtonID = `signOn-mobile-${nextId$5}`;
        // register button id
        this.registerButtonID = `signOn-register-${nextId$5}`;
        // activate button id
        this.activateButtonID = `signOn-activate-${nextId$5++}`;
        // to focus input on load.
        // @Input() focusInput: boolean = false;
        //toggle for mobile login button
        this.showMobileLogin = false;
        this._isRememberUsernameChecked = false;
        /**
         * @Event: SubmitButtonClicked
         * @Description: This event that is fired when the submit button is clicked
         * Can be bound to with (SubmitButtonClicked)='myFunction($event)'
         * @Return: Object
         */
        this.SubmitButtonClicked = new EventEmitter();
        /**
         * @Event: mobileLoginClicked
         * @Description: This event that is fired when the mobile login button is clicked
         * Can be bound to with (mobileLoginClicked)='myFunction($event)'
         * @Return: Object
         */
        this.mobileLoginClicked = new EventEmitter();
        /**
         * @Event: registerClicked
         * @Description: This event that is fired when the register link button is clicked
         * Can be bound to with (registerClicked)='myFunction($event)'
         * @Return: Object
         */
        this.registerClicked = new EventEmitter();
        /**
         * @Event: activateClicked
         * @Description: This event that is fired when the activate link button is clicked
         * Can be bound to with (activateClicked)='myFunction($event)'
         * @Return: Object
         */
        this.activateClicked = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Sign On <citi-signon> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    // #region // ---------------- Methods ---------------- ///
    onSubmit(remember) {
        this.SubmitButtonClicked.emit({ remember });
    }
    mobileLogin() {
        this.mobileLoginClicked.emit(true);
    }
    register() {
        this.registerClicked.emit(true);
    }
    activate() {
        this.activateClicked.emit(true);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SignOnComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: SignOnComponent, selector: "cds-signon", inputs: { title: "title", buttonText: "buttonText", mobileButtonText: "mobileButtonText", registerButtonText: "registerButtonText", activateButtonText: "activateButtonText", rememberIdText: "rememberIdText", rememberText: "rememberText", signonButtonID: "signonButtonID", mobileButtonID: "mobileButtonID", registerButtonID: "registerButtonID", activateButtonID: "activateButtonID", showMobileLogin: "showMobileLogin", isRememberUsernameChecked: "isRememberUsernameChecked" }, outputs: { SubmitButtonClicked: "SubmitButtonClicked", mobileLoginClicked: "mobileLoginClicked", registerClicked: "registerClicked", activateClicked: "activateClicked" }, host: { classAttribute: "cds-signon" }, ngImport: i0, template: `
    <section class="theme-light SampleErrorStates">
      <h4 class="cds-signon-title">{{ title }}</h4>
      <div><ng-content select="[alertmessage]"></ng-content></div>

      <div class="username">
        <ng-content select="[username]"></ng-content>
        <!--username input end-->
      </div>

      <!--  checkbox -->
      <div *ngIf="!usesDD" class="remember">
        <ng-content select="[remember]"></ng-content>
      </div>

      <div *ngIf="!usesDD && isRememberUsernameChecked">
        <small role="alert">{{ rememberText }}</small>
      </div>

      <!--password input-->
      <div class="password">
        <ng-content select="[password]"></ng-content>
        <!--password input end-->
      </div>

      <!--  links -->

      <div class="cds-signon-link">
        <ng-content select="[links]"></ng-content>
      </div>

      <!--  link ends -->

      <!--  signon button -->

      <div class="form-group col-xs-12">
        <button
          class="cds-signon-cta"
          cdsButton="primary"
          size="large"
          (click)="onSubmit(isRememberUsernameChecked)"
          [id]="signonButtonID"
        >
          {{ buttonText }}
        </button>
      </div>

      <!-- signon button end -->

      <!--  mobile app button -->

      <div *ngIf="showMobileLogin" class="form-group col-xs-12">
        <button
          class="cds-signon-cta mobile"
          cdsButton="alternate"
          size="large"
          (click)="mobileLogin()"
          [id]="mobileButtonID"
          [innerHTML]="mobileText"
        ></button>
      </div>

      <!-- mobile app button end -->

      <!--  register link button -->

      <div class="register-activate">
        <div class="col-xs-6 anchor-container">
          <a
            role="link"
            class="cds-signon-anchor register"
            (click)="register()"
            tabindex="0"
            [id]="registerButtonID"
          >
            {{ registerButtonText }}
          </a>
        </div>

        <span class="register-connector">{{
          activateButtonText == null || registerButtonText == null ? '' : '/'
        }}</span>
        <div class="col-xs-6 anchor-container">
          <a
            role="link"
            class="cds-signon-anchor activate"
            (click)="activate()"
            tabindex="0"
            [id]="activateButtonID"
          >
            {{ activateButtonText }}
          </a>
        </div>
      </div>

      <!-- register link button end -->
    </section>
  `, isInline: true, styles: [""], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SignOnComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-signon', host: { class: 'cds-signon' }, template: `
    <section class="theme-light SampleErrorStates">
      <h4 class="cds-signon-title">{{ title }}</h4>
      <div><ng-content select="[alertmessage]"></ng-content></div>

      <div class="username">
        <ng-content select="[username]"></ng-content>
        <!--username input end-->
      </div>

      <!--  checkbox -->
      <div *ngIf="!usesDD" class="remember">
        <ng-content select="[remember]"></ng-content>
      </div>

      <div *ngIf="!usesDD && isRememberUsernameChecked">
        <small role="alert">{{ rememberText }}</small>
      </div>

      <!--password input-->
      <div class="password">
        <ng-content select="[password]"></ng-content>
        <!--password input end-->
      </div>

      <!--  links -->

      <div class="cds-signon-link">
        <ng-content select="[links]"></ng-content>
      </div>

      <!--  link ends -->

      <!--  signon button -->

      <div class="form-group col-xs-12">
        <button
          class="cds-signon-cta"
          cdsButton="primary"
          size="large"
          (click)="onSubmit(isRememberUsernameChecked)"
          [id]="signonButtonID"
        >
          {{ buttonText }}
        </button>
      </div>

      <!-- signon button end -->

      <!--  mobile app button -->

      <div *ngIf="showMobileLogin" class="form-group col-xs-12">
        <button
          class="cds-signon-cta mobile"
          cdsButton="alternate"
          size="large"
          (click)="mobileLogin()"
          [id]="mobileButtonID"
          [innerHTML]="mobileText"
        ></button>
      </div>

      <!-- mobile app button end -->

      <!--  register link button -->

      <div class="register-activate">
        <div class="col-xs-6 anchor-container">
          <a
            role="link"
            class="cds-signon-anchor register"
            (click)="register()"
            tabindex="0"
            [id]="registerButtonID"
          >
            {{ registerButtonText }}
          </a>
        </div>

        <span class="register-connector">{{
          activateButtonText == null || registerButtonText == null ? '' : '/'
        }}</span>
        <div class="col-xs-6 anchor-container">
          <a
            role="link"
            class="cds-signon-anchor activate"
            (click)="activate()"
            tabindex="0"
            [id]="activateButtonID"
          >
            {{ activateButtonText }}
          </a>
        </div>
      </div>

      <!-- register link button end -->
    </section>
  ` }]
        }], ctorParameters: () => [], propDecorators: { title: [{
                type: Input
            }], buttonText: [{
                type: Input
            }], mobileButtonText: [{
                type: Input
            }], registerButtonText: [{
                type: Input
            }], activateButtonText: [{
                type: Input
            }], rememberIdText: [{
                type: Input
            }], rememberText: [{
                type: Input
            }], signonButtonID: [{
                type: Input
            }], mobileButtonID: [{
                type: Input
            }], registerButtonID: [{
                type: Input
            }], activateButtonID: [{
                type: Input
            }], showMobileLogin: [{
                type: Input
            }], isRememberUsernameChecked: [{
                type: Input
            }], SubmitButtonClicked: [{
                type: Output
            }], mobileLoginClicked: [{
                type: Output
            }], registerClicked: [{
                type: Output
            }], activateClicked: [{
                type: Output
            }] } });

const COMPONENTS$s = [SignOnComponent];
const MODULES$c = [CommonModule, CdsInputModule, CdsCTAModule, CdsButtonModule];
class CdsSignOn {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSignOn, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsSignOn, declarations: [SignOnComponent], imports: [CommonModule, CdsInputModule, CdsCTAModule, CdsButtonModule], exports: [SignOnComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSignOn, imports: [MODULES$c] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSignOn, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$c],
                    declarations: [...COMPONENTS$s],
                    exports: [...COMPONENTS$s]
                }]
        }] });

;
let nextUniqueId$5 = 1;
class CdsDropdown {
    set formRef(ref) {
        console.warn('formRef is deprecated');
        this._formRef = ref;
    }
    get formRef() {
        return this._formRef;
    }
    /** Placeholder to be shown if no value has been selected. */
    get placeholder() {
        return this._placeholder;
    }
    set placeholder(value) {
        this._placeholder = value;
        this._placeholderStatic = value;
    }
    /**
     * This defines the id of the input.
     */
    get idStr() {
        return this._id || this._uid;
    }
    set idStr(value) {
        this._id = value || this._uid;
        this.fieldId = this._id;
    }
    /** Whether the component is required. */
    get required() {
        return this._required;
    }
    set required(value) {
        this._required = value;
    }
    /** Value of the select control. */
    get value() {
        return this._value;
    }
    set value(newValue) {
        if (newValue !== this._value) {
            this._value = newValue;
            this.onChangeFn(newValue);
        }
    }
    // #endregion
    // #region // ------- Constructor/Lifecycle Hooks -------- //
    constructor(overlay, elementRef, dropdownService, liveAnnouncer, input, changeRef, id) {
        this.overlay = overlay;
        this.elementRef = elementRef;
        this.dropdownService = dropdownService;
        this.liveAnnouncer = liveAnnouncer;
        this.input = input;
        this.changeRef = changeRef;
        this.id = id;
        // #region =========== FormBase ========== //
        this.fieldType = 'dropdown';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        /** Whether filling out the select is required in the form. */
        this._required = false;
        this.isDisabled = false;
        this.isPanelOpen = false;
        // #endregion
        // #region ========== Outputs ========== //
        /**
         * The event is emitted when the value is changed
         * The event is returned.
         */
        this.selectionChange = new EventEmitter();
        /**
         * The event is emitted when the dropdown is closed
         */
        this.panelClose = new EventEmitter();
        /**
         * The event is emitted when the dropdown is blurred
         */
        this.blur = new EventEmitter();
        // #endregion
        // #region ========== Inputs ========== //
        /** Whether the dropDown Trigger is disabled. */
        this.disabled = false;
        this._uid = `cds-dropdown-${nextUniqueId$5++}-input`;
        // #endregion
        // #region // ----------------------- Form Binding Methods ----------------------- //
        /** `View -> model callback called when value changes` */
        this.onChangeFn = (_) => { };
        /** `View -> model callback called when select has been touched` */
        this.onTouchedFn = () => { };
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Dropdown <citi-dropdown> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
        // this.fieldId = this.idStr || `cds-dropdown-${nextUniqueId++}`;
    }
    ngAfterContentInit() {
        this._initKeyManager();
        setTimeout(() => {
            if (this.value) {
                this._setActiveItemAndPlaceholder(this.value);
            }
        });
    }
    ngOnInit() {
        this.fieldId = this.idStr;
    }
    // #endregion
    // #region // ------- Getters -------- //
    // find options length to be announce
    get optionsLength() {
        return this.options.length;
    }
    // find the index number of the active option to announce its position in the list
    get findActiveOption() {
        let activeId = '';
        const activeOptionIndex = this.options
            .map((opt) => opt.isActive)
            .indexOf(true);
        return activeOptionIndex + 1;
    }
    get select() {
        if (!this._keyManager.activeItem)
            return null;
        return this._keyManager.activeItem.value;
    }
    get label() {
        return this._keyManager.activeItem.label;
    }
    // #endregion
    // #region // ------- Methods -------- //
    /**
     * Set the selected option active
     * DEPRECATED - please use Reactive/Template forms to set option
     * @param option CdsOption
     */
    selectOption(option) {
        this.writeValue(option.value);
        this.closePanel();
        this.checkValue();
    }
    _announceSelectInOrder() {
        const announceTxt = `${this.findActiveOption} out of ${this.optionsLength}`;
        this.liveAnnouncer.announce(announceTxt, 400);
    }
    _announceSelectedOnClose() {
        const announceTxt = this.value ? `${this.label} selected` : '';
        if (announceTxt) {
            this.liveAnnouncer.announce(announceTxt, 400);
        }
    }
    _announceOnOpen() {
        const announceTxt = this.value
            ? `${this.label} ${this.findActiveOption} out of ${this.optionsLength}`
            : `${this.optionsLength} total options`;
        this.liveAnnouncer.announce(announceTxt, 400);
    }
    /**
     * Finds the CdsOption from the value
     * @param val value of option
     */
    _getOptionInfoFromValue(val) {
        const opt = this.options?.find(o => o.value === val);
        let optInfo;
        if (opt) {
            optInfo = {
                option: opt,
                label: opt.label,
                value: opt.value,
                index: this.options.toArray().indexOf(opt)
            };
        }
        return optInfo;
    }
    /**
     * Sets the active item and placeholder text
     * @param value value of selected option
     */
    _setActiveItemAndPlaceholder(value) {
        // Set ActiveItem
        const optionInfo = this._getOptionInfoFromValue(value);
        this._keyManager?.setActiveItem(!!optionInfo ? optionInfo.index : -1);
        // Set placeholder text
        this._placeholder = optionInfo?.label ?? this._placeholderStatic ?? '';
        this.changeRef.markForCheck();
    }
    openPanel() {
        if (this.disabled)
            return;
        this.isPanelOpen = true;
        const optionWrapperClass = Array.isArray(this.optionsClass)
            ? this.optionsClass
            : this.optionsClass?.split(' ') || [];
        this.panelPopup.openDropdown({
            panelClass: ['cds-dropdown-overlay-panel', ...optionWrapperClass],
            hasBackdrop: true,
            backdropClass: 'overlay-backdrop-class',
        });
        if (this._keyManager?.activeItem) {
            this.scrollTo();
        }
    }
    onPanelClose($event) {
        this.isPanelOpen = false;
    }
    closePanel() {
        if (this.panelPopup.showing) {
            this.panelPopup.close();
            this.panelClose.emit(this.value);
            this.isPanelOpen = false;
        }
    }
    toggleDropdown($event) {
        $event.stopPropagation();
        if (!this.panelPopup.showing) {
            this.openPanel();
            setTimeout(() => {
                this._announceOnOpen();
            }, 0);
        }
        else {
            this.closePanel();
            setTimeout(() => {
                this._announceSelectedOnClose();
            }, 0);
        }
        this.inputRef.nativeElement.focus();
    }
    toggleDropdownIcon(event) {
        if (this.inputRef.nativeElement.hasAttribute('disabled')) {
            this.dropdownArrow.nativeElement.disabled = true;
            this.isDisabled = true;
        }
        event.stopPropagation();
        setTimeout(() => {
            this.inputRef.nativeElement.focus();
            this.inputRef.nativeElement.click();
        }, 10);
    }
    // Scrolls the active option into view.
    scrollTo() {
        const itemToScrollTo = this._keyManager?.activeItem?.element;
        if (itemToScrollTo) {
            itemToScrollTo.scrollIntoView(true);
        }
    }
    onBlur() {
        if (!this._isRefocusingForAria) {
            if (this.panelPopup.showing) {
                this.closePanel();
                setTimeout(() => {
                    this._announceSelectedOnClose();
                }, 400);
            }
            this.blur.emit();
            // TODO: formRef is deprecated. Should be removed at some point.
            this.formRef?.validate();
        }
    }
    // #endregion
    // #region // ------- Keyboard Methods -------- //
    /** Sets up a key manager to listen to keyboard events on the overlay panel. */
    _initKeyManager() {
        this._keyManager = new ActiveDescendantKeyManager(this.options)
            .withVerticalOrientation()
            .withWrap()
            .withTypeAhead()
            .withHomeAndEnd()
            .withAllowedModifierKeys(['shiftKey']);
        // subscribe to changes of active item
        this._keyManager.change.subscribe({
            next: indx => {
                this._displayTextInDropdown();
                this.selectionChange.emit(this._keyManager?.activeItem?.value);
                // scroll into view
                this.scrollTo();
                // announce after typing characters
                if (this._keyManager.isTyping()) {
                    setTimeout(() => {
                        this._announceSelectInOrder();
                    }, 0);
                }
            }
        });
        // subscribes to when the option is clicked with mouse
        this.dropdownService.clickedOptionValue$.subscribe({
            next: val => {
                const opt = this._getOptionInfoFromValue(val);
                this._keyManager.setActiveItem(opt.index);
                this.closePanel();
                setTimeout(() => {
                    this._announceSelectedOnClose();
                }, 0);
            }
        });
    }
    _displayTextInDropdown() {
        this.selectedOption = this._keyManager.activeItem;
        this.value = this._keyManager?.activeItem?.value;
        this.selected = this.value; // <- this.selected is DEPRECATED.  Should be removed at some future time.
        this._placeholder = this._keyManager?.activeItem?.label ?? this._placeholderStatic ?? '';
        this.changeRef.markForCheck();
        this.checkValue();
        this.onChange();
    }
    closeOnEsc() {
        this.closePanel();
        setTimeout(() => {
            this._announceSelectedOnClose();
        }, 0);
    }
    /** Handles all keydown events on the dropdown. */
    _handleKeydown(event) {
        if (!this.disabled) {
            this.panelPopup.showing
                ? this._handelOpenKeydown(event)
                : this._handelClosedKeydown(event);
        }
    }
    /** Handles keyboard events when the selected is open. */
    _handelOpenKeydown(event) {
        const manager = this._keyManager;
        const keyCode = event.keyCode;
        const isArrowKey = keyCode === DOWN_ARROW ||
            keyCode === UP_ARROW ||
            keyCode === RIGHT_ARROW ||
            keyCode === LEFT_ARROW;
        const isCloseKey = keyCode === ENTER || keyCode === SPACE;
        const isHomeEndKey = keyCode === HOME || keyCode === END;
        manager.onKeydown(event);
        if (this.panelPopup.showing) {
            if (isArrowKey || isHomeEndKey) {
                event.preventDefault();
                setTimeout(() => {
                    this._announceSelectInOrder();
                }, 0);
            }
            if (isCloseKey) {
                event.preventDefault();
                this.closePanel();
                setTimeout(() => {
                    this._announceSelectedOnClose();
                }, 0);
            }
        }
    }
    /** Handles keyboard events while the dropdown is closed. */
    _handelClosedKeydown(event) {
        const keyCode = event.keyCode;
        this._keyManager.onKeydown(event);
        if (this.disabled)
            return;
        if (!this.panelPopup.showing) {
            if (keyCode === HOME || keyCode === END) {
                event.preventDefault();
                setTimeout(() => {
                    this._announceSelectInOrder();
                }, 0);
            }
            if ((event.altKey && keyCode === DOWN_ARROW) ||
                keyCode === UP_ARROW ||
                keyCode === DOWN_ARROW || keyCode === ENTER || keyCode === SPACE) {
                event.preventDefault();
                this._isRefocusingForAria = true;
                const otherEl = this.elementRef.nativeElement.querySelector('.cds-dd-other-focus');
                otherEl.focus();
                this.openPanel();
                setTimeout(() => {
                    this.inputRef.nativeElement.focus();
                    this._isRefocusingForAria = false;
                    setTimeout(() => {
                        this._announceOnOpen();
                    }, 0);
                }, 200);
            }
        }
    }
    // upon touching the element, this method gets triggered
    registerOnTouched(fn) {
        this.onTouchedFn = fn;
    }
    // upon UI element value changes, this method gets triggered
    registerOnChange(fn) {
        this.onChangeFn = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
        this.changeRef.detectChanges();
    }
    /**
     * @description Allows Angular to update the model. The form will pass new values in here
     */
    writeValue(value) {
        this._value = value;
        this.selected = value; // <- this.selected is DEPRECATED.  Should be removed at some future time.
        this._setActiveItemAndPlaceholder(value);
    }
    onTouched() {
        this.onTouchedFn();
    }
    onChange() {
        this.onChangeFn(this.value);
    }
    focusChanged(isFocused) {
        if (this.elementRef.nativeElement.hasAttribute('disabled'))
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown, deps: [{ token: i3.Overlay }, { token: i0.ElementRef }, { token: DropdownService }, { token: i1$1.LiveAnnouncer }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: 'id', attribute: true }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsDropdown, selector: "cds-dropdown", inputs: { disabled: "disabled", formRef: "formRef", placeholder: "placeholder", selected: "selected", idStr: "idStr", required: "required", value: "value", optionsClass: "optionsClass" }, outputs: { selectionChange: "selectionChange", panelClose: "panelClose", blur: "blur" }, host: { attributes: { "role": "presentation" }, listeners: { "keydown": "_handleKeydown($event)", "click": "toggleDropdown($event)", "focusin": "focusChanged(true)", "focusout": "focusChanged(false)", "input": "checkValue()", "keydown.escape": "closeOnEsc()" }, properties: { "class.cds-dropdown-error": "this.isInErrorState" }, classAttribute: "cds-dropdown" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsDropdown)),
                multi: true
            },
            DropdownService,
            { provide: CdsFormBase, useExisting: CdsDropdown }
        ], queries: [{ propertyName: "option", first: true, predicate: ["option"], descendants: true }, { propertyName: "options", predicate: CdsOption }], viewQueries: [{ propertyName: "panelPopup", first: true, predicate: ["panelPopup"], descendants: true }, { propertyName: "panel", first: true, predicate: ["panel"], descendants: true }, { propertyName: "inputRef", first: true, predicate: ["inputRef"], descendants: true }, { propertyName: "dropdownArrow", first: true, predicate: ["dropdownArrow"], descendants: true }], ngImport: i0, template: `
  <div>
    <input
      class="cds-dd-input"
      #inputRef
      [attr.aria-expanded]="isPanelOpen"
      [attr.aria-required]="required"
      aria-autocomplete="none"
      role="combobox"
      [disabled]="disabled"
      [(ngModel)]="placeholder"
      placeholder="{{ placeholder }}"
      readonly
      cdsOverlayPanel
      #panelPopup="cdsOverlayPanel"
      [templateRef]="tmpl"
      (blur)="onTouched(); onBlur()"
      (backdropClick)="onPanelClose($event)"
      [attr.id]="idStr"
    />
  </div>
  <div class="cds-dd-other-focus" tabindex="-1" role="none"></div>

    <span
      [attr.disabled]="disabled"
      [ngClass]="disabled ? 'cds-dropdown-disabled' : null"
      aria-hidden="true"
      (click)="toggleDropdownIcon($event)"
    >
      <svg
        class="dropdown-arrow"
        #dropdownArrow
        [@rotatedState]="isPanelOpen ? 'rotated' : 'default'"
        version="1.1"
        width="14px"
        height="9px"
        viewBox="0 0 14 9"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" fill-rule="evenodd">
          <path
            fill="#056DAE"
            d="m7 8.6724c-0.531 0-1.046-0.227-1.413-0.62l-5.319-5.719c-0.376-0.404-0.353-1.037 0.051-1.413 0.406-0.375 1.037-0.352 1.413 0.052l5.268 5.662 5.268-5.662c0.378-0.404 1.009-0.426 1.413-0.052 0.405 0.376 0.427 1.009 0.051 1.413l-5.318 5.718c-0.368 0.394-0.883 0.621-1.414 0.621"
          />
        </g>
      </svg>
    </span>

    <!--options content-->
    <ng-template #tmpl>
      <ng-content></ng-content>
    </ng-template>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: CdsOverlayPanel, selector: "[cdsOverlayPanel]", inputs: ["templateRef", "positionStrategy", "scrollStrategy"], outputs: ["backdropClick"], exportAs: ["cdsOverlayPanel"] }, { kind: "directive", type: i1$3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }], animations: [
            // Each unique animation requires its own trigger. The first argument of the trigger function is the name
            trigger('rotatedState', [
                state('default', style({ transform: 'rotate(0)' })),
                state('rotated', style({ transform: 'rotate(-180deg)' })),
                transition('rotated => default', animate('200ms ease-out')),
                transition('default => rotated', animate('200ms ease-in'))
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-dropdown',
                    template: `
  <div>
    <input
      class="cds-dd-input"
      #inputRef
      [attr.aria-expanded]="isPanelOpen"
      [attr.aria-required]="required"
      aria-autocomplete="none"
      role="combobox"
      [disabled]="disabled"
      [(ngModel)]="placeholder"
      placeholder="{{ placeholder }}"
      readonly
      cdsOverlayPanel
      #panelPopup="cdsOverlayPanel"
      [templateRef]="tmpl"
      (blur)="onTouched(); onBlur()"
      (backdropClick)="onPanelClose($event)"
      [attr.id]="idStr"
    />
  </div>
  <div class="cds-dd-other-focus" tabindex="-1" role="none"></div>

    <span
      [attr.disabled]="disabled"
      [ngClass]="disabled ? 'cds-dropdown-disabled' : null"
      aria-hidden="true"
      (click)="toggleDropdownIcon($event)"
    >
      <svg
        class="dropdown-arrow"
        #dropdownArrow
        [@rotatedState]="isPanelOpen ? 'rotated' : 'default'"
        version="1.1"
        width="14px"
        height="9px"
        viewBox="0 0 14 9"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g fill="none" fill-rule="evenodd">
          <path
            fill="#056DAE"
            d="m7 8.6724c-0.531 0-1.046-0.227-1.413-0.62l-5.319-5.719c-0.376-0.404-0.353-1.037 0.051-1.413 0.406-0.375 1.037-0.352 1.413 0.052l5.268 5.662 5.268-5.662c0.378-0.404 1.009-0.426 1.413-0.052 0.405 0.376 0.427 1.009 0.051 1.413l-5.318 5.718c-0.368 0.394-0.883 0.621-1.414 0.621"
          />
        </g>
      </svg>
    </span>

    <!--options content-->
    <ng-template #tmpl>
      <ng-content></ng-content>
    </ng-template>
  `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsDropdown)),
                            multi: true
                        },
                        DropdownService,
                        { provide: CdsFormBase, useExisting: CdsDropdown }
                    ],
                    changeDetection: ChangeDetectionStrategy.OnPush,
                    host: {
                        class: 'cds-dropdown',
                        role: 'presentation',
                        '(keydown)': '_handleKeydown($event)',
                        '(click)': 'toggleDropdown($event)',
                        '(focusin)': 'focusChanged(true)',
                        '(focusout)': 'focusChanged(false)',
                        '(input)': 'checkValue()'
                    },
                    // encapsulation: ViewEncapsulation.None,
                    animations: [
                        // Each unique animation requires its own trigger. The first argument of the trigger function is the name
                        trigger('rotatedState', [
                            state('default', style({ transform: 'rotate(0)' })),
                            state('rotated', style({ transform: 'rotate(-180deg)' })),
                            transition('rotated => default', animate('200ms ease-out')),
                            transition('default => rotated', animate('200ms ease-in'))
                        ])
                    ]
                }]
        }], ctorParameters: () => [{ type: i3.Overlay }, { type: i0.ElementRef }, { type: DropdownService }, { type: i1$1.LiveAnnouncer }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }], propDecorators: { isInErrorState: [{
                type: HostBinding,
                args: ['class.cds-dropdown-error']
            }], panelPopup: [{
                type: ViewChild,
                args: ['panelPopup']
            }], panel: [{
                type: ViewChild,
                args: ['panel']
            }], inputRef: [{
                type: ViewChild,
                args: ['inputRef']
            }], dropdownArrow: [{
                type: ViewChild,
                args: ['dropdownArrow']
            }], option: [{
                type: ContentChild,
                args: ['option']
            }], options: [{
                type: ContentChildren,
                args: [CdsOption]
            }], selectionChange: [{
                type: Output
            }], panelClose: [{
                type: Output
            }], blur: [{
                type: Output
            }], disabled: [{
                type: Input
            }], formRef: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], selected: [{
                type: Input
            }], idStr: [{
                type: Input
            }], required: [{
                type: Input
            }], value: [{
                type: Input
            }], optionsClass: [{
                type: Input
            }], closeOnEsc: [{
                type: HostListener,
                args: ['keydown.escape']
            }] } });

const COMPONENTS$r = [CdsDropdown, CdsOption];
const MODULES$b = [CommonModule, OverlayPanelModule, FormsModule];
class CdsDropdownModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownModule, declarations: [CdsDropdown, CdsOption], imports: [CommonModule, OverlayPanelModule, FormsModule, ReactiveFormsModule], exports: [CdsDropdown, CdsOption] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownModule, imports: [MODULES$b, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$r],
                    imports: [...MODULES$b, ReactiveFormsModule],
                    exports: [...COMPONENTS$r]
                }]
        }] });

class CdsDropdown2Service {
    constructor() {
        this.currentDropdown2Value$ = new BehaviorSubject(null);
        this.optionClick$ = new Subject();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Service, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Service, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Service, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _optionUniqueIdCounter$1 = 1;
class CdsOption2Component {
    get element() {
        return this.host.nativeElement;
    }
    set label(lbl) {
        // Converts html entities (such as &reg; into text)
        const tempEl = this._document.createElement('span');
        tempEl.innerHTML = lbl || '';
        this._label = tempEl.textContent;
    }
    get label() {
        return this._label;
    }
    // #endregion
    // #region =========== HostBinding ========== //
    get _id() {
        return this.id;
    }
    get _hostIsDisabled() {
        return this.isDisabled ? true : null;
    }
    get _hostIsSelected() {
        // for some reason, when aria-disable is true, it also reads as "selected",
        // even when aria-selected is not present.  By setting aria-selected to "false"
        // when it is disabled, it prevents it from reading as "selected"
        return this.isDisabled ? false : this._isSelected;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor(host, liveAnnouncer, dropdownService, _doc) {
        this.host = host;
        this.liveAnnouncer = liveAnnouncer;
        this.dropdownService = dropdownService;
        this._doc = _doc;
        // #endregion
        // #region =========== @Input ========== //
        this.id = `cds-option2-${_optionUniqueIdCounter$1++}`;
        this.sectionTypeAnnounceText = ' grouping';
        this._label = '';
        // #endregion
        // #region =========== @Output ========== //
        /**
         * Emits value
         * @deprecated
         */
        this.click = new EventEmitter();
        /**
         * Emits label
         * @deprecated
         */
        this.ddValue = new EventEmitter();
        this._document = _doc;
    }
    ngOnInit() {
        this.dropdownService?.currentDropdown2Value$?.subscribe(val => {
            this._isSelected = this._isActive = val === this.value ? true : null;
        });
        this._isSelected = this._isActive = null;
    }
    // #endregion
    // #region =========== HostListeners ========== //
    optionClick(e) {
        if (!this.isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            this.emitValues();
            // console.log('option mousedown', this.value, this.label);
            this._isSelected = !this._isSelected;
            this._isActive = !this._isActive;
            // lets service know when option is clicked and passes value
            this.dropdownService.optionClick$.next(this.value);
        }
    }
    // #endregion
    // #region =========== Methods ========== //
    isDisabledFunction() {
        return this.isDisabled;
    }
    /**
     * Previous functionality from original CDS Dropdown.
     * Should be deprecated.
     * They should use events emitted from dropdown instead.
     */
    emitValues() {
        this.click.emit(this.value);
        this.ddValue.emit(this.label);
    }
    // #endregion
    // #region =========== Highlightable ========== //
    /**
     * This method sets display styles on the option to make it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setActiveStyles() {
        // TODO: what about disabled?
        this._isActive = this.isDisabled ? false : true;
    }
    /**
     * This method removes display styles on the option that made it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setInactiveStyles() {
        this._isActive = null;
    }
    /** Gets the label to be used when determining whether the option should be focused. */
    getLabel() {
        return this.label;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOption2Component, deps: [{ token: i0.ElementRef }, { token: i1$1.LiveAnnouncer }, { token: CdsDropdown2Service }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsOption2Component, selector: "li[cdsOption2]", inputs: { id: "id", value: "value", sectionTypeAnnounceText: "sectionTypeAnnounceText", label: "label", isDisabled: "isDisabled", groupName: "groupName" }, outputs: { click: "click", ddValue: "ddValue" }, host: { attributes: { "role": "option" }, listeners: { "mousedown": "optionClick($event)" }, properties: { "attr.id": "this._id", "attr.aria-disabled": "this._hostIsDisabled", "class.cds-option2-disabled": "this._hostIsDisabled", "class.cds-option2-active": "this._isActive", "class.cds-option2-selected": "this._isSelected", "attr.aria-selected": "this._hostIsSelected" }, classAttribute: "cds-option2" }, ngImport: i0, template: `
  <div class="cds-option2-item-container">
    <span *ngIf="groupName" class="sr-only">{{groupName}} {{sectionTypeAnnounceText}}.</span>
    {{label}}
    <cds-icon
      *ngIf="_isSelected"
      altText=""
      type="2021"
      name="059-check"
      [svgStyle]="{'width.px':22,'height.px':16}"
    ></cds-icon>
  </div>`, isInline: true, styles: [":host{padding:8px 16px;line-height:24px;cursor:default}:host.cds-option2{border:2px solid rgba(0,42,84,0);border-radius:4px}:host.cds-option2.cds-option2-active{border:2px solid #002A54}:host.cds-option2-selected{color:#056dae;font-weight:700}:host.cds-option2-disabled{color:#ccc}:host-context(.cds-dropdown2-sm){padding:5px 16px}.cds-option2-item-container{display:flex;justify-content:space-between}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOption2Component, decorators: [{
            type: Component,
            args: [{ selector: 'li[cdsOption2]', template: `
  <div class="cds-option2-item-container">
    <span *ngIf="groupName" class="sr-only">{{groupName}} {{sectionTypeAnnounceText}}.</span>
    {{label}}
    <cds-icon
      *ngIf="_isSelected"
      altText=""
      type="2021"
      name="059-check"
      [svgStyle]="{'width.px':22,'height.px':16}"
    ></cds-icon>
  </div>`, host: {
                        class: 'cds-option2',
                        role: 'option'
                    }, styles: [":host{padding:8px 16px;line-height:24px;cursor:default}:host.cds-option2{border:2px solid rgba(0,42,84,0);border-radius:4px}:host.cds-option2.cds-option2-active{border:2px solid #002A54}:host.cds-option2-selected{color:#056dae;font-weight:700}:host.cds-option2-disabled{color:#ccc}:host-context(.cds-dropdown2-sm){padding:5px 16px}.cds-option2-item-container{display:flex;justify-content:space-between}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1$1.LiveAnnouncer }, { type: CdsDropdown2Service }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { id: [{
                type: Input
            }], value: [{
                type: Input
            }], sectionTypeAnnounceText: [{
                type: Input
            }], label: [{
                type: Input
            }], isDisabled: [{
                type: Input
            }], groupName: [{
                type: Input
            }], click: [{
                type: Output
            }], ddValue: [{
                type: Output
            }], _id: [{
                type: HostBinding,
                args: ['attr.id']
            }], _hostIsDisabled: [{
                type: HostBinding,
                args: ['attr.aria-disabled']
            }, {
                type: HostBinding,
                args: ['class.cds-option2-disabled']
            }], _isActive: [{
                type: HostBinding,
                args: ['class.cds-option2-active']
            }], _isSelected: [{
                type: HostBinding,
                args: ['class.cds-option2-selected']
            }], _hostIsSelected: [{
                type: HostBinding,
                args: ['attr.aria-selected']
            }], optionClick: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

;
/**
 * Dropdown IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _dropdown2UniqueIdCounter = 1;
class CdsDropdown2Component {
    set isInErrorState(isErr) {
        this._inErrorState = isErr;
        this.showErrorChange.emit(isErr);
        this._changeRef.markForCheck();
    }
    get isInErrorState() {
        return this._inErrorState;
    }
    set value(val) {
        // if same selection was chosen break out of function
        if (this._value === val) {
            return;
        }
        this._value = val;
        this._dropdown2Service?.currentDropdown2Value$?.next(val);
        this._updateDisplayText();
        this.onChange(val);
        this.hasValueChange.next(val);
        // Don't emit until after content is initialized
        if (this._isContentInitialized) {
            this.selectionChange.emit(val);
        }
        if (!val && this._keyManager?.activeItem) {
            this._keyManager.setActiveItem(null);
        }
    }
    get value() {
        return this._value;
    }
    // #endregion
    // #region =========== @Input ========== //
    set idStr(val) {
        this._id = val || this._uid;
        this.fieldId = this._id;
    }
    get idStr() {
        const id = this._id || this._uid;
        return id;
    }
    /**
     * Sets the error state.
     * This should only be used for Template based forms (not reactive forms)
     */
    set showError(isError) {
        this._inErrorState = isError;
    }
    get showError() {
        return this._inErrorState;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor(announcer, _el, _dropdown2Service, _changeRef, _doc) {
        this.announcer = announcer;
        this._el = _el;
        this._dropdown2Service = _dropdown2Service;
        this._changeRef = _changeRef;
        this._doc = _doc;
        // #region =========== CdsFormBase ========== //
        this.fieldType = 'dropdown';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        this._wrapperPadding = 8;
        this._eventUnsub$ = new Subject();
        this._destroyUnsub$ = new Subject();
        this._inErrorState = false;
        this._uid = `CdsDropdown2_${_dropdown2UniqueIdCounter++}`;
        /**
         * Defines aria-describedby
         */
        this.ariaDescribedby = '';
        /**
         * Defines aria-labelledby attribute
         * You may consider adding the id of the `<label>`
         */
        this.ariaLabelledby = '';
        this.hasSectionsAnnounceText = 'This dropdown  has multiple sections. ';
        this.sectionTypeAnnounceText = ' grouping';
        // #endregion
        // #region ========== @Outputs ========== //
        /**
         * The event is emitted when the value is changed
         * The event is returned.
         */
        this.selectionChange = new EventEmitter();
        /**
         * The event is emitted when the dropdown is closed
         */
        this.panelClose = new EventEmitter();
        /**
         * The event is emitted when the dropdown is blurred
         */
        this.blur = new EventEmitter();
        /**
         * The event is emitted when the error state changes
         */
        this.showErrorChange = new EventEmitter();
        // #endregion
        // #region =========== ControlValueAccessor =========== //
        this.onChange = (_) => { };
        this.onTouch = () => { };
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    ngOnInit() {
        // Initialize fieldId
        this.fieldId = this.idStr;
    }
    ngAfterContentInit() {
        this._initKeyManager();
        this._updateDisplayText();
        this._isContentInitialized = true;
        this._dropdown2Service.optionClick$.subscribe(val => {
            this.value = val;
            this.close();
        });
    }
    ngAfterViewInit() {
        // set listbox max-height and change if window is resized
        this._setValidMaxHeight();
        fromEvent(this._window, 'resize')
            .pipe(takeUntil(this._destroyUnsub$), debounceTime(10))
            .subscribe(obv => {
            if (this.listMaxHeight) {
                this._setValidMaxHeight();
            }
        });
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            const change = simpleChanges[propName];
            switch (propName) {
                case 'placeholder':
                    this._updateDisplayText();
                    break;
                case 'listMaxHeight':
                    if (!change.isFirstChange()) {
                        this._setValidMaxHeight();
                    }
                    break;
            }
        }
    }
    ngOnDestroy() {
        this._eventUnsub$.next();
        this._eventUnsub$.complete();
        this._destroyUnsub$.next();
        this._destroyUnsub$.complete();
    }
    // #endregion
    // #region =========== Methods =========== //
    /**
     * Toggles open the listbox
     */
    toggleOpen() {
        if (this._isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }
    /**
     * Opens cds dropdown2 component, scrolls to the active option, and focuses on listbox
     */
    open() {
        // Sets display to block, but still not visible.
        // This allows the code to get the dimensions of the listbox before making it visible.
        this._isOpen = true;
        // on open, sets the currently selected option to the "active" option
        const currentOptionInfo = this._getOptionInfoFromValue(this.value);
        if (currentOptionInfo) {
            this._keyManager.setActiveItem(currentOptionInfo.index);
        }
        else {
            this._keyManager.setFirstItemActive();
        }
        if (this.hasSections) {
            // NOTE: announcing the groupName is not necessary since I changed how it announces in the cdsOptions2 child component
            this.announcer.announce(this.hasSectionsAnnounceText /* + this._keyManager?.activeItem.groupName + this.sectionTypeAnnounceText */);
        }
        setTimeout(() => {
            // sets size and position of listbox
            this._repositionListbox();
            // when scroll or resize events happen, listbox is resized/repositioned
            const fromScroll$ = fromEvent(this._window, 'scroll');
            const fromResize$ = fromEvent(this._window, 'resize');
            const scrollParents$ = getScrollParents(this.listbox.nativeElement)
                .filter(el => el?.nodeName?.toLowerCase() !== 'html')
                .map(elem => fromEvent(elem, 'scroll'));
            merge(fromResize$, fromScroll$, ...scrollParents$)
                .pipe(takeUntil(this._eventUnsub$), debounceTime(10))
                .subscribe(obv => {
                this._repositionListbox();
            });
            // Scroll to active item after open
            const listboxEl = this.listbox.nativeElement;
            const activeEl = this._keyManager?.activeItem?.element;
            if (activeEl) {
                listboxEl.scrollTop = activeEl.offsetTop - this._wrapperPadding;
            }
            else {
                listboxEl.scrollTop = 0;
            }
            // Sets the listbox to visible after getting dimensions and setting the listbox in the correct position.
            this._isVisible = true;
            // focus on list after visible
            setTimeout(() => {
                this.listbox.nativeElement.focus();
            }, 0);
        }, 0);
    }
    /**
     * Closes cds dropdown2 listbox
     * @param [isRefocusOnButtonSkipped] when the listbox is closed, this prevents it from focusing back on the button
     */
    close(isRefocusOnButtonSkipped) {
        // do nothing if already closed
        if (!this._isOpen) {
            return;
        }
        // No need to reposition while closed, unsubscribing from event observable
        this._eventUnsub$.next();
        setTimeout(() => {
            this._isOpen = false;
            this._isVisible = false;
            this.panelClose.emit(this.value);
            // focus back on button if needed
            if (!isRefocusOnButtonSkipped) {
                this.listboxButton.nativeElement.focus();
            }
            this._changeRef.markForCheck();
        }, 0);
    }
    /**
     * Finds the CdsOption from the value
     * @param val value of option
     */
    _getOptionInfoFromValue(val) {
        const opt = this.options?.find(o => o.value === val);
        let optInfo;
        if (opt) {
            optInfo = {
                option: opt,
                label: opt.label,
                value: opt.value,
                index: this.options.toArray().indexOf(opt)
            };
        }
        return optInfo;
    }
    _updateDisplayText() {
        const currentOptionInfo = this._getOptionInfoFromValue(this.value);
        this._isPlaceholder = !this.value;
        this._displayText = currentOptionInfo?.label || this.placeholder || '';
        this._changeRef.markForCheck();
    }
    /**
     * Finds out if focus truly leaves the dropdown component.
     * @param e FocusEvent
     */
    _focusoutDropdown(e) {
        const isInDropdown = this._el.nativeElement.contains(e.relatedTarget);
        if (!isInDropdown) {
            this._isFocused = false;
            this.focusChange.next(this._isFocused);
            this.close(!isInDropdown);
            this.onTouch();
            this.blur.emit();
        }
    }
    _focusinDropdown(e) {
        if (!this._isFocused) {
            this._isFocused = true;
            this.focusChange.next(this._isFocused);
        }
    }
    /**
     * Handles keyboard events when focus is on the button (mainly to open the listbox)
     * @param e KeyboardEvent
     */
    _keydownButtonHandler(e) {
        const keyCode = e.keyCode || e.which || 0;
        // if alt+arrowdown: open
        // deque opens dropdown with simple ArrowDown (even without alt key)
        if (keyCode === DOWN_ARROW) {
            e.preventDefault();
            this.open();
        }
    }
    /**
     * Handles keyboard events when the listbox is open and in focus
     * @param e KeyboardEvent
     */
    _keydownListHandler(e) {
        const keyCode = e.keyCode || e.which || 0;
        // passes event to keyManager
        this._keyManager.onKeydown(e);
        if (this._keyManager?.activeItem.isDisabledFunction()) {
            this._keyManager?.activeItem.setInactiveStyles();
        }
        // NOTE: this no longer needs to be triggered since I changed the way it works in the cdsOption2 component
        // if (this.hasSections) {
        //   this._keyManager?.activeItem.readSectionName();
        // }
        // functionality that the keyManager does not do
        switch (keyCode) {
            // escape closes without change
            case ESCAPE:
                e.preventDefault();
                e.stopPropagation();
                this.close();
                break;
            // enter/space selects the currently active item and closes
            case ENTER:
            case SPACE:
                if (!this._keyManager?.activeItem.isDisabledFunction()) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.value = this._keyManager?.activeItem?.value;
                    this.close();
                }
                break;
        }
    }
    /**
     * Initializes the ActiveDescendantKeyManager
     */
    _initKeyManager() {
        this._keyManager = new ActiveDescendantKeyManager(this.options)
            .withVerticalOrientation()
            .withAllowedModifierKeys(['shiftKey'])
            .withHomeAndEnd()
            .withTypeAhead().skipPredicate((item) => item.isDisabled);
        // subscribe to changes of active item
        this._keyManager.change.subscribe({
            next: indx => {
                // sets the aria-activedescendant id
                this._activeDescendantId = this._keyManager?.activeItem?.id;
                // scroll active item into view if needed
                const listboxEl = this.listbox.nativeElement;
                const activeEl = this._keyManager?.activeItem?.element;
                this._scrollToIfNeeded(listboxEl, activeEl);
                this._changeRef.markForCheck();
            }
        });
    }
    /**
     * Scrolls the item into view if needed.  Does not scroll if item already in view.
     * @param wrapperEl The wrapper element that contains the scrollable elements
     * @param itemEl The item that needs to scroll into view
     */
    _scrollToIfNeeded(wrapperEl, itemEl) {
        if (!itemEl) {
            if (!!wrapperEl) {
                wrapperEl.scrollTop = 0;
            }
            return;
        }
        const wrapTop = wrapperEl.scrollTop + this._wrapperPadding;
        const wrapBottom = wrapTop + wrapperEl.offsetHeight;
        const itemTop = itemEl.offsetTop;
        const itemBottom = itemTop + itemEl.offsetHeight;
        if (itemTop < wrapTop) {
            wrapperEl.scrollTop = itemTop - this._wrapperPadding;
        }
        else if (itemBottom > wrapBottom) {
            wrapperEl.scrollTop = itemBottom - wrapperEl.offsetHeight - this._wrapperPadding;
        }
    }
    /**
     * Repositions listbox - find out if dropdown is above/below middle of view, then add/remove class so dropdown can be repositioned.
     */
    _repositionListbox() {
        const viewportHeight = this._window?.innerHeight;
        const viewportMidpoint = Math.round(viewportHeight / 2);
        const buttonRect = this.listboxButton?.nativeElement?.getBoundingClientRect();
        const buttonMidpoint = Math.round(buttonRect.top + (buttonRect.height / 2));
        const listboxRect = this.listbox?.nativeElement?.getBoundingClientRect();
        this._isPlacedAbove = buttonRect.bottom + listboxRect.height - 8 > viewportHeight;
        // Set width of listbox
        this._listboxWidth = buttonRect?.width;
        // Set left position of listbox
        this._listboxLeft = buttonRect?.left;
        // Set top position of listbox
        if (this._isPlacedAbove) {
            this._listboxTop = buttonRect.top - listboxRect.height - (this._wrapperPadding * 2);
        }
        else {
            this._listboxTop = buttonRect.bottom;
        }
        this._changeRef.markForCheck();
    }
    _getValidMaxHeight(maxHeight) {
        const viewportHeight = this._window?.innerHeight;
        const buttonRect = this.listboxButton?.nativeElement?.getBoundingClientRect();
        const buttonHeight = buttonRect.height;
        const maxAvailableViewportHeight = viewportHeight - buttonHeight;
        const buttonDistanceFromTop = Math.round(buttonRect.top + this._window.scrollY);
        const buttonDistanceFromBottom = this._document.body.clientHeight - buttonHeight - buttonDistanceFromTop;
        const buttonDistanceMax = Math.max(buttonDistanceFromTop, buttonDistanceFromBottom);
        const distanceVsViewportMin = Math.min(buttonDistanceMax, maxAvailableViewportHeight);
        if (maxHeight > distanceVsViewportMin) {
            return distanceVsViewportMin - 20;
        }
        return maxHeight;
    }
    _setValidMaxHeight() {
        if (this.listMaxHeight) {
            this._listMaxHeight = this._getValidMaxHeight(this.listMaxHeight);
        }
        else {
            this._listMaxHeight = null;
        }
        this._changeRef.detectChanges();
    }
    writeValue(value) {
        this.value = value;
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this._isDisabled = isDisabled;
        this.disabledChange.next(isDisabled);
        this._changeRef.markForCheck();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Component, deps: [{ token: i1$1.LiveAnnouncer }, { token: i0.ElementRef }, { token: CdsDropdown2Service }, { token: i0.ChangeDetectorRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsDropdown2Component, selector: "cds-dropdown2", inputs: { idStr: "idStr", placeholder: "placeholder", ariaDescribedby: "ariaDescribedby", ariaLabelledby: "ariaLabelledby", showError: "showError", required: "required", listMaxHeight: "listMaxHeight", hasSectionsAnnounceText: "hasSectionsAnnounceText", sectionTypeAnnounceText: "sectionTypeAnnounceText", hasSections: "hasSections" }, outputs: { selectionChange: "selectionChange", panelClose: "panelClose", blur: "blur", showErrorChange: "showErrorChange" }, host: { listeners: { "focusout": "_focusoutDropdown($event)", "focusin": "_focusinDropdown($event)" }, properties: { "class.cds-dd2-error": "this._inErrorState" }, classAttribute: "cds-dropdown2" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsDropdown2Component)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsDropdown2Component },
            CdsDropdown2Service
        ], queries: [{ propertyName: "options", predicate: CdsOption2Component, descendants: true }], viewQueries: [{ propertyName: "listboxButton", first: true, predicate: ["listboxButton"], descendants: true }, { propertyName: "listbox", first: true, predicate: ["listbox"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <button
      type="button"
      #listboxButton
      [attr.id]="idStr"
      class="cds-dd2-button"
      [class.cds-dd2-button-open]="_isOpen"
      [class.cds-dd2-button-placeholder]="_isPlaceholder"
      [class.cds-dd2-button-error]="_inErrorState"
      [class.cds-dd2-button-focused]="_isFocused"
      role="combobox"
      aria-haspopup="listbox"
      [attr.aria-label]="_isDisabled?_displayText:null"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="ariaDescribedby"
      [attr.aria-expanded]="!!_isOpen"
      [attr.aria-controls]="idStr + '-listbox'"
      (click)="toggleOpen()"
      (keydown)="_keydownButtonHandler($event)"
      [disabled]="_isDisabled"
      [attr.required]="required ? '' : null"
    >
      <div
        [id]="idStr + '-button-value'"
        class="cds-dd2-pseudo-value"
      >
        {{ _displayText || '&nbsp;' }}
      </div>
    </button>
    <div
    class="cds-dd2-options-container"
    [style.display]="_isOpen ? 'block' : 'none'"
    [style.visibility]="_isVisible ? 'visible' : 'hidden'"
    >
      <div
        class="cds-dd2-options"
        [style.width.px]="_listboxWidth"
        [style.top.px]="_listboxTop"
        [style.left.px]="_listboxLeft"
      >
        <ul
          #listbox
          [id]="idStr + '-listbox'"
          class="cds-dd2-listbox"
          role="listbox"
          tabindex="-1"
          (keydown)="_keydownListHandler($event)"
          [attr.aria-activedescendant]="_activeDescendantId"
          [style.maxHeight.px]="_listMaxHeight"
        >
          <ng-content></ng-content>
        </ul>
      </div>
    </div>
  `, isInline: true, styles: [":host{position:relative;width:100%;display:block}.cds-dropdown2 h6{font-weight:700;margin-top:16px;margin-bottom:16px;margin-left:16px}.cds-dd2-border-top{border-top:1px solid #cccccc;margin-top:20px}.cds-dd2-button{color:#056dae;font-weight:700;border:1px solid #bbb;border-radius:8px;padding:0 40px 0 16px;background-color:#fff;width:100%;text-align:left;line-height:46px;outline:none}.cds-dd2-button:after{content:\"\";width:24px;height:48px;position:absolute;top:0;right:16px;background-repeat:no-repeat;background-position:right center;background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23056dae%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\");transition:transform .2s}.cds-dd2-button.cds-dd2-button-open{border-color:#056dae}.cds-dd2-button:focus:not([disabled]),.cds-dd2-button:active:not([disabled]),.cds-dd2-button.cds-dd2-button-focused:not([disabled]){outline:#002a54 solid 1px;border-radius:8px;outline-offset:1px}.cds-dd2-button.cds-dd2-button-error{border-color:#d60000;color:#d60000}.cds-dd2-button.cds-dd2-button-error:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23d60000%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-dd2-button.cds-dd2-button-error:focus,.cds-dd2-button.cds-dd2-button-error:active,.cds-dd2-button.cds-dd2-button-error.cds-dd2-button-focused{background-color:#ffebeb;outline:#002a54 solid 1px;border-radius:8px;outline-offset:1px}.cds-dd2-button.cds-dd2-button-placeholder{font-family:Interstate_Light;font-style:italic;font-weight:300}.cds-dd2-button.cds-dd2-button-placeholder:not(.cds-dd2-button-open,.cds-dd2-button-error){color:#666}.cds-dd2-button.cds-dd2-button-error{font-style:italic}.cds-dd2-button[disabled]{cursor:default;border-color:#bbb;background-color:#eee;color:#ccc}.cds-dd2-button[disabled]:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23ccc%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-dd2-button-open:after{transform:rotateX(-180deg)}.cds-dd2-pseudo-value{text-overflow:ellipsis;overflow:hidden;white-space:nowrap}.cds-dd2-options-container{display:none;visibility:hidden;position:fixed;top:0;left:0;z-index:1000;pointer-events:none;width:100%;height:100%}.cds-dd2-options{position:absolute;pointer-events:auto;padding:8px 0;background-color:#fff;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-dd2-listbox{list-style-type:none;overflow-y:auto;max-height:240px;margin:0;padding:0;outline:none}:host.cds-dropdown2-sm .cds-dd2-button{line-height:30px;padding-right:32px}:host.cds-dropdown2-sm .cds-dd2-button:after{height:32px;right:8px}:host.cds-dropdown2-sm .cds-dd2-listbox{max-height:238px}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Component, decorators: [{
            type: Component,
            args: [{ selector: 'cds-dropdown2', template: `
    <button
      type="button"
      #listboxButton
      [attr.id]="idStr"
      class="cds-dd2-button"
      [class.cds-dd2-button-open]="_isOpen"
      [class.cds-dd2-button-placeholder]="_isPlaceholder"
      [class.cds-dd2-button-error]="_inErrorState"
      [class.cds-dd2-button-focused]="_isFocused"
      role="combobox"
      aria-haspopup="listbox"
      [attr.aria-label]="_isDisabled?_displayText:null"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="ariaDescribedby"
      [attr.aria-expanded]="!!_isOpen"
      [attr.aria-controls]="idStr + '-listbox'"
      (click)="toggleOpen()"
      (keydown)="_keydownButtonHandler($event)"
      [disabled]="_isDisabled"
      [attr.required]="required ? '' : null"
    >
      <div
        [id]="idStr + '-button-value'"
        class="cds-dd2-pseudo-value"
      >
        {{ _displayText || '&nbsp;' }}
      </div>
    </button>
    <div
    class="cds-dd2-options-container"
    [style.display]="_isOpen ? 'block' : 'none'"
    [style.visibility]="_isVisible ? 'visible' : 'hidden'"
    >
      <div
        class="cds-dd2-options"
        [style.width.px]="_listboxWidth"
        [style.top.px]="_listboxTop"
        [style.left.px]="_listboxLeft"
      >
        <ul
          #listbox
          [id]="idStr + '-listbox'"
          class="cds-dd2-listbox"
          role="listbox"
          tabindex="-1"
          (keydown)="_keydownListHandler($event)"
          [attr.aria-activedescendant]="_activeDescendantId"
          [style.maxHeight.px]="_listMaxHeight"
        >
          <ng-content></ng-content>
        </ul>
      </div>
    </div>
  `, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsDropdown2Component)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsDropdown2Component },
                        CdsDropdown2Service
                    ], changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'cds-dropdown2',
                        '(focusout)': '_focusoutDropdown($event)',
                        '(focusin)': '_focusinDropdown($event)'
                    }, styles: [":host{position:relative;width:100%;display:block}.cds-dropdown2 h6{font-weight:700;margin-top:16px;margin-bottom:16px;margin-left:16px}.cds-dd2-border-top{border-top:1px solid #cccccc;margin-top:20px}.cds-dd2-button{color:#056dae;font-weight:700;border:1px solid #bbb;border-radius:8px;padding:0 40px 0 16px;background-color:#fff;width:100%;text-align:left;line-height:46px;outline:none}.cds-dd2-button:after{content:\"\";width:24px;height:48px;position:absolute;top:0;right:16px;background-repeat:no-repeat;background-position:right center;background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23056dae%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\");transition:transform .2s}.cds-dd2-button.cds-dd2-button-open{border-color:#056dae}.cds-dd2-button:focus:not([disabled]),.cds-dd2-button:active:not([disabled]),.cds-dd2-button.cds-dd2-button-focused:not([disabled]){outline:#002a54 solid 1px;border-radius:8px;outline-offset:1px}.cds-dd2-button.cds-dd2-button-error{border-color:#d60000;color:#d60000}.cds-dd2-button.cds-dd2-button-error:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23d60000%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-dd2-button.cds-dd2-button-error:focus,.cds-dd2-button.cds-dd2-button-error:active,.cds-dd2-button.cds-dd2-button-error.cds-dd2-button-focused{background-color:#ffebeb;outline:#002a54 solid 1px;border-radius:8px;outline-offset:1px}.cds-dd2-button.cds-dd2-button-placeholder{font-family:Interstate_Light;font-style:italic;font-weight:300}.cds-dd2-button.cds-dd2-button-placeholder:not(.cds-dd2-button-open,.cds-dd2-button-error){color:#666}.cds-dd2-button.cds-dd2-button-error{font-style:italic}.cds-dd2-button[disabled]{cursor:default;border-color:#bbb;background-color:#eee;color:#ccc}.cds-dd2-button[disabled]:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23ccc%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-dd2-button-open:after{transform:rotateX(-180deg)}.cds-dd2-pseudo-value{text-overflow:ellipsis;overflow:hidden;white-space:nowrap}.cds-dd2-options-container{display:none;visibility:hidden;position:fixed;top:0;left:0;z-index:1000;pointer-events:none;width:100%;height:100%}.cds-dd2-options{position:absolute;pointer-events:auto;padding:8px 0;background-color:#fff;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-dd2-listbox{list-style-type:none;overflow-y:auto;max-height:240px;margin:0;padding:0;outline:none}:host.cds-dropdown2-sm .cds-dd2-button{line-height:30px;padding-right:32px}:host.cds-dropdown2-sm .cds-dd2-button:after{height:32px;right:8px}:host.cds-dropdown2-sm .cds-dd2-listbox{max-height:238px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i0.ElementRef }, { type: CdsDropdown2Service }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { _inErrorState: [{
                type: HostBinding,
                args: ['class.cds-dd2-error']
            }], listboxButton: [{
                type: ViewChild,
                args: ['listboxButton']
            }], listbox: [{
                type: ViewChild,
                args: ['listbox']
            }], options: [{
                type: ContentChildren,
                args: [CdsOption2Component, { descendants: true }]
            }], idStr: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], showError: [{
                type: Input
            }], required: [{
                type: Input
            }], listMaxHeight: [{
                type: Input
            }], hasSectionsAnnounceText: [{
                type: Input
            }], sectionTypeAnnounceText: [{
                type: Input
            }], hasSections: [{
                type: Input
            }], selectionChange: [{
                type: Output
            }], panelClose: [{
                type: Output
            }], blur: [{
                type: Output
            }], showErrorChange: [{
                type: Output
            }] } });

const COMPONENTS$q = [CdsDropdown2Component, CdsOption2Component];
class CdsDropdown2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Module, declarations: [CdsDropdown2Component, CdsOption2Component], imports: [CommonModule, CdsIconModule], exports: [CdsDropdown2Component, CdsOption2Component] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Module, imports: [CommonModule, CdsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdown2Module, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$q],
                    imports: [
                        CommonModule, CdsIconModule
                    ],
                    exports: [...COMPONENTS$q]
                }]
        }] });

class CdsDropdownMultiService {
    constructor() {
        this.currentDropdownMultiValue$ = new BehaviorSubject(null);
        this.optionClick$ = new Subject();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

/**
 * Option IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _optionUniqueIdCounter = 1;
class CdsOptionMultiComponent {
    get element() {
        return this.host.nativeElement;
    }
    set label(lbl) {
        // Converts html entities (such as &reg; into text)
        const tempEl = this._document.createElement('span');
        tempEl.innerHTML = lbl || '';
        this._label = tempEl.textContent;
    }
    get label() {
        return this._label;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor(host, liveAnnouncer, dropdownService, _doc) {
        this.host = host;
        this.liveAnnouncer = liveAnnouncer;
        this.dropdownService = dropdownService;
        this._doc = _doc;
        // #endregion
        // #region =========== @Input ========== //
        this.id = `cds-option-multi-${_optionUniqueIdCounter++}`;
        this._label = '';
        // #endregion
        // #region =========== @Output ========== //
        /**
         * Emits value
         * @deprecated
         */
        this.click = new EventEmitter();
        /**
         * Emits label
         * @deprecated
         */
        this.ddValue = new EventEmitter();
        this.present = false;
        this._document = _doc;
    }
    ngOnInit() {
        if (this.isDisabled) {
            this._isActive = false;
        }
        this.dropdownService?.currentDropdownMultiValue$?.subscribe(val => {
            if (val != null) {
                this.present = val.includes(this.value);
            }
            else {
                this.present = false;
            }
            this._isSelected = this._isActive = this.present ? true : null;
        });
    }
    // #endregion
    // #region =========== HostListeners ========== //
    optionClick(e) {
        if (!this.isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            this.emitValues();
            console.log('option mousedown', this.value, this.label);
            this._isSelected = !this._isSelected;
            this._isActive = !this._isActive;
            // lets service know when option is clicked and passes value
            this.dropdownService.optionClick$.next(this.value);
        }
    }
    // For when user presses space and enter keys (can't use hostlistener as focus is on pseudo element :before)
    optionSelect() {
        this._isSelected = !this._isSelected;
    }
    // #endregion
    // #region =========== Methods ========== //
    isDisabledFunction() {
        return this.isDisabled;
    }
    readSectionName() {
        if (this.groupName != "") {
            this.liveAnnouncer.announce(this.groupName + " grouping");
        }
    }
    /**
     * Previous functionality from original CDS Dropdown.
     * Should be deprecated.
     * They should use events emitted from dropdown instead.
     */
    emitValues() {
        this.click.emit(this.value);
        this.ddValue.emit(this.label);
    }
    // #endregion
    // #region =========== Highlightable ========== //
    /**
     * This method sets display styles on the option to make it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setActiveStyles() {
        // TODO: what about disabled?
        this._isActive = true;
    }
    /**
     * This method removes display styles on the option that made it appear
     * active. This is used by the ActiveDescendantKeyManager so key
     * events will display the proper options as active on arrow key events.
     */
    setInactiveStyles() {
        this._isActive = null;
    }
    /** Gets the label to be used when determining whether the option should be focused. */
    getLabel() {
        return this.label;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOptionMultiComponent, deps: [{ token: i0.ElementRef }, { token: i1$1.LiveAnnouncer }, { token: CdsDropdownMultiService }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsOptionMultiComponent, selector: "li[cdsOptionMulti]", inputs: { id: "id", value: "value", label: "label", isDisabled: "isDisabled", groupName: "groupName" }, outputs: { click: "click", ddValue: "ddValue" }, host: { attributes: { "role": "option" }, listeners: { "mousedown": "optionClick($event)" }, properties: { "attr.id": "this.id", "class.cds-option-multi-disabled": "this.isDisabled", "class.cds-option-multi-active": "this._isActive", "attr.aria-selected": "this._isActive", "class.cds-option-multi-selected": "this._isSelected" }, classAttribute: "cds-option-multi" }, ngImport: i0, template: `<div class="cds-option-multi-item-container"><div *ngIf="_isSelected
  " class="sr-only">Selected: </div>{{label}}</div>`, isInline: true, styles: [":host{padding:8px 16px;line-height:24px;cursor:default}:host .cds-option-multi-item-container:before{content:\"\";position:relative;display:inline-block;min-width:24px;height:24px;margin-right:16px;margin-left:16px!important;border-radius:4px;background-color:transparent;margin-left:-13px}:host.cds-option-multi-selected .cds-option-multi-item-container:before{background-repeat:no-repeat;background-position:center}:host .cds-option-multi-active .cds-option-multi-item-container:before{border-radius:6px;backface-visibility:hidden}.sr-only{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;white-space:nowrap;clip:rect(0,0,0,0);border:0}.cds-option-multi-disabled{color:#ccc;cursor:default}:host.cds-option-multi-disabled{color:#ccc}.cds-option-multi-item-container{display:flex}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsOptionMultiComponent, decorators: [{
            type: Component,
            args: [{ selector: 'li[cdsOptionMulti]', template: `<div class="cds-option-multi-item-container"><div *ngIf="_isSelected
  " class="sr-only">Selected: </div>{{label}}</div>`, host: {
                        class: 'cds-option-multi',
                        role: 'option',
                    }, styles: [":host{padding:8px 16px;line-height:24px;cursor:default}:host .cds-option-multi-item-container:before{content:\"\";position:relative;display:inline-block;min-width:24px;height:24px;margin-right:16px;margin-left:16px!important;border-radius:4px;background-color:transparent;margin-left:-13px}:host.cds-option-multi-selected .cds-option-multi-item-container:before{background-repeat:no-repeat;background-position:center}:host .cds-option-multi-active .cds-option-multi-item-container:before{border-radius:6px;backface-visibility:hidden}.sr-only{position:absolute!important;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;white-space:nowrap;clip:rect(0,0,0,0);border:0}.cds-option-multi-disabled{color:#ccc;cursor:default}:host.cds-option-multi-disabled{color:#ccc}.cds-option-multi-item-container{display:flex}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1$1.LiveAnnouncer }, { type: CdsDropdownMultiService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { id: [{
                type: HostBinding,
                args: ['attr.id']
            }, {
                type: Input
            }], value: [{
                type: Input
            }], label: [{
                type: Input
            }], isDisabled: [{
                type: HostBinding,
                args: ['class.cds-option-multi-disabled']
            }, {
                type: Input
            }], groupName: [{
                type: Input
            }], click: [{
                type: Output
            }], ddValue: [{
                type: Output
            }], _isActive: [{
                type: HostBinding,
                args: ['class.cds-option-multi-active']
            }, {
                type: HostBinding,
                args: ['attr.aria-selected']
            }], _isSelected: [{
                type: HostBinding,
                args: ['class.cds-option-multi-selected']
            }], optionClick: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }] } });

;
/**
 * Dropdown IDs need to be unique across components, so this counter exists outside of
 * the component definition.
 */
let _dropdownMultiUniqueIdCounter = 1;
class CdsDropdownMultiComponent {
    set isInErrorState(isErr) {
        this._inErrorState = isErr;
        this.showErrorChange.emit(isErr);
        this._changeRef.markForCheck();
    }
    get isInErrorState() {
        return this._inErrorState;
    }
    set value(val) {
        // Functionality from Checkbox2 Group
        if (isEqual(this._value, val))
            return;
        isEqual(this._value, val) ? (this._value = val) : (this._value = []);
        this._dropdown2Service?.currentDropdownMultiValue$?.next(this._value);
        this._updateDisplayText();
        this.onChange(this._value);
        // Don't emit until after content is initialized
        if (this._isContentInitialized) {
            this.selectionChange.emit(this._value);
        }
        if (!val && this._keyManager?.activeItem) {
            this._keyManager.setActiveItem(null);
        }
    }
    get value() {
        return this._value;
    }
    // #endregion
    // #region =========== @Input ========== //
    set idStr(val) {
        this._id = val || this._uid;
        this.fieldId = this._id;
    }
    get idStr() {
        const id = this._id || this._uid;
        return id;
    }
    /**
     * Sets the error state.
     * This should only be used for Template based forms (not reactive forms)
     */
    set showError(isError) {
        this._inErrorState = isError;
    }
    get showError() {
        return this._inErrorState;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor(liveAnnouncer, _el, _dropdown2Service, _changeRef, _doc) {
        this.liveAnnouncer = liveAnnouncer;
        this._el = _el;
        this._dropdown2Service = _dropdown2Service;
        this._changeRef = _changeRef;
        this._doc = _doc;
        // #region =========== CdsFormBase ========== //
        this.fieldType = 'dropdown';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        this._wrapperPadding = 8;
        this._eventUnsub$ = new Subject();
        this._destroyUnsub$ = new Subject();
        this._value = [];
        this._inErrorState = false;
        this._uid = `CdsDropdownMulti_${_dropdownMultiUniqueIdCounter++}`;
        /**
         * Defines aria-labelledby attribute
         * You may consider adding the id of the `<label>`
         */
        this.ariaLabelledby = '';
        // #endregion
        // #region ========== @Outputs ========== //
        /**
         * The event is emitted when the value is changed
         * The event is returned.
         */
        this.selectionChange = new EventEmitter();
        /**
         * The event is emitted when the dropdown is closed
         */
        this.panelClose = new EventEmitter();
        /**
         * The event is emitted when the dropdown is blurred
         */
        this.blur = new EventEmitter();
        /**
         * The event is emitted when the error state changes
         */
        this.showErrorChange = new EventEmitter();
        // #endregion
        // #region =========== ControlValueAccessor =========== //
        this.onChange = (_) => { };
        this.onTouch = () => { };
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    ngOnInit() {
        // Initialize fieldId
        this.fieldId = this.idStr;
    }
    ngAfterContentInit() {
        this._initKeyManager();
        this._updateDisplayText();
        this._isContentInitialized = true;
        this._dropdown2Service.optionClick$.subscribe(val => {
            this.isValueSelected(val, this._value) ? this.deleteSelection(val) : this.addSelection(val);
            //this.close();
        });
    }
    ngAfterViewInit() {
        // set listbox max-height and change if window is resized
        this._setValidMaxHeight();
        fromEvent(this._window, 'resize')
            .pipe(takeUntil(this._destroyUnsub$), debounceTime(10))
            .subscribe(obv => {
            if (this.listMaxHeight) {
                this._setValidMaxHeight();
            }
        });
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            const change = simpleChanges[propName];
            switch (propName) {
                case 'placeholder':
                    this._updateDisplayText();
                    break;
                case 'listMaxHeight':
                    if (!change.isFirstChange()) {
                        this._setValidMaxHeight();
                    }
                    break;
            }
        }
    }
    ngOnDestroy() {
        this._eventUnsub$.next();
        this._eventUnsub$.complete();
        this._destroyUnsub$.next();
        this._destroyUnsub$.complete();
    }
    // #endregion
    // #region =========== Methods =========== //
    /**
     * Deletes a selection if it is triggered while already selected.
     * @param newValue string
     * @param valueList Array
     */
    isValueSelected(newValue, valueList) {
        return this._value.includes(newValue);
    }
    /**
     * Adds a new selection to the list of selected values.
     * @param newValue string
     */
    addSelection(newValue) {
        // console.log('Add Function Entered');
        //Creating new array allows setter function to be called, which has functionality in it
        let val = [];
        val = this._value;
        val = val || [];
        val.push(newValue);
        this._value = val;
        this.writeValue(this._value);
        this._updateDisplayText();
        this.selectionChange.emit(this._value);
        // console.log('Value Added: ', this._value);
    }
    /**
     * Deletes a selection if it is triggered while already selected.
     * @param newValue string
     */
    deleteSelection(newValue) {
        // console.log('Delete Function Entered');
        if (this._value.includes(newValue)) {
            //Making sure setter is called
            this._value = this._value.filter(str => str !== newValue);
            this.writeValue(this._value);
            this._updateDisplayText();
            this.selectionChange.emit(this._value);
            // console.log('Value Deleted');
            // console.log('Value Array Now: ', this._value);
        }
    }
    /**
     * Toggles open the listbox
     */
    toggleOpen() {
        if (this._isOpen) {
            this.close();
        }
        else {
            this.open();
        }
    }
    /**
     * Opens cds dropdown2 component, scrolls to the active option, and focuses on listbox
     */
    open() {
        // Sets display to block, but still not visible.
        // This allows the code to get the dimensions of the listbox before making it visible.
        this._isOpen = true;
        // on open, sets the currently selected option to the "active" option
        /*const currentOptionInfo = this._getOptionInfoFromValue(this._value[0]);
    
        if (currentOptionInfo) {
          this._keyManager.setActiveItem(currentOptionInfo.index);
        } else {
          this._keyManager.setFirstItemActive();
        }*/
        this._keyManager.setFirstItemActive();
        if (this._keyManager?.activeItem.isDisabledFunction()) {
            this._keyManager?.activeItem.setInactiveStyles();
        }
        if (this.hasSections) {
            this.liveAnnouncer.announce("This dropdown allows multiple selections and has multiple sections. " + this._keyManager?.activeItem.groupName + " grouping");
        }
        else {
            this.liveAnnouncer.announce("This dropdown allows multiple selections.");
        }
        setTimeout(() => {
            // sets size and position of listbox
            this._repositionListbox();
            // when scroll or resize events happen, listbox is resized/repositioned
            const fromScroll$ = fromEvent(this._window, 'scroll');
            const fromResize$ = fromEvent(this._window, 'resize');
            const scrollParents$ = getScrollParents(this.listbox.nativeElement)
                .filter(el => el?.nodeName?.toLowerCase() !== 'html')
                .map(elem => fromEvent(elem, 'scroll'));
            merge(fromResize$, fromScroll$, ...scrollParents$)
                .pipe(takeUntil(this._eventUnsub$), debounceTime(10))
                .subscribe(obv => {
                this._repositionListbox();
            });
            // Scroll to active item after open
            const listboxEl = this.listbox.nativeElement;
            const activeEl = this._keyManager?.activeItem?.element;
            if (activeEl) {
                listboxEl.scrollTop = activeEl.offsetTop - this._wrapperPadding;
            }
            else {
                listboxEl.scrollTop = 0;
            }
            // Sets the listbox to visible after getting dimensions and setting the listbox in the correct position.
            this._isVisible = true;
            // focus on list after visible
            setTimeout(() => {
                this.listbox.nativeElement.focus();
            }, 0);
        }, 0);
    }
    /**
     * Closes cds dropdown2 listbox
     * @param [isRefocusOnButtonSkipped] when the listbox is closed, this prevents it from focusing back on the button
     */
    close(isRefocusOnButtonSkipped) {
        // do nothing if already closed
        if (!this._isOpen) {
            return;
        }
        // No need to reposition while closed, unsubscribing from event observable
        this._eventUnsub$.next();
        setTimeout(() => {
            this._isOpen = false;
            this._isVisible = false;
            this.panelClose.emit(this._value);
            // focus back on button if needed
            if (!isRefocusOnButtonSkipped) {
                this.listboxButton.nativeElement.focus();
            }
            this._changeRef.markForCheck();
        }, 0);
    }
    /**
     * Finds the CdsOption from the value
     * @param val value of option
     */
    _getOptionInfoFromValue(val) {
        const opt = this.options?.find(o => o.value === val);
        let optInfo;
        if (opt) {
            optInfo = {
                option: opt,
                label: opt.label,
                value: opt.value,
                index: this.options.toArray().indexOf(opt)
            };
        }
        return optInfo;
    }
    /**
     * Adds current value to display text, plus a comma and space at the end.
     * @param val String
     */
    _addtoDisplayText(val) {
        const currentOptionInfo = this._getOptionInfoFromValue(val);
        this._displayText += currentOptionInfo?.label + ', ';
    }
    /**
     * Removes the final comma and space from multiple selection display text.
     * @param list Array/String
     */
    _removeFinalComma(list) {
        var size = this._listSize(list);
        let newString = '';
        for (let i = 0; i < size - 2; i++) {
            newString += list[i];
        }
        this._displayText = newString;
    }
    /**
     * Replacement function for Array.length(), since that was throwing errors.
     * @param list Array/String
     */
    _listSize(list) {
        var count = 0;
        for (var i in list) {
            count++;
        }
        return count;
    }
    /**
     * Updates the display text of the dropdown.
     */
    _updateDisplayText() {
        let val = [];
        val = this._value;
        val = val || [];
        // console.log('Update Text Value: ', val);
        // console.log(val == [])
        if (val != null) {
            // console.log('Adding to Display Text');
            this._isPlaceholder = false;
            this._displayText = '';
            for (let i = 0; i < this._listSize(val); i++) {
                this._addtoDisplayText(val[i]);
            }
            this._removeFinalComma(this._displayText);
            // This is an edge case that keeps getting through, console says that [] != []
            // Accounting for it here for now
            if (this._displayText == '') {
                this._displayText = this.placeholder;
                this._isPlaceholder = true;
            }
            this._changeRef.markForCheck();
        }
        else {
            // console.log('Empty display text');
            this._isPlaceholder = true;
            this._displayText = this.placeholder;
        }
    }
    _blurList(e) {
        const isInDropdown = this._el.nativeElement.contains(e.relatedTarget);
        if (this._isOpen) {
            this.close(!isInDropdown);
        }
    }
    /**
     * Finds out if focus truly leaves the dropdown component.
     * @param e FocusEvent
     */
    _focusoutDropdown(e) {
        const isInDropdown = this._el.nativeElement.contains(e.relatedTarget);
        if (!isInDropdown) {
            this._isFocused = false;
            this.focusChange.next(this._isFocused);
            this.onTouch();
            this.blur.emit();
        }
    }
    _focusinDropdown(e) {
        if (!this._isFocused) {
            this._isFocused = true;
            this.focusChange.next(this._isFocused);
        }
    }
    /**
     * Handles keyboard events when focus is on the button (mainly to open the listbox)
     * @param e KeyboardEvent
     */
    _keydownButtonHandler(e) {
        const keyCode = e.keyCode || e.which || 0;
        // if alt+arrowdown: open
        // deque opens dropdown with simple ArrowDown (even without alt key)
        if (keyCode === DOWN_ARROW) {
            e.preventDefault();
            this.open();
        }
    }
    /**
     * Handles keyboard events when the listbox is open and in focus
     * @param e KeyboardEvent
     */
    _keydownListHandler(e) {
        const keyCode = e.keyCode || e.which || 0;
        // passes event to keyManager
        this._keyManager.onKeydown(e);
        if (this._keyManager?.activeItem.isDisabledFunction()) {
            this._keyManager?.activeItem.setInactiveStyles();
        }
        if (this.hasSections) {
            this._keyManager?.activeItem.readSectionName();
        }
        // functionality that the keyManager does not do
        switch (keyCode) {
            // escape closes without change
            case ESCAPE:
                e.preventDefault();
                e.stopPropagation();
                this.close();
                break;
            // enter/space selects the currently active item and closes
            case ENTER:
            case SPACE:
                if (!this._keyManager?.activeItem.isDisabledFunction()) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.isValueSelected(this._keyManager?.activeItem?.value, this._value) ? this.deleteSelection(this._keyManager?.activeItem?.value) : this.addSelection(this._keyManager?.activeItem?.value);
                    this._keyManager?.activeItem.optionSelect();
                }
                //this.close();
                break;
        }
    }
    /**
     * Initializes the ActiveDescendantKeyManager
     */
    _initKeyManager() {
        this._keyManager = new ActiveDescendantKeyManager(this.options)
            .withVerticalOrientation()
            .withAllowedModifierKeys(['shiftKey'])
            .withHomeAndEnd()
            .withTypeAhead();
        // subscribe to changes of active item
        this._keyManager.change.subscribe({
            next: indx => {
                // sets the aria-activedescendant id
                this._activeDescendantId = this._keyManager?.activeItem?.id;
                // scroll active item into view if needed
                const listboxEl = this.listbox.nativeElement;
                const activeEl = this._keyManager?.activeItem?.element;
                this._scrollToIfNeeded(listboxEl, activeEl);
                this._changeRef.markForCheck();
            }
        });
    }
    /**
     * Scrolls the item into view if needed.  Does not scroll if item already in view.
     * @param wrapperEl The wrapper element that contains the scrollable elements
     * @param itemEl The item that needs to scroll into view
     */
    _scrollToIfNeeded(wrapperEl, itemEl) {
        if (!itemEl) {
            if (!!wrapperEl) {
                wrapperEl.scrollTop = 0;
            }
            return;
        }
        const wrapTop = wrapperEl.scrollTop + this._wrapperPadding;
        const wrapBottom = wrapTop + wrapperEl.offsetHeight;
        const itemTop = itemEl.offsetTop;
        const itemBottom = itemTop + itemEl.offsetHeight;
        if (itemTop < wrapTop) {
            wrapperEl.scrollTop = itemTop - this._wrapperPadding;
        }
        else if (itemBottom > wrapBottom) {
            wrapperEl.scrollTop = itemBottom - wrapperEl.offsetHeight - this._wrapperPadding;
        }
    }
    /**
     * Repositions listbox - find out if dropdown is above/below middle of view, then add/remove class so dropdown can be repositioned.
     */
    _repositionListbox() {
        const viewportHeight = this._window?.innerHeight;
        const viewportMidpoint = Math.round(viewportHeight / 2);
        const buttonRect = this.listboxButton?.nativeElement?.getBoundingClientRect();
        const buttonMidpoint = Math.round(buttonRect.top + (buttonRect.height / 2));
        const listboxRect = this.listbox?.nativeElement?.getBoundingClientRect();
        this._isPlacedAbove = buttonRect.bottom + listboxRect.height - 8 > viewportHeight;
        // Set width of listbox
        this._listboxWidth = buttonRect?.width;
        // Set left position of listbox
        this._listboxLeft = buttonRect?.left;
        // Set top position of listbox
        if (this._isPlacedAbove) {
            this._listboxTop = buttonRect.top - listboxRect.height - (this._wrapperPadding * 2);
        }
        else {
            this._listboxTop = buttonRect.bottom;
        }
        this._changeRef.markForCheck();
    }
    _getValidMaxHeight(maxHeight) {
        const viewportHeight = this._window?.innerHeight;
        const buttonRect = this.listboxButton?.nativeElement?.getBoundingClientRect();
        const buttonHeight = buttonRect.height;
        const maxAvailableViewportHeight = viewportHeight - buttonHeight;
        const buttonDistanceFromTop = Math.round(buttonRect.top + this._window.scrollY);
        const buttonDistanceFromBottom = this._document.body.clientHeight - buttonHeight - buttonDistanceFromTop;
        const buttonDistanceMax = Math.max(buttonDistanceFromTop, buttonDistanceFromBottom);
        const distanceVsViewportMin = Math.min(buttonDistanceMax, maxAvailableViewportHeight);
        if (maxHeight > distanceVsViewportMin) {
            return distanceVsViewportMin - 20;
        }
        return maxHeight;
    }
    _setValidMaxHeight() {
        if (this.listMaxHeight) {
            this._listMaxHeight = this._getValidMaxHeight(this.listMaxHeight);
        }
        else {
            this._listMaxHeight = null;
        }
        this._changeRef.detectChanges();
    }
    writeValue(value) {
        this._value = value;
        // onChange required for proper 'required' validation
        this.onChange(value);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setDisabledState(isDisabled) {
        this._isDisabled = isDisabled;
        this.disabledChange.next(isDisabled);
        this._changeRef.markForCheck();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiComponent, deps: [{ token: i1$1.LiveAnnouncer }, { token: i0.ElementRef }, { token: CdsDropdownMultiService }, { token: i0.ChangeDetectorRef }, { token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsDropdownMultiComponent, selector: "cds-dropdown-multi", inputs: { idStr: "idStr", placeholder: "placeholder", ariaLabelledby: "ariaLabelledby", showError: "showError", required: "required", listMaxHeight: "listMaxHeight", hasSections: "hasSections" }, outputs: { selectionChange: "selectionChange", panelClose: "panelClose", blur: "blur", showErrorChange: "showErrorChange" }, host: { listeners: { "focusout": "_focusoutDropdown($event)", "focusin": "_focusinDropdown($event)" }, properties: { "class.cds-multi-error": "this._inErrorState" }, classAttribute: "cds-dropdown-multi" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsDropdownMultiComponent)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsDropdownMultiComponent },
            CdsDropdownMultiService
        ], queries: [{ propertyName: "options", predicate: CdsOptionMultiComponent }], viewQueries: [{ propertyName: "listboxButton", first: true, predicate: ["listboxButton"], descendants: true }, { propertyName: "listbox", first: true, predicate: ["listbox"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <button
      type="button"
      #listboxButton
      [attr.id]="idStr"
      class="cds-multi-button"
      [class.cds-multi-button-open]="_isOpen"
      [class.cds-multi-button-placeholder]="_isPlaceholder"
      [class.cds-multi-button-error]="_inErrorState"
      [class.cds-multi-button-focused]="_isFocused"
      aria-haspopup="listbox"
      [attr.aria-labelledby]="ariaLabelledby + ' ' + idStr + '-button-value'"
      [attr.aria-expanded]="!!_isOpen"
      (click)="toggleOpen()"
      (keydown)="_keydownButtonHandler($event)"
      [disabled]="_isDisabled"
      [attr.required]="required ? '' : null"
    >
      <div
        [id]="idStr + '-button-value'"
        class="cds-multi-pseudo-value"
      >
        {{ _displayText || '&nbsp;' }}
      </div>
    </button>
    <div
    class="cds-multi-options-container"
    [style.display]="_isOpen ? 'block' : 'none'"
    [style.visibility]="_isVisible ? 'visible' : 'hidden'"
    >
      <div
        class="cds-multi-options"
        [style.width.px]="_listboxWidth"
        [style.top.px]="_listboxTop"
        [style.left.px]="_listboxLeft"
      >
        <ul
          #listbox
          [id]="idStr + '-listbox'"
          class="cds-multi-listbox"
          role="listbox"
          tabindex="-1"
          (keydown)="_keydownListHandler($event)"
          (blur)="_blurList($event)"
          [attr.aria-activedescendant]="_activeDescendantId"
          [style.maxHeight.px]="_listMaxHeight"
          aria-multiselectable="true"
        >
          <ng-content></ng-content>
        </ul>
      </div>
    </div>
  `, isInline: true, styles: [":host{position:relative;width:100%;display:block}.cds-multi-button{color:#056dae;font-weight:700;border:1px solid #bbb;border-radius:8px;padding:0 40px 0 16px;background-color:#fff;width:100%;text-align:left;line-height:46px;outline:none}.cds-multi-button:after{content:\"\";width:24px;height:48px;position:absolute;top:0;right:16px;background-repeat:no-repeat;background-position:right center;background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23056dae%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\");transition:transform .2s}.cds-multi-button:focus,.cds-multi-button:active,.cds-multi-button.cds-multi-button-focused{border-color:#056dae;background-color:#eff3f8;color:#056dae}.cds-multi-button.cds-multi-button-error{border-color:#d60000;color:#d60000}.cds-multi-button.cds-multi-button-error:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23d60000%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-multi-button.cds-multi-button-error:focus,.cds-multi-button.cds-multi-button-error:active,.cds-multi-button.cds-multi-button-error.cds-multi-button-focused{background-color:#ffebeb}.cds-multi-button[disabled]{cursor:default;border-color:#bbb;background-color:#eee;color:#ccc}.cds-multi-button[disabled]:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23ccc%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-multi-button-open:after{transform:rotateX(-180deg)}.cds-multi-pseudo-value{text-overflow:ellipsis;overflow:hidden;white-space:nowrap;padding-right:8px}.cds-multi-options-container{display:none;visibility:hidden;position:fixed;top:0;left:0;z-index:1000;pointer-events:none;width:100%;height:100%}.cds-multi-options{position:absolute;pointer-events:auto;padding:8px 0;background-color:#fff;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-multi-listbox{list-style-type:none;overflow-y:auto;max-height:240px;margin:0;padding:0;outline:none}:host.cds-multi-sm .cds-multi-button{line-height:30px;padding-right:32px}:host.cds-multi-sm .cds-multi-button:after{height:32px;right:8px}:host.cds-multi-sm .cds-multi-listbox{max-height:238px}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-dropdown-multi', template: `
    <button
      type="button"
      #listboxButton
      [attr.id]="idStr"
      class="cds-multi-button"
      [class.cds-multi-button-open]="_isOpen"
      [class.cds-multi-button-placeholder]="_isPlaceholder"
      [class.cds-multi-button-error]="_inErrorState"
      [class.cds-multi-button-focused]="_isFocused"
      aria-haspopup="listbox"
      [attr.aria-labelledby]="ariaLabelledby + ' ' + idStr + '-button-value'"
      [attr.aria-expanded]="!!_isOpen"
      (click)="toggleOpen()"
      (keydown)="_keydownButtonHandler($event)"
      [disabled]="_isDisabled"
      [attr.required]="required ? '' : null"
    >
      <div
        [id]="idStr + '-button-value'"
        class="cds-multi-pseudo-value"
      >
        {{ _displayText || '&nbsp;' }}
      </div>
    </button>
    <div
    class="cds-multi-options-container"
    [style.display]="_isOpen ? 'block' : 'none'"
    [style.visibility]="_isVisible ? 'visible' : 'hidden'"
    >
      <div
        class="cds-multi-options"
        [style.width.px]="_listboxWidth"
        [style.top.px]="_listboxTop"
        [style.left.px]="_listboxLeft"
      >
        <ul
          #listbox
          [id]="idStr + '-listbox'"
          class="cds-multi-listbox"
          role="listbox"
          tabindex="-1"
          (keydown)="_keydownListHandler($event)"
          (blur)="_blurList($event)"
          [attr.aria-activedescendant]="_activeDescendantId"
          [style.maxHeight.px]="_listMaxHeight"
          aria-multiselectable="true"
        >
          <ng-content></ng-content>
        </ul>
      </div>
    </div>
  `, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsDropdownMultiComponent)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsDropdownMultiComponent },
                        CdsDropdownMultiService
                    ], changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'cds-dropdown-multi',
                        '(focusout)': '_focusoutDropdown($event)',
                        '(focusin)': '_focusinDropdown($event)'
                    }, styles: [":host{position:relative;width:100%;display:block}.cds-multi-button{color:#056dae;font-weight:700;border:1px solid #bbb;border-radius:8px;padding:0 40px 0 16px;background-color:#fff;width:100%;text-align:left;line-height:46px;outline:none}.cds-multi-button:after{content:\"\";width:24px;height:48px;position:absolute;top:0;right:16px;background-repeat:no-repeat;background-position:right center;background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23056dae%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\");transition:transform .2s}.cds-multi-button:focus,.cds-multi-button:active,.cds-multi-button.cds-multi-button-focused{border-color:#056dae;background-color:#eff3f8;color:#056dae}.cds-multi-button.cds-multi-button-error{border-color:#d60000;color:#d60000}.cds-multi-button.cds-multi-button-error:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23d60000%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-multi-button.cds-multi-button-error:focus,.cds-multi-button.cds-multi-button-error:active,.cds-multi-button.cds-multi-button-error.cds-multi-button-focused{background-color:#ffebeb}.cds-multi-button[disabled]{cursor:default;border-color:#bbb;background-color:#eee;color:#ccc}.cds-multi-button[disabled]:after{background-image:url(\"data:image/svg+xml,%3csvg width=%2722%27 height=%2712%27 viewBox=%270 0 22 12%27 fill=%27%23ccc%27 xmlns=%27http://www.w3.org/2000/svg%27%3e%3cpath fill-rule=%27evenodd%27 clip-rule=%27evenodd%27 d=%27M0.145514 0.92293C-0.0492316 0.711009 -0.0483957 0.36832 0.147381 0.157514C0.343158 -0.0532915 0.65974 -0.0523866 0.854486 0.159535L10.594 10.758C10.7887 10.9699 11.1053 10.9708 11.3011 10.76L21.1474 0.15752C21.3431 -0.0532888 21.6597 -0.0523894 21.8545 0.159529C22.0492 0.371448 22.0484 0.714136 21.8526 0.924945L12.0063 11.5275C11.419 12.1599 10.4692 12.1572 9.88498 11.5214L0.145514 0.92293Z%27 /%3e%3c/svg%3e\")}.cds-multi-button-open:after{transform:rotateX(-180deg)}.cds-multi-pseudo-value{text-overflow:ellipsis;overflow:hidden;white-space:nowrap;padding-right:8px}.cds-multi-options-container{display:none;visibility:hidden;position:fixed;top:0;left:0;z-index:1000;pointer-events:none;width:100%;height:100%}.cds-multi-options{position:absolute;pointer-events:auto;padding:8px 0;background-color:#fff;border-radius:8px;box-shadow:0 1px 10px #0000001f}.cds-multi-listbox{list-style-type:none;overflow-y:auto;max-height:240px;margin:0;padding:0;outline:none}:host.cds-multi-sm .cds-multi-button{line-height:30px;padding-right:32px}:host.cds-multi-sm .cds-multi-button:after{height:32px;right:8px}:host.cds-multi-sm .cds-multi-listbox{max-height:238px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i0.ElementRef }, { type: CdsDropdownMultiService }, { type: i0.ChangeDetectorRef }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { _inErrorState: [{
                type: HostBinding,
                args: ['class.cds-multi-error']
            }], listboxButton: [{
                type: ViewChild,
                args: ['listboxButton']
            }], listbox: [{
                type: ViewChild,
                args: ['listbox']
            }], options: [{
                type: ContentChildren,
                args: [CdsOptionMultiComponent]
            }], idStr: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], showError: [{
                type: Input
            }], required: [{
                type: Input
            }], listMaxHeight: [{
                type: Input
            }], hasSections: [{
                type: Input
            }], selectionChange: [{
                type: Output
            }], panelClose: [{
                type: Output
            }], blur: [{
                type: Output
            }], showErrorChange: [{
                type: Output
            }] } });

const COMPONENTS$p = [CdsDropdownMultiComponent, CdsOptionMultiComponent];
class CdsDropdownMultiModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiModule, declarations: [CdsDropdownMultiComponent, CdsOptionMultiComponent], imports: [CommonModule, CdsIconModule], exports: [CdsDropdownMultiComponent, CdsOptionMultiComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiModule, imports: [CommonModule, CdsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDropdownMultiModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$p],
                    imports: [
                        CommonModule, CdsIconModule
                    ],
                    exports: [...COMPONENTS$p]
                }]
        }] });

const FILE_UPLOAD_CONTROL_VALUE_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => CdsFileUploadComponent),
    multi: true
};
let nextUniqueId$4 = 0;
class CdsFileUploadComponent {
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * @description disables the entire group
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    // This is used to upload the file.
    uploadFile(fileList) {
        let combinedFileCount = this.files.length;
        if (this.fileToReplace === 12345) {
            combinedFileCount += fileList.length;
            this.announcer.announce('fileuploaded');
        }
        else {
            combinedFileCount += fileList.length - 1;
        }
        if (combinedFileCount == 3) {
            this.message =
                'You have successfully attached the maximum number of documents';
        }
        //To check the maximum file limit
        if (combinedFileCount > 3) {
            this.showMaxFileCount = true;
        }
        else {
            for (let i = 0; i < fileList.length; i++) {
                const fileinfo = {
                    file: fileList[i],
                    newName: fileList[i].name.replace(/\s/g, ''),
                    display: false,
                    imageUrl: ''
                };
                //to check the size of the file
                if (this.isTooLarge(fileList[i]))
                    alert(fileList[i].name + ' is over the limit');
                else {
                    if (this.fileToReplace == 12345) {
                        this.files.push(fileinfo);
                    }
                    else {
                        this.files[this.fileToReplace] = fileinfo;
                        this.titlename.nativeElement.focus();
                        this.announcer.announce('Replaced the File');
                        this.fileToReplace = 12345;
                    }
                    // need to update write value when new file is added or file is replaced
                    this.writeValue(fileinfo);
                }
            }
        }
        //To enable the submit and exit button when file are uploaded
        if (this.files.length > 0) {
            this.isSubmitOrExitEnabled = true;
        }
    }
    // To check the size of the file
    isTooLarge(file) {
        return file.size > 1048576;
    }
    //To preview the file
    preview(file) {
        if (!file)
            return;
        file.display = !file.display;
        let reader = new FileReader();
        this.imagePath = file.file;
        reader.readAsDataURL(file.file);
        reader.onload = _event => {
            file.imageUrl = reader.result;
        };
    }
    // To delete the file
    remove(index) {
        this.files.splice(index, 1);
    }
    //To display the delete modal
    confirmModal() {
        this.showConfirmModal = true;
    }
    confirm(file) {
        this.remove(file);
        this.message = '';
        this.showConfirmModal = false;
        this.announcer.announce('filedeleted');
        this.titlename.nativeElement.focus();
    }
    // To focus the input when modal is closed
    onDeleteModalHide() {
        this.titlename.nativeElement.focus();
    }
    //To display the max file modal
    confirmMaxFile() {
        this.showMaxFileCount = true;
    }
    //To display the submit modal
    confirmSubmit() {
        this.showSubmitModal = true;
    }
    onSubmit() {
        this.SubmitButtonClicked.emit(this.files);
        this.files = [];
        this.isSubmitOrExitEnabled = false;
        this.message = '';
        this.showSubmitModal = false;
    }
    //To display the exit file modal
    confirmExit() {
        this.showExitModal = true;
    }
    exit() {
        this.ExitButtonClicked.emit(this.files);
        this.files = [];
        this.isSubmitOrExitEnabled = false;
        this.message = '';
        this.showExitModal = false;
    }
    //Lifecycle Hooks
    constructor(announcer) {
        this.announcer = announcer;
        // #region // ------------------         Properties              -------------------- //
        this.files = [];
        // This is always set to a random number
        // when a file is clicked for replacement, that is stored here
        // and the upload method checks for this variable and replaces it.
        this.fileToReplace = 12345;
        //To disable the submit and exit button
        this.isSubmitOrExitEnabled = false;
        /**
         * @property title
         * @description To define the title
         * @return string
         */
        this.title = 'Drag and Drop Files here';
        this._uid = `cds-fileupload-${nextUniqueId$4++}`;
        this._disabled = false;
        // #endregion
        // #region // ------------------         Outputs                 -------------------- //
        /**
         * @property SubmitButtonClicked
         * @description Emits when the submitbutton is clicked
         * @type: EventEmitter
         */
        this.SubmitButtonClicked = new EventEmitter();
        /**
         * @property ExitButtonClicked
         * @description Emits when the exitbutton is clicked
         * @type: EventEmitter
         */
        this.ExitButtonClicked = new EventEmitter();
        // #region // ----------------------- Form Binding Methods ----------------------- //
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (file) => { };
        this.onTouched = () => { };
        if (isDevMode()) {
            console.error('%cThe Common UI CDS File Upload <citi-file-upload> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    announce(txt) {
        this.announcer.announce(txt);
    }
    /**
     * @description Allows Angular to update the model. The form will pass new values in here
     */
    writeValue(file) {
        /**
         * @note must be in the order of
         * 1) onChange()
         * 2) checkedChange
         */
        this.uploadFile(file);
        this.SubmitButtonClicked.emit(file);
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFileUploadComponent, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsFileUploadComponent, selector: "cds-file-upload", inputs: { accept: "accept", title: "title", idStr: "idStr", disabled: "disabled" }, outputs: { SubmitButtonClicked: "SubmitButtonClicked", ExitButtonClicked: "ExitButtonClicked" }, host: { classAttribute: "cds-file-upload" }, providers: [FILE_UPLOAD_CONTROL_VALUE_ACCESSOR], viewQueries: [{ propertyName: "titlename", first: true, predicate: ["titlename"], descendants: true }], ngImport: i0, template: `
    <div
      class="uploadfilecontainer"
      (click)="fileInput.click()"
      fileDragDrop
      (onFileDropped)="uploadFile($event)"
    >
      <input
        hidden
        type="file"
        #fileInput
        (change)="uploadFile($event.target.files)"
        multiple="multiple"
        accept="{{ accept }}"
        [disabled]="disabled"
      />

      <div class="title">
        <span #titlename (keydown.enter)="fileInput.click()" tabindex="0">{{
          title
        }}</span>
      </div>
    </div>

    <div class="files-list" *ngFor="let file of files; let i = index">
      <div class="filename">
        <span aria-describedby="fileInput"> {{ file.newName }} </span>
      </div>

      <div class="button">
        <button class="preview-file" (click)="preview(file)">
          <!-- <citi-icon2 shade="dark" name="006-search" size="large"></citi-icon2> -->
          <img [src]="file.imageUrl" height="300" *ngIf="file.display" />
          Preview
        </button>

        <button
          class="replace-file"
          (click)="fileInput.click(); fileToReplace = i"
        >
          <!-- <citi-icon2 shade="dark" name="131-refresh" size="large"></citi-icon2> -->
          Replace
        </button>

        <button class="delete-file" (click)="confirmModal()">
          <!-- <citi-icon2 shade="dark" name="156-trash" size="large"></citi-icon2> -->
          Delete
        </button>

        <!-- <citi-modal
          cancelButtonText="Cancel"
          disablePrimaryButton="false"
          [idStr]="'myModal'"
          primaryButtonText="Delete"
          showCancelButton="true"
          [title]="'Are you sure you want to delete the file?'"
          (primaryButtonClick)="confirm(i)"
          [(show)]="showConfirmModal"
          (onHide)="onDeleteModalHide()"
        >
        </citi-modal> -->
      </div>
    </div>

    <div class="message">{{ message }}</div>
    <!--
    <citi-modal [title]="'can upload only 3 files'" [(show)]="showMaxFileCount">
    </citi-modal> -->

    <!--
  <div class="notes">
    <p> Please note: Make sure you have attached the relevant information. </p>
  </div>
  -->

    <div>
      <!-- <citi-cta
        value="Submit form"
        type="primary"
        [disabled]="!isSubmitOrExitEnabled"
        (click)="confirmSubmit()"
      >
      </citi-cta> -->

      <!-- <citi-modal
        cancelButtonText="Goback"
        disablePrimaryButton="false"
        [idStr]="'myModal'"
        primaryButtonText="Submit"
        showCancelButton="true"
        [title]="'Are you ready to Submit?'"
        (primaryButtonClick)="onSubmit()"
        [(show)]="showSubmitModal"
      >
      </citi-modal> -->

      <button
        class="btn btn-link"
        type="button"
        [disabled]="!isSubmitOrExitEnabled"
        (click)="confirmExit()"
      >
        Exit
      </button>

      <!-- <citi-modal
        cancelButtonText="Goback"
        disablePrimaryButton="false"
        [idStr]="'myModal'"
        [title]="' Are you sure you want to Exit'"
        primaryButtonText="Exit"
        showCancelButton="true"
        (primaryButtonClick)="exit()"
        [(show)]="showExitModal"
      >
      </citi-modal> -->
    </div>
  `, isInline: true, styles: [".uploadfilecontainer{height:200px;width:80%;margin:20px auto;border:2px dashed #1c8adb;border-radius:10px}.files-list{display:flex;justify-content:space-between;width:80%;margin:10px auto;background:#ffffff;border:1px solid;border-radius:12px;padding:5px;color:#1c8adb}.title{display:flex;align-items:center;justify-content:center;padding-top:80px;cursor:pointer}.title:hover{text-decoration:underline;color:#1c8adb}.filename{flex-grow:0;padding-top:20px;flex-basis:auto;overflow:hidden;white-space:nowrap}.filename p{overflow:hidden;text-overflow:ellipsis}.message{width:80%;margin:10px auto;background:#fff;padding:5px}.button{flex-grow:0;padding-top:10px;margin-bottom:10px;display:flex;flex-direction:row;align-items:center}.button button{margin-left:10px;white-space:nowrap;text-decoration:underline}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFileUploadComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-file-upload', host: { class: 'cds-file-upload' }, providers: [FILE_UPLOAD_CONTROL_VALUE_ACCESSOR], template: `
    <div
      class="uploadfilecontainer"
      (click)="fileInput.click()"
      fileDragDrop
      (onFileDropped)="uploadFile($event)"
    >
      <input
        hidden
        type="file"
        #fileInput
        (change)="uploadFile($event.target.files)"
        multiple="multiple"
        accept="{{ accept }}"
        [disabled]="disabled"
      />

      <div class="title">
        <span #titlename (keydown.enter)="fileInput.click()" tabindex="0">{{
          title
        }}</span>
      </div>
    </div>

    <div class="files-list" *ngFor="let file of files; let i = index">
      <div class="filename">
        <span aria-describedby="fileInput"> {{ file.newName }} </span>
      </div>

      <div class="button">
        <button class="preview-file" (click)="preview(file)">
          <!-- <citi-icon2 shade="dark" name="006-search" size="large"></citi-icon2> -->
          <img [src]="file.imageUrl" height="300" *ngIf="file.display" />
          Preview
        </button>

        <button
          class="replace-file"
          (click)="fileInput.click(); fileToReplace = i"
        >
          <!-- <citi-icon2 shade="dark" name="131-refresh" size="large"></citi-icon2> -->
          Replace
        </button>

        <button class="delete-file" (click)="confirmModal()">
          <!-- <citi-icon2 shade="dark" name="156-trash" size="large"></citi-icon2> -->
          Delete
        </button>

        <!-- <citi-modal
          cancelButtonText="Cancel"
          disablePrimaryButton="false"
          [idStr]="'myModal'"
          primaryButtonText="Delete"
          showCancelButton="true"
          [title]="'Are you sure you want to delete the file?'"
          (primaryButtonClick)="confirm(i)"
          [(show)]="showConfirmModal"
          (onHide)="onDeleteModalHide()"
        >
        </citi-modal> -->
      </div>
    </div>

    <div class="message">{{ message }}</div>
    <!--
    <citi-modal [title]="'can upload only 3 files'" [(show)]="showMaxFileCount">
    </citi-modal> -->

    <!--
  <div class="notes">
    <p> Please note: Make sure you have attached the relevant information. </p>
  </div>
  -->

    <div>
      <!-- <citi-cta
        value="Submit form"
        type="primary"
        [disabled]="!isSubmitOrExitEnabled"
        (click)="confirmSubmit()"
      >
      </citi-cta> -->

      <!-- <citi-modal
        cancelButtonText="Goback"
        disablePrimaryButton="false"
        [idStr]="'myModal'"
        primaryButtonText="Submit"
        showCancelButton="true"
        [title]="'Are you ready to Submit?'"
        (primaryButtonClick)="onSubmit()"
        [(show)]="showSubmitModal"
      >
      </citi-modal> -->

      <button
        class="btn btn-link"
        type="button"
        [disabled]="!isSubmitOrExitEnabled"
        (click)="confirmExit()"
      >
        Exit
      </button>

      <!-- <citi-modal
        cancelButtonText="Goback"
        disablePrimaryButton="false"
        [idStr]="'myModal'"
        [title]="' Are you sure you want to Exit'"
        primaryButtonText="Exit"
        showCancelButton="true"
        (primaryButtonClick)="exit()"
        [(show)]="showExitModal"
      >
      </citi-modal> -->
    </div>
  `, styles: [".uploadfilecontainer{height:200px;width:80%;margin:20px auto;border:2px dashed #1c8adb;border-radius:10px}.files-list{display:flex;justify-content:space-between;width:80%;margin:10px auto;background:#ffffff;border:1px solid;border-radius:12px;padding:5px;color:#1c8adb}.title{display:flex;align-items:center;justify-content:center;padding-top:80px;cursor:pointer}.title:hover{text-decoration:underline;color:#1c8adb}.filename{flex-grow:0;padding-top:20px;flex-basis:auto;overflow:hidden;white-space:nowrap}.filename p{overflow:hidden;text-overflow:ellipsis}.message{width:80%;margin:10px auto;background:#fff;padding:5px}.button{flex-grow:0;padding-top:10px;margin-bottom:10px;display:flex;flex-direction:row;align-items:center}.button button{margin-left:10px;white-space:nowrap;text-decoration:underline}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { accept: [{
                type: Input
            }], title: [{
                type: Input
            }], idStr: [{
                type: Input
            }], disabled: [{
                type: Input
            }], SubmitButtonClicked: [{
                type: Output
            }], ExitButtonClicked: [{
                type: Output
            }], titlename: [{
                type: ViewChild,
                args: ['titlename']
            }] } });

const MODULES$a = [
    CommonModule
    // ContentHtml,
    // CTA,
    // Modal,
    // Icons2
];
class CdsFileUpload {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFileUpload, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFileUpload, declarations: [CdsFileUploadComponent], imports: [CommonModule
            // ContentHtml,
            // CTA,
            // Modal,
            // Icons2
        ], exports: [CdsFileUploadComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFileUpload, imports: [MODULES$a] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFileUpload, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$a],
                    declarations: [CdsFileUploadComponent],
                    exports: [CdsFileUploadComponent]
                }]
        }] });

const BACKSPACE = 8;
const KEY_TAB = 9;
const KEY_ENTER = 13;
const KEY_SHIFT = 16;
const KEY_ESC = 27;
const KEY_SPACEBAR = 32;
const KEY_PAGE_UP = 33;
const KEY_PAGE_DOWN = 34;
const KEY_END = 35;
const KEY_HOME = 36;
const KEY_ARROW_LEFT = 37;
const KEY_ARROW_UP = 38;
const KEY_ARROW_RIGHT = 39;
const KEY_ARROW_DOWN = 40;
const DELETE = 46;
const KEY_0 = 48;
const KEY_1 = 49;
const KEY_2 = 50;
const KEY_3 = 51;
const KEY_4 = 52;
const KEY_5 = 53;
const KEY_6 = 54;
const KEY_7 = 55;
const KEY_8 = 56;
const KEY_9 = 57;
const KEY_NUMPAD_0 = 96;
const KEY_NUMPAD_1 = 97;
const KEY_NUMPAD_2 = 98;
const KEY_NUMPAD_3 = 99;
const KEY_NUMPAD_4 = 100;
const KEY_NUMPAD_5 = 101;
const KEY_NUMPAD_6 = 102;
const KEY_NUMPAD_7 = 103;
const KEY_NUMPAD_8 = 104;
const KEY_NUMPAD_9 = 105;
const PERIOD = 190;

class DayComponent {
    get class() {
        let classes = this.disabled || this.day.classes == 'empty' ? this.day.classes + ' disabled' : this.day.classes;
        if (this.isSmall)
            classes += ' responsive';
        return classes;
    }
    // @HostBinding('attr.tabindex') get tabindex(){
    //   return this.tabIndex
    // }
    get ariaLabel() {
        // if(this.day.curMonth === 'December' && this.day.classes.includes('prev')) return this.day.weekday + " " +  this.day.curMonth + ' ' + this.day.day + ' ,' + this.day.curYear + ' ' + this.day.text;
        // if(this.day.curMonth == 'January' && this.day.classes.includes('next')) return this.day.weekday + " " +  this.day.curMonth + ' ' + this.day.day + ' ,' + this.day.curYear + ' ' + this.day.text;
        if (this.day.classes.includes('selected'))
            return this.day.weekday + " " + this.day.curMonth + ' ' + this.day.day + ' ,' + this.day.curYear + ' ' + this.day.text + ' selected';
        return this.day.weekday + " " + this.day.curMonth + ' ' + this.day.day + ' ,' + this.day.curYear + ' ' + this.day.text;
    }
    get setDisabled() {
        return !!this.disabled;
    }
    get role() {
        return 'button';
    }
    get dayStyle() {
        return this.day.style;
    }
    newValue() {
        if (!this.disabled)
            this.daySelected.emit(this.day.day);
    }
    constructor(host) {
        this.host = host;
        this.disabled = false;
        this.isSmall = false;
        this.daySelected = new EventEmitter();
    }
    ngOnInit() {
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DayComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: DayComponent, selector: "cds-day", inputs: { day: "day", disabled: "disabled", tabIndex: "tabIndex", isSmall: "isSmall" }, outputs: { daySelected: "daySelected" }, host: { listeners: { "keydown.Space": "newValue()", "keydown.Enter": "newValue()", "click": "newValue()" }, properties: { "class": "this.class", "attr.aria-label": "this.ariaLabel", "attr.aria-disabled": "this.setDisabled", "attr.role": "this.role", "style": "this.dayStyle" }, classAttribute: "cds-day" }, ngImport: i0, template: `
    <div>
      <!-- <div class='extra-month' *ngIf="day.classes == 'next-month-first'">{{ !!day.month ? day.month : ''}}</div> -->
      <div class='day-number'>{{ day.day }}</div>
      <!-- <div class='text'>{{ day.text }}</div> -->
    </div>
    `, isInline: true, styles: [":host{display:flex;border-radius:4px;line-height:27px;width:calc((100% - 7px) / 7);margin:2px;text-align:center;justify-content:center;border:1px solid #EEEEEE;padding-top:22.34px;width:80px;height:80px;font-size:21px;font-family:Interstate_Regular;pointer-events:auto;touch-action:auto}:host.cds-success{background:linear-gradient(to right,#006E0A 50%,#006E0A 100%) no-repeat;background-size:100% 10px;background-color:#006e0a1a}:host.cds-warning{background:linear-gradient(to right,#FFCA00 50%,#FFCA00 100%) no-repeat;background-size:100% 10px;background-color:#ffca001a}:host.cds-critical{background:linear-gradient(to right,#D60000 50%,#D60000 100%) no-repeat;background-size:100% 10px;color:#d60000;background-color:#d600001a}:host.cds-critical .text{color:#d60000}:host.cds-informative{background:linear-gradient(to right,#333333 50%,#333333 100%) no-repeat;background-size:100% 10px;background-color:#3333331a}:host.cds-payment-date{background:linear-gradient(to right,#4C9DA0 50%,#4C9DA0 100%) no-repeat;background-size:100% 10px;background-color:#4c9da01a}:host.today{font-family:Interstate_Bold;text-decoration:underline;text-underline-offset:3px;text-decoration-thickness:3px}:host.today .text{display:none}:host.next-month-first{padding-top:7px}:host.next-month-first .extra-month{line-height:15px;font-size:10px;color:#666}:host .text{line-height:14px;font-size:12px;font-family:Interstate_Light;color:#333;padding:0 2px}:host button{border:0px;color:#056dae;background:transparent}:host.empty{cursor:default;pointer-events:auto;touch-action:auto}:host.disabled{cursor:default;color:#aaa}:host.disabled:hover{box-shadow:none}:host.disabled .day-number{color:#aaa}:host .responsive-text{display:none}:host.selected{padding-top:22.34px;color:#fff}:host.selected.disabled .day-number{color:#fcfcfc}:host.selected .text{color:#fff}:host.responsive{width:42.24px;height:55px;padding-top:9px;margin:0}:host.responsive.cds-success{background-size:100% 6px}:host.responsive.cds-success .day-number{font-size:18px;line-height:16px}:host.responsive.cds-warning{background-size:100% 6px}:host.responsive.cds-warning .day-number{font-size:18px;line-height:16px}:host.responsive.cds-critical{background-size:100% 6px;color:#d60000}:host.responsive.cds-critical .text{color:#d60000}:host.responsive.cds-critical .day-number{font-size:18px;line-height:16px}:host.responsive.cds-informative{background-size:100% 6px}:host.responsive.cds-informative .day-number{font-size:18px;line-height:16px}:host.responsive.cds-payment-date{background-size:100% 6px}:host.responsive.cds-payment-date .day-number{font-size:18px;line-height:16px}:host.responsive.next-month-first{padding-top:0;line-height:17px}:host.responsive.next-month-first .extra-month{line-height:15px;font-size:10px;color:#aaa}:host.responsive .responsive-text{display:block;line-height:14px;font-size:12px;font-family:Interstate_Light;color:#333}:host.responsive.selected{padding-top:9px!important;text-align:center}:host.responsive.selected .day-number{font-size:21px;color:#fff;line-height:27px}:host.responsive.selected .text{display:none}:host.responsive .text{font-size:10px;line-height:10px;margin:2px}@media only screen and (max-width: 780px){:host{width:42.24px;height:55px;padding-top:9px;margin:0}:host.cds-success{background-size:100% 6px}:host.cds-success .day-number{font-size:18px;line-height:16px}:host.cds-warning{background-size:100% 6px}:host.cds-warning .day-number{font-size:18px;line-height:16px}:host.cds-critical{background-size:100% 6px;color:#d60000}:host.cds-critical .text{color:#d60000}:host.cds-critical .day-number{font-size:18px;line-height:16px}:host.cds-informative{background-size:100% 6px}:host.cds-informative .day-number{font-size:18px;line-height:16px}:host.cds-payment-date{background-size:100% 6px}:host.cds-payment-date .day-number{font-size:18px;line-height:16px}:host.next-month-first{padding-top:0;line-height:17px}:host.next-month-first .extra-month{line-height:15px;font-size:10px;color:#666}:host .responsive-text{display:block;line-height:14px;font-size:12px;font-family:Interstate_Light;color:#333}:host.selected{padding-top:9px!important;text-align:center}:host.selected .day-number{font-size:21px;color:#fff;line-height:27px}:host.selected .text{display:none}:host .text{font-size:10px;line-height:10px;margin:2px}}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DayComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-day', template: `
    <div>
      <!-- <div class='extra-month' *ngIf="day.classes == 'next-month-first'">{{ !!day.month ? day.month : ''}}</div> -->
      <div class='day-number'>{{ day.day }}</div>
      <!-- <div class='text'>{{ day.text }}</div> -->
    </div>
    `, host: { class: 'cds-day' }, styles: [":host{display:flex;border-radius:4px;line-height:27px;width:calc((100% - 7px) / 7);margin:2px;text-align:center;justify-content:center;border:1px solid #EEEEEE;padding-top:22.34px;width:80px;height:80px;font-size:21px;font-family:Interstate_Regular;pointer-events:auto;touch-action:auto}:host.cds-success{background:linear-gradient(to right,#006E0A 50%,#006E0A 100%) no-repeat;background-size:100% 10px;background-color:#006e0a1a}:host.cds-warning{background:linear-gradient(to right,#FFCA00 50%,#FFCA00 100%) no-repeat;background-size:100% 10px;background-color:#ffca001a}:host.cds-critical{background:linear-gradient(to right,#D60000 50%,#D60000 100%) no-repeat;background-size:100% 10px;color:#d60000;background-color:#d600001a}:host.cds-critical .text{color:#d60000}:host.cds-informative{background:linear-gradient(to right,#333333 50%,#333333 100%) no-repeat;background-size:100% 10px;background-color:#3333331a}:host.cds-payment-date{background:linear-gradient(to right,#4C9DA0 50%,#4C9DA0 100%) no-repeat;background-size:100% 10px;background-color:#4c9da01a}:host.today{font-family:Interstate_Bold;text-decoration:underline;text-underline-offset:3px;text-decoration-thickness:3px}:host.today .text{display:none}:host.next-month-first{padding-top:7px}:host.next-month-first .extra-month{line-height:15px;font-size:10px;color:#666}:host .text{line-height:14px;font-size:12px;font-family:Interstate_Light;color:#333;padding:0 2px}:host button{border:0px;color:#056dae;background:transparent}:host.empty{cursor:default;pointer-events:auto;touch-action:auto}:host.disabled{cursor:default;color:#aaa}:host.disabled:hover{box-shadow:none}:host.disabled .day-number{color:#aaa}:host .responsive-text{display:none}:host.selected{padding-top:22.34px;color:#fff}:host.selected.disabled .day-number{color:#fcfcfc}:host.selected .text{color:#fff}:host.responsive{width:42.24px;height:55px;padding-top:9px;margin:0}:host.responsive.cds-success{background-size:100% 6px}:host.responsive.cds-success .day-number{font-size:18px;line-height:16px}:host.responsive.cds-warning{background-size:100% 6px}:host.responsive.cds-warning .day-number{font-size:18px;line-height:16px}:host.responsive.cds-critical{background-size:100% 6px;color:#d60000}:host.responsive.cds-critical .text{color:#d60000}:host.responsive.cds-critical .day-number{font-size:18px;line-height:16px}:host.responsive.cds-informative{background-size:100% 6px}:host.responsive.cds-informative .day-number{font-size:18px;line-height:16px}:host.responsive.cds-payment-date{background-size:100% 6px}:host.responsive.cds-payment-date .day-number{font-size:18px;line-height:16px}:host.responsive.next-month-first{padding-top:0;line-height:17px}:host.responsive.next-month-first .extra-month{line-height:15px;font-size:10px;color:#aaa}:host.responsive .responsive-text{display:block;line-height:14px;font-size:12px;font-family:Interstate_Light;color:#333}:host.responsive.selected{padding-top:9px!important;text-align:center}:host.responsive.selected .day-number{font-size:21px;color:#fff;line-height:27px}:host.responsive.selected .text{display:none}:host.responsive .text{font-size:10px;line-height:10px;margin:2px}@media only screen and (max-width: 780px){:host{width:42.24px;height:55px;padding-top:9px;margin:0}:host.cds-success{background-size:100% 6px}:host.cds-success .day-number{font-size:18px;line-height:16px}:host.cds-warning{background-size:100% 6px}:host.cds-warning .day-number{font-size:18px;line-height:16px}:host.cds-critical{background-size:100% 6px;color:#d60000}:host.cds-critical .text{color:#d60000}:host.cds-critical .day-number{font-size:18px;line-height:16px}:host.cds-informative{background-size:100% 6px}:host.cds-informative .day-number{font-size:18px;line-height:16px}:host.cds-payment-date{background-size:100% 6px}:host.cds-payment-date .day-number{font-size:18px;line-height:16px}:host.next-month-first{padding-top:0;line-height:17px}:host.next-month-first .extra-month{line-height:15px;font-size:10px;color:#666}:host .responsive-text{display:block;line-height:14px;font-size:12px;font-family:Interstate_Light;color:#333}:host.selected{padding-top:9px!important;text-align:center}:host.selected .day-number{font-size:21px;color:#fff;line-height:27px}:host.selected .text{display:none}:host .text{font-size:10px;line-height:10px;margin:2px}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { day: [{
                type: Input
            }], disabled: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], isSmall: [{
                type: Input
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], ariaLabel: [{
                type: HostBinding,
                args: ['attr.aria-label']
            }], setDisabled: [{
                type: HostBinding,
                args: ['attr.aria-disabled']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], dayStyle: [{
                type: HostBinding,
                args: ['style']
            }], newValue: [{
                type: HostListener,
                args: ['keydown.Space']
            }, {
                type: HostListener,
                args: ['keydown.Enter']
            }, {
                type: HostListener,
                args: ['click']
            }], daySelected: [{
                type: Output
            }] } });

class MonthComponent {
    changeMonth(event, direction) {
        event.stopPropagation();
        event.preventDefault();
        direction === 'next'
            ? this.nextMonth.emit('month')
            : this.prevMonth.emit('month');
    }
    checkMin() {
        let lastday = new Date(this.year, this.numMonth + 1, 0).getDate();
        let cur = new Date(this.year, this.numMonth - 1, lastday);
        if (this.minDate > cur) {
            return true;
        }
        else {
            return false;
        }
    }
    checkMax() {
        let lastday = new Date(this.year, this.numMonth + 1, 0).getDate();
        let cur = new Date(this.year, this.numMonth, lastday);
        if (cur > this.maxDate) {
            return true;
        }
        else {
            return false;
        }
    }
    constructor() {
        this.prevMonth = new EventEmitter();
        this.nextMonth = new EventEmitter();
    }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: MonthComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: MonthComponent, selector: "cds-month", inputs: { month: "month", year: "year", disabled: "disabled", maxDate: "maxDate", minDate: "minDate", numMonth: "numMonth", nextMonthLabel: "nextMonthLabel", prevMonthLabel: "prevMonthLabel", isSmall: "isSmall" }, outputs: { prevMonth: "prevMonth", nextMonth: "nextMonth" }, ngImport: i0, template: `
    <div class="cds-calendar-header" [class.responsive]="isSmall">
      <div class="nav">
        <button
          type="button"
          role="button"
          class="cds-calendar-nav"
          (click)="changeMonth($event, 'prev')"
          (keydown.enter)="changeMonth($event, 'prev')"
          (keydown.space)="changeMonth($event, 'prev')"
          [disabled]="disabled || checkMin()"
          [attr.aria-label]="'Previous Month, ' + prevMonthLabel"
        >
          <cds-icon
            type="2021"
            name="03-left-float"
            shade="dark"
            [svgStyle]="{ 'width.px': '32', 'height.px': '32' }"
            altText="previous month"
            ariaLabel="Previous Month"
          ></cds-icon>
        </button>
      </div>
      <div class="middle">{{ this.month }} {{ this.year }}</div>
      <div class="nav">
        <button
          type="button"
          role="button"
          class="cds-calendar-nav"
          (click)="changeMonth($event, 'next')"
          (keydown.enter)="changeMonth($event, 'next')"
          (keydown.space)="changeMonth($event, 'next')"
          [disabled]="disabled || checkMax()"
          [attr.aria-label]="'Next Month, ' + nextMonthLabel"
        >
          <cds-icon
            type="2021"
            name="04-right-float"
            shade="dark"
            [isHoverSfi]="false"
            [svgStyle]="{ 'width.px': '32', 'height.px': '32' }"
            altText="next month"
            ariaLabel="Next Month"
          ></cds-icon>
        </button>
      </div>
    </div>
  `, isInline: true, styles: [".cds-calendar-header{display:flex;border-top:1px dashed #DDDDDD;border-bottom:1px dashed #DDDDDD;margin-bottom:24px;flex-direction:row;height:66px;flex:1}.cds-calendar-header.responsive{height:60.49px}.cds-calendar-header .middle{text-align:center;margin:15px 0;width:calc(100% - (100% - 7px) / 7 * 2);font-size:21px;line-height:27px;font-family:Interstate_Regular}.cds-calendar-header .nav{width:calc((100% - 7px) / 7);margin:15px 0;text-align:center;line-height:26px}.cds-calendar-header .nav button:active ::ng-deep path{fill:#002a54}.cds-calendar-header .nav button:disabled{cursor:default}.cds-calendar-header .nav button:disabled ::ng-deep path{fill:#ccc}.cds-calendar-header .right{text-align:right}.cds-calendar-nav{width:32px;height:32px;background:none;border:none;padding:0}\n"], dependencies: [{ kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: MonthComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-month', template: `
    <div class="cds-calendar-header" [class.responsive]="isSmall">
      <div class="nav">
        <button
          type="button"
          role="button"
          class="cds-calendar-nav"
          (click)="changeMonth($event, 'prev')"
          (keydown.enter)="changeMonth($event, 'prev')"
          (keydown.space)="changeMonth($event, 'prev')"
          [disabled]="disabled || checkMin()"
          [attr.aria-label]="'Previous Month, ' + prevMonthLabel"
        >
          <cds-icon
            type="2021"
            name="03-left-float"
            shade="dark"
            [svgStyle]="{ 'width.px': '32', 'height.px': '32' }"
            altText="previous month"
            ariaLabel="Previous Month"
          ></cds-icon>
        </button>
      </div>
      <div class="middle">{{ this.month }} {{ this.year }}</div>
      <div class="nav">
        <button
          type="button"
          role="button"
          class="cds-calendar-nav"
          (click)="changeMonth($event, 'next')"
          (keydown.enter)="changeMonth($event, 'next')"
          (keydown.space)="changeMonth($event, 'next')"
          [disabled]="disabled || checkMax()"
          [attr.aria-label]="'Next Month, ' + nextMonthLabel"
        >
          <cds-icon
            type="2021"
            name="04-right-float"
            shade="dark"
            [isHoverSfi]="false"
            [svgStyle]="{ 'width.px': '32', 'height.px': '32' }"
            altText="next month"
            ariaLabel="Next Month"
          ></cds-icon>
        </button>
      </div>
    </div>
  `, styles: [".cds-calendar-header{display:flex;border-top:1px dashed #DDDDDD;border-bottom:1px dashed #DDDDDD;margin-bottom:24px;flex-direction:row;height:66px;flex:1}.cds-calendar-header.responsive{height:60.49px}.cds-calendar-header .middle{text-align:center;margin:15px 0;width:calc(100% - (100% - 7px) / 7 * 2);font-size:21px;line-height:27px;font-family:Interstate_Regular}.cds-calendar-header .nav{width:calc((100% - 7px) / 7);margin:15px 0;text-align:center;line-height:26px}.cds-calendar-header .nav button:active ::ng-deep path{fill:#002a54}.cds-calendar-header .nav button:disabled{cursor:default}.cds-calendar-header .nav button:disabled ::ng-deep path{fill:#ccc}.cds-calendar-header .right{text-align:right}.cds-calendar-nav{width:32px;height:32px;background:none;border:none;padding:0}\n"] }]
        }], ctorParameters: () => [], propDecorators: { month: [{
                type: Input
            }], year: [{
                type: Input
            }], disabled: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], minDate: [{
                type: Input
            }], numMonth: [{
                type: Input
            }], nextMonthLabel: [{
                type: Input
            }], prevMonthLabel: [{
                type: Input
            }], isSmall: [{
                type: Input
            }], prevMonth: [{
                type: Output
            }], nextMonth: [{
                type: Output
            }] } });

class InputMonthComponent {
    changeMonth(event, direction) {
        if (direction === 'month') {
            this.monthChange.emit(this.calendarForm.get(['month']).value);
        }
        else {
            this.yearChange.emit(this.calendarForm.get(['year']).value);
        }
    }
    constructor() {
        this.disabled = false;
        this.isSmall = false;
        this.monthChange = new EventEmitter();
        this.yearChange = new EventEmitter();
    }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: InputMonthComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: InputMonthComponent, selector: "cds-input-month", inputs: { month: "month", year: "year", maxDate: "maxDate", minDate: "minDate", months: "months", years: "years", disabled: "disabled", calendarForm: "calendarForm", isSmall: "isSmall" }, outputs: { monthChange: "monthChange", yearChange: "yearChange" }, ngImport: i0, template: `
    <div class="cds-calendar-header" [class.responsive]="isSmall">
      <form [formGroup]="calendarForm">
        <div class="dropdown">
          <cds-form-field #field [disabled]="disabled">
            <label cdsLabel></label>
            <cds-dropdown
              formControlName="month"
              (panelClose)="changeMonth($event, 'month')"
              [disabled]="disabled"
            >
              <cds-option
                *ngFor="let month of months"
                [value]="month.value"
                [label]="month.label"
              >
              </cds-option>
            </cds-dropdown>
          </cds-form-field>
        </div>
        <div class="dropdown">
          <cds-form-field [disabled]="disabled">
            <label cdsLabel></label>
            <cds-dropdown
              formControlName="year"
              (panelClose)="changeMonth($event, 'year')"
              [disabled]="disabled"
            >
              <cds-option
                *ngFor="let year of years; let i = index"
                [value]="year"
                [label]="year.toString()"
              >
              </cds-option>
            </cds-dropdown>
          </cds-form-field>
        </div>
      </form>
    </div>
  `, isInline: true, styles: [".cds-calendar-header{display:flex;border-top:1px solid #DDDDDD;border-bottom:1px solid #DDDDDD;margin-bottom:24px;flex-direction:row;padding:0 20px;flex:1;height:66px}.cds-calendar-header .dropdown{display:inline-block;width:276px;margin:9px 5px;vertical-align:middle}.cds-calendar-header .cds-label{width:0}.cds-calendar-header.responsive{height:60.41px;padding:0 7px}.cds-calendar-header.responsive .dropdown{width:146.93px;margin:7px 3px}@media only screen and (max-width: 780px){.cds-calendar-header{padding:0 7px}.cds-calendar-header .cds-calendar-header{height:60.41px}.cds-calendar-header .dropdown{width:146.93px;margin:7px 3px}}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1$3.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i1$3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$3.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "component", type: CdsDropdown, selector: "cds-dropdown", inputs: ["disabled", "formRef", "placeholder", "selected", "idStr", "required", "value", "optionsClass"], outputs: ["selectionChange", "panelClose", "blur"] }, { kind: "component", type: CdsOption, selector: "cds-option", inputs: ["value", "disabled", "label", "id"], outputs: ["click", "ddValue"] }, { kind: "directive", type: i1$3.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i1$3.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: CdsFormField, selector: "cds-form-field,fieldset[cds-form-field],fieldset[cdsFormField]", inputs: ["disabled", "toggleWarningIcon"] }, { kind: "directive", type: CdsFormLabel, selector: "label[cdsLabel]", inputs: ["static"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: InputMonthComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-input-month', template: `
    <div class="cds-calendar-header" [class.responsive]="isSmall">
      <form [formGroup]="calendarForm">
        <div class="dropdown">
          <cds-form-field #field [disabled]="disabled">
            <label cdsLabel></label>
            <cds-dropdown
              formControlName="month"
              (panelClose)="changeMonth($event, 'month')"
              [disabled]="disabled"
            >
              <cds-option
                *ngFor="let month of months"
                [value]="month.value"
                [label]="month.label"
              >
              </cds-option>
            </cds-dropdown>
          </cds-form-field>
        </div>
        <div class="dropdown">
          <cds-form-field [disabled]="disabled">
            <label cdsLabel></label>
            <cds-dropdown
              formControlName="year"
              (panelClose)="changeMonth($event, 'year')"
              [disabled]="disabled"
            >
              <cds-option
                *ngFor="let year of years; let i = index"
                [value]="year"
                [label]="year.toString()"
              >
              </cds-option>
            </cds-dropdown>
          </cds-form-field>
        </div>
      </form>
    </div>
  `, styles: [".cds-calendar-header{display:flex;border-top:1px solid #DDDDDD;border-bottom:1px solid #DDDDDD;margin-bottom:24px;flex-direction:row;padding:0 20px;flex:1;height:66px}.cds-calendar-header .dropdown{display:inline-block;width:276px;margin:9px 5px;vertical-align:middle}.cds-calendar-header .cds-label{width:0}.cds-calendar-header.responsive{height:60.41px;padding:0 7px}.cds-calendar-header.responsive .dropdown{width:146.93px;margin:7px 3px}@media only screen and (max-width: 780px){.cds-calendar-header{padding:0 7px}.cds-calendar-header .cds-calendar-header{height:60.41px}.cds-calendar-header .dropdown{width:146.93px;margin:7px 3px}}\n"] }]
        }], ctorParameters: () => [], propDecorators: { month: [{
                type: Input
            }], year: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], minDate: [{
                type: Input
            }], months: [{
                type: Input
            }], years: [{
                type: Input
            }], disabled: [{
                type: Input
            }], calendarForm: [{
                type: Input
            }], isSmall: [{
                type: Input
            }], monthChange: [{
                type: Output
            }], yearChange: [{
                type: Output
            }] } });

let nextUniqueId$3 = 0;
class CdsCalendar {
    set value(value) {
        this._value = value;
        this._dateObj = new Date(value);
        this.month = this._dateObj.getMonth();
        this.year = this._dateObj.getFullYear();
        this.getDays();
    }
    set maxDate(value) {
        this._maxDate = new Date(value);
    }
    set minDate(value) {
        this._minDate = new Date(value);
    }
    set disableDays(value) {
        if (value) {
            value.forEach(day => this._disableDays.push(this.weekdays.findIndex(weekday => weekday == day)));
        }
        else {
            this._disableDays = [];
        }
    }
    ;
    set disableSpecificDays(value) {
        if (value) {
            value.forEach(day => this._disableDates.push(new Date(day)));
        }
        else {
            this._disableDates = [];
        }
    }
    ;
    // -------------       Constructor             --------------- //
    constructor(element, id, _ngZone, liveannouncer, fb) {
        this.element = element;
        this._ngZone = _ngZone;
        this.liveannouncer = liveannouncer;
        this.fb = fb;
        this.fieldType = 'calendar';
        /**
         * Implemented as part of CdsFormBase.
         */
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        this.months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
        ];
        this.inputMonths = [
            { value: 0, label: 'January' },
            { value: 1, label: 'February' },
            { value: 2, label: 'March' },
            { value: 3, label: 'April' },
            { value: 4, label: 'May' },
            { value: 5, label: 'June' },
            { value: 6, label: 'July' },
            { value: 7, label: 'August' },
            { value: 8, label: 'September' },
            { value: 9, label: 'October' },
            { value: 10, label: 'November' },
            { value: 11, label: 'December' }
        ];
        this.filteredMonths = [];
        this.monthsShort = [
            'Jan',
            'Feb',
            'Mar',
            'Apr',
            'May',
            'Jun',
            'Jul',
            'Aug',
            'Sep',
            'Oct',
            'Nov',
            'Dec'
        ];
        this.weekdays = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
        this.fullName = [
            'Sunday',
            'Monday',
            'Tuesday',
            'Wednesday',
            'Thursday',
            'Friday',
            'Saturday'
        ];
        this.today = new Date();
        this._disableDays = [];
        this._disableDates = [];
        this.years = [
            1987,
            1988,
            1989,
            1990,
            1991,
            1992,
            1993,
            1994,
            1995,
            1996,
            1997,
            1998,
            1999,
            2000,
            2001,
            2002,
            2003,
            2004,
            2005,
            2006,
            2007,
            2008,
            2009,
            2010,
            2011,
            2012,
            2013,
            2014,
            2015,
            2016,
            2017,
            2018,
            2019,
            2020,
            2021
        ];
        // -------------       Outputs             --------------- //
        this.valueChange = new EventEmitter();
        this.disabled = false;
        this.fromPopupPanel = false;
        this.dateFormat = 'dd/MM/yyyy';
        this.localString = 'en-US';
        this.styledDays = [];
        this.inputNav = false;
        this.isSmall = false;
        // -------------       Control Value Accessor             --------------- //
        this.onChange = (value) => { };
        this.propagateTouched = () => { };
        this.fieldId = id || `cds-calendar-${nextUniqueId$3++}`;
        setTimeout(() => this.checkValue());
        this.calendarForm = this.fb.group({
            month: new FormControl({ value: null, disabled: false }),
            year: new FormControl({ value: null, disabled: false })
        });
    }
    focusChanged(isFocused) {
        if (!this.element.nativeElement.hasAttribute('disabled') || !isFocused)
            this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.element.nativeElement.value);
    }
    // -------------       Life Cycle Hooks             --------------- //
    ngAfterViewInit() {
        if (!!this.fromPopupPanel) {
            setTimeout(() => {
                this.applyFocus();
            }, 100);
        }
    }
    ngAfterContentInit() {
        this.calendarForm.patchValue({
            month: `${this.month}`,
            year: `${this.year}`
        });
        this.updateInputMonths();
        this.getDays();
        this.focusDate(this._value);
    }
    ngOnInit() {
        this.year = this.today.getFullYear();
        this.month = this.today.getMonth();
        if (this._maxDate && this._minDate) {
            this.years = [];
            let max = this._maxDate.getFullYear();
            let min = this._minDate.getFullYear();
            for (let i = min; i <= max; i++) {
                this.years.push(i);
            }
        }
        this._currentfocusDay = new Date(this.year, this.month, 1).getDay();
        this._currentfocusDay = this._currentfocusDay + this.today.getDate() - 1;
        this.getDays();
    }
    // -------------       Methods             --------------- //
    onKeyDown(e) {
        let firstday = new Date(this.year, this.month, 1);
        let nextFirstDay = new Date(this.year, this.month + 1, 1);
        let prevFirstDay = new Date(this.year, this.month - 1, 1);
        let lastday = new Date(this.year, this.month + 1, 0).getDate();
        let prevlastday = new Date(this.year, this.month, 0).getDate();
        var prevoffset = prevFirstDay.getDay();
        var nextoffset = nextFirstDay.getDay();
        var offset = firstday.getDay();
        let changeFocusDate = this._currentfocusDay;
        const keyCode = e.keyCode;
        switch (keyCode) {
            case KEY_ARROW_DOWN: {
                e.preventDefault();
                e.stopPropagation();
                if (this._currentfocusDay + 7 >= lastday + offset) {
                    if (nextoffset > this._currentfocusDay % 7) {
                        changeFocusDate = 7 + (this._currentfocusDay % 7);
                    }
                    else {
                        changeFocusDate = this._currentfocusDay % 7;
                    }
                    if (!this.checkMaxMin(this.month + 1, this.year, changeFocusDate - nextoffset)) {
                        this.nextMonth();
                    }
                }
                else {
                    changeFocusDate += 7;
                }
                break;
            }
            case KEY_ARROW_UP: {
                e.preventDefault();
                e.stopPropagation();
                if (this._currentfocusDay - 7 < offset) {
                    let remainder = Math.abs(7 - (this._currentfocusDay - offset));
                    changeFocusDate = prevlastday + prevoffset - remainder;
                    if (!this.checkMaxMin(this.month - 1, this.year, changeFocusDate - prevoffset)) {
                        this.prevMonth();
                    }
                }
                else {
                    changeFocusDate -= 7;
                }
                break;
            }
            case KEY_ARROW_LEFT: {
                e.preventDefault();
                e.stopPropagation();
                changeFocusDate = this.prevAvailableDate(changeFocusDate - 1);
                break;
            }
            case KEY_ARROW_RIGHT: {
                e.preventDefault();
                e.stopPropagation();
                changeFocusDate = this.nextAvailableDate(changeFocusDate + 1);
                break;
            }
            case e.shiftKey && KEY_TAB: {
                break;
            }
            case KEY_TAB: {
                break;
            }
        }
        let newfirstday = new Date(this.year, this.month, 1);
        var newoffset = firstday.getDay();
        if (!this.checkMaxMin(this.month, this.year, changeFocusDate - newoffset + 1)) {
            this._currentfocusDay = changeFocusDate;
        }
        this.applyFocus();
    }
    executeOnStable(func) {
        this._ngZone.runOutsideAngular(() => {
            this._ngZone.onStable
                .asObservable()
                .pipe(take(1))
                .subscribe(() => func());
        });
    }
    applyFocus() {
        this.executeOnStable(() => {
            const focusDay = this.element.nativeElement.querySelectorAll('td.cds-cell');
            focusDay[this._currentfocusDay].focus();
        });
    }
    getDateAriaLabel(index) {
        let curLabel = `${this.days[index].curMonth} ${this.days[index].day}, ${this.days[index].curYear} ${this.days[index].weekday} button ${this.days[index].text ? this.days[index].text : ''} `;
        if (this.days[index].classes.includes('selected')) {
            return curLabel + ' selected';
        }
        else if (this.days[index].classes.includes('empty')) {
            return;
        }
        return curLabel;
    }
    getCheckDisabled(index) {
        return (this.disabled ||
            this._disableDays.includes(index % 7) ||
            this.checkMaxMin(this.month, this.year, this.days[index].day) ||
            this.days[index].classes.includes('prev') ||
            this.days[index].classes.includes('next') ||
            this.days[index].classes.includes('disabled'));
    }
    addFocus(row, column) {
        if (!this.disabled && this._currentfocusDay === row * 7 + column) {
            return ' cds-modal-initial-focus';
        }
        return '';
    }
    nextAvailableDate(index) {
        let curIndex;
        let nextFirstDay = new Date(this.year, this.month + 1, 1);
        var nextoffset = nextFirstDay.getDay();
        for (let i = index; i < this.days.length; i++) {
            if (!this.getCheckDisabled(i)) {
                curIndex = i;
                break;
            }
        }
        if (curIndex === undefined) {
            return index - 1;
        }
        return curIndex;
    }
    prevAvailableDate(index) {
        let curIndex;
        let prevFirstDay = new Date(this.year, this.month - 1, 1);
        let prevlastday = new Date(this.year, this.month, 0).getDate();
        let prevoffset = prevFirstDay.getDay();
        for (let i = index; i > 0; i--) {
            if (!this.getCheckDisabled(i)) {
                curIndex = i;
                break;
            }
        }
        if (curIndex === undefined) {
            return index + 1;
        }
        return curIndex;
    }
    newValue(value, event = null) {
        if (event)
            event.preventDefault();
        let date = (this.month + 1).toString() + '/' + value + '/' + this.year.toString();
        let parsedDate = this.parseDate(date);
        this.dateStr = this.formatedDate(parsedDate);
        this.valueChange.emit(this.dateStr);
        this.value = this.dateStr;
        this.focusDate(this.dateStr);
        this.onChange(this._value);
        this.propagateTouched();
    }
    focusDate(date) {
        if (!date)
            return;
        let parsedDate = this.parseDate(date);
        if (parsedDate.getMonth() !== this.month ||
            parsedDate.getFullYear() != this.year)
            return;
        let day = parsedDate.getDate().toString();
        let currentIndex = this.days.findIndex(t => t.day == day && !t.classes.includes('prev'));
        if (this.getCheckDisabled(currentIndex))
            return;
        this._currentfocusDay = currentIndex;
        this.days[currentIndex].classes += ' selected';
        this.propagateTouched();
    }
    // -------------       Input Parsing Methods             --------------- //
    parseDate(str) {
        let date;
        if (this.dateParseFn) {
            date = this.dateParseFn(str);
        }
        else {
            date = new Date(Date.parse(str));
        }
        let dateIsInvalid = isNaN(date.getTime());
        const isBlankDate = str === '';
        if (!str || str === '' || dateIsInvalid) {
            this.writeValue(undefined);
            if (dateIsInvalid && !isBlankDate)
                this.writeValue(undefined);
        }
        else
            return date;
    }
    ;
    getDays() {
        let firstday = new Date(this.year, this.month, 1);
        let lastPreviousMonth = new Date(this.year, this.month, 0).getDate();
        let lastday = new Date(this.year, this.month + 1, 0).getDate();
        var offset = firstday.getDay();
        this.days = [];
        for (let i = offset - 1; i >= 0; i--) {
            let month = this.months[this.month - 1] ? this.month - 1 : 11;
            let year = this.month === 0 ? this.year - 1 : this.year;
            let date = new Date(year, month, lastPreviousMonth - i);
            this.days.push({
                day: (lastPreviousMonth - i).toString(),
                classes: 'prev',
                text: '',
                curMonth: this.months[month],
                curYear: year,
                weekday: this.fullName[offset - i - 1]
            });
            if (date.toString() === new Date(this._value).toString()) {
                this.days[this.days.length - 1].classes += ' selected';
            }
        }
        for (let i = 1; i <= lastday; i++) {
            if (this.checkMonthYearDay(this.today, i)) {
                this.days.push({
                    day: i.toString(),
                    classes: 'today',
                    curMonth: this.months[this.month],
                    curYear: this.year,
                    weekday: this.fullName[(offset + i - 1) % 7],
                    text: "Today's Date"
                });
                i++;
            }
            if (i <= lastday) {
                this.days.push({
                    day: i.toString(),
                    classes: '',
                    text: '',
                    curMonth: this.months[this.month],
                    curYear: this.year,
                    weekday: this.fullName[(offset + i - 1) % 7]
                });
            }
        }
        for (let i = 1; i <= 42 - (offset + lastday); i++) {
            let month = this.months[this.month + 1] ? this.month + 1 : 0;
            let year = this.month === 11 ? this.year + 1 : this.year;
            let date = new Date(year, month, i);
            this.days.push({
                day: i.toString(),
                classes: 'next',
                text: '',
                curMonth: this.months[month],
                weekday: this.fullName[(offset + lastday + i - 1) % 7],
                curYear: year
            });
            if (date.toString() === new Date(this._value).toString()) {
                this.days[this.days.length - 1].classes += ' selected';
            }
        }
        this._disableDates.forEach(disabledDay => {
            if (this.days[offset + disabledDay.getDate() - 1].curMonth ===
                this.months[disabledDay.getMonth()] &&
                this.days[offset + disabledDay.getDate() - 1].curYear ===
                    disabledDay.getFullYear() &&
                this.days[offset + disabledDay.getDate() - 1].day ==
                    disabledDay.getDate().toString()) {
                this.days[offset + disabledDay.getDate() - 1].classes += ' disabled';
            }
        });
        this.focusDate(this._value);
    }
    checkMonthYearDay(strValue, day) {
        let value = new Date(strValue);
        if (value &&
            value.getDate() == day &&
            value.getMonth() == this.month &&
            value.getFullYear() == this.year) {
            return true;
        }
        return false;
    }
    nextMonth(fromClick = null) {
        if (this.months[this.month + 1]) {
            this.month += 1;
        }
        else {
            this.year += 1;
            this.month = 0;
        }
        this.announceMonthYear();
        this.calendarForm.patchValue({
            month: `${this.month}`,
            year: `${this.year}`
        });
        if (fromClick)
            this.updateFocusDateOnClick();
        this.getDays();
    }
    prevMonth(fromClick = null) {
        if (this.months[this.month - 1]) {
            this.month -= 1;
        }
        else {
            this.year -= 1;
            this.month = 11;
        }
        this.announceMonthYear();
        this.calendarForm.patchValue({
            month: `${this.month}`,
            year: `${this.year}`
        });
        if (fromClick)
            this.updateFocusDateOnClick();
        this.getDays();
    }
    updateFocusDateOnClick() {
        this.getDays();
        setTimeout(() => {
            this._currentfocusDay = this.nextAvailableDate(new Date(this.year, this.month, 1).getDay());
            if (this.today &&
                this.today.getFullYear() == this.year &&
                this.today.getMonth() == this.month) {
                this._currentfocusDay =
                    this._currentfocusDay + this.today.getDate() - 1;
            }
        }, 100);
    }
    announceMonthYear() {
        this.liveannouncer.announce(this.months[this.month] + ', ' + this.year);
    }
    monthChange(month) {
        if (month)
            this.month = parseInt(month);
        this.updateFocusDateOnClick();
        this.updateInputMonths();
    }
    yearChange(year) {
        let parsedYear = parseInt(year);
        if (year) {
            if (this.checkMaxMin(this.month, parsedYear, 0) &&
                parsedYear == this._maxDate.getFullYear()) {
                this.year = parsedYear;
                this.month = this._maxDate.getMonth();
                this.calendarForm.patchValue({
                    month: `${this.month}`
                });
            }
            else {
                this.year = parsedYear;
            }
        }
        this.updateFocusDateOnClick();
        this.updateInputMonths();
    }
    updateInputMonths() {
        if (this._minDate && this.year == this._minDate.getFullYear()) {
            this.filteredMonths = this.inputMonths.slice(this._minDate.getMonth());
        }
        else if (this._maxDate && this.year == this._maxDate.getFullYear()) {
            this.filteredMonths = this.inputMonths.slice(0, this._maxDate.getMonth() + 1);
        }
        else {
            this.filteredMonths = this.inputMonths;
        }
    }
    checkMaxMin(month, year, day) {
        let date = new Date(year, month, day, 0, 0, 0, 0);
        if (this._minDate > date || date > this._maxDate) {
            return true;
        }
        else {
            return false;
        }
    }
    formatedDate(parsedDate) {
        return formatDate(parsedDate, this.dateFormat, this.localString);
    }
    writeValue(value) {
        if (!!value) {
            let parsedDate = this.parseDate(value);
            this.dateStr = this.formatedDate(parsedDate);
            this.focusDate(value);
            this.value = this.dateStr;
            this.onChange(this.value);
            this.propagateTouched();
        }
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.propagateTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCalendar, deps: [{ token: i0.ElementRef }, { token: 'id', attribute: true }, { token: i0.NgZone }, { token: i1$1.LiveAnnouncer }, { token: i1$3.FormBuilder }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCalendar, selector: "cds-calendar", inputs: { dateParseFn: "dateParseFn", value: "value", disabled: "disabled", fromPopupPanel: "fromPopupPanel", dateFormat: "dateFormat", localString: "localString", styledDays: "styledDays", inputNav: "inputNav", maxDate: "maxDate", minDate: "minDate", disableDays: "disableDays", disableSpecificDays: "disableSpecificDays", isSmall: "isSmall" }, outputs: { valueChange: "valueChange" }, host: { listeners: { "focusin": "focusChanged(true)", "focusout": "focusChanged(false)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId" }, classAttribute: "cds-calendar" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCalendar)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsCalendar }
        ], ngImport: i0, template: `
    <div class="cds-calendar-wrapper" [class.responsive]="isSmall">
      <cds-month
        *ngIf="!inputNav"
        [month]="months[this.month]"
        [nextMonthLabel]="
          months[month + 1]
            ? months[month + 1] + ', ' + year
            : months[0] + ', ' + (year + 1)
        "
        [prevMonthLabel]="
          month - 1 >= 0
            ? months[month - 1] + ', ' + year
            : months[11] + ', ' + (year - 1)
        "
        [numMonth]="month"
        [year]="year"
        [disabled]="this.disabled"
        (nextMonth)="nextMonth($event)"
        (prevMonth)="prevMonth($event)"
        [minDate]="_minDate"
        [maxDate]="_maxDate"
        [isSmall]="isSmall"
      ></cds-month>
      <cds-input-month
        *ngIf="inputNav"
        [month]="month"
        [year]="year"
        [months]="filteredMonths"
        [years]="years"
        [disabled]="disabled"
        (monthChange)="monthChange($event)"
        (yearChange)="yearChange($event)"
        [calendarForm]="calendarForm"
        [isSmall]="isSmall"
      >
      </cds-input-month>
      <table (keydown)="onKeyDown($event)">
        <thead>
          <tr class="cds-weekdays">
            <th scope="col" class="weekday" *ngFor="let day of weekdays">
              {{ day }}
            </th>
          </tr>
        </thead>

        <tbody class="cds-calendar">
          <tr *ngFor="let row of [0, 1, 2, 3, 4, 5]">
            <td
              [class]="
                getCheckDisabled(row * 7 + column)
                  ? 'cds-cell disabled'
                  : 'cds-cell' + addFocus(row, column)
              "
              *ngFor="let column of [0, 1, 2, 3, 4, 5, 6]"
              [tabIndex]="
                !this.disabled && _currentfocusDay === row * 7 + column ? 0 : -1
              "
              [attr.aria-disabled]="
                !this.days[row * 7 + column].classes.includes('empty')
                  ? getCheckDisabled(row * 7 + column)
                  : undefined
              "
              (keydown.enter)="
                !getCheckDisabled(row * 7 + column)
                  ? newValue(this.days[row * 7 + column].day, $event)
                  : undefined
              "
              (keydown.space)="
                !getCheckDisabled(row * 7 + column)
                  ? newValue(this.days[row * 7 + column].day, $event)
                  : undefined
              "
            >
              <cds-day
                [day]="this.days[row * 7 + column]"
                (daySelected)="newValue($event)"
                [disabled]="getCheckDisabled(row * 7 + column)"
                [isSmall]="isSmall"
              >
              </cds-day>
            </td>

            <td></td>
          </tr>
        </tbody>
      </table>
      <input
        [attr.id]="fieldId ? fieldId + '_hidden' : null"
        [(ngModel)]="_value"
        hidden
      />
    </div>
  `, isInline: true, styles: [".cds-calendar-wrapper{width:612px}.cds-weekdays{justify-content:space-around;font-size:12px;display:flex;font-family:Interstate_Regular;margin:0 20px}.weekday{margin:2px;text-align:center;width:80px;height:18px;line-height:18px}.cds-calendar{display:flex;flex-wrap:wrap;margin:0 20px 20px}.cds-calendar td{padding:0;margin:0}.cds-calendar td:hover,.cds-calendar td:focus,.cds-calendar td:active{outline:none;background:#FCFCFC}.cds-calendar td:hover ::ng-deep .cds-day,.cds-calendar td:focus ::ng-deep .cds-day,.cds-calendar td:active ::ng-deep .cds-day{outline:none}.responsive.cds-calendar-wrapper{width:320px}.responsive .cds-weekdays{justify-content:space-around;display:flex;margin:0 12px}.responsive .weekday{margin:0;width:42.24px}.responsive .cds-calendar{display:flex;flex-wrap:wrap;margin:0 12.5px 12.5px}.responsive table{width:320px}@media only screen and (max-width: 780px){.cds-calendar-wrapper{width:320px}.cds-weekdays{justify-content:space-around;display:flex;margin:0 12px}.weekday{margin:0;width:42.24px}.cds-calendar{display:flex;flex-wrap:wrap;margin:0 16px 16px}}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: DayComponent, selector: "cds-day", inputs: ["day", "disabled", "tabIndex", "isSmall"], outputs: ["daySelected"] }, { kind: "component", type: MonthComponent, selector: "cds-month", inputs: ["month", "year", "disabled", "maxDate", "minDate", "numMonth", "nextMonthLabel", "prevMonthLabel", "isSmall"], outputs: ["prevMonth", "nextMonth"] }, { kind: "component", type: InputMonthComponent, selector: "cds-input-month", inputs: ["month", "year", "maxDate", "minDate", "months", "years", "disabled", "calendarForm", "isSmall"], outputs: ["monthChange", "yearChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCalendar, decorators: [{
            type: Component,
            args: [{ selector: 'cds-calendar', host: {
                        class: 'cds-calendar',
                        '(focusin)': 'focusChanged(true)',
                        '(focusout)': 'focusChanged(false)',
                        '(input)': 'checkValue()'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCalendar)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsCalendar }
                    ], template: `
    <div class="cds-calendar-wrapper" [class.responsive]="isSmall">
      <cds-month
        *ngIf="!inputNav"
        [month]="months[this.month]"
        [nextMonthLabel]="
          months[month + 1]
            ? months[month + 1] + ', ' + year
            : months[0] + ', ' + (year + 1)
        "
        [prevMonthLabel]="
          month - 1 >= 0
            ? months[month - 1] + ', ' + year
            : months[11] + ', ' + (year - 1)
        "
        [numMonth]="month"
        [year]="year"
        [disabled]="this.disabled"
        (nextMonth)="nextMonth($event)"
        (prevMonth)="prevMonth($event)"
        [minDate]="_minDate"
        [maxDate]="_maxDate"
        [isSmall]="isSmall"
      ></cds-month>
      <cds-input-month
        *ngIf="inputNav"
        [month]="month"
        [year]="year"
        [months]="filteredMonths"
        [years]="years"
        [disabled]="disabled"
        (monthChange)="monthChange($event)"
        (yearChange)="yearChange($event)"
        [calendarForm]="calendarForm"
        [isSmall]="isSmall"
      >
      </cds-input-month>
      <table (keydown)="onKeyDown($event)">
        <thead>
          <tr class="cds-weekdays">
            <th scope="col" class="weekday" *ngFor="let day of weekdays">
              {{ day }}
            </th>
          </tr>
        </thead>

        <tbody class="cds-calendar">
          <tr *ngFor="let row of [0, 1, 2, 3, 4, 5]">
            <td
              [class]="
                getCheckDisabled(row * 7 + column)
                  ? 'cds-cell disabled'
                  : 'cds-cell' + addFocus(row, column)
              "
              *ngFor="let column of [0, 1, 2, 3, 4, 5, 6]"
              [tabIndex]="
                !this.disabled && _currentfocusDay === row * 7 + column ? 0 : -1
              "
              [attr.aria-disabled]="
                !this.days[row * 7 + column].classes.includes('empty')
                  ? getCheckDisabled(row * 7 + column)
                  : undefined
              "
              (keydown.enter)="
                !getCheckDisabled(row * 7 + column)
                  ? newValue(this.days[row * 7 + column].day, $event)
                  : undefined
              "
              (keydown.space)="
                !getCheckDisabled(row * 7 + column)
                  ? newValue(this.days[row * 7 + column].day, $event)
                  : undefined
              "
            >
              <cds-day
                [day]="this.days[row * 7 + column]"
                (daySelected)="newValue($event)"
                [disabled]="getCheckDisabled(row * 7 + column)"
                [isSmall]="isSmall"
              >
              </cds-day>
            </td>

            <td></td>
          </tr>
        </tbody>
      </table>
      <input
        [attr.id]="fieldId ? fieldId + '_hidden' : null"
        [(ngModel)]="_value"
        hidden
      />
    </div>
  `, styles: [".cds-calendar-wrapper{width:612px}.cds-weekdays{justify-content:space-around;font-size:12px;display:flex;font-family:Interstate_Regular;margin:0 20px}.weekday{margin:2px;text-align:center;width:80px;height:18px;line-height:18px}.cds-calendar{display:flex;flex-wrap:wrap;margin:0 20px 20px}.cds-calendar td{padding:0;margin:0}.cds-calendar td:hover,.cds-calendar td:focus,.cds-calendar td:active{outline:none;background:#FCFCFC}.cds-calendar td:hover ::ng-deep .cds-day,.cds-calendar td:focus ::ng-deep .cds-day,.cds-calendar td:active ::ng-deep .cds-day{outline:none}.responsive.cds-calendar-wrapper{width:320px}.responsive .cds-weekdays{justify-content:space-around;display:flex;margin:0 12px}.responsive .weekday{margin:0;width:42.24px}.responsive .cds-calendar{display:flex;flex-wrap:wrap;margin:0 12.5px 12.5px}.responsive table{width:320px}@media only screen and (max-width: 780px){.cds-calendar-wrapper{width:320px}.cds-weekdays{justify-content:space-around;display:flex;margin:0 12px}.weekday{margin:0;width:42.24px}.cds-calendar{display:flex;flex-wrap:wrap;margin:0 16px 16px}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: i0.NgZone }, { type: i1$1.LiveAnnouncer }, { type: i1$3.FormBuilder }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], valueChange: [{
                type: Output
            }], dateParseFn: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], fromPopupPanel: [{
                type: Input
            }], dateFormat: [{
                type: Input
            }], localString: [{
                type: Input
            }], styledDays: [{
                type: Input
            }], inputNav: [{
                type: Input
            }], maxDate: [{
                type: Input
            }], minDate: [{
                type: Input
            }], disableDays: [{
                type: Input
            }], disableSpecificDays: [{
                type: Input
            }], isSmall: [{
                type: Input
            }] } });

const COMPONENTS$o = [CdsCalendar, DayComponent, MonthComponent, InputMonthComponent];
const MODULES$9 = [CommonModule, FormsModule, CdsIconModule, CdsDropdownModule, CdsFormsModule];
class CdsCalendarModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCalendarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCalendarModule, declarations: [CdsCalendar, DayComponent, MonthComponent, InputMonthComponent], imports: [CommonModule, FormsModule, CdsIconModule, CdsDropdownModule, CdsFormsModule], exports: [CdsCalendar, DayComponent, MonthComponent, InputMonthComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCalendarModule, imports: [MODULES$9] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCalendarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$9],
                    declarations: [...COMPONENTS$o],
                    exports: [...COMPONENTS$o]
                }]
        }] });

let nextId$4 = 0;
/**
 * @Component: Disclaimer
 * @Section: Forms
 * @Description: This is the disclaimer component and is used to create reusable disclaimer
 * from Bootstrap.
 */
class Signon2Component {
    get mobileText() {
        return '<div class=qr-icon></div>' + this.mobileButtonText;
    }
    set isRememberUsernameChecked(checked) {
        this._isRememberUsernameChecked = checked;
        // if (!this.userDD && checked) this.announcer.announce(this.rememberText)
    }
    get isRememberUsernameChecked() {
        return this._isRememberUsernameChecked;
    }
    set isRememberDeviceChecked(checked) {
        this._isRememberDeviceChecked = checked;
        // if (!this.userDD && checked) this.announcer.announce(this.rememberText)
    }
    get isRememberDeviceChecked() {
        return this._isRememberDeviceChecked;
    }
    // #endregion
    // #region // ---------------- Constructor / Lifecycle Hooks ---------------- ///
    constructor() {
        // signon button id
        this.signonButtonID = `signOn-${nextId$4}`;
        this.activateButtonHrefBlankText = ', opens in a new window';
        this.registerButtonHrefBlankText = ', opens in a new window';
        // mobile button id
        this.mobileButtonID = `signOn-mobile-${nextId$4}`;
        // register button id
        this.registerButtonID = `signOn-register-${nextId$4}`;
        // activate button id
        this.activateButtonID = `signOn-activate-${nextId$4++}`;
        // to focus input on load.
        // @Input() focusInput: boolean = false;
        //toggle for mobile login button
        this.showMobileLogin = false;
        this.showSignOnButton = true;
        this._isRememberUsernameChecked = false;
        this._isRememberDeviceChecked = false;
        /**
         * @Event: SubmitButtonClicked
         * @Description: This event that is fired when the submit button is clicked
         * Can be bound to with (SubmitButtonClicked)='myFunction($event)'
         * @Return: Object
         */
        this.SubmitButtonClicked = new EventEmitter();
        /**
         * @Event: mobileLoginClicked
         * @Description: This event that is fired when the mobile login button is clicked
         * Can be bound to with (mobileLoginClicked)='myFunction($event)'
         * @Return: Object
         */
        this.mobileLoginClicked = new EventEmitter();
        /**
         * @Event: registerClicked
         * @Description: This event that is fired when the register link button is clicked
         * Can be bound to with (registerClicked)='myFunction($event)'
         * @Return: Object
         */
        this.registerClicked = new EventEmitter();
        /**
         * @Event: registerClicked
         * @Description: This event that is fired when the register link button is clicked
         * Can be bound to with (registerClicked)='myFunction($event)'
         * @Return: Object
         */
        this.forgetUserClicked = new EventEmitter();
        /**
         * @Event: activateClicked
         * @Description: This event that is fired when the activate link button is clicked
         * Can be bound to with (activateClicked)='myFunction($event)'
         * @Return: Object
         */
        this.activateClicked = new EventEmitter();
        /**
         * @Event: activateClicked
         * @Description: This event that is fired when the activate link button is clicked
         * Can be bound to with (activateClicked)='myFunction($event)'
         * @Return: Object
         */
        this.useAnotherIdClicked = new EventEmitter();
        /**
         * @Event: activateClicked
         * @Description: This event that is fired when the activate link button is clicked
         * Can be bound to with (activateClicked)='myFunction($event)'
         * @Return: Object
         */
        this.clearUserIdClicked = new EventEmitter();
    }
    // #region // ---------------- Methods ---------------- ///
    onSubmit(remember, rememberDevice) {
        this.SubmitButtonClicked.emit({ remember, rememberDevice });
    }
    mobileLogin() {
        this.mobileLoginClicked.emit(true);
    }
    register(event) {
        const keyCode = event.code;
        console.log(event);
        if (keyCode === 'Space') {
            this.registerClicked.emit(false);
        }
        else {
            this.registerClicked.emit(true);
        }
    }
    forgetUser(event) {
        const keyCode = event.code;
        console.log(event);
        if (keyCode === 'Space') {
            this.forgetUserClicked.emit(false);
        }
        else {
            this.forgetUserClicked.emit(true);
        }
    }
    activate(event) {
        const keyCode = event.code;
        console.log(event);
        if (keyCode === 'Space') {
            this.activateClicked.emit(false);
        }
        else {
            this.activateClicked.emit(true);
        }
    }
    useAnotherId(event) {
        const keyCode = event.code;
        console.log(event);
        if (keyCode === 'Space') {
            this.useAnotherIdClicked.emit(false);
        }
        else {
            this.useAnotherIdClicked.emit(true);
        }
    }
    clearUserID(event) {
        const keyCode = event.code;
        console.log(event);
        if (keyCode === 'Space') {
            this.clearUserIdClicked.emit(false);
        }
        else {
            this.clearUserIdClicked.emit(true);
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Signon2Component, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: Signon2Component, selector: "cds-signon2", inputs: { usesDD: "usesDD", title: "title", buttonText: "buttonText", mobileButtonText: "mobileButtonText", registerButtonText: "registerButtonText", activateButtonText: "activateButtonText", rememberIdText: "rememberIdText", rememberDeviceText: "rememberDeviceText", rememberText: "rememberText", signonButtonID: "signonButtonID", activateButtonHrefValue: "activateButtonHrefValue", activateButtonHrefTarget: "activateButtonHrefTarget", activateButtonHrefBlankText: "activateButtonHrefBlankText", registerButtonHrefValue: "registerButtonHrefValue", registerButtonHrefTarget: "registerButtonHrefTarget", registerButtonHrefBlankText: "registerButtonHrefBlankText", mobileButtonID: "mobileButtonID", registerButtonID: "registerButtonID", activateButtonID: "activateButtonID", showMobileLogin: "showMobileLogin", showSignOnButton: "showSignOnButton", isRememberUsernameChecked: "isRememberUsernameChecked", isRememberDeviceChecked: "isRememberDeviceChecked" }, outputs: { SubmitButtonClicked: "SubmitButtonClicked", mobileLoginClicked: "mobileLoginClicked", registerClicked: "registerClicked", forgetUserClicked: "forgetUserClicked", activateClicked: "activateClicked", useAnotherIdClicked: "useAnotherIdClicked", clearUserIdClicked: "clearUserIdClicked" }, host: { classAttribute: "cds-signon2" }, ngImport: i0, template: `
    <section class="theme-light SampleErrorStates">
      <h4 class="cds-signon-title">{{ title }}</h4>
      <div><ng-content select="[alertmessage]"></ng-content></div>
      <div class="row">
        <div class="col-sm-6 sign-user">
          <div class="username">
            <ng-content select="[username]"></ng-content>
            <!--username input end-->
          </div>
        </div>
        <div class="col-sm-6 sign-password">
          <div class="password">
            <ng-content select="[password]"></ng-content>
            <!--password input end-->
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 pb-2 mb-1">
          <!--  checkbox -->
          <div *ngIf="!usesDD" class="remember-user remember">
            <ng-content select="[remember]" class="remember"></ng-content>
          </div>
          <div *ngIf="usesDD">
            <p class="mb-0">
              <a
                role="link"
                tabindex="0"
                (click)="useAnotherId($event)"
                (keydown.enter)="useAnotherId($event)"
                class="another-user-id"
                >Use Another User ID</a
              ><span class="or"> or</span><br />

              <a
                role="link"
                tabindex="0"
                (click)="clearUserID($event)"
                (keydown.enter)="clearUserID($event)"
                class="another-user-id"
                >Clear a User ID</a
              >
            </p>
          </div>
        </div>
        <div class="col-sm-6 pb-2 mb-1 pl-0">
          <div class="remember">
            <ng-content select="[rememberDevice]"></ng-content>
          </div>
        </div>
      </div>
      <div *ngIf="!usesDD && isRememberUsernameChecked">
        <small class="rememberText pl-1" role="alert">{{ rememberText }}</small>
      </div>
      <!--password input-->

      <!--  signon button -->

      <div *ngIf="showSignOnButton" class="form-group col-xs-12 pl-0 pt-2">
        <button
          class="cds-signon-button"
          cdsButton="primary"
          size="large"
          (click)="onSubmit(isRememberUsernameChecked, isRememberDeviceChecked)"
          [id]="signonButtonID"
        >
          {{ buttonText }}
        </button>
      </div>

      <!-- signon button end -->

      <!--  mobile app button -->

      <div *ngIf="showMobileLogin" class="form-group col-xs-12 pl-0">
        <button
          class="cds-signon-button mobile"
          cdsButton="alternate"
          size="large"
          (click)="mobileLogin()"
          [id]="mobileButtonID"
          [innerHTML]="mobileText"
        ></button>
      </div>

      <!-- mobile app button end -->

      <!--  register link button -->
      <div class="row register-main align-items-center">
        <div class="col-sm-5">
          <div class="register-activate d-flex">
            <div class="col-xs-6 anchor-container">
              <a
                class="cds-signon-anchor register"
                (click)="register($event)"
                (keydown.enter)="register($event)"
                tabindex="0"
                [id]="registerButtonID"
                [attr.href]="registerButtonHrefValue ? registerButtonHrefValue : null"
                [attr.target]="registerButtonHrefTarget ? registerButtonHrefTarget : null"
              >
              {{ registerButtonText }}
              <span class="sr-only" *ngIf="registerButtonHrefTarget == '_blank'" [innerHtml]="registerButtonHrefBlankText"></span>
              </a>
            </div>
            <span class="register-connector">{{
              activateButtonText == null || registerButtonText == null
                ? ''
                : '/'
            }}</span>
            <div class="col-xs-6 anchor-container">
              <a
                class="cds-signon-anchor activate "
                (click)="activate($event)"
                (keydown.enter)="activate($event)"
                tabindex="0"
                [id]="activateButtonID"
                [attr.href]="activateButtonHrefValue ? activateButtonHrefValue : null"
                [attr.target]="activateButtonHrefTarget ? activateButtonHrefTarget : null"
              >
              {{ activateButtonText }}
              <span class="sr-only" *ngIf="activateButtonHrefTarget == '_blank'" [innerHtml]="activateButtonHrefBlankText"></span>
              </a>
            </div>
          </div>
        </div>
        <div class="col-sm-7">
          <div class="col-xs-6 anchor-container">
            <!--  links -->

            <div class="cds-signon-anchor register">
              <ng-content select="[links]"></ng-content>
            </div>

            <!--  link ends -->
          </div>
        </div>
      </div>
    </section>
  `, isInline: true, styles: [".rememberText{font-size:10px}.register-main{margin-top:20px;position:relative;top:5px}.register-activate{margin-top:0}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: CdsButton, selector: "button[cdsButton],button[cds-button],a[cdsButton],a[cds-button]", inputs: ["cdsButton", "size", "buttonIcon", "arrowRight", "arrowLeft", "JFPToken"], exportAs: ["cdsButton"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Signon2Component, decorators: [{
            type: Component,
            args: [{ selector: 'cds-signon2', host: { class: 'cds-signon2' }, template: `
    <section class="theme-light SampleErrorStates">
      <h4 class="cds-signon-title">{{ title }}</h4>
      <div><ng-content select="[alertmessage]"></ng-content></div>
      <div class="row">
        <div class="col-sm-6 sign-user">
          <div class="username">
            <ng-content select="[username]"></ng-content>
            <!--username input end-->
          </div>
        </div>
        <div class="col-sm-6 sign-password">
          <div class="password">
            <ng-content select="[password]"></ng-content>
            <!--password input end-->
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-6 pb-2 mb-1">
          <!--  checkbox -->
          <div *ngIf="!usesDD" class="remember-user remember">
            <ng-content select="[remember]" class="remember"></ng-content>
          </div>
          <div *ngIf="usesDD">
            <p class="mb-0">
              <a
                role="link"
                tabindex="0"
                (click)="useAnotherId($event)"
                (keydown.enter)="useAnotherId($event)"
                class="another-user-id"
                >Use Another User ID</a
              ><span class="or"> or</span><br />

              <a
                role="link"
                tabindex="0"
                (click)="clearUserID($event)"
                (keydown.enter)="clearUserID($event)"
                class="another-user-id"
                >Clear a User ID</a
              >
            </p>
          </div>
        </div>
        <div class="col-sm-6 pb-2 mb-1 pl-0">
          <div class="remember">
            <ng-content select="[rememberDevice]"></ng-content>
          </div>
        </div>
      </div>
      <div *ngIf="!usesDD && isRememberUsernameChecked">
        <small class="rememberText pl-1" role="alert">{{ rememberText }}</small>
      </div>
      <!--password input-->

      <!--  signon button -->

      <div *ngIf="showSignOnButton" class="form-group col-xs-12 pl-0 pt-2">
        <button
          class="cds-signon-button"
          cdsButton="primary"
          size="large"
          (click)="onSubmit(isRememberUsernameChecked, isRememberDeviceChecked)"
          [id]="signonButtonID"
        >
          {{ buttonText }}
        </button>
      </div>

      <!-- signon button end -->

      <!--  mobile app button -->

      <div *ngIf="showMobileLogin" class="form-group col-xs-12 pl-0">
        <button
          class="cds-signon-button mobile"
          cdsButton="alternate"
          size="large"
          (click)="mobileLogin()"
          [id]="mobileButtonID"
          [innerHTML]="mobileText"
        ></button>
      </div>

      <!-- mobile app button end -->

      <!--  register link button -->
      <div class="row register-main align-items-center">
        <div class="col-sm-5">
          <div class="register-activate d-flex">
            <div class="col-xs-6 anchor-container">
              <a
                class="cds-signon-anchor register"
                (click)="register($event)"
                (keydown.enter)="register($event)"
                tabindex="0"
                [id]="registerButtonID"
                [attr.href]="registerButtonHrefValue ? registerButtonHrefValue : null"
                [attr.target]="registerButtonHrefTarget ? registerButtonHrefTarget : null"
              >
              {{ registerButtonText }}
              <span class="sr-only" *ngIf="registerButtonHrefTarget == '_blank'" [innerHtml]="registerButtonHrefBlankText"></span>
              </a>
            </div>
            <span class="register-connector">{{
              activateButtonText == null || registerButtonText == null
                ? ''
                : '/'
            }}</span>
            <div class="col-xs-6 anchor-container">
              <a
                class="cds-signon-anchor activate "
                (click)="activate($event)"
                (keydown.enter)="activate($event)"
                tabindex="0"
                [id]="activateButtonID"
                [attr.href]="activateButtonHrefValue ? activateButtonHrefValue : null"
                [attr.target]="activateButtonHrefTarget ? activateButtonHrefTarget : null"
              >
              {{ activateButtonText }}
              <span class="sr-only" *ngIf="activateButtonHrefTarget == '_blank'" [innerHtml]="activateButtonHrefBlankText"></span>
              </a>
            </div>
          </div>
        </div>
        <div class="col-sm-7">
          <div class="col-xs-6 anchor-container">
            <!--  links -->

            <div class="cds-signon-anchor register">
              <ng-content select="[links]"></ng-content>
            </div>

            <!--  link ends -->
          </div>
        </div>
      </div>
    </section>
  `, styles: [".rememberText{font-size:10px}.register-main{margin-top:20px;position:relative;top:5px}.register-activate{margin-top:0}\n"] }]
        }], ctorParameters: () => [], propDecorators: { usesDD: [{
                type: Input
            }], title: [{
                type: Input
            }], buttonText: [{
                type: Input
            }], mobileButtonText: [{
                type: Input
            }], registerButtonText: [{
                type: Input
            }], activateButtonText: [{
                type: Input
            }], rememberIdText: [{
                type: Input
            }], rememberDeviceText: [{
                type: Input
            }], rememberText: [{
                type: Input
            }], signonButtonID: [{
                type: Input
            }], activateButtonHrefValue: [{
                type: Input
            }], activateButtonHrefTarget: [{
                type: Input
            }], activateButtonHrefBlankText: [{
                type: Input
            }], registerButtonHrefValue: [{
                type: Input
            }], registerButtonHrefTarget: [{
                type: Input
            }], registerButtonHrefBlankText: [{
                type: Input
            }], mobileButtonID: [{
                type: Input
            }], registerButtonID: [{
                type: Input
            }], activateButtonID: [{
                type: Input
            }], showMobileLogin: [{
                type: Input
            }], showSignOnButton: [{
                type: Input
            }], isRememberUsernameChecked: [{
                type: Input
            }], isRememberDeviceChecked: [{
                type: Input
            }], SubmitButtonClicked: [{
                type: Output
            }], mobileLoginClicked: [{
                type: Output
            }], registerClicked: [{
                type: Output
            }], forgetUserClicked: [{
                type: Output
            }], activateClicked: [{
                type: Output
            }], useAnotherIdClicked: [{
                type: Output
            }], clearUserIdClicked: [{
                type: Output
            }] } });

const COMPONENTS$n = [Signon2Component];
const MODULES$8 = [CommonModule, CdsInputModule, CdsCTAModule, CdsButtonModule];
class SignOn2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SignOn2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: SignOn2Module, declarations: [Signon2Component], imports: [CommonModule, CdsInputModule, CdsCTAModule, CdsButtonModule], exports: [Signon2Component] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SignOn2Module, imports: [MODULES$8] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: SignOn2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$8],
                    declarations: [...COMPONENTS$n],
                    exports: [...COMPONENTS$n]
                }]
        }] });

class CdsQuantity {
    constructor() {
        // #region // ---------------- Input/Output ---------------- ///
        this.title = 'Quantity';
        this.disabled = false;
        this.decrementBtnDisabled = false;
        this.incrementBtnDisabled = false;
        this.displayValue = 1;
        this.minLimit = 0;
        this.maxLimit = 9999;
        this.limit = 6;
        this.stepper = 1;
        /**
           * @name quantityNumberChange
           * @description Emitted when a page in the quantity is active
           * @return Object
           * @Type: EventEmitter<number>
           */
        this.quantityNumberChange = new EventEmitter();
    }
    set incrementBy(val) {
        this.displayValue = val;
        this.stepper = val;
        if (val == 1) {
            this.maxLimit = val * 99;
        }
        if (val == 5) {
            this.maxLimit = 50;
        }
        if (val == 100) {
            this.maxLimit = 1000;
        }
    }
    decrement() {
        if (this.displayValue > this.minLimit) {
            this.displayValue = this.displayValue - this.stepper;
        }
        if (this.displayValue === this.minLimit || this.disabled === true) {
            this.increase.nativeElement.focus();
        }
        this.quantityNumberChange.emit(this.displayValue);
    }
    increment() {
        if (this.displayValue < this.maxLimit) {
            this.displayValue = this.displayValue + this.stepper;
        }
        if (this.displayValue === this.maxLimit || this.disabled === true) {
            this.decrease.nativeElement.focus();
        }
        if (this.displayValue === this.limit || this.disabled === true) {
            this.decrease.nativeElement.focus();
        }
        this.quantityNumberChange.emit(this.displayValue);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsQuantity, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsQuantity, selector: "cds-quantity", inputs: { title: "title", errorMessage: "errorMessage", note: "note", disabled: "disabled", decrementBtnDisabled: "decrementBtnDisabled", incrementBtnDisabled: "incrementBtnDisabled", displayValue: "displayValue", minLimit: "minLimit", maxLimit: "maxLimit", limit: "limit", incrementBy: "incrementBy" }, outputs: { quantityNumberChange: "quantityNumberChange" }, host: { classAttribute: "cds-quantity" }, viewQueries: [{ propertyName: "increase", first: true, predicate: ["increase"], descendants: true }, { propertyName: "decrease", first: true, predicate: ["decrease"], descendants: true }], ngImport: i0, template: `
  <div class="container">
    <span class="title">{{title}}</span>
      <div class="quantity-parent-container">
          <button
          class="quantity-left quantity-button"
          #decrease
              type="button"
              aria-label="Decrease Quantity"
              buttonIcon="icon"
              (click)="decrement()"
              [disabled]="displayValue === minLimit || disabled"
              >
              <cds-icon
                  type="2021"
                  name="008-minus" 
                  role="presentation"
                  [svgStyle]="{ 'width.px': 24, 'height.px': 12 }"
              ></cds-icon>
          </button>
            <div [ngClass]="!disabled ? 'quantity-input' : 'disable-display'"  aria-live="polite"> {{displayValue}}</div>
          <button
             class="quantity-right quantity-button"
             #increase
              type="button" 
              aria-label="Increase Quantity"
              buttonIcon="icon"
              (click)="increment()"
              [disabled]="displayValue === maxLimit || disabled"
              [disabled]="displayValue === limit || disabled"

              >
              <cds-icon
                  type="2021"
                  role="presentation"
                  name="007-plus"
                  [svgStyle]="{ 'width.px': 24, 'height.px': 24 }"
              ></cds-icon>
          </button>
      </div>
      <p class="error-message" *ngIf="errorMessage && displayValue >=limit" role="alert">{{errorMessage}}</p>
      <p class="note" *ngIf="note">{{note}}</p>
</div>

  `, isInline: true, styles: [".quantity-parent-container{display:flex}.error-message{color:#d60000;font-size:14px;margin-bottom:-5px;overflow:auto;line-height:21px;font-weight:300;font-family:Interstate_Regular}.note{color:#666;font-size:12px;font-family:Interstate_Regular;line-height:18px;font-weight:300}.quantity-input{min-width:48px;padding:8px;text-align:center;border-top:1px solid #bbbbbb;border-bottom:1px solid #bbbbbb}.quantity-button{background-color:#fff;border:1px solid #bbbbbb;width:48px}.quantity-button:disabled{cursor:initial}.quantity-button:disabled ::ng-deep svg path{fill:#ccc}.quantity-left{border-radius:8px 0 0 8px}.quantity-right{border-radius:0 8px 8px 0}.disable-display{min-width:48px;padding:8px;text-align:center;border-top:1px solid #cccccc;border-bottom:1px solid #cccccc;color:#ccc}button:focus-visible{outline:#002a54 solid 1px;border-radius:8px;outline-offset:1.5px}.title{font-family:Interstate_Regular;line-height:24px;font-size:16px;font-weight:300}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsQuantity, decorators: [{
            type: Component,
            args: [{ selector: 'cds-quantity', template: `
  <div class="container">
    <span class="title">{{title}}</span>
      <div class="quantity-parent-container">
          <button
          class="quantity-left quantity-button"
          #decrease
              type="button"
              aria-label="Decrease Quantity"
              buttonIcon="icon"
              (click)="decrement()"
              [disabled]="displayValue === minLimit || disabled"
              >
              <cds-icon
                  type="2021"
                  name="008-minus" 
                  role="presentation"
                  [svgStyle]="{ 'width.px': 24, 'height.px': 12 }"
              ></cds-icon>
          </button>
            <div [ngClass]="!disabled ? 'quantity-input' : 'disable-display'"  aria-live="polite"> {{displayValue}}</div>
          <button
             class="quantity-right quantity-button"
             #increase
              type="button" 
              aria-label="Increase Quantity"
              buttonIcon="icon"
              (click)="increment()"
              [disabled]="displayValue === maxLimit || disabled"
              [disabled]="displayValue === limit || disabled"

              >
              <cds-icon
                  type="2021"
                  role="presentation"
                  name="007-plus"
                  [svgStyle]="{ 'width.px': 24, 'height.px': 24 }"
              ></cds-icon>
          </button>
      </div>
      <p class="error-message" *ngIf="errorMessage && displayValue >=limit" role="alert">{{errorMessage}}</p>
      <p class="note" *ngIf="note">{{note}}</p>
</div>

  `, host: { class: 'cds-quantity' }, styles: [".quantity-parent-container{display:flex}.error-message{color:#d60000;font-size:14px;margin-bottom:-5px;overflow:auto;line-height:21px;font-weight:300;font-family:Interstate_Regular}.note{color:#666;font-size:12px;font-family:Interstate_Regular;line-height:18px;font-weight:300}.quantity-input{min-width:48px;padding:8px;text-align:center;border-top:1px solid #bbbbbb;border-bottom:1px solid #bbbbbb}.quantity-button{background-color:#fff;border:1px solid #bbbbbb;width:48px}.quantity-button:disabled{cursor:initial}.quantity-button:disabled ::ng-deep svg path{fill:#ccc}.quantity-left{border-radius:8px 0 0 8px}.quantity-right{border-radius:0 8px 8px 0}.disable-display{min-width:48px;padding:8px;text-align:center;border-top:1px solid #cccccc;border-bottom:1px solid #cccccc;color:#ccc}button:focus-visible{outline:#002a54 solid 1px;border-radius:8px;outline-offset:1.5px}.title{font-family:Interstate_Regular;line-height:24px;font-size:16px;font-weight:300}\n"] }]
        }], propDecorators: { title: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], note: [{
                type: Input
            }], disabled: [{
                type: Input
            }], decrementBtnDisabled: [{
                type: Input
            }], incrementBtnDisabled: [{
                type: Input
            }], displayValue: [{
                type: Input
            }], minLimit: [{
                type: Input
            }], maxLimit: [{
                type: Input
            }], limit: [{
                type: Input
            }], incrementBy: [{
                type: Input
            }], increase: [{
                type: ViewChild,
                args: ['increase']
            }], decrease: [{
                type: ViewChild,
                args: ['decrease']
            }], quantityNumberChange: [{
                type: Output
            }] } });

const COMPONENTS$m = [CdsQuantity];
const MODULES$7 = [CommonModule, CdsIconModule, CdsButtonModule];
class CdsQuantityModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsQuantityModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsQuantityModule, declarations: [CdsQuantity], imports: [CommonModule, CommonModule, CdsIconModule, CdsButtonModule], exports: [CdsQuantity] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsQuantityModule, imports: [CommonModule, MODULES$7] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsQuantityModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$m],
                    imports: [CommonModule, ...MODULES$7],
                    exports: [...COMPONENTS$m]
                }]
        }] });

class CdsTabBarService {
    constructor() {
        this.hostId$ = new Subject();
        this.theme$ = new Subject();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

let nextId$3 = 0;
class CdsTabBar {
    constructor(tabBarService) {
        this.tabBarService = tabBarService;
        this.isActive = false;
        this.isSelected = false;
        this.themeDark = false;
        this.themeLight = false;
        this._destroyUnsub$ = new Subject();
        this.iconID = "051-information";
    }
    ngOnInit() {
        this._tabId = `tab-${nextId$3++}`;
        // this.keyboardFocused = false;
        this.tabBarService.hostId$
            .pipe(takeUntil$1(this._destroyUnsub$))
            .subscribe(hostId => {
            this._hostId = hostId;
        });
        this.tabBarService.theme$
            .pipe(takeUntil$1(this._destroyUnsub$))
            .subscribe(theme => {
            this.themeDark = theme === 'dark';
            this.themeLight = !this.themeDark;
        });
    }
    ngOnDestroy() {
        this._destroyUnsub$.next();
        this._destroyUnsub$.complete();
    }
    setIsActive(isActive) {
        this.isActive = isActive;
    }
    setIsSelected(isSelected) {
        this.isSelected = isSelected;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBar, deps: [{ token: CdsTabBarService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTabBar, selector: "cds-tab-bar", inputs: { label: "label", iconID: "iconID" }, host: { properties: { "class.tab-bar-panel-active": "this.isSelected", "class.cds-tab-bar-dark": "this.themeDark", "class.cds-tab-bar-light": "this.themeLight" }, classAttribute: "cds-tab-bar" }, exportAs: ["cdsTabBar"], ngImport: i0, template: `
    <div
      role="tabpanel"
      class="tab-bar-panel"
      [class.tab-bar-panel-active]="isActive"
      [attr.id]="_tabId"
      [attr.aria-labelledby]="_hostId + '-' + _tabId"
      tabindex="false"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBar, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-tab-bar',
                    exportAs: 'cdsTabBar',
                    host: { class: 'cds-tab-bar' },
                    template: `
    <div
      role="tabpanel"
      class="tab-bar-panel"
      [class.tab-bar-panel-active]="isActive"
      [attr.id]="_tabId"
      [attr.aria-labelledby]="_hostId + '-' + _tabId"
      tabindex="false"
    >
      <ng-content></ng-content>
    </div>
  `
                }]
        }], ctorParameters: () => [{ type: CdsTabBarService }], propDecorators: { isActive: [{
                type: HostBinding,
                args: ['class.tab-bar-panel-active']
            }], isSelected: [{
                type: HostBinding,
                args: ['class.tab-bar-panel-active']
            }], themeDark: [{
                type: HostBinding,
                args: ['class.cds-tab-bar-dark']
            }], themeLight: [{
                type: HostBinding,
                args: ['class.cds-tab-bar-light']
            }], label: [{
                type: Input
            }], iconID: [{
                type: Input
            }] } });

let nextId$2 = 0;
class CdsTabBarGroup {
    // #endregion
    // #region // -------- Inputs/Outputs ---------- //
    /**
     * Sets active tab
     */
    set activeTab(index) {
        if (Number(index) === this.activeTab)
            return;
        this._activeTab = Number(index);
        if (this.tabs && this.tabs.length)
            this.selectTab(this._activeTab);
    }
    get activeTab() {
        return this._activeTab;
    }
    set ariaLabel(label) {
        if (typeof label !== 'undefined') {
            this._ariaLabel = label;
        }
    }
    set cdsTabBarGroup(tabBarGroupType) {
        if (!tabBarGroupType)
            return;
        this.themeDark = tabBarGroupType === 'dark';
        this.themeLight = !this.themeDark;
        this.tabBarService.theme$.next(this.themeDark ? 'dark' : 'light');
    }
    // #endregion
    // #region // ------- Constructor/Lifecycle Hooks -------- //
    constructor(el, tabBarService) {
        this.el = el;
        this.tabBarService = tabBarService;
        this.iconMargin = '10px';
        this._focusedTab = 0;
        this._activeTab = 0;
        this.iconAdded = false;
        this.labelAdded = true;
        this.disableTabBar = false;
        this.largeTabs = false;
        this.horizontalScroll = false;
        this.activeTabChange = new EventEmitter();
        // #endregion
        // #region // ------- HostBinding -------- //
        this.themeDark = true;
        this.themeLight = false;
        this.hostId = `tabs-${nextId$2++}`;
        setTimeout(() => { this.tabBarService.hostId$.next(this.hostId); }, 0);
    }
    ngAfterContentInit() {
        this.setInitial(this.activeTab);
        this.tabBarService.theme$.next(this.themeDark ? 'dark' : 'light');
    }
    // #endregion
    // #region // ------------ Methods ------------- //
    get iconColor() {
        return this.disableTabBar ? '#CCCCCC' : this.themeDark ? '#FFF' : '#000';
    }
    get iconSize() {
        return this.largeTabs ? 24 : 16;
    }
    setInitial(index) {
        this._focusedTab = index;
        const tab = this.tab(index);
        tab?.setIsActive(true);
        tab?.setIsSelected(true);
    }
    unSelectAll() {
        this.tabs.forEach(tab => {
            this.setActiveTab(tab, false);
            this.setSelectedTab(tab, false);
        });
    }
    tab(index) {
        return this.tabs.find((tab, i) => i === index);
    }
    setActiveTab(tab, bool = true) {
        tab.setIsActive(bool);
    }
    setSelectedTab(tab, bool = true) {
        tab.setIsSelected(bool);
    }
    focusActive() {
        setTimeout(() => {
            const focusTab = this.tabListButtons.find((tabBtn, index) => { return index === this._focusedTab; });
            focusTab.nativeElement.focus();
        }, 100);
    }
    handleTabClick(index, _tabId) {
        this.selectTab(index);
        this.scrollTabList(index);
    }
    scrollTabList(tabIndex) {
        const tabListButtonEl = this.tabListButtons?.find((x, i) => i === tabIndex)?.nativeElement;
        const tabListEl = this.tabList?.nativeElement;
        tabListButtonEl.scrollIntoView({
            behavior: 'smooth',
            inline: 'center',
            block: 'nearest'
        });
        //Keeps whole tab bar from shifting
        tabListEl.scrollIntoView({
            inline: 'end',
            block: 'nearest'
        });
    }
    selectTab(index) {
        this.unSelectAll();
        this.setActiveTab(this.tab(index));
        this.tab(index).setIsSelected(true);
        this.focusActive();
        this._activeTab = this._focusedTab = index;
        this.activeTabChange.emit(index);
    }
    removeKeyboardFocus() {
        this._focusedTab = this.activeTab;
    }
    moveFocus(dir) {
        const newTabIndex = (this._focusedTab + this.tabs.length + dir) % this.tabs.length;
        this.setTabFocus(newTabIndex);
        this.scrollTabList(newTabIndex);
    }
    setTabFocus(index) {
        this.focusActive();
        this._focusedTab = index;
    }
    stopBubble(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    handleKeyPress(event) {
        if (event.which !== KEY_TAB)
            this.stopBubble(event);
        switch (event.which) {
            case KEY_HOME:
                this.setTabFocus(0);
                break;
            case KEY_END:
                this.setTabFocus(this.tabs.length - 1);
                break;
            case KEY_ARROW_LEFT:
                this.moveFocus(-1);
                break;
            case KEY_ARROW_RIGHT:
                this.moveFocus(1);
                break;
            case KEY_SPACEBAR:
            case KEY_ENTER:
                this.selectTab(this._focusedTab);
                break;
            case KEY_TAB:
                this.removeKeyboardFocus();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarGroup, deps: [{ token: i0.ElementRef }, { token: CdsTabBarService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTabBarGroup, selector: "cds-tab-bar-group", inputs: { activeTab: "activeTab", iconAdded: "iconAdded", labelAdded: "labelAdded", disableTabBar: "disableTabBar", largeTabs: "largeTabs", horizontalScroll: "horizontalScroll", ariaLabel: "ariaLabel", cdsTabBarGroup: "cdsTabBarGroup" }, outputs: { activeTabChange: "activeTabChange" }, host: { properties: { "class.cds-tab-bar-theme-dark": "this.themeDark", "class.cds-tab-bar-theme-light": "this.themeLight" }, classAttribute: "cds-tab-bar-group" }, providers: [CdsTabBarService], queries: [{ propertyName: "tabs", predicate: CdsTabBar }], viewQueries: [{ propertyName: "tabList", first: true, predicate: ["tabList"], descendants: true }, { propertyName: "tabListButtons", predicate: ["tabListButton"], descendants: true }], exportAs: ["cdsTabBarGroup"], ngImport: i0, template: `
  <div
  role="tablist"
  #tabList
  [attr.id]="hostId"
  [class]="'tab-list tabs-' + tabs.length"
  *ngIf="!horizontalScroll"
  [ngClass]="{
    'd-block': tabs.length >= 2,
    'disabled-tab-list': disableTabBar
  }"
  [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
>
    <button
        *ngFor="let tab of tabs.toArray(); index as idx"
        #tabListButton
        role="tab"
        type="button"
        [attr.id]="hostId + '-tab-' + tab._tabId.split('-')[1]"
        [attr.tabindex]="tab.isActive ? 0 : -1"
        [attr.aria-label]="tab.label"
        class="tab-label"
        [class.tab-bar-disable]="disableTabBar"
        [class.tab-label-active]="activeTab === idx"
        [class.tab-label-inactive]="activeTab !== idx"
        [class.large-tabs]="largeTabs"
        [class.large-label]="largeTabs && !iconAdded"
        [class.large-tabs-icons]="largeTabs && !labelAdded"
        [class.tab-focused]="_focusedTab === idx"
        (click)="handleTabClick(idx, tab.tabId)"
        (keydown)="handleKeyPress($event)"
        [attr.aria-selected]="tab.isSelected"
        [attr.aria-controls]="tab.tabId"
        [disabled]="disableTabBar"
      >
        <div
          class="tab-contents"
          [ngClass]="{
            'icon-only': !disableTabBar && !labelAdded,
            'large-tabs': largeTabs,
            'tab-contents-focused': tab.isFocused
          }"
        >
          <cds-icon
            *ngIf="iconAdded"
            class="tab-icon-content"
            [ngClass]="{ 'tab-icon-content-icon-only': !labelAdded }"
            [idStr]="hostId + '-' + tab.tabId + '-' + tab.label + '-icon'"
            [ariaLabel]="tab.label + ' icon'"
            altText="icon"
            type="2021"
            [name]="tab.iconID"
            [pathColor]="iconColor"
            [style.margin-right]="iconMargin"
            [svgStyle]="{ 'width.px': iconSize, 'height.px': iconSize }"
          ></cds-icon>
          <div
            class="tab-label-text"
            *ngIf="labelAdded"
            [innerHtml]="tab.label"
          ></div>
        </div>
        <div
          class="tab-label-content"
          [ngClass]="{
            'large-tabs': largeTabs,
            'tab-label-content-icon-only': !labelAdded,
            'tab-icon-content-large-icon': !labelAdded && largeTabs
          }"
        ></div>
      </button>
    </div>
    <div
      role="tablist"
      #tabListScroll
      *ngIf="horizontalScroll"
      [attr.id]="hostId"
      [class]="'tab-list tabs-' + tabs.length"
      [ngClass]="{
        'd-block': tabs.length >= 2,
        'disabled-tab-list': disableTabBar
      }"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
    >
        <div class="cds-tab-scroll">
          <button
              *ngFor="let tab of tabs.toArray(); index as idx"
              #tabListButton
              role="tab"
              type="button"
              [attr.id]="hostId + '-tab-' + tab._tabId.split('-')[1]"
              [attr.tabindex]="tab.isActive ? 0 : -1"
              [attr.aria-label]="tab.label"
              class="tab-label"
              [class.tab-bar-disable]="disableTabBar"
              [class.tab-label-active]="activeTab === idx"
              [class.tab-label-inactive]="activeTab !== idx"
              [class.large-tabs]="largeTabs"
              [class.large-label]="largeTabs && !iconAdded"
              [class.large-tabs-icons]="largeTabs && !labelAdded"
              [class.tab-focused]="_focusedTab === idx"
              (click)="handleTabClick(idx, tab.tabId)"
              (keydown)="handleKeyPress($event)"
              [attr.aria-selected]="tab.isSelected"
              [attr.aria-controls]="tab.tabId"
              [disabled]="disableTabBar"
            >
              <div
                class="tab-contents"
                [ngClass]="{
                  'icon-only': !disableTabBar && !labelAdded,
                  'large-tabs': largeTabs,
                  'tab-contents-focused': tab.isFocused
                }"
              >
                <cds-icon
                  *ngIf="iconAdded"
                  class="tab-icon-content"
                  [ngClass]="{ 'tab-icon-content-icon-only': !labelAdded }"
                  [idStr]="hostId + '-' + tab.tabId + '-' + tab.label + '-icon'"
                  [ariaLabel]="tab.label + ' icon'"
                  altText="icon"
                  type="2021"
                  [name]="tab.iconID"
                  [pathColor]="iconColor"
                  [style.margin-right]="iconMargin"
                  [svgStyle]="{ 'width.px': iconSize, 'height.px': iconSize }"
                ></cds-icon>
                <div
                  class="tab-label-text"
                  *ngIf="labelAdded"
                  [innerHtml]="tab.label"
                ></div>
              </div>
              <div
                class="tab-label-content"
                [ngClass]="{
                  'large-tabs': largeTabs,
                  'tab-label-content-icon-only': !labelAdded,
                  'tab-icon-content-large-icon': !labelAdded && largeTabs
                }"
              ></div>
            </button>
        </div>
      </div>

    <div class="tab-content-wrapper noPadding">
      <ng-content></ng-content>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-tab-bar-group',
                    template: `
  <div
  role="tablist"
  #tabList
  [attr.id]="hostId"
  [class]="'tab-list tabs-' + tabs.length"
  *ngIf="!horizontalScroll"
  [ngClass]="{
    'd-block': tabs.length >= 2,
    'disabled-tab-list': disableTabBar
  }"
  [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
>
    <button
        *ngFor="let tab of tabs.toArray(); index as idx"
        #tabListButton
        role="tab"
        type="button"
        [attr.id]="hostId + '-tab-' + tab._tabId.split('-')[1]"
        [attr.tabindex]="tab.isActive ? 0 : -1"
        [attr.aria-label]="tab.label"
        class="tab-label"
        [class.tab-bar-disable]="disableTabBar"
        [class.tab-label-active]="activeTab === idx"
        [class.tab-label-inactive]="activeTab !== idx"
        [class.large-tabs]="largeTabs"
        [class.large-label]="largeTabs && !iconAdded"
        [class.large-tabs-icons]="largeTabs && !labelAdded"
        [class.tab-focused]="_focusedTab === idx"
        (click)="handleTabClick(idx, tab.tabId)"
        (keydown)="handleKeyPress($event)"
        [attr.aria-selected]="tab.isSelected"
        [attr.aria-controls]="tab.tabId"
        [disabled]="disableTabBar"
      >
        <div
          class="tab-contents"
          [ngClass]="{
            'icon-only': !disableTabBar && !labelAdded,
            'large-tabs': largeTabs,
            'tab-contents-focused': tab.isFocused
          }"
        >
          <cds-icon
            *ngIf="iconAdded"
            class="tab-icon-content"
            [ngClass]="{ 'tab-icon-content-icon-only': !labelAdded }"
            [idStr]="hostId + '-' + tab.tabId + '-' + tab.label + '-icon'"
            [ariaLabel]="tab.label + ' icon'"
            altText="icon"
            type="2021"
            [name]="tab.iconID"
            [pathColor]="iconColor"
            [style.margin-right]="iconMargin"
            [svgStyle]="{ 'width.px': iconSize, 'height.px': iconSize }"
          ></cds-icon>
          <div
            class="tab-label-text"
            *ngIf="labelAdded"
            [innerHtml]="tab.label"
          ></div>
        </div>
        <div
          class="tab-label-content"
          [ngClass]="{
            'large-tabs': largeTabs,
            'tab-label-content-icon-only': !labelAdded,
            'tab-icon-content-large-icon': !labelAdded && largeTabs
          }"
        ></div>
      </button>
    </div>
    <div
      role="tablist"
      #tabListScroll
      *ngIf="horizontalScroll"
      [attr.id]="hostId"
      [class]="'tab-list tabs-' + tabs.length"
      [ngClass]="{
        'd-block': tabs.length >= 2,
        'disabled-tab-list': disableTabBar
      }"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
    >
        <div class="cds-tab-scroll">
          <button
              *ngFor="let tab of tabs.toArray(); index as idx"
              #tabListButton
              role="tab"
              type="button"
              [attr.id]="hostId + '-tab-' + tab._tabId.split('-')[1]"
              [attr.tabindex]="tab.isActive ? 0 : -1"
              [attr.aria-label]="tab.label"
              class="tab-label"
              [class.tab-bar-disable]="disableTabBar"
              [class.tab-label-active]="activeTab === idx"
              [class.tab-label-inactive]="activeTab !== idx"
              [class.large-tabs]="largeTabs"
              [class.large-label]="largeTabs && !iconAdded"
              [class.large-tabs-icons]="largeTabs && !labelAdded"
              [class.tab-focused]="_focusedTab === idx"
              (click)="handleTabClick(idx, tab.tabId)"
              (keydown)="handleKeyPress($event)"
              [attr.aria-selected]="tab.isSelected"
              [attr.aria-controls]="tab.tabId"
              [disabled]="disableTabBar"
            >
              <div
                class="tab-contents"
                [ngClass]="{
                  'icon-only': !disableTabBar && !labelAdded,
                  'large-tabs': largeTabs,
                  'tab-contents-focused': tab.isFocused
                }"
              >
                <cds-icon
                  *ngIf="iconAdded"
                  class="tab-icon-content"
                  [ngClass]="{ 'tab-icon-content-icon-only': !labelAdded }"
                  [idStr]="hostId + '-' + tab.tabId + '-' + tab.label + '-icon'"
                  [ariaLabel]="tab.label + ' icon'"
                  altText="icon"
                  type="2021"
                  [name]="tab.iconID"
                  [pathColor]="iconColor"
                  [style.margin-right]="iconMargin"
                  [svgStyle]="{ 'width.px': iconSize, 'height.px': iconSize }"
                ></cds-icon>
                <div
                  class="tab-label-text"
                  *ngIf="labelAdded"
                  [innerHtml]="tab.label"
                ></div>
              </div>
              <div
                class="tab-label-content"
                [ngClass]="{
                  'large-tabs': largeTabs,
                  'tab-label-content-icon-only': !labelAdded,
                  'tab-icon-content-large-icon': !labelAdded && largeTabs
                }"
              ></div>
            </button>
        </div>
      </div>

    <div class="tab-content-wrapper noPadding">
      <ng-content></ng-content>
    </div>
  `,
                    exportAs: 'cdsTabBarGroup',
                    providers: [CdsTabBarService],
                    host: { class: 'cds-tab-bar-group' }
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CdsTabBarService }], propDecorators: { tabList: [{
                type: ViewChild,
                args: ['tabList']
            }], tabListButtons: [{
                type: ViewChildren,
                args: ['tabListButton']
            }], tabs: [{
                type: ContentChildren,
                args: [CdsTabBar, { descendants: false }]
            }], activeTab: [{
                type: Input
            }], iconAdded: [{
                type: Input
            }], labelAdded: [{
                type: Input
            }], disableTabBar: [{
                type: Input
            }], largeTabs: [{
                type: Input
            }], horizontalScroll: [{
                type: Input
            }], activeTabChange: [{
                type: Output
            }], ariaLabel: [{
                type: Input
            }], cdsTabBarGroup: [{
                type: Input
            }], themeDark: [{
                type: HostBinding,
                args: ['class.cds-tab-bar-theme-dark']
            }], themeLight: [{
                type: HostBinding,
                args: ['class.cds-tab-bar-theme-light']
            }] } });

const COMPONENTS$l = [CdsTabBar, CdsTabBarGroup];
const MODULES$6 = [CommonModule, CdsIconModule, ScrollingModule];
class CdsTabBarModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarModule, declarations: [CdsTabBar, CdsTabBarGroup], imports: [CommonModule, CdsIconModule, ScrollingModule], exports: [CdsTabBarGroup, CdsTabBar] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarModule, imports: [MODULES$6] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabBarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$l],
                    imports: [...MODULES$6],
                    exports: [CdsTabBarGroup, CdsTabBar]
                }]
        }] });

class CdsElectronicSignatureDrawComponent {
    set height(h) {
        this._height = h >= 90 ? h : 200;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor(_doc) {
        this._doc = _doc;
        this._signCanvasWidth = 200;
        this._height = 200;
        this._unsub$ = new Subject();
        this._mouseupUnsub$ = new Subject();
        this._calculate = false;
        // #endregion
        // #region =========== @Input / @Output ========== //
        this.canvasAriaLabel = 'Electronic Signature';
        this.signHereText = 'Please Sign Here';
        this.clearButtonText = 'Clear';
        this.lineWidth = 2;
        this.canvasImageDataChange = new EventEmitter();
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    ngAfterViewInit() {
        this._canvasEl = this.drawCanvasRef.nativeElement;
        this._canvasContext = this._canvasEl.getContext('2d');
        // will resize the canvas when screen is resized
        fromEvent(this._window, 'resize')
            .pipe(takeUntil$1(this._unsub$), throttleTime(10, null, { leading: false, trailing: true }))
            .subscribe(obv => {
            this._resizeCanvas();
        });
        // listens to mousedown/touchstart on canvas
        const fromMousedown$ = fromEvent(this._canvasEl, 'mousedown');
        const fromTouchstart$ = fromEvent(this._canvasEl, 'touchstart');
        merge(fromMousedown$, fromTouchstart$)
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(e => {
            this._onMousedown(e);
        });
        // resize canvas on init (needs settimout to wait for DOM)
        setTimeout(() => {
            this._resizeCanvas();
            this._clearSign();
        }, 0);
    }
    ngOnDestroy() {
        this._mouseupUnsub$.next();
        this._mouseupUnsub$.complete();
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region =========== Methods =========== //
    _clearSign() {
        this._isClearSignEnabled = false;
        this._canvasContext.clearRect(0, 0, this._canvasEl.width, this._canvasEl.height);
        this._dataUrl = null;
        this.canvasImageDataChange.emit(this._dataUrl);
        this._resizeCanvas();
    }
    _getCoords(e) {
        let x, y;
        if ('changedTouches' in e && e.changedTouches && e.changedTouches[0]) {
            const canvasRec = this._canvasEl.getBoundingClientRect();
            const offsetx = canvasRec.x || 0;
            const offsety = canvasRec.y || 0;
            x = e.changedTouches[0].clientX - offsetx;
            y = e.changedTouches[0].clientY - offsety;
        }
        else if ('layerX' in e && 'layerY' in e && (e.layerX || 0 == e.layerX)) {
            x = e.layerX;
            y = e.layerY;
        }
        else if ('offsetX' in e && (e.offsetX || 0 == e.offsetX)) {
            x = e.offsetX;
            y = e.offsetY;
        }
        return {
            x: x, y: y
        };
    }
    _onMousedown(e) {
        e.preventDefault();
        e.stopPropagation();
        this._isClearSignEnabled = true;
        // event listeners for mouseup
        const fromCanvasMouseup$ = fromEvent(this._canvasEl, 'mouseup');
        const fromCanvasTouchend$ = fromEvent(this._canvasEl, 'touchend');
        const fromDocumentMouseup$ = fromEvent(this._document, 'mouseup');
        const fromDocumentTouchend$ = fromEvent(this._document, 'touchend');
        merge(fromCanvasMouseup$, fromCanvasTouchend$, fromDocumentMouseup$, fromDocumentTouchend$)
            .pipe(takeUntil$1(this._mouseupUnsub$))
            .subscribe(event => {
            this._onMouseup(event);
        });
        // event listeners for mousemove
        const fromCanvasMousemove$ = fromEvent(this._canvasEl, 'mousemove');
        const fromCanvasTouchmove$ = fromEvent(this._canvasEl, 'touchmove');
        merge(fromCanvasMousemove$, fromCanvasTouchmove$)
            .pipe(takeUntil$1(this._mouseupUnsub$))
            .subscribe(event => {
            this._onMousemove(event);
        });
        const xy = this._getCoords(e);
        this._canvasContext.beginPath();
        this._canvasContext.lineWidth = this.lineWidth;
        this._canvasContext.lineCap = 'round';
        this._canvasContext.moveTo(xy.x, xy.y);
        this._canvasContext.lineTo(xy.x + 1, xy.y);
        this._canvasContext.stroke();
        this._xyLast = xy;
    }
    _onMousemove(e) {
        e.preventDefault();
        e.stopPropagation();
        const xy = this._getCoords(e);
        const xyAdd = {
            x: (this._xyLast.x + xy.x) / 2,
            y: (this._xyLast.y + xy.y) / 2
        };
        if (this._calculate) {
            const xLast = (this._xyAddLast.x + this._xyLast.x + xyAdd.x) / 3;
            const yLast = (this._xyAddLast.y + this._xyLast.y + xyAdd.y) / 3;
        }
        else {
            this._calculate = true;
        }
        this._canvasContext.quadraticCurveTo(this._xyLast.x, this._xyLast.y, xyAdd.x, xyAdd.y);
        this._canvasContext.stroke();
        this._canvasContext.beginPath();
        this._canvasContext.lineWidth = this.lineWidth;
        this._canvasContext.lineCap = 'round';
        this._canvasContext.moveTo(xyAdd.x, xyAdd.y);
        this._xyAddLast = xyAdd;
        this._xyLast = xy;
    }
    _onMouseup(e) {
        this._mouseupUnsub$.next();
        this._canvasContext.stroke();
        this._calculate = false;
        this._dataUrl = this._canvasEl.toDataURL('image/png');
        this.canvasImageDataChange.emit(this._dataUrl);
    }
    resize() {
        this._resizeCanvas();
    }
    _resizeCanvas() {
        // only allow resize if they have not started drawing on canvas yet
        if (!this._isClearSignEnabled) {
            const signWidth = Math.round(this.drawRef?.nativeElement?.getBoundingClientRect().width);
            this._signCanvasWidth = signWidth;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureDrawComponent, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsElectronicSignatureDrawComponent, selector: "cds-electronic-signature-draw", inputs: { canvasAriaLabel: "canvasAriaLabel", signHereText: "signHereText", clearButtonText: "clearButtonText", lineWidth: "lineWidth", height: "height" }, outputs: { canvasImageDataChange: "canvasImageDataChange" }, viewQueries: [{ propertyName: "drawRef", first: true, predicate: ["drawRef"], descendants: true }, { propertyName: "drawCanvasRef", first: true, predicate: ["drawCanvasRef"], descendants: true }], ngImport: i0, template: `
    <label class="cds-label">{{signHereText}}</label>
    <div class="esd-wrapper" #drawRef>
      <div [style.width.px]="_signCanvasWidth">
        <div class="esd-clear-wrapper">
          <button
            class="es-button esd-clear"
            type="button"
            (click)="_clearSign()"
            [disabled]="!_isClearSignEnabled"
          >{{clearButtonText}}</button>
        </div>
        <div
          role="img"
          class="esd-tile-wrapper d-flex flex-column"
          [attr.aria-label]="canvasAriaLabel"
          [style.height.px]="_height"
        >
          <div class="esd-line flex-grow-1 d-flex flex-column justify-content-end">
            <cds-icon
              ariaLabel="Signature X"
              altText="Signature X"
              type="2021"
              name="044-close"
              [svgStyle]="{'width.px': '24' , 'height.px': '24' }"
            ></cds-icon>
          </div>
          <canvas
            #drawCanvasRef
            class="esd-canvas"
            [height]="_height"
            [width]="_signCanvasWidth"
          ></canvas>
        </div>
      </div>
    </div>
  `, isInline: true, styles: ["button.es-button{border:none;background-color:transparent;text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;padding:16px;font-weight:700;color:#056dae}button.es-button:hover,button.es-button:active,button.es-button:focus{color:#034c81}button.es-button:disabled{color:#ccc}\n", ".esd-wrapper{overflow:auto}.esd-clear-wrapper{position:relative;z-index:2}button.esd-clear{position:absolute;top:0;right:40px}.esd-tile-wrapper{position:relative;background-color:#eff3f8;border-radius:16px;padding:0 40px 40px}.esd-line{border-bottom:1px solid #bbbbbb}.esd-line .cds-icon{display:block;padding:8px;--cds-icon-path-fill: #cccccc}.esd-canvas{position:absolute;top:0;left:0;z-index:1}\n"], dependencies: [{ kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureDrawComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-electronic-signature-draw', template: `
    <label class="cds-label">{{signHereText}}</label>
    <div class="esd-wrapper" #drawRef>
      <div [style.width.px]="_signCanvasWidth">
        <div class="esd-clear-wrapper">
          <button
            class="es-button esd-clear"
            type="button"
            (click)="_clearSign()"
            [disabled]="!_isClearSignEnabled"
          >{{clearButtonText}}</button>
        </div>
        <div
          role="img"
          class="esd-tile-wrapper d-flex flex-column"
          [attr.aria-label]="canvasAriaLabel"
          [style.height.px]="_height"
        >
          <div class="esd-line flex-grow-1 d-flex flex-column justify-content-end">
            <cds-icon
              ariaLabel="Signature X"
              altText="Signature X"
              type="2021"
              name="044-close"
              [svgStyle]="{'width.px': '24' , 'height.px': '24' }"
            ></cds-icon>
          </div>
          <canvas
            #drawCanvasRef
            class="esd-canvas"
            [height]="_height"
            [width]="_signCanvasWidth"
          ></canvas>
        </div>
      </div>
    </div>
  `, styles: ["button.es-button{border:none;background-color:transparent;text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;padding:16px;font-weight:700;color:#056dae}button.es-button:hover,button.es-button:active,button.es-button:focus{color:#034c81}button.es-button:disabled{color:#ccc}\n", ".esd-wrapper{overflow:auto}.esd-clear-wrapper{position:relative;z-index:2}button.esd-clear{position:absolute;top:0;right:40px}.esd-tile-wrapper{position:relative;background-color:#eff3f8;border-radius:16px;padding:0 40px 40px}.esd-line{border-bottom:1px solid #bbbbbb}.esd-line .cds-icon{display:block;padding:8px;--cds-icon-path-fill: #cccccc}.esd-canvas{position:absolute;top:0;left:0;z-index:1}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { drawRef: [{
                type: ViewChild,
                args: ['drawRef']
            }], drawCanvasRef: [{
                type: ViewChild,
                args: ['drawCanvasRef']
            }], canvasAriaLabel: [{
                type: Input
            }], signHereText: [{
                type: Input
            }], clearButtonText: [{
                type: Input
            }], lineWidth: [{
                type: Input
            }], height: [{
                type: Input
            }], canvasImageDataChange: [{
                type: Output
            }] } });

class CdsElectronicSignatureTypeComponent {
    set height(h) {
        this._height = h >= 20 ? h : 120;
    }
    set name(_name) {
        this._name = _name;
        this._updateCanvas(_name);
    }
    get name() {
        return this._name;
    }
    // #endregion
    // #region =========== Constructor / Life-cycle Hooks ========== //
    constructor(_doc) {
        this._doc = _doc;
        // #region =========== Properties ========== //
        this._typeCanvasWidth = 200;
        this._height = 120;
        this._unsub$ = new Subject();
        // #endregion
        // #region =========== @Input / @Output ========== //
        this.canvasAriaLabel = 'Signature of';
        this.clearButtonAriaLabel = 'Clear';
        this.typeNameLabel = 'Legal Name';
        this.namePlaceholder = 'Please type your legal name';
        this.previewText = 'Preview';
        this.nameChange = new EventEmitter();
        this.canvasImageDataChange = new EventEmitter();
        this._document = this._doc;
        this._window = this._document?.defaultView;
    }
    ngAfterViewInit() {
        this._canvasEl = this.typeCanvasRef.nativeElement;
        this._canvasContext = this._canvasEl.getContext('2d');
        // will resize the canvas when screen is resized
        fromEvent(this._window, 'resize')
            .pipe(takeUntil$1(this._unsub$), throttleTime(10, null, { leading: false, trailing: true }))
            .subscribe(obv => {
            this._resizeCanvas();
        });
        // resize canvas on init (needs settimout to wait for DOM)
        setTimeout(() => {
            this._resizeCanvas();
        }, 0);
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region =========== Methods =========== //
    _clear() {
        this.name = '';
        this.nameChange.emit('');
        this.nameInput?.nativeElement?.focus();
    }
    _getTextHeight(text) {
        const metrics = this._canvasContext.measureText(text);
        return metrics.fontBoundingBoxAscent + metrics.fontBoundingBoxDescent;
    }
    _getTextWidth(text) {
        return this._canvasContext.measureText(text).width;
    }
    resize() {
        this._resizeCanvas();
    }
    _resizeCanvas() {
        const typeSignWidth = Math.round(this.typeTileRef?.nativeElement?.getBoundingClientRect().width);
        this._typeCanvasWidth = typeSignWidth;
        // After resizing, redraw canvas
        setTimeout(() => {
            this._updateCanvas(this.name);
        }, 0);
    }
    _updateCanvas(name) {
        // check if canvas exits
        if (this._canvasContext) {
            const canvasRect = this._canvasEl.getBoundingClientRect();
            const canvasWidth = canvasRect.width;
            const canvasHeight = canvasRect.height;
            const fontFamily = '"Brush Script MT", "Bradley Hand", "Comic Sans MS"';
            const leftOffset = 4;
            let fontSize = 96;
            // clean canvas before drawing
            this._canvasContext.clearRect(0, 0, canvasWidth, canvasHeight);
            // font settings
            this._canvasContext.textBaseline = 'middle';
            // lower the font size until the text fits the canvas
            do {
                fontSize--;
                this._canvasContext.font = `${fontSize}px ${fontFamily}`;
                if (fontSize <= 10) {
                    break;
                }
            } while (this._getTextWidth(name) > canvasWidth - (leftOffset * 2)
                || this._getTextHeight(name) > canvasHeight);
            this._canvasContext.fillText(name, leftOffset, canvasHeight / 2);
            // send img to output parameter
            this._dataUrl = name ? this._canvasEl.toDataURL('image/png') : null;
            setTimeout(() => {
                this.canvasImageDataChange.emit(this._dataUrl);
            }, 0);
        }
    }
    _updateName(name) {
        this._name = name;
        this.nameChange.emit(this.name);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureTypeComponent, deps: [{ token: DOCUMENT }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsElectronicSignatureTypeComponent, selector: "cds-electronic-signature-type", inputs: { canvasAriaLabel: "canvasAriaLabel", clearButtonAriaLabel: "clearButtonAriaLabel", typeNameLabel: "typeNameLabel", namePlaceholder: "namePlaceholder", previewText: "previewText", idStr: "idStr", height: "height", name: "name" }, outputs: { nameChange: "nameChange", canvasImageDataChange: "canvasImageDataChange" }, viewQueries: [{ propertyName: "nameInput", first: true, predicate: ["nameInput"], descendants: true }, { propertyName: "typeTileRef", first: true, predicate: ["typeTileRef"], descendants: true }, { propertyName: "typeCanvasRef", first: true, predicate: ["typeCanvasRef"], descendants: true }], ngImport: i0, template: `
    <label
      class="cds-label"
      [id]="idStr + '_nameLabel'"
      [for]="idStr + '_nameInput'"
    >{{typeNameLabel}}</label>
    <div class="cds-form-field-content">
      <input
        #nameInput
        cdsInput
        type="text"
        class="pr-5"
        [id]="idStr + '_nameInput'"
        [attr.aria-labelledby]="idStr + '_nameLabel'"
        [placeholder]="namePlaceholder"
        [ngModel]="name"
        (ngModelChange)="_updateName($event)"
      />
      <button
        *ngIf="!!name"
        type="button"
        class="cds-input-suffix bg-transparent border-0 est-close-btn"
        [attr.aria-label]="clearButtonAriaLabel"
        (click)="_clear()"
      >
        <cds-icon
          [ariaLabel]="clearButtonAriaLabel"
          [altText]="clearButtonAriaLabel"
          type="2021"
          name="044-close"
          [svgStyle]="{'width.px': '20' , 'height.px': '20' }"
        ></cds-icon>
      </button>
    </div>
    <label class="cds-label mt-4">{{previewText}}</label>
    <div
      role="img"
      class="est-tile-wrapper"
      [attr.aria-label]="canvasAriaLabel + ' ' + name"
    >
      <div #typeTileRef>
        <canvas
          #typeCanvasRef
          class="est-canvas"
          [height]="_height"
          [width]="_typeCanvasWidth"
        ></canvas>
      </div>
    </div>
  `, isInline: true, styles: ["button.es-button{border:none;background-color:transparent;text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;padding:16px;font-weight:700;color:#056dae}button.es-button:hover,button.es-button:active,button.es-button:focus{color:#034c81}button.es-button:disabled{color:#ccc}\n", ".est-tile-wrapper{background-color:#fcfcfc;border-radius:16px;padding:40px 16px}.est-canvas{border-bottom:1px solid #bbb}.est-close-btn{padding:12px 16px 12px 12px}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1$3.DefaultValueAccessor, selector: "input:not([type=checkbox])[formControlName],textarea[formControlName],input:not([type=checkbox])[formControl],textarea[formControl],input:not([type=checkbox])[ngModel],textarea[ngModel],[ngDefaultControl]" }, { kind: "directive", type: i1$3.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i1$3.NgModel, selector: "[ngModel]:not([formControlName]):not([formControl])", inputs: ["name", "disabled", "ngModel", "ngModelOptions"], outputs: ["ngModelChange"], exportAs: ["ngModel"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }, { kind: "directive", type: CdsInput, selector: "input[cdsInput],input[cds-input]", exportAs: ["cdsInput"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureTypeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-electronic-signature-type', template: `
    <label
      class="cds-label"
      [id]="idStr + '_nameLabel'"
      [for]="idStr + '_nameInput'"
    >{{typeNameLabel}}</label>
    <div class="cds-form-field-content">
      <input
        #nameInput
        cdsInput
        type="text"
        class="pr-5"
        [id]="idStr + '_nameInput'"
        [attr.aria-labelledby]="idStr + '_nameLabel'"
        [placeholder]="namePlaceholder"
        [ngModel]="name"
        (ngModelChange)="_updateName($event)"
      />
      <button
        *ngIf="!!name"
        type="button"
        class="cds-input-suffix bg-transparent border-0 est-close-btn"
        [attr.aria-label]="clearButtonAriaLabel"
        (click)="_clear()"
      >
        <cds-icon
          [ariaLabel]="clearButtonAriaLabel"
          [altText]="clearButtonAriaLabel"
          type="2021"
          name="044-close"
          [svgStyle]="{'width.px': '20' , 'height.px': '20' }"
        ></cds-icon>
      </button>
    </div>
    <label class="cds-label mt-4">{{previewText}}</label>
    <div
      role="img"
      class="est-tile-wrapper"
      [attr.aria-label]="canvasAriaLabel + ' ' + name"
    >
      <div #typeTileRef>
        <canvas
          #typeCanvasRef
          class="est-canvas"
          [height]="_height"
          [width]="_typeCanvasWidth"
        ></canvas>
      </div>
    </div>
  `, styles: ["button.es-button{border:none;background-color:transparent;text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;padding:16px;font-weight:700;color:#056dae}button.es-button:hover,button.es-button:active,button.es-button:focus{color:#034c81}button.es-button:disabled{color:#ccc}\n", ".est-tile-wrapper{background-color:#fcfcfc;border-radius:16px;padding:40px 16px}.est-canvas{border-bottom:1px solid #bbb}.est-close-btn{padding:12px 16px 12px 12px}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }], propDecorators: { nameInput: [{
                type: ViewChild,
                args: ['nameInput']
            }], typeTileRef: [{
                type: ViewChild,
                args: ['typeTileRef']
            }], typeCanvasRef: [{
                type: ViewChild,
                args: ['typeCanvasRef']
            }], canvasAriaLabel: [{
                type: Input
            }], clearButtonAriaLabel: [{
                type: Input
            }], typeNameLabel: [{
                type: Input
            }], namePlaceholder: [{
                type: Input
            }], previewText: [{
                type: Input
            }], idStr: [{
                type: Input
            }], height: [{
                type: Input
            }], name: [{
                type: Input
            }], nameChange: [{
                type: Output
            }], canvasImageDataChange: [{
                type: Output
            }] } });

let _esUniqueIdCounter = 0;
class CdsElectronicSignatureComponent {
    constructor() {
        // #region =========== Properties ========== //
        this._activeTab = 0;
        // #endregion
        // #region =========== @Input / Output ========== //
        this.ariaLabel = 'Electronic Signature';
        this.drawCanvasAriaLabel = 'Electronic Signature';
        this.typeCanvasAriaLabel = 'Signature of';
        this.drawTitle = 'Draw';
        this.typeTitle = 'Type';
        this.uploadTitle = 'Upload';
        this.signHereText = 'Please Sign Here';
        this.clearButtonAriaLabel = 'Clear';
        this.clearButtonText = 'Clear';
        this.typeNameLabel = 'Legal Name';
        this.namePlaceholder = 'Please type your legal name';
        this.previewText = 'Preview';
        this.name = '';
        this.lineWidth = 2;
        this._uid = `CdsElectronicSignature_${_esUniqueIdCounter++}`;
        this.nameChange = new EventEmitter();
        this.canvasImageDataChange = new EventEmitter();
    }
    set idStr(val) {
        this._idStr = val || this._uid;
    }
    get idStr() {
        return this._idStr || this._uid;
    }
    // #endregion
    // #region =========== Methods =========== //
    _activeTabChange(tabIndex) {
        if (tabIndex !== 1) {
            this.name = '';
        }
    }
    _canvasImageDataChange(data) {
        this.canvasImageDataChange.emit(data);
    }
    readjustWidth() {
        this.esDraw?.resize();
        this.esType?.resize();
    }
    _updateName(name) {
        this.nameChange.emit(name);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsElectronicSignatureComponent, selector: "cds-electronic-signature", inputs: { ariaLabel: "ariaLabel", drawCanvasAriaLabel: "drawCanvasAriaLabel", typeCanvasAriaLabel: "typeCanvasAriaLabel", drawTitle: "drawTitle", typeTitle: "typeTitle", uploadTitle: "uploadTitle", signHereText: "signHereText", clearButtonAriaLabel: "clearButtonAriaLabel", clearButtonText: "clearButtonText", typeNameLabel: "typeNameLabel", namePlaceholder: "namePlaceholder", previewText: "previewText", name: "name", lineWidth: "lineWidth", drawCanvasHeight: "drawCanvasHeight", typeCanvasHeight: "typeCanvasHeight", idStr: "idStr" }, outputs: { nameChange: "nameChange", canvasImageDataChange: "canvasImageDataChange" }, viewQueries: [{ propertyName: "esDraw", first: true, predicate: ["esDraw"], descendants: true }, { propertyName: "esType", first: true, predicate: ["esType"], descendants: true }], ngImport: i0, template: `
    <cds-tab-bar-group
      cdsTabBarGroup="light"
      [ariaLabel]="ariaLabel"
      [iconAdded]="true"
      [labelAdded]="true"
      [(activeTab)]="_activeTab"
      (activeTabChange)="_activeTabChange($event)"
    >
      <cds-tab-bar
        [label]="drawTitle"
        iconID="022-edit"
      >
        <cds-electronic-signature-draw
          #esDraw
          *ngIf="!_activeTab"
          [canvasAriaLabel]="drawCanvasAriaLabel"
          [signHereText]="signHereText"
          [clearButtonText]="clearButtonText"
          [lineWidth]="lineWidth"
          [height]="drawCanvasHeight"
          (canvasImageDataChange)="_canvasImageDataChange($event)"
        ></cds-electronic-signature-draw>
      </cds-tab-bar>
      <cds-tab-bar
        [label]="typeTitle"
        iconID="keyboard"
      >
        <cds-electronic-signature-type
          #esType
          *ngIf="_activeTab === 1"
          [idStr]="idStr + '_type'"
          [canvasAriaLabel]="typeCanvasAriaLabel"
          [(name)]="name"
          (nameChange)="_updateName($event)"
          [typeNameLabel]="typeNameLabel"
          [previewText]="previewText"
          [height]="typeCanvasHeight"
          [clearButtonAriaLabel]="clearButtonAriaLabel"
          (canvasImageDataChange)="_canvasImageDataChange($event)"
        ></cds-electronic-signature-type>
      </cds-tab-bar>
    </cds-tab-bar-group>
  `, isInline: true, styles: ["button.es-button{border:none;background-color:transparent;text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;padding:16px;font-weight:700;color:#056dae}button.es-button:hover,button.es-button:active,button.es-button:focus{color:#034c81}button.es-button:disabled{color:#ccc}\n", ":host{--cds-tab-bar-label-height: auto;--cds-tab-bar-panel-padding: 20px 0 0 0;--cds-tab-bar-panel-padding-sm: 20px 0 0 0;--cds-tab-bar-panel-width: 100%;--cds-tab-bar-list-button-last-child-margin-right: 0;--cds-tab-bar-list-button-first-child-margin-left: 0;--cds-tab-bar-list-button-padding: 0 16px 0 0;--cds-tab-bar-list-border-radius: 0;--cds-tab-bar-list-margin: 0 0 4px 0}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "component", type: CdsTabBarGroup, selector: "cds-tab-bar-group", inputs: ["activeTab", "iconAdded", "labelAdded", "disableTabBar", "largeTabs", "horizontalScroll", "ariaLabel", "cdsTabBarGroup"], outputs: ["activeTabChange"], exportAs: ["cdsTabBarGroup"] }, { kind: "component", type: CdsTabBar, selector: "cds-tab-bar", inputs: ["label", "iconID"], exportAs: ["cdsTabBar"] }, { kind: "component", type: CdsElectronicSignatureDrawComponent, selector: "cds-electronic-signature-draw", inputs: ["canvasAriaLabel", "signHereText", "clearButtonText", "lineWidth", "height"], outputs: ["canvasImageDataChange"] }, { kind: "component", type: CdsElectronicSignatureTypeComponent, selector: "cds-electronic-signature-type", inputs: ["canvasAriaLabel", "clearButtonAriaLabel", "typeNameLabel", "namePlaceholder", "previewText", "idStr", "height", "name"], outputs: ["nameChange", "canvasImageDataChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-electronic-signature', template: `
    <cds-tab-bar-group
      cdsTabBarGroup="light"
      [ariaLabel]="ariaLabel"
      [iconAdded]="true"
      [labelAdded]="true"
      [(activeTab)]="_activeTab"
      (activeTabChange)="_activeTabChange($event)"
    >
      <cds-tab-bar
        [label]="drawTitle"
        iconID="022-edit"
      >
        <cds-electronic-signature-draw
          #esDraw
          *ngIf="!_activeTab"
          [canvasAriaLabel]="drawCanvasAriaLabel"
          [signHereText]="signHereText"
          [clearButtonText]="clearButtonText"
          [lineWidth]="lineWidth"
          [height]="drawCanvasHeight"
          (canvasImageDataChange)="_canvasImageDataChange($event)"
        ></cds-electronic-signature-draw>
      </cds-tab-bar>
      <cds-tab-bar
        [label]="typeTitle"
        iconID="keyboard"
      >
        <cds-electronic-signature-type
          #esType
          *ngIf="_activeTab === 1"
          [idStr]="idStr + '_type'"
          [canvasAriaLabel]="typeCanvasAriaLabel"
          [(name)]="name"
          (nameChange)="_updateName($event)"
          [typeNameLabel]="typeNameLabel"
          [previewText]="previewText"
          [height]="typeCanvasHeight"
          [clearButtonAriaLabel]="clearButtonAriaLabel"
          (canvasImageDataChange)="_canvasImageDataChange($event)"
        ></cds-electronic-signature-type>
      </cds-tab-bar>
    </cds-tab-bar-group>
  `, styles: ["button.es-button{border:none;background-color:transparent;text-decoration:underline;text-decoration-thickness:2px;text-underline-offset:3px;padding:16px;font-weight:700;color:#056dae}button.es-button:hover,button.es-button:active,button.es-button:focus{color:#034c81}button.es-button:disabled{color:#ccc}\n", ":host{--cds-tab-bar-label-height: auto;--cds-tab-bar-panel-padding: 20px 0 0 0;--cds-tab-bar-panel-padding-sm: 20px 0 0 0;--cds-tab-bar-panel-width: 100%;--cds-tab-bar-list-button-last-child-margin-right: 0;--cds-tab-bar-list-button-first-child-margin-left: 0;--cds-tab-bar-list-button-padding: 0 16px 0 0;--cds-tab-bar-list-border-radius: 0;--cds-tab-bar-list-margin: 0 0 4px 0}\n"] }]
        }], propDecorators: { esDraw: [{
                type: ViewChild,
                args: ['esDraw']
            }], esType: [{
                type: ViewChild,
                args: ['esType']
            }], ariaLabel: [{
                type: Input
            }], drawCanvasAriaLabel: [{
                type: Input
            }], typeCanvasAriaLabel: [{
                type: Input
            }], drawTitle: [{
                type: Input
            }], typeTitle: [{
                type: Input
            }], uploadTitle: [{
                type: Input
            }], signHereText: [{
                type: Input
            }], clearButtonAriaLabel: [{
                type: Input
            }], clearButtonText: [{
                type: Input
            }], typeNameLabel: [{
                type: Input
            }], namePlaceholder: [{
                type: Input
            }], previewText: [{
                type: Input
            }], name: [{
                type: Input
            }], lineWidth: [{
                type: Input
            }], drawCanvasHeight: [{
                type: Input
            }], typeCanvasHeight: [{
                type: Input
            }], idStr: [{
                type: Input
            }], nameChange: [{
                type: Output
            }], canvasImageDataChange: [{
                type: Output
            }] } });

const COMPONENTS$k = [
    CdsElectronicSignatureComponent
];
class CdsElectronicSignatureModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureModule, declarations: [CdsElectronicSignatureComponent, CdsElectronicSignatureDrawComponent,
            CdsElectronicSignatureTypeComponent], imports: [CommonModule,
            FormsModule,
            CdsTabBarModule,
            CdsIconModule,
            CdsInputModule,
            CdsFormsModule], exports: [CdsElectronicSignatureComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureModule, imports: [CommonModule,
            FormsModule,
            CdsTabBarModule,
            CdsIconModule,
            CdsInputModule,
            CdsFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsElectronicSignatureModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        CdsTabBarModule,
                        CdsIconModule,
                        CdsInputModule,
                        CdsFormsModule
                    ],
                    declarations: [
                        ...COMPONENTS$k,
                        CdsElectronicSignatureDrawComponent,
                        CdsElectronicSignatureTypeComponent
                    ],
                    exports: [...COMPONENTS$k]
                }]
        }] });

/** Change event object emitted by CdsCheckbox  */
class CdsCheckboxTreeChange {
    constructor(
    /** A reference to the CdsCheckbox itself */
    source, 
    /** The string value assigned to the CdsCheckbox. */
    value, 
    /** whether the new state is checked or not */
    checked) {
        this.source = source;
        this.value = value;
        this.checked = checked;
    }
}

class CdsCheckboxTreeService {
    constructor() {
        this.groupChange$ = new BehaviorSubject([]);
        this.checkboxChange$ = new Subject();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

let nextUniqueId$2 = 0;
let nextUniqueName = 0;
class CdsCheckboxTreeNodeComponent {
    /**
     * Whether this checkbox button is checked
     * @note only 1 checkbox can be checked, therefore only the last will reamain so
     */
    set checked(checked) {
        this._checked = checked;
    }
    get checked() {
        return this._checked;
    }
    /**
     * whether the checkbox should be disabled
     */
    set disabled(disabled) {
        this._disabled = coerceBooleanProperty(disabled);
    }
    get disabled() {
        return this._disabled;
    }
    /**
     * id assigned to the native input element
     * @note if an id is not provided, an auto-increment id is applied
     */
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    /**
     * name of the individual checkbox to group certain inputs.
     * @note sets an auto increment name by deafult if none is provided
     */
    set name(name) {
        this._name = name || this._uname;
    }
    get name() {
        return this._name;
    }
    set readonly(isReadonly) {
        this._readonly = isReadonly;
        this._updateAriaDescribedby();
    }
    get readonly() {
        return this._readonly;
    }
    // #endregion
    // #region ---------- Constructor / Lifecycle Hooks ---------- //
    constructor(_checkboxTreeService, input, id, chxed) {
        this._checkboxTreeService = _checkboxTreeService;
        this.input = input;
        this.id = id;
        this.chxed = chxed;
        // #region ---------- Form Base ---------- //
        this.fieldType = 'checkbox-tree-node';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        // #endregion
        // #region ---------- Properties ---------- //
        this.unsub$ = new Subject();
        this._checked = false;
        this.collapsedMessage = 'Additional information collapsed';
        this.expandedMessage = 'Additional information expanded';
        this._uid = `cds-checkbox-tree-node-${nextUniqueId$2++}`;
        this._uname = `cds-checkbox-tree-${nextUniqueName++}`;
        // #endregion
        // #region ---------- @Output ---------- //
        /**
         * Emitted when the input loses focus
         */
        this.blur = new EventEmitter();
        /**
         * Emitted when change is fired on the <input>.
         * Primarily used to pass the value up the the group, but also and easy way for
         * consumers to get a referece to the selected component (and element)
         */
        this.change = new EventEmitter();
        this.checkedChange = new EventEmitter();
        /**
         * Emitted when the input gains focus
         */
        this.focus = new EventEmitter();
        /**
         * Emitted when the input loses focus
         */
        this.onShiftTab = new EventEmitter();
        /**
         * Emitted when the input loses focus
         */
        this.onTab = new EventEmitter();
        // #endregion
        // #region ---------- ControlValueAccessor Methods ---------- //
        // All methods below are required for the Value Control Accessor interface to work with [(ngModel)]
        this.onChange = (value) => { };
        this.onTouched = () => { };
        this.fieldId = this.id || `cds-checkbox-tree-node-${nextUniqueId$2++}`;
        setTimeout(() => this.checkValue());
        // set the default values if not provided
        if (chxed)
            this._checked = coerceBooleanProperty(this.chxed);
        this.idStr = this.idStr;
        // applies the provided name or an autoincrement one
        this.name = this.name;
    }
    ngOnInit() {
        this._checkboxTreeService.groupChange$
            .pipe(takeUntil$1(this.unsub$))
            .subscribe(checkboxList => {
            this.checked = checkboxList.includes(this.value);
        });
    }
    ngOnChanges(changes) {
        for (const propName in changes) {
            const changeList = ['readonly', 'ariaDescribedby', 'hasContent'];
            if (changeList.includes(propName)) {
                this._updateAriaDescribedby();
            }
        }
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region ---------- Methods ---------- //
    /** Callback for the cases where the focused state of the input changes. */
    focusChanged(isFocused) {
        if (this.disabled)
            return;
        this.focusChange.next(isFocused);
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    /**
     * Triggered when the checkbox button received a click or the
     * input recognized any change.  Clicking on a label element, will trigger
     * a change event on the associated input.
     */
    onCheckboxTreeNodeChange(event) {
        // We always have to stop propagation on the change event.
        // Otherwise the change event, from the input element, will bubble up and
        // emit its event object to the `change` output.
        event.stopPropagation();
        event.preventDefault();
        if (this.disabled || this.readonly) {
            return;
        }
        this._checked = !this.checked;
        // tell the FormControl to consider the control as 'touched'
        this.onTouched();
        this.change.emit(new CdsCheckboxTreeChange(this, this.value, this._checked));
        this._checkboxTreeService.checkboxChange$.next();
        this.onChange(this._checked);
        this.checkedChange.next(this._checked);
    }
    _updateAriaDescribedby() {
        const ariaDescIdList = [];
        if (this.ariaDescribedby)
            ariaDescIdList.push(this.ariaDescribedby);
        // if (this.hasContent) ariaDescIdList.push(this.idStr + '-expand');
        this._inputAriaDescribedby = ariaDescIdList.length > 0 ? ariaDescIdList.join(' ') : null;
    }
    readonlyCheckbox() {
        if (this.readonly)
            return false;
    }
    /**
     * Allows Angular to update the model. The form will pass new values in here
     */
    writeValue(checked) {
        this.checked = checked;
    }
    // Allows Angular to register a function to call when the model changes.
    // Save the function as a property to call later here.
    registerOnChange(fn) {
        this.onChange = fn;
    }
    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    // Allows Angular to disable the input.
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeNodeComponent, deps: [{ token: CdsCheckboxTreeService }, { token: i0.ElementRef }, { token: 'id', attribute: true }, { token: 'checked', attribute: true }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCheckboxTreeNodeComponent, selector: "cds-checkbox-tree-node", inputs: { alignLabel: "alignLabel", ariaDescribedby: "ariaDescribedby", ariaLabelledby: "ariaLabelledby", ariaRequired: "ariaRequired", checked: "checked", collapsedMessage: "collapsedMessage", disabled: "disabled", expandedMessage: "expandedMessage", hasContent: "hasContent", idStr: "idStr", label: "label", name: "name", readonly: "readonly", value: "value" }, outputs: { blur: "blur", change: "change", checkedChange: "checkedChange", focus: "focus", onShiftTab: "onShiftTab", onTab: "onTab" }, host: { listeners: { "focusin": "focusChanged(true)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId", "attr.role": "this.role" }, classAttribute: "cds-checkbox-tree-node" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCheckboxTreeNodeComponent)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsCheckboxTreeNodeComponent }
        ], usesOnChanges: true, ngImport: i0, template: `
  <div class="cbt-input-label-wrapper">
    <input
      class="cds-checkbox-tree-node-input cbt-input"
      type="checkbox"
      [class.cbt-error]="isInErrorState"
      [class.cbt-readonly]="readonly"
      [attr.id]="idStr"
      [value]="value"
      [attr.name]="name"
      [checked]="checked"
      [attr.aria-required]="ariaRequired"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="_inputAriaDescribedby"
      [attr.aria-expanded]="hasContent ? checked ? 'true' : 'false' : null"
      [attr.aria-readonly]="readonly"
      [disabled]="disabled"
      (blur)="blur.emit($event)"
      (keydown.Tab)="onTab.emit($event)"
      (keydown.shift.Tab)="onShiftTab.emit($event)"
      (focus)="focus.emit($event)"
      (change)="onCheckboxTreeNodeChange($event)"
      (click)="readonlyCheckbox()"
    />
    <label
      class="cds-checkbox-tree-node-label cbt-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
      [innerHTML]="label"
      [class.position-top]="alignLabel === 'top'"
      [class.position-bottom]="alignLabel === 'bottom'"
    >
    </label>
  </div>

  <ng-content *ngIf="checked && hasContent" select="[cdsCheckboxTreeAddition]"></ng-content>
  `, isInline: true, styles: [".cbt-input-label-wrapper{display:flex;gap:var(--cds-checkbox-tree-label-gap, 16px)}.cbt-input{width:var(--cds-checkbox-tree-icon-size, 24px);height:var(--cds-checkbox-tree-icon-size, 24px);border-radius:var(--cds-checkbox-tree-border-radius, 4px);background-repeat:no-repeat;background-size:var(--cds-checkbox-tree-icon-size, 24px);background-position:center;flex:0 0 auto;border:var(--cds-checkbox-tree-border-width, 2px) solid var(--cds-checkbox-tree-border-color, #056dae);background-color:var(--cds-checkbox-tree-bg-color, none);appearance:none}.cbt-input:enabled:focus-visible,.cbt-input.cbt-enabled:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-checkbox-tree-outline-color, #002a54);outline-offset:1px}.cbt-input:enabled:hover,.cbt-input.cbt-enabled:hover{border-color:var(--cds-checkbox-tree-hover-border-color, #034c81)}.cbt-input:enabled:active,.cbt-input.cbt-enabled:active{border-color:var(--cds-checkbox-tree-active-border-color, #002a54)}.cbt-input:enabled.cbt-error,.cbt-input.cbt-enabled.cbt-error{border-color:var(--cds-checkbox-tree-error-border-color, #d60000)}.cbt-input:enabled.cbt-readonly,.cbt-input.cbt-enabled.cbt-readonly{border-color:var(--cds-checkbox-tree-readonly-border-color, #666666)}.cbt-input:enabled.cbt-readonly+.cbt-label,.cbt-input.cbt-enabled.cbt-readonly+.cbt-label{color:var(--cds-checkbox-tree-readonly-color, #666666)}.cbt-input:enabled:checked,.cbt-input:enabled.cbt-checked,.cbt-input.cbt-enabled:checked,.cbt-input.cbt-enabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-inline-img, none)}.cbt-input:enabled:checked:hover,.cbt-input:enabled.cbt-checked:hover,.cbt-input.cbt-enabled:checked:hover,.cbt-input.cbt-enabled.cbt-checked:hover{background-image:var(--cds-checkbox-tree-checked-hover-inline-img, none)}.cbt-input:enabled:checked:active,.cbt-input:enabled.cbt-checked:active,.cbt-input.cbt-enabled:checked:active,.cbt-input.cbt-enabled.cbt-checked:active{background-image:var(--cds-checkbox-tree-checked-active-inline-img, none)}.cbt-input:enabled:checked.cbt-error,.cbt-input:enabled.cbt-checked.cbt-error,.cbt-input.cbt-enabled:checked.cbt-error,.cbt-input.cbt-enabled.cbt-checked.cbt-error{background-image:var(--cds-checkbox-tree-checked-error-inline-img, none)}.cbt-input:enabled:checked.cbt-readonly,.cbt-input:enabled.cbt-checked.cbt-readonly,.cbt-input.cbt-enabled:checked.cbt-readonly,.cbt-input.cbt-enabled.cbt-checked.cbt-readonly{background-image:var(--cds-checkbox-tree-checked-readonly-inline-img, none)}.cbt-input:enabled.cbt-intermediate,.cbt-input.cbt-enabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-inline-img, none)}.cbt-input:enabled.cbt-intermediate:hover,.cbt-input.cbt-enabled.cbt-intermediate:hover{background-image:var(--cds-checkbox-tree-intermediate-hover-inline-img, none)}.cbt-input:enabled.cbt-intermediate:active,.cbt-input.cbt-enabled.cbt-intermediate:active{background-image:var(--cds-checkbox-tree-intermediate-active-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-error,.cbt-input.cbt-enabled.cbt-intermediate.cbt-error{background-image:var(--cds-checkbox-tree-intermediate-error-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-readonly,.cbt-input.cbt-enabled.cbt-intermediate.cbt-readonly{background-image:var(--cds-checkbox-tree-intermediate-readonly-inline-img, none)}.cbt-input:disabled,.cbt-input.cbt-disabled{border-color:var(--cds-checkbox-tree-disabled-border-color, #bbb);background-color:var(--cds-checkbox-tree-disabled-bg-color, none)}.cbt-input:disabled:checked,.cbt-input:disabled.cbt-checked,.cbt-input.cbt-disabled:checked,.cbt-input.cbt-disabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-disabled-inline-img, none)}.cbt-input:disabled.cbt-intermediate,.cbt-input.cbt-disabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-disabled-inline-img, none)}.cbt-input:disabled+.cbt-label,.cbt-input.cbt-disabled+.cbt-label{color:var(--cds-checkbox-tree-disabled-color, #ccc)}.cbt-label{margin:0}\n", ":host{position:relative}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeNodeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox-tree-node', host: {
                        class: 'cds-checkbox-tree-node',
                        '(focusin)': 'focusChanged(true)',
                        '(input)': 'checkValue()'
                    }, providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCheckboxTreeNodeComponent)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsCheckboxTreeNodeComponent }
                    ], template: `
  <div class="cbt-input-label-wrapper">
    <input
      class="cds-checkbox-tree-node-input cbt-input"
      type="checkbox"
      [class.cbt-error]="isInErrorState"
      [class.cbt-readonly]="readonly"
      [attr.id]="idStr"
      [value]="value"
      [attr.name]="name"
      [checked]="checked"
      [attr.aria-required]="ariaRequired"
      [attr.aria-labelledby]="ariaLabelledby"
      [attr.aria-describedby]="_inputAriaDescribedby"
      [attr.aria-expanded]="hasContent ? checked ? 'true' : 'false' : null"
      [attr.aria-readonly]="readonly"
      [disabled]="disabled"
      (blur)="blur.emit($event)"
      (keydown.Tab)="onTab.emit($event)"
      (keydown.shift.Tab)="onShiftTab.emit($event)"
      (focus)="focus.emit($event)"
      (change)="onCheckboxTreeNodeChange($event)"
      (click)="readonlyCheckbox()"
    />
    <label
      class="cds-checkbox-tree-node-label cbt-label"
      [attr.for]="idStr"
      [attr.id]="idStr + '-label'"
      [innerHTML]="label"
      [class.position-top]="alignLabel === 'top'"
      [class.position-bottom]="alignLabel === 'bottom'"
    >
    </label>
  </div>

  <ng-content *ngIf="checked && hasContent" select="[cdsCheckboxTreeAddition]"></ng-content>
  `, styles: [".cbt-input-label-wrapper{display:flex;gap:var(--cds-checkbox-tree-label-gap, 16px)}.cbt-input{width:var(--cds-checkbox-tree-icon-size, 24px);height:var(--cds-checkbox-tree-icon-size, 24px);border-radius:var(--cds-checkbox-tree-border-radius, 4px);background-repeat:no-repeat;background-size:var(--cds-checkbox-tree-icon-size, 24px);background-position:center;flex:0 0 auto;border:var(--cds-checkbox-tree-border-width, 2px) solid var(--cds-checkbox-tree-border-color, #056dae);background-color:var(--cds-checkbox-tree-bg-color, none);appearance:none}.cbt-input:enabled:focus-visible,.cbt-input.cbt-enabled:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-checkbox-tree-outline-color, #002a54);outline-offset:1px}.cbt-input:enabled:hover,.cbt-input.cbt-enabled:hover{border-color:var(--cds-checkbox-tree-hover-border-color, #034c81)}.cbt-input:enabled:active,.cbt-input.cbt-enabled:active{border-color:var(--cds-checkbox-tree-active-border-color, #002a54)}.cbt-input:enabled.cbt-error,.cbt-input.cbt-enabled.cbt-error{border-color:var(--cds-checkbox-tree-error-border-color, #d60000)}.cbt-input:enabled.cbt-readonly,.cbt-input.cbt-enabled.cbt-readonly{border-color:var(--cds-checkbox-tree-readonly-border-color, #666666)}.cbt-input:enabled.cbt-readonly+.cbt-label,.cbt-input.cbt-enabled.cbt-readonly+.cbt-label{color:var(--cds-checkbox-tree-readonly-color, #666666)}.cbt-input:enabled:checked,.cbt-input:enabled.cbt-checked,.cbt-input.cbt-enabled:checked,.cbt-input.cbt-enabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-inline-img, none)}.cbt-input:enabled:checked:hover,.cbt-input:enabled.cbt-checked:hover,.cbt-input.cbt-enabled:checked:hover,.cbt-input.cbt-enabled.cbt-checked:hover{background-image:var(--cds-checkbox-tree-checked-hover-inline-img, none)}.cbt-input:enabled:checked:active,.cbt-input:enabled.cbt-checked:active,.cbt-input.cbt-enabled:checked:active,.cbt-input.cbt-enabled.cbt-checked:active{background-image:var(--cds-checkbox-tree-checked-active-inline-img, none)}.cbt-input:enabled:checked.cbt-error,.cbt-input:enabled.cbt-checked.cbt-error,.cbt-input.cbt-enabled:checked.cbt-error,.cbt-input.cbt-enabled.cbt-checked.cbt-error{background-image:var(--cds-checkbox-tree-checked-error-inline-img, none)}.cbt-input:enabled:checked.cbt-readonly,.cbt-input:enabled.cbt-checked.cbt-readonly,.cbt-input.cbt-enabled:checked.cbt-readonly,.cbt-input.cbt-enabled.cbt-checked.cbt-readonly{background-image:var(--cds-checkbox-tree-checked-readonly-inline-img, none)}.cbt-input:enabled.cbt-intermediate,.cbt-input.cbt-enabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-inline-img, none)}.cbt-input:enabled.cbt-intermediate:hover,.cbt-input.cbt-enabled.cbt-intermediate:hover{background-image:var(--cds-checkbox-tree-intermediate-hover-inline-img, none)}.cbt-input:enabled.cbt-intermediate:active,.cbt-input.cbt-enabled.cbt-intermediate:active{background-image:var(--cds-checkbox-tree-intermediate-active-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-error,.cbt-input.cbt-enabled.cbt-intermediate.cbt-error{background-image:var(--cds-checkbox-tree-intermediate-error-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-readonly,.cbt-input.cbt-enabled.cbt-intermediate.cbt-readonly{background-image:var(--cds-checkbox-tree-intermediate-readonly-inline-img, none)}.cbt-input:disabled,.cbt-input.cbt-disabled{border-color:var(--cds-checkbox-tree-disabled-border-color, #bbb);background-color:var(--cds-checkbox-tree-disabled-bg-color, none)}.cbt-input:disabled:checked,.cbt-input:disabled.cbt-checked,.cbt-input.cbt-disabled:checked,.cbt-input.cbt-disabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-disabled-inline-img, none)}.cbt-input:disabled.cbt-intermediate,.cbt-input.cbt-disabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-disabled-inline-img, none)}.cbt-input:disabled+.cbt-label,.cbt-input.cbt-disabled+.cbt-label{color:var(--cds-checkbox-tree-disabled-color, #ccc)}.cbt-label{margin:0}\n", ":host{position:relative}\n"] }]
        }], ctorParameters: () => [{ type: CdsCheckboxTreeService }, { type: i0.ElementRef }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['checked']
                }] }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], alignLabel: [{
                type: Input
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], checked: [{
                type: Input
            }], collapsedMessage: [{
                type: Input
            }], disabled: [{
                type: Input
            }], expandedMessage: [{
                type: Input
            }], hasContent: [{
                type: Input
            }], idStr: [{
                type: Input
            }], label: [{
                type: Input
            }], name: [{
                type: Input
            }], readonly: [{
                type: Input
            }], value: [{
                type: Input
            }], blur: [{
                type: Output
            }], change: [{
                type: Output
            }], checkedChange: [{
                type: Output
            }], focus: [{
                type: Output
            }], onShiftTab: [{
                type: Output
            }], onTab: [{
                type: Output
            }] } });

let nextUniqueId$1 = 0;
class CdsCheckboxTreeComponent {
    set value(val) {
        const isValueArray = val && Array.isArray(val);
        this._value = isValueArray ? [...val] : [];
        this._checkboxTreeService.groupChange$.next(this._value);
        this._updateCheckboxState();
    }
    get value() {
        return this._value;
    }
    get checked() {
        return this._checked;
    }
    /**
     * gets the currently selected CdsCheckbox component
     */
    get checkedCdsCheckbox() {
        if (!this._checkboxes)
            return null;
        return this._checkboxes.find(checkbox => checkbox.checked);
    }
    /**
     * disables the entire
     */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
        this._updateDisabled();
    }
    get idStr() {
        return this._id;
    }
    set idStr(value) {
        this._id = value || this._uid;
    }
    set readonly(isReadonly) {
        this._readonly = coerceBooleanProperty(isReadonly);
        this._updateReadonly();
    }
    get readonly() {
        return this._readonly;
    }
    // #endregion
    // #region ---------- Constructor / Lifecycle Hooks ---------- //
    constructor(_el, _checkboxTreeService, id, input) {
        this._el = _el;
        this._checkboxTreeService = _checkboxTreeService;
        this.id = id;
        this.input = input;
        // #region ---------- Form Base ---------- //
        this.fieldType = 'checkbox-tree';
        this.focusChange = new Subject();
        this.hasValueChange = new Subject();
        this.disabledChange = new Subject();
        // #endregion
        // #region ---------- Properties ---------- //
        this.unsub$ = new Subject();
        // Used for intermediate(-) checkbox
        this._isIntermediate = false;
        this._value = [];
        this._checked = false;
        this._disabled = false;
        this._uid = `cds-checkbox-tree-${nextUniqueId$1++}`;
        /**
         * Used for parent checkbox checked/unchecked
         */
        this.treeChecked = false;
        // #endregion
        // #region ---------- @Output ---------- //
        this.blur = new EventEmitter();
        this.change = new EventEmitter();
        this.focus = new EventEmitter();
        this.onShiftTab = new EventEmitter();
        this.onTab = new EventEmitter();
        // #endregion
        // #region ---------- ControlValueAccessor Methods ---------- //
        /** The method to be called in order to update ngModel */
        this.onChange = (value) => { };
        /** onTouch function registered via registerOnTouch (ControlValueAccessor) */
        this.onTouched = () => { };
        this.fieldId = this.id || `cds-checkbox-tree-${nextUniqueId$1++}`;
        setTimeout(() => this.checkValue());
    }
    ngOnInit() {
        // Listen to changes from checkboxes
        this._checkboxTreeService.checkboxChange$
            .pipe(takeUntil$1(this.unsub$))
            .subscribe(() => {
            const updatedValue = [];
            const checkboxArray = this._checkboxes.toArray();
            for (let i = 0, cbal = checkboxArray.length; i < cbal; i++) {
                const checkbox = checkboxArray[i];
                if (checkbox.checked) {
                    updatedValue.push(checkbox.value);
                }
            }
            // set value
            this._value = [...updatedValue];
            // update checkTree status/style
            this._updateCheckboxState();
            // emit change
            this.onChange([...this.value]);
            this.change.emit([...this.value]);
            this.onTouched();
        });
    }
    ngAfterContentInit() {
        this._updateCheckboxState();
        this._updateDisabled();
        this._updateReadonly();
        // update child checkboxes to have role of listitem. Need to wait until after init to prevent errors.
        setTimeout(() => {
            this._updateCheckboxRoles();
            this._updateAriaControls();
        }, 0);
        // when changes happen to list of checkboxes, will need to re-establish roles
        this._checkboxes.changes
            .pipe(takeUntil$1(this.unsub$))
            .subscribe(change => {
            this._updateCheckboxRoles();
            this._updateAriaControls();
        });
    }
    ngOnChanges(changes) {
        for (const propName in changes) {
            if (propName === 'readonly' || propName === 'ariaDescribedby') {
                this._updateAriaDescribedby();
            }
        }
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region ---------- Methods ---------- //
    /**
     * Updates main checkbox status/styles
     */
    _updateCheckboxState() {
        if (this._checkboxes) {
            const checkboxArray = this._checkboxes.toArray();
            for (let i = 0, cbal = this.value.length; i < cbal; i++) {
                // Value should not be empty
                if (this.value[i] === '' || this.value[i] === undefined) {
                    console.error(`CheckboxTreeNode value of index ` + i + ` is empty or undefined`);
                }
                for (let j = i + 1, cbal1 = this.value.length; j < cbal1; j++) {
                    if (this.value[i] === this.value[j]) {
                        console.error(`CheckboxTreeNode values are duplicated`);
                    }
                }
            }
            // Determines if is in intermediate state
            this._isIntermediate =
                this._value.length > 0
                    && this._value.length !== checkboxArray.length;
            this._updateAriaDescribedby();
            // Determines if is in checked state
            this.treeChecked = this._value.length === checkboxArray.length;
        }
    }
    _updateAriaDescribedby() {
        const ariaDescIdList = [];
        if (this.ariaDescribedby)
            ariaDescIdList.push(this.ariaDescribedby);
        this._inputAriaDescribedby = ariaDescIdList.length > 0 ? ariaDescIdList.join(' ') : null;
    }
    clear() {
        this._deselectCurrent();
    }
    _deselectCurrent() {
        if (this.checkedCdsCheckbox)
            this.checkedCdsCheckbox.checked = false;
    }
    // updates checkbox children to have a role of listitem
    // this needs to be done here so that checkboxes can be used without a checkboxtree
    // but when used with a checkboxtree they will hava role of "listitem" (ADA requirement)
    _updateCheckboxRoles() {
        if (this._checkboxes) {
            this._checkboxes.forEach(checkbox => {
                checkbox.role = 'listitem';
            });
        }
    }
    _updateAriaControls() {
        const controlsIdList = [];
        if (this._checkboxes) {
            this._checkboxes.forEach(checkbox => {
                controlsIdList.push(checkbox.idStr);
            });
        }
        this._ariaControls = controlsIdList?.length > 0 ? controlsIdList.join(' ') : null;
    }
    // Updates disabled property to all checkboxes.
    _updateDisabled() {
        if (this._checkboxes) {
            this._checkboxes.forEach(checkbox => {
                checkbox.disabled = this._disabled;
            });
        }
    }
    // Updates readonly property to all checkbox tree nodes
    _updateReadonly() {
        this._checkboxes?.forEach(checkbox => {
            checkbox.readonly = this.readonly;
        });
    }
    _focusoutCheckboxTree(e) {
        if (e.relatedTarget instanceof Node) {
            const isInCheckboxTree = this._el.nativeElement.contains(e.relatedTarget);
            if (!isInCheckboxTree) {
                this.focusChange.next(false);
                this.blur.next(e);
            }
        }
    }
    /** Callback for the cases where the focused state of the input changes. */
    _focusinCheckboxTree(e) {
        if (this.disabled)
            return;
        if (e.relatedTarget instanceof Node) {
            const isInCheckboxTree = this._el.nativeElement.contains(e.relatedTarget);
            // Only emit focus if the focus is coming from outside the checkbox tree
            if (!isInCheckboxTree) {
                this.focusChange.next(true);
            }
        }
    }
    checkValue() {
        this.hasValueChange.next(!!this.input.nativeElement.value);
    }
    _checkboxTreeClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.disabled || this.readonly) {
            return;
        }
        const checkboxValueList = this._checkboxes.map(checkbox => checkbox.value);
        console.log('_checkboxTreeClick', this.treeChecked, checkboxValueList);
        this.treeChecked = !this.treeChecked;
        this.value = this.treeChecked ? [...checkboxValueList] : [];
        this.onChange([...this.value]);
        this.change.emit([...this.value]);
        this.onTouched();
    }
    /**
     * Sets the model value. Implemented as part of ControlValueAccessor.
     */
    writeValue(val) {
        this.value = val;
    }
    /**
     * Registers a callback to be triggered when the model value changes.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnChange(fn) {
        this.onChange = fn;
    }
    /**
     * Registers a callback to be triggered when the control is touched.
     * Implemented as part of ControlValueAccessor.
     * @param fn Callback to be registered.
     */
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    /**
     * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
     * @param isDisabled Whether the control should be disabled.
     */
    setDisabledState(isDisabled) {
        this.disabled = isDisabled;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeComponent, deps: [{ token: i0.ElementRef }, { token: CdsCheckboxTreeService }, { token: 'id', attribute: true }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCheckboxTreeComponent, selector: "cds-checkbox-tree", inputs: { ariaDescribedby: "ariaDescribedby", ariaLabelledby: "ariaLabelledby", ariaRequired: "ariaRequired", disabled: "disabled", idStr: "idStr", label: "label", readonly: "readonly", treeChecked: "treeChecked" }, outputs: { blur: "blur", change: "change", focus: "focus", onShiftTab: "onShiftTab", onTab: "onTab" }, host: { listeners: { "focusin": "_focusinCheckboxTree($event)", "focusout": "_focusoutCheckboxTree($event)", "input": "checkValue()" }, properties: { "attr.id": "this.fieldId" }, classAttribute: "cds-checkbox-tree" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef((() => CdsCheckboxTreeComponent)),
                multi: true
            },
            { provide: CdsFormBase, useExisting: CdsCheckboxTreeComponent },
            CdsCheckboxTreeService
        ], queries: [{ propertyName: "_checkboxes", predicate: CdsCheckboxTreeNodeComponent }], usesOnChanges: true, ngImport: i0, template: `
    <div class="checkbox-tree-wrapper">
      <div class="cbt-input-label-wrapper">
        <div
          class="cbt-input"
          role="checkbox"
          [class.cbt-checked]="treeChecked"
          [class.cbt-intermediate]="_isIntermediate"
          [class.cbt-readonly]="readonly"
          [class.cbt-error]="isInErrorState"
          [class.cbt-disabled]="disabled"
          [class.cbt-enabled]="!disabled"
          [attr.id]="idStr"
          [attr.tabindex]="!disabled ? 0 : null"
          [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby : idStr + '-label'"
          [attr.aria-describedby]="_inputAriaDescribedby"
          [attr.aria-checked]="_isIntermediate ? 'mixed' : treeChecked ? 'true' : 'false'"
          [attr.aria-controls]="_ariaControls"
          [attr.aria-disabled]="disabled"
          [attr.aria-readonly]="readonly"
          (keydown.Tab)="onTab.emit($event)"
          (keydown.shift.Tab)="onShiftTab.emit($event)"
          (focus)="focus.emit($event)"
          (keydown.space)="$event.preventDefault()"
          (keyup.space)="_checkboxTreeClick($event)"
          (click)="_checkboxTreeClick($event)"
        ></div>
        <div
          class="cbt-label"
          [attr.id]="idStr + '-label'"
          aria-hidden="true"
          [innerHTML]="label"
          (click)="_checkboxTreeClick($event)"
        >
        </div>
      </div>
      <div
        class="cds-checkbox-tree-content-wrapper"
        [class.error-line]="isInErrorState"
        role="list"
      >
        <ng-content></ng-content>
      </div>
    </div>
  `, isInline: true, styles: [".cbt-input-label-wrapper{display:flex;gap:var(--cds-checkbox-tree-label-gap, 16px)}.cbt-input{width:var(--cds-checkbox-tree-icon-size, 24px);height:var(--cds-checkbox-tree-icon-size, 24px);border-radius:var(--cds-checkbox-tree-border-radius, 4px);background-repeat:no-repeat;background-size:var(--cds-checkbox-tree-icon-size, 24px);background-position:center;flex:0 0 auto;border:var(--cds-checkbox-tree-border-width, 2px) solid var(--cds-checkbox-tree-border-color, #056dae);background-color:var(--cds-checkbox-tree-bg-color, none);appearance:none}.cbt-input:enabled:focus-visible,.cbt-input.cbt-enabled:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-checkbox-tree-outline-color, #002a54);outline-offset:1px}.cbt-input:enabled:hover,.cbt-input.cbt-enabled:hover{border-color:var(--cds-checkbox-tree-hover-border-color, #034c81)}.cbt-input:enabled:active,.cbt-input.cbt-enabled:active{border-color:var(--cds-checkbox-tree-active-border-color, #002a54)}.cbt-input:enabled.cbt-error,.cbt-input.cbt-enabled.cbt-error{border-color:var(--cds-checkbox-tree-error-border-color, #d60000)}.cbt-input:enabled.cbt-readonly,.cbt-input.cbt-enabled.cbt-readonly{border-color:var(--cds-checkbox-tree-readonly-border-color, #666666)}.cbt-input:enabled.cbt-readonly+.cbt-label,.cbt-input.cbt-enabled.cbt-readonly+.cbt-label{color:var(--cds-checkbox-tree-readonly-color, #666666)}.cbt-input:enabled:checked,.cbt-input:enabled.cbt-checked,.cbt-input.cbt-enabled:checked,.cbt-input.cbt-enabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-inline-img, none)}.cbt-input:enabled:checked:hover,.cbt-input:enabled.cbt-checked:hover,.cbt-input.cbt-enabled:checked:hover,.cbt-input.cbt-enabled.cbt-checked:hover{background-image:var(--cds-checkbox-tree-checked-hover-inline-img, none)}.cbt-input:enabled:checked:active,.cbt-input:enabled.cbt-checked:active,.cbt-input.cbt-enabled:checked:active,.cbt-input.cbt-enabled.cbt-checked:active{background-image:var(--cds-checkbox-tree-checked-active-inline-img, none)}.cbt-input:enabled:checked.cbt-error,.cbt-input:enabled.cbt-checked.cbt-error,.cbt-input.cbt-enabled:checked.cbt-error,.cbt-input.cbt-enabled.cbt-checked.cbt-error{background-image:var(--cds-checkbox-tree-checked-error-inline-img, none)}.cbt-input:enabled:checked.cbt-readonly,.cbt-input:enabled.cbt-checked.cbt-readonly,.cbt-input.cbt-enabled:checked.cbt-readonly,.cbt-input.cbt-enabled.cbt-checked.cbt-readonly{background-image:var(--cds-checkbox-tree-checked-readonly-inline-img, none)}.cbt-input:enabled.cbt-intermediate,.cbt-input.cbt-enabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-inline-img, none)}.cbt-input:enabled.cbt-intermediate:hover,.cbt-input.cbt-enabled.cbt-intermediate:hover{background-image:var(--cds-checkbox-tree-intermediate-hover-inline-img, none)}.cbt-input:enabled.cbt-intermediate:active,.cbt-input.cbt-enabled.cbt-intermediate:active{background-image:var(--cds-checkbox-tree-intermediate-active-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-error,.cbt-input.cbt-enabled.cbt-intermediate.cbt-error{background-image:var(--cds-checkbox-tree-intermediate-error-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-readonly,.cbt-input.cbt-enabled.cbt-intermediate.cbt-readonly{background-image:var(--cds-checkbox-tree-intermediate-readonly-inline-img, none)}.cbt-input:disabled,.cbt-input.cbt-disabled{border-color:var(--cds-checkbox-tree-disabled-border-color, #bbb);background-color:var(--cds-checkbox-tree-disabled-bg-color, none)}.cbt-input:disabled:checked,.cbt-input:disabled.cbt-checked,.cbt-input.cbt-disabled:checked,.cbt-input.cbt-disabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-disabled-inline-img, none)}.cbt-input:disabled.cbt-intermediate,.cbt-input.cbt-disabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-disabled-inline-img, none)}.cbt-input:disabled+.cbt-label,.cbt-input.cbt-disabled+.cbt-label{color:var(--cds-checkbox-tree-disabled-color, #ccc)}.cbt-label{margin:0}\n", ":host{position:relative}.cds-checkbox-tree-content-wrapper{display:flex;flex-direction:column;gap:16px;margin:16px 0 0 30px;padding-left:8px;border-left:2px solid transparent}.error-line{border-color:red}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-checkbox-tree', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef((() => CdsCheckboxTreeComponent)),
                            multi: true
                        },
                        { provide: CdsFormBase, useExisting: CdsCheckboxTreeComponent },
                        CdsCheckboxTreeService
                    ], host: {
                        class: 'cds-checkbox-tree',
                        '(focusin)': '_focusinCheckboxTree($event)',
                        '(focusout)': '_focusoutCheckboxTree($event)',
                        '(input)': 'checkValue()'
                    }, template: `
    <div class="checkbox-tree-wrapper">
      <div class="cbt-input-label-wrapper">
        <div
          class="cbt-input"
          role="checkbox"
          [class.cbt-checked]="treeChecked"
          [class.cbt-intermediate]="_isIntermediate"
          [class.cbt-readonly]="readonly"
          [class.cbt-error]="isInErrorState"
          [class.cbt-disabled]="disabled"
          [class.cbt-enabled]="!disabled"
          [attr.id]="idStr"
          [attr.tabindex]="!disabled ? 0 : null"
          [attr.aria-labelledby]="ariaLabelledby ? ariaLabelledby : idStr + '-label'"
          [attr.aria-describedby]="_inputAriaDescribedby"
          [attr.aria-checked]="_isIntermediate ? 'mixed' : treeChecked ? 'true' : 'false'"
          [attr.aria-controls]="_ariaControls"
          [attr.aria-disabled]="disabled"
          [attr.aria-readonly]="readonly"
          (keydown.Tab)="onTab.emit($event)"
          (keydown.shift.Tab)="onShiftTab.emit($event)"
          (focus)="focus.emit($event)"
          (keydown.space)="$event.preventDefault()"
          (keyup.space)="_checkboxTreeClick($event)"
          (click)="_checkboxTreeClick($event)"
        ></div>
        <div
          class="cbt-label"
          [attr.id]="idStr + '-label'"
          aria-hidden="true"
          [innerHTML]="label"
          (click)="_checkboxTreeClick($event)"
        >
        </div>
      </div>
      <div
        class="cds-checkbox-tree-content-wrapper"
        [class.error-line]="isInErrorState"
        role="list"
      >
        <ng-content></ng-content>
      </div>
    </div>
  `, styles: [".cbt-input-label-wrapper{display:flex;gap:var(--cds-checkbox-tree-label-gap, 16px)}.cbt-input{width:var(--cds-checkbox-tree-icon-size, 24px);height:var(--cds-checkbox-tree-icon-size, 24px);border-radius:var(--cds-checkbox-tree-border-radius, 4px);background-repeat:no-repeat;background-size:var(--cds-checkbox-tree-icon-size, 24px);background-position:center;flex:0 0 auto;border:var(--cds-checkbox-tree-border-width, 2px) solid var(--cds-checkbox-tree-border-color, #056dae);background-color:var(--cds-checkbox-tree-bg-color, none);appearance:none}.cbt-input:enabled:focus-visible,.cbt-input.cbt-enabled:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-checkbox-tree-outline-color, #002a54);outline-offset:1px}.cbt-input:enabled:hover,.cbt-input.cbt-enabled:hover{border-color:var(--cds-checkbox-tree-hover-border-color, #034c81)}.cbt-input:enabled:active,.cbt-input.cbt-enabled:active{border-color:var(--cds-checkbox-tree-active-border-color, #002a54)}.cbt-input:enabled.cbt-error,.cbt-input.cbt-enabled.cbt-error{border-color:var(--cds-checkbox-tree-error-border-color, #d60000)}.cbt-input:enabled.cbt-readonly,.cbt-input.cbt-enabled.cbt-readonly{border-color:var(--cds-checkbox-tree-readonly-border-color, #666666)}.cbt-input:enabled.cbt-readonly+.cbt-label,.cbt-input.cbt-enabled.cbt-readonly+.cbt-label{color:var(--cds-checkbox-tree-readonly-color, #666666)}.cbt-input:enabled:checked,.cbt-input:enabled.cbt-checked,.cbt-input.cbt-enabled:checked,.cbt-input.cbt-enabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-inline-img, none)}.cbt-input:enabled:checked:hover,.cbt-input:enabled.cbt-checked:hover,.cbt-input.cbt-enabled:checked:hover,.cbt-input.cbt-enabled.cbt-checked:hover{background-image:var(--cds-checkbox-tree-checked-hover-inline-img, none)}.cbt-input:enabled:checked:active,.cbt-input:enabled.cbt-checked:active,.cbt-input.cbt-enabled:checked:active,.cbt-input.cbt-enabled.cbt-checked:active{background-image:var(--cds-checkbox-tree-checked-active-inline-img, none)}.cbt-input:enabled:checked.cbt-error,.cbt-input:enabled.cbt-checked.cbt-error,.cbt-input.cbt-enabled:checked.cbt-error,.cbt-input.cbt-enabled.cbt-checked.cbt-error{background-image:var(--cds-checkbox-tree-checked-error-inline-img, none)}.cbt-input:enabled:checked.cbt-readonly,.cbt-input:enabled.cbt-checked.cbt-readonly,.cbt-input.cbt-enabled:checked.cbt-readonly,.cbt-input.cbt-enabled.cbt-checked.cbt-readonly{background-image:var(--cds-checkbox-tree-checked-readonly-inline-img, none)}.cbt-input:enabled.cbt-intermediate,.cbt-input.cbt-enabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-inline-img, none)}.cbt-input:enabled.cbt-intermediate:hover,.cbt-input.cbt-enabled.cbt-intermediate:hover{background-image:var(--cds-checkbox-tree-intermediate-hover-inline-img, none)}.cbt-input:enabled.cbt-intermediate:active,.cbt-input.cbt-enabled.cbt-intermediate:active{background-image:var(--cds-checkbox-tree-intermediate-active-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-error,.cbt-input.cbt-enabled.cbt-intermediate.cbt-error{background-image:var(--cds-checkbox-tree-intermediate-error-inline-img, none)}.cbt-input:enabled.cbt-intermediate.cbt-readonly,.cbt-input.cbt-enabled.cbt-intermediate.cbt-readonly{background-image:var(--cds-checkbox-tree-intermediate-readonly-inline-img, none)}.cbt-input:disabled,.cbt-input.cbt-disabled{border-color:var(--cds-checkbox-tree-disabled-border-color, #bbb);background-color:var(--cds-checkbox-tree-disabled-bg-color, none)}.cbt-input:disabled:checked,.cbt-input:disabled.cbt-checked,.cbt-input.cbt-disabled:checked,.cbt-input.cbt-disabled.cbt-checked{background-image:var(--cds-checkbox-tree-checked-disabled-inline-img, none)}.cbt-input:disabled.cbt-intermediate,.cbt-input.cbt-disabled.cbt-intermediate{background-image:var(--cds-checkbox-tree-intermediate-disabled-inline-img, none)}.cbt-input:disabled+.cbt-label,.cbt-input.cbt-disabled+.cbt-label{color:var(--cds-checkbox-tree-disabled-color, #ccc)}.cbt-label{margin:0}\n", ":host{position:relative}.cds-checkbox-tree-content-wrapper{display:flex;flex-direction:column;gap:16px;margin:16px 0 0 30px;padding-left:8px;border-left:2px solid transparent}.error-line{border-color:red}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: CdsCheckboxTreeService }, { type: undefined, decorators: [{
                    type: Attribute,
                    args: ['id']
                }] }, { type: i0.ElementRef }], propDecorators: { fieldId: [{
                type: HostBinding,
                args: ['attr.id']
            }], ariaDescribedby: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], ariaRequired: [{
                type: Input
            }], disabled: [{
                type: Input
            }], idStr: [{
                type: Input
            }], label: [{
                type: Input
            }], readonly: [{
                type: Input
            }], treeChecked: [{
                type: Input
            }], blur: [{
                type: Output
            }], change: [{
                type: Output
            }], focus: [{
                type: Output
            }], onShiftTab: [{
                type: Output
            }], onTab: [{
                type: Output
            }], _checkboxes: [{
                type: ContentChildren,
                args: [CdsCheckboxTreeNodeComponent]
            }] } });

const COMPONENTS$j = [CdsCheckboxTreeNodeComponent, CdsCheckboxTreeComponent];
class CdsCheckboxTreeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeModule, declarations: [CdsCheckboxTreeNodeComponent, CdsCheckboxTreeComponent], imports: [CommonModule,
            FormsModule], exports: [CdsCheckboxTreeNodeComponent, CdsCheckboxTreeComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeModule, imports: [CommonModule,
            FormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCheckboxTreeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$j],
                    imports: [
                        CommonModule,
                        FormsModule
                    ],
                    exports: [...COMPONENTS$j]
                }]
        }] });

// Custom minlength when input has '_' placeholders
// export function customMinLength(control: AbstractControl): ValidationErrors | null {
//   if (control.value == null || control.value == '') return null
//   return control.value.replace(/[^*^\d^•]/g, '').length == 9  ? null : { 'minlength': true };
// }
// #region ========== Validation ========== //
const PHONE_MASK_VALIDATION = [
    Validators.required,
    Validators.minLength(14),
    Validators.pattern(/^\(\d{3}\)\s\d{3}-\d{4}$/)
];
const PARTIAL_PHONE_MASK_VALIDATION = [
    Validators.minLength(14),
    Validators.required,
];
const SSN_MASK_VALIDATION = [
    Validators.minLength(11),
    Validators.required,
    Validators.pattern(/^\d{3}-?\d{2}-?\d{4}$/)
];
const DATE_MASK_VALIDATION = [
    Validators.minLength(10),
    Validators.required
];
const PARTIAL_SSN_MASK_VALIDATION = [
    Validators.minLength(11),
    Validators.required,
    // customMinLength
];
const EMAIL_MASK_VALIDATION = [
    Validators.required,
    Validators.email,
];
const CVV_MASK_VALIDATION = [
    Validators.required,
    Validators.minLength(3),
    Validators.maxLength(3),
    Validators.pattern(/[0-9]/)
];
//needs to be 19 in order to account for spaces
const CC_MASK_VALIDATION = [
    Validators.minLength(19),
    Validators.required,
];
const SHORT_DATE_MASK_VALIDATION = Validators.pattern(/^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/);
const SHORT_MONTH_MASK_VALIDATION = Validators.pattern(/^\d{1,2}[\/-]\d{4}$/);
const EMAIL_MASK_DIRECTIVE_VALIDATION = [
    Validators.pattern(/^[a-zA-Z0-9+~._-]+[a-zA-Z0-9+~._-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
    Validators.required,
];
// #endregion
const MASK_HIDER = '•';
const EMAIL_MASK = '@';

class CdsPhoneMaskDirective {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the phone number
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        this.isMaskedChange = new EventEmitter();
        /**
         * Number of characters to be unmasked when masking the phone number
         */
        this.unmaskEndCount = 4;
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._ngControl.control.value;
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            // Updates _rawValue to new value
            this._rawValue = change;
            // Updates display value
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            console.log('mousedown');
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the phone number unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the phone number if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for keydown event.
     * Prevents the minus key from being entered.
     * This prevents iOS from triggering the "deleteContentBackward" when
     * trying to convert the dash to an endash, or emdash.
     * @param event Keyboard Event
     */
    handleKeyboardEvent(event) {
        if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
            event.preventDefault();
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering non-numeric characters.
     * @param event Input Event
     */
    onBeforeInput(event) {
        event.preventDefault();
        const target = event.target;
        const beforeSelection = target.value.slice(0, target.selectionStart);
        const selection = target.value.slice(target.selectionStart, target.selectionEnd);
        const beforeSelectionRaw = this._removeNonDigits(beforeSelection, true);
        const selectionRaw = this._removeNonDigits(selection, true);
        const currentRawValStr = this._rawValue?.toString() || '';
        const currentRawBefore = currentRawValStr.slice(0, beforeSelectionRaw.length);
        const currentRawAfter = currentRawValStr.slice(beforeSelectionRaw.length + selectionRaw.length);
        if (event.inputType.includes('insert')) {
            // new raw value
            const numericData = this._removeNonDigits(event.data || '');
            const possibleRawValueStr = `${currentRawBefore}${numericData}${currentRawAfter}`;
            const newRawValueStr = possibleRawValueStr.length <= 10 ? possibleRawValueStr : currentRawValStr;
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(`${currentRawBefore}${numericData}`, newRawValueStr);
            }
        }
        else if (event.inputType.includes('delete')) {
            let newRawValueStr = '';
            let newRawBefore = currentRawBefore;
            let newRawAfter = currentRawAfter;
            if (target.selectionStart < target.selectionEnd) {
                newRawValueStr = `${currentRawBefore}${currentRawAfter}`;
            }
            else if (event.inputType.toLowerCase().includes('forward')) {
                newRawAfter = newRawAfter.slice(1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            else {
                newRawBefore = newRawBefore.slice(0, newRawBefore.length - 1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(newRawBefore, newRawValueStr);
            }
        }
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Formats the number passed into the phone number format
     * @param num number characters (with possible hidden characters) to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(num, isMasked) {
        let formattedValue = '';
        if (num) {
            let numStr = num.toString();
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            numStr = this._removeNonDigits(numStr, isMasked);
            const areaCode = numStr.substring(0, 3);
            const phonePrefix = numStr.substring(3, 6);
            const lineNumber = numStr.substring(6, 10);
            // Area Code
            if (areaCode) {
                formattedValue += `(${areaCode})`;
            }
            if (phonePrefix) {
                formattedValue += ` ${phonePrefix}`;
            }
            if (lineNumber) {
                formattedValue += `-${lineNumber}`;
            }
        }
        return formattedValue;
    }
    /**
     * Masks the phone number
     * @param num number charactes that will be masked
     * @returns masked phone number string
     */
    _applyMask(num) {
        const unmaskEndCount = this.unmaskEndCount > 0 ? this.unmaskEndCount : 0;
        let maskedValue = '';
        if (num) {
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            const numStr = this._removeNonDigits(num.toString());
            const sliceIndex = 10 - unmaskEndCount > 0 ? 10 - unmaskEndCount : 0;
            const masked = numStr.slice(0, sliceIndex).replace(/./g, this._hider);
            const unmasked = numStr.slice(sliceIndex, 10);
            maskedValue = this._applyFormat(`${masked}${unmasked}`, true);
        }
        return maskedValue;
    }
    /**
     * Converts the number digits into the phone number or masked phone number
     * @param num number to convert to phone number string
     * @returns either phone number or masked phone number
     */
    _convertRawNumToDisplayValue(num) {
        return this.isMasked ? this._applyMask(num) : this._applyFormat(num);
    }
    /**
     * Sets the input to display the phone number or masked phone number string
     * @param displayVal phone number string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Removes non-digit characters (and hider characters if needed)
     * @param valStr string with possible numbers
     * @param isMasked will not remove hider character if true
     * @returns string with only digits
     */
    _removeNonDigits(valStr, isMasked) {
        const nonDigitOrHiderRegEx = isMasked ? new RegExp(`[^\\d${this._hider}]`, 'g') : /[^\d]/g;
        return valStr.replace(nonDigitOrHiderRegEx, '');
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * - sets the displayed value
     * - puts the cursor in the correct spot
     * @param newRawBefore just numbers before the modified characters
     * @param newRawValueStr new value to be displayed
     */
    _updateValueAndDisplay(newRawBefore, newRawValueStr) {
        const newBefore = this._applyFormat(newRawBefore);
        const newSelectionIndex = /\)$/.test(newBefore) ? newBefore.length - 1 : newBefore.length;
        // Setting the control value to new value
        const newRawNum = newRawValueStr.length > 0 ? newRawValueStr : null;
        // Wait a moment to update the value (helps with Android devices)
        setTimeout(() => {
            this._ngControl?.control?.setValue(newRawNum);
            // Wait for update to displayed value to set cursor position
            setTimeout(() => {
                this._elRef.nativeElement.setSelectionRange(newSelectionIndex, newSelectionIndex);
            }, 0);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPhoneMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsPhoneMaskDirective, selector: "[cdsPhoneMask]", inputs: { hider: "hider", isMasked: "isMasked", isMaskedOnBlur: "isMaskedOnBlur", unmaskEndCount: "unmaskEndCount" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "keydown": "handleKeyboardEvent($event)", "beforeinput": "onBeforeInput($event)" } }, exportAs: ["cdsPhoneMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPhoneMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsPhoneMask]',
                    exportAs: 'cdsPhoneMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], unmaskEndCount: [{
                type: Input
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }] } });

class CdsSsnMaskDirective {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the SSN number
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        this._dontUpdate = false;
        /**
         * Prevents pasting into input
         */
        this.preventPaste = false;
        /**
         * Prevents copying of input
         */
        this.preventCopy = false;
        this.isMaskedChange = new EventEmitter();
        /**
         * Number of characters to be unmasked when masking the SSN number
         */
        this.unmaskEndCount = 4;
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._ngControl.control.value;
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            // Updates _rawValue to new value
            if (!this._dontUpdate) {
                this._rawValue = change;
            }
            else {
                // fix samsung bug
                this._dontUpdate = false;
                this._ngControl.control.setValue(this._rawValue);
            }
            // Updates display value
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            console.log('mousedown');
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the SSN number unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the SSN number if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for keydown event.
     * Prevents the minus key from being entered.
     * This prevents iOS from triggering the "deleteContentBackward" when
     * trying to convert the dash to an endash, or emdash.
     * @param event Keyboard Event
     */
    handleKeyboardEvent(event) {
        if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
            event.preventDefault();
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering non-numeric characters.
     * @param event Input Event
     */
    onBeforeInput(event) {
        event.preventDefault();
        const target = event.target;
        const beforeSelection = target.value.slice(0, target.selectionStart);
        const selection = target.value.slice(target.selectionStart, target.selectionEnd);
        const beforeSelectionRaw = this._removeNonDigits(beforeSelection, true);
        const selectionRaw = this._removeNonDigits(selection, true);
        const currentRawValStr = this._rawValue?.toString() || '';
        const currentRawBefore = currentRawValStr.slice(0, beforeSelectionRaw.length);
        const currentRawAfter = currentRawValStr.slice(beforeSelectionRaw.length + selectionRaw.length);
        if (event.inputType.includes('insert')) {
            // new raw value
            const numericData = this._removeNonDigits(event.data || '');
            const possibleRawValueStr = `${currentRawBefore}${numericData}${currentRawAfter}`;
            const newRawValueStr = possibleRawValueStr.length <= 9 ? possibleRawValueStr : currentRawValStr;
            if (currentRawValStr !== newRawValueStr) {
                this._dontUpdate = false;
                this._updateValueAndDisplay(`${currentRawBefore}${numericData}`, newRawValueStr);
            }
            else {
                this._dontUpdate = true;
            }
        }
        else if (event.inputType.includes('delete')) {
            this._dontUpdate = false;
            let newRawValueStr = '';
            let newRawBefore = currentRawBefore;
            let newRawAfter = currentRawAfter;
            if (target.selectionStart < target.selectionEnd) {
                newRawValueStr = `${currentRawBefore}${currentRawAfter}`;
            }
            else if (event.inputType.toLowerCase().includes('forward')) {
                newRawAfter = newRawAfter.slice(1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            else {
                newRawBefore = newRawBefore.slice(0, newRawBefore.length - 1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(newRawBefore, newRawValueStr);
            }
        }
    }
    /**
     * Hosts listener for paste event.
     * Prevents the user from pasting in value.
     * @param event Paste Event
     */
    onPaste(event) {
        if (this.preventPaste)
            event.preventDefault();
    }
    /**
     * Hosts listener for copy event.
     * Prevents the user from copying value.
     * @param event Copy Event
     */
    onCopy(event) {
        console.log('paste', this.preventCopy);
        if (this.preventCopy)
            event.preventDefault();
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Formats the number passed into the SSN number format
     * @param num number characters (with possible hidden characters) to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(num, isMasked) {
        let formattedValue = '';
        if (num) {
            let numStr = num.toString();
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            numStr = this._removeNonDigits(numStr, isMasked);
            const areaCode = numStr.substring(0, 3);
            const phonePrefix = numStr.substring(3, 5);
            const lineNumber = numStr.substring(5, 9);
            // Area Code
            if (areaCode) {
                formattedValue += `${areaCode}`;
            }
            if (phonePrefix) {
                formattedValue += `-${phonePrefix}`;
            }
            if (lineNumber) {
                formattedValue += `-${lineNumber}`;
            }
        }
        return formattedValue;
    }
    /**
     * Masks the SSN number
     * @param num number charactes that will be masked
     * @returns masked SSN number string
     */
    _applyMask(num) {
        const unmaskEndCount = this.unmaskEndCount > 0 ? this.unmaskEndCount : 0;
        let maskedValue = '';
        if (num) {
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            const numStr = this._removeNonDigits(num.toString());
            const sliceIndex = 9 - unmaskEndCount > 0 ? 9 - unmaskEndCount : 0;
            const masked = numStr.slice(0, sliceIndex).replace(/./g, this._hider);
            const unmasked = numStr.slice(sliceIndex, 9);
            maskedValue = this._applyFormat(`${masked}${unmasked}`, true);
        }
        return maskedValue;
    }
    /**
     * Converts the number digits into the SSN number or masked SSN number
     * @param num number to convert to SSN number string
     * @returns either SSN number or masked SSN number
     */
    _convertRawNumToDisplayValue(num) {
        return this.isMasked ? this._applyMask(num) : this._applyFormat(num);
    }
    /**
     * Sets the input to display the SSN number or masked SSN number string
     * @param displayVal SSN number string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Removes non-digit characters (and hider characters if needed)
     * @param valStr string with possible numbers
     * @param isMasked will not remove hider character if true
     * @returns string with only digits
     */
    _removeNonDigits(valStr, isMasked) {
        const nonDigitOrHiderRegEx = isMasked ? new RegExp(`[^\\d${this._hider}]`, 'g') : /[^\d]/g;
        return valStr.replace(nonDigitOrHiderRegEx, '');
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * - sets the displayed value
     * - puts the cursor in the correct spot
     * @param newRawBefore just numbers before the modified characters
     * @param newRawValueStr new value to be displayed
     */
    _updateValueAndDisplay(newRawBefore, newRawValueStr) {
        const newBefore = this._applyFormat(newRawBefore);
        const newSelectionIndex = /\)$/.test(newBefore) ? newBefore.length - 1 : newBefore.length;
        // Setting the control value to new value
        const newRawNum = newRawValueStr.length > 0 ? newRawValueStr : null;
        // Wait a moment to update the value (helps with Android devices)
        setTimeout(() => {
            this._ngControl?.control?.setValue(newRawNum);
            // Wait for update to displayed value to set cursor position
            setTimeout(() => {
                this._elRef.nativeElement.setSelectionRange(newSelectionIndex, newSelectionIndex);
            }, 0);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSsnMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsSsnMaskDirective, selector: "[cdsSsnMask]", inputs: { hider: "hider", isMasked: "isMasked", preventPaste: "preventPaste", preventCopy: "preventCopy", isMaskedOnBlur: "isMaskedOnBlur", unmaskEndCount: "unmaskEndCount" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "keydown": "handleKeyboardEvent($event)", "beforeinput": "onBeforeInput($event)", "paste": "onPaste($event)", "copy": "onCopy($event)" } }, exportAs: ["cdsSsnMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsSsnMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsSsnMask]',
                    exportAs: 'cdsSsnMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], preventCopy: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], unmaskEndCount: [{
                type: Input
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], onCopy: [{
                type: HostListener,
                args: ['copy', ['$event']]
            }] } });

class CdsCCMaskDirective {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the phone number
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        this._dontUpdate = false;
        this.isMaskedChange = new EventEmitter();
        /**
         * Number of characters to be unmasked when masking the phone number
         */
        this.unmaskEndCount = 4;
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._ngControl.control.value;
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            // Updates _rawValue to new value
            if (!this._dontUpdate) {
                this._rawValue = change;
            }
            else {
                this._dontUpdate = false;
                this._ngControl.control.setValue(this._rawValue);
            }
            // Updates display value
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            console.log('mousedown');
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the phone number unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the phone number if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for keydown event.
     * Prevents the minus key from being entered.
     * This prevents iOS from triggering the "deleteContentBackward" when
     * trying to convert the dash to an endash, or emdash.
     * @param event Keyboard Event
     */
    handleKeyboardEvent(event) {
        if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
            event.preventDefault();
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering non-numeric characters.
     * @param event Input Event
     */
    onBeforeInput(event) {
        event.preventDefault();
        const target = event.target;
        const beforeSelection = target.value.slice(0, target.selectionStart);
        const selection = target.value.slice(target.selectionStart, target.selectionEnd);
        const beforeSelectionRaw = this._removeNonDigits(beforeSelection, true);
        const selectionRaw = this._removeNonDigits(selection, true);
        const currentRawValStr = this._rawValue?.toString() || '';
        const currentRawBefore = currentRawValStr.slice(0, beforeSelectionRaw.length);
        const currentRawAfter = currentRawValStr.slice(beforeSelectionRaw.length + selectionRaw.length);
        if (event.inputType.includes('insert')) {
            // new raw value
            const numericData = this._removeNonDigits(event.data || '');
            const possibleRawValueStr = `${currentRawBefore}${numericData}${currentRawAfter}`;
            const newRawValueStr = possibleRawValueStr.length <= 16 ? possibleRawValueStr : currentRawValStr;
            if (currentRawValStr !== newRawValueStr) {
                this._dontUpdate = false;
                this._updateValueAndDisplay(`${currentRawBefore}${numericData}`, newRawValueStr);
            }
            else {
                this._dontUpdate = true;
            }
        }
        else if (event.inputType.includes('delete')) {
            this._dontUpdate = false;
            let newRawValueStr = '';
            let newRawBefore = currentRawBefore;
            let newRawAfter = currentRawAfter;
            if (target.selectionStart < target.selectionEnd) {
                newRawValueStr = `${currentRawBefore}${currentRawAfter}`;
            }
            else if (event.inputType.toLowerCase().includes('forward')) {
                newRawAfter = newRawAfter.slice(1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            else {
                newRawBefore = newRawBefore.slice(0, newRawBefore.length - 1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(newRawBefore, newRawValueStr);
            }
        }
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Formats the number passed into the phone number format
     * @param num number characters (with possible hidden characters) to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(num, isMasked) {
        let formattedValue = '';
        if (num) {
            let numStr = num.toString();
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            numStr = this._removeNonDigits(numStr, isMasked);
            const firstFour = numStr.substring(0, 4);
            const secondFour = numStr.substring(4, 8);
            const thirdFour = numStr.substring(8, 12);
            const lastFour = numStr.substring(12, 16);
            // Area Code
            if (firstFour) {
                formattedValue += `${firstFour}`;
            }
            if (secondFour) {
                formattedValue += ` ${secondFour}`;
            }
            if (thirdFour) {
                formattedValue += ` ${thirdFour}`;
            }
            if (lastFour) {
                formattedValue += ` ${lastFour}`;
            }
        }
        return formattedValue;
    }
    /**
     * Masks the phone number
     * @param num number charactes that will be masked
     * @returns masked phone number string
     */
    _applyMask(num) {
        const unmaskEndCount = this.unmaskEndCount > 0 ? this.unmaskEndCount : 0;
        let maskedValue = '';
        if (num) {
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            const numStr = this._removeNonDigits(num.toString());
            const sliceIndex = 16 - unmaskEndCount > 0 ? 16 - unmaskEndCount : 0;
            const masked = numStr.slice(0, sliceIndex).replace(/./g, this._hider);
            const unmasked = numStr.slice(sliceIndex, 16);
            maskedValue = this._applyFormat(`${masked}${unmasked}`, true);
        }
        return maskedValue;
    }
    /**
     * Converts the number digits into the phone number or masked phone number
     * @param num number to convert to phone number string
     * @returns either phone number or masked phone number
     */
    _convertRawNumToDisplayValue(num) {
        return this.isMasked ? this._applyMask(num) : this._applyFormat(num);
    }
    /**
     * Sets the input to display the phone number or masked phone number string
     * @param displayVal phone number string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Removes non-digit characters (and hider characters if needed)
     * @param valStr string with possible numbers
     * @param isMasked will not remove hider character if true
     * @returns string with only digits
     */
    _removeNonDigits(valStr, isMasked) {
        const nonDigitOrHiderRegEx = isMasked ? new RegExp(`[^\\d${this._hider}]`, 'g') : /[^\d]/g;
        return valStr.replace(nonDigitOrHiderRegEx, '');
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * - sets the displayed value
     * - puts the cursor in the correct spot
     * @param newRawBefore just numbers before the modified characters
     * @param newRawValueStr new value to be displayed
     */
    _updateValueAndDisplay(newRawBefore, newRawValueStr) {
        const newBefore = this._applyFormat(newRawBefore);
        const newSelectionIndex = /\)$/.test(newBefore) ? newBefore.length - 1 : newBefore.length;
        // Setting the control value to new value
        const newRawNum = newRawValueStr.length > 0 ? newRawValueStr : null;
        // Wait a moment to update the value (helps with Android devices)
        setTimeout(() => {
            this._ngControl?.control?.setValue(newRawNum);
            // Wait for update to displayed value to set cursor position
            setTimeout(() => {
                this._elRef.nativeElement.setSelectionRange(newSelectionIndex, newSelectionIndex);
            }, 0);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCCMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsCCMaskDirective, selector: "[cdsCCMask]", inputs: { hider: "hider", isMasked: "isMasked", isMaskedOnBlur: "isMaskedOnBlur", unmaskEndCount: "unmaskEndCount" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "keydown": "handleKeyboardEvent($event)", "beforeinput": "onBeforeInput($event)" } }, exportAs: ["cdsCCMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCCMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCCMask]',
                    exportAs: 'cdsCCMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], unmaskEndCount: [{
                type: Input
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }] } });

class CdsEmailMaskDirective {
    // #region ========== Input / Output ========== //
    set reveal(reveal) {
        this.changeReveal(reveal);
    }
    changeReveal(reveal) {
        if (reveal === this._reveal)
            return;
        this._reveal = coerceBooleanProperty(reveal);
        if (!this._emailControl.errors) {
            const email = this.elem.nativeElement.value;
            const maskEmail = this.maskEmail(email);
            if (this.getEmail(email)) {
                this._renderer2.setProperty(this.elem.nativeElement, 'value', maskEmail);
            }
        }
    }
    get reveal() {
        return this._reveal;
    }
    get inputEl() {
        return this.elem.nativeElement;
    }
    constructor(elem, _renderer2, _emailControl) {
        this.elem = elem;
        this._renderer2 = _renderer2;
        this._emailControl = _emailControl;
        this._hider = MASK_HIDER;
        this._emailAt = EMAIL_MASK;
        this.preventPaste = false;
        this._reveal = true;
        // #endregion
        this.hostType = 'email';
    }
    getEmail(email) {
        return !email.endsWith(this._emailAt) && email.includes(this._emailAt);
    }
    onFocus() {
        this.inputEl.value = this.stored_string;
        this._emailControl.control.setValue(this.stored_string, { emitEvent: false });
    }
    onInput() {
        const email = this.elem.nativeElement.value;
        const maskEmail = this.maskEmail(email);
        if (this.getEmail(email)) {
            this._renderer2.setProperty(this.elem.nativeElement, 'value', maskEmail);
        }
    }
    onPaste() {
        if (this.preventPaste)
            event.preventDefault();
    }
    onModelChange(value) {
        if (!value) {
            this.stored_string = '';
        }
        else {
            this.stored_string = this.inputEl.value;
        }
    }
    maskEmail(email) {
        if (!email || typeof email !== 'string')
            return email;
        if (!this.reveal) {
            const [username, domain] = email.split(this._emailAt);
            let maskUsername;
            if (username.length <= 2)
                maskUsername = username;
            else
                maskUsername = username.charAt(0) + this._hider.repeat(username.length - 2) + username.charAt(username.length - 1);
            return maskUsername + this._emailAt + domain;
        }
        else
            return this.stored_string;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsEmailMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsEmailMaskDirective, selector: "[cdsEmailMask]", inputs: { reveal: "reveal", preventPaste: "preventPaste" }, host: { listeners: { "focus": "onFocus()", "blur": "onInput()", "paste": "onPaste($event)", "ngModelChange": "onModelChange($event)" }, properties: { "type": "this.hostType" } }, exportAs: ["cdsEmailMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsEmailMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsEmailMask]',
                    exportAs: 'cdsEmailMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { reveal: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], hostType: [{
                type: HostBinding,
                args: ['type']
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }], onInput: [{
                type: HostListener,
                args: ['blur']
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], onModelChange: [{
                type: HostListener,
                args: ['ngModelChange', ['$event']]
            }] } });

/**
 * @description base class with <input> helpers
 */
class MasksBase {
    constructor(_renderer) {
        this._renderer = _renderer;
    }
    /**
     * @description <input> cursor start index
     */
    get start() {
        return this.inputEl.selectionStart;
    }
    /**
     * @description <input> cursor end index
     */
    get end() {
        return this.inputEl.selectionEnd;
    }
    /**
     * @description whether or not a cursor range is selected
     */
    get isRange() {
        return this.start !== this.end;
    }
    /**
     * add partial mask
     * @param value pass in the value to be masked
     * @returns masked phone number
     */
    AddPartialMask(value, hider) {
        if (typeof value == typeof undefined)
            return value;
        let parsedValue = value
            .toString()
            .replace(/[0-9]/g, hider); // replace only number with '•'
        return parsedValue;
    }
    transform(value, showMask, visible, hider, phoneControl) {
        if (!showMask || value?.toString().length < 11)
            return value;
        const maskedSection = value.slice(0, -visible);
        const unmaskedSection = value.slice(-visible);
        const maskedValue = this.AddPartialMask(maskedSection, hider);
        if (maskedValue.length > 8)
            phoneControl.setValue(maskedValue.replace(/[0-9]/g, hider) + unmaskedSection, { emitEvent: false });
        else
            phoneControl.setValue(maskedValue.replace(/[0-9]/g, hider) + unmaskedSection, { emitEvent: false });
    }
    // Set Input maxLength
    maxlength(value) {
        this._renderer.setAttribute(this.inputEl, 'maxlength', value);
    }
    // Set Input minlength
    minlength(value) {
        this._renderer.setAttribute(this.inputEl, 'minlength', value);
    }
}

class CdsDateMaskDirective extends MasksBase {
    get_stored_value() {
        return this.stored_string;
    }
    get rawValue() {
        return this.inputEl.value;
    }
    set rawValue(str) {
        this.inputEl.value = str;
    }
    set reveal(reveal) {
        this.changeReveal(reveal);
    }
    changeReveal(reveal) {
        if (reveal === this._reveal)
            return;
        this._reveal = coerceBooleanProperty(reveal);
        let regex = this.getRegex();
        // only format input if it matches format pattern and the form control doesn't have errors
        if (this.stored_string && regex.test(this.stored_string) && !this._dateControl.errors) {
            let value = convertedDateString(this.stored_string, this.format);
            let parsed_string = Date.parse(value);
            let date_string = new Date(parsed_string);
            this.setTextFromDate(date_string);
        }
    }
    get reveal() {
        return this._reveal;
    }
    set hider(hider) {
        if (!['•', '*'].includes(hider))
            return;
        this._hider = hider;
    }
    get inputEl() { return this.elem.nativeElement; }
    // #endregion
    constructor(elem, renderer, cdsShortDateMask, cdsShortMonthMask, _dateControl) {
        super(renderer);
        this.elem = elem;
        this.renderer = renderer;
        this.cdsShortDateMask = cdsShortDateMask;
        this.cdsShortMonthMask = cdsShortMonthMask;
        this._dateControl = _dateControl;
        this._hider = MASK_HIDER;
        this.regex_map = new Map([
            ["M/d/yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["d/M/yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["M-d-yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["d-M-yyyy", /^\d{1,2}[\/-]\d{1,2}[\/-]\d{4}$/],
            ["M-yyyy", /^\d{1,2}[\/-]\d{4}$/],
            ["M/yyyy", /^\d{1,2}[\/-]\d{4}$/]
        ]);
        this.valueChange = new EventEmitter();
        this._reveal = true;
        this.locale = 'en';
        this.after = null;
        /**
         * @description formats the date based on angular provided format value for date object. Default format is 'mediumDate'. Please visit https://angular.io/api/common/DatePipe to select right format.
         */
        this.format = 'M/d/yyyy';
        /**
         * @description hiding elements
         */
        this.maskSelection = 'mdy';
        this.mask = false;
    }
    // #region ========== Life Cycle Hook ========== //
    ngOnInit() {
        if (this.rawValue) {
            this.stored_string = this.rawValue;
            this._dateControl.control.setValue(this.stored_string, { emitEvent: false });
        }
    }
    ngOnDestroy() { }
    // #endregion
    onBlur() { this.validate(); }
    onFocus() {
        this.inputEl.value = this.stored_string;
        this._dateControl.control.setValue(this.stored_string, { emitEvent: false });
    }
    onModelChange(value) { this.stored_string = value ? this.inputEl.value : ''; }
    getRegex() { return this.regex_map.get(this.format); }
    validate() {
        if (!this._dateControl.errors) {
            let value = convertedDateString(this.rawValue, this.format);
            this.setTextFromDate(new Date(Date.parse(value)));
        }
    }
    setTextFromDate(date) {
        if (!date)
            return;
        let dateStr = new DatePipe(this.locale).transform(date, "M/d/yyyy");
        // date formats have 8 chars and month formats have 6 chars
        if (this.format.length === 8)
            this.rawValue = this.cdsShortDateMask.transform(dateStr, this._hider, this.maskSelection, this.format, this.reveal);
        if (this.format.length === 6)
            this.rawValue = this.cdsShortMonthMask.transform(dateStr, this._hider, this.maskSelection, this.format, this.reveal);
        if (this.locale != 'en')
            this.rawValue = this.reveal ? dateStr : this.hider.repeat(dateStr.length);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: CdsShortDateMaskPipe }, { token: CdsShortMonthMaskPipe }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsDateMaskDirective, selector: "[cdsDateMask]", inputs: { reveal: "reveal", locale: "locale", after: "after", format: "format", maskSelection: "maskSelection", mask: "mask", hider: "hider" }, outputs: { valueChange: "valueChange" }, host: { listeners: { "blur": "onBlur()", "focus": "onFocus()", "ngModelChange": "onModelChange($event)" } }, exportAs: ["cdsDateMask"], usesInheritance: true, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDateMask]',
                    exportAs: 'cdsDateMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: CdsShortDateMaskPipe }, { type: CdsShortMonthMaskPipe }, { type: i1$3.NgControl }], propDecorators: { valueChange: [{
                type: Output
            }], reveal: [{
                type: Input
            }], locale: [{
                type: Input
            }], after: [{
                type: Input
            }], format: [{
                type: Input
            }], maskSelection: [{
                type: Input
            }], mask: [{
                type: Input
            }], hider: [{
                type: Input
            }], onBlur: [{
                type: HostListener,
                args: ['blur']
            }], onFocus: [{
                type: HostListener,
                args: ['focus']
            }], onModelChange: [{
                type: HostListener,
                args: ['ngModelChange', ['$event']]
            }] } });

class CdsDateMask2Directive {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the Date number
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        this._dontUpdate = false;
        /**
         * Prevents pasting into input
         */
        this.preventPaste = false;
        /**
         * Prevents copying of input
         */
        this.preventCopy = false;
        this.format = 'MM/dd/yyyy';
        /**
         * @description hiding elements
         */
        this.maskSelection = 'mdy';
        this.mask = false;
        this.isMaskedChange = new EventEmitter();
        /**
         * Number of characters to be unmasked when masking the Date number
         */
        this.unmaskEndCount = 4;
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._removeNonDigits(this._ngControl.control.value);
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            // Updates _rawValue to new value
            if (!this._dontUpdate) {
                this._rawValue = change;
            }
            else {
                // fix samsung bug
                this._dontUpdate = false;
                const _formattedRawValue = this._applyFormat(this._rawValue);
                this._ngControl.control.setValue(_formattedRawValue);
            }
            // Updates display value
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the Date number unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the Date number if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for keydown event.
     * Prevents the minus key from being entered.
     * This prevents iOS from triggering the "deleteContentBackward" when
     * trying to convert the dash to an endash, or emdash.
     * @param event Keyboard Event
     */
    handleKeyboardEvent(event) {
        if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
            event.preventDefault();
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering non-numeric characters.
     * @param event Input Event
     */
    onBeforeInput(event) {
        event.preventDefault();
        const target = event.target;
        const beforeSelection = target.value.slice(0, target.selectionStart);
        const selection = target.value.slice(target.selectionStart, target.selectionEnd);
        const beforeSelectionRaw = this._removeNonDigits(beforeSelection, true);
        const selectionRaw = this._removeNonDigits(selection, true);
        const currentRawValStr = this._removeNonDigits(this._rawValue?.toString()) || '';
        const currentRawBefore = currentRawValStr.slice(0, beforeSelectionRaw.length);
        const currentRawAfter = currentRawValStr.slice(beforeSelectionRaw.length + selectionRaw.length);
        const splitFormat = this.format.split(this._getDelimiter(this.format));
        const twoSectionDate = splitFormat.length === 2;
        const shortYear = splitFormat[splitFormat.length - 1].length === 2;
        if (event.inputType.includes('insert')) {
            // new raw value
            const numericData = this._removeNonDigits(event.data || '');
            const possibleRawValueStr = `${currentRawBefore}${numericData}${currentRawAfter}`;
            let newRawValueStr;
            if ((twoSectionDate && !shortYear && possibleRawValueStr.length <= 6) || (!twoSectionDate && !shortYear && possibleRawValueStr.length <= 8) || shortYear && possibleRawValueStr.length <= 4) {
                newRawValueStr = possibleRawValueStr;
            }
            else {
                newRawValueStr = currentRawValStr;
            }
            //  const newRawValueStr = twoSectionDate ? possibleRawValueStr.length <= 6 ? possibleRawValueStr : currentRawValStr : possibleRawValueStr.length <= 8 ? possibleRawValueStr : currentRawValStr;
            if (currentRawValStr !== newRawValueStr) {
                this._dontUpdate = false;
                this._updateValueAndDisplay(`${currentRawBefore}${numericData}`, newRawValueStr);
            }
            else {
                this._dontUpdate = true;
            }
        }
        else if (event.inputType.includes('delete')) {
            this._dontUpdate = false;
            let newRawValueStr = '';
            let newRawBefore = currentRawBefore;
            let newRawAfter = currentRawAfter;
            if (target.selectionStart < target.selectionEnd) {
                newRawValueStr = `${currentRawBefore}${currentRawAfter}`;
            }
            else if (event.inputType.toLowerCase().includes('forward')) {
                newRawAfter = newRawAfter.slice(1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            else {
                newRawBefore = newRawBefore.slice(0, newRawBefore.length - 1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(newRawBefore, newRawValueStr);
            }
        }
    }
    /**
     * Hosts listener for paste event.
     * Prevents the user from pasting in value.
     * @param event Paste Event
     */
    onPaste(event) {
        if (this.preventPaste)
            event.preventDefault();
    }
    /**
     * Hosts listener for copy event.
     * Prevents the user from copying value.
     * @param event Copy Event
     */
    onCopy(event) {
        if (this.preventCopy)
            event.preventDefault();
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Formats the number passed into the Date number format
     * @param num number characters (with possible hidden characters) to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(num, isMasked) {
        let formattedValue = '';
        if (num) {
            let numStr = num.toString();
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            numStr = this._removeNonDigits(numStr, isMasked);
            const splitFormat = this.format.split(this._getDelimiter(this.format));
            const twoSectionDate = splitFormat.length === 2;
            const shortYear = splitFormat[splitFormat.length - 1].length === 2;
            let phoneParts = [];
            const delimiter = this._getDelimiter(this.format);
            if (twoSectionDate) {
                const firstSection = numStr.substring(0, 2);
                const secondSection = numStr.substring(2, 6);
                phoneParts = [firstSection, secondSection];
            }
            else if (shortYear) {
                const firstSection = numStr.substring(0, 2);
                const secondSection = numStr.substring(2, 4);
                phoneParts = [firstSection, secondSection];
            }
            else {
                const firstSection = numStr.substring(0, 2);
                const secondSection = numStr.substring(2, 4);
                const thirdSection = numStr.substring(4, 8);
                // Create an array of the parts of the phone number
                phoneParts = [firstSection, secondSection, thirdSection];
            }
            // Filter out any empty parts and join the remaining parts with the delimiter
            formattedValue = phoneParts.filter(part => part).join(delimiter);
        }
        return formattedValue;
    }
    /**
     * Masks the Date number
     * @param num number charactes that will be masked
     * @returns masked Date number string
     */
    _applyMask(num) {
        let formmatedMaskedValue = '';
        if (num) {
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            const maskedValue = this._maskValue(num);
            formmatedMaskedValue = this._applyFormat(`${maskedValue}`, true);
        }
        return formmatedMaskedValue;
    }
    _maskValue(date) {
        if (date) {
            const delimiter = this._getDelimiter(this.format);
            const rawFormat = this.format.replace(new RegExp(delimiter, 'g'), '').toLowerCase();
            // Remove non-digits from the date
            const rawNum = this._removeNonDigits(date.toString());
            // Iterate over the raw format, replacing characters that are in the mask selection with the hider
            const result = Array.from(rawNum).map((char, i) => this.maskSelection.includes(rawFormat[i]) ? this._hider : char).join('');
            return this._ngControl.errors ? date : result;
        }
        return '';
    }
    _getDelimiter(format) {
        return format.includes('/') ? '/' : '-';
    }
    /**
     * Converts the number digits into the Date number or masked Date number
     * @param num number to convert to Date number string
     * @returns either Date number or masked Date number
     */
    _convertRawNumToDisplayValue(num) {
        return this.isMasked ? this._applyMask(num) : this._applyFormat(num);
    }
    /**
     * Sets the input to display the Date number or masked Date number string
     * @param displayVal Date number string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Removes non-digit characters (and hider characters if needed)
     * @param valStr string with possible numbers
     * @param isMasked will not remove hider character if true
     * @returns string with only digits
     */
    _removeNonDigits(valStr, isMasked) {
        if (valStr) {
            const nonDigitOrHiderRegEx = isMasked ? new RegExp(`[^\\d${this._hider}]`, 'g') : /[^\d]/g;
            return valStr.replace(nonDigitOrHiderRegEx, '');
        }
        return valStr;
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * - sets the displayed value
     * - puts the cursor in the correct spot
     * @param newRawBefore just numbers before the modified characters
     * @param newRawValueStr new value to be displayed
     */
    _updateValueAndDisplay(newRawBefore, newRawValueStr) {
        const newBefore = this._applyFormat(newRawBefore);
        const newSelectionIndex = /\)$/.test(newBefore) ? newBefore.length - 1 : newBefore.length;
        // Setting the control value to new value
        const newRawNum = this._applyFormat(newRawValueStr);
        // Wait a moment to update the value (helps with Android devices)
        setTimeout(() => {
            this._ngControl?.control?.setValue(newRawNum);
            // Wait for update to displayed value to set cursor position
            setTimeout(() => {
                this._elRef.nativeElement.setSelectionRange(newSelectionIndex, newSelectionIndex);
            }, 0);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMask2Directive, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsDateMask2Directive, selector: "[cdsDateMask2]", inputs: { hider: "hider", isMasked: "isMasked", preventPaste: "preventPaste", preventCopy: "preventCopy", format: "format", maskSelection: "maskSelection", mask: "mask", isMaskedOnBlur: "isMaskedOnBlur", unmaskEndCount: "unmaskEndCount" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "keydown": "handleKeyboardEvent($event)", "beforeinput": "onBeforeInput($event)", "paste": "onPaste($event)", "copy": "onCopy($event)" } }, exportAs: ["cdsDateMask2"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDateMask2Directive, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsDateMask2]',
                    exportAs: 'cdsDateMask2',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], preventCopy: [{
                type: Input
            }], format: [{
                type: Input
            }], maskSelection: [{
                type: Input
            }], mask: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], unmaskEndCount: [{
                type: Input
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], onCopy: [{
                type: HostListener,
                args: ['copy', ['$event']]
            }] } });

class CdsCvvMaskDirective {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the phone number
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        this.isMaskedChange = new EventEmitter();
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._ngControl.control.value;
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            // Updates _rawValue to new value
            this._rawValue = change;
            // Updates display value
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the phone number unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the phone number if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for keydown event.
     * Prevents the minus key from being entered.
     * This prevents iOS from triggering the "deleteContentBackward" when
     * trying to convert the dash to an endash, or emdash.
     * @param event Keyboard Event
     */
    handleKeyboardEvent(event) {
        if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
            event.preventDefault();
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering non-numeric characters.
     * @param event Input Event
     */
    onBeforeInput(event) {
        event.preventDefault();
        const target = event.target;
        const beforeSelection = target.value.slice(0, target.selectionStart);
        const selection = target.value.slice(target.selectionStart, target.selectionEnd);
        const beforeSelectionRaw = this._removeNonDigits(beforeSelection, true);
        const selectionRaw = this._removeNonDigits(selection, true);
        const currentRawValStr = this._rawValue?.toString() || '';
        const currentRawBefore = currentRawValStr.slice(0, beforeSelectionRaw.length);
        const currentRawAfter = currentRawValStr.slice(beforeSelectionRaw.length + selectionRaw.length);
        if (event.inputType.includes('insert')) {
            // new raw value
            const numericData = this._removeNonDigits(event.data || '');
            const possibleRawValueStr = `${currentRawBefore}${numericData}${currentRawAfter}`;
            const newRawValueStr = possibleRawValueStr.length <= 3 ? possibleRawValueStr : currentRawValStr;
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(`${currentRawBefore}${numericData}`, newRawValueStr);
            }
        }
        else if (event.inputType.includes('delete')) {
            let newRawValueStr = '';
            let newRawBefore = currentRawBefore;
            let newRawAfter = currentRawAfter;
            if (target.selectionStart < target.selectionEnd) {
                newRawValueStr = `${currentRawBefore}${currentRawAfter}`;
            }
            else if (event.inputType.toLowerCase().includes('forward')) {
                newRawAfter = newRawAfter.slice(1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            else {
                newRawBefore = newRawBefore.slice(0, newRawBefore.length - 1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(newRawBefore, newRawValueStr);
            }
        }
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Formats the number passed into the phone number format
     * @param num number characters (with possible hidden characters) to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(num, isMasked) {
        let formattedValue = '';
        if (num) {
            let numStr = num.toString();
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            numStr = this._removeNonDigits(numStr, isMasked);
            numStr = numStr.slice(0, 3);
            formattedValue = numStr;
        }
        return formattedValue;
    }
    /**
     * Masks the phone number
     * @param num number charactes that will be masked
     * @returns masked phone number string
     */
    _applyMask(num) {
        let maskedValue = '';
        if (num) {
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            const numStr = this._removeNonDigits(num.toString());
            const masked = numStr.replace(/./g, this._hider);
            maskedValue = this._applyFormat(`${masked}`, true);
        }
        return maskedValue;
    }
    /**
     * Converts the number digits into the phone number or masked phone number
     * @param num number to convert to phone number string
     * @returns either phone number or masked phone number
     */
    _convertRawNumToDisplayValue(num) {
        return this.isMasked ? this._applyMask(num) : this._applyFormat(num);
    }
    /**
     * Sets the input to display the phone number or masked phone number string
     * @param displayVal phone number string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Removes non-digit characters (and hider characters if needed)
     * @param valStr string with possible numbers
     * @param isMasked will not remove hider character if true
     * @returns string with only digits
     */
    _removeNonDigits(valStr, isMasked) {
        const nonDigitOrHiderRegEx = isMasked ? new RegExp(`[^\\d${this._hider}]`, 'g') : /[^\d]/g;
        return valStr.replace(nonDigitOrHiderRegEx, '');
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * - sets the displayed value
     * - puts the cursor in the correct spot
     * @param newRawBefore just numbers before the modified characters
     * @param newRawValueStr new value to be displayed
     */
    _updateValueAndDisplay(newRawBefore, newRawValueStr) {
        const newBefore = this._applyFormat(newRawBefore);
        const newSelectionIndex = /\)$/.test(newBefore) ? newBefore.length - 1 : newBefore.length;
        // Setting the control value to new value
        const newRawNum = newRawValueStr.length > 0 ? newRawValueStr : null;
        // Wait a moment to update the value (helps with Android devices)
        setTimeout(() => {
            this._ngControl?.control?.setValue(newRawNum);
            // Wait for update to displayed value to set cursor position
            setTimeout(() => {
                this._elRef.nativeElement.setSelectionRange(newSelectionIndex, newSelectionIndex);
            }, 0);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCvvMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsCvvMaskDirective, selector: "[cdsCvvMask]", inputs: { hider: "hider", isMasked: "isMasked", isMaskedOnBlur: "isMaskedOnBlur" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "keydown": "handleKeyboardEvent($event)", "beforeinput": "onBeforeInput($event)" } }, exportAs: ["cdsCvvMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCvvMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCvvMask]',
                    exportAs: 'cdsCvvMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }] } });

class CdsZipcodeMaskDirective {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the Zipcode number
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        this.isMaskedChange = new EventEmitter();
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._ngControl.control.value;
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue));
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            // Updates _rawValue to new value
            this._rawValue = change;
            // Updates display value
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the Zipcode number unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the Zipcode number if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for keydown event.
     * Prevents the minus key from being entered.
     * This prevents iOS from triggering the "deleteContentBackward" when
     * trying to convert the dash to an endash, or emdash.
     * @param event Keyboard Event
     */
    handleKeyboardEvent(event) {
        if (event.code === 'Minus' || event.code === 'NumpadSubtract') {
            event.preventDefault();
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering non-numeric characters.
     * @param event Input Event
     */
    onBeforeInput(event) {
        event.preventDefault();
        const target = event.target;
        const beforeSelection = target.value.slice(0, target.selectionStart);
        const selection = target.value.slice(target.selectionStart, target.selectionEnd);
        const beforeSelectionRaw = this._removeNonDigits(beforeSelection, true);
        const selectionRaw = this._removeNonDigits(selection, true);
        const currentRawValStr = this._rawValue?.toString() || '';
        const currentRawBefore = currentRawValStr.slice(0, beforeSelectionRaw.length);
        const currentRawAfter = currentRawValStr.slice(beforeSelectionRaw.length + selectionRaw.length);
        if (event.inputType.includes('insert')) {
            // new raw value
            const numericData = this._removeNonDigits(event.data || '');
            const possibleRawValueStr = `${currentRawBefore}${numericData}${currentRawAfter}`;
            const newRawValueStr = possibleRawValueStr.length <= 5 ? possibleRawValueStr : currentRawValStr;
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(`${currentRawBefore}${numericData}`, newRawValueStr);
            }
        }
        else if (event.inputType.includes('delete')) {
            let newRawValueStr = '';
            let newRawBefore = currentRawBefore;
            let newRawAfter = currentRawAfter;
            if (target.selectionStart < target.selectionEnd) {
                newRawValueStr = `${currentRawBefore}${currentRawAfter}`;
            }
            else if (event.inputType.toLowerCase().includes('forward')) {
                newRawAfter = newRawAfter.slice(1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            else {
                newRawBefore = newRawBefore.slice(0, newRawBefore.length - 1);
                newRawValueStr = `${newRawBefore}${newRawAfter}`;
            }
            if (currentRawValStr !== newRawValueStr) {
                this._updateValueAndDisplay(newRawBefore, newRawValueStr);
            }
        }
    }
    // #endregion
    // #region ========== Methods ========== //
    /**
     * Formats the number passed into the Zipcode number format
     * @param num number characters (with possible hidden characters) to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(num, isMasked) {
        let formattedValue = '';
        if (num) {
            let numStr = num.toString();
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            numStr = this._removeNonDigits(numStr, isMasked);
            numStr = numStr.slice(0, 5);
            formattedValue = numStr;
        }
        return formattedValue;
    }
    /**
     * Masks the Zipcode number
     * @param num number charactes that will be masked
     * @returns masked Zipcode number string
     */
    _applyMask(num) {
        let maskedValue = '';
        if (num) {
            // Remove any non-numeric characters from numStr in case a string was passed in to num
            const numStr = this._removeNonDigits(num.toString());
            const masked = numStr.replace(/./g, this._hider);
            maskedValue = this._applyFormat(`${masked}`, true);
        }
        return maskedValue;
    }
    /**
     * Converts the number digits into the Zipcode number or masked Zipcode number
     * @param num number to convert to Zipcode number string
     * @returns either Zipcode number or masked Zipcode number
     */
    _convertRawNumToDisplayValue(num) {
        return this.isMasked ? this._applyMask(num) : this._applyFormat(num);
    }
    /**
     * Sets the input to display the Zipcode number or masked Zipcode number string
     * @param displayVal Zipcode number string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Removes non-digit characters (and hider characters if needed)
     * @param valStr string with possible numbers
     * @param isMasked will not remove hider character if true
     * @returns string with only digits
     */
    _removeNonDigits(valStr, isMasked) {
        const nonDigitOrHiderRegEx = isMasked ? new RegExp(`[^\\d${this._hider}]`, 'g') : /[^\d]/g;
        return valStr.replace(nonDigitOrHiderRegEx, '');
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * - sets the displayed value
     * - puts the cursor in the correct spot
     * @param newRawBefore just numbers before the modified characters
     * @param newRawValueStr new value to be displayed
     */
    _updateValueAndDisplay(newRawBefore, newRawValueStr) {
        const newBefore = this._applyFormat(newRawBefore);
        const newSelectionIndex = /\)$/.test(newBefore) ? newBefore.length - 1 : newBefore.length;
        // Setting the control value to new value
        const newRawNum = newRawValueStr.length > 0 ? newRawValueStr : null;
        // Wait a moment to update the value (helps with Android devices)
        setTimeout(() => {
            this._ngControl?.control?.setValue(newRawNum);
            // Wait for update to displayed value to set cursor position
            setTimeout(() => {
                this._elRef.nativeElement.setSelectionRange(newSelectionIndex, newSelectionIndex);
            }, 0);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsZipcodeMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsZipcodeMaskDirective, selector: "[cdsZipcodeMask]", inputs: { hider: "hider", isMasked: "isMasked", isMaskedOnBlur: "isMaskedOnBlur" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "keydown": "handleKeyboardEvent($event)", "beforeinput": "onBeforeInput($event)" } }, exportAs: ["cdsZipcodeMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsZipcodeMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsZipcodeMask]',
                    exportAs: 'cdsZipcodeMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], handleKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }] } });

class CdsInputMaskDirective {
    // #endregion
    // #region ========== Input / Output ========== //
    /**
     * Sets the hider character
     */
    set hider(char) {
        this._hider = char ? char.slice(0, 1) : MASK_HIDER;
    }
    /**
     * Masks the value
     */
    set isMasked(isMasked) {
        this._isMasked = !!isMasked;
        if (this._rawValue) {
            this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue).toString());
        }
    }
    get isMasked() {
        return !!this._isMasked;
    }
    set isMaskedOnBlur(isMaskedOnBlur) {
        this._isMaskedOnBlur = !!isMaskedOnBlur;
        // If isMaskedOnBlur is true, should also initialize _isMasked as true
        if (this._isMaskedOnBlur) {
            this._isMasked = true;
        }
    }
    get isMaskedOnBlur() {
        return !!this._isMaskedOnBlur;
    }
    // #endregion
    // #region ========== Constructor / Life-Cycle Hooks ========== //
    constructor(_elRef, renderer, _ngControl) {
        this._elRef = _elRef;
        this.renderer = renderer;
        this._ngControl = _ngControl;
        // #region ========== Properties ========== //
        this._hider = MASK_HIDER;
        this._unsub$ = new Subject();
        /**
         * Prevents pasting into input
         */
        this.preventPaste = false;
        /**
         * Prevents copying of input
         */
        this.preventCopy = false;
        this.isMaskedChange = new EventEmitter();
        /**
         * Number of characters to be unmasked when masking the input
         */
        this.unmaskEndCount = 0;
        /**
         * String of valid regex characters to allow in the input
         * The inverse of this expression is removed
         */
        this.allowedRegex = null;
    }
    ngOnInit() {
        // Initialize _rawValue
        this._rawValue = this._ngControl.control.value;
        // Initialize display value from initial _rawValue
        this._setDisplayValue(this._convertRawNumToDisplayValue(this._rawValue).toString());
        // Listen for changes to value and update display value
        this._ngControl.control.valueChanges
            .pipe(takeUntil$1(this._unsub$))
            .subscribe(change => {
            this._rawValue = change;
            const newDisplayValue = this._convertRawNumToDisplayValue(this._rawValue);
            this.renderer.setProperty(this._elRef.nativeElement, 'value', newDisplayValue);
        });
    }
    ngOnDestroy() {
        this._unsub$.next();
        this._unsub$.complete();
    }
    // #endregion
    // #region ========== HostListeners ========== //
    /**
     * On mouse down, prevent the focus event from turning isMask off,
     * this will be reserved for mouseup
     * @param event Mouse Event
     */
    onMousedown(event) {
        if (this.isMasked) {
            this._isFocusTriggeredByMouseEvent = true;
        }
    }
    /**
     * On mouse up, turn off isMask and set the cursor to the correct position
     * @param event Mouse Event
     */
    onMouseup(event) {
        if (this.isMasked) {
            const target = event.target;
            const start = target.selectionStart;
            const end = target.selectionEnd;
            setTimeout(() => {
                this.isMasked = false;
                this.isMaskedChange.emit(this.isMasked);
                target.setSelectionRange(start, end);
            }, 0);
        }
    }
    /**
     * On focus, unmask the input unless the focus was caused by a mouse event
     * @param event Focus Event
     */
    onFocus(event) {
        if (this.isMasked && !this._isFocusTriggeredByMouseEvent) {
            const target = event.target;
            const targetLength = target.value.length;
            this.isMasked = false;
            this.isMaskedChange.emit(this.isMasked);
            setTimeout(() => {
                target.setSelectionRange(0, targetLength);
            }, 0);
        }
        // reset the flag for the next focus event
        this._isFocusTriggeredByMouseEvent = false;
    }
    /**
     * On blur, mask the input if isMaskedOnBlur is true
     * @param event Focus Event
     */
    onBlur(event) {
        if (this.isMaskedOnBlur) {
            this.isMasked = true;
            this.isMaskedChange.emit(this.isMasked);
        }
    }
    /**
     * Hosts listener for input event.
     * Prevents the user from entering more than max characters
     * As well as pasting by detecting more than 1 character entered
     * @param event Input Event
     */
    onBeforeInput(event) {
        const inputElement = event.target;
        const inputType = event.inputType;
        if (inputType === 'deleteContentBackward') {
            return;
        }
        if (inputType.includes('insert')) {
            this._lastRaw = this._storedRaw ?? '';
            this._storedRaw = this._rawValue;
            this._lastSelection = this._storeSelection;
            this._storeSelection = inputElement.selectionStart;
            const valueLength = inputElement.value.length;
            const eventDataLength = event.data?.length ?? 0;
            const isReplaceDot = inputType === 'insertReplacementText' && event.data === '.';
            if ((this.maxLen && valueLength >= this.maxLen) ||
                (this.preventPaste && eventDataLength > 1) ||
                (this.allowedRegex && event.data?.match(new RegExp(`[^${this.allowedRegex}]`, 'g'))) ||
                (event.data === '. ') ||
                (isReplaceDot)) {
                event.preventDefault();
                event.stopPropagation();
                if (event.data === '. ' || (isReplaceDot)) {
                    const isSpaceCharAllowed = this.allowedRegex ? new RegExp(`[${this.allowedRegex}]`, 'g').test(' ') : true;
                    const isMaxLenReached = this.maxLen && this._lastRaw.length >= this.maxLen;
                    const rawLastStart = this._lastRaw?.slice(0, this._lastSelection) ?? '';
                    const rawLastEnd = this._lastRaw?.slice(this._lastSelection) ?? '';
                    const spaceLen = isMaxLenReached || !isSpaceCharAllowed ? 0 : this._lastRaw.length + 1 >= this.maxLen || isReplaceDot ? 1 : 2;
                    const space = ' '.repeat(spaceLen);
                    this._rawValue = `${rawLastStart}${space}${rawLastEnd}`;
                    setTimeout(() => {
                        const updatedSelection = rawLastStart.length + spaceLen;
                        inputElement.setSelectionRange(updatedSelection, updatedSelection);
                    }, 1);
                }
            }
            this._updateValueAndDisplay();
        }
    }
    /**
     * Hosts listener for paste event.
     * Prevents the user from pasting in value.
     * @param event Paste Event
     */
    onPaste(event) {
        if (this.preventPaste)
            event.preventDefault();
    }
    /**
     * Hosts listener for copy event.
     * Prevents the user from copying value.
     * @param event Copy Event
     */
    onCopy(event) {
        if (this.preventCopy)
            event.preventDefault();
    }
    // #endregion
    /**
     * Formats the number passed into the phone number format
     * @param val string to be formatted
     * @param isMasked will not removed hider character if true
     * @returns formatted string of number
     */
    _applyFormat(val, isMasked) {
        let formattedValue = '';
        if (val) {
            // apply regex
            if (this.allowedRegex) {
                let regex = isMasked ? new RegExp(`[^${this.allowedRegex}${this._hider}]`, 'g') : new RegExp(`[^${this.allowedRegex}]`, 'g');
                val = val.replace(regex, '');
            }
            // trim to max length
            if (this.maxLen) {
                val = val.slice(0, this.maxLen);
            }
            formattedValue = val;
        }
        return formattedValue;
    }
    /**
     * Masks the input
     * @param val number charactes that will be masked
     * @returns masked input string
     */
    _applyMask(val) {
        const unmaskEndCount = this.unmaskEndCount > 0 ? this.unmaskEndCount : 0;
        let maskedValue = '';
        if (val) {
            const sliceIndex = val.length - unmaskEndCount > 0 ? val.length - unmaskEndCount : 0;
            const masked = val.slice(0, sliceIndex).replace(/./g, this._hider);
            const unmasked = val.slice(sliceIndex);
            maskedValue = `${masked}${unmasked}`;
        }
        return maskedValue;
    }
    /**
     * Applies the mask to the input if enabled
     * @param val string to optionally mask
     * @returns either original val or masked val
     */
    _convertRawNumToDisplayValue(val) {
        val = val ? val : '';
        return this.isMasked ? this._applyMask(val) : this._applyFormat(val);
    }
    /**
     * Sets the input to display the val or masked val string
     * @param displayVal input value string
     */
    _setDisplayValue(displayVal) {
        this.renderer.setProperty(this._elRef.nativeElement, 'value', displayVal);
    }
    /**
     * Updates value and display
     * - sets the ngControl value to number
     * @param newRawBefore just numbers before the modified characters
     */
    _updateValueAndDisplay() {
        setTimeout(() => {
            let newRawValue = this._convertRawNumToDisplayValue(this._rawValue);
            newRawValue = newRawValue.length > 0 ? newRawValue : null;
            this._ngControl?.control?.setValue(newRawValue);
        }, 0);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInputMaskDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsInputMaskDirective, selector: "[cdsInputMask]", inputs: { hider: "hider", isMasked: "isMasked", preventPaste: "preventPaste", preventCopy: "preventCopy", maxLen: "maxLen", isMaskedOnBlur: "isMaskedOnBlur", unmaskEndCount: "unmaskEndCount", allowedRegex: "allowedRegex" }, outputs: { isMaskedChange: "isMaskedChange" }, host: { listeners: { "mousedown": "onMousedown($event)", "mouseup": "onMouseup($event)", "focus": "onFocus($event)", "blur": "onBlur($event)", "beforeinput": "onBeforeInput($event)", "paste": "onPaste($event)", "copy": "onCopy($event)" } }, exportAs: ["cdsInputMask"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsInputMaskDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsInputMask]',
                    exportAs: 'cdsInputMask',
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i1$3.NgControl }], propDecorators: { hider: [{
                type: Input
            }], isMasked: [{
                type: Input
            }], preventPaste: [{
                type: Input
            }], preventCopy: [{
                type: Input
            }], maxLen: [{
                type: Input
            }], isMaskedOnBlur: [{
                type: Input
            }], isMaskedChange: [{
                type: Output
            }], unmaskEndCount: [{
                type: Input
            }], allowedRegex: [{
                type: Input
            }], onMousedown: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }], onMouseup: [{
                type: HostListener,
                args: ['mouseup', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }], onPaste: [{
                type: HostListener,
                args: ['paste', ['$event']]
            }], onCopy: [{
                type: HostListener,
                args: ['copy', ['$event']]
            }] } });

const COMPONENTS$i = [
    CdsPhoneMaskDirective,
    CdsSsnMaskDirective,
    CdsCCMaskDirective,
    CdsDateMaskDirective,
    CdsEmailMaskDirective,
    CdsCvvMaskDirective,
    CdsZipcodeMaskDirective,
    CdsInputMaskDirective,
    CdsDateMask2Directive
];
const MODULES$5 = [
    CommonModule,
    ReactiveFormsModule,
];
class CdsMaskModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMaskModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsMaskModule, declarations: [CdsPhoneMaskDirective,
            CdsSsnMaskDirective,
            CdsCCMaskDirective,
            CdsDateMaskDirective,
            CdsEmailMaskDirective,
            CdsCvvMaskDirective,
            CdsZipcodeMaskDirective,
            CdsInputMaskDirective,
            CdsDateMask2Directive], imports: [CommonModule,
            ReactiveFormsModule], exports: [CdsPhoneMaskDirective,
            CdsSsnMaskDirective,
            CdsCCMaskDirective,
            CdsDateMaskDirective,
            CdsEmailMaskDirective,
            CdsCvvMaskDirective,
            CdsZipcodeMaskDirective,
            CdsInputMaskDirective,
            CdsDateMask2Directive, ReactiveFormsModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMaskModule, providers: [NAVIGATOR_PROVIDER], imports: [MODULES$5, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsMaskModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$i],
                    imports: [...MODULES$5],
                    exports: [...COMPONENTS$i, ReactiveFormsModule],
                    providers: [NAVIGATOR_PROVIDER]
                }]
        }] });

class CdsCurrencyFormatDirective {
    get inputValue() {
        return this._inputEl.value;
    }
    // #endregion
    // #region ========== Lifecycle ========== //
    constructor(_el, _control) {
        this._el = _el;
        this._control = _control;
        // #endregion
        // #region ========== Input / Output ========== //
        this.precision = 2;
        this.allowNegative = false;
        this.decimalSeparator = '.';
        this.thousandsSeparator = ',';
        this._inputEl = this._el.nativeElement;
    }
    ngOnInit() {
        this._inputEl.value = this._applyFormat(this._inputEl.value);
    }
    // #endregion
    // #region ======== HostListener ======== //
    onFocus(target, targetValue) {
        setTimeout(() => {
            const isRange = target.selectionStart !== target.selectionEnd;
            const preCursorValue = targetValue.slice(0, target.selectionStart);
            const newCursorPosition = this._clearFormat(preCursorValue)?.length || 0;
            const clearValue = this._clearFormat(targetValue);
            this._inputEl.value = clearValue?.replace('.', this.decimalSeparator) || '';
            if (isRange) {
                this._inputEl.setSelectionRange(0, clearValue.length);
            }
            else {
                this._inputEl.setSelectionRange(newCursorPosition, newCursorPosition);
            }
        }, 0);
    }
    onBlur(targetValue) {
        const numericValue = getRoundedFloat(this._getNumericValue(targetValue), this.precision || 0);
        if (!isNaN(numericValue)) {
            this._control.control.setValue(numericValue);
            this._inputEl.value = this._applyFormat(this._inputEl.value);
        }
        else {
            this._control.control.setValue(null);
        }
    }
    onBeforeInput(event) {
        const t = event.target;
        if (event.inputType === 'insertText' || event.inputType === 'insertFromPaste') {
            const target = event.target;
            const currentValue = event.target.value;
            const beforeSelection = currentValue.slice(0, target.selectionStart);
            const afterSelection = currentValue.slice(target.selectionEnd);
            const valueAfterSelectionRemoved = beforeSelection + afterSelection;
            const newPossibleValue = beforeSelection + event.data + afterSelection;
            // Checks to see if they are typing the decimal separator and if it is already included
            const isDuplicateDecimalSeparator = new RegExp(`\\${this.decimalSeparator}`).test(valueAfterSelectionRemoved)
                && new RegExp(`\\${this.decimalSeparator}`).test(event.data);
            // Checks to see if non-numeric character is typed
            const isAlphaChar = new RegExp(`[^\\d${this.decimalSeparator}]`).test(event.data);
            const isPrecisionExceeded = newPossibleValue.split(this.decimalSeparator)[1]?.length >= this.precision + 1;
            // only allow numeric characters, including one decimal separator
            if (isAlphaChar || isDuplicateDecimalSeparator || isPrecisionExceeded) {
                event.preventDefault();
            }
        }
    }
    // #endregion
    // #region ========== Methods ========== //
    _applyFormat(value) {
        if (!value) {
            return '';
        }
        const testRawValue = new Number(value).toFixed(this.precision);
        const onlyNumbers = testRawValue.replace(/[^0-9]/g, '');
        let decimalPart = onlyNumbers.slice(onlyNumbers.length - this.precision);
        if (!onlyNumbers) {
            return '';
        }
        let integerPart = onlyNumbers
            .slice(0, onlyNumbers.length - this.precision)
            .replace(/^0*/g, '')
            .replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator);
        if (integerPart == '') {
            integerPart = '0';
        }
        let newRawValue = integerPart;
        if (this.precision > 0) {
            decimalPart = '0'.repeat(this.precision - decimalPart.length) + decimalPart;
            newRawValue += this.decimalSeparator + decimalPart;
        }
        const isZero = parseInt(integerPart) == 0
            && (parseInt(decimalPart) == 0 || decimalPart == '');
        const operator = value.indexOf('-') > -1 && this.allowNegative && !isZero ? '-' : '';
        return operator + newRawValue;
    }
    _clearFormat(formattedValue) {
        // Return null if nothing is passed
        if (formattedValue === null || formattedValue === '') {
            return null;
        }
        let clearValue = formattedValue;
        // Replace custom decimal separator with numeric one that JavaScript can parse float
        if (this.decimalSeparator) {
            clearValue = clearValue.replace(this.decimalSeparator, '.');
        }
        // Remove any non-numeric characters
        clearValue = clearValue.replace(/[^\d.-]/g, '');
        return clearValue;
    }
    _getNumericValue(formattedValue) {
        return parseFloat(this._clearFormat(formattedValue));
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCurrencyFormatDirective, deps: [{ token: i0.ElementRef }, { token: i1$3.NgControl }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsCurrencyFormatDirective, selector: "[cdsCurrencyFormat]", inputs: { precision: "precision", allowNegative: "allowNegative", decimalSeparator: "decimalSeparator", thousandsSeparator: "thousandsSeparator" }, host: { listeners: { "focus": "onFocus($event.target,$event.target.value)", "blur": "onBlur($event.target.value)", "beforeinput": "onBeforeInput($event)" } }, exportAs: ["cdsCurrencyFormat"], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCurrencyFormatDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCurrencyFormat]',
                    exportAs: 'cdsCurrencyFormat'
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: i1$3.NgControl }], propDecorators: { precision: [{
                type: Input
            }], allowNegative: [{
                type: Input
            }], decimalSeparator: [{
                type: Input
            }], thousandsSeparator: [{
                type: Input
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['$event.target', '$event.target.value']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur', ['$event.target.value']]
            }], onBeforeInput: [{
                type: HostListener,
                args: ['beforeinput', ['$event']]
            }] } });

const DIRECTIVES = [
    CdsCurrencyFormatDirective
];
class CdsFormatModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormatModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFormatModule, declarations: [CdsCurrencyFormatDirective], imports: [CommonModule], exports: [CdsCurrencyFormatDirective] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormatModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFormatModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...DIRECTIVES],
                    exports: [...DIRECTIVES],
                    imports: [
                        CommonModule
                    ]
                }]
        }] });

class CdsPillar {
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
        this.isSelected = false;
        /**
         * @property: onPillerClicked
         * @description emits when a pillar is clicked
         * @type: new EventEmitter<void>
         */
        this.onPillerClicked = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Pillar <citi-pillar> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    onclick() {
        this.onPillerClicked.emit();
    }
    /**
     * @property: onFocus
     * @description announces the selected pillar and it's selected state
     * @type: number
     */
    onFocus(isSelected, pillarName) {
        isSelected
            ? this.liveAnnouncer.announce(pillarName + ' selected.' + ' ' + this.getMessageToAnnounce)
            : this.liveAnnouncer.announce(pillarName);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillar, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPillar, selector: "cds-pillar", inputs: { height: "height", pillarName: "pillarName", getMessageToAnnounce: "getMessageToAnnounce", getCustomStyles: "getCustomStyles", setBackgroundColor: "setBackgroundColor" }, outputs: { onPillerClicked: "onPillerClicked" }, host: { attributes: { "role": "presentation" }, listeners: { "click": "onclick()" }, classAttribute: "cds-pillar" }, ngImport: i0, template: `
    <ng-content select=".topOfTheBar"></ng-content>
    <div
      class="bar"
      [ngStyle]="getCustomStyles"
      [style.background-color]="setBackgroundColor"
      [tabindex]="isSelected ? 0 : -1"
      role="listitem"
      [attr.name]="pillarName"
      [attr.isSelected]="isSelected"
      [class.active]="isSelected"
      [attr.aria-label]="isSelected == true ? 'selected' : 'not selected'"
      [style.height.px]="height"
      (focus)="onFocus(isSelected, pillarName)"
    >
      <ng-content></ng-content>
    </div>
    <ng-content select=".bottomOfTheBar"></ng-content>
  `, isInline: true, styles: [":host{font-family:Interstate_Light,sans-serif;color:#fff;font-size:12px;letter-spacing:0px 14px}.bar{width:100%;order:0;flex:0 1 auto;text-align:center;position:relative;border:none;color:#333;background-color:#eee;border-radius:20px 0 0}.bar.active{background-color:#014c79}.bar.active .percentageNumber,.bar.active .percentageSymbol,.bar.active .apyText,.bar.active .amountRange{color:#fff}\n"], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillar, decorators: [{
            type: Component,
            args: [{ selector: 'cds-pillar', host: { class: 'cds-pillar', role: 'presentation' }, template: `
    <ng-content select=".topOfTheBar"></ng-content>
    <div
      class="bar"
      [ngStyle]="getCustomStyles"
      [style.background-color]="setBackgroundColor"
      [tabindex]="isSelected ? 0 : -1"
      role="listitem"
      [attr.name]="pillarName"
      [attr.isSelected]="isSelected"
      [class.active]="isSelected"
      [attr.aria-label]="isSelected == true ? 'selected' : 'not selected'"
      [style.height.px]="height"
      (focus)="onFocus(isSelected, pillarName)"
    >
      <ng-content></ng-content>
    </div>
    <ng-content select=".bottomOfTheBar"></ng-content>
  `, styles: [":host{font-family:Interstate_Light,sans-serif;color:#fff;font-size:12px;letter-spacing:0px 14px}.bar{width:100%;order:0;flex:0 1 auto;text-align:center;position:relative;border:none;color:#333;background-color:#eee;border-radius:20px 0 0}.bar.active{background-color:#014c79}.bar.active .percentageNumber,.bar.active .percentageSymbol,.bar.active .apyText,.bar.active .amountRange{color:#fff}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { height: [{
                type: Input
            }], pillarName: [{
                type: Input
            }], getMessageToAnnounce: [{
                type: Input
            }], getCustomStyles: [{
                type: Input
            }], setBackgroundColor: [{
                type: Input
            }], onPillerClicked: [{
                type: Output
            }], onclick: [{
                type: HostListener,
                args: ['click']
            }] } });

class CdsPillarGroup {
    constructor() {
        this.unsub$ = new Subject();
        this._activePillar = 0;
        this.activePillarChange = new EventEmitter();
        /**
         * @property: enableClickSelected
         * @description selects the pillar when it is clicked
         * @type: boolean
         * @two-way: false
         */
        this.enableClickSelected = false;
    }
    /**
     * @property: activePillar
     * @description binds to the index of the selected pillar
     * @type: number
     * @two-way: true
     */
    set activePillar(newIndex) {
        if (this._activePillar === newIndex)
            return;
        if (this.pillars)
            this.setActivePillar(newIndex);
        this._activePillar = newIndex;
    }
    get activePillar() {
        return this._activePillar;
    }
    ngAfterContentInit() {
        setTimeout(() => {
            this.setActivePillar(this.activePillar);
            this.pillars.forEach((pillar, index) => pillar.onPillerClicked
                .pipe(takeUntil(this.unsub$), tap(() => {
                if (this.enableClickSelected)
                    this.activePillar = index;
            }))
                .subscribe());
        }, 0);
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    setActivePillar(newIndex) {
        const pillars = this.pillars.toArray();
        pillars[this.activePillar].isSelected = false;
        pillars[newIndex].isSelected = true;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillarGroup, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPillarGroup, selector: "cds-pillar-group", inputs: { activePillar: "activePillar", enableClickSelected: "enableClickSelected" }, outputs: { activePillarChange: "activePillarChange" }, host: { classAttribute: "cds-pillar-group" }, queries: [{ propertyName: "pillars", predicate: CdsPillar }], ngImport: i0, template: `
    <div class="pillarsWrapper" role="list">
      <ng-content></ng-content>
    </div>
    <ng-content select=".bottomContent"></ng-content>
  `, isInline: true, styles: [":host{display:block}.pillarsWrapper{height:auto;width:auto;display:inline-flex;flex-direction:row;flex-wrap:nowrap;justify-content:flex-start;align-content:center;align-items:flex-end}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillarGroup, decorators: [{
            type: Component,
            args: [{ selector: 'cds-pillar-group', host: { class: 'cds-pillar-group' }, template: `
    <div class="pillarsWrapper" role="list">
      <ng-content></ng-content>
    </div>
    <ng-content select=".bottomContent"></ng-content>
  `, styles: [":host{display:block}.pillarsWrapper{height:auto;width:auto;display:inline-flex;flex-direction:row;flex-wrap:nowrap;justify-content:flex-start;align-content:center;align-items:flex-end}\n"] }]
        }], propDecorators: { activePillar: [{
                type: Input
            }], activePillarChange: [{
                type: Output
            }], enableClickSelected: [{
                type: Input
            }], pillars: [{
                type: ContentChildren,
                args: [CdsPillar, { descendants: false }]
            }] } });

const COMPONENTS$h = [CdsPillarGroup, CdsPillar];
const MODULES$4 = [CommonModule];
class CdsPillarModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPillarModule, declarations: [CdsPillarGroup, CdsPillar], imports: [CommonModule], exports: [CdsPillarGroup, CdsPillar] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillarModule, imports: [MODULES$4] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPillarModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$h],
                    imports: [...MODULES$4],
                    exports: [CdsPillarGroup, CdsPillar]
                }]
        }] });

class CdsDonutChartService {
    constructor() {
        this.donutChartChange = new Subject();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class CdsDonutChartSegmentComponent {
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(donutService) {
        this.donutService = donutService;
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            if (simpleChanges.hasOwnProperty(propName)) {
                const change = simpleChanges[propName];
                if ([
                    'percent',
                    'color',
                    'secondColor',
                    'ariaLabel',
                    'ariaLabelledby',
                    'ariaDescribedby'
                ].includes(propName) &&
                    !change.isFirstChange()) {
                    this.donutService.donutChartChange.next();
                }
            }
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartSegmentComponent, deps: [{ token: CdsDonutChartService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsDonutChartSegmentComponent, selector: "cds-donut-chart-segment", inputs: { percent: "percent", color: "color", secondColor: "secondColor", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby", idStr: "idStr" }, usesOnChanges: true, ngImport: i0, template: `
    <span>template not used</span>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartSegmentComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-donut-chart-segment', template: `
    <span>template not used</span>
  `, changeDetection: ChangeDetectionStrategy.OnPush }]
        }], ctorParameters: () => [{ type: CdsDonutChartService }], propDecorators: { percent: [{
                type: Input
            }], color: [{
                type: Input
            }], secondColor: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], idStr: [{
                type: Input
            }] } });

class CdsDonutChartComponent {
    get radius() {
        const _radius = (this.diameter - this.strokeWidth) / 2;
        return _radius;
    }
    get circumference() {
        const _circumference = 2 * Math.PI * this.radius;
        return _circumference;
    }
    get gradientOffsetStart() {
        return 50 - (this.strokeWidth / this.diameter) * 100;
    }
    static { this.inputDefaults = Object.freeze({
        idStr: '',
        diameter: 100,
        strokeWidth: 12,
        separatorWidth: 2,
        rotateChart: -90,
        transitionTime: 400,
        isAnimated: true
    }); }
    // #endregion
    // #region ========== @Inputs ========== //
    /**
     * ID for donut chart. Will default to random id string if not set.
     */
    set idStr(id) {
        this._idStr = id || `DC${getRandomString()}`;
    }
    get idStr() {
        return this._idStr;
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(changeRef, donutService) {
        this.changeRef = changeRef;
        this.donutService = donutService;
        // #region ========== Properties ========== //
        this.unsub$ = new Subject();
        this.segmentList = [];
        this._idStr = `DC${getRandomString()}`;
        /**
         * Diameter of donut chart. Defaults to 100.
         */
        this.diameter = CdsDonutChartComponent.inputDefaults.diameter;
        /**
         * Width of the donut segment. Defaults to 20.
         */
        this.strokeWidth = CdsDonutChartComponent.inputDefaults.strokeWidth;
        /**
         * Width of separation between segments.
         */
        this.separatorWidth = CdsDonutChartComponent.inputDefaults.separatorWidth;
        /**
         * Rotate the chart. Defaults to -90.
         */
        this.rotateChart = CdsDonutChartComponent.inputDefaults.rotateChart;
        /**
         * Transition time for animation (in milliseconds). Defaults to 400.
         */
        this.transitionTime = CdsDonutChartComponent.inputDefaults.transitionTime;
        /**
         * Turns on/off transiton animation. Defaults to true.
         */
        this.isAnimated = CdsDonutChartComponent.inputDefaults.isAnimated;
        /**
         * Output for click
         */
        this.segmentClick = new EventEmitter();
    }
    ngAfterContentInit() {
        this._updateSegments();
        this.segments.changes.subscribe((c) => {
            this._updateSegments();
        });
        this.donutService.donutChartChange
            .pipe(takeUntil(this.unsub$), debounceTime(10))
            .subscribe(() => {
            this._updateSegments();
        });
    }
    ngOnDestroy() {
        this.unsub$.next();
        this.unsub$.complete();
    }
    // #endregion
    // #region ========== Methods ========== //
    segmentClicked(segment) {
        this.segmentClick.emit(segment);
    }
    _calculateRotation(percent) {
        return (360 / 100) * percent;
    }
    _arcLength(percent) {
        const _arcLength = this.circumference * percent * 0.01;
        return _arcLength;
    }
    _getMaskPoints() {
        return `0, 0 ${this.radius + this.strokeWidth}, 0`;
    }
    _getMaskTransform(segment) {
        return `rotate(${this._calculateRotation(segment.start)}deg) translate(50%, 50%)`;
    }
    _getColorOrGradient(color, secondColor, index) {
        return secondColor ? `url(#${this.idStr}_colorGradient_${index})` : color;
    }
    _getAriaLabels() {
        const labelIds = [];
        if (this.ariaLabel) {
            labelIds.push(`${this.idStr}_ariaLabel`);
        }
        else if (this.ariaLabelledby) {
            labelIds.push(this.ariaLabelledby);
        }
        this.segmentList.forEach((segment, i) => {
            if (segment.ariaLabel) {
                labelIds.push(`${this.idStr}_circleAriaLabel_${i}`);
            }
            else if (segment.ariaLabelledby) {
                labelIds.push(segment.ariaLabelledby);
            }
        });
        return labelIds.join(' ');
    }
    _updateSegments() {
        let previousSegmentEnd = 0;
        this.segmentList.length = 0;
        this.segments.forEach((segment, i) => {
            this.segmentList.push({
                start: previousSegmentEnd,
                percent: segment.percent,
                color: segment.color,
                secondColor: segment.secondColor,
                ariaLabel: segment.ariaLabel,
                ariaLabelledby: segment.ariaLabelledby,
                id: segment.idStr
            });
            previousSegmentEnd += segment.percent;
        });
        this.changeRef.markForCheck();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: CdsDonutChartService }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsDonutChartComponent, selector: "cds-donut-chart", inputs: { idStr: "idStr", diameter: "diameter", strokeWidth: "strokeWidth", separatorWidth: "separatorWidth", rotateChart: "rotateChart", transitionTime: "transitionTime", isAnimated: "isAnimated", ariaLabel: "ariaLabel", ariaLabelledby: "ariaLabelledby" }, outputs: { segmentClick: "segmentClick" }, providers: [CdsDonutChartService], queries: [{ propertyName: "segments", predicate: CdsDonutChartSegmentComponent }], ngImport: i0, template: `
    <svg
      [attr.id]="idStr"
      [attr.viewBox]="'0 0 ' + diameter + ' ' + diameter"
      [style.width.px]="diameter"
      [style.height.px]="diameter"
      role="img"
      [attr.aria-labelledby]="_getAriaLabels()"
    >
      <title *ngIf="ariaLabel" [attr.id]="idStr + '_ariaLabel'">
        {{ ariaLabel }}
      </title>
      <defs>
        <mask [attr.id]="idStr + '_mask'">
          <rect width="100%" height="100%" fill="white" />
          <polyline
            *ngFor="let segment of segmentList; index as i"
            [attr.points]="_getMaskPoints()"
            fill="black"
            stroke="black"
            [style.stroke-width]="separatorWidth"
            [style.transform-origin]="'center'"
            [style.stroke-linecap]="'round'"
            [style.transform]="_getMaskTransform(segment)"
            [style.transform-origin]="'50% 50%'"
          />
        </mask>
      </defs>
      <g
        class="dc-circle-group"
        [style.transform]="'rotate(' + rotateChart + 'deg)'"
      >
        <g [attr.mask]="'url(#' + idStr + '_mask)'">
          <circle
            cx="50%"
            cy="50%"
            [attr.r]="diameter / 2"
            fill="transparent"
          />
          <g
            *ngFor="let segment of segmentList; index as i"
            class="dc-segment"
            pointer-events="visibleStroke"
            (click)="segmentClicked(segment)"
          >
            <defs *ngIf="segment.secondColor">
              <radialGradient
                [attr.id]="idStr + '_colorGradient_' + i"
                r="100%"
              >
                <stop
                  [attr.offset]="gradientOffsetStart + '%'"
                  [style.stop-color]="segment.color"
                />
                <stop
                  [attr.offset]="100 - gradientOffsetStart + '%'"
                  [style.stop-color]="segment.secondColor"
                />
              </radialGradient>
            </defs>
            <style>
              #{{idStr + '_circle_' + i}} {
                stroke: {{_getColorOrGradient(segment.color, segment.secondColor, i)}};
                stroke-width: {{strokeWidth}}px;
                stroke-dasharray: {{_arcLength(segment.percent)}} {{circumference}};
                transform: rotate({{_calculateRotation(segment.start)}}deg);
              }
            </style>
            <style *ngIf="isAnimated">
              #{{idStr + '_circle_' + i}} {
                animation: {{idStr + '_circle_' + i}}-animate-arc {{transitionTime / 1000}}s ease-out forwards;
                transition: transform {{transitionTime / 1000}}s ease-out;
              }

              @keyframes {{idStr + '_circle_' + i}}-animate-arc {
                from {
                  stroke-dashoffset: {{_arcLength(segment.percent)}};
                  transform: rotate({{_calculateRotation(segment.start + (segment.percent / 2))}}deg);
                }
                to {
                  stroke-dashoffset: 0;
                  transform: rotate({{_calculateRotation(segment.start)}}deg);
                }
              }
            </style>
            <circle
              [attr.id]="idStr + '_circle_' + i"
              cx="50%"
              cy="50%"
              class="dc-circle"
              [attr.r]="radius"
            />
            <desc
              *ngIf="segment.ariaLabel"
              [attr.id]="idStr + '_circleAriaLabel_' + i"
            >
              {{ segment.ariaLabel }}
            </desc>
          </g>
        </g>
      </g>
    </svg>
  `, isInline: true, styles: [".dc-circle-group{transform-origin:50% 50%}.dc-circle{fill:transparent;stroke:#ccc;transform-origin:50% 50%}:host.cds-donut-chart-responsive svg{width:88px!important;height:88px!important}@media (min-width: 768px){:host.cds-donut-chart-responsive svg{width:104px!important;height:104px!important}}\n"], dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-donut-chart', providers: [CdsDonutChartService], template: `
    <svg
      [attr.id]="idStr"
      [attr.viewBox]="'0 0 ' + diameter + ' ' + diameter"
      [style.width.px]="diameter"
      [style.height.px]="diameter"
      role="img"
      [attr.aria-labelledby]="_getAriaLabels()"
    >
      <title *ngIf="ariaLabel" [attr.id]="idStr + '_ariaLabel'">
        {{ ariaLabel }}
      </title>
      <defs>
        <mask [attr.id]="idStr + '_mask'">
          <rect width="100%" height="100%" fill="white" />
          <polyline
            *ngFor="let segment of segmentList; index as i"
            [attr.points]="_getMaskPoints()"
            fill="black"
            stroke="black"
            [style.stroke-width]="separatorWidth"
            [style.transform-origin]="'center'"
            [style.stroke-linecap]="'round'"
            [style.transform]="_getMaskTransform(segment)"
            [style.transform-origin]="'50% 50%'"
          />
        </mask>
      </defs>
      <g
        class="dc-circle-group"
        [style.transform]="'rotate(' + rotateChart + 'deg)'"
      >
        <g [attr.mask]="'url(#' + idStr + '_mask)'">
          <circle
            cx="50%"
            cy="50%"
            [attr.r]="diameter / 2"
            fill="transparent"
          />
          <g
            *ngFor="let segment of segmentList; index as i"
            class="dc-segment"
            pointer-events="visibleStroke"
            (click)="segmentClicked(segment)"
          >
            <defs *ngIf="segment.secondColor">
              <radialGradient
                [attr.id]="idStr + '_colorGradient_' + i"
                r="100%"
              >
                <stop
                  [attr.offset]="gradientOffsetStart + '%'"
                  [style.stop-color]="segment.color"
                />
                <stop
                  [attr.offset]="100 - gradientOffsetStart + '%'"
                  [style.stop-color]="segment.secondColor"
                />
              </radialGradient>
            </defs>
            <style>
              #{{idStr + '_circle_' + i}} {
                stroke: {{_getColorOrGradient(segment.color, segment.secondColor, i)}};
                stroke-width: {{strokeWidth}}px;
                stroke-dasharray: {{_arcLength(segment.percent)}} {{circumference}};
                transform: rotate({{_calculateRotation(segment.start)}}deg);
              }
            </style>
            <style *ngIf="isAnimated">
              #{{idStr + '_circle_' + i}} {
                animation: {{idStr + '_circle_' + i}}-animate-arc {{transitionTime / 1000}}s ease-out forwards;
                transition: transform {{transitionTime / 1000}}s ease-out;
              }

              @keyframes {{idStr + '_circle_' + i}}-animate-arc {
                from {
                  stroke-dashoffset: {{_arcLength(segment.percent)}};
                  transform: rotate({{_calculateRotation(segment.start + (segment.percent / 2))}}deg);
                }
                to {
                  stroke-dashoffset: 0;
                  transform: rotate({{_calculateRotation(segment.start)}}deg);
                }
              }
            </style>
            <circle
              [attr.id]="idStr + '_circle_' + i"
              cx="50%"
              cy="50%"
              class="dc-circle"
              [attr.r]="radius"
            />
            <desc
              *ngIf="segment.ariaLabel"
              [attr.id]="idStr + '_circleAriaLabel_' + i"
            >
              {{ segment.ariaLabel }}
            </desc>
          </g>
        </g>
      </g>
    </svg>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".dc-circle-group{transform-origin:50% 50%}.dc-circle{fill:transparent;stroke:#ccc;transform-origin:50% 50%}:host.cds-donut-chart-responsive svg{width:88px!important;height:88px!important}@media (min-width: 768px){:host.cds-donut-chart-responsive svg{width:104px!important;height:104px!important}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: CdsDonutChartService }], propDecorators: { segments: [{
                type: ContentChildren,
                args: [CdsDonutChartSegmentComponent]
            }], idStr: [{
                type: Input
            }], diameter: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], separatorWidth: [{
                type: Input
            }], rotateChart: [{
                type: Input
            }], transitionTime: [{
                type: Input
            }], isAnimated: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], ariaLabelledby: [{
                type: Input
            }], segmentClick: [{
                type: Output
            }] } });

class CdsDonutChartModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartModule, declarations: [CdsDonutChartComponent, CdsDonutChartSegmentComponent], imports: [CommonModule], exports: [CdsDonutChartComponent, CdsDonutChartSegmentComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsDonutChartModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [CdsDonutChartComponent, CdsDonutChartSegmentComponent],
                    exports: [CdsDonutChartComponent, CdsDonutChartSegmentComponent]
                }]
        }] });

class CdsTextHeader {
    set textColor(colors) {
        this.warning = this.gray = this.error = this.blue = this.white = this.black = this.success = false;
        switch (colors) {
            case 'warning':
                this.warning = true;
                break;
            case 'gray':
                this.gray = true;
                break;
            case 'error':
                this.error = true;
                break;
            case 'blue':
                this.blue = true;
                break;
            case 'white':
                this.white = true;
                break;
            case 'black':
                this.black = true;
                break;
            case 'success':
                this.success = true;
                break;
        }
    }
    set textAlignment(align) {
        this.left = this.center = this.right = false;
        switch (align) {
            case 'left':
                this.left = true;
                break;
            case 'center':
                this.center = true;
                break;
            case 'right':
                this.right = true;
                break;
        }
    }
    set textSize(align) {
        this.large = this.h1 = this.h2 = this.h3 = this.body = this.bodySmall = this.subheader = this.footnote = this.body = false;
        switch (align) {
            case 'header-large':
                this.large = true;
                break;
            case 'header-1':
                this.h1 = true;
                break;
            case 'header-2':
                this.h2 = true;
                break;
            case 'header-3':
                this.h3 = true;
                break;
            case 'body':
                this.body = true;
                break;
            case 'body-small':
                this.bodySmall = true;
                break;
            case 'subheader':
                this.subheader = true;
                break;
            case 'footnote':
                this.footnote = true;
                break;
            case 'button':
                this.body = true;
                break;
        }
    }
    constructor() {
        this.elementClass = 'cds-text-header';
        this.warning = false;
        this.gray = false;
        this.error = false;
        this.blue = false;
        this.white = false;
        this.black = true;
        this.success = false;
        this.left = true;
        this.center = false;
        this.right = false;
        this.h1 = false;
        this.h2 = false;
        this.h3 = false;
        this.body = false;
        this.bodySmall = false;
        this.footnote = false;
        this.subheader = false;
        this.button = false;
        this.large = true;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextHeader, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsTextHeader, selector: "[cdsTextHeader]", inputs: { textColor: "textColor", textAlignment: "textAlignment", textSize: "textSize" }, host: { properties: { "class": "this.elementClass", "class.cds-text-header-warning": "this.warning", "class.cds-text-header-gray": "this.gray", "class.cds-text-header-error": "this.error", "class.cds-text-header-blue": "this.blue", "class.cds-text-header-white": "this.white", "class.cds-text-header-black": "this.black", "class.cds-text-header-success": "this.success", "class.cds-text-header-left": "this.left", "class.cds-text-header-center": "this.center", "class.cds-text-header-right": "this.right", "class.cds-text-header-1": "this.h1", "class.cds-text-header-2": "this.h2", "class.cds-text-header-3": "this.h3", "class.cds-text-header-body": "this.body", "class.cds-text-header-body-small": "this.bodySmall", "class.cds-text-header-footnote": "this.footnote", "class.cds-text-header-subheader": "this.subheader", "class.cds-text-header-button": "this.button", "class.cds-text-header-large": "this.large" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextHeader, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsTextHeader]'
                }]
        }], ctorParameters: () => [], propDecorators: { elementClass: [{
                type: HostBinding,
                args: ['class']
            }], warning: [{
                type: HostBinding,
                args: ['class.cds-text-header-warning']
            }], gray: [{
                type: HostBinding,
                args: ['class.cds-text-header-gray']
            }], error: [{
                type: HostBinding,
                args: ['class.cds-text-header-error']
            }], blue: [{
                type: HostBinding,
                args: ['class.cds-text-header-blue']
            }], white: [{
                type: HostBinding,
                args: ['class.cds-text-header-white']
            }], black: [{
                type: HostBinding,
                args: ['class.cds-text-header-black']
            }], success: [{
                type: HostBinding,
                args: ['class.cds-text-header-success']
            }], left: [{
                type: HostBinding,
                args: ['class.cds-text-header-left']
            }], center: [{
                type: HostBinding,
                args: ['class.cds-text-header-center']
            }], right: [{
                type: HostBinding,
                args: ['class.cds-text-header-right']
            }], h1: [{
                type: HostBinding,
                args: ['class.cds-text-header-1']
            }], h2: [{
                type: HostBinding,
                args: ['class.cds-text-header-2']
            }], h3: [{
                type: HostBinding,
                args: ['class.cds-text-header-3']
            }], body: [{
                type: HostBinding,
                args: ['class.cds-text-header-body']
            }], bodySmall: [{
                type: HostBinding,
                args: ['class.cds-text-header-body-small']
            }], footnote: [{
                type: HostBinding,
                args: ['class.cds-text-header-footnote']
            }], subheader: [{
                type: HostBinding,
                args: ['class.cds-text-header-subheader']
            }], button: [{
                type: HostBinding,
                args: ['class.cds-text-header-button']
            }], large: [{
                type: HostBinding,
                args: ['class.cds-text-header-large']
            }], textColor: [{
                type: Input
            }], textAlignment: [{
                type: Input
            }], textSize: [{
                type: Input
            }] } });

const COMPONENTS$g = [CdsTextHeader];
const MODULES$3 = [CommonModule];
class CdsTextHeaderModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextHeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTextHeaderModule, declarations: [CdsTextHeader], imports: [CommonModule], exports: [CdsTextHeader] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextHeaderModule, imports: [MODULES$3] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTextHeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$g],
                    imports: [...MODULES$3],
                    exports: [CdsTextHeader]
                }]
        }] });

class CdsCarouselSlide {
    constructor(tpl) {
        this.tpl = tpl;
        this.isActiveSlide = false;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselSlide, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    /** @nocollapse */ static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.0.3", type: CdsCarouselSlide, selector: "[cdsCarouselSlide]", inputs: { slideAnnounce: ["cdsCarouselSlide", "slideAnnounce"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselSlide, decorators: [{
            type: Directive,
            args: [{
                    selector: '[cdsCarouselSlide]'
                }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }], propDecorators: { slideAnnounce: [{
                type: Input,
                args: ['cdsCarouselSlide']
            }] } });

var Animation;
(function (Animation) {
    Animation["Fade"] = "fade";
    Animation["Slide"] = "slide";
})(Animation || (Animation = {}));
var Transition;
(function (Transition) {
    Transition[Transition["Next"] = 0] = "Next";
    Transition[Transition["Prev"] = 1] = "Prev";
})(Transition || (Transition = {}));
const TRANSITION_TIME = 1000;
class CdsCarouselComponent {
    set announceSlideChange(message) {
        if (this._announceSlideChange === message)
            return;
        this._announceSlideChange = message;
        if (this._announceAuto)
            this.announceSlide(message);
        this._announceAuto = false;
    }
    get announceSlideChange() {
        return this._announceSlideChange;
    }
    set activeSlide(activeSlide) {
        if (this._activeSlide === activeSlide)
            return;
        this._activeSlide = activeSlide;
        if (this.carouselSlides && this.carouselSlides.length) {
            // prevents setting before init
            this.deactiveCurentSlide();
            this.activeNewSlide(activeSlide);
        }
    }
    get activeSlides() {
        return this._activeSlides;
    }
    set activeSlides(activeSlides) {
        if (this._activeSlides === activeSlides)
            return;
        this._activeSlides = activeSlides;
    }
    // #endregion
    // #region // -------- Lifecycle Hooks ---------- //
    constructor(cd, ngZone, differs, announcer) {
        this.cd = cd;
        this.ngZone = ngZone;
        this.differs = differs;
        this.announcer = announcer;
        this.showPause = false;
        this.isActiveSlide = false;
        // detects if mouse in on the slide or not
        this.isMouseEnter = false;
        this.animationComplete = true;
        this.carouselSlideRefArray = [];
        this.carouselSlideContentArray = [];
        this.showLoader = true;
        this.pauseManual = false;
        this.toggleWait = true;
        /**
         * @Property: isIndicatorClicked
         * @Description: This boolean tells us which slide animation state to use.
         *  We need two slide animation state arrays for the following logic:
         *  1) Prev/next navigation
         *  2) Indicator (page dots) navigation
         * @Type: boolean
         */
        this.isIndicatorClicked = false;
        this.slideAnimationState = [];
        this.slideAnimationStateForIndicator = [];
        this._destroyUnsub$ = new Subject();
        // #endregion
        // #region // -------- Inputs/Outputs ---------- //
        /**
         * @Property: idStr
         * @Description: id for the carousel
         * @Type: string
         */
        this.idStr = 'carousel';
        /**
         * @Property: isNavigation
         * @Description: show/hide NEXT/PREV button
         * @Type: Boolean
         */
        this.isNavigation = false;
        /**
         * @Property: isControls
         * @Description: show/hide controls
         * @Type: Boolean
         */
        this.isControls = false;
        /**
         * @Property: autoPlay: boolean
         * @Description: set the auto rotation time
         * @Type: Number
         * @Default: false
         */
        this.autoPlay = true;
        /**
         * @Property: isContinuous: boolean
         * @Description: Set whether the carousel has infinite scrolling
         * @Type: Number
         * @Default: true
         */
        this.isContinuous = true;
        /**
         * @Property: disabled
         * @Description: Set the left/right arrow to be disabled
         * @Type: Boolean
         * @Default: false
        */
        this.disabled = false;
        /**
         * @Property: isPreviousDisabled
         * @Description: Set the left arrow to be disabled
         * @Type: Boolean
         * @Default: false
        */
        this.isPreviousDisabled = false;
        /**
         * @Property: isNextDisabled
         * @Description: Set the right arrow to be disabled
         * @Type: Boolean
         * @Default: false
        */
        this.isNextDisabled = false;
        /**
         * @Property: autoPlayDuration
         * @Description: set the auto rotation time
         * @Type: Number
         * @Default: 2000
         */
        this.autoPlayDuration = 5000;
        /**
         * @Property: ariaLabel
         * @Description: This defines the custom value for the aria-label attribute of the Carousel section tag
         * @Type: String
         * @Default:
         */
        this.ariaLabel = 'Carousel slide';
        /**
         * @Property: prevAriaLabel
         * @Description: This defines the custom value for the aria-label attribute of the previous slide button
         * @Type: String
         * @Default:
         */
        this.prevAriaLabel = 'Previous Slide';
        /**
         * @Property: nextAriaLabel
         * @Description: This defines the custom value for the aria-label attribute of the next slide button
         * @Type: String
         * @Default:
         */
        this.nextAriaLabel = 'Next Slide';
        /**
         * @Property: playAriaLabel
         * @Description: This defines the custom value for the aria-label attribute of the play button
         * @Type: String
         * @Default:
         */
        this.playAriaLabel = 'Auto-rotating carousel Play button. Paused. Press enter/Space to play.';
        /**
         * @Property: pauseAriaLabel
         * @Description: This defines the custom value for the aria-label attribute of the pause button
         * @Type: String
         * @Default:
         */
        this.pauseAriaLabel = 'Auto-rotating carousel button. Playing. Press enter to pause.';
        this._announceAuto = false;
        /**
         * @Property: activeSlide
         * @Description: This defines the index of the active slide that the carousel should display initially
         * @Type: Number
         * @Default: 0
         */
        this._activeSlide = 0;
        this.carouselHeight = 280;
        this.activeSlideChange = new EventEmitter();
        this.activeIndicator = new EventEmitter();
        this.carouselWidth = 1024;
        cd.detach();
        setInterval(() => {
            this.cd.detectChanges();
        }, 1000);
    }
    ngOnInit() {
        if (this.slides) {
            this.activeSlides = this.getPreviousCurrentNextIndexes(0);
            this.setSlideAnimationState(this.activeSlides);
            this.differ = this.differs.find(this.activeSlides).create();
            if (this.slides.length > 1 && this.autoPlayDuration > 0 && this.autoPlay) {
                this.startTimer();
            }
        }
        // Disable left/right navigation arrows depending on whether if isContinuous is toggled
        if (this.isContinuous === false)
            this.isPreviousDisabled = true;
        if (this.isContinuous === true)
            this.isPreviousDisabled = this.isNextDisabled = false;
    }
    ngAfterContentInit() {
        setTimeout(() => {
            this.carouselSlideContentArray = this.carouselSlides.toArray();
            this.currentSlide = this.carouselSlideContentArray[this._activeSlide];
            this.currentSlide.isActiveSlide = true;
        }, 1);
        setTimeout(() => {
            this.showLoader = false;
            // initialize carousel width
            this.carouselWidth = this.carouselRef.nativeElement.offsetWidth;
        }, 1000);
    }
    ngAfterViewInit() {
        if (this.carouselSlideRefs) {
            this.carouselSlideRefArray = this.carouselSlideRefs.toArray();
        }
        // listen for changes to slides
        this.carouselSlides.changes
            .pipe(takeUntil$1(this._destroyUnsub$))
            .subscribe(slideChange => {
            // update property carouselSlideContentArray when carouselSlides change
            this.carouselSlideContentArray = this.carouselSlides.toArray();
            // If active slide was removed, previous slide should be selected
            if (this._activeSlide >= slideChange.length) {
                setTimeout(() => {
                    this.select(slideChange.length - 1, true, false);
                }, 0);
            }
        });
    }
    ngOnDestroy() {
        this.cd.detach();
        this.stopTimer();
        this._destroyUnsub$.next();
        this._destroyUnsub$.complete();
    }
    // #endregion
    // #region // -------- Methods ---------- //
    // disablePreviousArrow and disableNextArrow disables the left/right arrows without
    // actually disabling it. These functions apply the disabled class so that they appear to
    // be disabled, so that focus isn't lost once these buttons are disabled
    disablePreviousArrow(index) {
        if (this.isContinuous)
            return false;
        if (index === 0) {
            return true;
        }
        else {
            return false;
        }
    }
    disableNextArrow(index) {
        if (this.isContinuous)
            return false;
        if (index === this.carouselSlideContentArray.length - 1) {
            return true;
        }
        else {
            return false;
        }
    }
    // disableArrowFn programatically disables the left/right buttons
    disableArrowFn(index, arrow) {
        // 2+ slides carousel
        if (this.carouselSlideContentArray.length > 2) {
            if (arrow === 'left')
                this.isPreviousDisabled = this.disablePreviousArrow(index);
            if (arrow === 'right')
                this.isNextDisabled = this.disableNextArrow(index);
            // 2 slides only carousel
        }
        else {
            if (this.disablePreviousArrow(index))
                this.isPreviousDisabled = true;
            else
                this.isPreviousDisabled = false;
            if (this.disableNextArrow(index))
                this.isNextDisabled = true;
            else
                this.isNextDisabled = false;
        }
    }
    select(index, announce, isPreviousClicked, isAutoplayOn, isIndicatorClicked) {
        let currentSlideIndex = this._activeSlides.current;
        const slideCount = this.carouselSlideContentArray.length;
        this.isIndicatorClicked = isIndicatorClicked;
        // #region ======= Autoplay Announcement Logic ======= /
        if (isAutoplayOn) {
            // Every time autoplay changes the slide, check where the current focus is
            let activeElement = document.activeElement;
            // Look for all focusable elements within the carousel
            let carouselEl = [this.prevButtonRef?.nativeElement, this.nextButtonRef?.nativeElement, this.playPauseBtnRef?.nativeElement];
            let indicatorTablistEl = []; // The focusable button is nested in a div, so we need to access its children
            let indicatorTabEl = [];
            if (this.isControls) {
                indicatorTablistEl = [...this.indicatorTablistRef?.nativeElement.children];
                // Gathers indicator tab elements
                for (let i = 0; i < indicatorTablistEl.length; i++) {
                    let child = indicatorTablistEl[i].children[0];
                    indicatorTabEl.push(child);
                }
            }
            // If a carousel element is currently focused, then NVDA should announce slide changes on autoplay
            let hasFocus = carouselEl.includes(activeElement) || indicatorTabEl?.includes(activeElement);
            if (hasFocus)
                this._announceAuto = true;
            else
                this._announceAuto = false;
        }
        // #endregion
        // #region ======= Slide Animation Logic ======= /
        // prevent user from clicking too fast
        if (!this.animationComplete)
            return;
        else
            setTimeout(() => (this.animationComplete = true), TRANSITION_TIME);
        if (announce)
            this._announceAuto = true;
        this.animationComplete = false;
        // For cases where there are only two slides, we need to determine if
        // previous or next was clicked.  Then we need to update the animation state
        // for the previous/next slide before we change the state of the current slide
        this.setSlideAnimationState(this.activeSlides, isPreviousClicked);
        // Force the Template to apply the new previous/next state before we change the
        // state of the current slide
        this.cd.detectChanges();
        // Now we change the animation state for current and previous/next slide
        // to trigger animation
        this.activeSlides = this.getPreviousCurrentNextIndexes(index);
        this.setSlideAnimationState(this.activeSlides, isPreviousClicked, true);
        this.activeSlideChange.emit(index);
        if (this.differ.diff(this.activeSlides)) {
            this.cd.detectChanges();
        }
        // #endregion
        // #region ======= Toggling Previous/Next Buttons  ======= /
        // Next/Right Button
        if (!this.isNextDisabled
            && this.currentSlide.isActiveSlide === true
            && index === slideCount - 1
            && !isAutoplayOn
            && !isIndicatorClicked
            && !this.isContinuous)
            setTimeout(() => this.announcer.announce('Next button disabled.'));
        // Previous/Left Button
        else if (!this.isPreviousDisabled
            && this.currentSlide.isActiveSlide === true
            && index === 0
            && !isAutoplayOn
            && !isIndicatorClicked
            && !this.isContinuous)
            setTimeout(() => this.announcer.announce('Previous button disabled.'));
        else
            this.isPreviousDisabled = this.isNextDisabled = false;
        // Edge Case: if we use the page dots to navigate through our non-continuous carousel,
        // and if we're on the first or last slide,
        // the next and/or previous buttons need to be programatically disabled.
        if (!this.isContinuous && isIndicatorClicked) {
            if (currentSlideIndex === 0)
                this.isNextDisabled = true;
            if (currentSlideIndex === slideCount - 1)
                this.isPreviousDisabled = true;
        }
        // Edge Case: if we have only 2 slides in our non-continuous carousel,
        // then the disabled buttons should be re-enabled upon clicking (instead of onBlur).
        if (!this.isContinuous && slideCount === 2) {
            if (index === 0)
                this.isNextDisabled = false;
            else
                this.isPreviousDisabled = false;
        }
        // #endregion
    }
    // In a non-continuous carousel, the select function works slightly different for
    // the previous and next button. When the disabled class is applied to the buttons,
    // the select method on the disabled button should not do anything.
    selectPrevious(index, announce, isPreviousClicked, isAutoplayOn) {
        const currentSlide = this.activeSlides.current;
        if (!this.isContinuous && currentSlide === 0)
            return;
        this.select(index, announce, isPreviousClicked);
    }
    selectNext(index, announce, isPreviousClicked, isAutoplayOn) {
        const currentSlide = this.activeSlides.current;
        if (!this.isContinuous && currentSlide === this.carouselSlideContentArray.length - 1)
            return;
        this.select(index, announce, isPreviousClicked);
    }
    select_indicator(index) {
        this._announceAuto = false;
        this.select(index, undefined, undefined, undefined, true);
        this.activeIndicator.emit();
    }
    handleIndicatorKeydown(event) {
        if (event.key === 'ArrowRight')
            this.select(this.activeSlides.next, true, true);
        if (event.key === 'ArrowLeft')
            this.select(this.activeSlides.previous, true, true);
        this.indicatorTablistRef.nativeElement.children[this.activeSlides.current].children[0].focus();
    }
    preventDefault(e) {
        e.preventDefault();
    }
    getPreviousCurrentNextIndexes(index) {
        const slidesLength = this.slides.length;
        return {
            previous: (index === 0 ? slidesLength - 1 : index - 1) % slidesLength,
            current: index % slidesLength,
            next: (index === slidesLength - 1 ? 0 : index + 1) % slidesLength
        };
    }
    getAnimationSlideState(index) {
        return index === this.activeSlides.current
            ? 'current'
            : index === this.activeSlides.next
                ? 'next'
                : index === this.activeSlides.previous
                    ? 'previous'
                    : 'none';
    }
    setSlideAnimationState(activeSlides, isPreviousClicked, isAfterChange) {
        const hasOnlyTwoSlides = this.carouselSlideContentArray.length === 2;
        // Animation state array example: ['previous', 'current', 'next']
        for (let i = 0, l = this.slides.length; i < l; i++) {
            // 'current' state
            if (i === activeSlides.current) {
                this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'current';
                // 'next' state
            }
            else if (i === activeSlides.next) {
                // If there are only 2 slides, then the state will be determined by
                // which button they clicked on.
                if (hasOnlyTwoSlides && ((!isPreviousClicked && !isAfterChange) || (isPreviousClicked && isAfterChange))) {
                    this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'next';
                }
                else {
                    this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'previous';
                }
                // Addresses specific edge case where we are on the 2nd slide and click on the 1st page dot
                if (hasOnlyTwoSlides && activeSlides.previous === 0 && activeSlides.current === 1 && activeSlides.next === 0) {
                    this.slideAnimationState[0] = this.slideAnimationStateForIndicator[0] = 'previous';
                }
                if (hasOnlyTwoSlides && activeSlides.previous === 1 && activeSlides.current === 0 && activeSlides.next === 1) {
                    this.slideAnimationState[1] = this.slideAnimationStateForIndicator[1] = 'next';
                }
                // When we have 3+ slides and it's auto-rotate, make sure it's continously rotating in one direction (from right to left)
                if (!hasOnlyTwoSlides) {
                    this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'next';
                    if (activeSlides.current - i === l - 1 || i > activeSlides.current) {
                        this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'next';
                        if (i - activeSlides.current === l - 1)
                            this.slideAnimationState[l - 1] = this.slideAnimationStateForIndicator[l - 1] = 'previous';
                    }
                    else if (i < activeSlides.current)
                        this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'previous';
                }
                // 'previous' state
            }
            else {
                this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'next';
                if (activeSlides.current - i === l - 1 || i > activeSlides.current) {
                    this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'next';
                    if (i - activeSlides.current === l - 1)
                        this.slideAnimationState[l - 1] = this.slideAnimationStateForIndicator[l - 1] = 'previous';
                }
                else if (i < activeSlides.current)
                    this.slideAnimationState[i] = this.slideAnimationStateForIndicator[i] = 'previous';
            }
            // Indicator navigation logic
            if (activeSlides.current === l - 1)
                this.slideAnimationStateForIndicator[0] = 'previous'; // if we're on the last slide, first slide is set as 'previous'
            if (activeSlides.current < i)
                this.slideAnimationStateForIndicator[i] = 'next'; // all slides after current are set to 'next'
        }
    }
    // Autoplay logic
    startTimer() {
        this.showPause = false;
        this.pauseManual = false;
        this.cd.detectChanges();
        if (this.autoPlayDuration > 0 && this.autoPlay && !this.pauseManual && !this.currentInterval) {
            this.currentInterval = setInterval(() => {
                this.select(this.activeSlides.next, false, false, true);
                // Disable left/right buttons are programatically on autoplay
                if (!this.isContinuous) {
                    this.disableArrowFn(this.activeSlides.current, 'left');
                    this.disableArrowFn(this.activeSlides.current, 'right');
                }
            }, this.autoPlayDuration);
        }
    }
    stopTimer() {
        this.showPause = true;
        this.pauseManual = true;
        this.cd.detectChanges();
        if (this.currentInterval) {
            this.currentInterval = clearInterval(this.currentInterval);
        }
    }
    activeNewSlide(index) {
        this._activeSlide = index;
        this.activeSlideChange.emit(index);
        if (this.carouselSlideContentArray.length) {
            this.currentSlide = this.carouselSlideContentArray[index];
            this.currentSlide.isActiveSlide = true;
        }
    }
    announceSlide(message) {
        if (this.activeSlides)
            this.announcer.announce(`Now Displaying slide ${this.activeSlides
                .current + 1}
      out of ${this.carouselSlideContentArray.length}. ${message}`);
    }
    // togglePlayPause method gets called when play or pause button is clicked
    togglePlayPause() {
        // prevent for going too fast
        if (!this.toggleWait)
            return;
        else
            setTimeout(() => (this.toggleWait = true), 150);
        this.toggleWait = false;
        if (!this.isMouseEnter) {
            this.pauseManual = !this.pauseManual;
            if (this.pauseManual) {
                this.pauseClicked();
            }
            else {
                this.playClicked();
            }
        }
    }
    // onMouseEnterSlide method gets called when user takes a mouse pointer to the slide panel. Slide stops with this action
    onMouseEnterSlide() {
        this.isMouseEnter = true;
        this.pauseManual = true;
        this.stopTimer();
    }
    // onMouseLeaveSlide method gets called when user movers away a mouse pointer from the slide panel. Slide starts with this action
    onMouseLeaveSlide() {
        this.isMouseEnter = false;
        this.pauseManual = false;
        this.startTimer();
    }
    // pauseClicked method gets called when pause button is clicked
    pauseClicked() {
        this.pauseManual = true;
        this.stopTimer();
    }
    // playClicked method gets called when play button is clicked
    playClicked() {
        this.pauseManual = false;
        if (!this.isMouseEnter)
            this.startTimer();
    }
    // method that is called when play or pause button is clicked. Takes an argument string for the message to announce
    playPauseAnnounce(message) {
        this.announcer.announce(message);
    }
    // onBlurNextBtn method gets called on blur out of the next button
    onBlurNextBtn() {
        if (!this.isMouseEnter)
            this.startTimer();
    }
    deactiveCurentSlide() {
        this.currentSlide.isActiveSlide = false;
    }
    indicator_aria_label(i) {
        return i + 1 === this.carouselSlides.length ? 'Last slide, ' + 'carousel slide ' + (i + 1) : 'carousel slide ' + (i + 1);
    }
    // #endregion
    // Window resize
    onWinResize() {
        setTimeout(() => {
            this.carouselWidth = this.carouselRef.nativeElement.offsetWidth;
        }, 1000);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.NgZone }, { token: i0.KeyValueDiffers }, { token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsCarouselComponent, selector: "cds-carousel", inputs: { idStr: "idStr", slides: "slides", isNavigation: "isNavigation", isControls: "isControls", autoPlay: "autoPlay", isContinuous: "isContinuous", disabled: "disabled", isPreviousDisabled: "isPreviousDisabled", isNextDisabled: "isNextDisabled", autoPlayDuration: "autoPlayDuration", ariaLabel: "ariaLabel", prevAriaLabel: "prevAriaLabel", nextAriaLabel: "nextAriaLabel", playAriaLabel: "playAriaLabel", pauseAriaLabel: "pauseAriaLabel", announceSlideChange: "announceSlideChange", activeSlide: "activeSlide", tabIndex: "tabIndex", slideTemplateRef: "slideTemplateRef", thumbnailTemplateRef: "thumbnailTemplateRef", carouselHeight: "carouselHeight" }, outputs: { activeSlideChange: "activeSlideChange", activeIndicator: "activeIndicator" }, host: { listeners: { "window:resize": "onWinResize()" } }, queries: [{ propertyName: "carouselSlides", predicate: CdsCarouselSlide }], viewQueries: [{ propertyName: "prevButtonRef", first: true, predicate: ["prev"], descendants: true }, { propertyName: "indicatorPrevButtonRef", first: true, predicate: ["indicatorPrev"], descendants: true }, { propertyName: "nextButtonRef", first: true, predicate: ["next"], descendants: true }, { propertyName: "indicatorNextButtonRef", first: true, predicate: ["indicatorNext"], descendants: true }, { propertyName: "playPauseBtnRef", first: true, predicate: ["playPauseBtn"], descendants: true }, { propertyName: "carouselRef", first: true, predicate: ["carousel"], descendants: true }, { propertyName: "indicatorTablistRef", first: true, predicate: ["indicatorTablist"], descendants: true }, { propertyName: "carouselSlideRefs", predicate: ["slide"], descendants: true, read: ElementRef }], ngImport: i0, template: "<div [ngClass]=\"carouselHeight === 280 ? 'carousel' : 'carousel-custom-height'\" [attr.id]=\"idStr\">\n  <!-- Loading Placeholder -->\n  <div class=\"loading\">\n    <cds-loading-placeholder\n      [visible]=\"showLoader\"\n      [isCircle]=\"false\"\n      [shapeWidth]=\"70\"\n      [shapeHeight]=\"200\"\n      waitMessage=\"Carousel is loading, please wait...\"\n      readyMessage=\"Carousel loaded.\"\n    ></cds-loading-placeholder>\n  </div>\n\n  <!----- All Carousel Slides ------>\n  <div #carousel [attr.id]=\"idStr + '-items'\" [ngStyle]=\"{ 'height.px': carouselHeight }\" class=\"slides\">\n    <!----- Individual Slide ----->\n    <div\n      #slide\n      *ngFor=\"let carouselSlide of carouselSlides; let i = index\"\n      class=\"slide\"\n      [class.is-active]=\"i === activeSlides.current\"\n      [attr.aria-hidden]=\"i !== activeSlides.current\"\n      [@slideState]=\"\n        this.isIndicatorClicked\n          ? slideAnimationStateForIndicator[i]\n          : slideAnimationState[i]\n      \"\n      [@displayState]=\"getAnimationSlideState(i)\"\n      \n      (focusin)=\"stopTimer()\"\n      (focusout)=\"startTimer()\"\n      (mouseenter)=\"onMouseEnterSlide()\"\n      (mouseleave)=\"onMouseLeaveSlide()\"\n    >\n      <ng-container [ngTemplateOutlet]=\"carouselSlide.tpl\" ></ng-container>\n    </div>\n  </div>\n\n  <!-- Previous/Left Arrow -->\n  <div class=\"cds-list-left-arrow\">\n    <button\n      #prev\n      tabindex=\"0\"\n      class=\"prev\"\n      [disabled]=\"isPreviousDisabled\"\n      [class.disabled]=\"disablePreviousArrow(activeSlides.current)\"\n      (focus)=\"stopTimer()\"\n      (blur)=\"startTimer()\"\n      (blur)=\"disableArrowFn(activeSlides.current, 'left')\"\n      (click)=\"selectPrevious(activeSlides.previous, true, true)\"\n      *ngIf=\"slides.length > 1 && isNavigation\"\n      [attr.aria-label]=\"prevAriaLabel\"\n      [attr.aria-controls]=\"idStr + '-items'\"\n      [attr.aria-disabled]=\"disablePreviousArrow(activeSlides.current)\"\n      (mouseenter)=\"stopTimer()\"\n      (mouseleave)=\"startTimer()\"\n      (keydown.enter)=\"selectPrevious(activeSlides.previous, true, true)\"\n      (keydown.space)=\"\n      [selectPrevious(activeSlides.previous, true, true), preventDefault($event)]\n      \"\n      ></button>\n    </div>\n    \n    <!-- Next/Right Arrow -->\n    <div class=\"cds-list-right-arrow\">\n      <button\n      #next\n      *ngIf=\"slides.length > 1 && isNavigation\"\n      class=\"next\"\n      role=\"button\"\n      [disabled]=\"isNextDisabled\"\n      [class.disabled]=\"disableNextArrow(activeSlides.current)\"\n      [attr.aria-disabled]=\"disableNextArrow(activeSlides.current)\"\n      [attr.aria-label]=\"nextAriaLabel\"\n      [attr.aria-controls]=\"idStr + '-items'\"\n      tabindex=\"0\"\n      (click)=\"selectNext(activeSlides.next, true)\"\n      (focus)=\"stopTimer()\"\n      (blur)=\"disableArrowFn(activeSlides.current, 'right')\"\n      (mouseenter)=\"stopTimer()\"\n      (mouseleave)=\"startTimer()\"\n      (keydown.enter)=\"selectNext(activeSlides.next, true)\"\n      (keydown.space)=\"\n        [selectNext(activeSlides.next, true), preventDefault($event)]\n      \"\n    ></button>\n  </div>\n\n  <!--- Play/Pause & Indicator (Page Dots) --->\n  <div [ngClass]=\"carouselHeight === 280 ? 'tab-panel-wrapper' : 'tab-panel-wrapper-custom-height'\">\n\n    <!-- Indicator (Page Dots) Previous/Left Arrow -->\n    <div [ngClass]=\"carouselHeight === 280 ? 'cds-list-left-arrow' : 'cds-list-left-arrow-custom-height'\">\n      <button\n        #indicatorPrev\n        tabindex=\"0\"\n        class=\"indicator-prev\"\n        [disabled]=\"isPreviousDisabled\"\n        [class.disabled]=\"disablePreviousArrow(activeSlides.current)\"\n        (focus)=\"stopTimer()\"\n        (blur)=\"startTimer()\"\n        (blur)=\"disableArrowFn(activeSlides.current, 'left')\"\n        (click)=\"selectPrevious(activeSlides.previous, true, true)\"\n        *ngIf=\"slides.length > 1 && isNavigation\"\n        [attr.aria-label]=\"prevAriaLabel\"\n        [attr.aria-controls]=\"idStr + '-items'\"\n        [attr.aria-disabled]=\"disablePreviousArrow(activeSlides.current)\"\n        (mouseenter)=\"stopTimer()\"\n        (mouseleave)=\"startTimer()\"\n        (keydown.enter)=\"selectPrevious(activeSlides.previous, true, true)\"\n        (keydown.space)=\"\n          [selectPrevious(activeSlides.previous, true, true), preventDefault($event)]\n        \"\n      ></button>\n    </div>\n\n    <!----- Play/Pause Button ----->\n    <div *ngIf=\"autoPlay\">\n      <button\n        #playPauseBtn\n        class=\"play\"\n        tabindex=\"0\"\n        type=\"button\"\n        [attr.aria-label]=\"pauseManual ? playAriaLabel : pauseAriaLabel\"\n        (click)=\"togglePlayPause()\"\n        (keydown.space)=\"[togglePlayPause(), preventDefault($event)]\"\n        *ngIf=\"autoPlay\"\n      >\n        <div [ngClass]=\"pauseManual ? 'play-button' : 'pause-button'\"></div>\n      </button>\n    </div>\n    \n    <!----- Indicator (Page Dots)  ----->\n    <div\n      #indicatorTablist\n      [ngClass]=\"autoPlay ? 'indicators' : 'no-autoplay-indicators'\"\n      class=\"indicators\"\n      *ngIf=\"slides.length > 1 && isControls\"\n      role=\"tablist\"\n      aria-label=\"Slides\"\n    >\n      <div\n        *ngFor=\"let carouselSlide of carouselSlides; let i = index\"\n        class=\"indicator\"\n        [class.is-active]=\"i === activeSlides.current\"\n        [ngClass]=\"!autoPlay && i == 0 ? 'indicator-first' : ''\"\n      >\n        <button\n          role=\"tab\"\n          class=\"indicator-button\"\n          type=\"button\"\n          (click)=\"select_indicator(i)\"\n          (keydown.enter)=\"[stopTimer(), select_indicator(i), preventDefault($event)]\"\n          (keydown.space)=\"[stopTimer(), select_indicator(i), preventDefault($event)]\"\n          (keydown.ArrowRight)=\"[stopTimer(), handleIndicatorKeydown($event)]\"\n          (keydown.ArrowLeft)=\"[stopTimer(), handleIndicatorKeydown($event)]\"\n          [attr.id]=\"idStr + 'Dot' + (i + 1)\"\n          [attr.aria-controls]=\"idStr + '-items'\"\n          [attr.aria-label]=\"indicator_aria_label(i)\"\n          [attr.aria-current]=\"i === activeSlides.current\"\n          [attr.tabindex]=\"i === activeSlides.current ? null : -1\"\n        ></button>\n      </div>\n    </div>\n\n    <!-- Indicator (Page Dots) Next/Right Arrow -->\n    <div [ngClass]=\"carouselHeight === 280 ? 'cds-list-right-arrow' : 'cds-list-right-arrow-custom-height'\">\n      <button\n        #indicatorNext\n        *ngIf=\"slides.length > 1 && isNavigation\"\n        class=\"indicator-next\"\n        role=\"button\"\n        [disabled]=\"isNextDisabled\"\n        [class.disabled]=\"disableNextArrow(activeSlides.current)\"\n        [attr.aria-disabled]=\"disableNextArrow(activeSlides.current)\"\n        [attr.aria-label]=\"nextAriaLabel\"\n        [attr.aria-controls]=\"idStr + '-items'\"\n        tabindex=\"0\"\n        (click)=\"selectNext(activeSlides.next, true)\"\n        (focus)=\"stopTimer()\"\n        (blur)=\"disableArrowFn(activeSlides.current, 'right')\"\n        (mouseenter)=\"stopTimer()\"\n        (mouseleave)=\"startTimer()\"\n        (keydown.enter)=\"selectNext(activeSlides.next, true)\"\n        (keydown.space)=\"\n          [selectNext(activeSlides.next, true), preventDefault($event)]\n        \"\n      ></button>\n    </div>\n  </div>\n\n  <!-- Static Content -->\n  <div\n    class=\"static-content\"\n    (focusin)=\"stopTimer()\"\n    (focusout)=\"startTimer()\"\n    (mouseenter)=\"stopTimer()\"\n    (mouseleave)=\"startTimer()\"\n  >\n    <ng-content select=\"[staticContent]\"></ng-content>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";.carousel,.carousel-custom-height{width:100%;height:100%;min-height:420px;display:flex;flex-direction:column;flex-wrap:nowrap;margin-bottom:30px}.carousel .is-active,.carousel-custom-height .is-active{visibility:visible}.carousel .is-inactive,.carousel-custom-height .is-inactive{visibility:hidden}.carousel-custom-height{margin-bottom:80px}.slides{position:relative;flex-grow:1;background:white;overflow:hidden;margin-left:92px;margin-right:92px;flex-direction:column;top:70px;margin-bottom:60px}.slide{position:absolute;width:100%;flex-direction:column;overflow:hidden}.loading{position:absolute;top:37%;text-align:center;width:100%}:host{display:block;overflow:hidden;position:relative;box-shadow:0 1px 10px #0000001f;min-width:150px}.static-content{display:block;z-index:1;width:100%}.prev,.next,.indicator-prev,.indicator-next{display:flex;width:46px;height:46px;cursor:auto;background:none;border:none;padding:0;z-index:1000;margin-bottom:16px}.prev,.indicator-prev{margin-left:16px;background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23056dae' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.prev:hover,.indicator-prev:hover{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23034c81' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.prev:focus,.indicator-prev:focus{outline:none;box-shadow:0 0 0 1px #002a54;border-radius:8px}.prev:active ::ng-deep,.indicator-prev:active ::ng-deep{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23002a54' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.prev:disabled,.prev.disabled,.indicator-prev:disabled,.indicator-prev.disabled{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat;cursor:auto}.prev:disabled:hover,.prev.disabled:hover,.indicator-prev:disabled:hover,.indicator-prev.disabled:hover{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next,.indicator-next{margin-right:16px;background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23056dae' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next:hover,.indicator-next:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23034c81' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next:focus,.indicator-next:focus{outline:none;box-shadow:0 0 0 1px #002a54;border-radius:8px}.next:active,.indicator-next:active{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23002a54' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next:disabled,.next.disabled,.indicator-next:disabled,.indicator-next.disabled{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat;cursor:auto}.next:disabled:hover,.next.disabled:hover,.indicator-next:disabled:hover,.indicator-next.disabled:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.cds-list-left-arrow,.cds-list-left-arrow-custom-height{position:absolute;bottom:45%;left:0;z-index:1000}.cds-list-right-arrow,.cds-list-right-arrow-custom-height{position:absolute;right:0;bottom:45%;z-index:1000}.tab-panel-wrapper{display:flex;justify-content:center;align-items:center;position:absolute;width:100%;bottom:0;z-index:999;margin-bottom:33px}.tab-panel-wrapper-custom-height{display:flex;justify-content:center;align-items:center;position:absolute;bottom:0;width:100%;z-index:999;margin-top:50px;margin-bottom:33px}.indicators{list-style:none;display:flex;justify-content:center;z-index:999;padding-inline-start:0px}.play,.pause{display:flex;cursor:auto;border:none;background:none;padding:0;justify-content:center;align-items:center}.indicator{position:relative;cursor:auto;background-color:#cecece;border-radius:47px;width:8px;height:8px;margin-left:20px}.indicator.is-active .indicator-button{width:100%;height:100%;cursor:auto;display:block;background:none;border:none;outline:none;padding:0}.indicator.is-active .indicator-button:focus:before{left:-6px}.indicator .indicator-button{width:100%;height:100%;cursor:auto;display:block;background:none;border:none;outline:none;padding:0}.indicator .indicator-button:focus:before{content:\"\";display:inline-block;position:absolute;height:28px;width:28px;border:1px solid #002a54;border-radius:8px;top:-10px;left:-10px}.indicator .indicator-button:hover{background-color:#888;border-radius:47px}.indicator-first{margin-left:0!important}.indicator.is-active{height:8px;width:16px;background-color:#056dae;opacity:2}.indicator-prev,.indicator-next{display:none}.indicator-prev{margin-left:8px}.indicator-next{margin-right:8px}.play:focus{outline:none;box-shadow:0 0 0 1px #002a54;-webkit-box-shadow:0 0 0 1px #002a54;border:1px solid #ffffff;border-radius:50%}.play:active ::ng-deep .pause-button{background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23002a54' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play:active ::ng-deep .play-button{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23002a54' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play:disabled ::ng-deep .pause-button{background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23cccccc' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play:disabled ::ng-deep .play-button{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23cccccc' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play-button{height:20px;width:20px;display:flex;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23056dae' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play-button:hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23034c81' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.pause-button{height:20px;width:20px;display:flex;background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23056dae' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.pause-button:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23034c81' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}@media only screen and (max-width: 768px){.slides{top:16px;margin-left:16px;margin-right:16px;margin-bottom:16px}.tab-panel-wrapper{margin-bottom:26px}.cds-list-buttons{top:unset!important;bottom:0}.carousel{display:block;min-height:340px}.slide{height:auto}.cds-list-left-arrow,.cds-list-right-arrow,.cds-list-left-arrow-custom-height,.cds-list-right-arrow-custom-height{bottom:-28px!important}.prev,.next{display:none}.indicator-prev,.indicator-next{display:block}}@media only screen and (max-width: 479px) and (min-width: 421px){.slides{top:16px}.carousel{display:block;min-height:340px}.slide{height:auto}}@media only screen and (max-width: 420px) and (min-width: 376px){.slides{top:16px}.carousel{display:block;min-height:340px}.slide{height:auto}}@media only screen and (max-width: 376px){.slides{top:16px}.carousel{display:block;min-height:340px}.slide{height:auto}}.indicator-button{width:100%;height:100%;cursor:auto;display:block;background:none;border:none;outline:none;padding:0}@media all and (-ms-high-contrast: none),(-ms-high-contrast: active){.slides{position:relative;flex-grow:1;background:white;overflow:hidden;flex-direction:column;height:auto;top:70px}.indicators{margin-bottom:30px}}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: CdsLoadingPlaceholder, selector: "cds-loading-placeholder", inputs: ["visible", "isCircle", "shapeWidth", "shapeHeight", "waitMessage", "readyMessage"] }], animations: [
            trigger('slideState', [
                state('current', style({
                    transform: 'translateX(0%)'
                })),
                state('next', style({
                    transform: 'translateX(100%)'
                })),
                state('previous', style({
                    transform: 'translateX(-100%)'
                })),
                transition('current => previous', animate(`${TRANSITION_TIME}ms ease-out`)),
                transition('next => current', animate(`${TRANSITION_TIME}ms ease-out`)),
                transition('current => next', animate(`${TRANSITION_TIME}ms ease-out`)),
                transition('previous => current', animate(`${TRANSITION_TIME}ms ease-out`))
            ]),
            trigger('displayState', [
                state('current', style({})),
                state('next', style({ display: 'none' })),
                state('previous', style({ display: 'none' })),
                state('none', style({ display: 'none' })),
                transition('current => previous', animate(`${TRANSITION_TIME}ms ease-out`)),
                transition('current => next', animate(`${TRANSITION_TIME}ms ease-out`)),
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-carousel', changeDetection: ChangeDetectionStrategy.OnPush, animations: [
                        trigger('slideState', [
                            state('current', style({
                                transform: 'translateX(0%)'
                            })),
                            state('next', style({
                                transform: 'translateX(100%)'
                            })),
                            state('previous', style({
                                transform: 'translateX(-100%)'
                            })),
                            transition('current => previous', animate(`${TRANSITION_TIME}ms ease-out`)),
                            transition('next => current', animate(`${TRANSITION_TIME}ms ease-out`)),
                            transition('current => next', animate(`${TRANSITION_TIME}ms ease-out`)),
                            transition('previous => current', animate(`${TRANSITION_TIME}ms ease-out`))
                        ]),
                        trigger('displayState', [
                            state('current', style({})),
                            state('next', style({ display: 'none' })),
                            state('previous', style({ display: 'none' })),
                            state('none', style({ display: 'none' })),
                            transition('current => previous', animate(`${TRANSITION_TIME}ms ease-out`)),
                            transition('current => next', animate(`${TRANSITION_TIME}ms ease-out`)),
                        ])
                    ], template: "<div [ngClass]=\"carouselHeight === 280 ? 'carousel' : 'carousel-custom-height'\" [attr.id]=\"idStr\">\n  <!-- Loading Placeholder -->\n  <div class=\"loading\">\n    <cds-loading-placeholder\n      [visible]=\"showLoader\"\n      [isCircle]=\"false\"\n      [shapeWidth]=\"70\"\n      [shapeHeight]=\"200\"\n      waitMessage=\"Carousel is loading, please wait...\"\n      readyMessage=\"Carousel loaded.\"\n    ></cds-loading-placeholder>\n  </div>\n\n  <!----- All Carousel Slides ------>\n  <div #carousel [attr.id]=\"idStr + '-items'\" [ngStyle]=\"{ 'height.px': carouselHeight }\" class=\"slides\">\n    <!----- Individual Slide ----->\n    <div\n      #slide\n      *ngFor=\"let carouselSlide of carouselSlides; let i = index\"\n      class=\"slide\"\n      [class.is-active]=\"i === activeSlides.current\"\n      [attr.aria-hidden]=\"i !== activeSlides.current\"\n      [@slideState]=\"\n        this.isIndicatorClicked\n          ? slideAnimationStateForIndicator[i]\n          : slideAnimationState[i]\n      \"\n      [@displayState]=\"getAnimationSlideState(i)\"\n      \n      (focusin)=\"stopTimer()\"\n      (focusout)=\"startTimer()\"\n      (mouseenter)=\"onMouseEnterSlide()\"\n      (mouseleave)=\"onMouseLeaveSlide()\"\n    >\n      <ng-container [ngTemplateOutlet]=\"carouselSlide.tpl\" ></ng-container>\n    </div>\n  </div>\n\n  <!-- Previous/Left Arrow -->\n  <div class=\"cds-list-left-arrow\">\n    <button\n      #prev\n      tabindex=\"0\"\n      class=\"prev\"\n      [disabled]=\"isPreviousDisabled\"\n      [class.disabled]=\"disablePreviousArrow(activeSlides.current)\"\n      (focus)=\"stopTimer()\"\n      (blur)=\"startTimer()\"\n      (blur)=\"disableArrowFn(activeSlides.current, 'left')\"\n      (click)=\"selectPrevious(activeSlides.previous, true, true)\"\n      *ngIf=\"slides.length > 1 && isNavigation\"\n      [attr.aria-label]=\"prevAriaLabel\"\n      [attr.aria-controls]=\"idStr + '-items'\"\n      [attr.aria-disabled]=\"disablePreviousArrow(activeSlides.current)\"\n      (mouseenter)=\"stopTimer()\"\n      (mouseleave)=\"startTimer()\"\n      (keydown.enter)=\"selectPrevious(activeSlides.previous, true, true)\"\n      (keydown.space)=\"\n      [selectPrevious(activeSlides.previous, true, true), preventDefault($event)]\n      \"\n      ></button>\n    </div>\n    \n    <!-- Next/Right Arrow -->\n    <div class=\"cds-list-right-arrow\">\n      <button\n      #next\n      *ngIf=\"slides.length > 1 && isNavigation\"\n      class=\"next\"\n      role=\"button\"\n      [disabled]=\"isNextDisabled\"\n      [class.disabled]=\"disableNextArrow(activeSlides.current)\"\n      [attr.aria-disabled]=\"disableNextArrow(activeSlides.current)\"\n      [attr.aria-label]=\"nextAriaLabel\"\n      [attr.aria-controls]=\"idStr + '-items'\"\n      tabindex=\"0\"\n      (click)=\"selectNext(activeSlides.next, true)\"\n      (focus)=\"stopTimer()\"\n      (blur)=\"disableArrowFn(activeSlides.current, 'right')\"\n      (mouseenter)=\"stopTimer()\"\n      (mouseleave)=\"startTimer()\"\n      (keydown.enter)=\"selectNext(activeSlides.next, true)\"\n      (keydown.space)=\"\n        [selectNext(activeSlides.next, true), preventDefault($event)]\n      \"\n    ></button>\n  </div>\n\n  <!--- Play/Pause & Indicator (Page Dots) --->\n  <div [ngClass]=\"carouselHeight === 280 ? 'tab-panel-wrapper' : 'tab-panel-wrapper-custom-height'\">\n\n    <!-- Indicator (Page Dots) Previous/Left Arrow -->\n    <div [ngClass]=\"carouselHeight === 280 ? 'cds-list-left-arrow' : 'cds-list-left-arrow-custom-height'\">\n      <button\n        #indicatorPrev\n        tabindex=\"0\"\n        class=\"indicator-prev\"\n        [disabled]=\"isPreviousDisabled\"\n        [class.disabled]=\"disablePreviousArrow(activeSlides.current)\"\n        (focus)=\"stopTimer()\"\n        (blur)=\"startTimer()\"\n        (blur)=\"disableArrowFn(activeSlides.current, 'left')\"\n        (click)=\"selectPrevious(activeSlides.previous, true, true)\"\n        *ngIf=\"slides.length > 1 && isNavigation\"\n        [attr.aria-label]=\"prevAriaLabel\"\n        [attr.aria-controls]=\"idStr + '-items'\"\n        [attr.aria-disabled]=\"disablePreviousArrow(activeSlides.current)\"\n        (mouseenter)=\"stopTimer()\"\n        (mouseleave)=\"startTimer()\"\n        (keydown.enter)=\"selectPrevious(activeSlides.previous, true, true)\"\n        (keydown.space)=\"\n          [selectPrevious(activeSlides.previous, true, true), preventDefault($event)]\n        \"\n      ></button>\n    </div>\n\n    <!----- Play/Pause Button ----->\n    <div *ngIf=\"autoPlay\">\n      <button\n        #playPauseBtn\n        class=\"play\"\n        tabindex=\"0\"\n        type=\"button\"\n        [attr.aria-label]=\"pauseManual ? playAriaLabel : pauseAriaLabel\"\n        (click)=\"togglePlayPause()\"\n        (keydown.space)=\"[togglePlayPause(), preventDefault($event)]\"\n        *ngIf=\"autoPlay\"\n      >\n        <div [ngClass]=\"pauseManual ? 'play-button' : 'pause-button'\"></div>\n      </button>\n    </div>\n    \n    <!----- Indicator (Page Dots)  ----->\n    <div\n      #indicatorTablist\n      [ngClass]=\"autoPlay ? 'indicators' : 'no-autoplay-indicators'\"\n      class=\"indicators\"\n      *ngIf=\"slides.length > 1 && isControls\"\n      role=\"tablist\"\n      aria-label=\"Slides\"\n    >\n      <div\n        *ngFor=\"let carouselSlide of carouselSlides; let i = index\"\n        class=\"indicator\"\n        [class.is-active]=\"i === activeSlides.current\"\n        [ngClass]=\"!autoPlay && i == 0 ? 'indicator-first' : ''\"\n      >\n        <button\n          role=\"tab\"\n          class=\"indicator-button\"\n          type=\"button\"\n          (click)=\"select_indicator(i)\"\n          (keydown.enter)=\"[stopTimer(), select_indicator(i), preventDefault($event)]\"\n          (keydown.space)=\"[stopTimer(), select_indicator(i), preventDefault($event)]\"\n          (keydown.ArrowRight)=\"[stopTimer(), handleIndicatorKeydown($event)]\"\n          (keydown.ArrowLeft)=\"[stopTimer(), handleIndicatorKeydown($event)]\"\n          [attr.id]=\"idStr + 'Dot' + (i + 1)\"\n          [attr.aria-controls]=\"idStr + '-items'\"\n          [attr.aria-label]=\"indicator_aria_label(i)\"\n          [attr.aria-current]=\"i === activeSlides.current\"\n          [attr.tabindex]=\"i === activeSlides.current ? null : -1\"\n        ></button>\n      </div>\n    </div>\n\n    <!-- Indicator (Page Dots) Next/Right Arrow -->\n    <div [ngClass]=\"carouselHeight === 280 ? 'cds-list-right-arrow' : 'cds-list-right-arrow-custom-height'\">\n      <button\n        #indicatorNext\n        *ngIf=\"slides.length > 1 && isNavigation\"\n        class=\"indicator-next\"\n        role=\"button\"\n        [disabled]=\"isNextDisabled\"\n        [class.disabled]=\"disableNextArrow(activeSlides.current)\"\n        [attr.aria-disabled]=\"disableNextArrow(activeSlides.current)\"\n        [attr.aria-label]=\"nextAriaLabel\"\n        [attr.aria-controls]=\"idStr + '-items'\"\n        tabindex=\"0\"\n        (click)=\"selectNext(activeSlides.next, true)\"\n        (focus)=\"stopTimer()\"\n        (blur)=\"disableArrowFn(activeSlides.current, 'right')\"\n        (mouseenter)=\"stopTimer()\"\n        (mouseleave)=\"startTimer()\"\n        (keydown.enter)=\"selectNext(activeSlides.next, true)\"\n        (keydown.space)=\"\n          [selectNext(activeSlides.next, true), preventDefault($event)]\n        \"\n      ></button>\n    </div>\n  </div>\n\n  <!-- Static Content -->\n  <div\n    class=\"static-content\"\n    (focusin)=\"stopTimer()\"\n    (focusout)=\"startTimer()\"\n    (mouseenter)=\"stopTimer()\"\n    (mouseleave)=\"startTimer()\"\n  >\n    <ng-content select=\"[staticContent]\"></ng-content>\n  </div>\n</div>\n", styles: ["@charset \"UTF-8\";.carousel,.carousel-custom-height{width:100%;height:100%;min-height:420px;display:flex;flex-direction:column;flex-wrap:nowrap;margin-bottom:30px}.carousel .is-active,.carousel-custom-height .is-active{visibility:visible}.carousel .is-inactive,.carousel-custom-height .is-inactive{visibility:hidden}.carousel-custom-height{margin-bottom:80px}.slides{position:relative;flex-grow:1;background:white;overflow:hidden;margin-left:92px;margin-right:92px;flex-direction:column;top:70px;margin-bottom:60px}.slide{position:absolute;width:100%;flex-direction:column;overflow:hidden}.loading{position:absolute;top:37%;text-align:center;width:100%}:host{display:block;overflow:hidden;position:relative;box-shadow:0 1px 10px #0000001f;min-width:150px}.static-content{display:block;z-index:1;width:100%}.prev,.next,.indicator-prev,.indicator-next{display:flex;width:46px;height:46px;cursor:auto;background:none;border:none;padding:0;z-index:1000;margin-bottom:16px}.prev,.indicator-prev{margin-left:16px;background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23056dae' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.prev:hover,.indicator-prev:hover{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23034c81' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.prev:focus,.indicator-prev:focus{outline:none;box-shadow:0 0 0 1px #002a54;border-radius:8px}.prev:active ::ng-deep,.indicator-prev:active ::ng-deep{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23002a54' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.prev:disabled,.prev.disabled,.indicator-prev:disabled,.indicator-prev.disabled{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat;cursor:auto}.prev:disabled:hover,.prev.disabled:hover,.indicator-prev:disabled:hover,.indicator-prev.disabled:hover{background-image:url(\"data:image/svg+xml, %3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M25.5277231,3.05321154 C25.2502231,2.77571154 24.7976846,2.77713462 24.5216077,3.05605769 L10.5825692,17.0676731 C9.75149231,17.9044423 9.75576154,19.2549423 10.5911077,20.0874423 L24.5244538,33.9467885 C24.8019538,34.2228654 25.2544923,34.2228654 25.5305692,33.9439423 C25.8066462,33.6664423 25.8052231,33.2139038 25.5277231,32.9378269 L11.5943769,19.0784808 C11.3154538,18.8009808 11.3140308,18.3498654 11.5915308,18.0723654 L25.5305692,4.05932692 C25.8066462,3.78040385 25.8052231,3.33071154 25.5277231,3.05321154' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Pause' transform='translate(-20.000000, -381.000000)'%3E%3Cg id='Left-Carousel-Arrow' transform='translate(20.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/255-chevron-left' transform='translate(5.612245, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next,.indicator-next{margin-right:16px;background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23056dae' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next:hover,.indicator-next:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23034c81' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next:focus,.indicator-next:focus{outline:none;box-shadow:0 0 0 1px #002a54;border-radius:8px}.next:active,.indicator-next:active{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23002a54' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.next:disabled,.next.disabled,.indicator-next:disabled,.indicator-next.disabled{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat;cursor:auto}.next:disabled:hover,.next.disabled:hover,.indicator-next:disabled:hover,.indicator-next.disabled:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='50px' height='50px' viewBox='0 0 50 50' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Cdefs%3E%3Cpath d='M10.1684538,4.05932692 L24.1074923,18.0723654 C24.3835692,18.3498654 24.3821462,18.8009808 24.1046462,19.0784808 L10.1713,32.9378269 C9.89237692,33.2139038 9.89095385,33.6664423 10.1684538,33.9439423 C10.4459538,34.2228654 10.8956462,34.2228654 11.1745692,33.9467885 L25.1079154,20.0874423 C25.9446846,19.2549423 25.9475308,17.9044423 25.1164538,17.0676731 L11.1774154,3.05605769 C10.8999154,2.77713462 10.4502231,2.77571154 10.1713,3.05321154 C9.89237692,3.33071154 9.89095385,3.78040385 10.1684538,4.05932692' id='path-1'%3E%3C/path%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel-Desktop-\\2013-Additional-Carousel-Image' transform='translate(-1370.000000, -381.000000)'%3E%3Cg id='Right-Carousel-Arrow' transform='translate(1370.000000, 381.000000)'%3E%3Ccircle id='Oval' fill='%23FFFFFF' opacity='0.600000024' cx='25' cy='25' r='25'%3E%3C/circle%3E%3Cg id='01-direction/257-chevron-right' transform='translate(7.653061, 6.632653)'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cuse id='Mask' fill='%23056DAE' fill-rule='evenodd' xlink:href='%23path-1'%3E%3C/use%3E%3Cg id='Color---B1' mask='url(%23mask-2)' fill='%23cccccc' fill-rule='evenodd'%3E%3Crect id='%23002A54' x='0' y='0' width='37' height='37'%3E%3C/rect%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.cds-list-left-arrow,.cds-list-left-arrow-custom-height{position:absolute;bottom:45%;left:0;z-index:1000}.cds-list-right-arrow,.cds-list-right-arrow-custom-height{position:absolute;right:0;bottom:45%;z-index:1000}.tab-panel-wrapper{display:flex;justify-content:center;align-items:center;position:absolute;width:100%;bottom:0;z-index:999;margin-bottom:33px}.tab-panel-wrapper-custom-height{display:flex;justify-content:center;align-items:center;position:absolute;bottom:0;width:100%;z-index:999;margin-top:50px;margin-bottom:33px}.indicators{list-style:none;display:flex;justify-content:center;z-index:999;padding-inline-start:0px}.play,.pause{display:flex;cursor:auto;border:none;background:none;padding:0;justify-content:center;align-items:center}.indicator{position:relative;cursor:auto;background-color:#cecece;border-radius:47px;width:8px;height:8px;margin-left:20px}.indicator.is-active .indicator-button{width:100%;height:100%;cursor:auto;display:block;background:none;border:none;outline:none;padding:0}.indicator.is-active .indicator-button:focus:before{left:-6px}.indicator .indicator-button{width:100%;height:100%;cursor:auto;display:block;background:none;border:none;outline:none;padding:0}.indicator .indicator-button:focus:before{content:\"\";display:inline-block;position:absolute;height:28px;width:28px;border:1px solid #002a54;border-radius:8px;top:-10px;left:-10px}.indicator .indicator-button:hover{background-color:#888;border-radius:47px}.indicator-first{margin-left:0!important}.indicator.is-active{height:8px;width:16px;background-color:#056dae;opacity:2}.indicator-prev,.indicator-next{display:none}.indicator-prev{margin-left:8px}.indicator-next{margin-right:8px}.play:focus{outline:none;box-shadow:0 0 0 1px #002a54;-webkit-box-shadow:0 0 0 1px #002a54;border:1px solid #ffffff;border-radius:50%}.play:active ::ng-deep .pause-button{background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23002a54' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play:active ::ng-deep .play-button{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23002a54' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play:disabled ::ng-deep .pause-button{background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23cccccc' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play:disabled ::ng-deep .play-button{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23cccccc' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play-button{height:20px;width:20px;display:flex;background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23056dae' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.play-button:hover{background-image:url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='20px' height='20px' viewBox='0 0 20 20' version='1.1'%3E%3Ctitle%3EE45939A3-0637-411D-BA58-3C2EBE1FD1D3@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M1.235,1 C0.9475,1 0.675,1.173 0.557,1.4605 C0.519,1.552 0.5,1.6505 0.5,1.75 L0.5,10.75 C0.5,11.164 0.829,11.5 1.235,11.5 C1.332,11.5 1.4285,11.48 1.5185,11.442 L11.0485,6.942 C11.423,6.7815 11.5995,6.3425 11.443,5.9605 C11.368,5.779 11.2265,5.6345 11.0485,5.5585 L1.5185,1.0585 C1.426,1.019 1.3295,1 1.235,1' id='path-3'/%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-132.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(132.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'/%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'/%3E%3Crect id='\\1f3a8-Fill' fill='%23034c81' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'/%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'/%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(5.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'/%3E%3Cmask id='mask-4' fill='white'%3E%3Cuse xlink:href='%23path-3'/%3E%3C/mask%3E%3Cuse id='icon-color' fill='%23FFFFFF' xlink:href='%23path-3'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.pause-button{height:20px;width:20px;display:flex;background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23056dae' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}.pause-button:hover{background-image:url(\"data:image/svg+xml,%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3Ctitle%3EF91BC3AA-7E85-423D-8B9F-3F93908E2CCE@1x%3C/title%3E%3Cdefs%3E%3Crect id='path-1' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3C/defs%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Cg id='Carousel' transform='translate(-176.000000, -299.000000)'%3E%3Cg id='_Ready-for-Production/Buttons/Primary-Default/Icon/Default' transform='translate(176.000000, 299.000000)'%3E%3Cg id='Shape/Fill/Circle'%3E%3Cmask id='mask-2' fill='white'%3E%3Cuse xlink:href='%23path-1'%3E%3C/use%3E%3C/mask%3E%3Cg id='\\1f312-Shadow'%3E%3C/g%3E%3Crect id='\\1f3a8-Fill' fill='%23034c81' mask='url(%23mask-2)' x='0' y='0' width='20' height='20' rx='10'%3E%3C/rect%3E%3Cpath d='M10,0 C15.5228475,-1.01453063e-15 20,4.4771525 20,10 C20,15.5228475 15.5228475,20 10,20 C4.4771525,20 6.76353751e-16,15.5228475 0,10 C-6.76353751e-16,4.4771525 4.4771525,1.01453063e-15 10,0 Z M10,1 C5.02943725,1 1,5.02943725 1,10 C1,14.9705627 5.02943725,19 10,19 C14.9705627,19 19,14.9705627 19,10 C19,5.02943725 14.9705627,1 10,1 Z' id='\\1f532-Outline' mask='url(%23mask-2)'%3E%3C/path%3E%3C/g%3E%3Cg id='\\1f171\\fe0f-Button-Icon' transform='translate(4.000000, 4.000000)'%3E%3Crect id='view-box' x='0' y='0' width='12' height='12'%3E%3C/rect%3E%3Cpath d='M4.88270378,1 C5.22362707,1 5.5,1.27916458 5.5,1.62353154 L5.5,10.3764685 C5.5,10.7208354 5.22362707,11 4.88270378,11 L1.61729622,11 C1.27637293,11 1,10.7208354 1,10.3764685 L1,1.62353154 C1,1.27916458 1.27637293,1 1.61729622,1 L4.88270378,1 Z M10.3827038,1 C10.7236271,1 11,1.27916458 11,1.62353154 L11,10.3764685 C11,10.7208354 10.7236271,11 10.3827038,11 L7.11729622,11 C6.77637293,11 6.5,10.7208354 6.5,10.3764685 L6.5,1.62353154 C6.5,1.27916458 6.77637293,1 7.11729622,1 L10.3827038,1 Z' id='icon-color' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E\");background-repeat:no-repeat}@media only screen and (max-width: 768px){.slides{top:16px;margin-left:16px;margin-right:16px;margin-bottom:16px}.tab-panel-wrapper{margin-bottom:26px}.cds-list-buttons{top:unset!important;bottom:0}.carousel{display:block;min-height:340px}.slide{height:auto}.cds-list-left-arrow,.cds-list-right-arrow,.cds-list-left-arrow-custom-height,.cds-list-right-arrow-custom-height{bottom:-28px!important}.prev,.next{display:none}.indicator-prev,.indicator-next{display:block}}@media only screen and (max-width: 479px) and (min-width: 421px){.slides{top:16px}.carousel{display:block;min-height:340px}.slide{height:auto}}@media only screen and (max-width: 420px) and (min-width: 376px){.slides{top:16px}.carousel{display:block;min-height:340px}.slide{height:auto}}@media only screen and (max-width: 376px){.slides{top:16px}.carousel{display:block;min-height:340px}.slide{height:auto}}.indicator-button{width:100%;height:100%;cursor:auto;display:block;background:none;border:none;outline:none;padding:0}@media all and (-ms-high-contrast: none),(-ms-high-contrast: active){.slides{position:relative;flex-grow:1;background:white;overflow:hidden;flex-direction:column;height:auto;top:70px}.indicators{margin-bottom:30px}}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.NgZone }, { type: i0.KeyValueDiffers }, { type: i1$1.LiveAnnouncer }], propDecorators: { idStr: [{
                type: Input
            }], slides: [{
                type: Input
            }], isNavigation: [{
                type: Input
            }], isControls: [{
                type: Input
            }], autoPlay: [{
                type: Input
            }], isContinuous: [{
                type: Input
            }], disabled: [{
                type: Input
            }], isPreviousDisabled: [{
                type: Input
            }], isNextDisabled: [{
                type: Input
            }], autoPlayDuration: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], prevAriaLabel: [{
                type: Input
            }], nextAriaLabel: [{
                type: Input
            }], playAriaLabel: [{
                type: Input
            }], pauseAriaLabel: [{
                type: Input
            }], announceSlideChange: [{
                type: Input
            }], activeSlide: [{
                type: Input
            }], tabIndex: [{
                type: Input
            }], slideTemplateRef: [{
                type: Input
            }], thumbnailTemplateRef: [{
                type: Input
            }], carouselHeight: [{
                type: Input
            }], activeSlideChange: [{
                type: Output
            }], activeIndicator: [{
                type: Output
            }], carouselSlideRefs: [{
                type: ViewChildren,
                args: ['slide', { read: ElementRef }]
            }], prevButtonRef: [{
                type: ViewChild,
                args: ['prev']
            }], indicatorPrevButtonRef: [{
                type: ViewChild,
                args: ['indicatorPrev']
            }], nextButtonRef: [{
                type: ViewChild,
                args: ['next']
            }], indicatorNextButtonRef: [{
                type: ViewChild,
                args: ['indicatorNext']
            }], playPauseBtnRef: [{
                type: ViewChild,
                args: ['playPauseBtn']
            }], carouselRef: [{
                type: ViewChild,
                args: ['carousel']
            }], indicatorTablistRef: [{
                type: ViewChild,
                args: ['indicatorTablist']
            }], carouselSlides: [{
                type: ContentChildren,
                args: [CdsCarouselSlide]
            }], onWinResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

class CdsCarouselModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselModule, declarations: [CdsCarouselComponent, CdsCarouselSlide], imports: [CommonModule,
            CdsLoadingPlaceholderModule,
            CdsIconModule,
            CdsPageDotsModule], exports: [CdsCarouselComponent, CdsCarouselSlide] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselModule, imports: [CommonModule,
            CdsLoadingPlaceholderModule,
            CdsIconModule,
            CdsPageDotsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCarouselModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsCarouselComponent, CdsCarouselSlide],
                    imports: [
                        CommonModule,
                        CdsLoadingPlaceholderModule,
                        CdsIconModule,
                        CdsPageDotsModule
                    ],
                    exports: [CdsCarouselComponent, CdsCarouselSlide]
                }]
        }] });

const SlideInOutAnimation = [
    trigger('slideDrawer', [
        state('left', style({
            transform: 'translateX(-29%)'
        })),
        state('right', style({
            transform: 'translateX(0)'
        })),
        transition('* => *', animate(200))
    ])
];

class DrawerComponent {
    open() {
        this.direction = 'left';
        this.openDrawer = true;
        if (!this.turnOffAnnouncement)
            this.liveAnnouncer.announce(this.drawerOpenedAnnouncement);
    }
    close() {
        if (this.openDrawer)
            this.direction = 'right';
        if (!this.turnOffAnnouncement)
            this.liveAnnouncer.announce(this.drawerClosedAnnouncement);
        return true;
    }
    toggle() {
        this.direction == 'left' ? this.close() : this.open();
    }
    checkIfOpen() {
        if (this.direction == 'left')
            return true;
        else
            return false;
    }
    constructor(liveAnnouncer, mediaMatcher) {
        this.liveAnnouncer = liveAnnouncer;
        this.mediaMatcher = mediaMatcher;
        /**
         * @property: openDrawer
         * @description sets openDrawer to true if the drawer is open, else sets to false.
         * @type: boolean
         */
        this.openDrawer = false;
        /**
         * @property: direction
         * @description sets the animation direction for the drawer. Valid directions are left or right.
         * @type: string
         */
        this.direction = 'right';
        /**
         * @property: contentPanelOpenPercent
         * @description determines how much of the percentage of content drawer to open.
         * @type: number
         */
        this.contentPanelOpenPercent = 60;
        /**
         * @property: actionPanelOpenPercent
         * @description determines how much of the percentage of action drawer to open.
         * @type: number
         */
        this.actionPanelOpenPercent = 40;
        /**
         * @property: drawerOpenedAnnouncement
         * @description determines the message to read when drawer is opened.
         * @type: string
         */
        this.drawerOpenedAnnouncement = 'Drawer Opened';
        /**
         * @property: drawerClosedAnnouncement
         * @description  determines the message to read when drawer is closed.
         * @type: string
         */
        this.drawerClosedAnnouncement = 'Drawer Closed';
        /**
         * @property: turn off announcement
         * @description  determines the message to read when drawer is closed.
         * @type: string
         */
        this.turnOffAnnouncement = false;
        this.drawerClick = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Drawer <citi-drawer> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    ngOnInit() {
        this.matcher = this.mediaMatcher.matchMedia('(max-width: 767px)');
        if (this.matcher.matches) {
            this.contentPanelOpenPercent = 40;
            this.actionPanelOpenPercent = 60;
        }
    }
    onDrawerClick(event) {
        event.preventDefault();
        this.drawerClick.emit(event);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DrawerComponent, deps: [{ token: i1$1.LiveAnnouncer }, { token: i2$1.MediaMatcher }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: DrawerComponent, selector: "cds-drawer", inputs: { contentPanelStyle: "contentPanelStyle", actionPanelStyle: "actionPanelStyle", actionPanelBgColor: "actionPanelBgColor", contentPanelBgColor: "contentPanelBgColor", openDrawer: "openDrawer", direction: "direction", contentPanelOpenPercent: "contentPanelOpenPercent", actionPanelOpenPercent: "actionPanelOpenPercent", drawerOpenedAnnouncement: "drawerOpenedAnnouncement", drawerClosedAnnouncement: "drawerClosedAnnouncement", turnOffAnnouncement: "turnOffAnnouncement" }, outputs: { drawerClick: "drawerClick" }, viewQueries: [{ propertyName: "contentPanelWrap", first: true, predicate: ["contentPanelWrap"], descendants: true }, { propertyName: "actionPanelWrap", first: true, predicate: ["actionPanelWrap"], descendants: true }], ngImport: i0, template: `
    <div [@slideDrawer]="direction" class="ddl-drawer-wrapper">
      <div
        #contentPanelWrap
        class="contentPanel"
        [ngStyle]="contentPanelStyle"
        [style.background-color]="contentPanelBgColor"
        [style.width]="checkIfOpen() ? contentPanelOpenPercent + '%' : '71%'"
        (click)="onDrawerClick($event)"
        tabindex="0"
      >
        <ng-content select="[contentPanel]"></ng-content>
      </div>

      <div
        #actionPanelWrap
        class="actionPanel"
        [ngStyle]="actionPanelStyle"
        [style.background-color]="actionPanelBgColor"
        [style.width]="checkIfOpen() ? actionPanelOpenPercent + '%' : ''"
        [attr.aria-hidden]="checkIfOpen() === true ? false : true"
      >
        <ng-content select="[actionPanel]"></ng-content>
      </div>
    </div>
  `, isInline: true, styles: [":host{font-family:Interstate_Light,sans-serif;font-size:15px;padding:5px 0}:host .ddl-drawer-wrapper{display:flex;width:145%}:host .ddl-drawer-wrapper .contentPanel{padding:6px;background-color:#fff}:host .ddl-drawer-wrapper .contentPanel:hover{background-color:#f4f4f4}:host .ddl-drawer-wrapper .actionPanel{background-color:#ebebeb}@media only screen and (max-width: 767px){:host .cds-drawer-wrapper{width:159%}}\n"], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [SlideInOutAnimation] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: DrawerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-drawer', animations: [SlideInOutAnimation], template: `
    <div [@slideDrawer]="direction" class="ddl-drawer-wrapper">
      <div
        #contentPanelWrap
        class="contentPanel"
        [ngStyle]="contentPanelStyle"
        [style.background-color]="contentPanelBgColor"
        [style.width]="checkIfOpen() ? contentPanelOpenPercent + '%' : '71%'"
        (click)="onDrawerClick($event)"
        tabindex="0"
      >
        <ng-content select="[contentPanel]"></ng-content>
      </div>

      <div
        #actionPanelWrap
        class="actionPanel"
        [ngStyle]="actionPanelStyle"
        [style.background-color]="actionPanelBgColor"
        [style.width]="checkIfOpen() ? actionPanelOpenPercent + '%' : ''"
        [attr.aria-hidden]="checkIfOpen() === true ? false : true"
      >
        <ng-content select="[actionPanel]"></ng-content>
      </div>
    </div>
  `, styles: [":host{font-family:Interstate_Light,sans-serif;font-size:15px;padding:5px 0}:host .ddl-drawer-wrapper{display:flex;width:145%}:host .ddl-drawer-wrapper .contentPanel{padding:6px;background-color:#fff}:host .ddl-drawer-wrapper .contentPanel:hover{background-color:#f4f4f4}:host .ddl-drawer-wrapper .actionPanel{background-color:#ebebeb}@media only screen and (max-width: 767px){:host .cds-drawer-wrapper{width:159%}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i2$1.MediaMatcher }], propDecorators: { contentPanelWrap: [{
                type: ViewChild,
                args: ['contentPanelWrap']
            }], actionPanelWrap: [{
                type: ViewChild,
                args: ['actionPanelWrap']
            }], contentPanelStyle: [{
                type: Input
            }], actionPanelStyle: [{
                type: Input
            }], actionPanelBgColor: [{
                type: Input
            }], contentPanelBgColor: [{
                type: Input
            }], openDrawer: [{
                type: Input
            }], direction: [{
                type: Input
            }], contentPanelOpenPercent: [{
                type: Input
            }], actionPanelOpenPercent: [{
                type: Input
            }], drawerOpenedAnnouncement: [{
                type: Input
            }], drawerClosedAnnouncement: [{
                type: Input
            }], turnOffAnnouncement: [{
                type: Input
            }], drawerClick: [{
                type: Output
            }] } });

class Drawer {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Drawer, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: Drawer, declarations: [DrawerComponent], imports: [CommonModule], exports: [DrawerComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Drawer, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: Drawer, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [DrawerComponent],
                    imports: [CommonModule],
                    exports: [DrawerComponent]
                }]
        }] });

let nextUniqueId = 0;
/**
 * @Component: Pagination
 * @description Automated generation of consecutive numbers to
 * identify the sequential order of pages
 */
class CdsPaginationItem {
    constructor(number, isActive, isEllipsis, ellipsisPosition, hideEllipsis, id, ariaLabel) {
        this.ellipsisPosition = 'alter-front | alter-back | default';
        this.number = number;
        this.isActive = isActive;
        this.isEllipsis = isEllipsis;
        this.ellipsisPosition = ellipsisPosition;
        this.hideEllipsis = hideEllipsis;
        this.id = id;
        this.ariaLabel = ariaLabel;
    }
}
class Ellipses {
    constructor(front, back) {
        this.front = front;
        this.back = back;
    }
}
class CdsPagination {
    get activePageID() {
        return this._id;
    }
    set activePageID(value) {
        this._id = value || this._uid;
    }
    constructor(_announcer, el, _ngZone, _navigator) {
        this._announcer = _announcer;
        this.el = el;
        this._ngZone = _ngZone;
        this._navigator = _navigator;
        this.visibleEllipsis = new CdsPaginationItem(null, null, true, 'default', false, null, null);
        this.hiddenEllipsis = new CdsPaginationItem(null, null, true, 'default', true, null, null);
        this.mobileFrontHiddenEllipsis = new CdsPaginationItem(null, null, true, 'alter-front', true, null, null);
        this.mobileEndHiddenEllipsis = new CdsPaginationItem(null, null, true, 'alter-back', true, null, null);
        this.pages = [];
        this.disablePrevButton = false;
        this.disableNextButton = false;
        this._isNavButtonClicked = false;
        /**
         * @property ariaLabelPrefix
         * @description Pagination label prefix string for screen reader
         * @return string
         */
        this.ariaLabelPrefix = 'Page';
        this.alternateText = 'of';
        this.showAlternateLayout = false;
        /**
         * @property prevAriaLabel
         * @description Previous button label string for screen reader
         * @return string
         */
        this.prevAriaLabel = 'Previous Page';
        /**
         * @property nextAriaLabel
         * @description Next button label string for screen reader
         * @return string
         */
        this.nextAriaLabel = 'Next Page';
        this._uid = `cds-pagination-${nextUniqueId++}`;
        this.morepgDesc = ``;
        /**
         * @name activePageNumberChange
         * @description Emitted when a page in the pagination is active
         * @return Object
         * @Type: EventEmitter<number>
         */
        this.activePageNumberChange = new EventEmitter();
        /**
         * @property showNavigationButtons
         * @description Option to show previous/next button when page is less than 10
         * @Type: boolean
         */
        this.showNavigationButtons = false;
    }
    ngOnInit() {
        this.activePageID = this.activePageID;
    }
    ngOnChanges() {
        this.generatePages();
    }
    setPgEllipses(pgnum) {
        const internalPageItems = this.pages;
        const index = internalPageItems.findIndex(page => page.number == pgnum);
        if (index > -1) {
            if (pgnum < this.pageCount) {
                if (index > 2) {
                    if (internalPageItems[index + 1].number == null) {
                        this.morepgDesc =
                            'pages ' +
                                (pgnum + 1) +
                                ' to ' +
                                (internalPageItems[index + 2].number - 1) +
                                ' are hidden';
                    }
                }
                else {
                    if (pgnum > 1) {
                        if (internalPageItems[index - 1].number == null) {
                            this.morepgDesc =
                                'pages 2 to ' +
                                    (internalPageItems[index].number - 1) +
                                    ' are hidden';
                        }
                    }
                    else if (pgnum == 1) {
                        this.morepgDesc =
                            'pages 2 to ' +
                                (internalPageItems[index + 2].number - 1) +
                                ' are hidden';
                    }
                }
            }
            else if (pgnum == this.pageCount) {
                if (internalPageItems[index - 1].number == null) {
                    this.morepgDesc =
                        'pages ' +
                            (internalPageItems[index - 2].number + 1) +
                            ' to ' +
                            (this.pageCount - 1) +
                            ' are hidden';
                }
            }
        }
    }
    generatePages() {
        if (this.pageCount <= 0) {
            return new Error('Pagination configuration not accounted for.');
        }
        this.pages = this.sequentialPages;
        this.disablePrevButton = this.activePageNumber === 1;
        this.disableNextButton = this.activePageNumber === +this.pageCount;
        const ellipses = this.paginationEllipses;
        this.pages = [
            this.firstPageItem,
            ellipses.front,
            ...this.internalPagesItem(this.pages),
            ellipses.back,
            this.lastPageItem
        ].filter(page => page != null);
    }
    get sequentialPages() {
        return Array.apply(null, { length: this.pageCount }).map((val, index) => new CdsPaginationItem(index + 1, // (display) number
        this.activePageNumber === index + 1, // isActive
        false, // isEllipsis
        'default', // ellipsisPosition
        false, // hideEllipsis
        `page-${index + 1}`, // id
        `${this.pageAriaLabel(this.activePageNumber === index + 1)} ${index +
            1} of ${this.pageCount}`));
    }
    isButtonAriaLabel() {
        if (this.activePageNumber === 1) {
            setTimeout(() => {
                this._announcer.announce('previous page button unavailable');
            }, 100);
        }
        if (this.activePageNumber == this.pageCount) {
            setTimeout(() => {
                this._announcer.announce('next page button unavailable');
            }, 100);
        }
    }
    get isButtonDisabledAndClicked() {
        return (this._isNavButtonClicked &&
            (this.activePageNumber === 1 || this.activePageNumber === +this.pageCount));
    }
    pageAriaLabel(isActive) {
        return isActive ? `current ${this.ariaLabelPrefix}` : this.ariaLabelPrefix;
    }
    hideNavButtons() {
        return this.pageCount < 10 && !this.showNavigationButtons;
    }
    get firstPageItem() {
        return this.pages[0];
    }
    get lastPageItem() {
        return this.pageCount > 1 ? this.pages[this.pageCount - 1] : null;
    }
    internalPagesItem(totalPages) {
        const internalPageItems = totalPages.slice(1, -1);
        if (totalPages.length < 10)
            return internalPageItems;
        else if (this.activePageNumber <= 7)
            return internalPageItems.filter(page => page.number <= 7);
        else if (this.isCurrentLocationGreaterThanPage7 &&
            this.isCurrentLocationLessThan7PagesAway) {
            return internalPageItems.splice(-6);
        }
        else if (this.isCurrentLocationGreaterThanPage7) {
            return internalPageItems.filter(page => page.number >= this.activePageNumber - 2 &&
                page.number <= this.activePageNumber + 2);
        }
        else
            throw new Error('Pagination configuration not accounted for.');
    }
    get paginationEllipses() {
        if (this.pageCount < 10)
            return new Ellipses(this.mobileFrontHiddenEllipsis, this.mobileEndHiddenEllipsis);
        else if (this.activePageNumber <= 7)
            return new Ellipses(this.hiddenEllipsis, this.visibleEllipsis);
        else if (this.isCurrentLocationGreaterThanPage7 &&
            this.isCurrentLocationLessThan7PagesAway)
            return new Ellipses(this.visibleEllipsis, this.hiddenEllipsis);
        else if (this.isCurrentLocationGreaterThanPage7)
            return new Ellipses(this.visibleEllipsis, this.visibleEllipsis);
        else
            throw new Error('Pagination configuration not accounted for.');
    }
    get isCurrentLocationGreaterThanPage7() {
        return this.activePageNumber > 7;
    }
    get isCurrentLocationLessThan7PagesAway() {
        return this.pageCount - this.activePageNumber < 7;
    }
    get isCurrentLocationLessThan3PagesAway() {
        return this.pageCount - this.activePageNumber < 3;
    }
    get isCurrentLocationGreaterThanPage3() {
        return this.activePageNumber > 3;
    }
    arrowClick(index) {
        const currentPage = this.activePageNumber + index;
        let announceString = this.showAlternateLayout ? `current ${this.ariaLabelPrefix} is ${currentPage} of ${this.pageCount}` : `current ${this.ariaLabelPrefix} is ${currentPage} of ${this.pageCount}`;
        if (currentPage < 1) {
            setTimeout(() => {
            }, 100);
            return;
        }
        else if (currentPage > this.pageCount) {
            setTimeout(() => {
            }, 100);
            return;
        }
        else {
            setTimeout(() => {
                this._announcer.announce(announceString);
            }, 100);
        }
        this.setActivePage(currentPage, true);
    }
    arrowFocus(index, pageLabel) {
        const currentPage = this.activePageNumber + index;
        if (currentPage >= 1 && currentPage <= this.pageCount) {
            setTimeout(() => {
                this._announcer.announce(pageLabel);
            }, 100);
        }
    }
    pageClick(currentPage) {
        this.setActivePage(currentPage);
        this.reCastFocus();
    }
    setActivePage(currentPage, isNavButtonClicked = false) {
        this.disableNextPrevbuttons(currentPage);
        this.activePageNumber = currentPage;
        this.activePageNumberChange.emit(this.activePageNumber);
        this._isNavButtonClicked = isNavButtonClicked;
        if ((!this.isAndroidDevice && !this.isiOSDevice) ||
            this._isNavButtonClicked) {
        }
    }
    announcePageCount() {
        return 'Pagination. The Total pages are ' + this.pageCount;
    }
    announceEllipses(ele) {
        let announcemessage = '';
        announcemessage = this.morepgDesc;
        return announcemessage;
    }
    /**
     * In Mobile, pagination only shows the first 3 pages or the last 3 pages (1,2,3...10 or 1...8,9,10)
     * If current page greater than 3 and less than the last 3 pages then pagination only shows the (1...5..10)
     * If there are more than 6 pages, show ellipses
     */
    hidePageInMobile(page) {
        if (this.pageCount <= 6)
            return false;
        return !this.visiblePages(page) && this.shouldHidePage(page);
    }
    displayHiddenEllipsis() {
        return (this.isCurrentLocationGreaterThanPage3 &&
            !this.isCurrentLocationLessThan3PagesAway);
    }
    visiblePages(page) {
        return (page === this.activePageNumber || page === 1 || page === +this.pageCount);
    }
    shouldHidePage(page) {
        return ((this.isCurrentLocationGreaterThanPage3 || page > 3) &&
            (!this.isCurrentLocationLessThan3PagesAway || this.pageCount - page >= 3));
    }
    hideNavButtonsMobile() {
        return this.pageCount < 7 && !this.showNavigationButtons;
    }
    hideEllipsis(isHiddenEllipsis, ellipsisPosition) {
        return this.pageCount < 10 && this.pageCount > 6
            ? this.hideEllipsIfUnder10Pages(isHiddenEllipsis, ellipsisPosition)
            : isHiddenEllipsis && !this.displayHiddenEllipsis();
    }
    hideEllipsIfUnder10Pages(isHiddenEllipsis, ellipsisPosition) {
        return (this.isCurrentLocationLessThan3PagesAway &&
            ellipsisPosition === 'alter-front') ||
            (!this.isCurrentLocationGreaterThanPage3 &&
                ellipsisPosition === 'alter-back')
            ? false
            : isHiddenEllipsis && !this.displayHiddenEllipsis();
    }
    get isAndroidDevice() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return /(android)/i.test(this._navigator.userAgent);
    }
    get isiOSDevice() {
        if (!this._navigator || !this._navigator.userAgent)
            return false;
        else
            return (!!this._navigator.platform &&
                /(iPad|iPhone|iPod)/i.test(this._navigator.userAgent));
    }
    reCastFocus() {
        onStable(this._ngZone, () => this.el.nativeElement.querySelector('.cds-pagination-active').focus());
    }
    disableNextPrevbuttons(currentPage) {
        let nxtbtnId = this._id + '-next-button';
        let prevbtnId = this._id + '-prev-button';
        if (currentPage == this.pageCount) {
            document.getElementById(nxtbtnId).tabIndex = -1;
        }
        else {
            document.getElementById(nxtbtnId).tabIndex = 0;
        }
        if (currentPage == 1) {
            document.getElementById(prevbtnId).tabIndex = -1;
        }
        else {
            document.getElementById(prevbtnId).tabIndex = 0;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPagination, deps: [{ token: i1$1.LiveAnnouncer }, { token: i0.ElementRef }, { token: i0.NgZone }, { token: NAVIGATOR }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsPagination, selector: "cds-pagination", inputs: { ariaLabelPrefix: "ariaLabelPrefix", alternateText: "alternateText", showAlternateLayout: "showAlternateLayout", prevAriaLabel: "prevAriaLabel", nextAriaLabel: "nextAriaLabel", activePageNumber: "activePageNumber", activePageID: "activePageID", showNavigationButtons: "showNavigationButtons", pageCount: "pageCount" }, outputs: { activePageNumberChange: "activePageNumberChange" }, host: { classAttribute: "cds-pagination" }, usesOnChanges: true, ngImport: i0, template: `
    <nav>
      <ul class="cds-pagination" role="presentation">
        <li>
          <!-- Previous Button-->
          <a
            href="javascript:;"
            [attr.aria-label]="!disablePrevButton ? prevAriaLabel: prevAriaLabel + ' ' + 'unavailable'"
            [attr.id]="_id + '-prev-button'"
            [attr.tabIndex]="!disablePrevButton ?0 : -1"
            [ngClass]="{
              'cds-pagination-previous': true,
              disabled: disablePrevButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(-1)"
            (focus) = "arrowFocus(-1,'previous page')"
          >
            <svg viewBox="0 0 38 38">
              <circle cx="19" cy="19" r="19" fill="#002a54"></circle>
              <path
                d="M24.4 8.15a.5.5 0 0 0-.71 0L13.89 18a1.51 1.51 0 0 0 0 2.13l9.79 9.73a.5.5 0 0 0 .71 0 .5.5 0 0 0 0-.7l-9.8-9.74a.51.51 0 0 1 0-.71l9.8-9.85a.5.5 0 0 0 0-.7"
                fill="#fff"
                fill-rule="evenodd"
              ></path>
            </svg>
          </a>
        </li>
        <ng-container *ngIf="showAlternateLayout">
        <li class="sr-only">
          Current {{ariaLabelPrefix}} is
          </li>
        <li >
          {{activePageNumber}} {{alternateText}} {{pageCount}} 
          </li> &nbsp;
          </ng-container>
        <ng-container *ngIf="!showAlternateLayout">
        <ng-container  *ngFor="let page of pages">
          <!-- Both Ellipses -->
          <li
            class="cds-page-ellipsis"
            *ngIf="page.isEllipsis"
            [ngClass]="{
              'hidden-lg': page.hideEllipsis,
              'hidden-xs': hideEllipsis(
                page.hideEllipsis,
                page.ellipsisPosition
              )
            }"
          >
            <div role="navigation" [attr.tabIndex]="0" [attr.aria-label]="announceEllipses(this)">
              &#8230;
            </div>
          </li>

          <!-- All Page Items-->
          <li
            class="page-item"
            *ngIf="page.number"
            (click)="pageClick(page.number)"
          >
            <a
              [attr.id]="page.isActive ? _id : null"
              href="javascript:;"
              [attr.aria-label]="(page.number) === 1? page.ariaLabel + ' first page' : (page.number) === pageCount? page.ariaLabel + ' last page' : page.ariaLabel"
              (focus)="setPgEllipses(page.number)"
              [ngClass]="{
                'cds-pagination-active': page.isActive,
                'hidden-xs': hidePageInMobile(page.number)
              }"
            >
              {{ page.number | number: '2.0' }}
            </a>
          </li>
        </ng-container>
            </ng-container>

        <!-- Next Button-->
        <li>
          <a
            href="javascript:;"
            [attr.aria-label]="!disableNextButton ? nextAriaLabel: nextAriaLabel + ' ' + 'unavailable'"
            [attr.id]="_id + '-next-button'"
            [attr.tabIndex]="0"
            [ngClass]="{
              'cds-pagination-next': true,
              disabled: disableNextButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(1)"
            (focus) = "arrowFocus(1,'next page')"
          >
            <svg viewBox="0 0 38 38">
              <circle cx="19" cy="19" r="19" fill="#002a54"></circle>
              <path
                d="M13.6 8.85l9.8 9.85a.51.51 0 0 1 0 .71l-9.8 9.74a.5.5 0 0 0 0 .7.5.5 0 0 0 .71 0l9.79-9.73a1.51 1.51 0 0 0 0-2.13l-9.8-9.84a.5.5 0 0 0-.71 0 .5.5 0 0 0 0 .7"
                fill="#fff"
                fill-rule="evenodd"
              ></path>
            </svg>
          </a>
        </li>
      </ul>
    </nav>
  `, isInline: true, styles: [".cds-pagination-previous circle:hover,.cds-pagination-previous circle:focus,.cds-pagination-next circle:hover,.cds-pagination-next circle:focus{fill:#002a54!important}.cds-pagination-previous svg,.cds-pagination-next svg{max-width:25px;max-height:25px}.cds-pagination-previous.disabled:focus-visible,.cds-pagination-next.disabled:focus-visible{outline:none}.cds-pagination-previous.disabled circle,.cds-pagination-next.disabled circle{fill:#767676!important}.cds-pagination-previous,.cds-pagination-next{border:none;margin:0;padding:0;height:25px;background-color:unset}.hidden-lg{display:none!important}.cds-pagination{display:inline-block;padding-left:0;margin:24px 0;border-radius:6px}.cds-pagination>li{float:left;display:inline;text-align:center;line-height:26px;margin:0;min-width:0px;padding-right:10px}.cds-pagination>li>a{min-height:25px;min-width:25px;position:relative;float:left;text-decoration:none;margin-left:-1px;font-family:Interstate_Light,sans-serif,Arial,sans-serif;font-size:16px;font-weight:300;letter-spacing:0;line-height:24px;text-align:center}@-moz-document url-prefix(){.cds-pagination>li>a:focus{outline:#333 solid 2px}}.cds-pagination>li>a.cds-pagination-active{font-family:Interstate_Light,sans-serif;color:#333;cursor:default;pointer-events:none}.cds-pagination>li>a .hidden-lg{display:none!important}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i2.DecimalPipe, name: "number" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPagination, decorators: [{
            type: Component,
            args: [{ selector: 'cds-pagination', host: { class: 'cds-pagination' }, template: `
    <nav>
      <ul class="cds-pagination" role="presentation">
        <li>
          <!-- Previous Button-->
          <a
            href="javascript:;"
            [attr.aria-label]="!disablePrevButton ? prevAriaLabel: prevAriaLabel + ' ' + 'unavailable'"
            [attr.id]="_id + '-prev-button'"
            [attr.tabIndex]="!disablePrevButton ?0 : -1"
            [ngClass]="{
              'cds-pagination-previous': true,
              disabled: disablePrevButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(-1)"
            (focus) = "arrowFocus(-1,'previous page')"
          >
            <svg viewBox="0 0 38 38">
              <circle cx="19" cy="19" r="19" fill="#002a54"></circle>
              <path
                d="M24.4 8.15a.5.5 0 0 0-.71 0L13.89 18a1.51 1.51 0 0 0 0 2.13l9.79 9.73a.5.5 0 0 0 .71 0 .5.5 0 0 0 0-.7l-9.8-9.74a.51.51 0 0 1 0-.71l9.8-9.85a.5.5 0 0 0 0-.7"
                fill="#fff"
                fill-rule="evenodd"
              ></path>
            </svg>
          </a>
        </li>
        <ng-container *ngIf="showAlternateLayout">
        <li class="sr-only">
          Current {{ariaLabelPrefix}} is
          </li>
        <li >
          {{activePageNumber}} {{alternateText}} {{pageCount}} 
          </li> &nbsp;
          </ng-container>
        <ng-container *ngIf="!showAlternateLayout">
        <ng-container  *ngFor="let page of pages">
          <!-- Both Ellipses -->
          <li
            class="cds-page-ellipsis"
            *ngIf="page.isEllipsis"
            [ngClass]="{
              'hidden-lg': page.hideEllipsis,
              'hidden-xs': hideEllipsis(
                page.hideEllipsis,
                page.ellipsisPosition
              )
            }"
          >
            <div role="navigation" [attr.tabIndex]="0" [attr.aria-label]="announceEllipses(this)">
              &#8230;
            </div>
          </li>

          <!-- All Page Items-->
          <li
            class="page-item"
            *ngIf="page.number"
            (click)="pageClick(page.number)"
          >
            <a
              [attr.id]="page.isActive ? _id : null"
              href="javascript:;"
              [attr.aria-label]="(page.number) === 1? page.ariaLabel + ' first page' : (page.number) === pageCount? page.ariaLabel + ' last page' : page.ariaLabel"
              (focus)="setPgEllipses(page.number)"
              [ngClass]="{
                'cds-pagination-active': page.isActive,
                'hidden-xs': hidePageInMobile(page.number)
              }"
            >
              {{ page.number | number: '2.0' }}
            </a>
          </li>
        </ng-container>
            </ng-container>

        <!-- Next Button-->
        <li>
          <a
            href="javascript:;"
            [attr.aria-label]="!disableNextButton ? nextAriaLabel: nextAriaLabel + ' ' + 'unavailable'"
            [attr.id]="_id + '-next-button'"
            [attr.tabIndex]="0"
            [ngClass]="{
              'cds-pagination-next': true,
              disabled: disableNextButton,
              'hidden-lg': hideNavButtons(),
              'hidden-xs': hideNavButtonsMobile()
            }"
            (click)="arrowClick(1)"
            (focus) = "arrowFocus(1,'next page')"
          >
            <svg viewBox="0 0 38 38">
              <circle cx="19" cy="19" r="19" fill="#002a54"></circle>
              <path
                d="M13.6 8.85l9.8 9.85a.51.51 0 0 1 0 .71l-9.8 9.74a.5.5 0 0 0 0 .7.5.5 0 0 0 .71 0l9.79-9.73a1.51 1.51 0 0 0 0-2.13l-9.8-9.84a.5.5 0 0 0-.71 0 .5.5 0 0 0 0 .7"
                fill="#fff"
                fill-rule="evenodd"
              ></path>
            </svg>
          </a>
        </li>
      </ul>
    </nav>
  `, changeDetection: ChangeDetectionStrategy.OnPush, styles: [".cds-pagination-previous circle:hover,.cds-pagination-previous circle:focus,.cds-pagination-next circle:hover,.cds-pagination-next circle:focus{fill:#002a54!important}.cds-pagination-previous svg,.cds-pagination-next svg{max-width:25px;max-height:25px}.cds-pagination-previous.disabled:focus-visible,.cds-pagination-next.disabled:focus-visible{outline:none}.cds-pagination-previous.disabled circle,.cds-pagination-next.disabled circle{fill:#767676!important}.cds-pagination-previous,.cds-pagination-next{border:none;margin:0;padding:0;height:25px;background-color:unset}.hidden-lg{display:none!important}.cds-pagination{display:inline-block;padding-left:0;margin:24px 0;border-radius:6px}.cds-pagination>li{float:left;display:inline;text-align:center;line-height:26px;margin:0;min-width:0px;padding-right:10px}.cds-pagination>li>a{min-height:25px;min-width:25px;position:relative;float:left;text-decoration:none;margin-left:-1px;font-family:Interstate_Light,sans-serif,Arial,sans-serif;font-size:16px;font-weight:300;letter-spacing:0;line-height:24px;text-align:center}@-moz-document url-prefix(){.cds-pagination>li>a:focus{outline:#333 solid 2px}}.cds-pagination>li>a.cds-pagination-active{font-family:Interstate_Light,sans-serif;color:#333;cursor:default;pointer-events:none}.cds-pagination>li>a .hidden-lg{display:none!important}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }, { type: i0.ElementRef }, { type: i0.NgZone }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [NAVIGATOR]
                }] }], propDecorators: { ariaLabelPrefix: [{
                type: Input
            }], alternateText: [{
                type: Input
            }], showAlternateLayout: [{
                type: Input
            }], prevAriaLabel: [{
                type: Input
            }], nextAriaLabel: [{
                type: Input
            }], activePageNumber: [{
                type: Input
            }], activePageID: [{
                type: Input
            }], activePageNumberChange: [{
                type: Output
            }], showNavigationButtons: [{
                type: Input
            }], pageCount: [{
                type: Input
            }] } });

const COMPONENTS$f = [CdsPagination];
class CdsPaginationModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPaginationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsPaginationModule, declarations: [CdsPagination], imports: [CommonModule], exports: [CdsPagination] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPaginationModule, providers: [NAVIGATOR_PROVIDER], imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsPaginationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    providers: [NAVIGATOR_PROVIDER],
                    declarations: [...COMPONENTS$f],
                    exports: [...COMPONENTS$f]
                }]
        }] });

class CdsProgress {
    set currentStep(stepNumber) {
        this._currentStep = stepNumber;
        this.updateStep();
    }
    // Based on the input will return color code for backgraound color of progress bar
    getBGC() {
        return this.setBarColor;
    }
    get currentStep() {
        return this._currentStep;
    }
    constructor(_liveAnnouncer) {
        this._liveAnnouncer = _liveAnnouncer;
        this.totalSteps = 2;
        this.gradient = false;
        this._currentStep = 1;
        this.currentStepChange = new EventEmitter();
    }
    prev() {
        this.currentStep -= 1;
    }
    next() {
        this.currentStep += 1;
    }
    currentStepLimit(stepNumber) {
        return Math.min(this.totalSteps, Math.max(0, stepNumber));
    }
    updateStep() {
        // Make sure totalSteps is valid
        this.totalSteps = Math.max(2, this.totalSteps);
        // Make sure currentStep is valid
        this._currentStep = this.currentStepLimit(this.currentStep);
        // Sets nextStep. Needs to be null if on last step.
        this.nextStep =
            this.currentStep >= this.totalSteps ? null : this.currentStep + 1;
        // Sets percent of progress bar
        this.stepPercent = this._currentStep == 0 ? 0 : Math.round((this.currentStep / this.totalSteps) * 100);
        this.currentStepChange.emit(this.currentStep);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgress, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsProgress, selector: "cds-progress", inputs: { totalSteps: "totalSteps", setBarColor: "setBarColor", currentStep: "currentStep", gradient: "gradient", barGradient: "barGradient" }, outputs: { currentStepChange: "currentStepChange" }, host: { classAttribute: "cds-progress" }, ngImport: i0, template: `
    <div  aria-live="assertive">
      <ng-content select="span.cds-progress-label"></ng-content>
      <ng-content></ng-content>
    </div>
    <div class="cds-progress">
      <div
        class="cds-progress-bar"
        [ngStyle]="gradient ? barGradient : ''"
        aria-hidden="true"
        role="presentation"
        [attr.aria-valuemin]="1"
        [attr.aria-valuemax]="totalSteps"
        [attr.aria-valuenow]="currentStep"
        [style.width.%]="stepPercent"
        [style.background-color]="getBGC()">
      </div>
    </div>
  `, isInline: true, styles: [":host .cds-progress{overflow:hidden;height:10px;margin-top:10px;margin-bottom:23px;border-radius:5px}:host .cds-progress-bar{float:left;width:0%;height:100%;font-size:.75rem;line-height:23px;color:#fff;text-align:center;border-radius:5px}:host .cds-progress{box-shadow:none}:host .cds-progress-bar{transition:none}::ng-deep span.cds-progress-label{font-size:21px;font-weight:390;letter-spacing:0;line-height:27px;margin-right:25px}\n"], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgress, decorators: [{
            type: Component,
            args: [{ selector: 'cds-progress', host: { class: 'cds-progress' }, changeDetection: ChangeDetectionStrategy.OnPush, template: `
    <div  aria-live="assertive">
      <ng-content select="span.cds-progress-label"></ng-content>
      <ng-content></ng-content>
    </div>
    <div class="cds-progress">
      <div
        class="cds-progress-bar"
        [ngStyle]="gradient ? barGradient : ''"
        aria-hidden="true"
        role="presentation"
        [attr.aria-valuemin]="1"
        [attr.aria-valuemax]="totalSteps"
        [attr.aria-valuenow]="currentStep"
        [style.width.%]="stepPercent"
        [style.background-color]="getBGC()">
      </div>
    </div>
  `, styles: [":host .cds-progress{overflow:hidden;height:10px;margin-top:10px;margin-bottom:23px;border-radius:5px}:host .cds-progress-bar{float:left;width:0%;height:100%;font-size:.75rem;line-height:23px;color:#fff;text-align:center;border-radius:5px}:host .cds-progress{box-shadow:none}:host .cds-progress-bar{transition:none}::ng-deep span.cds-progress-label{font-size:21px;font-weight:390;letter-spacing:0;line-height:27px;margin-right:25px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { totalSteps: [{
                type: Input
            }], setBarColor: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], gradient: [{
                type: Input
            }], barGradient: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }] } });

const COMPONENTS$e = [CdsProgress];
class CdsProgressModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressModule, declarations: [CdsProgress], imports: [CommonModule], exports: [CdsProgress] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$e],
                    exports: [...COMPONENTS$e]
                }]
        }] });

class CdsProgressBar {
    set percent(pcnt) {
        // Limit the percent between 0 and 100
        if (pcnt > 100) {
            console.warn('percent should not be above 100... setting to 100');
            this._percent = 100;
        }
        else if (pcnt < 0) {
            console.warn('percent should not be less than 0... setting to 0');
            this._percent = 0;
        }
        else {
            this._percent = pcnt;
        }
    }
    get percent() {
        return this._percent;
    }
    set announceText(announceTxt) {
        this._announceText = announceTxt;
        this.announce(announceTxt);
    }
    get announceText() {
        return this._announceText;
    }
    announce(txt) {
        this._liveAnnouncer.announce(txt);
    }
    constructor(_liveAnnouncer) {
        this._liveAnnouncer = _liveAnnouncer;
        this._percent = 50;
        this.gradient = false;
        this.currentStepChange = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Progress Bar <citi-progress-bar> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsProgressBar, selector: "cds-progress-bar", inputs: { percent: "percent", announceText: "announceText", setBarColor: "setBarColor", gradient: "gradient", barGradient: "barGradient" }, outputs: { currentStepChange: "currentStepChange" }, host: { classAttribute: "cds-progress" }, ngImport: i0, template: `
    <!-- <div aria-live="assertive"> -->
    <div>
      <ng-content select="span.cds-progress-label"></ng-content>
      <ng-content></ng-content>
    </div>
    <div class="cds-progress">
      <div
        class="cds-progress-bar"
        [ngStyle]="gradient ? barGradient : ''"
        aria-hidden="true"
        role="presentation"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="100"
        [attr.aria-valuenow]="percent"
        [style.width.%]="percent"
        [style.background-color]="setBarColor">
      </div>
    </div>
  `, isInline: true, styles: [":host .cds-progress{overflow:hidden;height:12px;margin-top:10px;margin-bottom:23px;background-color:#ccc;border-radius:80px}:host .cds-progress-bar{float:left;width:0%;height:12px;font-size:.75rem;line-height:23px;color:#ccc;text-align:center;background-color:#056dae;border-radius:80px}:host .cds-progress{box-shadow:none}:host .cds-progress-bar{transition:none}::ng-deep span.cds-progress-label{font-size:21px;font-weight:390;letter-spacing:0;line-height:27px;margin-right:25px}\n"], dependencies: [{ kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar, decorators: [{
            type: Component,
            args: [{ selector: 'cds-progress-bar', host: { class: 'cds-progress' }, changeDetection: ChangeDetectionStrategy.OnPush, template: `
    <!-- <div aria-live="assertive"> -->
    <div>
      <ng-content select="span.cds-progress-label"></ng-content>
      <ng-content></ng-content>
    </div>
    <div class="cds-progress">
      <div
        class="cds-progress-bar"
        [ngStyle]="gradient ? barGradient : ''"
        aria-hidden="true"
        role="presentation"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="100"
        [attr.aria-valuenow]="percent"
        [style.width.%]="percent"
        [style.background-color]="setBarColor">
      </div>
    </div>
  `, styles: [":host .cds-progress{overflow:hidden;height:12px;margin-top:10px;margin-bottom:23px;background-color:#ccc;border-radius:80px}:host .cds-progress-bar{float:left;width:0%;height:12px;font-size:.75rem;line-height:23px;color:#ccc;text-align:center;background-color:#056dae;border-radius:80px}:host .cds-progress{box-shadow:none}:host .cds-progress-bar{transition:none}::ng-deep span.cds-progress-label{font-size:21px;font-weight:390;letter-spacing:0;line-height:27px;margin-right:25px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { percent: [{
                type: Input
            }], announceText: [{
                type: Input
            }], setBarColor: [{
                type: Input
            }], gradient: [{
                type: Input
            }], barGradient: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }] } });

const COMPONENTS$d = [CdsProgressBar];
class CdsProgressBarModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBarModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBarModule, declarations: [CdsProgressBar], imports: [CommonModule], exports: [CdsProgressBar] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBarModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBarModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$d],
                    exports: [...COMPONENTS$d]
                }]
        }] });

class CdsProgressBar2 {
    // #region // -------- Inputs/Outputs ---------- //
    get bufferValue() {
        return this._bufferValue || 0;
    }
    set bufferValue(v) {
        // Limit the value between 0 and 100
        if (v > 100) {
            console.warn('bufferValue should not be above 100... setting to 100');
            this._bufferValue = 100;
        }
        else if (v < 0) {
            console.warn('bufferValue should not be less than 0... setting to 0');
            this._bufferValue = 0;
        }
        else {
            this._bufferValue = v;
            this.valueChange.emit(this._bufferValue);
        }
    }
    get value() {
        return this._value;
    }
    set value(v) {
        // Limit the value between 0 and 100
        if (v > 100) {
            console.warn('value should not be above 100... setting to 100');
            this._value = 100;
        }
        else if (v < 0) {
            console.warn('value should not be less than 0... setting to 0');
            this._value = 0;
        }
        else {
            this._value = v;
            this.valueChange.emit(this._value);
        }
    }
    set announceText(announceTxt) {
        this._announceText = announceTxt;
        this.announce(announceTxt);
    }
    get announceText() {
        return this._announceText;
    }
    // #endregion
    // #region // ------- Constructor/Lifecycle Hooks -------- //
    constructor(_liveAnnouncer) {
        this._liveAnnouncer = _liveAnnouncer;
        this._bufferValue = 0;
        this._value = 0;
        /**
         * @Property: circular
         * @Description: Toggles appearance from default linear to circular layout
         * @Type: Boolean
         */
        this.circular = false;
        /**
         * @Property: small
         * @Description: Toggles appearance to smaller version of linear and circular layouts
         * @Type: Boolean
         */
        this.small = false;
        this.linearHeight = 0;
        this.circularWidth = 0;
        this.label = '';
        this.trackColor = '';
        this.fillColor = '';
        this.trackGradient = '';
        this.fillGradient = '';
        this.inverse = false;
        this.ariaLabel = 'Progress Bar';
        // Leaving announce Input in until QE goes over it
        this._announceText = '';
        this.valueChange = new EventEmitter();
    }
    // #endregion
    // #region // ------------ Methods ------------- //
    _getBufferBarFlexBasis() {
        return `${this.bufferValue ? this.bufferValue : 100}`;
    }
    _getPrimaryBarHeight() {
        if (this.small) {
            return '4px';
        }
        else if (!this.small && this.linearHeight) {
            return `${this.linearHeight}px`;
        }
        else {
            return 'null';
        }
    }
    _getCircularWidth() {
        if (this.small) {
            return '.75';
        }
        else if (!this.small && this.circularWidth) {
            return `${this.circularWidth}`;
        }
        else {
            return 'null';
        }
    }
    announce(txt) {
        this._liveAnnouncer.announce(txt);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar2, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsProgressBar2, selector: "cds-progress-bar2", inputs: { bufferValue: "bufferValue", value: "value", circular: "circular", small: "small", linearHeight: "linearHeight", circularWidth: "circularWidth", label: "label", trackColor: "trackColor", fillColor: "fillColor", trackGradient: "trackGradient", fillGradient: "fillGradient", inverse: "inverse", ariaLabel: "ariaLabel", announceText: "announceText" }, outputs: { valueChange: "valueChange" }, host: { attributes: { "role": "progressbar", "aria-valuemin": "0", "aria-valuemax": "100", "tabindex": "-1" }, properties: { "attr.aria-valuenow": "value", "attr.aria-label": "ariaLabel" }, classAttribute: "cds-progress2" }, ngImport: i0, template: `
    <!-- Linear -->
    <div *ngIf="!circular" class="cds-linear-progress" [ngClass]="{'inverse': inverse}">
      <div *ngIf="label" class="cds-linear-progress-label" [innerHtml]="label"></div>
      <div class="cds-linear-progress-buffer"
        [style.height]="_getPrimaryBarHeight()"
        [style.width.%]="_getBufferBarFlexBasis()"
        [style.background-color]="trackColor"
        [style.background]="trackGradient">
        <div
          class="cds-linear-progress-bar"
          [ngClass]="{'cds-linear-progress-inverse-fill': inverse}"
          aria-hidden="true"
          role="presentation"
          [style.width.%]="value"
          [style.height]="_getPrimaryBarHeight()"
          [style.background-color]="fillColor"
          [style.background]="fillGradient">
        </div>
      </div>
    </div>
    <!-- Circular -->
    <div *ngIf="circular" class="cds-circular-progress-wrapper" [ngClass]="{'inverse': inverse}">
      <div *ngIf="label" class="cds-circular-progress-label" [innerHtml]="label"></div>
      <div class="cds-circular-progress">
        <svg viewBox="0 0 36 36" class="cds-circular-progress-svg">
          <path
            class="cds-circular-progress-bg"
            [style.stroke]="trackColor"
            [style.stroke-width]="_getCircularWidth()"
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            [ngClass]="{'invisible': value <= 0}"
            class="cds-circular-progress-value"
            [style.stroke-dasharray]="value + ', 100'"
            [style.stroke]="fillColor"
            [style.stroke-width]="_getCircularWidth()"
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
          />
        </svg>
      </div>
    </div>
  `, isInline: true, styles: [":host .cds-linear-progress{display:flex;flex-direction:row;justify-content:flex-start;align-content:flex-start;align-items:center}:host .cds-linear-progress-buffer{position:relative;width:100%;height:100%;overflow:hidden;background-color:#ccc;border-radius:80px}:host .cds-linear-progress-bar{width:0%;height:12px;font-size:.75rem;line-height:24px;color:#ccc;text-align:center;background-color:#056dae;border-radius:80px}:host .cds-circular-progress-wrapper{display:flex;flex-direction:column;justify-content:flex-start;align-content:flex-start;align-items:center}:host .cds-circular-progress{align-self:auto;flex:0 1 auto;width:192px;height:192px}:host .cds-circular-progress-svg{display:block;max-height:250px}:host .cds-circular-progress-bg{fill:none;stroke:#ccc;stroke-width:2.25}:host .cds-circular-progress-value{fill:none;stroke:#056dae;stroke-linecap:round;stroke-width:2.25}@keyframes progress{0%{stroke-dasharray:0 100}}:host .cds-linear-progress-label{flex:0 1 auto;align-self:auto;font-family:Interstate,sans-serif;font-size:16px;font-style:italic;line-height:24px;font-weight:300;margin-right:24px;color:#666}:host .cds-circular-progress-label{flex:0 1 auto;align-self:auto;font-family:Interstate,sans-serif;font-size:16px;font-style:italic;font-weight:300;line-height:24px;margin-bottom:3px;color:#666}:host .inverse .cds-linear-progress-buffer{background-color:#ffffff4d}:host .inverse .cds-linear-progress-bar{background-color:#fff}:host .inverse .cds-circular-progress-bg{stroke:#ffffff4d}:host .inverse .cds-circular-progress-value{stroke:#fff}:host .inverse .cds-linear-progress-label{color:#ffffff4d}:host .inverse .cds-circular-progress-label{color:#ffffff4d}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar2, decorators: [{
            type: Component,
            args: [{ selector: 'cds-progress-bar2', host: {
                        class: 'cds-progress2',
                        'role': 'progressbar',
                        'aria-valuemin': '0',
                        'aria-valuemax': '100',
                        '[attr.aria-valuenow]': 'value',
                        '[attr.aria-label]': 'ariaLabel',
                        'tabindex': '-1'
                    }, changeDetection: ChangeDetectionStrategy.OnPush, template: `
    <!-- Linear -->
    <div *ngIf="!circular" class="cds-linear-progress" [ngClass]="{'inverse': inverse}">
      <div *ngIf="label" class="cds-linear-progress-label" [innerHtml]="label"></div>
      <div class="cds-linear-progress-buffer"
        [style.height]="_getPrimaryBarHeight()"
        [style.width.%]="_getBufferBarFlexBasis()"
        [style.background-color]="trackColor"
        [style.background]="trackGradient">
        <div
          class="cds-linear-progress-bar"
          [ngClass]="{'cds-linear-progress-inverse-fill': inverse}"
          aria-hidden="true"
          role="presentation"
          [style.width.%]="value"
          [style.height]="_getPrimaryBarHeight()"
          [style.background-color]="fillColor"
          [style.background]="fillGradient">
        </div>
      </div>
    </div>
    <!-- Circular -->
    <div *ngIf="circular" class="cds-circular-progress-wrapper" [ngClass]="{'inverse': inverse}">
      <div *ngIf="label" class="cds-circular-progress-label" [innerHtml]="label"></div>
      <div class="cds-circular-progress">
        <svg viewBox="0 0 36 36" class="cds-circular-progress-svg">
          <path
            class="cds-circular-progress-bg"
            [style.stroke]="trackColor"
            [style.stroke-width]="_getCircularWidth()"
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
          />
          <path
            [ngClass]="{'invisible': value <= 0}"
            class="cds-circular-progress-value"
            [style.stroke-dasharray]="value + ', 100'"
            [style.stroke]="fillColor"
            [style.stroke-width]="_getCircularWidth()"
            d="M18 2.0845
              a 15.9155 15.9155 0 0 1 0 31.831
              a 15.9155 15.9155 0 0 1 0 -31.831"
          />
        </svg>
      </div>
    </div>
  `, styles: [":host .cds-linear-progress{display:flex;flex-direction:row;justify-content:flex-start;align-content:flex-start;align-items:center}:host .cds-linear-progress-buffer{position:relative;width:100%;height:100%;overflow:hidden;background-color:#ccc;border-radius:80px}:host .cds-linear-progress-bar{width:0%;height:12px;font-size:.75rem;line-height:24px;color:#ccc;text-align:center;background-color:#056dae;border-radius:80px}:host .cds-circular-progress-wrapper{display:flex;flex-direction:column;justify-content:flex-start;align-content:flex-start;align-items:center}:host .cds-circular-progress{align-self:auto;flex:0 1 auto;width:192px;height:192px}:host .cds-circular-progress-svg{display:block;max-height:250px}:host .cds-circular-progress-bg{fill:none;stroke:#ccc;stroke-width:2.25}:host .cds-circular-progress-value{fill:none;stroke:#056dae;stroke-linecap:round;stroke-width:2.25}@keyframes progress{0%{stroke-dasharray:0 100}}:host .cds-linear-progress-label{flex:0 1 auto;align-self:auto;font-family:Interstate,sans-serif;font-size:16px;font-style:italic;line-height:24px;font-weight:300;margin-right:24px;color:#666}:host .cds-circular-progress-label{flex:0 1 auto;align-self:auto;font-family:Interstate,sans-serif;font-size:16px;font-style:italic;font-weight:300;line-height:24px;margin-bottom:3px;color:#666}:host .inverse .cds-linear-progress-buffer{background-color:#ffffff4d}:host .inverse .cds-linear-progress-bar{background-color:#fff}:host .inverse .cds-circular-progress-bg{stroke:#ffffff4d}:host .inverse .cds-circular-progress-value{stroke:#fff}:host .inverse .cds-linear-progress-label{color:#ffffff4d}:host .inverse .cds-circular-progress-label{color:#ffffff4d}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { bufferValue: [{
                type: Input
            }], value: [{
                type: Input
            }], circular: [{
                type: Input
            }], small: [{
                type: Input
            }], linearHeight: [{
                type: Input
            }], circularWidth: [{
                type: Input
            }], label: [{
                type: Input
            }], trackColor: [{
                type: Input
            }], fillColor: [{
                type: Input
            }], trackGradient: [{
                type: Input
            }], fillGradient: [{
                type: Input
            }], inverse: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], announceText: [{
                type: Input
            }], valueChange: [{
                type: Output
            }] } });

const COMPONENTS$c = [CdsProgressBar2];
class CdsProgressBar2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar2Module, declarations: [CdsProgressBar2], imports: [CommonModule], exports: [CdsProgressBar2] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar2Module, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressBar2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$c],
                    exports: [...COMPONENTS$c]
                }]
        }] });

class CdsStatusTracker {
    /**
     * @warning totalSteps needs to be higher than currentStep to initialize correctly
     */
    set totalSteps(stepNumber) {
        this._totalSteps = stepNumber;
        this.totalStepArray = [];
        for (let i = 0; i < this.totalSteps; i++) {
            this.totalStepArray[i] = i;
        }
    }
    get totalSteps() {
        return this._totalSteps;
    }
    /**
      Input property currentStep
     */
    set currentStep(stepNumber) {
        this._currentStep = stepNumber;
        this.updateStep();
    }
    get currentStep() {
        return this._currentStep;
    }
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
        this.currentStepArray = [];
        this.totalStepArray = [];
        this.stepLabels = [];
        this.isResponsive = false;
        this._currentStep = 0;
        this.currentStepChange = new EventEmitter();
        this.totalStepsChange = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Status Tracker <citi-status-tracker> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    currentStepLimit(stepNumber) {
        return Math.min(this.totalSteps, Math.max(1, stepNumber));
    }
    updateStep() {
        for (let i = 0; i < this._currentStep; i++) {
            this.currentStepArray[i] = i;
        }
        // Make sure totalSteps is valid
        this.totalSteps = Math.max(2, this.totalSteps);
        // Make sure currentStep is valid
        this._currentStep = this.currentStepLimit(this.currentStep);
        // Emit Change
        this.currentStepChange.emit(this.currentStep);
        this.totalStepsChange.emit(this.totalSteps);
        this.totalStepsString = 'list of ' + this.totalSteps + ' steps';
    }
    getTotalStepsString() {
        this.totalStepsString = 'list of ' + this.totalSteps + ' steps';
        return this.totalStepsString;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsStatusTracker, selector: "cds-status-tracker:not(.3Q21)", inputs: { totalSteps: "totalSteps", currentStep: "currentStep", stepLabels: "stepLabels", isResponsive: "isResponsive" }, outputs: { currentStepChange: "currentStepChange", totalStepsChange: "totalStepsChange" }, host: { classAttribute: "cds-status-tracker" }, ngImport: i0, template: "<div\n      role='tablist'\n      class=\"cds-progress-container\"\n      [attr.aria-label]=\"'Step '+currentStep+ ' of ' + totalSteps + ' steps. '+stepLabels[currentStep-1]\"\n      [ngClass]=\"{'responsive' : isResponsive}\"\n    >\n      <span class=\"sr-only\">{{ getTotalStepsString() }}</span>\n      <span class=\"cds-status-tracker\"  *ngFor=\"let i of totalStepArray\">\n        <span  class=\"cds-circle-tick\" *ngIf=\"i < currentStep\">\n        <span\n\n            [attr.aria-label]=\"\n              'Step' + (i + 1) + ' ' +stepLabels[i] +' completed'\n            \"\n            class=\"cds-span\"\n            role='tab'\n          >\n          <div class=text>\n            <div class=step>Step {{ i + 1 }}</div>\n            <div class=label>{{i < currentStep ? stepLabels[i] : ''}}</div>\n        </div>\n\n          <svg\n            xmlns=\"http://www.w3.org/2000/svg\"\n            viewBox=\"-2 -2 30 30\"\n            class=\"cds-svg\"\n          >\n            <circle cx=\"12.5\" cy=\"12.5\" r=\"12.5\" />\n            <image height=\"25\" width=\"25\" href=\"./cds-assets/images/checkmark.png\"/>\n          </svg>\n\n            <span *ngIf=\"currentStep == i + 1\" class=\"sr-only\">\n              current\n            </span>\n            <span *ngIf=\"i+1<currentStep\" class=\"sr-only\">\n              completed\n            </span>\n          </span>\n        </span>\n        <span\n          *ngIf=\"i < currentStep && i != totalSteps - 1\"\n          class=\"cds-line\"\n          [ngClass]=\"{'filled' : i < currentStep-1}\"\n        ></span>\n        <span class=\"cds-circle-tick\" *ngIf=\"i >= currentStep\">\n        <span [attr.aria-label]=\"i+1==currentStep ? 'Step' + (i + 1) + ' current': 'Step' + (i + 1)\" class=\"cds-span\" role=\"tab\">\n            <div class=text>\n                <div class=step>Step {{ i + 1 }}</div>\n                <div class=label>{{i < currentStep ? stepLabels[i] : ''}}</div>\n            </div>\n\n          <svg\n            xmlns=\"http://www.w3.org/2000/svg\"\n            viewBox=\"-2 -2 30 30\"\n            class=\"cds-svg\"\n          >\n            <circle\n              cx=\"12.5\"\n              cy=\"12.5\"\n              r=\"12.5\"\n              fill=\"#999999\"\n              stroke=\"#999999\"\n              stroke-width=\"1\"\n            />\n          </svg>\n\n        </span>\n    </span>\n        <span\n          *ngIf=\"i >= currentStep && i != totalSteps - 1\"\n          class=\"cds-line\"\n        ></span>\n      </span>\n    </div>", styles: [":host{width:100%}.cds-progress-container{display:flex;align-items:flex-end}.cds-circle-tick{width:min-content;display:flex}.cds-svg{height:24px;display:flex;z-index:2}.cds-line{margin:11px -20px;align-self:flex-end;padding:0;flex-grow:1;box-sizing:border-box;height:1px;border-top:1px dashed #999999}.cds-span{display:flex;flex:1 1 60px;flex-direction:column;align-items:center;text-align:center;align-self:flex-end}.cds-span .step{font-size:12px;font-family:Interstate_Bold;white-space:nowrap}.cds-span .label{font-size:12px;font-family:Interstate_Regular}.check{background-size:20px 30px;stroke:#fff;stroke-width:2px}.cds-progress-container.responsive{flex-direction:column;width:min-content;align-items:flex-start}.cds-progress-container.responsive .cds-svg{width:25px}.cds-progress-container.responsive .cds-status-tracker{flex-direction:column;align-items:center;justify-content:left}.cds-progress-container.responsive .cds-span{flex-direction:row-reverse;align-items:flex-start;flex:0 1 auto}.cds-progress-container.responsive .cds-circle-tick{width:160px}.cds-progress-container.responsive .cds-line{width:1px;display:block;height:50px;margin:-20px 11px -2px;border-top:none;align-self:flex-start}.cds-progress-container.responsive .cds-line.filled{border-top:none}.cds-progress-container.responsive .label{text-align:left}.cds-progress-container.responsive .step{text-align:left;margin-top:4px}.cds-progress-container.responsive .text{margin-left:5px}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker, decorators: [{
            type: Component,
            args: [{ selector: 'cds-status-tracker:not(.3Q21)', host: { class: 'cds-status-tracker' }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div\n      role='tablist'\n      class=\"cds-progress-container\"\n      [attr.aria-label]=\"'Step '+currentStep+ ' of ' + totalSteps + ' steps. '+stepLabels[currentStep-1]\"\n      [ngClass]=\"{'responsive' : isResponsive}\"\n    >\n      <span class=\"sr-only\">{{ getTotalStepsString() }}</span>\n      <span class=\"cds-status-tracker\"  *ngFor=\"let i of totalStepArray\">\n        <span  class=\"cds-circle-tick\" *ngIf=\"i < currentStep\">\n        <span\n\n            [attr.aria-label]=\"\n              'Step' + (i + 1) + ' ' +stepLabels[i] +' completed'\n            \"\n            class=\"cds-span\"\n            role='tab'\n          >\n          <div class=text>\n            <div class=step>Step {{ i + 1 }}</div>\n            <div class=label>{{i < currentStep ? stepLabels[i] : ''}}</div>\n        </div>\n\n          <svg\n            xmlns=\"http://www.w3.org/2000/svg\"\n            viewBox=\"-2 -2 30 30\"\n            class=\"cds-svg\"\n          >\n            <circle cx=\"12.5\" cy=\"12.5\" r=\"12.5\" />\n            <image height=\"25\" width=\"25\" href=\"./cds-assets/images/checkmark.png\"/>\n          </svg>\n\n            <span *ngIf=\"currentStep == i + 1\" class=\"sr-only\">\n              current\n            </span>\n            <span *ngIf=\"i+1<currentStep\" class=\"sr-only\">\n              completed\n            </span>\n          </span>\n        </span>\n        <span\n          *ngIf=\"i < currentStep && i != totalSteps - 1\"\n          class=\"cds-line\"\n          [ngClass]=\"{'filled' : i < currentStep-1}\"\n        ></span>\n        <span class=\"cds-circle-tick\" *ngIf=\"i >= currentStep\">\n        <span [attr.aria-label]=\"i+1==currentStep ? 'Step' + (i + 1) + ' current': 'Step' + (i + 1)\" class=\"cds-span\" role=\"tab\">\n            <div class=text>\n                <div class=step>Step {{ i + 1 }}</div>\n                <div class=label>{{i < currentStep ? stepLabels[i] : ''}}</div>\n            </div>\n\n          <svg\n            xmlns=\"http://www.w3.org/2000/svg\"\n            viewBox=\"-2 -2 30 30\"\n            class=\"cds-svg\"\n          >\n            <circle\n              cx=\"12.5\"\n              cy=\"12.5\"\n              r=\"12.5\"\n              fill=\"#999999\"\n              stroke=\"#999999\"\n              stroke-width=\"1\"\n            />\n          </svg>\n\n        </span>\n    </span>\n        <span\n          *ngIf=\"i >= currentStep && i != totalSteps - 1\"\n          class=\"cds-line\"\n        ></span>\n      </span>\n    </div>", styles: [":host{width:100%}.cds-progress-container{display:flex;align-items:flex-end}.cds-circle-tick{width:min-content;display:flex}.cds-svg{height:24px;display:flex;z-index:2}.cds-line{margin:11px -20px;align-self:flex-end;padding:0;flex-grow:1;box-sizing:border-box;height:1px;border-top:1px dashed #999999}.cds-span{display:flex;flex:1 1 60px;flex-direction:column;align-items:center;text-align:center;align-self:flex-end}.cds-span .step{font-size:12px;font-family:Interstate_Bold;white-space:nowrap}.cds-span .label{font-size:12px;font-family:Interstate_Regular}.check{background-size:20px 30px;stroke:#fff;stroke-width:2px}.cds-progress-container.responsive{flex-direction:column;width:min-content;align-items:flex-start}.cds-progress-container.responsive .cds-svg{width:25px}.cds-progress-container.responsive .cds-status-tracker{flex-direction:column;align-items:center;justify-content:left}.cds-progress-container.responsive .cds-span{flex-direction:row-reverse;align-items:flex-start;flex:0 1 auto}.cds-progress-container.responsive .cds-circle-tick{width:160px}.cds-progress-container.responsive .cds-line{width:1px;display:block;height:50px;margin:-20px 11px -2px;border-top:none;align-self:flex-start}.cds-progress-container.responsive .cds-line.filled{border-top:none}.cds-progress-container.responsive .label{text-align:left}.cds-progress-container.responsive .step{text-align:left;margin-top:4px}.cds-progress-container.responsive .text{margin-left:5px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { totalSteps: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], stepLabels: [{
                type: Input
            }], isResponsive: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }], totalStepsChange: [{
                type: Output
            }] } });

class CdsStatusTracker3Q21 {
    /**
     * @warning totalSteps needs to be higher than currentStep to initialize correctly
     */
    set totalSteps(stepNumber) {
        this._totalSteps = stepNumber;
        this.totalStepArray = [];
        for (let i = 0; i < this.totalSteps; i++) {
            this.totalStepArray[i] = i;
        }
    }
    get totalSteps() {
        return this._totalSteps;
    }
    /**
      Input property currentStep
     */
    set currentStep(stepNumber) {
        this._currentStep = stepNumber;
        this.updateStep();
    }
    get currentStep() {
        return this._currentStep;
    }
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
        this.currentStepArray = [];
        this.totalStepArray = [];
        this.stepLabels = [];
        this.stepDescription = [];
        this.isVertical = false;
        this._currentStep = 0;
        this.currentStepChange = new EventEmitter();
        this.totalStepsChange = new EventEmitter();
    }
    currentStepLimit(stepNumber) {
        return Math.min(this.totalSteps, Math.max(1, stepNumber));
    }
    updateStep() {
        for (let i = 0; i < this._currentStep; i++) {
            this.currentStepArray[i] = i;
        }
        // Make sure totalSteps is valid
        this.totalSteps = Math.max(2, this.totalSteps);
        // Make sure currentStep is valid
        this._currentStep = this.currentStepLimit(this.currentStep);
        // Emit Change
        this.currentStepChange.emit(this.currentStep);
        this.totalStepsChange.emit(this.totalSteps);
        this.totalStepsString = 'list of ' + this.totalSteps + ' steps';
    }
    getTotalStepsString() {
        this.totalStepsString = 'list of ' + this.totalSteps + ' steps';
        return this.totalStepsString;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker3Q21, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsStatusTracker3Q21, selector: "cds-status-tracker.3Q21", inputs: { totalSteps: "totalSteps", currentStep: "currentStep", stepLabels: "stepLabels", stepDescription: "stepDescription", isVertical: "isVertical" }, outputs: { currentStepChange: "currentStepChange", totalStepsChange: "totalStepsChange" }, host: { classAttribute: "cds-status-tracker" }, ngImport: i0, template: "<div class=\"cds-status-tracker-container\" [ngClass]=\"{'vertical' : isVertical}\">\n  <span class=\"sr-only\">{{ getTotalStepsString() }}</span>\n  <span class=\"cds-status-tracker-section\" *ngFor=\"let i of totalStepArray\">\n    <span class=\"cds-circle-tick\" *ngIf=\"i < currentStep\">\n      <span class=\"cds-span completed\">\n        <div class=\"text\">\n          <div class=\"step\">\n            {{i < currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"stepdesc\">\n            {{i >= currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"label\">{{i < currentStep ? stepLabels[i] : ''}}</div>\n        </div>\n\n        <svg\n          xmlns=\"http://www.w3.org/2000/svg\"\n          viewBox=\"-2 -2 30 30\"\n          class=\"cds-svg\"\n          role=\"presentation\"\n          aria-hidden=\"true\"\n        >\n          <circle cx=\"12.5\" cy=\"12.5\" r=\"12.5\" />\n          <image\n            height=\"25\"\n            width=\"25\"\n            href=\"./cds-assets/images/checkmark.png\"\n          />\n        </svg>\n        <span *ngIf=\"currentStep == i + 1\" class=\"sr-only\">\n          current\n        </span>\n        <span *ngIf=\"i+1<currentStep\" class=\"sr-only\">\n          completed\n        </span>\n      </span>\n    </span>\n    <span\n      *ngIf=\"i < currentStep && i != totalSteps - 1\"\n      class=\"cds-line\"\n      [ngClass]=\"{'filled' : i < currentStep-1}\"\n    ></span>\n    <span class=\"cds-circle-tick\" *ngIf=\"i >= currentStep\">\n      <span class=\"cds-span\">\n        <div class=\"text\">\n          <div class=\"step\">\n            {{i < currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"stepdesc\">\n            {{i >= currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"label\">{{i < currentStep ? stepLabels[i] : ''}}</div>\n        </div>\n\n        <svg\n          xmlns=\"http://www.w3.org/2000/svg\"\n          viewBox=\"-2 -2 30 30\"\n          class=\"cds-svg-inactive\"\n          role=\"presentation\"\n          aria-hidden=\"true\"\n        >\n          <circle\n            cx=\"12.5\"\n            cy=\"12.5\"\n            r=\"12.5\"\n            fill=\"#CCCCCC\"\n            stroke=\"#CCCCCC\"\n            stroke-width=\"1\"\n          />\n        </svg>\n      </span>\n    </span>\n    <span\n      *ngIf=\"i >= currentStep && i != totalSteps - 1\"\n      class=\"cds-line\"\n    ></span>\n  </span>\n</div>\n", styles: [":host{width:100%}.cds-status-tracker-container{display:flex}.cds-status-tracker-section{display:flex;flex-grow:1}.cds-status-tracker-section:last-child{flex-grow:0}.cds-circle-tick{width:min-content;display:flex}.cds-svg{height:24px;display:flex;z-index:2}.cds-svg-inactive{height:16px;display:flex;z-index:2;margin-bottom:4px;margin-right:5px}.cds-line{margin:11px -33px 11px -38px;align-self:flex-end;padding:0;flex-grow:1;box-sizing:border-box;height:1px;border-top:1px dashed #cccccc}.cds-span{display:flex;flex:1 1 60px;flex-direction:column;align-items:center;text-align:center}.cds-span svg{margin-top:auto}.cds-span .step{font-size:12px;font-family:Interstate_Bold;font-weight:700;letter-spacing:0;line-height:18px}.cds-span .stepdesc{font-size:12px;font-family:Interstate_Bold;color:#666;font-weight:700;letter-spacing:0;line-height:18px;width:55px}.cds-span .label{font-size:12px;font-family:Interstate_Light}.cds-status-tracker-container .cds-status-tracker-section:nth-child(2) .cds-span{align-items:flex-start;text-align:left}.cds-status-tracker-container .cds-status-tracker-section:last-child .cds-span{align-items:flex-end;text-align:right}.check{background-size:20px 30px;stroke:#fff;stroke-width:2px}.cds-status-tracker-container.vertical{flex-direction:column;width:min-content;align-items:flex-start}.cds-status-tracker-container.vertical .cds-svg{width:24px}.cds-status-tracker-container.vertical .cds-svg-inactive{width:16px;margin-left:4px;margin-bottom:-2px}.cds-status-tracker-container.vertical .cds-status-tracker-section{flex-direction:column;align-items:center;justify-content:left}.cds-status-tracker-container.vertical .cds-span{flex-direction:row-reverse;align-items:flex-start;flex:0 1 auto;align-self:flex-end}.cds-status-tracker-container.vertical .cds-span .step{text-align:left;white-space:nowrap}.cds-status-tracker-container.vertical .cds-span .stepdesc{text-align:left;width:0px;white-space:nowrap}.cds-status-tracker-container.vertical .cds-span .label{text-align:left}.cds-status-tracker-container.vertical .cds-circle-tick{width:160px}.cds-status-tracker-container.vertical .cds-line{width:1px;display:block;height:50px;margin:-18px 11px -11px;border-left:1px dashed #cccccc;border-top:none;align-self:flex-start}.cds-status-tracker-container.vertical .cds-line.filled{border-top:none}.cds-status-tracker-container.vertical .text{margin-left:5px}.cds-status-tracker-container.vertical .cds-status-tracker-section .cds-span svg{margin-top:0}.cds-status-tracker-container.vertical .cds-status-tracker-section:last-child .cds-span{align-items:flex-start}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker3Q21, decorators: [{
            type: Component,
            args: [{ selector: 'cds-status-tracker.3Q21', host: { class: 'cds-status-tracker' }, changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"cds-status-tracker-container\" [ngClass]=\"{'vertical' : isVertical}\">\n  <span class=\"sr-only\">{{ getTotalStepsString() }}</span>\n  <span class=\"cds-status-tracker-section\" *ngFor=\"let i of totalStepArray\">\n    <span class=\"cds-circle-tick\" *ngIf=\"i < currentStep\">\n      <span class=\"cds-span completed\">\n        <div class=\"text\">\n          <div class=\"step\">\n            {{i < currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"stepdesc\">\n            {{i >= currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"label\">{{i < currentStep ? stepLabels[i] : ''}}</div>\n        </div>\n\n        <svg\n          xmlns=\"http://www.w3.org/2000/svg\"\n          viewBox=\"-2 -2 30 30\"\n          class=\"cds-svg\"\n          role=\"presentation\"\n          aria-hidden=\"true\"\n        >\n          <circle cx=\"12.5\" cy=\"12.5\" r=\"12.5\" />\n          <image\n            height=\"25\"\n            width=\"25\"\n            href=\"./cds-assets/images/checkmark.png\"\n          />\n        </svg>\n        <span *ngIf=\"currentStep == i + 1\" class=\"sr-only\">\n          current\n        </span>\n        <span *ngIf=\"i+1<currentStep\" class=\"sr-only\">\n          completed\n        </span>\n      </span>\n    </span>\n    <span\n      *ngIf=\"i < currentStep && i != totalSteps - 1\"\n      class=\"cds-line\"\n      [ngClass]=\"{'filled' : i < currentStep-1}\"\n    ></span>\n    <span class=\"cds-circle-tick\" *ngIf=\"i >= currentStep\">\n      <span class=\"cds-span\">\n        <div class=\"text\">\n          <div class=\"step\">\n            {{i < currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"stepdesc\">\n            {{i >= currentStep ? stepDescription[i] : ''}}\n          </div>\n          <div class=\"label\">{{i < currentStep ? stepLabels[i] : ''}}</div>\n        </div>\n\n        <svg\n          xmlns=\"http://www.w3.org/2000/svg\"\n          viewBox=\"-2 -2 30 30\"\n          class=\"cds-svg-inactive\"\n          role=\"presentation\"\n          aria-hidden=\"true\"\n        >\n          <circle\n            cx=\"12.5\"\n            cy=\"12.5\"\n            r=\"12.5\"\n            fill=\"#CCCCCC\"\n            stroke=\"#CCCCCC\"\n            stroke-width=\"1\"\n          />\n        </svg>\n      </span>\n    </span>\n    <span\n      *ngIf=\"i >= currentStep && i != totalSteps - 1\"\n      class=\"cds-line\"\n    ></span>\n  </span>\n</div>\n", styles: [":host{width:100%}.cds-status-tracker-container{display:flex}.cds-status-tracker-section{display:flex;flex-grow:1}.cds-status-tracker-section:last-child{flex-grow:0}.cds-circle-tick{width:min-content;display:flex}.cds-svg{height:24px;display:flex;z-index:2}.cds-svg-inactive{height:16px;display:flex;z-index:2;margin-bottom:4px;margin-right:5px}.cds-line{margin:11px -33px 11px -38px;align-self:flex-end;padding:0;flex-grow:1;box-sizing:border-box;height:1px;border-top:1px dashed #cccccc}.cds-span{display:flex;flex:1 1 60px;flex-direction:column;align-items:center;text-align:center}.cds-span svg{margin-top:auto}.cds-span .step{font-size:12px;font-family:Interstate_Bold;font-weight:700;letter-spacing:0;line-height:18px}.cds-span .stepdesc{font-size:12px;font-family:Interstate_Bold;color:#666;font-weight:700;letter-spacing:0;line-height:18px;width:55px}.cds-span .label{font-size:12px;font-family:Interstate_Light}.cds-status-tracker-container .cds-status-tracker-section:nth-child(2) .cds-span{align-items:flex-start;text-align:left}.cds-status-tracker-container .cds-status-tracker-section:last-child .cds-span{align-items:flex-end;text-align:right}.check{background-size:20px 30px;stroke:#fff;stroke-width:2px}.cds-status-tracker-container.vertical{flex-direction:column;width:min-content;align-items:flex-start}.cds-status-tracker-container.vertical .cds-svg{width:24px}.cds-status-tracker-container.vertical .cds-svg-inactive{width:16px;margin-left:4px;margin-bottom:-2px}.cds-status-tracker-container.vertical .cds-status-tracker-section{flex-direction:column;align-items:center;justify-content:left}.cds-status-tracker-container.vertical .cds-span{flex-direction:row-reverse;align-items:flex-start;flex:0 1 auto;align-self:flex-end}.cds-status-tracker-container.vertical .cds-span .step{text-align:left;white-space:nowrap}.cds-status-tracker-container.vertical .cds-span .stepdesc{text-align:left;width:0px;white-space:nowrap}.cds-status-tracker-container.vertical .cds-span .label{text-align:left}.cds-status-tracker-container.vertical .cds-circle-tick{width:160px}.cds-status-tracker-container.vertical .cds-line{width:1px;display:block;height:50px;margin:-18px 11px -11px;border-left:1px dashed #cccccc;border-top:none;align-self:flex-start}.cds-status-tracker-container.vertical .cds-line.filled{border-top:none}.cds-status-tracker-container.vertical .text{margin-left:5px}.cds-status-tracker-container.vertical .cds-status-tracker-section .cds-span svg{margin-top:0}.cds-status-tracker-container.vertical .cds-status-tracker-section:last-child .cds-span{align-items:flex-start}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { totalSteps: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], stepLabels: [{
                type: Input
            }], stepDescription: [{
                type: Input
            }], isVertical: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }], totalStepsChange: [{
                type: Output
            }] } });

const COMPONENTS$b = [CdsStatusTracker3Q21];
class CdsStatusTrackerModule3Q21 {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule3Q21, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule3Q21, declarations: [CdsStatusTracker3Q21], imports: [CommonModule], exports: [CdsStatusTracker3Q21] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule3Q21, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule3Q21, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$b],
                    exports: [...COMPONENTS$b]
                }]
        }] });

const COMPONENTS$a = [CdsStatusTracker];
class CdsStatusTrackerModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule, declarations: [CdsStatusTracker], imports: [CommonModule, CdsStatusTrackerModule3Q21], exports: [CdsStatusTracker, CdsStatusTrackerModule3Q21] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule, imports: [CommonModule, CdsStatusTrackerModule3Q21, CdsStatusTrackerModule3Q21] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, CdsStatusTrackerModule3Q21],
                    declarations: [...COMPONENTS$a],
                    exports: [...COMPONENTS$a, CdsStatusTrackerModule3Q21]
                }]
        }] });

class CdsStatusTrackerStep {
    constructor() {
        this.role = 'listitem';
        this.customAltText = null;
    }
    getStepState() {
        if (this.customAltText) {
            return this.customAltText;
        }
        else if (this._isComplete && this._isNextComplete) {
            return 'complete step';
        }
        else if (this._isComplete && !this._isNextComplete) {
            return 'current step';
        }
        else if (!this._isComplete) {
            return 'incomplete step';
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerStep, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsStatusTrackerStep, selector: "cds-status-tracker2-step", inputs: { stepColor: "stepColor", stepIconColor: "stepIconColor", lineColor: "lineColor", customAltText: "customAltText" }, host: { properties: { "attr.role": "this.role" }, classAttribute: "cds-status-tracker2-step" }, queries: [{ propertyName: "stepIconTemplate", first: true, predicate: ["stepIcon"], descendants: true, read: TemplateRef }], exportAs: ["cdsStatusTrackerStep"], ngImport: i0, template: `
    <div
      class="cds-st2-content-wrapper"
      [class.cds-st2-content-complete]="_isComplete"
    >
      <ng-content></ng-content>
    </div>
    <div class="cds-st2-line-wrapper">
      <div class="cds-st2-icon" [class.cds-st2-icon-complete]="_isComplete">
        <!-- If journey teams use a custom icon, there will be a template. Otherwise we have a default for them-->
        <div *ngIf="stepIconTemplate; else stepIconDefault">
          <div
            class="cds-st2-check-icon-background"
            [ngStyle]="{ 'background-color': stepColor }"
          >
            <ng-container [ngTemplateOutlet]="stepIconTemplate"></ng-container>
            <span class="sr-only">{{ getStepState() }}</span>
          </div>
        </div>

        <ng-template #stepIconDefault>
          <div
            class="cds-st2-check-icon-background"
            [ngStyle]="{ 'background-color': stepColor }"
            *ngIf="_isComplete; else inactiveSvg"
          >
            <cds-icon
              type="2021"
              name="059-check"
              [svgStyle]="{ 'width.px': '16', 'height.px': '16' }"
              [pathColor]="stepIconColor || 'white'"
              aria-hidden="true"
              ariaLabel="CDS Icon"
            >
            </cds-icon>
          </div>
          <span class="sr-only">{{ getStepState() }}</span>

          <!-- Step not yet reached: gray circle -->
          <ng-template #inactiveSvg>
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              role="presentation"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="8" fill="#ccc" />
            </svg>
          </ng-template>
        </ng-template>
        
      </div>
      <div
        class="cds-st2-line-pre"
        [class.cds-st2-line-complete]="_isComplete"
        [style.borderColor]="_isComplete && _lineColorPre"
      ></div>
      <div
        class="cds-st2-line-post"
        [class.cds-st2-line-complete]="_isNextComplete"
        [style.borderColor]="_isNextComplete && _lineColorPost"
      ></div>
    </div>
  `, isInline: true, styles: [".cds-st2-content-wrapper{display:grid;align-content:end}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTrackerStep, decorators: [{
            type: Component,
            args: [{ selector: 'cds-status-tracker2-step', exportAs: 'cdsStatusTrackerStep', host: { class: 'cds-status-tracker2-step' }, template: `
    <div
      class="cds-st2-content-wrapper"
      [class.cds-st2-content-complete]="_isComplete"
    >
      <ng-content></ng-content>
    </div>
    <div class="cds-st2-line-wrapper">
      <div class="cds-st2-icon" [class.cds-st2-icon-complete]="_isComplete">
        <!-- If journey teams use a custom icon, there will be a template. Otherwise we have a default for them-->
        <div *ngIf="stepIconTemplate; else stepIconDefault">
          <div
            class="cds-st2-check-icon-background"
            [ngStyle]="{ 'background-color': stepColor }"
          >
            <ng-container [ngTemplateOutlet]="stepIconTemplate"></ng-container>
            <span class="sr-only">{{ getStepState() }}</span>
          </div>
        </div>

        <ng-template #stepIconDefault>
          <div
            class="cds-st2-check-icon-background"
            [ngStyle]="{ 'background-color': stepColor }"
            *ngIf="_isComplete; else inactiveSvg"
          >
            <cds-icon
              type="2021"
              name="059-check"
              [svgStyle]="{ 'width.px': '16', 'height.px': '16' }"
              [pathColor]="stepIconColor || 'white'"
              aria-hidden="true"
              ariaLabel="CDS Icon"
            >
            </cds-icon>
          </div>
          <span class="sr-only">{{ getStepState() }}</span>

          <!-- Step not yet reached: gray circle -->
          <ng-template #inactiveSvg>
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              xmlns="http://www.w3.org/2000/svg"
              role="presentation"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="8" fill="#ccc" />
            </svg>
          </ng-template>
        </ng-template>
        
      </div>
      <div
        class="cds-st2-line-pre"
        [class.cds-st2-line-complete]="_isComplete"
        [style.borderColor]="_isComplete && _lineColorPre"
      ></div>
      <div
        class="cds-st2-line-post"
        [class.cds-st2-line-complete]="_isNextComplete"
        [style.borderColor]="_isNextComplete && _lineColorPost"
      ></div>
    </div>
  `, styles: [".cds-st2-content-wrapper{display:grid;align-content:end}\n"] }]
        }], ctorParameters: () => [], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], stepColor: [{
                type: Input
            }], stepIconColor: [{
                type: Input
            }], lineColor: [{
                type: Input
            }], customAltText: [{
                type: Input
            }], stepIconTemplate: [{
                type: ContentChild,
                args: ['stepIcon', { read: TemplateRef }]
            }] } });

class CdsStatusTracker2 {
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor() {
        this.role = 'list';
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            const change = simpleChanges[propName];
            if (propName === 'currentStep' && !change.firstChange) {
                this.updateSteps(change.currentValue);
            }
        }
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.updateSteps(this.currentStep);
        }, 0);
    }
    ngAfterContentInit() {
        this.steps.forEach((step, index) => {
            const previousStep = this.steps.find((s, idx) => idx === index - 1);
            // If `lineColor` is set for the step, the `_lineColorPost` needs to be set for previous step
            if (step.lineColor && previousStep) {
                previousStep._lineColorPost = step.lineColor;
            }
            // If `lineColor` is set from the step component, it uses that color.  Otherwise it uses `lineColor` from this (the parent) component.
            step._lineColorPre = step.lineColor ? step.lineColor : !Array.isArray(this.lineColor) ? this.lineColor : this.lineColor[index - 1] || null;
            step._lineColorPost = !Array.isArray(this.lineColor) ? this.lineColor : this.lineColor[index] || null;
            // sets the icon color, unless already set from the child component
            if (!step.stepColor) {
                step.stepColor = !Array.isArray(this.stepColor) ? this.stepColor : this.stepColor[index] || null;
            }
        });
    }
    //endregion
    // #region ========== Methods ========== //
    updateSteps(currentStep) {
        this.steps.forEach((step, index) => {
            const stepNumber = index + 1;
            step._isComplete = currentStep >= stepNumber;
            step._isNextComplete = currentStep > stepNumber;
        });
    }
    getTotalStepsString() {
        return 'list of ' + this.steps.length + ' steps';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker2, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsStatusTracker2, selector: "cds-status-tracker2", inputs: { currentStep: "currentStep", isVertical: "isVertical", lineColor: "lineColor", stepColor: "stepColor" }, host: { properties: { "class.cds-status-tracker2-vertical": "this.isVertical", "attr.role": "this.role" }, classAttribute: "cds-status-tracker2" }, queries: [{ propertyName: "steps", predicate: CdsStatusTrackerStep }], exportAs: ["cdsStatusTracker2"], usesOnChanges: true, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker2, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-status-tracker2',
                    exportAs: 'cdsStatusTracker2',
                    host: { class: 'cds-status-tracker2' },
                    template: `
    <ng-content></ng-content>
  `,
                }]
        }], ctorParameters: () => [], propDecorators: { currentStep: [{
                type: Input
            }], isVertical: [{
                type: HostBinding,
                args: ['class.cds-status-tracker2-vertical']
            }, {
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], steps: [{
                type: ContentChildren,
                args: [CdsStatusTrackerStep]
            }], lineColor: [{
                type: Input
            }], stepColor: [{
                type: Input
            }] } });

const COMPONENTS$9 = [CdsStatusTracker2, CdsStatusTrackerStep];
const MODULES$2 = [CommonModule, CdsIconModule];
class CdsStatusTracker2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker2Module, declarations: [CdsStatusTracker2, CdsStatusTrackerStep], imports: [CommonModule, CdsIconModule], exports: [CdsStatusTracker2, CdsStatusTrackerStep] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker2Module, imports: [MODULES$2] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStatusTracker2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES$2],
                    declarations: [...COMPONENTS$9],
                    exports: [...COMPONENTS$9]
                }]
        }] });

class CdsProgressIndicator2 {
    /**
     * @warning totalSteps needs to be higher than currentStep to initialize correctly
     */
    set totalSteps(stepNumber) {
        this._totalSteps = stepNumber;
        this.totalStepArray = [];
        for (let i = 0; i < this.totalSteps; i++) {
            this.totalStepArray[i] = i;
        }
    }
    get totalSteps() {
        return this._totalSteps;
    }
    /**
      Input property currentStep
     */
    set currentStep(stepNumber) {
        this._currentStep = stepNumber;
        this.updateStep();
    }
    get currentStep() {
        return this._currentStep;
    }
    constructor(liveAnnouncer) {
        this.liveAnnouncer = liveAnnouncer;
        this.currentStepArray = [];
        this.totalStepArray = [];
        this.totalStepsStringArray = [];
        this._currentStep = 0;
        this.currentStepChange = new EventEmitter();
        this.totalStepsChange = new EventEmitter();
    }
    currentStepLimit(stepNumber) {
        return Math.min(this.totalSteps, Math.max(1, stepNumber));
    }
    updateStep() {
        for (let i = 0; i < this._currentStep; i++) {
            this.currentStepArray[i] = i;
        }
        // Make sure totalSteps is valid
        this.totalSteps = Math.max(2, this.totalSteps);
        // Make sure currentStep is valid
        this._currentStep = this.currentStepLimit(this.currentStep);
        // Emit Change
        this.currentStepChange.emit(this.currentStep);
        this.totalStepsChange.emit(this.totalSteps);
        this.totalStepsString = 'list of ' + this.totalSteps + ' steps';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressIndicator2, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsProgressIndicator2, selector: "cds-progress-indicator2", inputs: { totalStepsStringArray: "totalStepsStringArray", textDisplay: "textDisplay", totalSteps: "totalSteps", currentStep: "currentStep" }, outputs: { currentStepChange: "currentStepChange", totalStepsChange: "totalStepsChange" }, host: { classAttribute: "cds-progress-indicator2" }, ngImport: i0, template: `
    <div class="container">
      <div class="sr-only">{{ totalStepsString }}</div>
      <div
        class="row cds-totalStepsClass"
        *ngIf="textDisplay == 'both' || textDisplay == 'above'"
      >
        <span role="comment" *ngFor="let j of totalStepArray">
          <span *ngIf="j < totalSteps && j != currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
            >
              {{ totalStepsStringArray[j] }}
            </span>
            <span *ngIf="j + 1 < currentStep" class="sr-only">
              completed
            </span>
          </span>
          <span *ngIf="j == currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
              class="cds-Steps"
            >
              {{ totalStepsStringArray[j] }}
            </span>
            <span *ngIf="currentStep == j + 1" class="sr-only">
              current
            </span>
          </span>
        </span>
      </div>

      <div class="row" tabindex="-1" aria-hidden="true">
        <span class="cds-progress-indicator" *ngFor="let i of totalStepArray">
          <span
            class="cds-circle-tick"
            *ngIf="i < totalSteps && i != currentStep - 1"
          >
            <input
              type="radio"
              id="selectCircle{{ i }}"
              name="selectCircle{{ i }}"
              disabled="true"
            />
          </span>
          <span class="cds-circle-tick" *ngIf="i == currentStep - 1">
            <input
              type="radio"
              id="unselectCircle{{ i }}"
              name="unselectCirclem{{ i }}"
              checked
              disabled
            />
          </span>

          <span *ngIf="i < currentStep - 1" class="cds-line1"> </span>
          <span
            *ngIf="i >= currentStep - 1 && i < totalSteps - 1"
            class="cds-line"
          >
          </span>
        </span>
      </div>
      <div
        class="row cds-totalStepsClass"
        *ngIf="textDisplay == 'both' || textDisplay == 'below'"
      >
        <span role="comment" *ngFor="let j of totalStepArray">
          <span *ngIf="j < totalSteps && j != currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
            >
              {{ totalStepsStringArray[j] }}
              <span *ngIf="j + 1 < currentStep" class="sr-only">
                completed
              </span>
            </span></span
          >
          <span *ngIf="j == currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
              class="cds-Steps"
            >
              {{ totalStepsStringArray[j]
              }}<span *ngIf="currentStep == j + 1" class="sr-only">
                current
              </span>
            </span>
          </span>
        </span>
      </div>
    </div>
  `, isInline: true, styles: [".cds-progress-bar{display:inline}.cds-circle-tick{float:left;width:min-content;margin-top:7px}.cds-line{height:1px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-line1{height:4px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-totalStepsClass{font-size:11px}.cds-finalStep{margin-left:75px}.cds-Steps{margin-right:90px}@media (min-width: 310px) and (max-width: 767px){.cds-finalStep{margin-left:26px}.cds-Steps{margin-right:30px}.cds-totalStepsClass{font-size:6px}.cds-line{height:1px;width:72px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-line1{height:4px;width:72px;background-color:#bbb;float:left;margin:18px 0;padding:0}}@media all and (-ms-high-contrast: none){.cds-totalStepsClass{font-size:11px}.cds-finalStep{margin-left:75px}.cds-Steps{margin-right:90px}.cds-circle-tick{right:37px;display:inline;top:50px}cds-line1{height:4px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-line{height:1px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressIndicator2, decorators: [{
            type: Component,
            args: [{ selector: 'cds-progress-indicator2', host: { class: 'cds-progress-indicator2' }, changeDetection: ChangeDetectionStrategy.OnPush, template: `
    <div class="container">
      <div class="sr-only">{{ totalStepsString }}</div>
      <div
        class="row cds-totalStepsClass"
        *ngIf="textDisplay == 'both' || textDisplay == 'above'"
      >
        <span role="comment" *ngFor="let j of totalStepArray">
          <span *ngIf="j < totalSteps && j != currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
            >
              {{ totalStepsStringArray[j] }}
            </span>
            <span *ngIf="j + 1 < currentStep" class="sr-only">
              completed
            </span>
          </span>
          <span *ngIf="j == currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
              class="cds-Steps"
            >
              {{ totalStepsStringArray[j] }}
            </span>
            <span *ngIf="currentStep == j + 1" class="sr-only">
              current
            </span>
          </span>
        </span>
      </div>

      <div class="row" tabindex="-1" aria-hidden="true">
        <span class="cds-progress-indicator" *ngFor="let i of totalStepArray">
          <span
            class="cds-circle-tick"
            *ngIf="i < totalSteps && i != currentStep - 1"
          >
            <input
              type="radio"
              id="selectCircle{{ i }}"
              name="selectCircle{{ i }}"
              disabled="true"
            />
          </span>
          <span class="cds-circle-tick" *ngIf="i == currentStep - 1">
            <input
              type="radio"
              id="unselectCircle{{ i }}"
              name="unselectCirclem{{ i }}"
              checked
              disabled
            />
          </span>

          <span *ngIf="i < currentStep - 1" class="cds-line1"> </span>
          <span
            *ngIf="i >= currentStep - 1 && i < totalSteps - 1"
            class="cds-line"
          >
          </span>
        </span>
      </div>
      <div
        class="row cds-totalStepsClass"
        *ngIf="textDisplay == 'both' || textDisplay == 'below'"
      >
        <span role="comment" *ngFor="let j of totalStepArray">
          <span *ngIf="j < totalSteps && j != currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
            >
              {{ totalStepsStringArray[j] }}
              <span *ngIf="j + 1 < currentStep" class="sr-only">
                completed
              </span>
            </span></span
          >
          <span *ngIf="j == currentStep - 1">
            <span
              [ngClass]="j == totalSteps - 1 ? 'cds-finalStep' : 'cds-Steps'"
              class="cds-Steps"
            >
              {{ totalStepsStringArray[j]
              }}<span *ngIf="currentStep == j + 1" class="sr-only">
                current
              </span>
            </span>
          </span>
        </span>
      </div>
    </div>
  `, styles: [".cds-progress-bar{display:inline}.cds-circle-tick{float:left;width:min-content;margin-top:7px}.cds-line{height:1px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-line1{height:4px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-totalStepsClass{font-size:11px}.cds-finalStep{margin-left:75px}.cds-Steps{margin-right:90px}@media (min-width: 310px) and (max-width: 767px){.cds-finalStep{margin-left:26px}.cds-Steps{margin-right:30px}.cds-totalStepsClass{font-size:6px}.cds-line{height:1px;width:72px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-line1{height:4px;width:72px;background-color:#bbb;float:left;margin:18px 0;padding:0}}@media all and (-ms-high-contrast: none){.cds-totalStepsClass{font-size:11px}.cds-finalStep{margin-left:75px}.cds-Steps{margin-right:90px}.cds-circle-tick{right:37px;display:inline;top:50px}cds-line1{height:4px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}.cds-line{height:1px;width:200px;background-color:#bbb;float:left;margin:18px 0;padding:0}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { totalStepsStringArray: [{
                type: Input
            }], textDisplay: [{
                type: Input
            }], totalSteps: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }], totalStepsChange: [{
                type: Output
            }] } });

const COMPONENTS$8 = [CdsProgressIndicator2];
class CdsProgressIndicator2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressIndicator2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressIndicator2Module, declarations: [CdsProgressIndicator2], imports: [CommonModule], exports: [CdsProgressIndicator2] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressIndicator2Module, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsProgressIndicator2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$8],
                    exports: [...COMPONENTS$8]
                }]
        }] });

class CdsBreadcrumbComponent {
    constructor() {
        /**
         * List of links for the breadcrumb
         */
        this.links = [];
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBreadcrumbComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.0.3", type: CdsBreadcrumbComponent, selector: "cds-breadcrumb", inputs: { changeLastCrumb: "changeLastCrumb", links: "links" }, host: { classAttribute: "cds-breadcrumb" }, ngImport: i0, template: `
  <nav aria-label="Breadcrumb">
    <ul class="breadcrumb">
      @for (link of links; track $index) {
      <li>
        @if(link.routerLink) {
        <a
          routerLink="{{ link.routerLink }}"
          [innerHtml]="link.text"
          [attr.aria-current]="link.isCurrent ? 'page' : null"
        ></a>
        } @else {
        <a
          href="{{ link.href }}"
          [innerHtml]="link.text"
          [attr.aria-current]="link.isCurrent ? 'page' : null"
        ></a>
        }
      </li>
      } @if (changeLastCrumb) {
      <li>
        <span
          class="last-crumb"
          tabindex="0"
          aria-current="page"
          [innerHtml]="changeLastCrumb"
        ></span>
      </li>
      }
    </ul>
  </nav>
  `, isInline: true, styles: [":host{display:block}ul.breadcrumb{padding:8px 12px;list-style:none;border-radius:6px;background-color:transparent;margin:0}ul.breadcrumb>li{display:inline-block;font-size:16px;margin:2.5px!important;font-family:Interstate_Light;font-weight:300;height:24px;line-height:1}ul.breadcrumb>li:before{padding:0 2px;position:static;content:\"\";counter-increment:initial;color:#056dae}ul.breadcrumb>li+li:before{color:#666;content:\"\";background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 8.02 14%22%3E%3Cpath d=%22M17 27a1 1 0 0 1-.68-1.73L22 20l-5.66-5.27a1 1 0 0 1 1.36-1.46l5.72 5.32a1.91 1.91 0 0 1 0 2.82l-5.72 5.32a1 1 0 0 1-.7.27%22 transform=%22translate(-15.99 -13)%22 style=%22fill:%23666%22/%3E%3C/svg%3E\");margin:0 8px;height:9.33px;width:5.35px;display:inline-block;background-repeat:no-repeat}ul.breadcrumb a{display:inline-block;padding:7px 0;color:var(--cds-breadcrumb-link-color, #056dae);border-radius:4px}ul.breadcrumb a:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-breadcrumb-outline-color, #002A54);outline-offset:2px}ul.breadcrumb a:hover{color:var(--cds-breadcrumb-link-hover-color, #034C81);text-decoration:none}ul.breadcrumb a:active{color:var(--cds-breadcrumb-link-pressed-color, #002A54)}.last-crumb{color:var(--cds-breadcrumb-last-crumb-color, #333333);border-radius:4px}.last-crumb:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-breadcrumb-outline-color, #002A54);outline-offset:2px}@media only screen and (max-width: 480px){ul.breadcrumb{padding:8px 0}}\n"], dependencies: [{ kind: "directive", type: i1$4.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBreadcrumbComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-breadcrumb', template: `
  <nav aria-label="Breadcrumb">
    <ul class="breadcrumb">
      @for (link of links; track $index) {
      <li>
        @if(link.routerLink) {
        <a
          routerLink="{{ link.routerLink }}"
          [innerHtml]="link.text"
          [attr.aria-current]="link.isCurrent ? 'page' : null"
        ></a>
        } @else {
        <a
          href="{{ link.href }}"
          [innerHtml]="link.text"
          [attr.aria-current]="link.isCurrent ? 'page' : null"
        ></a>
        }
      </li>
      } @if (changeLastCrumb) {
      <li>
        <span
          class="last-crumb"
          tabindex="0"
          aria-current="page"
          [innerHtml]="changeLastCrumb"
        ></span>
      </li>
      }
    </ul>
  </nav>
  `, host: {
                        class: 'cds-breadcrumb'
                    }, changeDetection: ChangeDetectionStrategy.OnPush, styles: [":host{display:block}ul.breadcrumb{padding:8px 12px;list-style:none;border-radius:6px;background-color:transparent;margin:0}ul.breadcrumb>li{display:inline-block;font-size:16px;margin:2.5px!important;font-family:Interstate_Light;font-weight:300;height:24px;line-height:1}ul.breadcrumb>li:before{padding:0 2px;position:static;content:\"\";counter-increment:initial;color:#056dae}ul.breadcrumb>li+li:before{color:#666;content:\"\";background-image:url(\"data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 8.02 14%22%3E%3Cpath d=%22M17 27a1 1 0 0 1-.68-1.73L22 20l-5.66-5.27a1 1 0 0 1 1.36-1.46l5.72 5.32a1.91 1.91 0 0 1 0 2.82l-5.72 5.32a1 1 0 0 1-.7.27%22 transform=%22translate(-15.99 -13)%22 style=%22fill:%23666%22/%3E%3C/svg%3E\");margin:0 8px;height:9.33px;width:5.35px;display:inline-block;background-repeat:no-repeat}ul.breadcrumb a{display:inline-block;padding:7px 0;color:var(--cds-breadcrumb-link-color, #056dae);border-radius:4px}ul.breadcrumb a:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-breadcrumb-outline-color, #002A54);outline-offset:2px}ul.breadcrumb a:hover{color:var(--cds-breadcrumb-link-hover-color, #034C81);text-decoration:none}ul.breadcrumb a:active{color:var(--cds-breadcrumb-link-pressed-color, #002A54)}.last-crumb{color:var(--cds-breadcrumb-last-crumb-color, #333333);border-radius:4px}.last-crumb:focus-visible{box-shadow:0 0 0 2px #fff;outline:2px solid var(--cds-breadcrumb-outline-color, #002A54);outline-offset:2px}@media only screen and (max-width: 480px){ul.breadcrumb{padding:8px 0}}\n"] }]
        }], propDecorators: { changeLastCrumb: [{
                type: Input
            }], links: [{
                type: Input
            }] } });

class CdsBreadcrumbModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBreadcrumbModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsBreadcrumbModule, declarations: [CdsBreadcrumbComponent], imports: [CommonModule, RouterModule], exports: [CdsBreadcrumbComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBreadcrumbModule, imports: [CommonModule, RouterModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsBreadcrumbModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [CdsBreadcrumbComponent],
                    imports: [CommonModule, RouterModule],
                    exports: [CdsBreadcrumbComponent]
                }]
        }] });

class CdsRadialProgressBarComponent {
    // #region ========== Properties ========== //
    get radius() {
        const _radius = (this.diameter - this.strokeWidth) / 2;
        return _radius;
    }
    get circumference() {
        const _circumference = 2 * Math.PI * this.radius * (0.9 - 1 / this.strokeWidth);
        return _circumference;
    }
    get arcLength() {
        const _arcLength = this.circumference * this.percent * 0.01;
        return _arcLength;
    }
    get checkPath() {
        const d = this.diameter;
        const x1 = d * 0.27777;
        const y1 = d * 0.54795;
        const x2 = d * 0.40277;
        const y2 = d * 0.65753;
        const x3 = d * 0.75;
        const y3 = d * 0.34247;
        return `M${x1} ${y1} L${x2} ${y2} L${x3} ${y3}`;
    }
    get checkArc() {
        let r = this.diameter / 2;
        let c1 = r * 0.4812;
        let y = r * 1.94;
        let c2 = this.diameter + 20 - c1;
        let center = (c2 * c2 - c1 * c1) / (2 * (c2 - c1));
        return ` M ${c1} ${y} A ${r} ${r} 0 1 1 ${c2} ${y}`;
    }
    get checkWidth() {
        return this.diameter * 0.1;
    }
    get pointCoordinates() {
        let r = this.diameter / 2;
        let c1 = r * 0.4812;
        let y = r * 1.94;
        let c2 = this.diameter + 20 - c1;
        let center = (c2 * c2 - c1 * c1) / (2 * (c2 - c1));
        return null;
    }
    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
        var angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians)
        };
    }
    describeArc(startAngle = 130, endAngle = 230, largeArc = null, sweep = null) {
        let radius = this.diameter / 2;
        let x = radius + this.strokeWidth;
        let y = radius + this.strokeWidth;
        var start = this.polarToCartesian(x, y, radius, endAngle);
        var end = this.polarToCartesian(x, y, radius, startAngle);
        var largeArcFlag = largeArc != null ? largeArc : this.percent < 69 ? '0' : '1';
        var sweepFlag = sweep != null ? sweep : endAngle - startAngle <= 180 ? '0' : '1';
        var d = [
            'M',
            start.x,
            start.y,
            'A',
            radius,
            radius,
            0,
            largeArcFlag,
            sweepFlag,
            end.x,
            end.y
        ].join(' ');
        return d;
    }
    endPoint(angle) {
        let radius = this.diameter / 2;
        let x = radius + this.strokeWidth;
        let y = radius + this.strokeWidth;
        return this.polarToCartesian(x, y, radius, angle);
    }
    endAngle() {
        if (this.percent < 50) {
            return 230 + 260 * this.percent * 0.01;
        }
        if (this.percent == 50) {
            return 360;
        }
        else {
            return (this.percent - 50) * 2.6;
        }
    }
    set percent(pcnt) {
        // Limit the percent between 0 and 100
        if (pcnt > 100) {
            console.warn('percent should not be above 100... setting to 100');
            this._percent = 100;
        }
        else if (pcnt < 0) {
            console.warn('percent should not be less than 0... setting to 0');
            this._percent = 0;
        }
        else {
            this._percent = pcnt;
        }
    }
    get percent() {
        return this._percent;
    }
    set announceText(announceTxt) {
        this._announceText = announceTxt;
        this.announce(announceTxt);
    }
    get announceText() {
        return this._announceText;
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(announcer) {
        this.announcer = announcer;
        // #endregion
        // #region ========== @Inputs ========== //
        this._percent = 50;
        this.diameter = 100;
        this.strokeWidth = 8;
        // @Input() baseStrokeWidth: number = 1;
        this.stepText = '-';
        this.subText = null;
        this.progressColor = '';
        this.gaugeColor = '';
        this.disabled = false;
    }
    // #endregion
    // #region ========== Methods ========== //
    announce(txt) {
        this.announcer.announce(txt);
    }
    onFocus(e) {
        this.disabled ? this.announce('Gauge value hidden') : this.announce(this.announceText);
        this.gauge.nativeElement.blur();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadialProgressBarComponent, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsRadialProgressBarComponent, selector: "cds-radial-progress-bar", inputs: { percent: "percent", diameter: "diameter", strokeWidth: "strokeWidth", stepText: "stepText", subText: "subText", progressColor: "progressColor", gaugeColor: "gaugeColor", disabled: "disabled", announceText: "announceText" }, host: { classAttribute: "cds-radial-progress-bar" }, viewQueries: [{ propertyName: "gauge", first: true, predicate: ["gauge"], descendants: true }], ngImport: i0, template: `
    <svg
    #gauge
    tabindex="-1"
      [attr.viewBox]="
        '0 0 ' +
        (diameter + this.strokeWidth + 4) +
        ' ' +
        (diameter + this.strokeWidth + 4)
      "
      [style.width.px]="diameter + this.strokeWidth + 4"
      [style.height.px]="diameter"
      role="img"
      focusable="true"
      (focus)="onFocus($event)"
    >
      <title>Radial Gauge</title>
      <path
        [attr.d]="describeArc(130, 230, 1, 1)"
        class="rpb-back"
        [class.rpb-back-done]="percent === 100"
        [style.stroke]="gaugeColor"
        [style.stroke-width.px]="strokeWidth"
      />
      <path
        id=progress
        [attr.d]="describeArc(130, 230, 1, 1)"
        *ngIf="percent !== 0 && !disabled"
        class="rpb-arc"
        [style.stroke]="percent == 100 ? '#008a24' : progressColor"
        [style.stroke-dasharray]="'3'+', '+circumference"

        [class.rpb-arc-done]="percent === 100"
        [style.stroke-width.px]="strokeWidth"
        [@progress]="{value: !disabled, params: {default: '3'+', '+circumference, final: percent*circumference*.01+', '+circumference}}"
      >
      <!-- <animate 
      attributeName="stroke-dasharray"
      attributeType="CSS" 
      [attr.from]="'3, '+circumference" 
      [attr.to]="percent*circumference*.01 < 3 ? '3'+', '+circumference : percent*circumference*.01+', '+circumference" 
      dur="1s" 
      begin="click"
      repeatCount="1"
      /> -->
    </path>

      <circle
        [attr.cx]="endPoint(endAngle()).x"
        [attr.cy]="endPoint(endAngle()).y"
        [attr.r]="strokeWidth / 5"
        fill="white"
        *ngIf="percent != 100 && !disabled"
      />

      <text
        *ngIf="percent !== 100 && !disabled"
        class="rpb-text"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth"
        dominant-baseline="middle"
        text-anchor="middle"
      >
        {{ stepText }}
      </text>
      <text
        *ngIf="percent !== 100 && !disabled && subText!=null"
        class="rpb-subtext"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="(diameter / 2 + this.strokeWidth)+25"
        dominant-baseline="middle"
        text-anchor="middle"
      >
        {{ subText }}
      </text>
      <text
        *ngIf="disabled"
        class="rpb-text-disabled"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth"
        dominant-baseline="middle"
        text-anchor="middle"
        [ngStyle]="{'font-size': this.diameter/4}"
      >
        ?
      </text>
      <path
        *ngIf="percent === 100 && !disabled"
        class="rpb-done-check"
        [attr.d]="checkPath"
        [style.stroke-width.px]="checkWidth"
        [style.transform]="
          'translate(' +
          this.strokeWidth +
          'px, ' +
          this.strokeWidth +
          'px)'
        "
      />
    </svg>
    <div
      class="rpb-content"
      [style.margin-left]="this.strokeWidth + 'px'"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, styles: [":host{display:inline-block;text-align:center}.dot{fill:transparent;stroke-linecap:round;transition:all 1s ease-out;stroke:#fff}.rpb-back{fill:transparent;stroke-linecap:round}.rpb-back-done{stroke:#008a24;stroke-dasharray:initial}.rpb-arc{fill:transparent;stroke-linecap:round;transition:stroke-dasharray 2s ease-in;animation-fill-mode:forwards}.rpb-arc-done{stroke:#008a24;stroke-dasharray:initial}.rpb-done-check{fill:none;stroke:#008a24}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [
            trigger('progress', [
                state('true', style({
                    'stroke-dasharray': "{{final}}",
                }), { params: { default: "3,100", final: "100,100" } }),
                transition('* => true', [
                    animate('0.5s')
                ]),
                transition('* => false', [
                    animate('0s')
                ]),
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadialProgressBarComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-radial-progress-bar', template: `
    <svg
    #gauge
    tabindex="-1"
      [attr.viewBox]="
        '0 0 ' +
        (diameter + this.strokeWidth + 4) +
        ' ' +
        (diameter + this.strokeWidth + 4)
      "
      [style.width.px]="diameter + this.strokeWidth + 4"
      [style.height.px]="diameter"
      role="img"
      focusable="true"
      (focus)="onFocus($event)"
    >
      <title>Radial Gauge</title>
      <path
        [attr.d]="describeArc(130, 230, 1, 1)"
        class="rpb-back"
        [class.rpb-back-done]="percent === 100"
        [style.stroke]="gaugeColor"
        [style.stroke-width.px]="strokeWidth"
      />
      <path
        id=progress
        [attr.d]="describeArc(130, 230, 1, 1)"
        *ngIf="percent !== 0 && !disabled"
        class="rpb-arc"
        [style.stroke]="percent == 100 ? '#008a24' : progressColor"
        [style.stroke-dasharray]="'3'+', '+circumference"

        [class.rpb-arc-done]="percent === 100"
        [style.stroke-width.px]="strokeWidth"
        [@progress]="{value: !disabled, params: {default: '3'+', '+circumference, final: percent*circumference*.01+', '+circumference}}"
      >
      <!-- <animate 
      attributeName="stroke-dasharray"
      attributeType="CSS" 
      [attr.from]="'3, '+circumference" 
      [attr.to]="percent*circumference*.01 < 3 ? '3'+', '+circumference : percent*circumference*.01+', '+circumference" 
      dur="1s" 
      begin="click"
      repeatCount="1"
      /> -->
    </path>

      <circle
        [attr.cx]="endPoint(endAngle()).x"
        [attr.cy]="endPoint(endAngle()).y"
        [attr.r]="strokeWidth / 5"
        fill="white"
        *ngIf="percent != 100 && !disabled"
      />

      <text
        *ngIf="percent !== 100 && !disabled"
        class="rpb-text"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth"
        dominant-baseline="middle"
        text-anchor="middle"
      >
        {{ stepText }}
      </text>
      <text
        *ngIf="percent !== 100 && !disabled && subText!=null"
        class="rpb-subtext"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="(diameter / 2 + this.strokeWidth)+25"
        dominant-baseline="middle"
        text-anchor="middle"
      >
        {{ subText }}
      </text>
      <text
        *ngIf="disabled"
        class="rpb-text-disabled"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth"
        dominant-baseline="middle"
        text-anchor="middle"
        [ngStyle]="{'font-size': this.diameter/4}"
      >
        ?
      </text>
      <path
        *ngIf="percent === 100 && !disabled"
        class="rpb-done-check"
        [attr.d]="checkPath"
        [style.stroke-width.px]="checkWidth"
        [style.transform]="
          'translate(' +
          this.strokeWidth +
          'px, ' +
          this.strokeWidth +
          'px)'
        "
      />
    </svg>
    <div
      class="rpb-content"
      [style.margin-left]="this.strokeWidth + 'px'"
    >
      <ng-content></ng-content>
    </div>
  `, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'cds-radial-progress-bar'
                    }, animations: [
                        trigger('progress', [
                            state('true', style({
                                'stroke-dasharray': "{{final}}",
                            }), { params: { default: "3,100", final: "100,100" } }),
                            transition('* => true', [
                                animate('0.5s')
                            ]),
                            transition('* => false', [
                                animate('0s')
                            ]),
                        ])
                    ], styles: [":host{display:inline-block;text-align:center}.dot{fill:transparent;stroke-linecap:round;transition:all 1s ease-out;stroke:#fff}.rpb-back{fill:transparent;stroke-linecap:round}.rpb-back-done{stroke:#008a24;stroke-dasharray:initial}.rpb-arc{fill:transparent;stroke-linecap:round;transition:stroke-dasharray 2s ease-in;animation-fill-mode:forwards}.rpb-arc-done{stroke:#008a24;stroke-dasharray:initial}.rpb-done-check{fill:none;stroke:#008a24}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { percent: [{
                type: Input
            }], diameter: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], stepText: [{
                type: Input
            }], subText: [{
                type: Input
            }], progressColor: [{
                type: Input
            }], gaugeColor: [{
                type: Input
            }], disabled: [{
                type: Input
            }], announceText: [{
                type: Input
            }], gauge: [{
                type: ViewChild,
                args: ["gauge"]
            }] } });

const COMPONENTS$7 = [CdsRadialProgressBarComponent];
class CdsRadialProgressBar {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadialProgressBar, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsRadialProgressBar, declarations: [CdsRadialProgressBarComponent], imports: [CommonModule], exports: [CdsRadialProgressBarComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadialProgressBar, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsRadialProgressBar, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$7],
                    exports: [...COMPONENTS$7]
                }]
        }] });

class GaugeComponent {
    get radius() {
        const _radius = this.diameter / 2;
        return _radius;
    }
    get circumference() {
        const _circumference = 2 * Math.PI * this.radius;
        return _circumference;
    }
    get arcLength() {
        const _arcLength = this.circumference * this.amount * 0.01;
        return _arcLength;
    }
    get checkPath() {
        const d = this.diameter;
        const x1 = d * 0.27777;
        const y1 = d * 0.54795;
        const x2 = d * 0.40277;
        const y2 = d * 0.65753;
        const x3 = d * 0.75;
        const y3 = d * 0.34247;
        return `M${x1} ${y1} L${x2} ${y2} L${x3} ${y3}`;
    }
    get checkArc() {
        let r = this.diameter / 2;
        let c1 = r * 0.4812;
        let y = r * 1.94;
        let c2 = this.diameter + 20 - c1;
        let center = (c2 * c2 - c1 * c1) / (2 * (c2 - c1));
        return ` M ${c1} ${y} A ${r} ${r} 0 1 1 ${c2} ${y}`;
    }
    get checkWidth() {
        return this.diameter * 0.1;
    }
    get pointCoordinates() {
        let r = this.diameter / 2;
        let c1 = r * 0.4812;
        let y = r * 1.94;
        let c2 = this.diameter + 20 - c1;
        let center = (c2 * c2 - c1 * c1) / (2 * (c2 - c1));
        return null;
    }
    polarToCartesian(centerX, centerY, radius, angleInDegrees) {
        var angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians)
        };
    }
    describeArc(startAngle = 100, endAngle = 260, largeArc = null, sweep = null) {
        let radius = this.diameter / 2;
        let x = radius + this.strokeWidth;
        let y = radius + this.strokeWidth;
        var start = this.polarToCartesian(x, y, radius, endAngle);
        var end = this.polarToCartesian(x, y, radius, startAngle);
        var largeArcFlag = largeArc != null ? largeArc : this.amount < 69 ? '0' : '1';
        var sweepFlag = sweep != null ? sweep : endAngle - startAngle <= 180 ? '0' : '1';
        var d = [
            'M',
            start.x,
            start.y,
            'A',
            radius,
            radius,
            0,
            largeArcFlag,
            sweepFlag,
            end.x,
            end.y
        ].join(' ');
        return d;
    }
    endPoint(angle) {
        let radius = this.diameter / 2;
        let x = radius + this.strokeWidth;
        let y = radius + this.strokeWidth;
        return this.polarToCartesian(x, y, radius, angle);
    }
    endAngle() {
        if (this.amount < this.maxValue / 2) {
            return 270 + 180 * (this.amount / this.maxValue);
        }
        if (this.amount == this.maxValue / 2) {
            return 360;
        }
        else {
            return (this.amount - this.maxValue / 2) * (180 / this.maxValue);
        }
    }
    get labelYCoordinate() {
        let _strokeWidth = 0;
        if (this.strokeWidth > 12) {
            _strokeWidth = this.strokeWidth - 12;
        }
        let val = 9 + this.labelSize + _strokeWidth;
        return val;
    }
    get bigLabelSize() {
        return Math.round(this.labelSize / 0.667);
    }
    set amount(pcnt) {
        // Limit the amount between 0 and maximum value
        this.announceText = '$' + this.amount + 'is current';
        if (pcnt > this.maxValue) {
            console.warn('amount should not be above max value... setting to 1000');
            this._amount = this.maxValue;
        }
        else if (pcnt < 0) {
            console.warn('amount should not be less than 0... setting to 0');
            this._amount = 0;
        }
        else {
            this._amount = pcnt;
        }
    }
    get amount() {
        return this._amount;
    }
    set announceText(announceTxt) {
        this._announceText = announceTxt;
        this.announce(announceTxt);
    }
    get announceText() {
        return this._announceText;
    }
    set stepTxt(subText) {
        this.announceText = 'The current value of subText is ' + this.subText;
        this._subText = this.subText;
    }
    get stepTxt() {
        return this._subText;
    }
    // #endregion
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor(announcer) {
        this.announcer = announcer;
        // #endregion
        // #region ========== @Inputs ========== //
        this.idStr = 'gauge-progress';
        this.labelSize = 12;
        this.diameter = 124;
        this.strokeWidth = 8;
        this.progressCircleSize = 6;
        this.subText = 'To Goal';
        this.subText1 = '-';
        this.progressColor = '';
        this.gaugeColor = '';
        this.outerCircleColor = '#46b7e6';
        this.maxValue = 1000;
        this._amount = this.maxValue / 2;
        this._subText = '';
    }
    // #endregion
    // #region ========== Methods ========== //
    announce(txt) {
        this.announcer.announce(txt);
    }
    onFocus(e) {
        this.gauge.nativeElement.blur();
    }
    getProgressed() {
        let str = '';
        let count = (this._amount / this.maxValue) * 100;
        str =
            'The user has progressed ' + Math.round(count) + '% towards the goal. ';
        return str;
    }
    getCompletedPerc() {
        let str = 'The user is now at $' + this._amount + '. ';
        return str;
    }
    getReamingingPerc() {
        let str = '';
        let count = ((this.maxValue - this._amount) / this.maxValue) * 100;
        str =
            'The user needs to progress ' +
                Math.round(count) +
                '% to acheive the goal.';
        return str;
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: GaugeComponent, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: GaugeComponent, selector: "cds-gauge", inputs: { idStr: "idStr", labelSize: "labelSize", diameter: "diameter", strokeWidth: "strokeWidth", progressCircleSize: "progressCircleSize", subText: "subText", subText1: "subText1", progressColor: "progressColor", gaugeColor: "gaugeColor", outerCircleColor: "outerCircleColor", maxValue: "maxValue", amount: "amount", announceText: "announceText", stepTxt: "stepTxt" }, host: { classAttribute: "cds-gauge" }, viewQueries: [{ propertyName: "gauge", first: true, predicate: ["gauge"], descendants: true }], ngImport: i0, template: `
    <span aria-label="Gauge" role="text"></span><br />
    <span aria-label="$0 is the minimum" role="text"></span><br />
    <span
      [attr.aria-label]="'$' + this.maxValue + ' is the maximum'"
      role="text"
    ></span
    ><br />
    <span [attr.aria-label]="getCompletedPerc()" role="text"></span> <br />
    <span [attr.aria-label]="getProgressed()" role="text"></span> <br />
    <span
      [attr.aria-label]="
        '$' + (maxValue - amount) + ' is the amount left to achieve the goal.'
      "
      role="text"
    ></span>
    <br />
    <span [attr.aria-label]="getReamingingPerc()" role="text"> </span> <br />
    <svg
      #gauge
      tabindex="-1"
      [attr.viewBox]="
        '0 0 ' +
        (diameter + this.strokeWidth + 4) +
        ' ' +
        (diameter + this.strokeWidth + 4)
      "
      [style.width.px]="diameter + this.strokeWidth + 4"
      [style.height.px]="diameter"
      role="presentation"
      focusable="true"
      (focus)="onFocus($event)"
      class="overflow-inherit"
    >
      <path
        [attr.d]="describeArc(90, 270, 1, 1)"
        class="rpb-back"
        [class.rpb-back-done]="amount === maxValue"
        [style.stroke]="gaugeColor"
        [style.stroke-width.px]="strokeWidth"
      />

      <path
        id="{{ idStr }}"
        [attr.d]="describeArc(90, 270, 1, 1)"
        *ngIf="amount >= 0"
        class="rpb-arc"
        [style.stroke-dasharray]="'0' + ', ' + circumference"
        [class.rpb-arc-done]="amount === maxValue"
        [style.stroke-width.px]="strokeWidth"
        [@progress]="{
          value: true,
          params: {
            default: '0' + ', ' + circumference,
            final:
              (circumference / 2) * (amount / maxValue) + ', ' + circumference
          }
        }"
      ></path>

      

      <circle
        _ngcontent-day-c112=""
        class="ng-tns-c112-4 ng-star-inserted ng-circle gauge-outer-circle"
        [attr.cx]="endPoint(endAngle()).x"
        [attr.cy]="endPoint(endAngle()).y"
        [attr.r]="progressCircleSize"
        fill="white"
        *ngIf="amount != maxValue"
        [attr.stroke]="outerCircleColor"
        stroke-width="4"
      />

      <text
        *ngIf="amount !== maxValue"
        class="rpb-text rpb-price"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 3.1 + this.strokeWidth + 0.5"
        dominant-baseline="middle"
        text-anchor="middle"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': bigLabelSize }"
      >
        {{ '$' + (maxValue - amount) }}
      </text>

      <text
        *ngIf="amount !== maxValue"
        class="rpb-text rpb-sub"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth"
        dominant-baseline="middle"
        text-anchor="middle"
        [attr.aria-valuenow]="subText"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': labelSize }"
      >
        {{ subText }}
      </text>
      <text
        [attr.x]="strokeWidth + 4"
        [attr.y]="diameter / 2 + this.strokeWidth + labelYCoordinate"
        text-anchor="end"
        dominant-baseline="auto"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': labelSize }"
      >
        $0
      </text>
      <text
        [attr.x]="diameter + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth + labelYCoordinate"
        text-anchor="middle"
        dominant-baseline="auto"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': labelSize }"
      >
        {{ '$' + maxValue }}
      </text>
      <path
        *ngIf="amount === maxValue"
        class="rpb-done-check"
        [attr.d]="checkPath"
        [style.stroke-width.px]="checkWidth"
        [style.transform]="
          'translate(' + this.strokeWidth + 'px, ' + this.strokeWidth + 'px)'
        "
      />
    </svg>
  `, isInline: true, styles: [":host{margin-top:-156px;display:inline-block}.dot{fill:transparent;stroke-linecap:round;transition:all 1s ease-out;stroke:#fff}.rpb-back{fill:transparent;stroke-linecap:round}.rpb-back-done{stroke:#008a24;stroke-dasharray:initial}.rpb-arc{fill:transparent;stroke-linecap:round;animation-fill-mode:forwards}.rpb-arc-done{stroke:#008a24;stroke-dasharray:initial!important}.rpb-done-check{fill:none;stroke:#008a24}.gauge-examples .gauge-start{padding-left:4px;min-width:auto}.rpb-price{font-size:18px}.rpb-sub{font-size:12px;width:105px;height:18px;line-height:18px}.overflow-inherit{overflow:inherit!important}.ng-circle{height:14px;width:14px}.rpb-arc{height:65px;width:124px}\n"], dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }], animations: [
            trigger('progress', [
                state('true', style({
                    'stroke-dasharray': '{{final}}'
                }), { params: { default: '0,100', final: '100,100' } }),
                transition('* => true', [animate('2s')]),
                transition('* => false', [animate('0s')])
            ])
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: GaugeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-gauge', template: `
    <span aria-label="Gauge" role="text"></span><br />
    <span aria-label="$0 is the minimum" role="text"></span><br />
    <span
      [attr.aria-label]="'$' + this.maxValue + ' is the maximum'"
      role="text"
    ></span
    ><br />
    <span [attr.aria-label]="getCompletedPerc()" role="text"></span> <br />
    <span [attr.aria-label]="getProgressed()" role="text"></span> <br />
    <span
      [attr.aria-label]="
        '$' + (maxValue - amount) + ' is the amount left to achieve the goal.'
      "
      role="text"
    ></span>
    <br />
    <span [attr.aria-label]="getReamingingPerc()" role="text"> </span> <br />
    <svg
      #gauge
      tabindex="-1"
      [attr.viewBox]="
        '0 0 ' +
        (diameter + this.strokeWidth + 4) +
        ' ' +
        (diameter + this.strokeWidth + 4)
      "
      [style.width.px]="diameter + this.strokeWidth + 4"
      [style.height.px]="diameter"
      role="presentation"
      focusable="true"
      (focus)="onFocus($event)"
      class="overflow-inherit"
    >
      <path
        [attr.d]="describeArc(90, 270, 1, 1)"
        class="rpb-back"
        [class.rpb-back-done]="amount === maxValue"
        [style.stroke]="gaugeColor"
        [style.stroke-width.px]="strokeWidth"
      />

      <path
        id="{{ idStr }}"
        [attr.d]="describeArc(90, 270, 1, 1)"
        *ngIf="amount >= 0"
        class="rpb-arc"
        [style.stroke-dasharray]="'0' + ', ' + circumference"
        [class.rpb-arc-done]="amount === maxValue"
        [style.stroke-width.px]="strokeWidth"
        [@progress]="{
          value: true,
          params: {
            default: '0' + ', ' + circumference,
            final:
              (circumference / 2) * (amount / maxValue) + ', ' + circumference
          }
        }"
      ></path>

      

      <circle
        _ngcontent-day-c112=""
        class="ng-tns-c112-4 ng-star-inserted ng-circle gauge-outer-circle"
        [attr.cx]="endPoint(endAngle()).x"
        [attr.cy]="endPoint(endAngle()).y"
        [attr.r]="progressCircleSize"
        fill="white"
        *ngIf="amount != maxValue"
        [attr.stroke]="outerCircleColor"
        stroke-width="4"
      />

      <text
        *ngIf="amount !== maxValue"
        class="rpb-text rpb-price"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 3.1 + this.strokeWidth + 0.5"
        dominant-baseline="middle"
        text-anchor="middle"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': bigLabelSize }"
      >
        {{ '$' + (maxValue - amount) }}
      </text>

      <text
        *ngIf="amount !== maxValue"
        class="rpb-text rpb-sub"
        [attr.x]="diameter / 2 + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth"
        dominant-baseline="middle"
        text-anchor="middle"
        [attr.aria-valuenow]="subText"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': labelSize }"
      >
        {{ subText }}
      </text>
      <text
        [attr.x]="strokeWidth + 4"
        [attr.y]="diameter / 2 + this.strokeWidth + labelYCoordinate"
        text-anchor="end"
        dominant-baseline="auto"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': labelSize }"
      >
        $0
      </text>
      <text
        [attr.x]="diameter + this.strokeWidth"
        [attr.y]="diameter / 2 + this.strokeWidth + labelYCoordinate"
        text-anchor="middle"
        dominant-baseline="auto"
        [attr.aria-hidden]="true"
        [ngStyle]="{ 'font-size': labelSize }"
      >
        {{ '$' + maxValue }}
      </text>
      <path
        *ngIf="amount === maxValue"
        class="rpb-done-check"
        [attr.d]="checkPath"
        [style.stroke-width.px]="checkWidth"
        [style.transform]="
          'translate(' + this.strokeWidth + 'px, ' + this.strokeWidth + 'px)'
        "
      />
    </svg>
  `, changeDetection: ChangeDetectionStrategy.OnPush, host: {
                        class: 'cds-gauge'
                    }, animations: [
                        trigger('progress', [
                            state('true', style({
                                'stroke-dasharray': '{{final}}'
                            }), { params: { default: '0,100', final: '100,100' } }),
                            transition('* => true', [animate('2s')]),
                            transition('* => false', [animate('0s')])
                        ])
                    ], styles: [":host{margin-top:-156px;display:inline-block}.dot{fill:transparent;stroke-linecap:round;transition:all 1s ease-out;stroke:#fff}.rpb-back{fill:transparent;stroke-linecap:round}.rpb-back-done{stroke:#008a24;stroke-dasharray:initial}.rpb-arc{fill:transparent;stroke-linecap:round;animation-fill-mode:forwards}.rpb-arc-done{stroke:#008a24;stroke-dasharray:initial!important}.rpb-done-check{fill:none;stroke:#008a24}.gauge-examples .gauge-start{padding-left:4px;min-width:auto}.rpb-price{font-size:18px}.rpb-sub{font-size:12px;width:105px;height:18px;line-height:18px}.overflow-inherit{overflow:inherit!important}.ng-circle{height:14px;width:14px}.rpb-arc{height:65px;width:124px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { idStr: [{
                type: Input
            }], labelSize: [{
                type: Input
            }], diameter: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], progressCircleSize: [{
                type: Input
            }], subText: [{
                type: Input
            }], subText1: [{
                type: Input
            }], progressColor: [{
                type: Input
            }], gaugeColor: [{
                type: Input
            }], outerCircleColor: [{
                type: Input
            }], maxValue: [{
                type: Input
            }], amount: [{
                type: Input
            }], announceText: [{
                type: Input
            }], stepTxt: [{
                type: Input
            }], gauge: [{
                type: ViewChild,
                args: ['gauge']
            }] } });

const COMPONENTS$6 = [GaugeComponent];
class CdsGaugeModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsGaugeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsGaugeModule, declarations: [GaugeComponent], imports: [CommonModule], exports: [GaugeComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsGaugeModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsGaugeModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$6],
                    exports: [...COMPONENTS$6]
                }]
        }] });

let nextId$1 = 0;
class CdsTab {
    constructor() {
        this.isActive = false;
        this.themeDark = false;
        this.themeLight = false;
        this.isFocused = false;
        this.isSelected = false;
        /**
         * emits when the mobile layout label is clicked
         */
        this.selectTabCall = new EventEmitter();
        this.tabId = `tab-${nextId$1++}`;
        this.keyboardFocused = false;
    }
    // Tabs/Tabs-dark Mobile Style Profile: emit index to parent TabGroup
    selectTab() {
        this.selectTabCall.emit(this.tabIdx);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTab, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTab, selector: "cds-tab", inputs: { label: "label" }, outputs: { selectTabCall: "selectTabCall" }, host: { properties: { "class.tab-panel-active": "this.isActive", "class.cds-tab-dark": "this.themeDark", "class.cds-tab-light": "this.themeLight" }, classAttribute: "cds-tab" }, exportAs: ["cdsTab"], ngImport: i0, template: `
    <!-- Mobile layout -->
    <button
      type="button"
      [attr.id]="hostId + '-label-mobile-' + tabId"
      (click)="selectTab()"
      class="d-block d-sm-none tabs-label-mobile"
      [ngClass]="{ 'tab-label-mobile-active': isActive }"
      [attr.aria-expanded]="isSelected"
    >
    <div>
      <cds-icon
        [ngClass]="{ 'icon-expanded': isSelected }"
        name="02-down"
        pathColor="#056dae"
        ariaLabel="Down arrow to expand"
        altText="Down arrow"
        
      >
      </cds-icon>
        <span class="tab-label-content-mobile" [innerHtml]="label"></span>
      </div>
    </button>
    <!-- End mobile layout -->
    <div
      role="tabpanel"
      tabindex="0"
      [attr.id]="tabId"
      [attr.aria-labelledby]="hostId + '-' + tabId"
      [ngClass]="{ 'tab-panel-active': isActive }"
      class="tab-panel"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTab, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-tab',
                    exportAs: 'cdsTab',
                    host: { class: 'cds-tab' },
                    template: `
    <!-- Mobile layout -->
    <button
      type="button"
      [attr.id]="hostId + '-label-mobile-' + tabId"
      (click)="selectTab()"
      class="d-block d-sm-none tabs-label-mobile"
      [ngClass]="{ 'tab-label-mobile-active': isActive }"
      [attr.aria-expanded]="isSelected"
    >
    <div>
      <cds-icon
        [ngClass]="{ 'icon-expanded': isSelected }"
        name="02-down"
        pathColor="#056dae"
        ariaLabel="Down arrow to expand"
        altText="Down arrow"
        
      >
      </cds-icon>
        <span class="tab-label-content-mobile" [innerHtml]="label"></span>
      </div>
    </button>
    <!-- End mobile layout -->
    <div
      role="tabpanel"
      tabindex="0"
      [attr.id]="tabId"
      [attr.aria-labelledby]="hostId + '-' + tabId"
      [ngClass]="{ 'tab-panel-active': isActive }"
      class="tab-panel"
    >
      <ng-content></ng-content>
    </div>
  `
                }]
        }], ctorParameters: () => [], propDecorators: { isActive: [{
                type: HostBinding,
                args: ['class.tab-panel-active']
            }], themeDark: [{
                type: HostBinding,
                args: ['class.cds-tab-dark']
            }], themeLight: [{
                type: HostBinding,
                args: ['class.cds-tab-light']
            }], label: [{
                type: Input
            }], selectTabCall: [{
                type: Output
            }] } });

let nextId = 0;
class CdsTabGroup {
    set activeTab(index) {
        if (Number(index) === this.activeTab)
            return;
        this._activeTab = Number(index);
        if (this.tabs && this.tabs.length)
            this.selectTab(this._activeTab);
    }
    get activeTab() {
        return this._activeTab;
    }
    set ariaLabel(label) {
        if (typeof label !== 'undefined') {
            this._ariaLabel = label;
        }
    }
    set cdsTabGroup(tabGroupType) {
        if (!tabGroupType)
            return;
        this.themeDark = this.themeLight = false;
        switch (tabGroupType) {
            case 'dark':
                this.themeDark = true;
                break;
            case 'light':
                this.themeLight = true;
                break;
        }
    }
    // #endregion
    // #region // ------- Constructor/Lifecycle Hooks -------- //
    constructor(el) {
        this.el = el;
        this.focusedTab = 0;
        // #endregion
        // #region // -------- Inputs/Outputs ---------- //
        this._activeTab = 0;
        // @Input() focusedTab: number = 0;
        this.activeTabChange = new EventEmitter();
        this.themeDark = true;
        this.themeLight = false;
        this.hostId = `tabs-${nextId++}`;
    }
    ngAfterContentInit() {
        this.setInitial(this.activeTab);
        // Set Tab child(ren) component properties based on TabGroup @Inputs and properties
        this.tabs.forEach((tab, index) => {
            tab.tabIdx = index;
            tab.hostId = this.hostId;
            tab.tabsLength = this.tabs.length;
            tab.themeDark = this.themeDark;
            tab.themeLight = this.themeLight;
            // Subscribe to Tab child(ren) selectTabCall EventEmitter for Tabs mobile style profile tab select
            tab.selectTabCall.subscribe(tabIdx => {
                this.selectTabMobile(tabIdx);
            });
        });
    }
    // #endregion
    // #region // ------------ Methods ------------- //
    setInitial(index) {
        this.focusedTab = index;
        const tab = this.tab(index);
        tab.isFocused = tab.isActive = tab.isSelected = true;
    }
    unSelectAll() {
        this.tabs.forEach(tab => this.setActiveTab(tab, false));
    }
    tab(index) {
        return this.tabs.find((tab, i) => i === index);
    }
    setActiveTab(tab, bool = true) {
        tab.isActive = tab.isFocused = tab.isSelected = tab.keyboardFocused = bool;
    }
    focusActive() {
        setTimeout(() => this.el.nativeElement.querySelector('.tab-focused').focus(), 100);
    }
    currentTab() {
        return this.tabs.find(tab => tab.isActive);
    }
    selectTab(index) {
        this.unSelectAll();
        this.setActiveTab(this.tab(index));
        this.focusActive();
        this._activeTab = this.focusedTab = index;
        this.activeTabChange.emit(index);
    }
    // Method for 'Tabs' style profile mobile accordion layout selection
    selectTabMobile(index) {
        this.unSelectAll();
        this.setActiveTab(this.tab(index));
        this._activeTab = this.focusedTab = index;
        this.activeTabChange.emit(index);
    }
    clearFocus() {
        this.tabs.forEach(tab => (tab.isFocused = tab.keyboardFocused = false));
    }
    removeKeyboardFocus() {
        this.tabs.forEach(tab => (tab.keyboardFocused = false));
        this.tabs.forEach(tab => (tab.isFocused = false));
        this.focusedTab = this.activeTab;
    }
    moveFocus(dir) {
        this.setTabFocus((this.focusedTab + this.tabs.length + dir) % this.tabs.length);
    }
    setTabFocus(index) {
        this.clearFocus();
        this.tab(index).isFocused = true;
        this.focusActive();
        this.focusedTab = index;
    }
    stopBubble(event) {
        event.preventDefault();
        event.stopPropagation();
    }
    handleKeyPress(event) {
        if (event.which !== KEY_TAB)
            this.stopBubble(event);
        switch (event.which) {
            case KEY_HOME:
                this.setTabFocus(0);
                break;
            case KEY_END:
                this.setTabFocus(this.tabs.length - 1);
                break;
            case KEY_ARROW_LEFT:
                this.moveFocus(-1);
                break;
            case KEY_ARROW_RIGHT:
                this.moveFocus(1);
                break;
            case KEY_SPACEBAR:
            case KEY_ENTER:
                this.selectTab(this.focusedTab);
                break;
            case KEY_TAB:
                this.removeKeyboardFocus();
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabGroup, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTabGroup, selector: "cds-tab-group", inputs: { activeTab: "activeTab", ariaLabel: "ariaLabel", cdsTabGroup: "cdsTabGroup" }, outputs: { activeTabChange: "activeTabChange" }, host: { properties: { "class.cds-tabs-theme-dark": "this.themeDark", "class.cds-tabs-theme-light": "this.themeLight" }, classAttribute: "cds-tab-group" }, queries: [{ propertyName: "tabs", predicate: i0.forwardRef(() => CdsTab) }], exportAs: ["cdsTabGroup"], ngImport: i0, template: `
    <div
      role="tablist"
      [attr.id]="hostId"
      [class]="'tab-list tabs-' + tabs.length"
      [ngClass]="{ 'd-none d-sm-block': tabs.length > 2 }"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
    >
      <button
        *ngFor="let tab of tabs.toArray(); let idx = index"
        role="tab"
        type="button"
        [attr.id]="hostId + '-' + tab.tabId"
        [attr.tabindex]="tab.isActive ? 0 : -1"
        class="tab-label"
        [ngClass]="{
          'tab-label-active': tab.isActive,
          'tab-label-inactive': !tab.isActive,
          'tab-focused': tab.isFocused,
          'tab-keyboard-focused': tab.keyboardFocused
        }"
        (click)="selectTab(idx)"
        (keydown)="handleKeyPress($event)"
        [attr.aria-selected]="tab.isSelected"
        [attr.aria-controls]="tab.tabId"
      >
        <span class="tab-label-content" [innerHtml]="tab.label"></span>
      </button>
    </div>

    <div class="tab-content-wrapper noPadding">
      <ng-content></ng-content>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabGroup, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-tab-group',
                    exportAs: 'cdsTabGroup',
                    host: { class: 'cds-tab-group' },
                    template: `
    <div
      role="tablist"
      [attr.id]="hostId"
      [class]="'tab-list tabs-' + tabs.length"
      [ngClass]="{ 'd-none d-sm-block': tabs.length > 2 }"
      [attr.aria-label]="_ariaLabel ? _ariaLabel : null"
    >
      <button
        *ngFor="let tab of tabs.toArray(); let idx = index"
        role="tab"
        type="button"
        [attr.id]="hostId + '-' + tab.tabId"
        [attr.tabindex]="tab.isActive ? 0 : -1"
        class="tab-label"
        [ngClass]="{
          'tab-label-active': tab.isActive,
          'tab-label-inactive': !tab.isActive,
          'tab-focused': tab.isFocused,
          'tab-keyboard-focused': tab.keyboardFocused
        }"
        (click)="selectTab(idx)"
        (keydown)="handleKeyPress($event)"
        [attr.aria-selected]="tab.isSelected"
        [attr.aria-controls]="tab.tabId"
      >
        <span class="tab-label-content" [innerHtml]="tab.label"></span>
      </button>
    </div>

    <div class="tab-content-wrapper noPadding">
      <ng-content></ng-content>
    </div>
  `
                }]
        }], ctorParameters: () => [{ type: i0.ElementRef }], propDecorators: { tabs: [{
                type: ContentChildren,
                args: [forwardRef(() => CdsTab), { descendants: false }]
            }], activeTab: [{
                type: Input
            }], activeTabChange: [{
                type: Output
            }], ariaLabel: [{
                type: Input
            }], themeDark: [{
                type: HostBinding,
                args: ['class.cds-tabs-theme-dark']
            }], themeLight: [{
                type: HostBinding,
                args: ['class.cds-tabs-theme-light']
            }], cdsTabGroup: [{
                type: Input
            }] } });

const COMPONENTS$5 = [CdsTabGroup, CdsTab];
const MODULES$1 = [CommonModule, CdsIconModule];
class CdsTabsModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTabsModule, declarations: [CdsTabGroup, CdsTab], imports: [CommonModule, CdsIconModule], exports: [CdsTabGroup, CdsTab] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabsModule, imports: [MODULES$1] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTabsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$5],
                    imports: [...MODULES$1],
                    exports: [CdsTabGroup, CdsTab]
                }]
        }] });

class CdsTimeline {
    set totalSteps(stepNumber) {
        this._totalSteps = stepNumber;
        this.totalStepArray = [];
        for (let i = 0; i < this.totalSteps; i++) {
            this.totalStepArray[i] = i;
        }
    }
    set currentStep(stepNumber) {
        this._currentStep = stepNumber;
        this.updateStep();
    }
    // #endregion --------Inputs/Outputs------------ //
    //#region  --------------getter -----------//
    get totalSteps() {
        return this._totalSteps;
    }
    get currentStep() {
        return this._currentStep;
    }
    //#endregion  --------------getter -----------//
    constructor(_liveAnnouncer) {
        this._liveAnnouncer = _liveAnnouncer;
        this.totalStepArray = [];
        this.currentStepArray = [];
        this._currentStep = 0;
        this.cardContent = false;
        this.adaLabel = false;
        this.isVertical = false;
        this.currentStepChange = new EventEmitter();
        this.totalStepsChange = new EventEmitter();
        if (isDevMode()) {
            console.error('%cThe Common UI CDS Timeline <citi-timeline> is deprecated. Please discontinue using.', 'background-color: #f00; color: #fff;');
        }
    }
    ngOnInit() { }
    currentStepLimit(stepNumber) {
        return Math.min(this.totalSteps, Math.max(1, stepNumber));
    }
    updateStep() {
        for (let i = 0; i < this._currentStep; i++) {
            this.currentStepArray[i] = i;
        }
        // Make sure totalSteps is valid
        this.totalSteps = Math.max(2, this.totalSteps);
        // Make sure currentStep is valid
        this._currentStep = this.currentStepLimit(this.currentStep);
        this.currentStepChange.emit(this.currentStep);
        this.totalStepsChange.emit(this.totalSteps);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTimeline, selector: "cds-timeline", inputs: { iconTemplateRefArr: "iconTemplateRefArr", cardTemplateRefArr: "cardTemplateRefArr", cardContent: "cardContent", adaLabel: "adaLabel", totalSteps: "totalSteps", currentStep: "currentStep", isVertical: "isVertical" }, outputs: { currentStepChange: "currentStepChange", totalStepsChange: "totalStepsChange" }, ngImport: i0, template: `
    <div class="cds-timeline" [ngClass]="{ 'timeline-vertical': isVertical }">
      <span class="sr-only">list of {{ totalSteps }} steps</span>
      <div class="cds-timeline-event" *ngFor="let step of totalStepArray">
        <div class="cds-timeline-track-container">
          <div
            class="cds-timeline-point"
            [ngClass]="{ 'timeline-completed': step < currentStep }"
          >
            <span class="sr-only" *ngIf="adaLabel">{{
              step + 1 === currentStep
                ? 'current'
                : step < currentStep
                ? 'completed'
                : ''
            }}</span>
            <div *ngIf="iconTemplateRefArr && step < currentStep">
              <ng-container
                [ngTemplateOutlet]="iconTemplateRefArr[step]"
              ></ng-container>
            </div>
          </div>
          <div
            class="cds-timeline-track"
            [ngClass]="{ 'timeline-completed': step + 1 < currentStep }"
          ></div>
        </div>
        <div class="cds-timeline-content">
          <div [ngClass]="{ 'cds-timeline-card': cardContent }">
            <ng-container
              [ngTemplateOutlet]="cardTemplateRefArr[step]"
            ></ng-container>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, styles: [".cds-timeline:not(.timeline-vertical).cds-timeline{display:flex;flex-grow:1;flex-direction:row}.cds-timeline:not(.timeline-vertical) .cds-timeline-event{display:flex;position:relative;flex-direction:column;flex:1;min-width:16%}.cds-timeline:not(.timeline-vertical) .cds-timeline-event:last-child{align-items:flex-start}.cds-timeline:not(.timeline-vertical) .cds-timeline-content{padding:8px 0 0;margin-right:16px}.cds-timeline:not(.timeline-vertical) .cds-timeline-track-container{flex:0;display:flex;align-items:center}.cds-timeline:not(.timeline-vertical) .cds-timeline-point{display:flex;align-self:baseline;border-radius:50%;width:1.2rem;height:1.2rem;background-color:#ccc;margin:8.3px 0}.cds-timeline:not(.timeline-vertical) .cds-timeline-point.timeline-completed{width:36px;height:36px;background-color:#056dae;margin:0;display:flex;align-items:center;justify-content:center}.cds-timeline:not(.timeline-vertical) .cds-timeline-track{flex-grow:1;border-top:2px dashed #cccccc}.cds-timeline:not(.timeline-vertical) .cds-timeline-track.timeline-completed{border-top:2px solid #056dae}.cds-timeline-card{padding:12px;border-radius:8px;background-color:#fff;box-shadow:0 1px 10px #0000001f}.cds-timeline.timeline-vertical .cds-timeline-card{width:240px}.cds-timeline.timeline-vertical.cds-timeline{display:flex;flex-grow:1;flex-direction:column;padding:1rem}.cds-timeline.timeline-vertical .cds-timeline-event{display:flex;position:relative;min-height:70px;flex:1}.cds-timeline.timeline-vertical .cds-timeline-event:last-child{min-height:0;align-items:flex-start}.cds-timeline.timeline-vertical .cds-timeline-content{flex:1;padding:0 12px;margin-bottom:16px}.cds-timeline.timeline-vertical .cds-timeline-track-container{flex:0;display:flex;align-items:center;flex-direction:column}.cds-timeline.timeline-vertical .cds-timeline-point{display:flex;align-self:baseline;border-radius:50%;width:1.2rem;height:1.2rem;background-color:#ccc;margin:0 8.3px}.cds-timeline.timeline-vertical .cds-timeline-point.timeline-completed{width:36px;height:36px;background-color:#056dae;margin:0;display:flex;align-items:center;justify-content:center}.cds-timeline.timeline-vertical .cds-timeline-track{flex-grow:1;border-left:2px dashed #cccccc}.cds-timeline.timeline-vertical .cds-timeline-track.timeline-completed{border-left:2px solid #056dae}@media only screen and (max-width: 767px){.cds-timeline{min-width:800px}}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline, decorators: [{
            type: Component,
            args: [{ selector: 'cds-timeline', template: `
    <div class="cds-timeline" [ngClass]="{ 'timeline-vertical': isVertical }">
      <span class="sr-only">list of {{ totalSteps }} steps</span>
      <div class="cds-timeline-event" *ngFor="let step of totalStepArray">
        <div class="cds-timeline-track-container">
          <div
            class="cds-timeline-point"
            [ngClass]="{ 'timeline-completed': step < currentStep }"
          >
            <span class="sr-only" *ngIf="adaLabel">{{
              step + 1 === currentStep
                ? 'current'
                : step < currentStep
                ? 'completed'
                : ''
            }}</span>
            <div *ngIf="iconTemplateRefArr && step < currentStep">
              <ng-container
                [ngTemplateOutlet]="iconTemplateRefArr[step]"
              ></ng-container>
            </div>
          </div>
          <div
            class="cds-timeline-track"
            [ngClass]="{ 'timeline-completed': step + 1 < currentStep }"
          ></div>
        </div>
        <div class="cds-timeline-content">
          <div [ngClass]="{ 'cds-timeline-card': cardContent }">
            <ng-container
              [ngTemplateOutlet]="cardTemplateRefArr[step]"
            ></ng-container>
          </div>
        </div>
      </div>
    </div>
  `, styles: [".cds-timeline:not(.timeline-vertical).cds-timeline{display:flex;flex-grow:1;flex-direction:row}.cds-timeline:not(.timeline-vertical) .cds-timeline-event{display:flex;position:relative;flex-direction:column;flex:1;min-width:16%}.cds-timeline:not(.timeline-vertical) .cds-timeline-event:last-child{align-items:flex-start}.cds-timeline:not(.timeline-vertical) .cds-timeline-content{padding:8px 0 0;margin-right:16px}.cds-timeline:not(.timeline-vertical) .cds-timeline-track-container{flex:0;display:flex;align-items:center}.cds-timeline:not(.timeline-vertical) .cds-timeline-point{display:flex;align-self:baseline;border-radius:50%;width:1.2rem;height:1.2rem;background-color:#ccc;margin:8.3px 0}.cds-timeline:not(.timeline-vertical) .cds-timeline-point.timeline-completed{width:36px;height:36px;background-color:#056dae;margin:0;display:flex;align-items:center;justify-content:center}.cds-timeline:not(.timeline-vertical) .cds-timeline-track{flex-grow:1;border-top:2px dashed #cccccc}.cds-timeline:not(.timeline-vertical) .cds-timeline-track.timeline-completed{border-top:2px solid #056dae}.cds-timeline-card{padding:12px;border-radius:8px;background-color:#fff;box-shadow:0 1px 10px #0000001f}.cds-timeline.timeline-vertical .cds-timeline-card{width:240px}.cds-timeline.timeline-vertical.cds-timeline{display:flex;flex-grow:1;flex-direction:column;padding:1rem}.cds-timeline.timeline-vertical .cds-timeline-event{display:flex;position:relative;min-height:70px;flex:1}.cds-timeline.timeline-vertical .cds-timeline-event:last-child{min-height:0;align-items:flex-start}.cds-timeline.timeline-vertical .cds-timeline-content{flex:1;padding:0 12px;margin-bottom:16px}.cds-timeline.timeline-vertical .cds-timeline-track-container{flex:0;display:flex;align-items:center;flex-direction:column}.cds-timeline.timeline-vertical .cds-timeline-point{display:flex;align-self:baseline;border-radius:50%;width:1.2rem;height:1.2rem;background-color:#ccc;margin:0 8.3px}.cds-timeline.timeline-vertical .cds-timeline-point.timeline-completed{width:36px;height:36px;background-color:#056dae;margin:0;display:flex;align-items:center;justify-content:center}.cds-timeline.timeline-vertical .cds-timeline-track{flex-grow:1;border-left:2px dashed #cccccc}.cds-timeline.timeline-vertical .cds-timeline-track.timeline-completed{border-left:2px solid #056dae}@media only screen and (max-width: 767px){.cds-timeline{min-width:800px}}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { iconTemplateRefArr: [{
                type: Input
            }], cardTemplateRefArr: [{
                type: Input
            }], cardContent: [{
                type: Input
            }], adaLabel: [{
                type: Input
            }], totalSteps: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], isVertical: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }], totalStepsChange: [{
                type: Output
            }] } });

const COMPONENTS$4 = [CdsTimeline];
class CdsTimelineModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimelineModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTimelineModule, declarations: [CdsTimeline], imports: [CommonModule, CdsIconModule], exports: [CdsTimeline] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimelineModule, imports: [CommonModule, CdsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimelineModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$4],
                    imports: [CommonModule, CdsIconModule],
                    exports: [...COMPONENTS$4]
                }]
        }] });

class CdsTimeline2Step {
    constructor() {
        this.role = 'listitem';
        this.checkmarkIcon = true;
    }
    getStepState() {
        if (this._isComplete && this._isNextComplete) {
            return 'completed';
        }
        else if (this._isComplete && !this._isNextComplete) {
            return 'current';
        }
    }
    _getCheckmarkSize() {
        if (this.smallPoint) {
            return 16;
        }
        else if (!this.smallPoint && !this.stepIconTemplate) {
            return 24;
        }
        else {
            return null;
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2Step, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTimeline2Step, selector: "cds-timeline2-step", inputs: { stepColor: "stepColor", stepIconColor: "stepIconColor", lineColor: "lineColor", checkmarkIcon: "checkmarkIcon" }, host: { properties: { "class.cds-tl2-step-point-small": "smallPoint", "attr.role": "this.role" }, classAttribute: "cds-timeline2-step" }, queries: [{ propertyName: "stepIconTemplate", first: true, predicate: ["stepIcon"], descendants: true, read: TemplateRef }], exportAs: ["cdsTimeline2Step"], ngImport: i0, template: `
    <!-- TODO: Revisit later adding selector here for content top -->
    <div class="cds-tl2-track-wrapper">
      <div class="cds-tl2-point" [class.cds-tl2-point-complete]="_isComplete">
        <!-- If journey teams use a custom icon, there will be a template. Otherwise we have a default for them-->
        <div *ngIf="stepIconTemplate; else stepIconDefault">
          <div
            [ngStyle]="{ 'background-color': stepColor }"
          >
            <ng-container [ngTemplateOutlet]="stepIconTemplate"></ng-container>
            <span class="sr-only">{{ getStepState() }}</span>
          </div>
        </div>

        <ng-template #stepIconDefault>
          <div
            [ngStyle]="{ 'background-color': stepColor }"
            *ngIf="_isComplete"
          >
            <cds-icon
              *ngIf="checkmarkIcon"
              type="2021"
              name="059-check"
              [svgStyle]="{ 'width.px': _getCheckmarkSize(), 'height.px': _getCheckmarkSize() }"
              [pathColor]="stepIconColor || 'white'"
              aria-hidden="true"
              altText="Check Icon"
              ariaLabel="Check Icon"
            >
            </cds-icon>
          </div>
          <span class="sr-only">{{ getStepState() }}</span>

        </ng-template>
      </div>
      <div
        class="cds-tl2-track"
        [class.cds-tl2-track-complete]="_isNextComplete"
        [style.borderColor]="_isNextComplete && _lineColorPost"
      ></div>
    </div>
    <div
      class="cds-tl2-content-wrapper"
      [class.cds-tl2-content-complete]="_isComplete"
    >
      <ng-content></ng-content>
    </div>
  `, isInline: true, dependencies: [{ kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2Step, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-timeline2-step',
                    exportAs: 'cdsTimeline2Step',
                    host: {
                        class: 'cds-timeline2-step',
                        '[class.cds-tl2-step-point-small]': 'smallPoint'
                    },
                    template: `
    <!-- TODO: Revisit later adding selector here for content top -->
    <div class="cds-tl2-track-wrapper">
      <div class="cds-tl2-point" [class.cds-tl2-point-complete]="_isComplete">
        <!-- If journey teams use a custom icon, there will be a template. Otherwise we have a default for them-->
        <div *ngIf="stepIconTemplate; else stepIconDefault">
          <div
            [ngStyle]="{ 'background-color': stepColor }"
          >
            <ng-container [ngTemplateOutlet]="stepIconTemplate"></ng-container>
            <span class="sr-only">{{ getStepState() }}</span>
          </div>
        </div>

        <ng-template #stepIconDefault>
          <div
            [ngStyle]="{ 'background-color': stepColor }"
            *ngIf="_isComplete"
          >
            <cds-icon
              *ngIf="checkmarkIcon"
              type="2021"
              name="059-check"
              [svgStyle]="{ 'width.px': _getCheckmarkSize(), 'height.px': _getCheckmarkSize() }"
              [pathColor]="stepIconColor || 'white'"
              aria-hidden="true"
              altText="Check Icon"
              ariaLabel="Check Icon"
            >
            </cds-icon>
          </div>
          <span class="sr-only">{{ getStepState() }}</span>

        </ng-template>
      </div>
      <div
        class="cds-tl2-track"
        [class.cds-tl2-track-complete]="_isNextComplete"
        [style.borderColor]="_isNextComplete && _lineColorPost"
      ></div>
    </div>
    <div
      class="cds-tl2-content-wrapper"
      [class.cds-tl2-content-complete]="_isComplete"
    >
      <ng-content></ng-content>
    </div>
  `
                }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], stepColor: [{
                type: Input
            }], stepIconColor: [{
                type: Input
            }], lineColor: [{
                type: Input
            }], checkmarkIcon: [{
                type: Input
            }], stepIconTemplate: [{
                type: ContentChild,
                args: ['stepIcon', { read: TemplateRef }]
            }] } });

class CdsTimeline2 {
    // #region ========== Constructor / Lifecycle Hooks ========== //
    constructor() {
        this.role = 'list';
        this.smallPoints = false;
    }
    ngOnChanges(simpleChanges) {
        for (const propName in simpleChanges) {
            const change = simpleChanges[propName];
            if (propName === 'currentStep' && !change.firstChange) {
                this.updateSteps(change.currentValue);
            }
        }
    }
    ngAfterViewInit() {
        setTimeout(() => {
            this.updateSteps(this.currentStep);
        }, 0);
    }
    ngAfterContentInit() {
        this.steps.forEach((step, index) => {
            const previousStep = this.steps.find((s, idx) => idx === index - 1);
            // If `lineColor` is set for the step, the `_lineColorPost` needs to be set for previous step
            if (step.lineColor && previousStep) {
                previousStep._lineColorPost = step.lineColor;
            }
            // If `lineColor` is set from the step component, it uses that color.  Otherwise it uses `lineColor` from this (the parent) component.
            step._lineColorPre = step.lineColor ? step.lineColor : !Array.isArray(this.lineColor) ? this.lineColor : this.lineColor[index - 1] || null;
            step._lineColorPost = !Array.isArray(this.lineColor) ? this.lineColor : this.lineColor[index] || null;
            // sets the icon color, unless already set from the child component
            if (!step.stepColor) {
                step.stepColor = !Array.isArray(this.stepColor) ? this.stepColor : this.stepColor[index] || null;
            }
            if (this.smallPoints)
                step.smallPoint = true;
        });
    }
    //endregion
    // #region ========== Methods ========== //
    updateSteps(currentStep) {
        this.steps.forEach((step, index) => {
            const stepNumber = index + 1;
            step._isComplete = currentStep >= stepNumber;
            step._isNextComplete = currentStep > stepNumber;
        });
    }
    getTotalStepsString() {
        return 'list of ' + this.steps.length + ' steps';
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsTimeline2, selector: "cds-timeline2", inputs: { currentStep: "currentStep", isVertical: "isVertical", smallPoints: "smallPoints", lineColor: "lineColor", stepColor: "stepColor" }, host: { properties: { "class.cds-timeline2-vertical": "this.isVertical", "attr.role": "this.role" }, classAttribute: "cds-timeline2" }, queries: [{ propertyName: "steps", predicate: CdsTimeline2Step }], exportAs: ["cdsTimeline2"], usesOnChanges: true, ngImport: i0, template: `
    <ng-content></ng-content>
  `, isInline: true }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2, decorators: [{
            type: Component,
            args: [{
                    selector: 'cds-timeline2',
                    exportAs: 'cdsTimeline2',
                    host: { class: 'cds-timeline2' },
                    template: `
    <ng-content></ng-content>
  `,
                }]
        }], ctorParameters: () => [], propDecorators: { currentStep: [{
                type: Input
            }], isVertical: [{
                type: HostBinding,
                args: ['class.cds-timeline2-vertical']
            }, {
                type: Input
            }], smallPoints: [{
                type: Input
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], steps: [{
                type: ContentChildren,
                args: [CdsTimeline2Step]
            }], lineColor: [{
                type: Input
            }], stepColor: [{
                type: Input
            }] } });

const COMPONENTS$3 = [CdsTimeline2, CdsTimeline2Step];
const MODULES = [CommonModule, CdsIconModule];
class CdsTimeline2Module {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2Module, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2Module, declarations: [CdsTimeline2, CdsTimeline2Step], imports: [CommonModule, CdsIconModule], exports: [CdsTimeline2, CdsTimeline2Step] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2Module, imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTimeline2Module, decorators: [{
            type: NgModule,
            args: [{
                    imports: [...MODULES],
                    declarations: [...COMPONENTS$3],
                    exports: [...COMPONENTS$3]
                }]
        }] });

class CdsStepper {
    constructor(_liveAnnouncer) {
        this._liveAnnouncer = _liveAnnouncer;
        this._currentStep = 0;
        this.small = false;
        //#endregion  --------------getter -----------//
        this.currentStepChange = new EventEmitter();
        this.totalStepsChange = new EventEmitter();
    }
    // #region --------Inputs------------ //
    set announceStr(announceString) {
        this._announceStr = announceString;
    }
    set currentStep(stepNumber) {
        this._currentStep = Math.min(Math.max(0, stepNumber), this.totalSteps);
        this.updateStep;
    }
    set totalSteps(stepNumber) {
        this._totalSteps = Math.max(2, stepNumber);
        this.totalStepsArray = [];
        for (let i = 1; i <= this._totalSteps; i++) {
            this.totalStepsArray.push(i);
        }
    }
    // #endregion --------Inputs------------ //
    //#region  --------------getter -----------//
    get currentStep() {
        return this._currentStep;
    }
    get totalSteps() {
        return this._totalSteps;
    }
    get announceStr() {
        return this._announceStr;
    }
    currentStepLimit(stepNumber) {
        return Math.min(this.totalSteps, Math.max(0, stepNumber));
    }
    updateStep() {
        //makes sure the values are valid
        this.totalSteps = Math.max(2, this.totalSteps);
        this._currentStep = this.currentStepLimit(this.currentStep);
        //emits the changes
        this.totalStepsChange.emit(this.totalSteps);
        this.currentStepChange.emit(this.currentStep);
    }
    ngOnInit() { }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStepper, deps: [{ token: i1$1.LiveAnnouncer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsStepper, selector: "cds-stepper", inputs: { announceStr: "announceStr", currentStep: "currentStep", totalSteps: "totalSteps", small: "small" }, outputs: { currentStepChange: "currentStepChange", totalStepsChange: "totalStepsChange" }, host: { classAttribute: "cds-stepper" }, ngImport: i0, template: `
    <div
      class="stepperContainer"
      [attr.aria-label]="announceStr"
      role="tablist" >
      <div class="stepContainer">
        <span
          class="misc-step-state-next"
          *ngFor="let step of totalStepsArray"
          [ngStyle]="{
            width: (277 - 4 * (totalSteps - 1)) / totalSteps + 'px'
          }"
          role="tab"
        >
          <span class="step done current" [ngClass]="small ? 'small-step' : ''" *ngIf="step < currentStep"></span>
          <span class="step current" [ngClass]="small ? 'small-step' : ''" *ngIf="step == currentStep"></span>
          <span
            class="step"
            [ngClass]="small ? 'small-step' : ''"
            *ngIf="step > currentStep && step <= totalSteps"
          ></span>
        </span>

        <cds-icon
          *ngIf="currentStep >= totalSteps"
          class="checkmark"
          aria-hidden="true"
          idStr="icon-id"
          ariaLabel="icon"
          type="2021"
          name="05-check-float"
          [svgStyle]="{
            'width.px': '22',
            'height.px': '22',
            'vertical-align': 'top'
          }"
          shade="dark"
        ></cds-icon>
      </div>
    </div>
  `, isInline: true, styles: [".stepper-container{width:310px;height:24px}.step-container{width:281px;height:12px;float:left}.step{height:12px;background-color:#ccc;border-radius:6px;display:inline-block;margin-right:4px;width:inherit}.small-step{height:4px}.current,.done{background-color:#056dae}.misc-step-state-next{height:12px}.checkmark{padding-left:5px}\n"], dependencies: [{ kind: "directive", type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "component", type: CdsIcon, selector: "cds-icon", inputs: ["role", "idStr", "ariaLabel", "size", "isHoverSfi", "includeCircle", "type", "svgStyle", "tabIndex", "altText", "shade", "name", "pathColor", "circleColor"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStepper, decorators: [{
            type: Component,
            args: [{ selector: 'cds-stepper', host: { class: 'cds-stepper' }, template: `
    <div
      class="stepperContainer"
      [attr.aria-label]="announceStr"
      role="tablist" >
      <div class="stepContainer">
        <span
          class="misc-step-state-next"
          *ngFor="let step of totalStepsArray"
          [ngStyle]="{
            width: (277 - 4 * (totalSteps - 1)) / totalSteps + 'px'
          }"
          role="tab"
        >
          <span class="step done current" [ngClass]="small ? 'small-step' : ''" *ngIf="step < currentStep"></span>
          <span class="step current" [ngClass]="small ? 'small-step' : ''" *ngIf="step == currentStep"></span>
          <span
            class="step"
            [ngClass]="small ? 'small-step' : ''"
            *ngIf="step > currentStep && step <= totalSteps"
          ></span>
        </span>

        <cds-icon
          *ngIf="currentStep >= totalSteps"
          class="checkmark"
          aria-hidden="true"
          idStr="icon-id"
          ariaLabel="icon"
          type="2021"
          name="05-check-float"
          [svgStyle]="{
            'width.px': '22',
            'height.px': '22',
            'vertical-align': 'top'
          }"
          shade="dark"
        ></cds-icon>
      </div>
    </div>
  `, styles: [".stepper-container{width:310px;height:24px}.step-container{width:281px;height:12px;float:left}.step{height:12px;background-color:#ccc;border-radius:6px;display:inline-block;margin-right:4px;width:inherit}.small-step{height:4px}.current,.done{background-color:#056dae}.misc-step-state-next{height:12px}.checkmark{padding-left:5px}\n"] }]
        }], ctorParameters: () => [{ type: i1$1.LiveAnnouncer }], propDecorators: { announceStr: [{
                type: Input
            }], currentStep: [{
                type: Input
            }], totalSteps: [{
                type: Input
            }], small: [{
                type: Input
            }], currentStepChange: [{
                type: Output
            }], totalStepsChange: [{
                type: Output
            }] } });

const COMPONENTS$2 = [CdsStepper];
class CdsStepperModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStepperModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsStepperModule, declarations: [CdsStepper], imports: [CommonModule, CdsIconModule], exports: [CdsStepper] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStepperModule, imports: [CommonModule, CdsIconModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsStepperModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS$2],
                    imports: [CommonModule, CdsIconModule],
                    exports: [...COMPONENTS$2]
                }]
        }] });

class CdsFicoChartComponent {
    // #endregion
    // #region ========== Constructor / Life-cycle Hooks ========== //
    constructor(_cdr) {
        this._cdr = _cdr;
        // #region ========== Properties ========== //
        this._defaultDiameter = 160;
        this._defaultStrokeWidth = 20;
        this._canvasWidth = this._defaultDiameter + this._defaultStrokeWidth;
        this._canvasHeight = (this._defaultDiameter / 2) + this._defaultStrokeWidth;
        // #endregion
        // #region ========== @Input ========== //
        this.percent = 50;
        this.diameter = this._defaultDiameter;
        this.strokeWidth = this._defaultStrokeWidth;
        this.colorList = ['#D32724', '#E16A0A', '#EFBD57', '#8FCC79', '#6BB82B'];
        this.backColor = '#dddddd';
    }
    ngOnInit() {
        this._updateCanvasDimensions();
    }
    ngAfterViewInit() {
        this._renderCanvas();
    }
    ngOnChanges(changes) {
        for (const propName in changes) {
            const change = changes[propName];
            if (!change.isFirstChange()) {
                if (['diameter', 'strokeWidth'].includes(propName)) {
                    this._updateCanvasDimensions();
                }
                this._renderCanvas();
            }
        }
    }
    // #endregion
    // #region ========== Methods ========== //
    _renderCanvas() {
        const canvasEl = this.canvasRef?.nativeElement;
        const canvasContext = canvasEl?.getContext('2d');
        const arcX = (this.diameter + this.strokeWidth) / 2;
        const arcY = arcX;
        const radius = this.diameter / 2;
        // do nothing if canvas element not available
        if (!canvasEl) {
            return;
        }
        // clear canvas
        canvasContext.clearRect(0, 0, canvasEl.width, canvasEl.height);
        // adding background
        canvasContext.beginPath();
        canvasContext.arc(arcX, arcY, radius, Math.PI, Math.PI * 2);
        canvasContext.strokeStyle = this.backColor;
        canvasContext.lineWidth = this.strokeWidth;
        canvasContext.lineCap = 'round';
        canvasContext.stroke();
        if (this.percent > 0) {
            // configure gradient
            const gradient = canvasContext.createConicGradient(Math.PI, arcX, arcY);
            for (let i = 0, cl = this.colorList.length; i < cl; i++) {
                const color = this.colorList[i];
                const offset = ((1 / (cl - 1)) * i) / 2;
                gradient.addColorStop(offset, color);
            }
            gradient.addColorStop(0.75, this.colorList[this.colorList.length - 1]);
            gradient.addColorStop(1, this.colorList[0]);
            // adding chart with gradient
            canvasContext.beginPath();
            canvasContext.arc(arcX, arcY, radius, Math.PI, Math.PI + ((this.percent / 100) * Math.PI));
            canvasContext.strokeStyle = gradient;
            canvasContext.lineWidth = this.strokeWidth;
            canvasContext.lineCap = 'round';
            canvasContext.stroke();
        }
    }
    _updateCanvasDimensions() {
        this._canvasWidth = this.diameter + this.strokeWidth;
        this._canvasHeight = (this.diameter / 2) + this.strokeWidth;
        this._cdr.detectChanges();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFicoChartComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.3", type: CdsFicoChartComponent, selector: "cds-fico-chart", inputs: { percent: "percent", diameter: "diameter", strokeWidth: "strokeWidth", colorList: "colorList", backColor: "backColor", startText: "startText", endText: "endText", ariaLabel: "ariaLabel", textMargin: "textMargin" }, host: { classAttribute: "cds-fico-chart" }, viewQueries: [{ propertyName: "canvasRef", first: true, predicate: ["canvasRef"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
  <div class="fico-chart-wrapper" role="img" [attr.aria-label]="ariaLabel">
    <div class="fico-canvas-wrapper" [style.height.px]="_canvasHeight">
      <canvas #canvasRef [width]="_canvasWidth" [height]="_canvasHeight"></canvas>
      <div class="fico-content-wrapper">
        <div class="fico-content">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
    <div class="fico-text" [style.margin-left.px]="textMargin" [style.margin-right.px]="textMargin">
      <div class="fico-text-start">{{startText}}</div>
      <div class="fico-text-end">{{endText}}</div>
    </div>
  </div>
  `, isInline: true, styles: [".fico-chart-wrapper{display:inline-block}.fico-canvas-wrapper{padding:0 6px;position:relative}.fico-text-end{margin-left:auto}.fico-text{display:flex;font-family:Interstate_Regular;font-size:14px}.fico-content-wrapper{display:flex;flex-direction:column;justify-content:flex-end;position:absolute;width:100%;height:100%;top:0;left:0;padding:0 6px}.fico-content{text-align:center}\n"] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFicoChartComponent, decorators: [{
            type: Component,
            args: [{ selector: 'cds-fico-chart', template: `
  <div class="fico-chart-wrapper" role="img" [attr.aria-label]="ariaLabel">
    <div class="fico-canvas-wrapper" [style.height.px]="_canvasHeight">
      <canvas #canvasRef [width]="_canvasWidth" [height]="_canvasHeight"></canvas>
      <div class="fico-content-wrapper">
        <div class="fico-content">
          <ng-content></ng-content>
        </div>
      </div>
    </div>
    <div class="fico-text" [style.margin-left.px]="textMargin" [style.margin-right.px]="textMargin">
      <div class="fico-text-start">{{startText}}</div>
      <div class="fico-text-end">{{endText}}</div>
    </div>
  </div>
  `, host: {
                        class: 'cds-fico-chart'
                    }, styles: [".fico-chart-wrapper{display:inline-block}.fico-canvas-wrapper{padding:0 6px;position:relative}.fico-text-end{margin-left:auto}.fico-text{display:flex;font-family:Interstate_Regular;font-size:14px}.fico-content-wrapper{display:flex;flex-direction:column;justify-content:flex-end;position:absolute;width:100%;height:100%;top:0;left:0;padding:0 6px}.fico-content{text-align:center}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { canvasRef: [{
                type: ViewChild,
                args: ['canvasRef']
            }], percent: [{
                type: Input
            }], diameter: [{
                type: Input
            }], strokeWidth: [{
                type: Input
            }], colorList: [{
                type: Input
            }], backColor: [{
                type: Input
            }], startText: [{
                type: Input
            }], endText: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], textMargin: [{
                type: Input
            }] } });

const COMPONENTS$1 = [CdsFicoChartComponent];
class CdsFicoChartModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFicoChartModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsFicoChartModule, declarations: [CdsFicoChartComponent], imports: [CommonModule], exports: [CdsFicoChartComponent] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFicoChartModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsFicoChartModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [...COMPONENTS$1],
                    exports: [...COMPONENTS$1]
                }]
        }] });

const COMPONENTS = [];
class CdsTablesModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTablesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsTablesModule, imports: [CommonModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTablesModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsTablesModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENTS],
                    imports: [
                        CommonModule
                    ],
                    exports: [...COMPONENTS]
                }]
        }] });

/**
 * @WARNING DO NOT add components here!
 * This module should only be used for applications singlton services.
 * Each component should be exported from it's own module and imported
 * individually by consuming teams
 */
class CdsCoreModule {
    static forRoot() {
        return {
            ngModule: CdsCoreModule,
            providers: [CdsIconRegistry]
        };
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.3", ngImport: i0, type: CdsCoreModule, imports: [CommonModule] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCoreModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.3", ngImport: i0, type: CdsCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: []
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { Accordion, Accordion2, Accordion2Component, Accordion2SectionComponent, AccordionComponent, AccordionSectionComponent, ActionBarComponent, Animation, BrowserNavigatorRef, CC_MASK_VALIDATION, CHECKBOX2_CONTROL_VALUE_ACCESSOR, CHECKBOX_CONTROL_VALUE_ACCESSOR, CHECKBOX_GROUP_CONTROL_VALUE_ACCESSOR, CVV_MASK_VALIDATION, CdsAlertDialog, CdsAlertDialogModule, CdsAnnouncerComponent, CdsAnnouncerService, CdsAriaHideOuterDomDirective, CdsAriaHideOuterDomModule, CdsBannerAlert, CdsBannerAlertModule, CdsBreadcrumbComponent, CdsBreadcrumbModule, CdsButton, CdsButtonModule, CdsCCMaskDirective, CdsCTA, CdsCTAModule, CdsCalendar, CdsCalendarModule, CdsCarouselComponent, CdsCarouselModule, CdsCarouselSlide, CdsCheckbox, CdsCheckbox2, CdsCheckbox2Group, CdsCheckbox2Module, CdsCheckboxGroup, CdsCheckboxModule, CdsCheckboxTreeComponent, CdsCheckboxTreeModule, CdsCheckboxTreeNodeComponent, CdsChip, CdsChip2, CdsChip2Module, CdsChip3Component, CdsChipBarComponent, CdsChipBarModule, CdsChipModule, CdsColumnComponent, CdsCoreModule, CdsCurrencyFormatDirective, CdsCurrencyMask, CdsCvvMaskDirective, CdsDateMask, CdsDateMask2Directive, CdsDateMaskDirective, CdsDateMaskPipeModule, CdsDonutChartComponent, CdsDonutChartModule, CdsDonutChartSegmentComponent, CdsDropdown, CdsDropdown2Component, CdsDropdown2Module, CdsDropdownModule, CdsDropdownMultiComponent, CdsDropdownMultiModule, CdsElectronicSignatureComponent, CdsElectronicSignatureModule, CdsEmailMaskDirective, CdsFicoChartComponent, CdsFicoChartModule, CdsFileUpload, CdsFileUploadComponent, CdsFixedMask, CdsFocusTrap, CdsFocusTrapModule, CdsForm, CdsFormError, CdsFormField, CdsFormLabel, CdsFormSuccess, CdsFormatModule, CdsFormsModule, CdsFullDateMaskPipe, CdsFullTextMaskPipe, CdsFullTextMaskPipeModule, CdsGaugeModule, CdsGridModule, CdsIcon, CdsIconModule, CdsIconRegistry, CdsIconWrapper, CdsInput, CdsInputMaskDirective, CdsInputModule, CdsJampModule, CdsLayoutComponent, CdsList, CdsListModule, CdsLoadingPlaceholder, CdsLoadingPlaceholderModule, CdsLogoComponent, CdsLogoModule, CdsLongDateMaskPipe, CdsMaskModule, CdsMediumDateMaskPipe, CdsMessageBarComponent, CdsModalDialogComponent, CdsModalDialogModule, CdsMonthYearDateMaskPipe, CdsOption, CdsOption2Component, CdsOptionMultiComponent, CdsOverlayPanel, CdsPageDotsComponent, CdsPageDotsModule, CdsPageLayoutModule, CdsPagination, CdsPaginationItem, CdsPaginationModule, CdsPartialEmailMaskPipe, CdsPartialEmailMaskPipeModule, CdsPartialMask, CdsPartialTextMaskPipe, CdsPartialTextMaskPipeModule, CdsPhoneMaskDirective, CdsPillar, CdsPillarGroup, CdsPillarModule, CdsPopup2Component, CdsPopup2Module, CdsPopupClose, CdsPopupDialogComponent, CdsPopupDialogModule, CdsPopupFocus, CdsPopupHost, CdsPopupPanel, CdsPopupPanelModule, CdsProgress, CdsProgressBar, CdsProgressBar2, CdsProgressBar2Module, CdsProgressBarModule, CdsProgressIndicator2, CdsProgressIndicator2Module, CdsProgressModule, CdsQuantity, CdsQuantityModule, CdsRadialProgressBar, CdsRadialProgressBarComponent, CdsRadio, CdsRadioChange, CdsRadioGroup, CdsRadioModule, CdsRowComponent, CdsSelectComponent, CdsShortDateMaskPipe, CdsShortMonthMaskPipe, CdsSignOn, CdsSlider, CdsSliderModule, CdsSsnMaskDirective, CdsStatusTracker, CdsStatusTracker2, CdsStatusTracker2Module, CdsStatusTracker3Q21, CdsStatusTrackerModule, CdsStatusTrackerModule3Q21, CdsStatusTrackerStep, CdsStepper, CdsStepperModule, CdsTab, CdsTabBar, CdsTabBarGroup, CdsTabBarModule, CdsTabGroup, CdsTablesModule, CdsTabsModule, CdsTextHeader, CdsTextHeaderModule, CdsTextarea, CdsTextareaModule, CdsThemeService, CdsTileModule, CdsTimeline, CdsTimeline2, CdsTimeline2Module, CdsTimeline2Step, CdsTimelineModule, CdsTimerComponent, CdsTimerModule, CdsTooltip, CdsTooltip2Host, CdsTooltipClose, CdsTooltipFocus, CdsTooltipHost, CdsTooltipModule, CdsTourComponent, CdsTourModule, CdsValidators, CdsWebService, CdsZipcodeMaskDirective, DATE_MASK_VALIDATION, DayComponent, Drawer, DrawerComponent, EMAIL_MASK, EMAIL_MASK_DIRECTIVE_VALIDATION, EMAIL_MASK_VALIDATION, EMAIL_PATTERN, Ellipses, EmailValidatorDir, FILE_UPLOAD_CONTROL_VALUE_ACCESSOR, FIXED_MASKS, GaugeComponent, HIDER, ICON_REGISTRY_PROVIDER, ICON_REGISTRY_PROVIDER_FACTORY, InputMonthComponent, JampComponent, MASK_CHECKERS, MASK_HIDER, MASK_SPECIALS, MaskingBase, MasksBase, MonthComponent, NAVIGATOR, NAVIGATOR_PROVIDER, NavigatorRef, OverlayPanelModule, PARTIAL_PHONE_MASK_VALIDATION, PARTIAL_SSN_MASK_VALIDATION, PHONE_MASK_VALIDATION, PHONE_PATTERN, SERVER_URL, SHORT_DATE_MASK_VALIDATION, SHORT_MONTH_MASK_VALIDATION, SSN_MASK_VALIDATION, SSN_PATTERN, SelectModule, SignOn2Module, SignOnComponent, Signon2Component, TaggingDataLayerService, TileComponent, Transition, URL_PATTERN, ZIPCODE_PATTERN, after, afterValidator, alphaNumericChecker, anyChecker, before, cdsHasLower, cdsHasNumber, cdsHasUpper, cdsNoNumbers, cdsPasswordVal, cdsValidateEmail, devError, devKill, devWarn, findLastIndex, focusable, getElementChildren, getElementParents, getElementParentsUntil, getElementSiblings, getFocableAbleElements, getRandomString, getRoundedFloat, getScrollParents, getScrollbarWidth, isDate, isSame, isWritable, lowerCaseChecker, lteChecker, navigatorFactory, neverChecker, nonZeroNumericChecker, numericChecker, onStable, replaceAt, singleChecker, toRawType, upperCaseChecker };
